import { useState, useEffect, useRef, useMemo } from "react";
import { BarChart, Bar, XAxis, YAxis, Tooltip, Legend, PieChart, Pie, Cell, ResponsiveContainer, LineChart, Line, CartesianGrid } from "recharts";

// ─── API ──────────────────────────────────────────────────────────────────────
async function apiFetch(method, path, body, token) {
  const res = await fetch(path, {
    method,
    headers: {
      "Content-Type": "application/json",
      ...(token ? { Authorization: `Bearer ${token}` } : {}),
    },
    body: body !== undefined ? JSON.stringify(body) : undefined,
  });
  const data = await res.json().catch(() => ({}));
  if (res.status === 401) {
    window.dispatchEvent(new CustomEvent("rh:sessao-expirada"));
    throw new Error("Sessão expirada. Faça login novamente.");
  }
  if (!res.ok) throw new Error(data.erro || data.message || `Erro ${res.status}`);
  return data;
}

function adaptFuncionario(f) {
  return {
    id: f.id,
    nome: f.nome || "",
    cargo: f.cargo || "",
    grupo: f.grupo || "",
    sub_grupo: f.sub_grupo || "",
    setor: f.setor || "",
    subsetor: f.subsetor || "",
    turno: f.turno || "Diurno",
    empresa: f.empresa || "",
    empresa_id: f.empresa_id,
    admissao: f.data_admissao || "",
    salario: parseFloat(f.salario) || 0,
    status: { ativo: "Ativo", ferias: "Férias", afastado: "Afastado", demitido: "Demitido" }[f.status] || "Ativo",
    dataDemissao: f.data_demissao || "",
    telefone: f.telefone || "",
    cpf: f.cpf || "",
    pis: f.pis || "",
    hora_entrada: f.hora_entrada ? String(f.hora_entrada).slice(0,5) : "",
    hora_saida: f.hora_saida ? String(f.hora_saida).slice(0,5) : "",
    dias_semana: f.dias_semana ? f.dias_semana.split(",").map(Number) : [1,2,3,4,5],
    horario_sabado: f.horario_sabado || null,
    data_ref_12x36: f.data_ref_12x36 || null,
    ferias: { disponivel: 0, tiradas: 0, proximas: "" },
    docs: { aso: f.aso_data || "", asoVenc: f.aso_vencimento || "" },
    pagamento: {
      tipo: f.tipo_pagamento || "",
      banco: f.banco || "",
      agencia: f.agencia || "",
      conta: f.conta || "",
      digito: f.digito || "",
      tipoPix: f.tipo_pix || "",
      chavePix: f.chave_pix || "",
      pixBancoCodigo: f.pix_banco_codigo || "",
      pixBancoNome: f.pix_banco_nome || "",
      pixBancoIspb: f.pix_banco_ispb || "",
      pixAgencia: f.pix_agencia || "",
      pixAgenciaDv: f.pix_agencia_dv || "",
      pixConta: f.pix_conta || "",
      pixContaDv: f.pix_conta_dv || "",
      pixTipoConta: f.pix_tipo_conta || "corrente",
      pixTipoChave: f.pix_tipo_chave || "",
      fcId:        f.fc_id         || null,
      fcTipo:      f.fc_tipo       || "",
      fcPixTipo:   f.fc_pix_tipo   || "",
      fcPixChave:  f.fc_pix_chave  || "",
      fcBancoNome: f.fc_banco_nome  || "",
      fcBancoCodigo: f.fc_banco_codigo || "",
      fcAgencia:   f.fc_agencia    || "",
      fcAgenciaDv: f.fc_agencia_dv || "",
      fcConta:     f.fc_conta      || "",
      fcContaDv:   f.fc_conta_dv   || "",
      fcTipoConta: f.fc_tipo_conta || "",
      fcDescricao: f.fc_descricao  || "",
    },
    contrato_id: f.contrato_id || "",
    vt_tipo: f.vt_tipo || "",
    cidade: f.cidade || "",
    ctps: f.ctps || "",
    titulo_eleitor: f.titulo_eleitor || "",
    matricula: f.matricula || "",
    tam_camisa: f.tam_camisa || "",
    tam_calca: f.tam_calca || "",
    tam_sapato: f.tam_sapato || "",
    tam_bota: f.tam_bota || "",
    hora_saida_almoco: f.hora_saida_almoco ? String(f.hora_saida_almoco).slice(0,5) : "",
    hora_volta_almoco: f.hora_volta_almoco ? String(f.hora_volta_almoco).slice(0,5) : "",
    sal_familia: parseFloat(f.sal_familia) || 0,
    adiantamento: f.adiantamento ? 1 : 0,
    insalubridade_grau: f.insalubridade_grau || "",
    vt_cartao_otimo: f.vt_cartao_otimo || "",
    vt_cartao_bhbus: f.vt_cartao_bhbus || "",
    vt_cartao_gasolina: f.vt_cartao_gasolina || "",
    vt_cartao_betimcard: f.vt_cartao_betimcard || "",
    vt_cartao_outros: f.vt_cartao_outros || "",
    vt_valor_otimo: parseFloat(f.vt_valor_otimo) || 0,
    vt_valor_bhbus: parseFloat(f.vt_valor_bhbus) || 0,
    vt_valor_gasolina: parseFloat(f.vt_valor_gasolina) || 0,
    vt_valor_betimcard: parseFloat(f.vt_valor_betimcard) || 0,
    vt_valor_outros: parseFloat(f.vt_valor_outros) || 0,
    vt_dias_otimo: parseInt(f.vt_dias_otimo) || 0,
    vt_dias_bhbus: parseInt(f.vt_dias_bhbus) || 0,
    vt_dias_gasolina: parseInt(f.vt_dias_gasolina) || 0,
    vt_dias_betimcard: parseInt(f.vt_dias_betimcard) || 0,
    vt_dias_outros: parseInt(f.vt_dias_outros) || 0,
    vr_dia: parseFloat(f.vr_dia) || 0,
    vr_dias: parseInt(f.vr_dias) || 0,
    cidade_moradia: f.cidade_moradia || "",
    endereco_trabalho: f.endereco_trabalho || "",
    nome_pai: f.nome_pai || "",
    nome_mae: f.nome_mae || "",
    estado_civil: f.estado_civil || "",
    cnh: f.cnh || "",
    pre_cadastro: f.pre_cadastro ? 1 : 0,
    endereco: f.endereco || "",
    data_nascimento: f.data_nascimento ? String(f.data_nascimento).slice(0, 10) : "",
    rg: f.rg || "",
    email: f.email || "",
  };
}

function adaptEscala(s) {
  return {
    id: s.id,
    funcionarioId: s.funcionario_id,
    dataInicio: s.data_inicio || "",
    dataFim: s.data_fim || "",
    data: s.data_inicio || "",
    local: s.local || "",
    turno: s.turno || "",
    status: s.status === "confirmado" ? "Confirmado" : s.status === "pendente" ? "Pendente" : "Substituição",
    observacao: s.observacao || "",
  };
}

function adaptAfastamento(a) {
  const inicio = a.data_inicio || "";
  const fim = a.data_fim || "";
  const dias = (inicio && fim) ? Math.round((new Date(fim) - new Date(inicio)) / 86400000) + 1 : 0;
  let tipo;
  if (a.tipo === "ferias") tipo = "Férias";
  else if (!a.tipo || a.tipo === "afastamento") tipo = "Atestado Médico";
  else tipo = a.tipo;
  return {
    id: a.id,
    funcionarioId: a.funcionario_id,
    tipo,
    inicio,
    fim,
    dias,
    motivo: a.motivo || "",
    cid: "",
    atestado_nome: a.atestado_nome || null,
    status: (!fim || new Date(fim + "T23:59:59") >= new Date()) ? "Em andamento" : "Encerrado",
  };
}

function adaptObservacao(o) {
  return {
    id: o.id,
    descricao: o.descricao || "",
    data: o.data || "",
    nivel: { urgente: "Urgente", normal: "Normal", baixo: "Normal" }[o.nivel] || "Normal",
  };
}

// Calcula vencimento de férias de um funcionário
// - tenure < 1 ano: ainda não acumulou → { tipo:'aguardando', mesesFaltando }
// - sem férias tiradas e tenure >= 1 ano: vence admissão + 2 anos
// - com férias tiradas: vence último fim + 12 meses
function calcVencimentoFerias(employee, afastamentos) {
  if (!employee.data_admissao) return null;
  const hoje = new Date(); hoje.setHours(0, 0, 0, 0);
  const admissao = new Date(employee.data_admissao + 'T00:00:00');
  const mesesNaEmpresa = (hoje - admissao) / (30.44 * 86400000);
  if (mesesNaEmpresa < 12) {
    return { tipo: 'aguardando', mesesFaltando: Math.ceil(12 - mesesNaEmpresa) };
  }
  const feriasFunc = (afastamentos || [])
    .filter(a => Number(a.funcionarioId) === Number(employee.id) && a.tipo === 'Férias' && a.fim)
    .sort((a, b) => new Date(b.fim) - new Date(a.fim));
  let vencimento;
  if (feriasFunc.length > 0) {
    const ultimaFim = new Date(feriasFunc[0].fim + 'T00:00:00');
    vencimento = new Date(ultimaFim);
    vencimento.setFullYear(vencimento.getFullYear() + 1);
  } else {
    vencimento = new Date(admissao);
    vencimento.setFullYear(vencimento.getFullYear() + 2);
  }
  const dias = Math.ceil((vencimento - hoje) / 86400000);
  return { tipo: 'data', vencimento, dias, meses: Math.ceil(dias / 30.44) };
}

const VT_TIPOS_OPTS = [
  { value: "bhbus", label: "BHBus" },
  { value: "otimo", label: "Ótimo" },
  { value: "gasolina", label: "Gasolina" },
  { value: "betimcard", label: "Betim Card" },
  { value: "outros", label: "Outros" },
  { value: "nao_pega", label: "Não pega VT" },
];
const VT_SEM_VALOR = ["nao_pega"];
const TIPO_LABELS_VT = { bhbus: "BHBus", otimo: "Ótimo", gasolina: "Gasolina", betimcard: "Betim Card", outros: "Outros", nao_pega: "Não pega VT" };
const parseVtTipos = (str) => str ? str.split(",").map(s => s.trim()).filter(Boolean) : [];
const joinVtTipos = (arr) => arr.join(",");

// Verifica se uma data (YYYY-MM-DD) está dentro do período do feriado (data..data_fim, ou só data)
function feriadoCobreData(f, dataStr) {
  const norm = (d) => d ? String(d).slice(0, 10) : null;
  const ini = norm(f.data);
  const fim = f.data_fim ? norm(f.data_fim) : ini;
  return dataStr >= ini && dataStr <= fim;
}

const COLORS_LIGHT = {
  primary:     "#2e7a00",
  primaryDark: "#1f5500",
  primaryBg:   "#edf7e3",
  accent:      "#d97706",
  danger:      "#ef4444",
  dangerBg:    "#fef2f2",
  warning:     "#f59e0b",
  warningBg:   "#fffbeb",
  info:        "#2e7a00",
  infoBg:      "#edf7e3",
  secondary:   "#2e7a00",
  white:       "#FFFFFF",
  bg:          "#dde6ec",
  surface:     "#FFFFFF",
  sidebar:     "#ffffff",
  sidebarItem: "rgba(46,122,0,0.09)",
  gray:        "#f0f4f7",
  border:      "#e8edf2",
  borderFocus: "#2e7a00",
  text:        "#111827",
  textMid:     "#6b7280",
  textLight:   "#9ca3af",
  dark:        "#111827",
  textDark:    "#111827",
};

const COLORS_DARK = {
  primary:     "#4ade80",   // verde mais claro — legível sobre fundo escuro
  primaryDark: "#22c55e",
  primaryBg:   "#052e16",   // verde escuro só em highlights pontuais
  accent:      "#fbbf24",
  danger:      "#f87171",
  dangerBg:    "#450a0a",
  warning:     "#fbbf24",
  warningBg:   "#451a03",
  info:        "#4ade80",
  infoBg:      "#052e16",
  secondary:   "#4ade80",
  white:       "#1e293b",   // branco vira cinza-card
  bg:          "#0f172a",   // fundo principal — azul-marinho escuro neutro
  surface:     "#1e293b",   // cards e painéis
  sidebar:     "#0f172a",   // sidebar igual ao fundo
  sidebarItem: "#334155",   // hover de item de menu
  gray:        "#293548",   // áreas sutis
  border:      "#334155",   // bordas visíveis mas discretas
  borderFocus: "#4ade80",
  text:        "#f1f5f9",   // texto principal — quase branco
  textMid:     "#94a3b8",   // texto secundário — cinza-ardósia
  textLight:   "#64748b",   // texto fraco
  dark:        "#f1f5f9",
  textDark:    "#f1f5f9",
};

const COLORS = { ...COLORS_LIGHT };
// Aplica preferência salva antes do primeiro render
if (localStorage.getItem("rh_dark") === "1") {
  Object.assign(COLORS, COLORS_DARK);
  document.body.style.background = COLORS_DARK.bg;
} else {
  document.body.style.background = COLORS_LIGHT.bg;
}

const INITIAL_EMPLOYEES = [
  { id: 1, nome: "Maria Silva", cargo: "Auxiliar de Limpeza", setor: "Limpeza", turno: "Manhã", admissao: "2022-03-15", salario: 1600, status: "Ativo", telefone: "(31) 9 8888-1111", cpf: "123.456.789-00", pis: "12345678901", ferias: { disponivel: 15, tiradas: 15, proximas: "2025-03-15" }, docs: { aso: "2024-12-01", ctps: "OK", nr35: "2024-06-10" } },
  { id: 2, nome: "João Souza", cargo: "Supervisor", setor: "Conservação", turno: "Manhã", admissao: "2020-07-01", salario: 2800, status: "Ativo", telefone: "(31) 9 7777-2222", cpf: "987.654.321-00", pis: "98765432100", ferias: { disponivel: 30, tiradas: 0, proximas: "2025-07-01" }, docs: { aso: "2024-11-20", ctps: "OK", nr35: "2024-05-15" } },
  { id: 3, nome: "Ana Oliveira", cargo: "Auxiliar de Conservação", setor: "Conservação", turno: "Tarde", admissao: "2023-01-10", salario: 1600, status: "Férias", telefone: "(31) 9 6666-3333", cpf: "456.789.123-00", pis: "45678912300", ferias: { disponivel: 0, tiradas: 30, proximas: "2026-01-10" }, docs: { aso: "2025-01-05", ctps: "OK", nr35: "2025-01-05" } },
  { id: 4, nome: "Carlos Lima", cargo: "Auxiliar de Limpeza", setor: "Limpeza", turno: "Noite", admissao: "2021-05-20", salario: 1750, status: "Afastado", telefone: "(31) 9 5555-4444", cpf: "321.654.987-00", pis: "32165498700", ferias: { disponivel: 20, tiradas: 10, proximas: "2025-05-20" }, docs: { aso: "2024-10-10", ctps: "OK", nr35: "2024-03-20" } },
  { id: 5, nome: "Fernanda Costa", cargo: "Encarregada", setor: "Limpeza", turno: "Manhã", admissao: "2019-11-08", salario: 2400, status: "Ativo", telefone: "(31) 9 4444-5555", cpf: "654.321.987-00", pis: "65432198700", ferias: { disponivel: 30, tiradas: 0, proximas: "2024-11-08" }, docs: { aso: "2025-02-14", ctps: "OK", nr35: "2024-08-01" } },
];

const PONTO_INICIAL = {
  1: { "2025-03-03": { entrada: "07:00", saida: "16:00", extra: 0 }, "2025-03-04": { entrada: "07:00", saida: "17:30", extra: 1.5 }, "2025-03-05": { entrada: "07:00", saida: "16:00", extra: 0 } },
  2: { "2025-03-03": { entrada: "07:00", saida: "16:00", extra: 0 }, "2025-03-04": { entrada: "07:00", saida: "16:00", extra: 0 }, "2025-03-05": { entrada: "07:00", saida: "18:00", extra: 2 } },
};

const ESCALA_INICIAL = [
  { id: 1, funcionarioId: 1, data: "2025-03-10", local: "Hospital Central", turno: "Manhã", status: "Confirmado" },
  { id: 2, funcionarioId: 2, data: "2025-03-10", local: "Prédio Comercial A", turno: "Manhã", status: "Confirmado" },
  { id: 3, funcionarioId: 5, data: "2025-03-10", local: "Shopping Norte", turno: "Manhã", status: "Pendente" },
  { id: 4, funcionarioId: 1, data: "2025-03-11", local: "Hospital Central", turno: "Manhã", status: "Confirmado" },
  { id: 5, funcionarioId: 4, data: "2025-03-12", local: "Condomínio Bela Vista", turno: "Noite", status: "Substituição" },
];

const AFASTAMENTOS_INICIAL = [
  { id: 1, funcionarioId: 4, tipo: "Atestado Médico", inicio: "2025-03-01", fim: "2025-03-15", dias: 15, motivo: "Cirurgia", cid: "K35", status: "Em andamento" },
  { id: 2, funcionarioId: 3, tipo: "Férias", inicio: "2025-03-05", fim: "2025-04-04", dias: 30, motivo: "Férias anuais", cid: "", status: "Em andamento" },
];

const SETORES_INICIAL = [
  { id: 1, nome: "Limpeza", subsetores: ["Limpeza Hospitalar", "Limpeza Comercial", "Limpeza Residencial"] },
  { id: 2, nome: "Conservação", subsetores: ["Conservação Predial", "Conservação de Jardins"] },
  { id: 3, nome: "Administrativo", subsetores: ["RH", "Financeiro", "Operações"] },
];

const EMPRESAS_INICIAL = [
  { id: 1, nome: "Empresa A", cnpj: "", endereco: "", telefone: "", email: "" },
  { id: 2, nome: "Empresa B", cnpj: "", endereco: "", telefone: "", email: "" },
];

const statusColor = (s) => ({
  "Ativo":    COLORS.primary,
  "Férias":   COLORS.warning,
  "Afastado": COLORS.warning,
  "Demitido": COLORS.danger,
}[s] || COLORS.textMid);

// inject global styles once
if (!document.getElementById("rh-global-styles")) {
  const s = document.createElement("style");
  s.id = "rh-global-styles";
  s.textContent = `
    .rh-tr:hover td { background: #F1F5F9 !important; }
    .rh-btn-ghost:hover { background: #F1F5F9 !important; }
    .rh-sidebar-item:hover { background: rgba(46,122,0,0.10) !important; }
    .rh-input:focus { border-color: #2e7a00 !important; box-shadow: 0 0 0 3px rgba(46,122,0,0.14) !important; }
    .rh-card-kpi { position: relative; overflow: hidden; }
    .rh-card-kpi::before { content:''; position:absolute; top:0; left:0; right:0; height:3px; background:linear-gradient(90deg,#2e7a00,#fee140); opacity:0; transition:opacity .25s; border-radius:20px 20px 0 0; }
    .rh-card-kpi:hover::before { opacity:1; }
    .rh-card-kpi:hover { transform: translateY(-2px); box-shadow: 0 8px 32px rgba(46,122,0,0.14) !important; }
    .rh-glass { backdrop-filter: blur(16px); -webkit-backdrop-filter: blur(16px); }
    .rh-card-hover:hover { transform: translateY(-1px); box-shadow: 0 8px 28px rgba(46,122,0,0.10) !important; }
    ::-webkit-scrollbar { width: 8px; height: 8px; }
    ::-webkit-scrollbar-track { background: #f0f4f7; border-radius: 99px; }
    ::-webkit-scrollbar-thumb { background: #14532d; border-radius: 99px; }
    ::-webkit-scrollbar-thumb:hover { background: #166534; }
  `;
  document.head.appendChild(s);
}

const Badge = ({ color, children }) => (
  <span style={{
    background: color + "12", color,
    borderRadius: 5, padding: "2px 8px",
    fontSize: 11, fontWeight: 600, whiteSpace: "nowrap",
    letterSpacing: "0.03em", display: "inline-block",
  }}>{children}</span>
);

const SectionTitle = ({ children, action }) => (
  <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 16 }}>
    <h2 style={{ fontSize: 15, fontWeight: 700, color: COLORS.text, margin: 0 }}>{children}</h2>
    {action}
  </div>
);

const Card = ({ children, style = {}, accent = false }) => (
  <div style={{
    background: COLORS.surface,
    borderRadius: 14,
    boxShadow: "0 0 0 1px rgba(0,0,0,0.04), 0 2px 6px rgba(0,0,0,0.04), 0 8px 24px rgba(0,0,0,0.06)",
    padding: 20,
    transition: "box-shadow .2s, transform .2s",
    ...(accent ? { borderLeft: `3px solid ${COLORS.primary}`, paddingLeft: 17 } : {}),
    ...style,
  }}>{children}</div>
);

const Input = ({ label, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    {label && <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid, letterSpacing: "0.03em" }}>{label}</label>}
    <input {...props} className="rh-input" style={{
      border: `1.5px solid ${COLORS.border}`, borderRadius: 8,
      padding: "9px 12px", fontSize: 13.5, background: COLORS.gray,
      color: COLORS.text, transition: "border .15s, box-shadow .15s, background .15s",
      outline: "none", ...props.style,
    }} />
  </div>
);

const Select = ({ label, children, ...props }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 5 }}>
    {label && <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid, letterSpacing: "0.03em" }}>{label}</label>}
    <select {...props} className="rh-input" style={{
      border: `1.5px solid ${COLORS.border}`, borderRadius: 8,
      padding: "9px 12px", fontSize: 13.5, background: COLORS.gray,
      color: COLORS.text, outline: "none", ...props.style,
    }}>{children}</select>
  </div>
);

function ThFilter({ label, value, onChange, options, align = "left", style: thStyle = {}, multi = false }) {
  const [open, setOpen] = useState(false);
  const [dropPos, setDropPos] = useState({ top: 0, left: 0 });
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return;
    const handler = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    document.addEventListener('mousedown', handler);
    return () => document.removeEventListener('mousedown', handler);
  }, [open]);
  const active = multi ? value.length > 0 : !!value;
  const toggleOpt = (opt) => onChange(value.includes(opt) ? value.filter(v => v !== opt) : [...value, opt]);
  const displayLabel = multi && value.length > 0
    ? (value.length === 1 ? value[0] : `${value.length} selecionados`)
    : label;
  const handleOpen = () => {
    if (!open && ref.current) {
      const rect = ref.current.getBoundingClientRect();
      const left = Math.min(rect.left, window.innerWidth - 190);
      setDropPos({ top: rect.bottom + 2, left: Math.max(0, left) });
    }
    setOpen(o => !o);
  };
  return (
    <th ref={ref} onClick={handleOpen} style={{
      position: 'relative', padding: '10px 12px', color: active ? COLORS.primary : COLORS.textMid, fontWeight: 700,
      textAlign: align, whiteSpace: 'nowrap', cursor: 'pointer', userSelect: 'none',
      background: active ? COLORS.primaryBg : undefined, ...thStyle,
    }}>
      <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5 }}>
        {displayLabel}
        <span style={{ fontSize: 11, opacity: 0.85, lineHeight: 1 }}>{active ? '▼' : '⌄'}</span>
      </span>
      {open && (
        <div onClick={e => e.stopPropagation()} style={{
          position: 'fixed', top: dropPos.top, left: dropPos.left, zIndex: 9999,
          background: '#fff', border: '1px solid #d1d5db', borderRadius: 6,
          boxShadow: '0 6px 20px rgba(0,0,0,0.18)', minWidth: 180, padding: '4px 0', color: '#111',
          maxHeight: '60vh', overflowY: 'auto',
        }}>
          {multi ? (
            <>
              <div onClick={() => onChange([])} style={{
                padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                fontWeight: value.length === 0 ? 700 : 400,
                background: value.length === 0 ? '#e8f0fe' : undefined,
                color: value.length === 0 ? '#1e3a5f' : '#444',
                borderBottom: '1px solid #f0f0f0',
              }}>(Todas)</div>
              {options.map(opt => {
                const sel = value.includes(opt);
                return (
                  <div key={opt} onClick={() => toggleOpt(opt)} style={{
                    padding: '7px 14px 7px 12px', cursor: 'pointer', fontSize: 13,
                    background: sel ? '#e8f0fe' : undefined,
                    color: sel ? '#1e3a5f' : '#333',
                    display: 'flex', alignItems: 'center', gap: 8,
                  }}>
                    <span style={{
                      width: 15, height: 15, borderRadius: 3,
                      border: `2px solid ${sel ? '#2d5a9e' : '#bbb'}`,
                      background: sel ? '#2d5a9e' : '#fff', flexShrink: 0,
                      display: 'inline-flex', alignItems: 'center', justifyContent: 'center',
                    }}>
                      {sel && <span style={{ color: '#fff', fontSize: 10, lineHeight: 1 }}>✓</span>}
                    </span>
                    <span style={{ fontWeight: sel ? 700 : 400 }}>{opt}</span>
                  </div>
                );
              })}
            </>
          ) : (
            <>
              <div onClick={() => { onChange(''); setOpen(false); }} style={{
                padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                fontWeight: value === '' ? 700 : 400,
                background: value === '' ? '#e8f0fe' : undefined,
                color: value === '' ? '#1e3a5f' : '#444',
              }}>(Todas)</div>
              {options.map(opt => (
                <div key={opt} onClick={() => { onChange(opt); setOpen(false); }} style={{
                  padding: '7px 14px', cursor: 'pointer', fontSize: 13,
                  fontWeight: value === opt ? 700 : 400,
                  background: value === opt ? '#e8f0fe' : undefined,
                  color: value === opt ? '#1e3a5f' : '#333',
                }}>{opt}</div>
              ))}
            </>
          )}
        </div>
      )}
    </th>
  );
}

const BTN_ICONS = {
  plus:     <svg width="13" height="13" viewBox="0 0 14 14" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="7" y1="1" x2="7" y2="13"/><line x1="1" y1="7" x2="13" y2="7"/></svg>,
  pdf:      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M9 2H4a1 1 0 0 0-1 1v10a1 1 0 0 0 1 1h8a1 1 0 0 0 1-1V6"/><polyline points="9 2 9 6 13 6"/><line x1="5" y1="10" x2="11" y2="10"/><line x1="5" y1="13" x2="9" y2="13"/></svg>,
  csv:      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M8 2v9M4 8l4 5 4-5"/><path d="M2 14h12"/></svg>,
  falta:    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="8" cy="8" r="6"/><line x1="8" y1="5" x2="8" y2="11"/><line x1="5" y1="8" x2="11" y2="8"/></svg>,
  hExtra:   <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><circle cx="8" cy="8" r="6"/><polyline points="8 5 8 8 10 10"/></svg>,
  save:     <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M13 13H3a1 1 0 0 1-1-1V4l3-3h7a1 1 0 0 1 1 1v11a1 1 0 0 1-1 1z"/><polyline points="5 1 5 6 11 6"/><polyline points="5 13 5 10 11 10 11 13"/></svg>,
  entrada:  <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="12" x2="8" y2="2"/><polyline points="4 6 8 2 12 6"/><line x1="3" y1="14" x2="13" y2="14"/></svg>,
  saida:    <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round"><line x1="8" y1="4" x2="8" y2="14"/><polyline points="4 10 8 14 12 10"/><line x1="3" y1="2" x2="13" y2="2"/></svg>,
};
const BTN_ICON_MAP = [
  [/^[+➕]\s*Novo\b/,            "plus"],
  [/^[+➕]\s*Nova\b/,            "plus"],
  [/^[+➕]\s*Adicionar\b/,       "plus"],
  [/^[+➕]\s*Cadastrar\b/,       "plus"],
  [/^[+➕]\s*Novo Lançamento\b/, "plus"],
  [/^Gerar PDF\b/,               "pdf"],
  [/^Exportar CSV\b/,            "csv"],
  [/^➕\s*Lançar Falta\b/,       "falta"],
  [/^⏱\s*Lançar H\. Extra\b/,   "hExtra"],
  [/^Registrar Entrada\b/,       "entrada"],
  [/^Registrar Saída\b/,         "saida"],
];
const Btn = ({ children, variant = "primary", size = "md", onClick, style = {}, disabled, title, ...rest }) => {
  const vs = {
    primary:   { bg: "linear-gradient(180deg,#369502 0%,#2a7100 100%)", color: "#fff",          border: "none",                                    shadow: "0 1px 2px rgba(46,122,0,0.25),0 3px 10px rgba(46,122,0,0.18)" },
    secondary: { bg: "linear-gradient(180deg,#369502 0%,#2a7100 100%)", color: "#fff",          border: "none",                                    shadow: "0 1px 2px rgba(46,122,0,0.25),0 3px 10px rgba(46,122,0,0.18)" },
    accent:    { bg: "linear-gradient(180deg,#e07d06 0%,#c46c04 100%)", color: "#fff",          border: "none",                                    shadow: "0 1px 2px rgba(217,119,6,0.25),0 3px 10px rgba(217,119,6,0.15)" },
    danger:    { bg: "linear-gradient(180deg,#f55353 0%,#dc2626 100%)", color: "#fff",          border: "none",                                    shadow: "0 1px 2px rgba(239,68,68,0.25),0 3px 10px rgba(239,68,68,0.15)" },
    ghost:     { bg: COLORS.surface,                                     color: COLORS.textMid, border: `1.5px solid ${COLORS.border}`,            shadow: "0 1px 2px rgba(0,0,0,0.04)" },
    warning:   { bg: COLORS.warningBg,                                   color: COLORS.warning, border: `1.5px solid ${COLORS.warning}40`,         shadow: "none" },
    print:     { bg: COLORS.surface,                                     color: COLORS.textMid, border: `1.5px solid ${COLORS.border}`,            shadow: "0 1px 2px rgba(0,0,0,0.04)" },
    edit:      { bg: COLORS.surface,                                     color: COLORS.primary, border: `1.5px solid ${COLORS.primary}55`,         shadow: "0 1px 2px rgba(46,122,0,0.08)" },
  };
  const s = vs[variant] || vs.primary;
  const pad = size === "sm" ? "4px 13px" : "8px 20px";
  const fs  = size === "sm" ? 12 : 13;
  const label = typeof children === "string" ? children : null;
  let icon = null, cleanLabel = children;
  if (label) {
    for (const [re, key] of BTN_ICON_MAP) {
      if (re.test(label)) {
        icon = BTN_ICONS[key];
        cleanLabel = label.replace(/^[+➕⏱]\s*/, "").trim();
        break;
      }
    }
  }
  return (
    <button onClick={onClick} disabled={disabled} title={title} {...rest}
      style={{
        background: s.bg, color: s.color, border: s.border,
        boxShadow: s.shadow, borderRadius: 8, padding: pad,
        fontSize: fs, fontWeight: 600, cursor: disabled ? "not-allowed" : "pointer",
        transition: "all .15s", letterSpacing: "0.01em", display: "inline-flex",
        alignItems: "center", gap: 6,
        whiteSpace: "nowrap", opacity: disabled ? .55 : 1, ...style,
      }}
      onMouseEnter={e => { if (!disabled) { e.currentTarget.style.filter = "brightness(1.07)"; e.currentTarget.style.transform = "translateY(-1px)"; } }}
      onMouseLeave={e => { e.currentTarget.style.filter = "brightness(1)"; e.currentTarget.style.transform = "translateY(0)"; }}>
      {icon}{cleanLabel}
    </button>
  );
};

const ConfirmModal = ({ msg, onOk, onClose }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.42)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(6px)", WebkitBackdropFilter: "blur(6px)" }}>
    <div style={{ background: "#fff", borderRadius: 16, padding: 28, width: "92%", maxWidth: 420, boxShadow: "0 20px 60px rgba(15,23,42,0.18)", border: `1px solid ${COLORS.border}` }}>
      <div style={{ fontSize: 15, color: COLORS.text, marginBottom: 24, lineHeight: 1.5 }}>{msg}</div>
      <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
        <Btn variant="ghost" onClick={onClose}>Cancelar</Btn>
        <Btn variant="danger" onClick={async () => { onClose(); await onOk(); }}>Confirmar</Btn>
      </div>
    </div>
  </div>
);

const Modal = ({ title, onClose, children }) => (
  <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.28)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(10px)", WebkitBackdropFilter: "blur(10px)" }}>
    <div style={{
      background: COLORS.surface, borderRadius: 20, padding: 28,
      width: "92%", maxWidth: 700, maxHeight: "92vh", overflowY: "auto", overflowX: "hidden",
      boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 24px 64px rgba(15,23,42,0.18)",
    }}>
      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 24, paddingBottom: 16, borderBottom: `1px solid ${COLORS.border}` }}>
        <span style={{ fontWeight: 700, fontSize: 16, color: COLORS.text }}>{title}</span>
        <button onClick={onClose} style={{
          background: COLORS.gray, border: `1px solid ${COLORS.border}`, borderRadius: 8,
          width: 30, height: 30, cursor: "pointer", color: COLORS.textMid,
          fontSize: 18, display: "flex", alignItems: "center", justifyContent: "center",
          transition: "background .15s",
        }}>×</button>
      </div>
      {children}
    </div>
  </div>
);

// Reusable table header style
const TH = ({ children, sortable, active, dir, onClick, style = {} }) => (
  <th onClick={onClick} style={{
    padding: "10px 14px", textAlign: "left", fontSize: 11,
    fontWeight: 700, color: COLORS.textMid, letterSpacing: "0.06em",
    textTransform: "uppercase", whiteSpace: "nowrap",
    cursor: sortable ? "pointer" : "default", userSelect: "none",
    background: active ? COLORS.primaryBg : "transparent", ...style,
  }}>
    {children}{sortable && <span style={{ marginLeft: 4, opacity: active ? 1 : 0.4 }}>{active ? (dir === "asc" ? " ↑" : " ↓") : " ↕"}</span>}
  </th>
);

const RhTable = ({ cols, children, style = {}, headBg }) => (
  <div style={{ overflowX: "auto", borderRadius: 14, border: `1px solid ${COLORS.border}`, ...style }}>
    <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
      <thead>
        <tr style={{ borderBottom: `1px solid ${COLORS.border}`, background: headBg || COLORS.gray }}>
          {cols.map((c, i) => typeof c === "string"
            ? <TH key={i}>{c}</TH>
            : <TH key={i} sortable={c.sortable} active={c.active} dir={c.dir} onClick={c.onClick}>{c.label}</TH>
          )}
        </tr>
      </thead>
      <tbody>{children}</tbody>
    </table>
  </div>
);

const RhTr = ({ children, style = {} }) => (
  <tr className="rh-tr" style={{ borderBottom: `1px solid ${COLORS.border}`, ...style }}>
    {children}
  </tr>
);

const TD = ({ children, muted, bold, style = {} }) => (
  <td style={{ padding: "11px 14px", color: muted ? COLORS.textMid : COLORS.text, fontWeight: bold ? 600 : 400, verticalAlign: "middle", ...style }}>
    {children}
  </td>
);

// ─── LOGIN ────────────────────────────────────────────────────────────────────
function LoginScreen({ onLogin }) {
  const [email, setEmail] = useState("");
  const [senha, setSenha] = useState("");
  const [erro, setErro] = useState("");
  const [loading, setLoading] = useState(false);
  const [isMob, setIsMob] = useState(window.innerWidth < 700);
  useEffect(() => {
    const fn = () => setIsMob(window.innerWidth < 700);
    window.addEventListener("resize", fn);
    return () => window.removeEventListener("resize", fn);
  }, []);

  const login = async () => {
    if (!email || !senha) return;
    setLoading(true);
    setErro("");
    try {
      const data = await apiFetch("POST", "/api/auth/login", { email, senha });
      onLogin(data.token, data.usuario);
    } catch {
      setErro("E-mail ou senha inválidos");
    } finally {
      setLoading(false);
    }
  };

  const STATS = [
    { value: "ERP", label: "Completo" },
    { value: "360°", label: "Gestão total" },
    { value: "100%", label: "Digital" },
  ];

  return (
    <div style={{ height: "100vh", overflow: "hidden", display: "flex", fontFamily: "'Inter', system-ui, sans-serif" }}>

      {/* ── Metade esquerda — brand + imagem ── */}
      {!isMob && (
        <div style={{
          width: "48%", flexShrink: 0, position: "relative", overflow: "hidden",
          background: "linear-gradient(145deg, #122600 0%, #1f5500 38%, #2e7a00 68%, #c9a800 88%, #fee140 100%)",
          display: "flex", flexDirection: "column",
        }}>
          {/* Círculos decorativos */}
          <div style={{ position: "absolute", width: 420, height: 420, borderRadius: "50%", background: "rgba(255,255,255,0.06)", top: -120, right: -120, pointerEvents: "none" }} />
          <div style={{ position: "absolute", width: 240, height: 240, borderRadius: "50%", background: "rgba(254,225,64,0.10)", bottom: 80, left: -60, pointerEvents: "none" }} />

          {/* Logo topo */}
          <div style={{ position: "relative", zIndex: 2, padding: "28px 36px 0", flexShrink: 0 }}>
            <img src="/olavio-pt.png" alt="Olavio" style={{ height: 80, objectFit: "contain" }} />
          </div>

          {/* Texto */}
          <div style={{ position: "relative", zIndex: 2, padding: "18px 36px 0", flexShrink: 0 }}>
            <h1 style={{ color: "#fff", fontSize: 34, fontWeight: 900, lineHeight: 1.18, margin: "0 0 12px", letterSpacing: "-0.02em" }}>
              O ERP completo<br />para conservadoras
            </h1>
            <p style={{ color: "rgba(255,255,255,0.82)", fontSize: 14, lineHeight: 1.65, margin: 0 }}>
              Funcionários, contratos, ponto, escalas e financeiro reunidos em uma única plataforma.
            </p>
          </div>

          {/* Imagem hero */}
          <div style={{ flex: 1, position: "relative", zIndex: 2, display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", padding: "8px 36px 0", minHeight: 0 }}>
            <img
              src="/hero-login.webp"
              alt=""
              style={{
                width: "86%",
                maxHeight: "100%",
                borderRadius: 14,
                objectFit: "contain",
                objectPosition: "center center",
                display: "block",
              }}
              onError={e => { e.currentTarget.style.display = "none"; }}
            />
          </div>

          {/* Stats */}
          <div style={{ position: "relative", zIndex: 3, flexShrink: 0, display: "flex", gap: 10, padding: "12px 36px 26px" }}>
            {STATS.map((s, i) => (
              <div key={i} style={{
                background: "rgba(0,0,0,0.28)", backdropFilter: "blur(12px)",
                borderRadius: 12, padding: "12px 16px", flex: 1,
                border: "1px solid rgba(255,255,255,0.18)",
              }}>
                <div style={{ color: "#fff", fontWeight: 900, fontSize: 18, lineHeight: 1 }}>{s.value}</div>
                <div style={{ color: "#fff", fontSize: 12, marginTop: 5, opacity: 0.82 }}>{s.label}</div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* ── Metade direita — formulário ── */}
      <div style={{
        flex: 1, display: "flex", alignItems: "center", justifyContent: "center",
        padding: isMob ? "40px 24px" : "48px 56px",
        background: "#ffffff", overflow: "auto",
      }}>
        <div style={{ width: "100%", maxWidth: 400 }}>
          <img src="/olavio-bc.png" alt="Olavio" style={{ height: isMob ? 72 : 96, objectFit: "contain", display: "block", margin: "0 auto 28px" }} />

          <p style={{ fontSize: 11, fontWeight: 700, color: "#2e7a00", textTransform: "uppercase", letterSpacing: "0.12em", margin: "0 0 7px" }}>
            Acesso seguro
          </p>
          <h2 style={{ fontSize: 24, fontWeight: 800, color: "#182e0e", margin: "0 0 5px", letterSpacing: "-0.03em" }}>
            Entrar na plataforma
          </h2>
          <p style={{ fontSize: 13, color: "#64748B", margin: "0 0 28px" }}>
            Use suas credenciais para continuar
          </p>

          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#426028", display: "block", marginBottom: 6 }}>E-mail</label>
              <input
                type="email" value={email}
                onChange={e => setEmail(e.target.value)}
                onKeyDown={e => e.key === "Enter" && login()}
                placeholder="seuemail@empresa.com.br"
                style={{
                  width: "100%", boxSizing: "border-box",
                  border: "1.5px solid #E4EAF0", borderRadius: 10,
                  padding: "11px 14px", fontSize: 14,
                  background: "#F7F9FB", color: "#182e0e",
                  outline: "none", transition: "border .15s, box-shadow .2s",
                }}
              />
            </div>
            <div>
              <label style={{ fontSize: 12, fontWeight: 600, color: "#426028", display: "block", marginBottom: 6 }}>Senha</label>
              <input
                type="password" value={senha}
                onChange={e => setSenha(e.target.value)}
                onKeyDown={e => e.key === "Enter" && login()}
                placeholder="••••••••"
                style={{
                  width: "100%", boxSizing: "border-box",
                  border: "1.5px solid #E4EAF0", borderRadius: 10,
                  padding: "11px 14px", fontSize: 14,
                  background: "#F7F9FB", color: "#182e0e",
                  outline: "none", transition: "border .15s, box-shadow .2s",
                }}
              />
            </div>

            {erro && (
              <div style={{ color: "#EF4444", fontSize: 13, background: "#FEF2F2", border: "1px solid #FECACA", borderRadius: 8, padding: "9px 13px" }}>
                {erro}
              </div>
            )}

            <button
              onClick={login} disabled={loading}
              style={{
                width: "100%", padding: "14px 18px", marginTop: 4,
                background: loading ? "#5a7a50" : "linear-gradient(135deg, #1f5500 0%, #2e7a00 100%)",
                color: "#fff", border: "none", borderRadius: 14,
                fontSize: 15, fontWeight: 700, cursor: loading ? "not-allowed" : "pointer",
                boxShadow: "0 4px 20px rgba(46,122,0,0.35)",
                transition: "opacity .2s, transform .15s",
                letterSpacing: "0.01em",
              }}
              onMouseEnter={e => { if (!loading) { e.currentTarget.style.opacity = ".88"; e.currentTarget.style.transform = "translateY(-1px)"; }}}
              onMouseLeave={e => { e.currentTarget.style.opacity = "1"; e.currentTarget.style.transform = "translateY(0)"; }}
            >
              {loading ? "Entrando..." : "Entrar"}
            </button>
          </div>

          <p style={{ marginTop: 28, fontSize: 11, color: "#B0BEC5", textAlign: "center" }}>
            Olavio · Gestão Completa
          </p>
        </div>
      </div>
    </div>
  );
}

// ─── DASHBOARD ───────────────────────────────────────────────────────────────
const NIVEL_COR  = { "Urgente": COLORS.danger, "Pendente": COLORS.warning, "Normal": COLORS.info };
const NIVEL_DOT  = { "Urgente": COLORS.danger, "Pendente": COLORS.warning, "Normal": COLORS.info };

function DotBadge({ color, label }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: color + "10", borderRadius: 5, padding: "2px 8px" }}>
      <span style={{ width: 6, height: 6, borderRadius: "50%", background: color, flexShrink: 0, display: "inline-block" }} />
      <span style={{ fontSize: 11, fontWeight: 600, color, letterSpacing: "0.03em" }}>{label}</span>
    </span>
  );
}

function TabelaDash({ titulo, cor, badge, colunas, linhas, vazio, acaoHeader, headBg }) {
  return (
    <div>
      <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 10 }}>
        <span style={{ width: 8, height: 8, borderRadius: "50%", background: cor, flexShrink: 0, display: "inline-block" }} />
        <span style={{ fontWeight: 700, fontSize: 14, color: COLORS.text }}>{titulo}</span>
        {badge > 0 && (
          <span style={{ background: cor + "15", color: cor, borderRadius: 5, fontSize: 11, padding: "1px 7px", fontWeight: 700 }}>{badge}</span>
        )}
        {acaoHeader && <span style={{ marginLeft: "auto" }}>{acaoHeader}</span>}
      </div>
      <RhTable cols={colunas} headBg={headBg}>
        {linhas.length === 0
          ? <RhTr><td colSpan={colunas.length} style={{ padding: "20px 14px", color: COLORS.textLight, fontSize: 13 }}>{vazio}</td></RhTr>
          : linhas.map((row, i) => (
            <RhTr key={i}>
              {row.map((cel, j) => <TD key={j}>{cel}</TD>)}
            </RhTr>
          ))
        }
      </RhTable>
    </div>
  );
}

function Dashboard({ employees, afastamentos, escala, ponto, observacoes, setObservacoes, token, usuario, setAba }) {
  const canEdit = ["admin","administrador","rh"].includes(usuario?.perfil);
  const naodemitidos = employees.filter(e => e.status !== "Demitido");
  const ativos   = employees.filter(e => e.status === "Ativo").length;
  const ferias   = employees.filter(e => e.status === "Férias").length;
  const afastados = employees.filter(e => e.status === "Afastado").length;
  const totalFolha = naodemitidos.reduce((s, e) => {
    const pontoArr = Array.isArray(ponto) ? ponto : [];
    const extra = pontoArr.filter(p => p.funcionario_id === e.id).reduce((a, p) => a + (p.horas_extras || 0), 0);
    return s + e.salario + (e.salario / 220) * 1.5 * extra;
  }, 0);

  const hoje = new Date().toISOString().split("T")[0];

  // ── dados ──
  const docVencendo = employees.filter(e => {
    if (!e.docs?.aso) return false;
    const diff = (new Date(e.docs.aso) - new Date()) / 86400000;
    return diff > 0 && diff < 30;
  });
  const emFerias       = employees.filter(e => e.status === "Férias");
  const emAfastamento  = employees.filter(e => e.status === "Afastado");
  const feriaDisponivel = employees.filter(e => e.status !== "Demitido" && e.ferias?.disponivel >= 30);
  const feriasVencendo = employees
    .filter(e => e.status !== "Demitido")
    .map(e => ({ e, venc: calcVencimentoFerias(e, afastamentos) }))
    .filter(({ venc }) => venc?.tipo === 'data' && venc.dias <= 90 && venc.dias > 0)
    .sort((a, b) => a.venc.dias - b.venc.dias);
  const obsNormal      = observacoes.filter(o => o.nivel === "Normal");
  const escalaPendente = escala.filter(s => s.status === "Pendente");
  const obsPendUrgente = observacoes.filter(o => o.nivel === "Urgente" || o.nivel === "Pendente");

  // form nova observação
  const [showObs, setShowObs] = useState(false);
  const [formObs, setFormObs] = useState({ data: hoje, descricao: "", nivel: "Normal" });
  const salvarObs = async () => {
    if (!formObs.descricao.trim()) return;
    try {
      const nova = await apiFetch("POST", "/api/observacoes", { descricao: formObs.descricao, data: formObs.data, nivel: formObs.nivel.toLowerCase() }, token);
      setObservacoes(prev => [...prev, adaptObservacao(nova)]);
      setFormObs({ data: hoje, descricao: "", nivel: "Normal" });
      setShowObs(false);
    } catch(e) { alert("Erro ao salvar: " + e.message); }
  };
  const excluirObs = async (id) => {
    try {
      await apiFetch("DELETE", `/api/observacoes/${id}`, undefined, token);
      setObservacoes(prev => prev.filter(o => o.id !== id));
    } catch(e) { alert("Erro ao excluir: " + e.message); }
  };

  // ── alertas de ponto (dashboard-alertas) ──
  const [alertasPonto, setAlertasPonto] = useState({ semPontoHoje: [], ocorrenciasPendentes: 0 });
  useEffect(() => {
    apiFetch("GET", "/api/registro-ponto/dashboard-alertas", undefined, token)
      .then(r => setAlertasPonto(r || { semPontoHoje: [], ocorrenciasPendentes: 0 })).catch(() => {});
  }, [token]);

  // Férias ativas hoje (com dias restantes)
  const feriasHoje = afastamentos.filter(fa => {
    if (fa.tipo !== 'ferias') return false;
    const ini = new Date(fa.data_inicio), fim = fa.data_fim ? new Date(fa.data_fim) : null;
    const now = new Date(); now.setHours(0,0,0,0);
    return ini <= now && (!fim || fim >= now);
  }).map(fa => ({
    ...fa,
    diasRestantes: fa.data_fim ? Math.max(0, Math.ceil((new Date(fa.data_fim) - new Date()) / 86400000)) : null
  }));

  // Afastamentos ativos hoje
  const afastamentosHoje = afastamentos.filter(fa => {
    if (fa.tipo === 'ferias') return false;
    const ini = new Date(fa.data_inicio), fim = fa.data_fim ? new Date(fa.data_fim) : null;
    const now = new Date(); now.setHours(0,0,0,0);
    return ini <= now && (!fim || fim >= now);
  });

  // ── ocultar alertas ──
  const [alertasOcultos, setAlertasOcultos] = useState(new Set());
  const ocultarAlerta = (id) => setAlertasOcultos(prev => new Set([...prev, id]));
  const mostrarTodos  = () => setAlertasOcultos(new Set());

  // helper: dot colorido para observações
  const NivelDot = ({ nivel }) => (
    <span style={{ width: 8, height: 8, borderRadius: "50%", background: NIVEL_DOT[nivel], display: "inline-block", flexShrink: 0 }} />
  );

  // helper: botão discreto
  const BtnDiscret = ({ onClick, children, title }) => (
    <button onClick={onClick} title={title} style={{
      background: "none", border: `1px solid ${COLORS.border}`, borderRadius: 5,
      cursor: "pointer", color: COLORS.textMid, fontSize: 11,
      padding: "2px 7px", fontWeight: 500, lineHeight: 1.4,
      transition: "border-color .15s, color .15s",
    }}
      onMouseEnter={e => { e.currentTarget.style.borderColor = COLORS.textMid; e.currentTarget.style.color = COLORS.text; }}
      onMouseLeave={e => { e.currentTarget.style.borderColor = COLORS.border; e.currentTarget.style.color = COLORS.textMid; }}
    >{children}</button>
  );

  // ── build alertas como objetos com ID ──
  const alertasObj = [
    ...feriaDisponivel.map(e => ({
      id: `ferias-${e.id}`, deletable: false,
      cells: ["🏖️", <Badge color={COLORS.info}>Férias disponíveis</Badge>, <strong>{e.nome}</strong>, `${e.ferias.disponivel} dias acumulados`, "—"],
    })),
    ...emFerias.map(e => {
      const af = [...afastamentos].filter(a => a.funcionarioId === e.id && a.tipo === "Férias").pop();
      return {
        id: `emferias-${e.id}`, deletable: false,
        cells: ["🌴", <Badge color={COLORS.secondary}>Em férias</Badge>, <strong>{e.nome}</strong>,
          af ? `Início: ${new Date(af.inicio + "T00:00:00").toLocaleDateString("pt-BR")}` : "—",
          af ? `Retorno: ${new Date(af.fim + "T00:00:00").toLocaleDateString("pt-BR")}` : "—"],
      };
    }),
    ...emAfastamento.map(e => {
      const af = [...afastamentos].filter(a => a.funcionarioId === e.id && a.status === "Em andamento").pop();
      return {
        id: `afastado-${e.id}`, deletable: false,
        cells: ["🏥", <Badge color={COLORS.warning}>Afastado</Badge>, <strong>{e.nome}</strong>,
          af ? `${af.tipo}${af.motivo ? " — " + af.motivo : ""}` : "—",
          af ? `Até: ${new Date(af.fim + "T00:00:00").toLocaleDateString("pt-BR")}` : "—"],
      };
    }),
    ...docVencendo.map(e => ({
      id: `aso-${e.id}`, deletable: false,
      cells: ["📋", <Badge color={COLORS.warning}>ASO vencendo</Badge>, <strong>{e.nome}</strong>,
        `Vence em ${new Date(e.docs.aso + "T00:00:00").toLocaleDateString("pt-BR")}`, "—"],
    })),
    ...feriasVencendo.map(({ e, venc }) => ({
      id: `feriasvenc-${e.id}`, deletable: false,
      cells: ["⏰", <Badge color={COLORS.danger}>Férias vencendo</Badge>, <strong>{e.nome}</strong>,
        `Vence em ${venc.vencimento.toLocaleDateString("pt-BR")}`,
        `Faltam ${venc.dias} dia${venc.dias !== 1 ? "s" : ""}`],
    })),
    ...obsNormal.map(o => ({
      id: `obs-${o.id}`, deletable: true, onDelete: () => excluirObs(o.id),
      cells: [<NivelDot nivel={o.nivel} />, <Badge color={NIVEL_COR[o.nivel]}>{o.nivel}</Badge>,
        <strong>{o.descricao}</strong>,
        new Date(o.data + "T00:00:00").toLocaleDateString("pt-BR"), "—"],
    })),
  ];

  const alertasVisiveis = alertasObj.filter(a => !alertasOcultos.has(a.id));
  const linhasAlertas = alertasVisiveis.map(a => [
    ...a.cells,
    <div style={{ display: "flex", gap: 5, justifyContent: "flex-end" }}>
      {a.deletable && (
        <button onClick={a.onDelete} title="Apagar" style={{
          background: "none", border: "none", cursor: "pointer",
          color: COLORS.textLight, fontSize: 14, padding: "2px 4px", borderRadius: 4,
          transition: "color .15s",
        }}
          onMouseEnter={e => e.currentTarget.style.color = COLORS.textMid}
          onMouseLeave={e => e.currentTarget.style.color = COLORS.textLight}
        >✕</button>
      )}
      <BtnDiscret onClick={() => ocultarAlerta(a.id)} title="Ocultar este alerta">Ocultar</BtnDiscret>
    </div>,
  ]);

  // ── linhas da tabela PENDÊNCIAS ──
  const linhasPendencias = [
    ...escalaPendente.map(s => {
      const func = employees.find(f => f.id === s.funcionarioId);
      return [
        "📅", <Badge color={COLORS.danger}>Escala</Badge>,
        <strong>{func?.nome || "—"}</strong>,
        `${s.local} — ${new Date((s.dataInicio || s.data) + "T00:00:00").toLocaleDateString("pt-BR")}`,
        <Badge color={COLORS.danger}>Pendente</Badge>,
        "—",
      ];
    }),
    ...obsPendUrgente.map(o => [
      <NivelDot nivel={o.nivel} />, <Badge color={NIVEL_COR[o.nivel]}>{o.nivel}</Badge>,
      <strong>{o.descricao}</strong>,
      new Date(o.data + "T00:00:00").toLocaleDateString("pt-BR"),
      <Badge color={NIVEL_COR[o.nivel]}>{o.nivel}</Badge>,
      canEdit ? <Btn size="sm" variant="danger" onClick={() => excluirObs(o.id)}>Excluir</Btn> : null,
    ]),
  ];

  const kpis = [
    { label: "Total",    sub: "Funcionários", value: naodemitidos.length, color: COLORS.primary, icon: "M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2 M23 21v-2a4 4 0 0 0-3-3.87 M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z M16 3.13a4 4 0 0 1 0 7.75" },
    { label: "Ativos",   sub: "no momento",   value: ativos,              color: "#10b981",       icon: "M22 11.08V12a10 10 0 1 1-5.93-9.14 M22 4 12 14.01l-3-3" },
    { label: "Férias",   sub: "em andamento", value: ferias,              color: COLORS.info,     icon: "M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z" },
    { label: "Afastados",sub: "com atestado", value: afastados,           color: COLORS.warning,  icon: "M22 12h-4l-3 9L9 3l-3 9H2" },
    { label: "Folha",    sub: "do mês",       value: `R$ ${totalFolha.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`, color: COLORS.accent, icon: "M12 2v20 M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6" },
  ];

  const acaoHeaderAlertas = alertasOcultos.size > 0 ? (
    <BtnDiscret onClick={mostrarTodos}>Mostrar todos ({alertasOcultos.size} ocultos)</BtnDiscret>
  ) : null;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 24 }}>


      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(5,1fr)", gap: 14 }}>
        {kpis.map((k, i) => (
          <div key={i} className="rh-card-kpi" style={{
            background: COLORS.surface, borderRadius: 16,
            border: `1px solid ${COLORS.border}`,
            boxShadow: "0 1px 3px rgba(30,41,59,0.05), 0 4px 16px rgba(30,41,59,0.07)",
            padding: "18px 20px", display: "flex", flexDirection: "column", gap: 12,
            transition: "box-shadow .25s, transform .25s", cursor: "default",
          }}>
            <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start" }}>
              <div>
                <div style={{ fontSize: 11, fontWeight: 500, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.05em" }}>{k.label}</div>
                <div style={{ fontSize: 11, color: COLORS.textLight }}>{k.sub}</div>
              </div>
              <div style={{ width: 34, height: 34, borderRadius: 9, background: k.color + "12", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke={k.color} strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                  {k.icon.split(" M").map((p, pi) => <path key={pi} d={(pi === 0 ? "" : "M") + p} />)}
                </svg>
              </div>
            </div>
            <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.text, letterSpacing: "-0.02em" }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* PENDÊNCIAS */}
      <Card style={{ padding: "20px 20px 16px" }}>
        <TabelaDash
          titulo="Pendências"
          cor={COLORS.danger}
          badge={linhasPendencias.length}
          colunas={["", "Tipo", "Descrição", "Detalhe", "Status", "Ação"]}
          linhas={linhasPendencias}
          vazio="Nenhuma pendência no momento"
        />
      </Card>

      {/* ALERTAS */}
      <Card style={{ padding: "20px 20px 16px" }}>
        <TabelaDash
          titulo="Alertas"
          cor={COLORS.warning}
          badge={alertasVisiveis.length}
          colunas={["", "Categoria", "Funcionário / Descrição", "Info", "Detalhe", ""]}
          linhas={linhasAlertas}
          vazio="Nenhum alerta no momento"
          acaoHeader={acaoHeaderAlertas}
        />
      </Card>

      {/* PONTO — ALERTAS EM TEMPO REAL */}
      {canEdit && (alertasPonto.semPontoHoje.length > 0 || alertasPonto.ocorrenciasPendentes > 0 || feriasHoje.length > 0 || afastamentosHoje.length > 0) && (
        <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit,minmax(280px,1fr))", gap: 14 }}>

          {/* Sem ponto hoje */}
          {alertasPonto.semPontoHoje.length > 0 && (
            <Card style={{ borderLeft: `4px solid ${COLORS.danger}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>⏰</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: COLORS.danger }}>Sem ponto hoje</span>
                <Badge color={COLORS.danger}>{alertasPonto.semPontoHoje.length}</Badge>
              </div>
              {alertasPonto.semPontoHoje.map(f => (
                <div key={f.id} style={{ padding: "5px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13, color: COLORS.text }}>{f.nome}</div>
              ))}
            </Card>
          )}

          {/* Ocorrências pendentes de aprovação */}
          {alertasPonto.ocorrenciasPendentes > 0 && (
            <Card style={{ borderLeft: `4px solid ${COLORS.warning}`, cursor: "pointer" }} onClick={() => setAba && setAba("ponto")}>
              <div style={{ display: "flex", alignItems: "center", gap: 8 }}>
                <span style={{ fontSize: 18 }}>⚠️</span>
                <div>
                  <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.warning }}>Ocorrências pendentes</div>
                  <div style={{ fontSize: 12, color: COLORS.textMid, marginTop: 2 }}>
                    {alertasPonto.ocorrenciasPendentes} atraso(s)/falta(s) aguardando sua aprovação
                  </div>
                </div>
                <span style={{ marginLeft: "auto", background: COLORS.warning, color: "#fff", borderRadius: 12, padding: "2px 10px", fontWeight: 700, fontSize: 13 }}>{alertasPonto.ocorrenciasPendentes}</span>
              </div>
            </Card>
          )}

          {/* Em férias */}
          {feriasHoje.length > 0 && (
            <Card style={{ borderLeft: `4px solid ${COLORS.info}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>🏖️</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: COLORS.info }}>Em férias agora</span>
                <Badge color={COLORS.info}>{feriasHoje.length}</Badge>
              </div>
              {feriasHoje.map((fa, i) => (
                <div key={i} style={{ padding: "5px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{fa.funcionario_nome}</span>
                  {fa.diasRestantes !== null && (
                    <span style={{ marginLeft: 8, fontSize: 11, color: COLORS.textMid }}>
                      retorna em {fa.diasRestantes} dia(s) — {fa.data_fim ? new Date(fa.data_fim + "T00:00").toLocaleDateString("pt-BR") : "sem data"}
                    </span>
                  )}
                </div>
              ))}
            </Card>
          )}

          {/* Em afastamento */}
          {afastamentosHoje.length > 0 && (
            <Card style={{ borderLeft: `4px solid ${COLORS.warning}` }}>
              <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 12 }}>
                <span style={{ fontSize: 18 }}>🏥</span>
                <span style={{ fontWeight: 700, fontSize: 13, color: COLORS.warning }}>Em afastamento</span>
                <Badge color={COLORS.warning}>{afastamentosHoje.length}</Badge>
              </div>
              {afastamentosHoje.map((fa, i) => (
                <div key={i} style={{ padding: "5px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <span style={{ fontWeight: 600 }}>{fa.funcionario_nome}</span>
                  {fa.motivo && <span style={{ marginLeft: 8, fontSize: 11, color: COLORS.textMid }}>— {fa.motivo}</span>}
                </div>
              ))}
            </Card>
          )}
        </div>
      )}

      {/* NOVA OBSERVAÇÃO */}
      {canEdit && (
      <Card>
        <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: showObs ? 16 : 0 }}>
          <div style={{ fontWeight: 600, fontSize: 14, color: COLORS.text }}>Nova Observação / Tarefa</div>
          <Btn size="sm" variant={showObs ? "ghost" : "primary"} onClick={() => setShowObs(v => !v)}>{showObs ? "Cancelar" : "+ Adicionar"}</Btn>
        </div>
        {showObs && (
          <div style={{ display: "grid", gridTemplateColumns: "160px 1fr 150px auto", gap: 10, alignItems: "flex-end" }}>
            <Input label="Data" type="date" value={formObs.data} onChange={e => setFormObs(f => ({ ...f, data: e.target.value }))} />
            <Input label="Descrição / Tarefa" value={formObs.descricao} onChange={e => setFormObs(f => ({ ...f, descricao: e.target.value }))} />
            <Select label="Nível" value={formObs.nivel} onChange={e => setFormObs(f => ({ ...f, nivel: e.target.value }))}>
              <option>Normal</option><option>Pendente</option><option>Urgente</option>
            </Select>
            <Btn onClick={salvarObs}>Salvar</Btn>
          </div>
        )}
      </Card>
      )}

      {/* ESCALA HOJE + DISTRIBUIÇÃO */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <SectionTitle>Escala Hoje</SectionTitle>
          {escala.filter(s => s.data === hoje).length === 0
            ? <p style={{ color: COLORS.textLight, fontSize: 13, margin: 0 }}>Nenhuma escala para hoje.</p>
            : escala.filter(s => s.data === hoje).map((s, i) => {
              const func = employees.find(f => f.id === s.funcionarioId);
              return (
                <div key={i} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", padding: "9px 0", borderBottom: `1px solid ${COLORS.border}`, fontSize: 13 }}>
                  <span style={{ fontWeight: 600, color: COLORS.text }}>{func?.nome}</span>
                  <span style={{ color: COLORS.textMid, fontSize: 12 }}>{s.local}</span>
                  <Badge color={s.status === "Confirmado" ? COLORS.primary : s.status === "Substituição" ? COLORS.warning : COLORS.danger}>{s.status}</Badge>
                </div>
              );
            })
          }
        </Card>

        <Card>
          <SectionTitle>Distribuição por Setor</SectionTitle>
          {[...new Set(naodemitidos.map(e => e.setor).filter(Boolean))].map(setor => {
            const count = naodemitidos.filter(e => e.setor === setor).length;
            const pct = naodemitidos.length ? Math.round(count / naodemitidos.length * 100) : 0;
            return (
              <div key={setor} style={{ marginBottom: 16 }}>
                <div style={{ display: "flex", justifyContent: "space-between", marginBottom: 6, fontSize: 13 }}>
                  <span style={{ fontWeight: 500, color: COLORS.text }}>{setor}</span>
                  <span style={{ color: COLORS.textMid, fontSize: 12 }}>{count} · {pct}%</span>
                </div>
                <div style={{ background: COLORS.border, borderRadius: 99, height: 6 }}>
                  <div style={{ background: COLORS.primary, width: `${pct}%`, height: 6, borderRadius: 99, transition: "width .5s" }} />
                </div>
              </div>
            );
          })}
        </Card>
      </div>

    </div>
  );
}

// ─── BANCOS BRASILEIROS ────────────────────────────────────────────────────────
const BANCOS_BR = [
  { nome:"Banco do Brasil",                   codigo:"001", ispb:"00000000" },
  { nome:"Bradesco",                           codigo:"237", ispb:"60746948" },
  { nome:"Itaú Unibanco",                      codigo:"341", ispb:"60701190" },
  { nome:"Santander",                          codigo:"033", ispb:"90400888" },
  { nome:"Caixa Econômica Federal",            codigo:"104", ispb:"00360305" },
  { nome:"Nubank",                             codigo:"260", ispb:"18236120" },
  { nome:"Banco Inter",                        codigo:"077", ispb:"00416968" },
  { nome:"C6 Bank",                            codigo:"336", ispb:"31872495" },
  { nome:"BTG Pactual",                        codigo:"208", ispb:"30306294" },
  { nome:"Sicredi",                            codigo:"748", ispb:"01181521" },
  { nome:"Sicoob / Bancoob",                   codigo:"756", ispb:"02038232" },
  { nome:"Banrisul",                           codigo:"041", ispb:"92702067" },
  { nome:"BNB - Banco do Nordeste",            codigo:"004", ispb:"07237373" },
  { nome:"PagBank (PagSeguro)",                codigo:"290", ispb:"08561701" },
  { nome:"Mercado Pago",                       codigo:"323", ispb:"10573521" },
  { nome:"PicPay",                             codigo:"380", ispb:"22896431" },
  { nome:"Stone",                              codigo:"197", ispb:"16501555" },
  { nome:"Neon",                               codigo:"735", ispb:"20855875" },
  { nome:"Banco Original",                     codigo:"212", ispb:"92894922" },
  { nome:"Banco Safra",                        codigo:"422", ispb:"58160789" },
  { nome:"Banco BMG",                          codigo:"318", ispb:"61186680" },
  { nome:"Banco Pan",                          codigo:"623", ispb:"59285411" },
  { nome:"Agibank",                            codigo:"121", ispb:"10664513" },
  { nome:"BRB - Banco de Brasília",            codigo:"070", ispb:"00000208" },
  { nome:"Daycoval",                           codigo:"707", ispb:"62232889" },
  { nome:"Cresol",                             codigo:"133", ispb:"10398952" },
  { nome:"Unicred",                            codigo:"084", ispb:"01634601" },
  { nome:"Uniprime Norte do Paraná",           codigo:"099", ispb:"04257795" },
  { nome:"Asaas",                              codigo:"461", ispb:"19540550" },
  { nome:"Banco Modal / XP",                   codigo:"746", ispb:"30723886" },
  { nome:"Banestes",                           codigo:"021", ispb:"28127603" },
  { nome:"BNDES",                              codigo:"007", ispb:"33657248" },
  { nome:"Banco da Amazônia (BASA)",           codigo:"003", ispb:"04902979" },
  { nome:"BV (Banco Votorantim)",              codigo:"655", ispb:"59588111" },
  { nome:"Banco Rendimento",                   codigo:"633", ispb:"68900810" },
  { nome:"ABC Brasil",                         codigo:"246", ispb:"28195667" },
  { nome:"Digio",                              codigo:"335", ispb:"27098060" },
  { nome:"Ailos",                              codigo:"085", ispb:"05463212" },
  { nome:"Banco Topázio",                      codigo:"082", ispb:"07679404" },
  { nome:"CrediSIS",                           codigo:"097", ispb:"04632856" },
  { nome:"Uniprime Central",                   codigo:"099", ispb:"04257795" },
  { nome:"Banco Sofisa",                       codigo:"637", ispb:"60889128" },
  { nome:"Banco Fibra",                        codigo:"224", ispb:"58616418" },
  { nome:"Banco Pine",                         codigo:"643", ispb:"62144175" },
  { nome:"Banco Paulista",                     codigo:"611", ispb:"61820817" },
  { nome:"Banco Itaú BBA",                     codigo:"184", ispb:"17298092" },
  { nome:"Banco XP",                           codigo:"348", ispb:"33264668" },
  { nome:"Avenue Securities",                  codigo:"378", ispb:"36586946" },
  { nome:"Banco Ourinvest",                    codigo:"712", ispb:"78632767" },
  { nome:"Banco Genial",                       codigo:"125", ispb:"45246410" },
  { nome:"Banco BS2",                          codigo:"218", ispb:"71027866" },
  { nome:"Banco Semear",                       codigo:"743", ispb:"00795423" },
  { nome:"Banco Arbi",                         codigo:"213", ispb:"54403563" },
  { nome:"Banco Cora",                         codigo:"403", ispb:"37880206" },
  { nome:"Banco Banese",                       codigo:"047", ispb:"13009717" },
  { nome:"Banco do Estado do Pará (Banpará)",  codigo:"037", ispb:"04913129" },
  { nome:"Bancoob",                            codigo:"756", ispb:"02038232" },
  { nome:"Banco Cooperativo Sicredi",          codigo:"748", ispb:"01181521" },
  { nome:"Banco ABN AMRO",                     codigo:"075", ispb:"03532415" },
  { nome:"Banco Mizuho",                       codigo:"370", ispb:"61088183" },
  { nome:"Banco MUFG Brasil",                  codigo:"456", ispb:"60498557" },
  { nome:"Banco JP Morgan",                    codigo:"376", ispb:"33172537" },
  { nome:"Banco Citibank",                     codigo:"477", ispb:"33042953" },
  { nome:"Banco Credit Suisse",                codigo:"505", ispb:"32062580" },
  { nome:"Deutsche Bank",                      codigo:"487", ispb:"62331228" },
  { nome:"Banco BNP Paribas",                  codigo:"752", ispb:"01522368" },
  { nome:"Goldman Sachs do Brasil",            codigo:"064", ispb:"04332281" },
  { nome:"Banco Rabobank",                     codigo:"747", ispb:"01023570" },
  { nome:"Banco Ribeirão Preto",               codigo:"741", ispb:"00517645" },
  { nome:"Banco Mercantil do Brasil",          codigo:"389", ispb:"17184037" },
  { nome:"Banco Cetelem",                      codigo:"739", ispb:"00558456" },
  { nome:"Banco Triangulo",                    codigo:"634", ispb:"17351180" },
  { nome:"Banco Losango",                      codigo:"212", ispb:"92894922" },
  { nome:"Banco Intermedium",                  codigo:"077", ispb:"00416968" },
  { nome:"Banco Western Union",                codigo:"119", ispb:"13720915" },
  { nome:"Banco Woori",                        codigo:"124", ispb:"15357060" },
  { nome:"Banco Industrial e Comercial",       codigo:"604", ispb:"61023328" },
  { nome:"Paraná Banco",                       codigo:"254", ispb:"14388334" },
  { nome:"Banco Letsbank",                     codigo:"630", ispb:"58497702" },
  { nome:"Banco Bari",                         codigo:"330", ispb:"00556603" },
  { nome:"Banco Omni",                         codigo:"613", ispb:"60850229" },
  { nome:"Banco Finaxis",                      codigo:"094", ispb:"11758741" },
  { nome:"Banco RCI Brasil",                   codigo:"098", ispb:"07693858" },
  { nome:"Banco Máxima",                       codigo:"243", ispb:"33923798" },
  { nome:"Banco Bradesco BBI",                 codigo:"225", ispb:"06271464" },
  { nome:"Banco Porto Seguro",                 codigo:"263", ispb:"61348538" },
  { nome:"Itaú Consignado",                    codigo:"029", ispb:"33885724" },
  { nome:"Banco Volkswagen",                   codigo:"652", ispb:"59109165" },
  { nome:"Banco Toyota",                       codigo:"457", ispb:"65913436" },
  { nome:"Banco John Deere",                   codigo:"217", ispb:"91884981" },
  { nome:"Banco Caterpillar",                  codigo:"630", ispb:"58497702" },
  { nome:"Banco CNH Industrial Capital",       codigo:"122", ispb:"44189447" },
  { nome:"Banco Hyundai Capital",              codigo:"119", ispb:"13720915" },
  { nome:"Acesso Soluções de Pagamento",       codigo:"332", ispb:"13140088" },
  { nome:"Banco Inbursa",                      codigo:"012", ispb:"04866275" },
  { nome:"Banco Andbank",                      codigo:"065", ispb:"48795256" },
  { nome:"Banco Investcred Unibanco",          codigo:"249", ispb:"61182980" },
  { nome:"Banco Modal",                        codigo:"746", ispb:"30723886" },
  { nome:"Planner Corretora de Valores",       codigo:"100", ispb:"00806535" },
  { nome:"Banco Bocom BBM",                    codigo:"107", ispb:"15114366" },
  { nome:"Banco Luso Brasileiro",              codigo:"600", ispb:"59118133" },
  { nome:"Banco Guanabara",                    codigo:"612", ispb:"31880826" },
  { nome:"Banco Pottencial",                   codigo:"735", ispb:"20855875" },
  { nome:"Banco Zema",                         codigo:"359", ispb:"05351887" },
  { nome:"Realize CFI",                        codigo:"374", ispb:"27351731" },
  { nome:"Banco Digimais",                     codigo:"654", ispb:"92874270" },
  { nome:"Banco Agiplan",                      codigo:"121", ispb:"10664513" },
  { nome:"Banco Societe Generale",             codigo:"366", ispb:"61533584" },
  { nome:"Banco Indusval",                     codigo:"653", ispb:"61024352" },
  { nome:"Banco Haitong",                      codigo:"111", ispb:"14388334" },
  { nome:"Banco Original do Agronegócio",      codigo:"079", ispb:"07253654" },
  { nome:"Banco Bonsucesso",                   codigo:"218", ispb:"71027866" },
  { nome:"Banco Rodobens",                     codigo:"120", ispb:"00806535" },
  { nome:"Banco Alfa",                         codigo:"025", ispb:"03323840" },
  { nome:"Banco Fator",                        codigo:"265", ispb:"33644196" },
  { nome:"Banco HSBC (agora Bradesco)",        codigo:"269", ispb:"53518684" },
  { nome:"Uniprime Pioneira",                  codigo:"191", ispb:"15581638" },
  { nome:"Banco Pefisa",                       codigo:"174", ispb:"43180355" },
  { nome:"Banco Moneo",                        codigo:"613", ispb:"60850229" },
  { nome:"PJBank",                             codigo:"301", ispb:"00714671" },
  { nome:"Banco Bradescard",                   codigo:"063", ispb:"04184779" },
  { nome:"Banco iFood",                        codigo:"456", ispb:"60498557" },
  { nome:"Caixa Aqui / Correspondente CEF",    codigo:"104", ispb:"00360305" },
  { nome:"Banco Will Financeira",              codigo:"280", ispb:"23862762" },
  { nome:"Banco Confidence de Câmbio",         codigo:"060", ispb:"04904166" },
  { nome:"Frente Corretora",                   codigo:"285", ispb:"71677850" },
  { nome:"Banco Máxima S.A.",                  codigo:"243", ispb:"33923798" },
  { nome:"Banco BRL Trust",                    codigo:"173", ispb:"13486793" },
  { nome:"Banco Fibra",                        codigo:"224", ispb:"58616418" },
  { nome:"Banco Luso Brasileiro",              codigo:"600", ispb:"59118133" },
  { nome:"Banco Sicredi RS",                   codigo:"748", ispb:"01181521" },
  { nome:"Coop. de Crédito Rural Cocred",      codigo:"078", ispb:"01701201" },
  { nome:"Banco Classico",                     codigo:"241", ispb:"31597552" },
  { nome:"Easynvest / Nuinvest",               codigo:"140", ispb:"62169875" },
  { nome:"Banco Genial",                       codigo:"125", ispb:"45246410" },
  { nome:"Banco Votorantim",                   codigo:"655", ispb:"59588111" },
  { nome:"Banco do Estado de Sergipe (Banese)",codigo:"047", ispb:"13009717" },
  { nome:"Banco do Estado do RS (Banrisul)",   codigo:"041", ispb:"92702067" },
  { nome:"Banco de Brasília (BRB)",            codigo:"070", ispb:"00000208" },
  { nome:"Banco Amazônia (BASA)",              codigo:"003", ispb:"04902979" },
  { nome:"Banco Regional de Desenvolvimento ES",codigo:"021",ispb:"28127603" },
  { nome:"Pag! (Banco Pag)",                   codigo:"303", ispb:"19540550" },
  { nome:"TrustBank",                          codigo:"177", ispb:"17826860" },
  { nome:"Ótimo",                              codigo:"355", ispb:"34711571" },
  { nome:"Banco Voiter",                       codigo:"653", ispb:"61024352" },
  { nome:"Cloudwalk",                          codigo:"354", ispb:"32074986" },
  { nome:"Banco Itaú Consignado",              codigo:"029", ispb:"33885724" },
  { nome:"Banco Daycoval",                     codigo:"707", ispb:"62232889" },
  { nome:"Banco ABC Brasil",                   codigo:"246", ispb:"28195667" },
  { nome:"Banco Caixa Geral Brasil",           codigo:"473", ispb:"33466988" },
  { nome:"Will Bank",                          codigo:"280", ispb:"23862762" },
  { nome:"Banco Itaú",                         codigo:"341", ispb:"60701190" },
  { nome:"Banco Santander Brasil",             codigo:"033", ispb:"90400888" },
  { nome:"Banco Bradesco S.A.",                codigo:"237", ispb:"60746948" },
  { nome:"Banco Next",                         codigo:"237", ispb:"60746948" },
  { nome:"Iti (Itaú)",                         codigo:"341", ispb:"60701190" },
  { nome:"Revolut Brasil",                     codigo:"435", ispb:"25095180" },
  { nome:"Banco Neon",                         codigo:"735", ispb:"20855875" },
  { nome:"InfinitePay",                        codigo:"329", ispb:"21018182" },
  { nome:"Conta Simples",                      codigo:"329", ispb:"21018182" },
  { nome:"Banco Besc (incorporado Banco Brasil)",codigo:"047",ispb:"13009717" },
  { nome:"Banco Itaú Unibanco S.A.",           codigo:"341", ispb:"60701190" },
  { nome:"QI Tech",                            codigo:"329", ispb:"21018182" },
  { nome:"Banco Sicredi PR",                   codigo:"748", ispb:"01181521" },
  { nome:"Banco Sicredi SC",                   codigo:"748", ispb:"01181521" },
  { nome:"Sicoob Credimota",                   codigo:"756", ispb:"02038232" },
  { nome:"Sicoob Cofal",                       codigo:"756", ispb:"02038232" },
  { nome:"Unicred do Brasil",                  codigo:"136", ispb:"00315557" },
  { nome:"Banco Rendimento S.A.",              codigo:"633", ispb:"68900810" },
  { nome:"Inter Digital Bank",                 codigo:"077", ispb:"00416968" },
  { nome:"Banco Modal S.A.",                   codigo:"746", ispb:"30723886" },
  { nome:"Banco Jota",                         codigo:"306", ispb:"04062902" },
  { nome:"Banco Superdigital",                 codigo:"340", ispb:"09554480" },
  { nome:"Banco Triângulo",                    codigo:"634", ispb:"17351180" },
  { nome:"Banco Crefisa",                      codigo:"069", ispb:"61033106" },
  { nome:"Banco Cetip / B3",                   codigo:"739", ispb:"00558456" },
  { nome:"Banco Fintech / Hub Fintech",        codigo:"084", ispb:"01634601" },
  { nome:"Banco Fidúcia",                      codigo:"382", ispb:"04307598" },
  { nome:"Banco Neon / Votorantim",            codigo:"655", ispb:"59588111" },
  { nome:"BMP Money Plus",                     codigo:"274", ispb:"11581339" },
  { nome:"Atar",                               codigo:"329", ispb:"21018182" },
  { nome:"Grafeno",                            codigo:"389", ispb:"17184037" },
  { nome:"Banco Órama",                        codigo:"325", ispb:"13293225" },
  { nome:"Banco Vitreo",                       codigo:"335", ispb:"27098060" },
  { nome:"Banco Bari S.A.",                    codigo:"330", ispb:"00556603" },
  { nome:"Banco Cora S.A.",                    codigo:"403", ispb:"37880206" },
  { nome:"Banco C6 S.A.",                      codigo:"336", ispb:"31872495" },
  { nome:"Banco Nubank S.A.",                  codigo:"260", ispb:"18236120" },
  { nome:"Banco PagSeguro",                    codigo:"290", ispb:"08561701" },
  { nome:"Mercado Crédito",                    codigo:"323", ispb:"10573521" },
  { nome:"Banco Transação",                    codigo:"312", ispb:"07693858" },
  { nome:"Banco Zipdin",                       codigo:"418", ispb:"62283978" },
  { nome:"Banco Digio S.A.",                   codigo:"335", ispb:"27098060" },
];

function BancoPicker({ bancoNome, bancoCodigo, bancoIspb, onSelect }) {
  const [busca, setBusca]     = useState(bancoNome || "");
  const [aberto, setAberto]   = useState(false);
  const ref = useRef(null);

  useEffect(() => { setBusca(bancoNome || ""); }, [bancoNome]);

  useEffect(() => {
    const h = (e) => { if (ref.current && !ref.current.contains(e.target)) setAberto(false); };
    document.addEventListener("mousedown", h);
    return () => document.removeEventListener("mousedown", h);
  }, []);

  const q = busca.trim().toLowerCase();
  const filtrados = BANCOS_BR.filter(b =>
    !q || b.nome.toLowerCase().includes(q) || b.codigo.startsWith(q) || b.ispb.includes(q)
  ).slice(0, 10);

  return (
    <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 1.2fr", gap:10 }}>
      <div ref={ref} style={{ position:"relative" }}>
        <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:4 }}>Instituição Bancária</label>
        <input
          value={busca}
          onChange={e => { setBusca(e.target.value); setAberto(true); onSelect({ nome: e.target.value, codigo: "", ispb: "" }); }}
          onFocus={() => setAberto(true)}
          placeholder="Buscar banco..."
          style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text, boxSizing:"border-box" }}
        />
        {aberto && filtrados.length > 0 && (
          <div style={{ position:"absolute", top:"100%", left:0, right:0, zIndex:999, background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:7, boxShadow:"0 4px 16px rgba(0,0,0,.14)", maxHeight:230, overflowY:"auto", marginTop:2 }}>
            {filtrados.map((b, bi) => (
              <div key={b.codigo + "_" + bi}
                onMouseDown={e => { e.preventDefault(); setBusca(b.nome); setAberto(false); onSelect(b); }}
                style={{ padding:"8px 12px", cursor:"pointer", fontSize:12, display:"flex", gap:10, alignItems:"center", borderBottom:`1px solid ${COLORS.border}` }}
              >
                <span style={{ flex:1, fontWeight:600, color:COLORS.text }}>{b.nome}</span>
                <span style={{ color:COLORS.textLight, fontSize:11 }}>{b.codigo}</span>
              </div>
            ))}
          </div>
        )}
      </div>
      <div>
        <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:4 }}>Código (COMPE)</label>
        <input value={bancoCodigo||""} onChange={e=>onSelect({ nome:bancoNome, codigo:e.target.value, ispb:bancoIspb })} placeholder="001" style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text, boxSizing:"border-box" }} />
      </div>
      <div>
        <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:4 }}>ISPB</label>
        <input value={bancoIspb||""} onChange={e=>onSelect({ nome:bancoNome, codigo:bancoCodigo, ispb:e.target.value })} placeholder="00000000" style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text, boxSizing:"border-box" }} />
      </div>
    </div>
  );
}

// ─── CONTAS BANCÁRIAS (sub-componente do formulário de funcionário) ────────────
function ContasBancarias({ funcId, token, onSaved, onDraftChange }) {
  const VAZIO_CONTA = { tipo:"pix_chave", pix_tipo:"", pix_chave:"", banco_nome:"", banco_codigo:"", banco_ispb:"", agencia:"", agencia_dv:"", conta:"", conta_dv:"", tipo_conta:"corrente", descricao:"", padrao:0 };
  const isDraft = !funcId;
  const [contas, setContas] = useState([]);
  const [mostraForm, setMostraForm] = useState(false);
  const [editConta, setEditConta] = useState(null);
  const [form, setForm] = useState(VAZIO_CONTA);
  const [salvando, setSalvando] = useState(false);
  const [carregando, setCarregando] = useState(false);
  const [erroApi, setErroApi] = useState("");
  const topoRef = useRef(null);

  const TIPOS = [
    { val:"pix_chave",       label:"🔑 PIX (Chave)" },
    { val:"conta_corrente",  label:"🏦 Conta Corrente" },
    { val:"conta_poupanca",  label:"🏦 Conta Poupança" },
    { val:"ted",             label:"📤 TED" },
    { val:"dinheiro",        label:"💵 Dinheiro" },
  ];
  const TIPOS_PIX = ["CPF","Telefone","E-mail","Chave Aleatória"];

  const carregar = (id) => {
    if (!id) return;
    setCarregando(true); setErroApi("");
    apiFetch("GET", `/api/funcionarios/${id}/contas?_=${Date.now()}`, undefined, token)
      .then(r => { setContas(Array.isArray(r) ? r : []); setCarregando(false); })
      .catch(e => { setErroApi(e.message||"Erro ao carregar"); setCarregando(false); });
  };
  useEffect(() => { carregar(funcId); }, [funcId]);
  useEffect(() => { if (isDraft && onDraftChange) onDraftChange(contas); }, [contas, isDraft]);

  const abrirNova = () => { setForm({...VAZIO_CONTA}); setEditConta(null); setMostraForm(true); };
  const abrirEditar = (c) => { setForm({...c}); setEditConta(c.id); setMostraForm(true); };

  const salvar = async () => {
    if (isDraft) {
      const draftId = editConta || `draft_${Date.now()}`;
      if (editConta) {
        setContas(prev => prev.map(c => c.id === editConta
          ? { ...c, ...form, padrao: form.padrao ? 1 : 0 }
          : form.padrao ? { ...c, padrao: 0 } : c
        ));
      } else {
        if (form.padrao) setContas(prev => prev.map(c => ({ ...c, padrao: 0 })));
        setContas(prev => [...prev, { ...form, id: draftId }]);
      }
      setMostraForm(false);
      return;
    }
    setSalvando(true);
    try {
      if (editConta) {
        await apiFetch("PUT", `/api/funcionarios/${funcId}/contas/${editConta}`, form, token);
        if (form.padrao) {
          await apiFetch("PATCH", `/api/funcionarios/${funcId}/contas/${editConta}/padrao`, {}, token);
          setContas(prev => prev.map(c => ({ ...c, ...( c.id === editConta ? form : {}), padrao: c.id === editConta ? 1 : 0 })));
        } else {
          setContas(prev => prev.map(c => c.id === editConta ? { ...c, ...form } : c));
        }
      } else {
        const nova = await apiFetch("POST", `/api/funcionarios/${funcId}/contas`, form, token);
        if (form.padrao) setContas(prev => prev.map(c => ({ ...c, padrao: 0 })));
        setContas(prev => [...prev, { ...form, id: nova.id }]);
      }
      setMostraForm(false);
      if (onSaved) onSaved();
    } catch(e) { alert("Erro: " + e.message); }
    setSalvando(false);
  };

  const marcarPadrao = async (c) => {
    if (isDraft) {
      setContas(prev => prev.map(x => ({ ...x, padrao: x.id === c.id ? 1 : 0 })));
      return;
    }
    try {
      await apiFetch("PATCH", `/api/funcionarios/${funcId}/contas/${c.id}/padrao`, {}, token);
      setContas(prev => prev.map(x => ({ ...x, padrao: x.id === c.id ? 1 : 0 })));
      if (onSaved) onSaved();
    } catch(e) { alert("Erro: " + e.message); }
  };

  const excluir = async (c) => {
    if (!window.confirm("Excluir este dado bancário?")) return;
    if (isDraft) {
      setContas(prev => prev.filter(x => x.id !== c.id));
      return;
    }
    try {
      await apiFetch("DELETE", `/api/funcionarios/${funcId}/contas/${c.id}`, undefined, token);
      setContas(prev => prev.filter(x => x.id !== c.id));
      if (onSaved) onSaved();
    } catch(e) { alert("Erro: " + e.message); }
  };

  const fmtConta = (c) => {
    if (c.tipo === "pix_chave") return c.pix_tipo ? `${c.pix_tipo}: ${c.pix_chave||""}` : "PIX";
    if (c.tipo === "dinheiro") return "Pagamento em dinheiro";
    const ag = c.agencia ? `Ag. ${c.agencia}${c.agencia_dv?`-${c.agencia_dv}`:""}` : "";
    const ct = c.conta ? `Cc. ${c.conta}${c.conta_dv?`-${c.conta_dv}`:""}` : "";
    return [c.banco_nome, ag, ct].filter(Boolean).join(" · ");
  };

  const f = form;
  const upd = (k,v) => setForm(prev => ({ ...prev, [k]: v }));

  return (
    <div ref={topoRef} style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 14 }}>
      <div style={{ display:"flex", alignItems:"center", justifyContent:"space-between", marginBottom: 12 }}>
        <div>
          <div style={{ fontSize: 10, fontWeight: 800, color: "#6366f1", letterSpacing: 1, textTransform: "uppercase" }}>🏦 Dados Bancários / PIX</div>
          <div style={{ fontSize: 11, color: COLORS.textLight }}>Cadastre contas PIX, CC, TED — marque a padrão para Pix em Lotes</div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {funcId && <button onClick={()=>carregar(funcId)} style={{ background:"none", border:`1px solid ${COLORS.border}`, color:COLORS.textMid, borderRadius:7, padding:"4px 10px", fontSize:11, cursor:"pointer" }} title="Recarregar">↺</button>}
          <button onClick={abrirNova} style={{ background:"#6366f1", color:"#fff", border:"none", borderRadius:7, padding:"6px 14px", fontWeight:700, cursor:"pointer", fontSize:12 }}>+ Adicionar</button>
          {isDraft && <span style={{ fontSize:11, color:"#f59e0b", fontWeight:600 }}>⚠ Salve o funcionário para confirmar</span>}
        </div>
      </div>

      {carregando && <div style={{ fontSize:12, color:COLORS.textLight, padding:"6px 0" }}>Carregando...</div>}
      {erroApi && <div style={{ fontSize:12, color:"#dc2626", padding:"6px 0" }}>⚠ {erroApi}</div>}

      {/* Lista de contas já cadastradas */}
      {contas.length > 0 && (
        <div style={{ display:"flex", flexDirection:"column", gap:8, marginBottom: mostraForm ? 12 : 0 }}>
          {contas.map(c => (
            <div key={c.id} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px", borderRadius:10, border:`2px solid ${c.padrao ? "#6366f1" : COLORS.border}`, background: c.padrao ? "#eef2ff" : COLORS.surface }}>
              <div style={{ flex:1, minWidth:0 }}>
                <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                  <span style={{ fontWeight:700, fontSize:13, color: c.padrao ? "#6366f1" : COLORS.text }}>
                    {TIPOS.find(t=>t.val===c.tipo)?.label || c.tipo}
                  </span>
                  {c.padrao && <span style={{ fontSize:10, background:"#6366f1", color:"#fff", borderRadius:4, padding:"1px 6px", fontWeight:700 }}>PADRÃO</span>}
                  {c.descricao && <span style={{ fontSize:11, color:COLORS.textLight }}>— {c.descricao}</span>}
                </div>
                <div style={{ fontSize:12, color:COLORS.textMid, marginTop:2 }}>{fmtConta(c)}</div>
              </div>
              <div style={{ display:"flex", gap:6, flexShrink:0 }}>
                {!c.padrao && <button onClick={()=>marcarPadrao(c)} style={{ background:"none", border:`1px solid #6366f1`, color:"#6366f1", borderRadius:6, padding:"4px 10px", fontSize:11, fontWeight:700, cursor:"pointer" }}>★ Padrão</button>}
                <Btn size="sm" variant="edit" onClick={()=>abrirEditar(c)}>✏️ Editar</Btn>
                <button onClick={()=>excluir(c)} style={{ background:"none", border:`1px solid ${COLORS.danger}`, color:COLORS.danger, borderRadius:6, padding:"4px 10px", fontSize:11, cursor:"pointer" }}>✕</button>
              </div>
            </div>
          ))}
        </div>
      )}
      {contas.length === 0 && !mostraForm && (
        <div style={{ fontSize:12, color:COLORS.textLight, padding:"8px 0" }}>Nenhum dado cadastrado.</div>
      )}

      {/* Mini-form */}
      {mostraForm && (
        <div style={{ background: COLORS.bg, borderRadius:10, padding:"14px 16px", border:`1px solid ${COLORS.border}`, marginTop:4 }}>
          <div style={{ fontSize:12, fontWeight:700, color:"#6366f1", marginBottom:12 }}>{editConta ? "Editar dado bancário" : "Novo dado bancário"}</div>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 2fr", gap:12, marginBottom:10 }}>
            <div>
              <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:4 }}>Tipo</label>
              <select value={f.tipo} onChange={e=>upd("tipo",e.target.value)} style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text }}>
                {TIPOS.map(t=><option key={t.val} value={t.val}>{t.label}</option>)}
              </select>
            </div>
            <Input label="Descrição (opcional)" value={f.descricao||""} onChange={e=>upd("descricao",e.target.value)} placeholder='Ex: "Inter PIX CPF", "Caixa CC"' />
          </div>

          {f.tipo === "pix_chave" && (
            <>
              <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:10 }}>
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:4 }}>Tipo de Chave PIX</label>
                  <select value={f.pix_tipo||""} onChange={e=>upd("pix_tipo",e.target.value)} style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text }}>
                    <option value="">Selecione...</option>
                    {TIPOS_PIX.map(t=><option key={t}>{t}</option>)}
                  </select>
                </div>
                <Input label="Chave PIX" value={f.pix_chave||""} onChange={e=>upd("pix_chave",e.target.value)} placeholder="CPF, telefone, e-mail..." />
              </div>
              <div style={{ marginBottom:10 }}>
                <div style={{ fontSize:11, color:COLORS.textLight, marginBottom:6 }}>Banco recebedor <span style={{ color:"#94a3b8" }}>(opcional — usado na remessa CNAB)</span></div>
                <BancoPicker
                  bancoNome={f.banco_nome}
                  bancoCodigo={f.banco_codigo}
                  bancoIspb={f.banco_ispb}
                  onSelect={b => setForm(prev => ({ ...prev, banco_nome: b.nome, banco_codigo: b.codigo, banco_ispb: b.ispb }))}
                />
              </div>
            </>
          )}

          {f.tipo !== "pix_chave" && f.tipo !== "dinheiro" && (
            <>
              <div style={{ marginBottom:10 }}>
                <BancoPicker
                  bancoNome={f.banco_nome}
                  bancoCodigo={f.banco_codigo}
                  bancoIspb={f.banco_ispb}
                  onSelect={b => setForm(prev => ({ ...prev, banco_nome: b.nome, banco_codigo: b.codigo, banco_ispb: b.ispb }))}
                />
              </div>
              <div style={{ display:"grid", gridTemplateColumns:"2fr 1fr 2fr 1fr 1.2fr", gap:10, marginBottom:10 }}>
                <Input label="Agência" value={f.agencia||""} onChange={e=>upd("agencia",e.target.value)} placeholder="0001" />
                <Input label="DV Ag." value={f.agencia_dv||""} onChange={e=>upd("agencia_dv",e.target.value)} placeholder="0" />
                <Input label="Conta" value={f.conta||""} onChange={e=>upd("conta",e.target.value)} placeholder="38707714" />
                <Input label="DV Conta" value={f.conta_dv||""} onChange={e=>upd("conta_dv",e.target.value)} placeholder="6" />
                <div>
                  <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:4 }}>Tipo de Conta</label>
                  <select value={f.tipo_conta||"corrente"} onChange={e=>upd("tipo_conta",e.target.value)} style={{ width:"100%", padding:"8px 10px", borderRadius:7, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text }}>
                    <option value="corrente">Corrente</option>
                    <option value="poupanca">Poupança</option>
                  </select>
                </div>
              </div>
            </>
          )}

          <label style={{ display:"flex", alignItems:"center", gap:8, fontSize:13, marginBottom:10, cursor:"pointer" }}>
            <input type="checkbox" checked={!!f.padrao} onChange={e=>upd("padrao",e.target.checked?1:0)} />
            Marcar como padrão para Pix em Lotes
          </label>

          <div style={{ display:"flex", gap:8, justifyContent:"flex-end" }}>
            <button onClick={()=>setMostraForm(false)} style={{ background:"none", border:`1px solid ${COLORS.border}`, borderRadius:7, padding:"7px 16px", cursor:"pointer", fontSize:13, color:COLORS.textMid }}>Cancelar</button>
            <button onClick={salvar} disabled={salvando} style={{ background:"#6366f1", color:"#fff", border:"none", borderRadius:7, padding:"7px 18px", fontWeight:700, cursor:"pointer", fontSize:13, opacity:salvando?.6:1 }}>{salvando?"Salvando...":"Salvar"}</button>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── FUNCIONÁRIOS ─────────────────────────────────────────────────────────────
function Funcionarios({ employees, setEmployees, setores, empresas, setEmpresas, setPonto, setAfastamentos, setEscala, token, usuario, tenantDominio, abaInicial }) {
  const canEdit = ["admin","administrador","rh","gestor"].includes(usuario?.perfil);
  const [search, setSearch] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState([]);
  const [filtroCargo, setFiltroCargo] = useState([]);
  const [filtroSetor, setFiltroSetor] = useState([]);
  const [filtroSubsetor, setFiltroSubsetor] = useState([]);
  const [filtroTurno, setFiltroTurno] = useState([]);
  const [filtroStatus, setFiltroStatus] = useState([]);
  const [sortCol, setSortCol] = useState(null);
  const [sortDir, setSortDir] = useState("asc");
  const [abaFunc, setAbaFunc] = useState(abaInicial || "efetivos");
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({});
  const [filhos, setFilhos] = useState([]);
  const [contasBancariasRascunho, setContasBancariasRascunho] = useState([]);
  const [modalDocs, setModalDocs] = useState(null);
  const [docPastas, setDocPastas] = useState([]);
  const [docArquivos, setDocArquivos] = useState([]);
  const [docPastaAtual, setDocPastaAtual] = useState(null);
  const [docBreadcrumb, setDocBreadcrumb] = useState([]);
  const [uploadingDoc, setUploadingDoc] = useState(false);
  const [docNovaPasta, setDocNovaPasta] = useState('');
  const [docCriandoPasta, setDocCriandoPasta] = useState(false);
  const [docRenamePasta, setDocRenamePasta] = useState(null);
  const [docDraggedFile, setDocDraggedFile] = useState(null);
  const [docDragOver, setDocDragOver] = useState(null);
  const [confirmExcluir, setConfirmExcluir] = useState(null);
  const [modalDemitir, setModalDemitir] = useState(null);
  const [dataDemissao, setDataDemissao] = useState("");
  const [contratos, setContratos] = useState([]);
  useEffect(() => {
    if (token) apiFetch("GET", "/api/contratos/contratos", undefined, token).then(r => setContratos(Array.isArray(r)?r:[])).catch(()=>{});
  }, [token]);

  // Histórico do funcionário
  const [abaModalFunc, setAbaModalFunc] = useState("cadastro");
  const [histFunc, setHistFunc] = useState([]);
  const [histLoading, setHistLoading] = useState(false);
  const [formHist, setFormHist] = useState({ descricao: "" });
  const [savingHist, setSavingHist] = useState(false);

  const carregarHist = async (fid) => {
    setHistLoading(true);
    try {
      const r = await apiFetch("GET", `/api/funcionarios/${fid}/historico`, undefined, token);
      setHistFunc(Array.isArray(r) ? r : []);
    } catch { setHistFunc([]); }
    setHistLoading(false);
  };

  const salvarHist = async (fid) => {
    if (!formHist.descricao.trim()) return;
    setSavingHist(true);
    try {
      const r = await apiFetch("POST", `/api/funcionarios/${fid}/historico`, { tipo: "observacao", descricao: formHist.descricao }, token);
      setHistFunc(prev => [r, ...prev]);
      setFormHist({ descricao: "" });
    } catch(e) { alert("Erro: " + e.message); }
    setSavingHist(false);
  };

  const deletarHist = async (fid, hid) => {
    if (!window.confirm("Remover este registro do histórico?")) return;
    try {
      await apiFetch("DELETE", `/api/funcionarios/${fid}/historico/${hid}`, undefined, token);
      setHistFunc(prev => prev.filter(h => h.id !== hid));
    } catch(e) { alert("Erro: " + e.message); }
  };

  const DIAS_SEMANA = [
    { v: 0, n: "Dom" }, { v: 1, n: "Seg" }, { v: 2, n: "Ter" },
    { v: 3, n: "Qua" }, { v: 4, n: "Qui" }, { v: 5, n: "Sex" }, { v: 6, n: "Sáb" },
  ];
  const toggleDiaSemana = (v) => setForm(f => ({
    ...f,
    dias_semana: (f.dias_semana || []).includes(v)
      ? (f.dias_semana || []).filter(x => x !== v)
      : [...(f.dias_semana || []), v].sort((a, b) => a - b)
  }));

  const ativos = employees.filter(e => e.status !== "Demitido");

  const optsEmpresa  = [...new Set(employees.map(e => e.empresa).filter(Boolean))].sort((a,b) => a.localeCompare(b,"pt-BR"));
  const optsCargo    = [...new Set(employees.map(e => e.cargo).filter(Boolean))].sort((a,b) => a.localeCompare(b,"pt-BR"));
  const optsSetor    = [...new Set(employees.map(e => e.setor).filter(Boolean))].sort((a,b) => a.localeCompare(b,"pt-BR"));
  const optsSubsetor = [...new Set(employees.map(e => e.subsetor).filter(Boolean))].sort((a,b) => a.localeCompare(b,"pt-BR"));
  const optsTurno    = [...new Set(employees.map(e => e.turno).filter(Boolean))].sort((a,b) => a.localeCompare(b,"pt-BR"));
  const optsStatus   = ["Ativo", "Férias", "Afastado", "Demitido"];

  const sortKey = {
    "Nome": e => e.nome, "Empresa": e => e.empresa || "", "Cargo": e => e.cargo, "Setor": e => e.setor,
    "Subsetor": e => e.subsetor || "", "Turno": e => e.turno,
    "Admissão": e => e.admissao, "Salário": e => e.salario, "Status": e => e.status,
  };
  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };
  const filtered = employees
    .filter(e => {
      if (abaFunc === "pre-cadastro") return !!e.pre_cadastro;
      if (e.pre_cadastro) return false; // efetivos: exclui pré-cadastro
      return (filtroStatus.length > 0 ? filtroStatus.includes(e.status) : e.status !== "Demitido") &&
        (filtroEmpresa.length === 0  || filtroEmpresa.includes(e.empresa)) &&
        (filtroCargo.length === 0    || filtroCargo.includes(e.cargo)) &&
        (filtroSetor.length === 0    || filtroSetor.includes(e.setor || "")) &&
        (filtroSubsetor.length === 0 || filtroSubsetor.includes(e.subsetor || "")) &&
        (filtroTurno.length === 0    || filtroTurno.includes(e.turno || "")) &&
        (!search || e.nome.toLowerCase().includes(search.toLowerCase()) || e.cargo.toLowerCase().includes(search.toLowerCase()));
    })
    .sort((a, b) => {
      if (!sortCol) return 0;
      const fn = sortKey[sortCol];
      const va = fn(a), vb = fn(b);
      if (typeof va === "number") return sortDir === "asc" ? va - vb : vb - va;
      return sortDir === "asc" ? String(va).localeCompare(String(vb), "pt-BR") : String(vb).localeCompare(String(va), "pt-BR");
    });

  const imprimirFuncionarios = () => {
    const fmt = (v) => v ? new Date(v + "T00:00:00").toLocaleDateString("pt-BR") : "";
    const fmtR = (v) => `R$ ${parseFloat(v || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
    const linhas = filtered.map(e => `<tr>
      <td>${e.nome}</td><td>${e.empresa || ""}</td><td>${e.cargo}</td>
      <td>${e.setor}</td><td>${e.subsetor || ""}</td><td>${e.turno}</td>
      <td>${fmt(e.admissao)}</td><td>${fmtR(e.salario)}</td><td>${e.status}</td>
    </tr>`).join("");
    const w = window.open("", "_blank");
    w.document.write(`<!DOCTYPE html><html><head><meta charset="utf-8"><title>Funcionários</title>
    <style>@page{size:landscape;margin:12mm}body{font-family:sans-serif;font-size:11px}
    table{width:100%;border-collapse:collapse}th{background:#1e4d00;color:#fff;padding:7px 9px;text-align:left}
    td{padding:6px 9px;border-bottom:1px solid #e5e7eb}tr:nth-child(even) td{background:#f9fafb}
    h2{font-size:14px;margin:0 0 10px}</style></head><body>
    <h2>Funcionários — ${filtered.length} registro(s)</h2>
    <table><thead><tr><th>Nome</th><th>Empresa</th><th>Cargo</th><th>Setor</th><th>Subsetor</th><th>Turno</th><th>Admissão</th><th>Salário</th><th>Status</th></tr></thead>
    <tbody>${linhas}</tbody></table></body></html>`);
    w.document.close();
    w.onload = () => w.print();
  };

  const openNew = () => {
    setForm({ nome: "", cargo: "", empresa: "", grupo: "", sub_grupo: "", setor: "", subsetor: "", turno: "Diurno", admissao: "", salario: "", status: "Ativo", telefone: "", cpf: "", pis: "", email: "", login_usuario: "", senha_acesso: "", hora_entrada: "08:00", hora_saida: "17:00", dias_semana: [1,2,3,4,5], ferias: { disponivel: 0, tiradas: 0, proximas: "" }, docs: { aso: "", ctps: "OK" }, pagamento: { tipo: "", banco: "", agencia: "", conta: "", digito: "", tipoPix: "", chavePix: "", pixBancoCodigo: "", pixBancoNome: "", pixBancoIspb: "", pixAgencia: "", pixAgenciaDv: "", pixConta: "", pixContaDv: "", pixTipoConta: "corrente" }, contrato_id: "", vt_tipo: "", cidade: "", ctps: "", titulo_eleitor: "", matricula: "", tam_camisa: "", tam_calca: "", tam_sapato: "", tam_bota: "", hora_saida_almoco: "", hora_volta_almoco: "", sal_familia: 0, adiantamento: 0, vr_dia: 0, vr_dias: 0, pre_cadastro: abaFunc === "pre-cadastro" ? 1 : 0 });
    setFilhos([]);
    setContasBancariasRascunho([]);
    setAbaModalFunc("cadastro");
    setHistFunc([]);
    setFormHist({ descricao: "" });
    setModal("novo");
  };

  const openEdit = (e) => {
    setForm({ ...e, hora_entrada: e.hora_entrada || "08:00", hora_saida: e.hora_saida || "17:00", dias_semana: e.dias_semana && e.dias_semana.length ? e.dias_semana : [1,2,3,4,5], vt_tipo: e.vt_tipo || "", cidade: e.cidade || "" });
    apiFetch("GET", `/api/funcionarios/${e.id}/filhos`, undefined, token)
      .then(rows => setFilhos(rows || []))
      .catch(() => setFilhos([]));
    setAbaModalFunc("cadastro");
    setHistFunc([]);
    setFormHist({ descricao: "" });
    setModal("editar");
  };

  const carregarDocs = async (fid, pasta_id) => {
    try {
      const qs = pasta_id ? `?pasta_id=${pasta_id}` : '';
      const data = await apiFetch("GET", `/api/documentos/${fid}${qs}`, undefined, token);
      setDocPastas(data?.pastas || []);
      setDocArquivos(data?.arquivos || []);
    } catch {}
  };

  const openDocs = (e) => {
    setModalDocs(e);
    setDocPastaAtual(null);
    setDocBreadcrumb([]);
    setDocCriandoPasta(false);
    setDocRenamePasta(null);
    carregarDocs(e.id, null);
  };

  const docNavegar = (pasta) => {
    setDocBreadcrumb(prev => [...prev, pasta]);
    setDocPastaAtual(pasta.id);
    setDocCriandoPasta(false);
    setDocRenamePasta(null);
    carregarDocs(modalDocs.id, pasta.id);
  };

  const docVoltar = (idx) => {
    if (idx < 0) {
      setDocBreadcrumb([]);
      setDocPastaAtual(null);
      carregarDocs(modalDocs.id, null);
    } else {
      const crumb = docBreadcrumb[idx];
      setDocBreadcrumb(prev => prev.slice(0, idx + 1));
      setDocPastaAtual(crumb.id);
      carregarDocs(modalDocs.id, crumb.id);
    }
    setDocCriandoPasta(false);
    setDocRenamePasta(null);
  };

  const docCriarPasta = async () => {
    if (!docNovaPasta.trim()) return;
    try {
      await apiFetch("POST", `/api/documentos/${modalDocs.id}/pasta`, { nome: docNovaPasta.trim(), pasta_pai_id: docPastaAtual }, token);
      setDocNovaPasta('');
      setDocCriandoPasta(false);
      carregarDocs(modalDocs.id, docPastaAtual);
    } catch(e) { alert("Erro ao criar pasta: " + e.message); }
  };

  const docRenomear = async (pid, nome) => {
    try {
      await apiFetch("PATCH", `/api/documentos/${modalDocs.id}/pasta/${pid}`, { nome }, token);
      setDocRenamePasta(null);
      carregarDocs(modalDocs.id, docPastaAtual);
    } catch(e) { alert("Erro ao renomear: " + e.message); }
  };

  const docExcluirPasta = async (pid) => {
    if (!window.confirm("Excluir pasta e todo o conteúdo dentro dela?")) return;
    try {
      await apiFetch("DELETE", `/api/documentos/${modalDocs.id}/pasta/${pid}`, undefined, token);
      carregarDocs(modalDocs.id, docPastaAtual);
    } catch(e) { alert("Erro ao excluir: " + e.message); }
  };

  const uploadDoc = async (fid, file) => {
    if (file.size > 30 * 1024 * 1024) { alert("Arquivo muito grande. Máximo 30MB."); return; }
    setUploadingDoc(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      if (docPastaAtual) fd.append("pasta_id", docPastaAtual);
      const res = await fetch(`/api/documentos/${fid}`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!res.ok) throw new Error("Erro no upload");
      carregarDocs(fid, docPastaAtual);
    } catch(e) { alert("Erro ao enviar: " + e.message); }
    setUploadingDoc(false);
  };

  const deleteDoc = async (docId) => {
    if (!window.confirm("Excluir este arquivo?")) return;
    try {
      await apiFetch("DELETE", `/api/documentos/arquivo/${docId}`, undefined, token);
      setDocArquivos(prev => prev.filter(d => d.id !== docId));
    } catch(e) { alert("Erro ao excluir: " + e.message); }
  };

  const docMoverArquivo = async (fileId, destPastaId) => {
    try {
      await apiFetch("PATCH", `/api/documentos/arquivo/${fileId}`, { pasta_id: destPastaId }, token);
      carregarDocs(modalDocs.id, docPastaAtual);
    } catch(e) { alert("Erro ao mover: " + e.message); }
  };

  const fmtBytes = (b) => b > 1024*1024 ? `${(b/1024/1024).toFixed(1)}MB` : `${(b/1024).toFixed(0)}KB`;

  const save = async () => {
    const isPrecadastro = !!form.pre_cadastro;
    // Validação dos campos obrigatórios
    const erros = [];
    if (!form.nome?.trim())           erros.push("Nome");
    if (!form.cpf?.trim())            erros.push("CPF");
    if (!isPrecadastro) {
      if (!form.data_nascimento)        erros.push("Data de Nascimento");
      if (!form.estado_civil?.trim())   erros.push("Estado Civil");
      if (!form.endereco?.trim())       erros.push("Endereço Residencial");

      if (!form.cargo?.trim())          erros.push("Cargo");
      if (!form.grupo?.trim())          erros.push("Grupo");
      if (!form.sub_grupo?.trim())      erros.push("Sub Grupo");
      if (!form.setor?.trim())          erros.push("Setor");
      if (!form.admissao)               erros.push("Data de Admissão");
      if (!form.turno?.trim())          erros.push("Turno");
      if (!form.hora_entrada?.trim())   erros.push("Horário de Entrada");
      if (!form.hora_saida?.trim())     erros.push("Horário de Saída");
      if (!form.tam_camisa?.trim())     erros.push("Tamanho de Uniforme (Camisa)");
      const vtTipos = parseVtTipos(form.vt_tipo);
      if (vtTipos.length === 0) {
        erros.push("Vale Transporte (selecione ao menos um tipo)");
      } else {
        vtTipos.filter(t => !VT_SEM_VALOR.includes(t)).forEach(tipo => {
          if (!(parseFloat(form[`vt_valor_${tipo}`]) > 0)) erros.push(`VT ${TIPO_LABELS_VT[tipo] || tipo} — Valor Diário`);
        });
      }
      if (!(parseFloat(form.vr_dia) > 0))  erros.push("Vale Refeição — Valor Diário");
    }

    if (erros.length > 0) {
      alert(`Preencha os campos obrigatórios antes de salvar:\n\n• ${erros.join("\n• ")}`);
      return;
    }

    const diasSemanaStr = Array.isArray(form.dias_semana) && form.dias_semana.length
      ? form.dias_semana.join(",") : null;
    const payload = {
      nome: form.nome, cpf: form.cpf, pis: form.pis, telefone: form.telefone,
      email: form.email || "",
      cargo: form.cargo, grupo: form.grupo || null, sub_grupo: form.sub_grupo || null, setor: form.setor, subsetor: form.subsetor, turno: form.turno,
      empresa: form.empresa, salario: parseFloat(form.salario) || 0,
      data_admissao: form.admissao || null,
      pre_cadastro: form.pre_cadastro ? 1 : 0,
      status: { "Ativo": "ativo", "Férias": "ferias", "Afastado": "afastado", "Demitido": "demitido" }[form.status] || "ativo",
      aso_data: form.docs?.aso || null,
      banco: form.pagamento?.banco || "", tipo_pagamento: form.pagamento?.tipo || "",
      chave_pix: form.pagamento?.chavePix || "", tipo_pix: form.pagamento?.tipoPix || "",
      agencia: form.pagamento?.agencia || "", conta: form.pagamento?.conta || "",
      digito: form.pagamento?.digito || "",
      hora_entrada: form.hora_entrada || null,
      hora_saida: form.hora_saida || null,
      dias_semana: diasSemanaStr,
      vt_tipo: form.vt_tipo || null,
      cidade: form.cidade || null,
      endereco: form.endereco || null,
      data_nascimento: form.data_nascimento || null,
      estado_civil: form.estado_civil || null,
    };
    // Campos extras salvos via PATCH (tanto em novo quanto em edição)
    const patchExtra = {
          hora_saida_almoco: form.hora_saida_almoco || null,
          hora_volta_almoco: form.hora_volta_almoco || null,
          ctps: form.ctps || null,
          titulo_eleitor: form.titulo_eleitor || null,
          matricula: form.matricula || null,
          tam_camisa: form.tam_camisa || null,
          tam_calca: form.tam_calca || null,
          tam_sapato: form.tam_sapato || null,
          tam_bota: form.tam_bota || null,
          sal_familia: parseFloat(form.sal_familia) || 0,
          adiantamento: form.adiantamento ? 1 : 0,
          insalubridade_grau: form.insalubridade_grau || null,
          data_nascimento: form.data_nascimento || null,
          vr_dia: parseFloat(form.vr_dia) || 0,
          vr_dias: parseInt(form.vr_dias) || 0,
          vt_cartao_otimo: form.vt_cartao_otimo || null,
          vt_valor_otimo: parseFloat(form.vt_valor_otimo) || 0,
          vt_dias_otimo: parseInt(form.vt_dias_otimo) || 0,
          vt_cartao_bhbus: form.vt_cartao_bhbus || null,
          vt_valor_bhbus: parseFloat(form.vt_valor_bhbus) || 0,
          vt_dias_bhbus: parseInt(form.vt_dias_bhbus) || 0,
          vt_cartao_gasolina: form.vt_cartao_gasolina || null,
          vt_valor_gasolina: parseFloat(form.vt_valor_gasolina) || 0,
          vt_dias_gasolina: parseInt(form.vt_dias_gasolina) || 0,
          vt_cartao_betimcard: form.vt_cartao_betimcard || null,
          vt_valor_betimcard: parseFloat(form.vt_valor_betimcard) || 0,
          vt_dias_betimcard: parseInt(form.vt_dias_betimcard) || 0,
          vt_cartao_outros: form.vt_cartao_outros || null,
          vt_valor_outros: parseFloat(form.vt_valor_outros) || 0,
          vt_dias_outros: parseInt(form.vt_dias_outros) || 0,
          cidade_moradia: form.cidade_moradia || null,
          endereco_trabalho: form.endereco_trabalho || null,
          horario_sabado: form.horario_sabado || null,
          nome_pai: form.nome_pai || null,
          nome_mae: form.nome_mae || null,
          estado_civil: form.estado_civil || null,
          cnh: form.cnh || null,
          pix_banco_codigo: form.pagamento?.pixBancoCodigo || null,
          pix_banco_nome: form.pagamento?.pixBancoNome || null,
          pix_banco_ispb: form.pagamento?.pixBancoIspb || null,
          pix_agencia: form.pagamento?.pixAgencia || null,
          pix_agencia_dv: form.pagamento?.pixAgenciaDv || null,
          pix_conta: form.pagamento?.pixConta || null,
          pix_conta_dv: form.pagamento?.pixContaDv || null,
          pix_tipo_conta: form.pagamento?.pixTipoConta || null,
          pix_tipo_chave: form.pagamento?.pixTipoChave || null,
    };
    try {
      if (modal === "novo") {
        const novo = await apiFetch("POST", "/api/funcionarios", payload, token);
        await apiFetch("PATCH", `/api/funcionarios/${novo.id}`, patchExtra, token).catch(() => {});
        setEmployees(prev => [...prev, adaptFuncionario({ ...payload, ...patchExtra, id: novo.id })]);
        if (filhos.length > 0) {
          await apiFetch("PUT", `/api/funcionarios/${novo.id}/filhos`, filhos, token).catch(() => {});
        }
        if (contasBancariasRascunho.length > 0) {
          for (const c of contasBancariasRascunho) {
            const { id: _id, ...dados } = c;
            const nova = await apiFetch("POST", `/api/funcionarios/${novo.id}/contas`, dados, token).catch(() => null);
            if (nova?.id && c.padrao) {
              await apiFetch("PATCH", `/api/funcionarios/${novo.id}/contas/${nova.id}/padrao`, {}, token).catch(() => {});
            }
          }
          setContasBancariasRascunho([]);
        }
        if (form.contrato_id) {
          await apiFetch("POST", `/api/contratos/contratos/${form.contrato_id}/funcionarios`, { funcionario_id: novo.id }, token).catch(()=>{});
        }
        if (form.login_usuario && form.senha_acesso) {
          try {
            await apiFetch("POST", "/api/auth/usuarios", {
              nome: form.nome, email: form.login_usuario, senha: form.senha_acesso,
              perfil: "visualizador", funcionario_id: novo.id,
            }, token);
            alert(`✅ Funcionário cadastrado!\nLogin criado: ${form.login_usuario}`);
          } catch(eu) {
            alert(`✅ Funcionário cadastrado!\n⚠️ Mas não foi possível criar o login: ${eu.message}`);
          }
        } else if (form.login_usuario && !form.senha_acesso) {
          alert(`✅ Funcionário cadastrado!\n⚠️ Login NÃO criado — você preencheu o login mas esqueceu a senha.`);
        }
      } else {
        await apiFetch("PUT", `/api/funcionarios/${form.id}`, payload, token);
        await apiFetch("PATCH", `/api/funcionarios/${form.id}`, patchExtra, token);
        await apiFetch("PUT", `/api/funcionarios/${form.id}/filhos`, filhos, token).catch(() => {});
        setEmployees(prev => prev.map(e => e.id === form.id
          ? adaptFuncionario({ ...payload, ...patchExtra, id: form.id })
          : e
        ));
        if (form.contrato_id) {
          await apiFetch("POST", `/api/contratos/contratos/${form.contrato_id}/funcionarios`, { funcionario_id: form.id }, token).catch(()=>{});
        }
      }
      setModal(null);
    } catch(e) { alert("Erro ao salvar: " + e.message); }
  };

  const confirmarDemissao = async () => {
    if (!dataDemissao) return;
    try {
      await apiFetch("PATCH", `/api/funcionarios/${modalDemitir.id}`, { status: "demitido", data_demissao: dataDemissao }, token);
      setEmployees(prev => prev.map(e => e.id === modalDemitir.id ? { ...e, status: "Demitido", dataDemissao } : e));
      setModalDemitir(null);
      setDataDemissao("");
    } catch(e) { alert("Erro ao demitir: " + e.message); }
  };

  const excluirFuncionario = async (id) => {
    try {
      await apiFetch("DELETE", `/api/funcionarios/${id}`, undefined, token);
      setEmployees(prev => prev.filter(e => e.id !== id));
      setPonto(prev => Array.isArray(prev) ? prev.filter(p => p.funcionario_id !== id) : prev);
      setAfastamentos(prev => prev.filter(a => a.funcionarioId !== id));
      setEscala(prev => prev.filter(s => s.funcionarioId !== id));
      setConfirmExcluir(null);
    } catch(e) { alert("Erro ao excluir: " + e.message); }
  };

  const efetivosCount = employees.filter(e => !e.pre_cadastro).length;
  const preCadastroCount = employees.filter(e => !!e.pre_cadastro).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Sub-abas removidas — navegação pelo sidebar */}

      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "center" }}>
        <Input placeholder="🔍 Buscar por nome..." value={search} onChange={e => setSearch(e.target.value)} style={{ minWidth: 220 }} />
        {canEdit && !modal && abaFunc === "efetivos" && <Btn onClick={openNew}>+ Novo Funcionário</Btn>}
        {canEdit && !modal && abaFunc === "pre-cadastro" && <Btn onClick={openNew}>+ Novo Pré Cadastro</Btn>}
        {!modal && abaFunc === "efetivos" && <Btn variant="ghost" onClick={imprimirFuncionarios} style={{ marginLeft: "auto" }}>🖨️ Imprimir</Btn>}
        <span style={{ color: COLORS.textMid, fontSize: 13 }}>
          {filtered.length} {abaFunc === "pre-cadastro" ? "pré-cadastro(s)" : "funcionário(s)"}
        </span>
      </div>

      {/* ── FORMULÁRIO INLINE ─────────────────────────────────── */}
      {modal && (
        <div style={{ background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 12, padding: 28, marginTop: 8 }}>
          {(() => { const isPrecadastro = !!form.pre_cadastro; return (
          <>
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 20 }}>
            <div>
              <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800 }}>
                {modal === "novo" ? (isPrecadastro ? "Novo Pré Cadastro" : "Novo Funcionário") : (isPrecadastro ? "Editar Pré Cadastro" : "Editar Funcionário")}
              </h2>
              {isPrecadastro && <div style={{ fontSize: 12, color: "#92400e", background: "#fef3c7", borderRadius: 6, padding: "2px 10px", marginTop: 4, display: "inline-block", fontWeight: 600 }}>Pré Cadastro — não aparece em Ponto, VT ou VR</div>}
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              {isPrecadastro && modal !== "novo" && canEdit && (
                <Btn onClick={() => setForm(f => ({ ...f, pre_cadastro: 0 }))} style={{ background: "#16a34a", color: "#fff" }}>
                  ✅ Efetivar Funcionário
                </Btn>
              )}
              <Btn variant="ghost" onClick={() => setModal(null)}>← Voltar</Btn>
            </div>
          </div>

          {/* Abas do modal — Cadastro | Histórico */}
          {form.id && (
            <div style={{ display: "flex", gap: 0, borderBottom: `2px solid ${COLORS.border}`, marginBottom: 20 }}>
              {[
                { key: "cadastro", label: "📋 Dados Cadastrais" },
                { key: "historico", label: "📈 Histórico" },
              ].map(ab => (
                <button key={ab.key} onClick={() => {
                  setAbaModalFunc(ab.key);
                  if (ab.key === "historico" && form.id && histFunc.length === 0 && !histLoading) carregarHist(form.id);
                }} type="button" style={{
                  padding: "9px 20px", fontSize: 13, fontWeight: abaModalFunc === ab.key ? 800 : 500,
                  border: "none", borderBottom: abaModalFunc === ab.key ? `3px solid ${COLORS.primary}` : "3px solid transparent",
                  background: "none", color: abaModalFunc === ab.key ? COLORS.primary : COLORS.textMid,
                  cursor: "pointer", marginBottom: -2, transition: "all .15s"
                }}>{ab.label}</button>
              ))}
            </div>
          )}

          {/* ── ABA CADASTRO ── */}
          {(abaModalFunc === "cadastro" || !form.id) && <>

          {/* Dados Pessoais */}
          <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase" }}>👤 Dados Pessoais</div>
            {form.id && <span style={{ fontSize: 12, color: COLORS.textMid, background: COLORS.bg, border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: "2px 8px" }}>ID: {form.id}</span>}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <Input label="Nome completo" value={form.nome || ""} onChange={e => {
              const nome = e.target.value;
              const partes = nome.trim().toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z\s]/g, "").split(/\s+/).filter(Boolean);
              const login = partes.length >= 2 ? `${partes[0]}.${partes[partes.length - 1]}` : partes[0] || "";
              const dominio = tenantDominio || "facilitae.com.br";
              setForm(f => ({ ...f, nome, login_usuario: login ? `${login}@${dominio}` : "" }));
            }} />
            <Input label="Telefone" value={form.telefone || ""} onChange={e => setForm(f => ({ ...f, telefone: e.target.value }))} />
            <Input label="E-mail" type="email" value={form.email || ""} onChange={e => setForm(f => ({ ...f, email: e.target.value }))} placeholder="email@exemplo.com" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <Input label="Data de Nascimento" type="date" value={form.data_nascimento || ""} onChange={e => setForm(f => ({ ...f, data_nascimento: e.target.value }))} />
            <Input label="CPF" value={form.cpf || ""} onChange={e => setForm(f => ({ ...f, cpf: e.target.value }))} placeholder="000.000.000-00" />
            <Input label="RG" value={form.rg || ""} onChange={e => setForm(f => ({ ...f, rg: e.target.value }))} />
            <Input label="PIS" value={form.pis || ""} onChange={e => setForm(f => ({ ...f, pis: e.target.value }))} />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <Input label="CTPS" value={form.ctps || ""} onChange={e => setForm(f => ({ ...f, ctps: e.target.value }))} />
            <Input label="Título de Eleitor" value={form.titulo_eleitor || ""} onChange={e => setForm(f => ({ ...f, titulo_eleitor: e.target.value }))} />
            <Input label="Matrícula" value={form.matricula || ""} onChange={e => setForm(f => ({ ...f, matricula: e.target.value }))} />
            <Input label="CNH (nº carteira)" value={form.cnh || ""} onChange={e => setForm(f => ({ ...f, cnh: e.target.value }))} placeholder="Ex: 00000000000" />
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 14 }}>
            <Input label="Nome do Pai" value={form.nome_pai || ""} onChange={e => setForm(f => ({ ...f, nome_pai: e.target.value }))} />
            <Input label="Nome da Mãe" value={form.nome_mae || ""} onChange={e => setForm(f => ({ ...f, nome_mae: e.target.value }))} />
            <Select label="Estado Civil" value={form.estado_civil || ""} onChange={e => setForm(f => ({ ...f, estado_civil: e.target.value }))}>
              <option value="">Selecione...</option>
              <option>Solteiro(a)</option><option>Casado(a)</option><option>Divorciado(a)</option><option>Viúvo(a)</option><option>União Estável</option>
            </Select>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 22 }}>
              <input type="checkbox" id="adiantamento" checked={!!form.adiantamento} onChange={e => setForm(f => ({ ...f, adiantamento: e.target.checked ? 1 : 0 }))} style={{ width: 16, height: 16 }} />
              <label htmlFor="adiantamento" style={{ fontSize: 13, color: COLORS.text, cursor: "pointer" }}>Adiantamento (40%)</label>
            </div>
          </div>
          {/* Insalubridade NR-15 */}
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMid, marginBottom: 6, textTransform: "uppercase", letterSpacing: 0.5 }}>Insalubridade (NR-15)</div>
            <div style={{ display: "flex", gap: 16, flexWrap: "wrap" }}>
              {[{val:"",label:"Nenhuma"},{val:"minimo",label:"Grau Mínimo — 10% (R$ 162,10)"},{val:"medio",label:"Grau Médio — 20% (R$ 324,20)"},{val:"maximo",label:"Grau Máximo — 40% (R$ 648,40)"}].map(opt => (
                <label key={opt.val} style={{ display: "flex", alignItems: "center", gap: 6, fontSize: 13, color: COLORS.text, cursor: "pointer" }}>
                  <input type="radio" name="insalubridade_grau" value={opt.val}
                    checked={(form.insalubridade_grau || "") === opt.val}
                    onChange={() => setForm(f => ({ ...f, insalubridade_grau: opt.val }))}
                    style={{ width: 15, height: 15 }} />
                  {opt.label}
                </label>
              ))}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 10 }}>
            <Input label="Endereço residencial" value={form.endereco || ""} onChange={e => setForm(f => ({ ...f, endereco: e.target.value }))} placeholder="Rua, número, bairro..." />
            <Input label="Cidade" value={form.cidade_moradia || ""} onChange={e => setForm(f => ({ ...f, cidade_moradia: e.target.value }))} placeholder="Ex: Belo Horizonte" />
          </div>
          {/* Dados do Trabalho — oculto no pré-cadastro */}
          {!isPrecadastro && <><div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase" }}>💼 Dados do Trabalho</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Select label="Empresa" value={form.empresa || ""} onChange={e => setForm(f => ({ ...f, empresa: e.target.value }))}>
              <option value="">Selecione...</option>
              {empresas.map(em => <option key={em.id} value={em.nome}>{em.nome}</option>)}
            </Select>
            <Input label="Cargo" value={form.cargo || ""} onChange={e => setForm(f => ({ ...f, cargo: e.target.value }))} />
            <Select label="Status" value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))}>
              <option>Ativo</option><option>Férias</option><option>Afastado</option><option>Demitido</option>
            </Select>
            {(() => {
              const grupos = [...new Set(employees.map(e => e.grupo).filter(Boolean))].sort();
              const subGrupos = [...new Set(employees.filter(e => !form.grupo || e.grupo === form.grupo).map(e => e.sub_grupo).filter(Boolean))].sort();
              const setoresSug = [...new Set(employees.filter(e => (!form.grupo || e.grupo === form.grupo) && (!form.sub_grupo || e.sub_grupo === form.sub_grupo)).map(e => e.setor).filter(Boolean))].sort();
              return (<>
                <datalist id="dl-grupo">{grupos.map(g => <option key={g} value={g} />)}</datalist>
                <datalist id="dl-subgrupo">{subGrupos.map(g => <option key={g} value={g} />)}</datalist>
                <datalist id="dl-setor">{setoresSug.map(g => <option key={g} value={g} />)}</datalist>
                <Input label="Grupo" value={form.grupo || ""} list="dl-grupo"
                  onChange={e => setForm(f => ({ ...f, grupo: e.target.value, sub_grupo: "", setor: "" }))}
                  placeholder="Ex: Limpeza, Portaria..." />
                <Input label="Sub Grupo" value={form.sub_grupo || ""} list="dl-subgrupo"
                  onChange={e => setForm(f => ({ ...f, sub_grupo: e.target.value, setor: "" }))}
                  placeholder="Ex: Diurno, Noturno..." />
                <Input label="Setor" value={form.setor || ""} list="dl-setor"
                  onChange={e => setForm(f => ({ ...f, setor: e.target.value }))}
                  placeholder="Ex: Bloco A, Portaria Principal..." />
              </>);
            })()}
            <Input label="Data de Admissão" type="date" value={form.admissao || ""} onChange={e => setForm(f => ({ ...f, admissao: e.target.value }))} />
            <Input label="Salário (R$)" type="number" value={form.salario || ""} onChange={e => setForm(f => ({ ...f, salario: e.target.value }))} />
            <Input label="Venc. ASO" type="date" value={form.docs?.aso || ""} onChange={e => setForm(f => ({ ...f, docs: { ...f.docs, aso: e.target.value } }))} />
            <Select label="Contrato Vinculado" value={form.contrato_id || ""} onChange={e => setForm(f => ({ ...f, contrato_id: e.target.value }))}>
              <option value="">Sem contrato</option>
              {contratos.filter(c => c.status === "ativo").map(c => (
                <option key={c.id} value={c.id}>{c.numero ? `${c.numero} — ` : ""}{c.cliente_nome}{c.objeto ? ` (${c.objeto.slice(0,40)})` : ""}</option>
              ))}
            </Select>
            <div style={{ display: "flex", alignItems: "center", gap: 8, paddingTop: 22 }}>
              <input type="checkbox" id="is12x36" checked={form.turno === "12X36"} onChange={e => {
                const checked = e.target.checked;
                setForm(f => ({ ...f, turno: checked ? "12X36" : "Diurno", dias_semana: checked ? [0,1,2,3,4,5,6] : f.dias_semana }));
              }} style={{ width: 16, height: 16, cursor: "pointer" }} />
              <label htmlFor="is12x36" style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, cursor: "pointer" }}>Escala 12X36</label>
            </div>
            {form.turno !== "12X36" && (
              <Select label="Turno" value={form.turno || "Diurno"} onChange={e => setForm(f => ({ ...f, turno: e.target.value }))}>
                <option>Diurno</option><option>Noturno</option>
              </Select>
            )}
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 14, marginBottom: 20 }}>
            <Input label="Endereço comercial" value={form.endereco_trabalho || ""} onChange={e => setForm(f => ({ ...f, endereco_trabalho: e.target.value }))} placeholder="Local de trabalho..." />
            <Input label="Cidade" value={form.cidade || ""} onChange={e => setForm(f => ({ ...f, cidade: e.target.value }))} placeholder="Ex: Betim" />
          </div>
          </>}

          {/* Horário de Trabalho — oculto no pré-cadastro */}
          {!isPrecadastro && <><div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase" }}>⏰ Horário de Trabalho</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 12, alignItems: "end" }}>
            <Input label="Entrada" type="time" value={form.hora_entrada || "08:00"} onChange={e => setForm(f => ({ ...f, hora_entrada: e.target.value }))} />
            <Input label="Saída Almoço" type="time" value={form.hora_saida_almoco || ""} onChange={e => setForm(f => ({ ...f, hora_saida_almoco: e.target.value }))} />
            <Input label="Volta Almoço" type="time" value={form.hora_volta_almoco || ""} onChange={e => setForm(f => ({ ...f, hora_volta_almoco: e.target.value }))} />
            <Input label="Saída" type="time" value={form.hora_saida || "17:00"} onChange={e => setForm(f => ({ ...f, hora_saida: e.target.value }))} />
          </div>
          <div style={{ display: "flex", gap: 14, alignItems: "flex-end", flexWrap: "wrap", marginBottom: 20 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 }}>Dias de trabalho</div>
              <div style={{ display: "flex", gap: 6, flexWrap: "wrap" }}>
                {DIAS_SEMANA.map(d => {
                  const ativo = (form.dias_semana || []).includes(d.v);
                  return (
                    <button key={d.v} onClick={() => toggleDiaSemana(d.v)} type="button" style={{
                      padding: "7px 12px", borderRadius: 8, border: `2px solid ${ativo ? COLORS.primary : COLORS.border}`,
                      background: ativo ? COLORS.primary : COLORS.surface,
                      color: ativo ? "#fff" : COLORS.text, fontWeight: 700, fontSize: 13, cursor: "pointer"
                    }}>{d.n}</button>
                  );
                })}
              </div>
            </div>
            {(form.dias_semana || []).includes(6) && form.turno !== "12X36" && (
              <Input label="Horário Sábado (ex: 08:00-12:00)" value={form.horario_sabado || ""} onChange={e => setForm(f => ({ ...f, horario_sabado: e.target.value }))} placeholder="08:00-12:00" style={{ minWidth: 200 }} />
            )}
          </div>
          </>}

          {/* Vale Transporte */}
          <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase" }}>🚌 Vale Transporte</div>
              {isPrecadastro && <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>Visual apenas — não entra em cálculos</span>}
            </div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 8 }}>Tipos de VT (selecione um ou mais)</div>
            <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
              {VT_TIPOS_OPTS.map(opt => {
                const selecionados = parseVtTipos(form.vt_tipo);
                const ativo = selecionados.includes(opt.value);
                return (
                  <button key={opt.value} type="button" onClick={() => {
                    const atual = parseVtTipos(form.vt_tipo);
                    const novo = ativo ? atual.filter(v => v !== opt.value) : [...atual, opt.value];
                    setForm(f => ({ ...f, vt_tipo: joinVtTipos(novo) }));
                  }} style={{
                    padding: "6px 14px", borderRadius: 8, border: `2px solid ${ativo ? COLORS.primary : COLORS.border}`,
                    background: ativo ? COLORS.primaryBg : "#fff", color: ativo ? COLORS.primary : COLORS.textMid,
                    fontWeight: ativo ? 700 : 400, fontSize: 13, cursor: "pointer", transition: "all .15s"
                  }}>{opt.label}</button>
                );
              })}
              {parseVtTipos(form.vt_tipo).length > 0 && (
                <button type="button" onClick={() => setForm(f => ({ ...f, vt_tipo: "" }))}
                  style={{ padding: "6px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: "none", color: COLORS.danger, fontSize: 12, cursor: "pointer" }}>
                  Limpar
                </button>
              )}
            </div>
            {/* Detalhes por tipo de VT */}
            {parseVtTipos(form.vt_tipo).map(tipo => (
              <div key={tipo} style={{ marginTop: 12, background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "12px 14px" }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary, marginBottom: 8 }}>{TIPO_LABELS_VT[tipo] || tipo}</div>
                {!VT_SEM_VALOR.includes(tipo) && (
                  <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10 }}>
                    <Input label="N° Cartão" value={form[`vt_cartao_${tipo}`] || ""} onChange={e => setForm(f => ({ ...f, [`vt_cartao_${tipo}`]: e.target.value }))} placeholder="Número do cartão" />
                    <Input label="Valor Diário (R$)" type="number" value={form[`vt_valor_${tipo}`] || ""} onChange={e => setForm(f => ({ ...f, [`vt_valor_${tipo}`]: e.target.value }))} />
                    <Input label="Dias/mês" type="number" value={form[`vt_dias_${tipo}`] || ""} onChange={e => setForm(f => ({ ...f, [`vt_dias_${tipo}`]: e.target.value }))} />
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Vale Refeição */}
          <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 10 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
              <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase" }}>🍽️ Vale Refeição</div>
              {isPrecadastro && <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 6, padding: "2px 8px", fontWeight: 600 }}>Visual apenas — não entra em cálculos</span>}
            </div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Input label="VR Valor Diário (R$)" type="number" value={form.vr_dia || ""} onChange={e => setForm(f => ({ ...f, vr_dia: e.target.value }))} />
            <Input label="VR Dias/mês" type="number" value={form.vr_dias || ""} onChange={e => setForm(f => ({ ...f, vr_dias: e.target.value }))} />
          </div>

          {/* Dependentes / Filhos — oculto no pré-cadastro */}
          {!isPrecadastro && <><div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase" }}>👶 Dependentes / Filhos</div>
          </div>
          <div style={{ marginBottom: 20 }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 12 }}>
              <span style={{ fontSize: 13, color: COLORS.text }}>Quantidade de filhos:</span>
              <input type="number" min="0" max="20" value={filhos.length}
                onChange={e => {
                  const n = Math.max(0, parseInt(e.target.value) || 0);
                  if (n > filhos.length) setFilhos(prev => [...prev, ...Array(n - prev.length).fill(null).map(() => ({ nome: "", cpf: "", data_nascimento: "" }))]);
                  else setFilhos(prev => prev.slice(0, n));
                }}
                style={{ width: 60, padding: "5px 8px", borderRadius: 6, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface }}
              />
            </div>
            {filhos.map((f, i) => (
              <div key={i} style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr auto", gap: 10, marginBottom: 8, alignItems: "end" }}>
                <Input label={`Nome do filho ${i + 1}`} value={f.nome || ""} onChange={e => setFilhos(prev => prev.map((x, j) => j === i ? { ...x, nome: e.target.value } : x))} />
                <Input label="CPF" value={f.cpf || ""} onChange={e => setFilhos(prev => prev.map((x, j) => j === i ? { ...x, cpf: e.target.value } : x))} placeholder="000.000.000-00" />
                <Input label="Data de Nascimento" type="date" value={f.data_nascimento || ""} onChange={e => setFilhos(prev => prev.map((x, j) => j === i ? { ...x, data_nascimento: e.target.value } : x))} />
                <button onClick={() => setFilhos(prev => prev.filter((_, j) => j !== i))}
                  style={{ padding: "6px 10px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#dc2626", cursor: "pointer", fontWeight: 700, marginBottom: 2 }}>✕</button>
              </div>
            ))}
            {filhos.length > 0 && (() => {
              const hoje = new Date();
              const elegiveis = filhos.filter(f => {
                if (!f.data_nascimento) return false;
                const nasc = new Date(f.data_nascimento);
                return (hoje - nasc) / (1000 * 60 * 60 * 24 * 365.25) < 14;
              });
              const salario = parseFloat(form.salario) || 0;
              const temDireito = elegiveis.length > 0 && salario > 0 && salario <= 1980.38;
              return (
                <div style={{ marginTop: 8, padding: "10px 14px", borderRadius: 8, background: temDireito ? "#f0fdf4" : "#f9fafb", border: `1px solid ${temDireito ? "#86efac" : COLORS.border}`, fontSize: 13 }}>
                  {elegiveis.length > 0 ? (
                    temDireito
                      ? <span style={{ color: "#16a34a", fontWeight: 700 }}>✅ Salário Família: {elegiveis.length} filho(s) menor(es) de 14 anos × R$ 67,54 = <strong>R$ {(elegiveis.length * 67.54).toFixed(2)}</strong></span>
                      : <span style={{ color: COLORS.textMid }}>⚠️ {elegiveis.length} filho(s) menor(es) de 14 anos, mas salário R$ {salario.toFixed(2)} excede o teto de R$ 1.980,38 — sem direito ao Salário Família.</span>
                  ) : (
                    <span style={{ color: COLORS.textMid }}>Nenhum filho menor de 14 anos cadastrado.</span>
                  )}
                </div>
              );
            })()}
          </div>
          </> /* fim bloco Dependentes */ }

          {/* Dados Bancários — multi-conta */}
          <ContasBancarias funcId={form.id} token={token}
            onDraftChange={setContasBancariasRascunho}
            onSaved={() => {
              apiFetch("GET", "/api/funcionarios", undefined, token).then(r => { if (Array.isArray(r)) setEmployees(r.map(adaptFuncionario)); }).catch(()=>{});
            }} />

          {/* Uniforme */}
          <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 14, marginBottom: 10 }}>
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase" }}>👕 Tamanhos de Uniforme</div>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr 1fr", gap: 14, marginBottom: 20 }}>
            <Input label="Camisa" value={form.tam_camisa || ""} onChange={e => setForm(f => ({ ...f, tam_camisa: e.target.value }))} placeholder="P, M, G, GG..." />
            <Input label="Calça" value={form.tam_calca || ""} onChange={e => setForm(f => ({ ...f, tam_calca: e.target.value }))} placeholder="38, 40, 42..." />
            <Input label="Sapato" value={form.tam_sapato || ""} onChange={e => setForm(f => ({ ...f, tam_sapato: e.target.value }))} placeholder="36, 38, 40..." />
            <Input label="Bota" value={form.tam_bota || ""} onChange={e => setForm(f => ({ ...f, tam_bota: e.target.value }))} placeholder="36, 38, 40..." />
          </div>

          {/* Acesso ao Sistema — só no cadastro novo de efetivo */}
          {modal === "novo" && !isPrecadastro && (
            <div style={{ marginTop: 12, background: COLORS.primaryBg, border: `1.5px solid ${COLORS.primary}`, borderRadius: 10, padding: "14px 16px" }}>
              <div style={{ fontSize: 12, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase", marginBottom: 10 }}>🔐 Acesso ao Sistema</div>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 14 }}>
                <div>
                  <Input label="Login" value={form.login_usuario || ""} onChange={e => setForm(f => ({ ...f, login_usuario: e.target.value }))} placeholder="gerado automaticamente pelo nome" />
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 3 }}>Gerado pelo nome — pode editar</div>
                </div>
                <div>
                  <Input label="Senha ✱ obrigatória para criar login" type="password" value={form.senha_acesso || ""} onChange={e => setForm(f => ({ ...f, senha_acesso: e.target.value }))} placeholder="Defina uma senha" />
                  <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 3 }}>Sem senha = não cria o login</div>
                </div>
              </div>
            </div>
          )}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 24 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={save}>Salvar</Btn>
          </div>
          </>}

          {/* ── ABA HISTÓRICO ── */}
          {abaModalFunc === "historico" && form.id && (() => {
            const HIST_ICONS = { salario: "💰", cargo: "💼", empresa: "🏢", status: "🔄", setor: "📍", turno: "⏰", observacao: "📝" };
            const HIST_LABELS = { salario: "Salário", cargo: "Cargo", empresa: "Empresa", status: "Status", setor: "Setor", turno: "Turno", observacao: "Observação" };
            const fmtData = (d) => {
              if (!d) return "";
              const dt = new Date(d);
              return dt.toLocaleDateString("pt-BR") + " " + dt.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });
            };
            const fmtSal = (v) => {
              const n = parseFloat(v);
              return isNaN(n) ? (v || "—") : "R$ " + n.toLocaleString("pt-BR", { minimumFractionDigits: 2 });
            };
            return (
              <div>
                {/* Adicionar observação manual */}
                {canEdit && (
                  <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 10, padding: "14px 16px", marginBottom: 20 }}>
                    <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.textMid, marginBottom: 8 }}>Adicionar observação ao histórico</div>
                    <div style={{ display: "flex", gap: 8 }}>
                      <input
                        type="text"
                        value={formHist.descricao}
                        onChange={e => setFormHist({ descricao: e.target.value })}
                        onKeyDown={e => e.key === "Enter" && salvarHist(form.id)}
                        placeholder="Ex: Promovido a supervisor, retornou de férias..."
                        style={{ flex: 1, padding: "8px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 13, background: COLORS.white }}
                      />
                      <Btn onClick={() => salvarHist(form.id)} disabled={savingHist || !formHist.descricao.trim()}>
                        {savingHist ? "..." : "+ Registrar"}
                      </Btn>
                    </div>
                  </div>
                )}

                {/* Timeline */}
                {histLoading ? (
                  <div style={{ textAlign: "center", padding: 32, color: COLORS.textMid }}>Carregando histórico...</div>
                ) : histFunc.length === 0 ? (
                  <div style={{ textAlign: "center", padding: 40, color: COLORS.textLight }}>
                    <div style={{ fontSize: 36, marginBottom: 8 }}>📋</div>
                    <div style={{ fontSize: 14, fontWeight: 600 }}>Nenhum histórico registrado</div>
                    <div style={{ fontSize: 12, marginTop: 4 }}>Alterações de salário, cargo e empresa serão registradas automaticamente a partir de agora.</div>
                  </div>
                ) : (
                  <div style={{ position: "relative", paddingLeft: 28 }}>
                    {/* linha vertical */}
                    <div style={{ position: "absolute", left: 10, top: 4, bottom: 4, width: 2, background: COLORS.border }} />
                    {histFunc.map(h => {
                      const icon = HIST_ICONS[h.tipo] || "📌";
                      const label = HIST_LABELS[h.tipo] || h.tipo;
                      const isSal = h.tipo === "salario";
                      const isObs = h.tipo === "observacao";
                      return (
                        <div key={h.id} style={{ position: "relative", marginBottom: 18 }}>
                          {/* bolinha */}
                          <div style={{
                            position: "absolute", left: -28, top: 12,
                            width: 22, height: 22, borderRadius: "50%",
                            background: isObs ? COLORS.surface : (isSal ? "#f0fdf4" : COLORS.primaryBg),
                            border: `2px solid ${isObs ? COLORS.border : (isSal ? "#86efac" : COLORS.primary)}`,
                            display: "flex", alignItems: "center", justifyContent: "center", fontSize: 11,
                            zIndex: 1
                          }}>{icon}</div>
                          <div style={{
                            background: COLORS.white, border: `1px solid ${COLORS.border}`, borderRadius: 10,
                            padding: "12px 14px", marginLeft: 2
                          }}>
                            <div style={{ display: "flex", alignItems: "flex-start", justifyContent: "space-between", gap: 8 }}>
                              <div style={{ flex: 1 }}>
                                <span style={{ fontSize: 12, fontWeight: 700, color: COLORS.primary }}>{label}</span>
                                {!isObs && h.valor_anterior !== undefined && h.valor_novo !== undefined && (
                                  <span style={{ fontSize: 12, color: COLORS.textMid, marginLeft: 8 }}>
                                    <span style={{ textDecoration: "line-through", color: COLORS.textLight }}>
                                      {isSal ? fmtSal(h.valor_anterior) : (h.valor_anterior || "—")}
                                    </span>
                                    {" → "}
                                    <strong style={{ color: isSal ? "#16a34a" : COLORS.text }}>
                                      {isSal ? fmtSal(h.valor_novo) : (h.valor_novo || "—")}
                                    </strong>
                                    {isSal && h.valor_anterior && h.valor_novo && (() => {
                                      const diff = parseFloat(h.valor_novo) - parseFloat(h.valor_anterior);
                                      if (!isNaN(diff) && diff !== 0) return (
                                        <span style={{ marginLeft: 6, fontSize: 11, color: diff > 0 ? "#16a34a" : "#dc2626", fontWeight: 700 }}>
                                          ({diff > 0 ? "+" : ""}R$ {diff.toLocaleString("pt-BR", { minimumFractionDigits: 2 })})
                                        </span>
                                      );
                                    })()}
                                  </span>
                                )}
                                {isObs && h.descricao && (
                                  <div style={{ fontSize: 13, color: COLORS.text, marginTop: 4 }}>{h.descricao}</div>
                                )}
                              </div>
                              <div style={{ display: "flex", alignItems: "center", gap: 8, flexShrink: 0 }}>
                                <div style={{ textAlign: "right" }}>
                                  <div style={{ fontSize: 11, color: COLORS.textLight }}>{fmtData(h.criado_em)}</div>
                                  {h.usuario && <div style={{ fontSize: 11, color: COLORS.textLight }}>{h.usuario}</div>}
                                </div>
                                {canEdit && (
                                  <button onClick={() => deletarHist(form.id, h.id)} type="button" title="Remover"
                                    style={{ padding: "3px 7px", borderRadius: 6, border: "none", background: "#fee2e2", color: "#dc2626", cursor: "pointer", fontSize: 11 }}>✕</button>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            );
          })()}

          </> ); })()}
        </div>
      )}

      {/* ── TABELA ────────────────────────────────────────────── */}
      {!modal && abaFunc === "efetivos" && (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
              {["Nome", "Admissão", "Salário"].map(h => {
                const ativo = sortCol === h;
                return (
                  <th key={h} onClick={() => handleSort(h)} style={{
                    padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, fontSize: 13,
                    cursor: "pointer", userSelect: "none", whiteSpace: "nowrap",
                    background: ativo ? "#2d5a9e" : undefined,
                  }}>
                    {h}
                    <span style={{ marginLeft: 5, opacity: ativo ? 1 : 0.6, fontSize: 11 }}>
                      {ativo ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}
                    </span>
                  </th>
                );
              })}
              <ThFilter label="Empresa"  value={filtroEmpresa}  onChange={setFiltroEmpresa}  options={optsEmpresa}  multi />
              <ThFilter label="Cargo"    value={filtroCargo}    onChange={setFiltroCargo}    options={optsCargo}    multi />
              <ThFilter label="Setor"    value={filtroSetor}    onChange={setFiltroSetor}    options={optsSetor}    multi />
              <ThFilter label="Subsetor" value={filtroSubsetor} onChange={setFiltroSubsetor} options={optsSubsetor} multi />
              <ThFilter label="Turno"    value={filtroTurno}    onChange={setFiltroTurno}    options={optsTurno}    multi />
              <ThFilter label="Status"   value={filtroStatus}   onChange={setFiltroStatus}   options={optsStatus}   multi />
              <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, fontSize: 13 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr key={e.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray }}>
                <td style={{ padding: "11px 14px", fontWeight: 700 }}>{e.nome}</td>
                <td style={{ padding: "11px 14px" }}>{new Date(e.admissao + "T00:00:00").toLocaleDateString("pt-BR")}</td>
                <td style={{ padding: "11px 14px", fontWeight: 700, color: COLORS.primary }}>R$ {e.salario.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</td>
                <td style={{ padding: "11px 14px" }}>{e.empresa ? <Badge color={COLORS.info}>{e.empresa}</Badge> : <span style={{ color: COLORS.textLight }}>—</span>}</td>
                <td style={{ padding: "11px 14px", color: COLORS.textMid }}>{e.cargo}</td>
                <td style={{ padding: "11px 14px" }}>{e.setor}</td>
                <td style={{ padding: "11px 14px", color: COLORS.textMid }}>{e.subsetor || "—"}</td>
                <td style={{ padding: "11px 14px" }}>{e.turno}</td>
                <td style={{ padding: "11px 14px" }}>
                  <Badge color={statusColor(e.status)}>{e.status}</Badge>
                  {e.status === "Demitido" && e.dataDemissao && (
                    <div style={{ fontSize: 11, color: COLORS.textMid, marginTop: 3 }}>
                      em {new Date(e.dataDemissao + "T00:00:00").toLocaleDateString("pt-BR")}
                    </div>
                  )}
                </td>
                <td style={{ padding: "11px 14px" }}>
                  {canEdit && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn size="sm" variant="ghost" onClick={() => openEdit(e)}>✏️</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => openDocs(e)} title="Documentos" style={{ fontSize: 16 }}>📁</Btn>
                      {e.status !== "Demitido" && (
                        <Btn size="sm" variant="warning" onClick={() => { setModalDemitir(e); setDataDemissao(new Date().toISOString().split("T")[0]); }}
                          style={{ background: COLORS.warning + "22", color: COLORS.warning, border: `1px solid ${COLORS.warning}44` }}>
                          Demitir
                        </Btn>
                      )}
                      <Btn size="sm" variant="danger" onClick={() => setConfirmExcluir(e)}>Excluir</Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      )}

      {/* ── TABELA PRÉ CADASTRO ───────────────────────────────── */}
      {!modal && abaFunc === "pre-cadastro" && (
      <div style={{ overflowX: "auto" }}>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
              {["Nome", "CPF", "Telefone", "E-mail"].map(h => (
                <th key={h} style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, fontSize: 13, textAlign: "left" }}>{h}</th>
              ))}
              <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, fontSize: 13 }}>Ações</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map((e, i) => (
              <tr key={e.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray }}>
                <td style={{ padding: "11px 14px", fontWeight: 700 }}>{e.nome}</td>
                <td style={{ padding: "11px 14px", color: COLORS.textMid }}>{e.cpf || "—"}</td>
                <td style={{ padding: "11px 14px" }}>{e.telefone || "—"}</td>
                <td style={{ padding: "11px 14px", color: COLORS.textMid }}>{e.email || "—"}</td>
                <td style={{ padding: "11px 14px" }}>
                  {canEdit && (
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn size="sm" variant="edit" onClick={() => openEdit(e)}>✏️ Editar</Btn>
                      <Btn size="sm" onClick={() => { openEdit(e); setForm(f => ({ ...f, pre_cadastro: 0 })); }}
                        style={{ background: "#16a34a", color: "#fff", fontSize: 12 }}>✅ Efetivar</Btn>
                      <Btn size="sm" variant="danger" onClick={() => setConfirmExcluir(e)}>Excluir</Btn>
                    </div>
                  )}
                </td>
              </tr>
            ))}
            {filtered.length === 0 && (
              <tr><td colSpan={5} style={{ textAlign: "center", padding: 24, color: COLORS.textMid }}>Nenhum pré-cadastro encontrado.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      )}

      {/* Modal demissão */}
      {modalDemitir && (
        <Modal title="Registrar Demissão" onClose={() => setModalDemitir(null)}>
          <div style={{ marginBottom: 16, color: COLORS.textDark, fontSize: 14 }}>
            Informe a data de demissão de <strong>{modalDemitir.nome}</strong>. O funcionário será marcado como <Badge color={COLORS.danger}>Demitido</Badge> e seu histórico será mantido.
          </div>
          <Input label="Data de Demissão" type="date" value={dataDemissao} onChange={e => setDataDemissao(e.target.value)} />
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setModalDemitir(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={confirmarDemissao}>Confirmar Demissão</Btn>
          </div>
        </Modal>
      )}

      {/* Modal confirmação de exclusão */}
      {confirmExcluir && (
        <Modal title="⚠️ Excluir Funcionário" onClose={() => setConfirmExcluir(null)}>
          <div style={{ background: COLORS.danger + "11", border: `1px solid ${COLORS.danger}44`, borderRadius: 10, padding: 16, marginBottom: 16 }}>
            <div style={{ fontWeight: 800, color: COLORS.danger, marginBottom: 8, fontSize: 15 }}>
              Esta ação é irreversível!
            </div>
            <div style={{ color: COLORS.textDark, fontSize: 14, lineHeight: 1.6 }}>
              Ao excluir <strong>{confirmExcluir.nome}</strong>, serão removidos permanentemente:
              <ul style={{ margin: "8px 0 0 16px", padding: 0 }}>
                <li>Cadastro do funcionário</li>
                <li>Todo o histórico de ponto</li>
                <li>Todos os afastamentos e férias</li>
                <li>Todos os registros de escala</li>
              </ul>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Btn variant="ghost" onClick={() => setConfirmExcluir(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={() => excluirFuncionario(confirmExcluir.id)}>Excluir Permanentemente</Btn>
          </div>
        </Modal>
      )}

      {/* Modal Documentos — Gerenciador de Arquivos */}
      {modalDocs && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(15,23,42,0.55)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center", backdropFilter: "blur(4px)", padding: 16 }}>
          <div style={{ background: "#fff", borderRadius: 16, width: "100%", maxWidth: 720, maxHeight: "90vh", display: "flex", flexDirection: "column", boxShadow: "0 24px 80px rgba(15,23,42,0.28)" }}>

            {/* Header */}
            <div style={{ padding: "18px 24px 0", flexShrink: 0 }}>
              <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", marginBottom: 10 }}>
                <div>
                  <div style={{ fontWeight: 800, fontSize: 16, color: COLORS.text }}>📁 Documentos</div>
                  <div style={{ fontSize: 12, color: COLORS.textMid, marginTop: 2 }}>{modalDocs.nome}</div>
                </div>
                <button onClick={() => setModalDocs(null)} style={{ background: "none", border: "none", fontSize: 20, cursor: "pointer", color: COLORS.textMid, padding: "0 4px" }}>✕</button>
              </div>
              {/* Safety notice */}
              <div style={{ background: "#f0fdf4", border: "1px solid #86efac", borderRadius: 8, padding: "6px 12px", fontSize: 11, color: "#166534", marginBottom: 12, display: "flex", alignItems: "center", gap: 6 }}>
                <span>🔒</span>
                <span><strong>Arquivos protegidos.</strong> Armazenados fora da área de deploy — nunca apagados por atualizações do sistema.</span>
              </div>
              {/* Breadcrumb */}
              <div style={{ display: "flex", alignItems: "center", gap: 4, fontSize: 13, marginBottom: 12, flexWrap: "wrap" }}>
                <button onClick={() => docVoltar(-1)} style={{ background: "none", border: "none", cursor: "pointer", color: docBreadcrumb.length === 0 ? COLORS.primary : COLORS.textMid, fontWeight: docBreadcrumb.length === 0 ? 700 : 400, padding: "2px 4px", fontSize: 13 }}>🏠 Raiz</button>
                {docBreadcrumb.map((p, idx) => (
                  <span key={p.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                    <span style={{ color: COLORS.textMid }}>›</span>
                    <button onClick={() => docVoltar(idx)} style={{ background: "none", border: "none", cursor: "pointer", color: idx === docBreadcrumb.length - 1 ? COLORS.primary : COLORS.textMid, fontWeight: idx === docBreadcrumb.length - 1 ? 700 : 400, padding: "2px 4px", fontSize: 13 }}>{p.nome}</button>
                  </span>
                ))}
              </div>
              {/* Toolbar */}
              <div style={{ display: "flex", gap: 8, marginBottom: 12, flexWrap: "wrap", alignItems: "center" }}>
                {!docCriandoPasta ? (
                  <button onClick={() => setDocCriandoPasta(true)}
                    style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 7, padding: "6px 14px", fontSize: 12, cursor: "pointer", color: COLORS.text, fontWeight: 600, display: "flex", alignItems: "center", gap: 6 }}>
                    📂 Nova Pasta
                  </button>
                ) : (
                  <div style={{ display: "flex", gap: 6, alignItems: "center" }}>
                    <input autoFocus value={docNovaPasta} onChange={e => setDocNovaPasta(e.target.value)}
                      onKeyDown={e => { if (e.key === "Enter") docCriarPasta(); if (e.key === "Escape") { setDocCriandoPasta(false); setDocNovaPasta(''); } }}
                      placeholder="Nome da pasta..." style={{ padding: "6px 10px", borderRadius: 7, border: `1px solid ${COLORS.primary}`, fontSize: 12, outline: "none", width: 160 }} />
                    <button onClick={docCriarPasta} style={{ background: COLORS.primary, border: "none", borderRadius: 7, padding: "6px 12px", fontSize: 12, cursor: "pointer", color: "#fff", fontWeight: 700 }}>Criar</button>
                    <button onClick={() => { setDocCriandoPasta(false); setDocNovaPasta(''); }} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textMid, fontSize: 14 }}>✕</button>
                  </div>
                )}
                <label style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}`, borderRadius: 7, padding: "6px 14px", fontSize: 12, cursor: uploadingDoc ? "not-allowed" : "pointer", color: COLORS.primary, fontWeight: 700, display: "flex", alignItems: "center", gap: 6 }}>
                  {uploadingDoc ? "⏳ Enviando..." : "📤 Enviar arquivo"}
                  <input type="file" accept=".pdf,.jpg,.jpeg,.png,.doc,.docx,.xls,.xlsx,.txt,.zip" style={{ display: "none" }} disabled={uploadingDoc}
                    onChange={e => { if (e.target.files[0]) uploadDoc(modalDocs.id, e.target.files[0]); e.target.value = ""; }} />
                </label>
                {docBreadcrumb.length > 0 && (
                  <span style={{ fontSize: 11, color: COLORS.textMid, marginLeft: 4 }}>
                    Enviando em: <strong>{docBreadcrumb[docBreadcrumb.length - 1].nome}</strong>
                  </span>
                )}
              </div>
              <div style={{ height: 1, background: COLORS.border, margin: "0 -24px 0" }} />
            </div>

            {/* Conteúdo */}
            <div style={{ flex: 1, overflowY: "auto", padding: "14px 24px 20px" }}>

              {/* Pastas */}
              {docPastas.length > 0 && (
                <div style={{ marginBottom: 16 }}>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>Pastas</div>
                  <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(150px, 1fr))", gap: 8 }}>
                    {docPastas.map(p => (
                      <div key={p.id}
                        onDragOver={e => { e.preventDefault(); setDocDragOver(p.id); }}
                        onDragLeave={() => setDocDragOver(null)}
                        onDrop={e => { e.preventDefault(); if (docDraggedFile) { docMoverArquivo(docDraggedFile, p.id); setDocDraggedFile(null); } setDocDragOver(null); }}
                        style={{ border: `2px solid ${docDragOver === p.id ? COLORS.primary : COLORS.border}`, borderRadius: 10, padding: "10px 12px", background: docDragOver === p.id ? COLORS.primaryBg : COLORS.surface, cursor: "pointer", transition: "all 0.15s", position: "relative" }}>
                        {docRenamePasta?.id === p.id ? (
                          <div style={{ display: "flex", flexDirection: "column", gap: 6 }} onClick={e => e.stopPropagation()}>
                            <input autoFocus value={docRenamePasta.nome} onChange={e => setDocRenamePasta(prev => ({ ...prev, nome: e.target.value }))}
                              onKeyDown={e => { if (e.key === "Enter") docRenomear(p.id, docRenamePasta.nome); if (e.key === "Escape") setDocRenamePasta(null); }}
                              style={{ padding: "4px 8px", borderRadius: 6, border: `1px solid ${COLORS.primary}`, fontSize: 12, outline: "none" }} />
                            <div style={{ display: "flex", gap: 4 }}>
                              <button onClick={() => docRenomear(p.id, docRenamePasta.nome)} style={{ background: COLORS.primary, border: "none", borderRadius: 5, padding: "3px 8px", fontSize: 11, color: "#fff", cursor: "pointer" }}>OK</button>
                              <button onClick={() => setDocRenamePasta(null)} style={{ background: "none", border: "none", color: COLORS.textMid, cursor: "pointer", fontSize: 12 }}>✕</button>
                            </div>
                          </div>
                        ) : (
                          <>
                            <div onClick={() => docNavegar(p)} style={{ display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 24 }}>📁</span>
                              <span style={{ fontSize: 12, fontWeight: 600, color: COLORS.text, wordBreak: "break-word", lineHeight: 1.3 }}>{p.nome}</span>
                            </div>
                            <div style={{ position: "absolute", top: 6, right: 6, display: "flex", gap: 2 }}>
                              <button onClick={e => { e.stopPropagation(); setDocRenamePasta({ id: p.id, nome: p.nome }); }} title="Renomear"
                                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: COLORS.textMid, padding: "1px 4px" }}>✏️</button>
                              <button onClick={e => { e.stopPropagation(); docExcluirPasta(p.id); }} title="Excluir pasta"
                                style={{ background: "none", border: "none", cursor: "pointer", fontSize: 12, color: COLORS.danger, padding: "1px 4px" }}>🗑️</button>
                            </div>
                          </>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
              )}

              {/* Arquivos */}
              {docArquivos.length > 0 && (
                <div>
                  <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: 1, marginBottom: 8 }}>
                    Arquivos {docPastas.length > 0 ? "" : ""} <span style={{ fontWeight: 400, textTransform: "none", letterSpacing: 0, fontSize: 11 }}>— arraste para uma pasta para mover</span>
                  </div>
                  <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
                    <tbody>
                      {docArquivos.map((d, i) => {
                        const icon = d.mime_type === "application/pdf" ? "📄" : d.mime_type?.startsWith("image") ? "🖼️" : d.mime_type?.includes("word") ? "📝" : d.mime_type?.includes("excel") || d.mime_type?.includes("sheet") ? "📊" : "📎";
                        return (
                          <tr key={d.id}
                            draggable
                            onDragStart={() => setDocDraggedFile(d.id)}
                            onDragEnd={() => setDocDraggedFile(null)}
                            style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray, borderBottom: `1px solid ${COLORS.border}`, cursor: "grab", opacity: docDraggedFile === d.id ? 0.5 : 1 }}>
                            <td style={{ padding: "9px 12px", fontWeight: 500, display: "flex", alignItems: "center", gap: 8 }}>
                              <span style={{ fontSize: 16 }}>{icon}</span>
                              <span style={{ wordBreak: "break-word" }}>{d.nome_original}</span>
                            </td>
                            <td style={{ padding: "9px 12px", color: COLORS.textMid, whiteSpace: "nowrap", fontSize: 12 }}>{fmtBytes(d.tamanho)}</td>
                            <td style={{ padding: "9px 12px", color: COLORS.textMid, whiteSpace: "nowrap", fontSize: 12 }}>{new Date(d.created_at).toLocaleDateString("pt-BR")}</td>
                            <td style={{ padding: "9px 12px", whiteSpace: "nowrap" }}>
                              <a href={`/api/documentos/arquivo/${d.id}/ver?token=${token}`} target="_blank" rel="noreferrer"
                                onClick={e => { e.preventDefault(); window.open(`/uploads/documentos/${modalDocs.id}/${d.nome_arquivo}`, "_blank"); }}
                                style={{ marginRight: 10, fontSize: 12, color: COLORS.primary, textDecoration: "none", fontWeight: 600 }}>Ver</a>
                              {docBreadcrumb.length > 0 && (
                                <button onClick={() => docMoverArquivo(d.id, null)} title="Mover para raiz"
                                  style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textMid, fontSize: 12, marginRight: 8 }}>↑ Raiz</button>
                              )}
                              <button onClick={() => deleteDoc(d.id)}
                                style={{ background: "none", border: "none", color: COLORS.danger, cursor: "pointer", fontSize: 12 }}>Excluir</button>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}

              {docPastas.length === 0 && docArquivos.length === 0 && (
                <div style={{ textAlign: "center", padding: "40px 20px", color: COLORS.textMid }}>
                  <div style={{ fontSize: 40, marginBottom: 10 }}>📭</div>
                  <div style={{ fontSize: 14 }}>Pasta vazia.</div>
                  <div style={{ fontSize: 12, marginTop: 4 }}>Crie subpastas ou envie arquivos aqui.</div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

    </div>
  );
}

// ─── PONTO ────────────────────────────────────────────────────────────────────
function PontoLancamentos({ employees, ponto, setPonto, token, usuario }) {
  const canEdit = ["admin","administrador","rh"].includes(usuario?.perfil);
  const canAdmin = ["admin","administrador"].includes(usuario?.perfil);

  const now = new Date();
  const [mesAno, setMesAno] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [funcId, setFuncId] = useState("");
  const [editForm, setEditForm] = useState({});
  const [showConfig, setShowConfig] = useState(false);
  const [configForm, setConfigForm] = useState({ ponto_api_url: "", ponto_api_email: "", ponto_api_senha: "" });
  const [hasConfig, setHasConfig] = useState(false);
  const [sincronizando, setSincronizando] = useState(false);
  const [syncMsg, setSyncMsg] = useState("");
  const [loading, setLoading] = useState(false);
  const [modalLancamento, setModalLancamento] = useState(null); // 'falta' | 'extra' | null
  const [lancamentoForm, setLancamentoForm] = useState({ quantidade: 1, observacao: "" });

  const [mesNum, anoNum] = mesAno.split('-').map(Number);

  useEffect(() => {
    if (!funcId && employees.length > 0) setFuncId(employees[0].id);
  }, [employees]);

  useEffect(() => {
    if (!token) return;
    setLoading(true);
    apiFetch("GET", "/api/ponto", undefined, token)
      .then(rows => setPonto(Array.isArray(rows) ? rows : []))
      .catch(() => {})
      .finally(() => setLoading(false));
  }, [token]);

  useEffect(() => {
    if (!canAdmin || !token) return;
    apiFetch("GET", "/api/integracoes/ponto/config", undefined, token)
      .then(data => {
        setHasConfig(!!data.hasConfig);
        if (data.ponto_api_url) setConfigForm(f => ({ ...f, ponto_api_url: data.ponto_api_url, ponto_api_email: data.ponto_api_email || "" }));
      })
      .catch(() => {});
  }, [token, canAdmin]);

  const func = employees.find(e => e.id == funcId);
  const registro = ponto.find(p => p.funcionario_id == funcId && p.mes == mesNum && p.ano == anoNum);

  const salvarRegistro = async () => {
    try {
      if (registro?.id) {
        await apiFetch("PUT", `/api/ponto/${registro.id}`, editForm, token);
        setPonto(prev => prev.map(p => p.id === registro.id ? { ...p, ...editForm } : p));
      } else {
        const novo = await apiFetch("POST", "/api/ponto", { funcionario_id: funcId, mes: mesNum, ano: anoNum, ...editForm }, token);
        setPonto(prev => [...prev, { ...novo, funcionario_id: parseInt(funcId), mes: mesNum, ano: anoNum, ...editForm }]);
      }
      setEditForm({});
      setSyncMsg("✅ Registro salvo.");
    } catch(e) { alert("Erro ao salvar: " + e.message); }
  };

  const salvarConfig = async () => {
    try {
      await apiFetch("POST", "/api/integracoes/ponto/config", configForm, token);
      setHasConfig(!!(configForm.ponto_api_url && configForm.ponto_api_email));
      setShowConfig(false);
      setSyncMsg("✅ Configuração da API salva.");
    } catch(e) { alert("Erro ao salvar configuração: " + e.message); }
  };

  const sincronizar = async () => {
    setSincronizando(true);
    setSyncMsg("");
    try {
      const res = await apiFetch("POST", "/api/integracoes/ponto/sincronizar", { mes: mesNum, ano: anoNum }, token);
      setSyncMsg(`✅ ${res.sincronizados} funcionário(s) sincronizado(s) de ${res.total} registros na API`);
      const rows = await apiFetch("GET", "/api/ponto", undefined, token);
      setPonto(Array.isArray(rows) ? rows : []);
    } catch(e) {
      setSyncMsg("❌ " + e.message);
    }
    setSincronizando(false);
  };

  const abrirLancamento = (tipo) => {
    setLancamentoForm({ quantidade: tipo === 'falta' ? 1 : 1, observacao: "" });
    setModalLancamento(tipo);
  };

  const salvarLancamento = async () => {
    try {
      const qtd = parseFloat(lancamentoForm.quantidade) || 0;
      if (qtd <= 0) { alert("Informe uma quantidade válida."); return; }
      const atual = {
        horas_normais: registro?.horas_normais || 0,
        horas_extras: registro?.horas_extras || 0,
        faltas: registro?.faltas || 0,
        observacao: registro?.observacao || "",
      };
      const novosDados = { ...atual };
      if (modalLancamento === 'falta') novosDados.faltas = parseFloat(atual.faltas) + qtd;
      if (modalLancamento === 'extra') novosDados.horas_extras = parseFloat(atual.horas_extras) + qtd;
      if (lancamentoForm.observacao) novosDados.observacao = lancamentoForm.observacao;

      if (registro?.id) {
        await apiFetch("PUT", `/api/ponto/${registro.id}`, novosDados, token);
        setPonto(prev => prev.map(p => p.id === registro.id ? { ...p, ...novosDados } : p));
      } else {
        const novo = await apiFetch("POST", "/api/ponto", { funcionario_id: funcId, mes: mesNum, ano: anoNum, ...novosDados }, token);
        setPonto(prev => [...prev, { ...novo, funcionario_id: parseInt(funcId), mes: mesNum, ano: anoNum, ...novosDados }]);
      }
      setModalLancamento(null);
      setSyncMsg(modalLancamento === 'falta' ? `✅ Falta de ${qtd} dia(s) lançada.` : `✅ ${qtd}h extra lançada(s).`);
    } catch(e) { alert("Erro ao lançar: " + e.message); }
  };

  const exportar = () => {
    const linhas = ["Funcionário,Mês,Ano,H.Normais,H.Extras,Faltas,Observação"];
    employees.forEach(e => {
      const reg = ponto.find(p => p.funcionario_id === e.id && p.mes == mesNum && p.ano == anoNum);
      if (reg) linhas.push(`${e.nome},${mesNum},${anoNum},${reg.horas_normais || 0},${reg.horas_extras || 0},${reg.faltas || 0},"${reg.observacao || ""}"`);
    });
    const blob = new Blob([linhas.join("\n")], { type: "text/csv" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `ponto_${mesAno}.csv`; a.click();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {/* Filter bar */}
      <div style={{ display: "flex", gap: 12, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Select label="Funcionário" value={funcId} onChange={e => setFuncId(e.target.value)} style={{ minWidth: 200 }}>
          {employees.filter(e => e.status !== "Demitido").map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </Select>
        <Input label="Mês" type="month" value={mesAno} onChange={e => setMesAno(e.target.value)} />
        <Btn variant="accent" onClick={exportar}>Exportar CSV</Btn>
        {canEdit && (
          <>
            <Btn variant="danger" onClick={() => abrirLancamento('falta')}>➕ Lançar Falta</Btn>
            <Btn variant="secondary" onClick={() => abrirLancamento('extra')}>⏱ Lançar H. Extra</Btn>
          </>
        )}
        {canAdmin && (
          <>
            {hasConfig && (
              <Btn variant="primary" onClick={sincronizar} disabled={sincronizando}>
                {sincronizando ? "⏳ Sincronizando..." : "🔄 Sincronizar API"}
              </Btn>
            )}
            <Btn variant="ghost" onClick={() => setShowConfig(v => !v)}>⚙️ Config API de Ponto</Btn>
          </>
        )}
      </div>

      {syncMsg && (
        <div style={{ background: syncMsg.startsWith("✅") ? COLORS.primaryBg : COLORS.dangerBg, border: `1px solid ${syncMsg.startsWith("✅") ? COLORS.primary : COLORS.danger}`, borderRadius: 8, padding: "10px 14px", fontSize: 13, color: syncMsg.startsWith("✅") ? COLORS.primaryDark : COLORS.danger }}>
          {syncMsg}
        </div>
      )}

      {/* Config panel */}
      {showConfig && canAdmin && (
        <Card style={{ border: `1px solid ${COLORS.border}` }}>
          <div style={{ fontWeight: 700, marginBottom: 12 }}>Integração com API de Ponto</div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap", marginBottom: 12 }}>
            <Input label="URL Base da API" value={configForm.ponto_api_url} onChange={e => setConfigForm(f => ({ ...f, ponto_api_url: e.target.value }))} style={{ minWidth: 260 }} placeholder="https://api.pontotel.com.br/v4" />
            <Input label="E-mail" value={configForm.ponto_api_email} onChange={e => setConfigForm(f => ({ ...f, ponto_api_email: e.target.value }))} style={{ minWidth: 200 }} />
            <Input label="Senha" type="password" value={configForm.ponto_api_senha} onChange={e => setConfigForm(f => ({ ...f, ponto_api_senha: e.target.value }))} style={{ minWidth: 160 }} placeholder="(manter em branco para não alterar)" />
          </div>
          <div style={{ display: "flex", gap: 8 }}>
            <Btn variant="primary" onClick={salvarConfig}>Salvar</Btn>
            <Btn variant="ghost" onClick={() => setShowConfig(false)}>Cancelar</Btn>
          </div>
          <div style={{ marginTop: 10, fontSize: 12, color: COLORS.textLight }}>
            Informe a URL base da API (ex: https://api.pontotel.com.br/v4). O sistema buscará funcionários por CPF e importará horas normais, extras e faltas do mês selecionado.
          </div>
        </Card>
      )}

      {/* Modal lançamento rápido */}
      {modalLancamento && (
        <Modal title={modalLancamento === 'falta' ? `Lançar Falta — ${func?.nome}` : `Lançar H. Extra — ${func?.nome}`} onClose={() => setModalLancamento(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <div style={{ fontSize: 13, color: COLORS.textMid }}>
              {modalLancamento === 'falta'
                ? `Faltas atuais: ${registro?.faltas || 0} dia(s)`
                : `Horas extras atuais: ${registro?.horas_extras || 0}h`}
              {" — "}{String(mesNum).padStart(2,'0')}/{anoNum}
            </div>
            <Input
              label={modalLancamento === 'falta' ? "Quantidade de dias" : "Quantidade de horas"}
              type="number"
              step={modalLancamento === 'falta' ? "0.5" : "0.5"}
              min="0.5"
              value={lancamentoForm.quantidade}
              onChange={e => setLancamentoForm(f => ({ ...f, quantidade: e.target.value }))}
            />
            <Input
              label="Observação (opcional)"
              value={lancamentoForm.observacao}
              onChange={e => setLancamentoForm(f => ({ ...f, observacao: e.target.value }))}
              placeholder={modalLancamento === 'falta' ? "ex: falta justificada, atestado..." : "ex: reunião emergencial, plantão..."}
            />
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant={modalLancamento === 'falta' ? "danger" : "secondary"} onClick={salvarLancamento}>
                {modalLancamento === 'falta' ? "Confirmar Falta" : "Confirmar H. Extra"}
              </Btn>
              <Btn variant="ghost" onClick={() => setModalLancamento(null)}>Cancelar</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Cards do funcionário selecionado */}
      {func && (
        <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
          <Card style={{ flex: 1, minWidth: 120 }}><div style={{ fontSize: 12, color: COLORS.textMid }}>Funcionário</div><div style={{ fontWeight: 800 }}>{func.nome}</div></Card>
          <Card style={{ flex: 1, minWidth: 100 }}><div style={{ fontSize: 12, color: COLORS.textMid }}>Cargo</div><div style={{ fontWeight: 800 }}>{func.cargo}</div></Card>
          <Card style={{ flex: 1, minWidth: 100 }}><div style={{ fontSize: 12, color: COLORS.textMid }}>Horas Normais</div><div style={{ fontWeight: 800, color: COLORS.primary }}>{registro?.horas_normais || 0}h</div></Card>
          <Card style={{ flex: 1, minWidth: 100 }}><div style={{ fontSize: 12, color: COLORS.textMid }}>Horas Extras</div><div style={{ fontWeight: 800, color: COLORS.accent }}>{registro?.horas_extras || 0}h</div></Card>
          <Card style={{ flex: 1, minWidth: 100 }}><div style={{ fontSize: 12, color: COLORS.textMid }}>Valor Extra (50%)</div><div style={{ fontWeight: 800, color: COLORS.secondary }}>R$ {(((func.salario || 0) / 220) * 1.5 * (registro?.horas_extras || 0)).toFixed(2)}</div></Card>
        </div>
      )}

      {/* Formulário de registro manual */}
      {canEdit && (
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 14 }}>
            Lançamento Manual — {func?.nome || "..."} — {String(mesNum).padStart(2,'0')}/{anoNum}
          </div>
          <div style={{ display: "flex", gap: 12, flexWrap: "wrap" }}>
            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 4 }}>Horas Normais</div>
              <input type="number" step="0.5" min="0"
                value={editForm.horas_normais !== undefined ? editForm.horas_normais : (registro?.horas_normais ?? "")}
                onChange={e => setEditForm(f => ({ ...f, horas_normais: parseFloat(e.target.value) || 0 }))}
                style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13.5, background: COLORS.white, color: COLORS.text, outline: "none" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 4 }}>Horas Extras</div>
              <input type="number" step="0.5" min="0"
                value={editForm.horas_extras !== undefined ? editForm.horas_extras : (registro?.horas_extras ?? "")}
                onChange={e => setEditForm(f => ({ ...f, horas_extras: parseFloat(e.target.value) || 0 }))}
                style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13.5, background: COLORS.white, color: COLORS.text, outline: "none" }}
              />
            </div>
            <div style={{ flex: 1, minWidth: 120 }}>
              <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 4 }}>Faltas (dias)</div>
              <input type="number" step="0.5" min="0"
                value={editForm.faltas !== undefined ? editForm.faltas : (registro?.faltas ?? "")}
                onChange={e => setEditForm(f => ({ ...f, faltas: parseFloat(e.target.value) || 0 }))}
                style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13.5, background: COLORS.white, color: COLORS.text, outline: "none" }}
              />
            </div>
            <div style={{ flex: 2, minWidth: 200 }}>
              <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 4 }}>Observação</div>
              <input type="text"
                value={editForm.observacao !== undefined ? editForm.observacao : (registro?.observacao ?? "")}
                onChange={e => setEditForm(f => ({ ...f, observacao: e.target.value }))}
                style={{ width: "100%", border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "8px 12px", fontSize: 13.5, background: COLORS.white, color: COLORS.text, outline: "none" }}
              />
            </div>
          </div>
          <div style={{ marginTop: 14 }}>
            <Btn variant="primary" onClick={salvarRegistro}>Salvar Registro</Btn>
          </div>
        </Card>
      )}

      {/* Resumo do mês — todos os funcionários */}
      <Card>
        <div style={{ fontWeight: 700, marginBottom: 14 }}>Resumo do Mês {String(mesNum).padStart(2,'0')}/{anoNum}</div>
        <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
          <thead>
            <tr style={{ background: COLORS.bg }}>
              {["Funcionário", "H. Normais", "H. Extras", "Faltas", "Valor Extra", "Observação"].map(h => (
                <th key={h} style={{ padding: "11px 14px", textAlign: "left" }}>{h}</th>
              ))}
            </tr>
          </thead>
          <tbody>
            {loading && <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: COLORS.textMid }}>Carregando...</td></tr>}
            {!loading && employees.filter(e => e.status !== "Demitido" && !e.pre_cadastro).map((e, i) => {
              const reg = ponto.find(p => p.funcionario_id === e.id && p.mes == mesNum && p.ano == anoNum);
              const valorExtra = ((e.salario || 0) / 220) * 1.5 * (reg?.horas_extras || 0);
              return (
                <tr key={e.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray }}>
                  <td style={{ padding: "11px 14px", fontWeight: 700 }}>{e.nome}</td>
                  <td style={{ padding: "11px 14px" }}>{reg?.horas_normais || 0}h</td>
                  <td style={{ padding: "11px 14px" }}><Badge color={(reg?.horas_extras || 0) > 0 ? COLORS.accent : COLORS.textMid}>{reg?.horas_extras || 0}h</Badge></td>
                  <td style={{ padding: "11px 14px" }}>{(reg?.faltas || 0) > 0 ? <Badge color={COLORS.danger}>{reg.faltas}</Badge> : "—"}</td>
                  <td style={{ padding: "11px 14px", color: valorExtra > 0 ? COLORS.accent : COLORS.textMid }}>R$ {valorExtra.toFixed(2)}</td>
                  <td style={{ padding: "11px 14px", color: COLORS.textLight, fontSize: 12 }}>{reg?.observacao || ""}</td>
                </tr>
              );
            })}
            {!loading && employees.filter(e => e.status !== "Demitido").length === 0 && (
              <tr><td colSpan={6} style={{ textAlign: "center", padding: 24, color: COLORS.textMid }}>Nenhum funcionário ativo</td></tr>
            )}
          </tbody>
        </table>
      </Card>
    </div>
  );
}

// ─── FÉRIAS & AFASTAMENTOS ────────────────────────────────────────────────────
function FeriasAfastamentos({ employees, setEmployees, afastamentos, setAfastamentos, token, usuario, abaInicial, hideTabSwitch }) {
  const canEdit = ["admin","administrador","rh"].includes(usuario?.perfil);
  const [confirmOpts, setConfirmOpts] = useState(null);
  const [aba, setAba] = useState(abaInicial || "ferias");
  const [modal, setModal] = useState(false);
  const [form, setForm] = useState({ funcionarioId: "", tipo: "Férias", inicio: "", fim: "", motivo: "", cid: "" });
  const [pdfFile, setPdfFile] = useState(null);
  const [uploadingAtestado, setUploadingAtestado] = useState(false);
  const pdfInputRef = useRef(null);
  const [editRegistro, setEditRegistro] = useState(null);
  const [uploadInlineId, setUploadInlineId] = useState(null);
  const uploadInlineRef = useRef(null);
  const [sortColF, setSortColF] = useState(null); const [sortDirF, setSortDirF] = useState("asc");
  const [sortColA, setSortColA] = useState(null); const [sortDirA, setSortDirA] = useState("asc");
  const mkSort = (col, cur, setCol, dir, setDir) => { if (cur === col) setDir(d => d === "asc" ? "desc" : "asc"); else { setCol(col); setDir("asc"); } };
  const thSort = (h, sortCol, onSort, sortDir) => {
    const ativo = sortCol === h;
    return <th key={h} onClick={() => onSort(h)} style={{ padding: "11px 14px", textAlign: "left", cursor: "pointer", userSelect: "none", whiteSpace: "nowrap", background: ativo ? COLORS.primaryBg : undefined }}>{h}<span style={{ marginLeft: 5, opacity: ativo ? 1 : 0.35, fontSize: 11 }}>{ativo ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}</span></th>;
  };
  const sortArr = (arr, col, dir, keyFn) => !col ? arr : [...arr].sort((a, b) => { const va = keyFn(a, col), vb = keyFn(b, col); return (typeof va === "number" ? (dir === "asc" ? va - vb : vb - va) : (dir === "asc" ? String(va).localeCompare(String(vb), "pt-BR") : String(vb).localeCompare(String(va), "pt-BR"))); });

  const fecharModal = () => { setModal(false); setEditRegistro(null); setPdfFile(null); };

  const abrirEditar = (af) => {
    setEditRegistro(af.id);
    setForm({ funcionarioId: String(af.funcionarioId), tipo: af.tipo, inicio: af.inicio, fim: af.fim || "", motivo: af.motivo || "" });
    setPdfFile(null);
    setModal(true);
  };

  const salvar = async () => {
    if (!form.funcionarioId) return alert("Selecione um funcionário.");
    if (!form.inicio) return alert("Informe a data de início.");
    const docObrigatorio = form.tipo !== "Férias" && form.tipo !== "Outros";
    if (docObrigatorio && !pdfFile && !editRegistro) return alert("Documento obrigatório para este tipo de afastamento. Anexe o arquivo antes de salvar.");
    const tipoApi = form.tipo === "Férias" ? "ferias" : form.tipo;

    if (editRegistro) {
      try {
        await apiFetch("PATCH", `/api/ferias/${editRegistro}`, {
          tipo: tipoApi, data_inicio: form.inicio, data_fim: form.fim || null, motivo: form.motivo,
        }, token);
        let novoAtestadoNome;
        if (pdfFile) {
          setUploadingAtestado(true);
          try {
            const fd = new FormData();
            fd.append("atestado", pdfFile);
            const res = await fetch(`/api/ferias/${editRegistro}/atestado`, {
              method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
            });
            if (res.ok) novoAtestadoNome = pdfFile.name;
          } catch(e) {}
          setUploadingAtestado(false);
        }
        setAfastamentos(prev => prev.map(a => {
          if (a.id !== editRegistro) return a;
          const ini = form.inicio, fim = form.fim || "";
          const dias = (ini && fim) ? Math.round((new Date(fim) - new Date(ini)) / 86400000) + 1 : a.dias;
          const updated = { ...a, tipo: form.tipo, inicio: ini, fim, dias, motivo: form.motivo,
            status: (!fim || new Date(fim + "T23:59:59") >= new Date()) ? "Em andamento" : "Encerrado" };
          if (novoAtestadoNome) updated.atestado_nome = novoAtestadoNome;
          return updated;
        }));
        fecharModal();
      } catch(e) { alert("Erro ao editar: " + e.message); }
      return;
    }

    const statusFunc = form.tipo === "Férias" ? "ferias" : "afastado";
    try {
      const novo = await apiFetch("POST", "/api/ferias", {
        funcionario_id: parseInt(form.funcionarioId),
        tipo: tipoApi, data_inicio: form.inicio, data_fim: form.fim || null, motivo: form.motivo,
      }, token);
      let novoAdaptado = adaptAfastamento(novo);
      if (pdfFile && novo.id) {
        setUploadingAtestado(true);
        try {
          const fd = new FormData();
          fd.append("atestado", pdfFile);
          const res = await fetch(`/api/ferias/${novo.id}/atestado`, {
            method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
          });
          if (res.ok) novoAdaptado = { ...novoAdaptado, atestado_nome: pdfFile.name };
          else { const j = await res.json().catch(() => ({})); alert("Aviso: registro salvo, mas o anexo não foi enviado. " + (j.erro || "")); }
        } catch(e) { alert("Aviso: registro salvo, mas erro ao enviar anexo: " + e.message); }
        setUploadingAtestado(false);
      }
      setAfastamentos(prev => [...prev, novoAdaptado]);
      await apiFetch("PATCH", `/api/funcionarios/${form.funcionarioId}`, { status: statusFunc }, token);
      setEmployees(prev => prev.map(e => e.id === parseInt(form.funcionarioId) ? { ...e, status: form.tipo === "Férias" ? "Férias" : "Afastado" } : e));
      fecharModal();
    } catch(e) { alert("Erro ao salvar: " + e.message); }
  };

  const encerrar = async (id) => {
    const af = afastamentos.find(a => a.id === id);
    const hoje = new Date().toISOString().slice(0, 10);
    try {
      await apiFetch("PATCH", `/api/ferias/${id}/encerrar`, { data_fim: hoje }, token);
      setAfastamentos(prev => prev.map(a => a.id === id ? { ...a, fim: hoje, status: "Encerrado" } : a));
      await apiFetch("PATCH", `/api/funcionarios/${af.funcionarioId}`, { status: "ativo" }, token);
      setEmployees(prev => prev.map(e => e.id === af.funcionarioId ? { ...e, status: "Ativo" } : e));
    } catch(e) { alert("Erro ao encerrar: " + e.message); }
  };

  const apagarRegistro = (id) => {
    const af = afastamentos.find(a => a.id === id);
    setConfirmOpts({ msg: "Apagar este registro permanentemente?", onOk: async () => {
      try {
        await apiFetch("DELETE", `/api/ferias/${id}`, undefined, token);
        setAfastamentos(prev => prev.filter(a => a.id !== id));
        if (af) {
          // Sempre tenta reverter — o backend só reseta se não houver outros registros ativos
          const funcAtual = await apiFetch("GET", `/api/funcionarios/${af.funcionarioId}`, undefined, token);
          setEmployees(prev => prev.map(e => e.id === af.funcionarioId ? { ...e, status: funcAtual.status === "ativo" ? "Ativo" : funcAtual.status } : e));
        }
      } catch(e) { alert("Erro ao apagar: " + e.message); }
    }});
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {confirmOpts && <ConfirmModal msg={confirmOpts.msg} onOk={confirmOpts.onOk} onClose={() => setConfirmOpts(null)} />}
      {!hideTabSwitch && (
        <div style={{ display: "flex", gap: 8 }}>
          {["ferias", "afastamentos"].map(a => (
            <Btn key={a} variant={aba === a ? "primary" : "ghost"} onClick={() => setAba(a)}>
              {a === "ferias" ? "🏖️ Férias" : "🏥 Afastamentos"}
            </Btn>
          ))}
          {canEdit && <Btn style={{ marginLeft: "auto" }} onClick={() => {
            setForm(f => ({ ...f, tipo: aba === "afastamentos" ? "Atestado Médico" : "Férias", motivo: "", inicio: "", fim: "", funcionarioId: "" }));
            setPdfFile(null);
            setModal(true);
          }}>+ Registrar</Btn>}
        </div>
      )}
      {hideTabSwitch && canEdit && (
        <div style={{ display: "flex" }}>
          <Btn style={{ marginLeft: "auto" }} onClick={() => {
            setForm(f => ({ ...f, tipo: aba === "afastamentos" ? "Atestado Médico" : "Férias", motivo: "", inicio: "", fim: "", funcionarioId: "" }));
            setPdfFile(null);
            setModal(true);
          }}>+ Registrar</Btn>
        </div>
      )}

      {aba === "ferias" && (<>
        <Card>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: COLORS.bg }}>
                {["Funcionário", "Dias Disponíveis", "Dias Tirados", "Situação", "Vencimento"].map(h =>
                  thSort(h, sortColF, col => mkSort(col, sortColF, setSortColF, sortDirF, setSortDirF), sortDirF)
                )}
              </tr>
            </thead>
            <tbody>
              {sortArr(employees.filter(e => e.status !== "Demitido"), sortColF, sortDirF, (e, col) => ({
                "Funcionário": e.nome, "Dias Disponíveis": e.ferias.disponivel, "Dias Tirados": e.ferias.tiradas,
                "Próximas Férias": e.ferias.proximas || "", "Situação": e.status,
              }[col])).map((e, i) => {
                const venc = calcVencimentoFerias(e, afastamentos);
                const vencUrgente = venc?.tipo === 'data' && venc.dias <= 90;
                const vencLabel = !venc ? '—'
                  : venc.tipo === 'aguardando' ? <span style={{ color: COLORS.textMid, fontSize: 11 }}>Aguardando {venc.mesesFaltando}m</span>
                  : vencUrgente ? <span style={{ color: COLORS.danger, fontWeight: 700 }}>{venc.vencimento.toLocaleDateString('pt-BR')} <span style={{ fontSize: 11 }}>({venc.dias}d)</span></span>
                  : <span style={{ color: venc.dias <= 180 ? COLORS.warning : COLORS.textMid }}>{venc.vencimento.toLocaleDateString('pt-BR')}</span>;
                return (
                  <tr key={e.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray }}>
                    <td style={{ padding: "11px 14px", fontWeight: 700 }}>{e.nome}</td>
                    <td style={{ padding: "11px 14px" }}>
                      <span style={{ fontWeight: 700, color: e.ferias.disponivel >= 30 ? COLORS.danger : COLORS.secondary }}>{e.ferias.disponivel} dias</span>
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      {(() => {
                        const feriasEmp = afastamentos.filter(a => a.tipo === "Férias" && Number(a.funcionarioId) === Number(e.id));
                        const diasTirados = feriasEmp.filter(a => a.inicio && a.fim).reduce((s, a) => s + (a.dias || 0), 0);
                        const ultimo = feriasEmp.filter(a => a.inicio).sort((a, b) => b.inicio.localeCompare(a.inicio))[0];
                        return <>
                          {diasTirados} dias
                          {ultimo && <span style={{ display: "block", fontSize: 11, color: COLORS.textMid, marginTop: 2 }}>
                            {new Date(ultimo.inicio + "T00:00:00").toLocaleDateString("pt-BR")}{ultimo.fim ? ` a ${new Date(ultimo.fim + "T00:00:00").toLocaleDateString("pt-BR")}` : ""}
                          </span>}
                        </>;
                      })()}
                    </td>
                    <td style={{ padding: "11px 14px" }}>
                      {e.ferias.disponivel >= 30 ? <Badge color={COLORS.danger}>Vencidas</Badge> : e.status === "Férias" ? <Badge color={COLORS.info}>Em gozo</Badge> : <Badge color={COLORS.secondary}>Regular</Badge>}
                    </td>
                    <td style={{ padding: "11px 14px" }}>{vencLabel}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>

        {afastamentos.filter(a => a.tipo === "Férias").length > 0 && (
          <Card>
            <div style={{ fontWeight: 700, fontSize: 13, marginBottom: 12, color: COLORS.text }}>Lançamentos de Férias</div>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: COLORS.bg }}>
                  {["Funcionário", "Início", "Fim", "Dias", "Status"].map(h => (
                    <th key={h} style={{ padding: "9px 14px", textAlign: "left", fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>{h}</th>
                  ))}
                  {canEdit && <th style={{ padding: "9px 14px" }}></th>}
                </tr>
              </thead>
              <tbody>
                {afastamentos.filter(a => a.tipo === "Férias").map((a, i) => {
                  const func = employees.find(e => e.id === a.funcionarioId);
                  return (
                    <tr key={a.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                      <td style={{ padding: "10px 14px", fontWeight: 700 }}>{func?.nome || "—"}</td>
                      <td style={{ padding: "10px 14px" }}>{new Date(a.inicio + "T00:00:00").toLocaleDateString("pt-BR")}</td>
                      <td style={{ padding: "10px 14px" }}>{a.fim ? new Date(a.fim + "T00:00:00").toLocaleDateString("pt-BR") : "—"}</td>
                      <td style={{ padding: "10px 14px" }}>{a.dias}</td>
                      <td style={{ padding: "10px 14px" }}><Badge color={a.status === "Em andamento" ? COLORS.info : COLORS.secondary}>{a.status}</Badge></td>
                      {canEdit && (
                        <td style={{ padding: "10px 14px", display: "flex", gap: 6 }}>
                          <Btn size="sm" variant="ghost" onClick={() => abrirEditar(a)}>Editar</Btn>
                          <Btn size="sm" variant="danger" onClick={() => apagarRegistro(a.id)}>Excluir</Btn>
                        </td>
                      )}
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </Card>
        )}
      </>)}

      {aba === "afastamentos" && (
        <Card>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
            <thead>
              <tr style={{ background: COLORS.bg }}>
                {["Funcionário", "Tipo", "Início", "Fim", "Dias", "Motivo", "Status"].map(h =>
                  thSort(h, sortColA, col => mkSort(col, sortColA, setSortColA, sortDirA, setSortDirA), sortDirA)
                )}
                <th style={{ padding: "11px 14px" }}>Documento</th>
                <th style={{ padding: "11px 14px" }}>Ação</th>
              </tr>
            </thead>
            <tbody>
              {sortArr(afastamentos, sortColA, sortDirA, (a, col) => {
                const func = employees.find(e => e.id === a.funcionarioId);
                return { "Funcionário": func?.nome || "", "Tipo": a.tipo, "Início": a.inicio, "Fim": a.fim, "Dias": a.dias, "Motivo": a.motivo, "CID": a.cid || "", "Status": a.status }[col];
              }).map((a, i) => {
                const func = employees.find(e => e.id === a.funcionarioId);
                return (
                  <tr key={a.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray }}>
                    <td style={{ padding: "11px 14px", fontWeight: 700 }}>{func?.nome}</td>
                    <td style={{ padding: "11px 14px" }}>{a.tipo}</td>
                    <td style={{ padding: "11px 14px" }}>{new Date(a.inicio + "T00:00:00").toLocaleDateString("pt-BR")}</td>
                    <td style={{ padding: "11px 14px" }}>{a.fim ? new Date(a.fim + "T00:00:00").toLocaleDateString("pt-BR") : "—"}</td>
                    <td style={{ padding: "11px 14px" }}>{a.dias}</td>
                    <td style={{ padding: "11px 14px" }}>{a.motivo || "—"}</td>
                    <td style={{ padding: "11px 14px" }}><Badge color={a.status === "Em andamento" ? COLORS.warning : COLORS.secondary}>{a.status}</Badge></td>
                    <td style={{ padding: "11px 14px", textAlign: "center" }}>
                      {a.atestado_nome ? (
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
                          <a href={`/api/ferias/${a.id}/atestado?token=${token}`} target="_blank" rel="noopener noreferrer" title={a.atestado_nome}
                            style={{ color: COLORS.primary, fontSize: 18, textDecoration: "none" }}>📎</a>
                          {canEdit && (
                            <button title="Apagar anexo" onClick={async () => {
                              if (!window.confirm("Apagar o anexo deste registro?")) return;
                              try {
                                await apiFetch("DELETE", `/api/ferias/${a.id}/atestado`, undefined, token);
                                setAfastamentos(prev => prev.map(x => x.id === a.id ? { ...x, atestado_nome: null } : x));
                              } catch(e) { alert("Erro ao apagar anexo: " + e.message); }
                            }} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.danger, fontSize: 14, padding: 0, lineHeight: 1 }}>✕</button>
                          )}
                        </div>
                      ) : canEdit ? (
                        <button title="Anexar documento" onClick={() => { setUploadInlineId(a.id); uploadInlineRef.current?.click(); }}
                          style={{ background: "none", border: `1.5px dashed ${COLORS.border}`, borderRadius: 6, cursor: "pointer", color: COLORS.textMid, fontSize: 12, padding: "3px 8px" }}>
                          📎+
                        </button>
                      ) : <span style={{ color: COLORS.border, fontSize: 12 }}>—</span>}
                    </td>
                    <td style={{ padding: "11px 14px", display: "flex", gap: 6 }}>
                      {canEdit && <Btn size="sm" variant="ghost" onClick={() => abrirEditar(a)}>Editar</Btn>}
                      {canEdit && a.status === "Em andamento" && <Btn size="sm" variant="secondary" onClick={() => encerrar(a.id)}>Encerrar</Btn>}
                      {canEdit && <Btn size="sm" variant="ghost" style={{ color: COLORS.danger, borderColor: COLORS.danger }} onClick={() => apagarRegistro(a.id)}>Apagar</Btn>}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </Card>
      )}

      {/* Input hidden para upload inline de anexo em registro já lançado */}
      <input ref={uploadInlineRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }}
        onChange={async e => {
          const file = e.target.files[0];
          e.target.value = "";
          if (!file || !uploadInlineId) return;
          const id = uploadInlineId;
          setUploadInlineId(null);
          try {
            const fd = new FormData();
            fd.append("atestado", file);
            const res = await fetch(`/api/ferias/${id}/atestado`, { method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd });
            if (!res.ok) { const j = await res.json().catch(() => ({})); alert("Erro ao anexar: " + (j.erro || "erro desconhecido")); return; }
            setAfastamentos(prev => prev.map(x => x.id === id ? { ...x, atestado_nome: file.name } : x));
          } catch(e) { alert("Erro ao anexar documento: " + e.message); }
        }} />

      {modal && (
        <Modal title={editRegistro ? "Editar Registro" : "Registrar Férias / Afastamento"} onClose={fecharModal}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Select label="Funcionário" value={form.funcionarioId} disabled={!!editRegistro} onChange={e => setForm(f => ({ ...f, funcionarioId: e.target.value }))}>
              <option value="">Selecione...</option>
              {employees.filter(e => e.status !== "Demitido").map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
            </Select>
            <Select label="Tipo" value={form.tipo} onChange={e => setForm(f => ({ ...f, tipo: e.target.value }))}>
              {aba === "ferias" ? (
                <option>Férias</option>
              ) : (<>
                <option>Atestado Médico</option>
                <option>Licença Maternidade</option>
                <option>Licença Paternidade</option>
                <option>Acidente de Trabalho</option>
                <option>Outros</option>
              </>)}
            </Select>
            <Input label="Data Início" type="date" value={form.inicio} onChange={e => setForm(f => ({ ...f, inicio: e.target.value }))} />
            <Input label="Data Fim" type="date" value={form.fim} onChange={e => setForm(f => ({ ...f, fim: e.target.value }))} />
            <div style={{ gridColumn: "1 / -1" }}>
              <Input label="Motivo" value={form.motivo} onChange={e => setForm(f => ({ ...f, motivo: e.target.value }))} placeholder="Descreva o motivo..." />
            </div>
            {form.tipo !== "Férias" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 }}>
                  Documento (PDF, JPG, PNG — máx. 10MB)
                  {form.tipo !== "Outros" && <span style={{ color: "#dc2626", marginLeft: 4 }}>* obrigatório</span>}
                </div>
                <input ref={pdfInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }}
                  onChange={e => setPdfFile(e.target.files[0] || null)} />
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Btn size="sm" variant="ghost" onClick={() => pdfInputRef.current?.click()}>
                    📎 {pdfFile ? "Trocar arquivo" : "Anexar documento"}
                  </Btn>
                  {pdfFile && (
                    <span style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600 }}>
                      {pdfFile.name}
                      <button onClick={() => { setPdfFile(null); if (pdfInputRef.current) pdfInputRef.current.value = ""; }}
                        style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: COLORS.danger, fontSize: 14 }}>✕</button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={fecharModal}>Cancelar</Btn>
            <Btn onClick={salvar} disabled={uploadingAtestado}>{uploadingAtestado ? "Enviando..." : "Salvar"}</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── ESCALAS E TURNOS ─────────────────────────────────────────────────────────
function Escala({ employees, setEmployees, token, usuario, setores: setoresProp, setAba }) {
  const DIAS_NOMES = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
  const [filtroSetor, setFiltroSetor] = useState("");
  const [filtroNome, setFiltroNome] = useState("");
  const now = new Date();
  const [mesRef, setMesRef] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const dateInputRefs = useRef({});

  const is12x36 = t => t && /12[Xx]36/i.test(t);
  const isNoturno = t => t && /noturno/i.test(t);

  function calcDias12x36(dataRef, mes, ano) {
    if (!dataRef) return null;
    const [ry, rm, rd] = dataRef.split('-').map(Number);
    const refMs = Date.UTC(ry, rm - 1, rd);
    const diasNoMes = new Date(ano, mes, 0).getDate();
    let count = 0;
    for (let d = 1; d <= diasNoMes; d++) {
      const diff = Math.round((Date.UTC(ano, mes - 1, d) - refMs) / 86400000);
      if (Math.abs(diff) % 2 === 0) count++;
    }
    return count;
  }

  const saveRef = async (funcId, date) => {
    try {
      await apiFetch("PATCH", `/api/funcionarios/${funcId}`, { data_ref_12x36: date || null }, token);
      if (setEmployees) setEmployees(prev => prev.map(e => e.id === funcId ? { ...e, data_ref_12x36: date || null } : e));
    } catch(err) { alert("Erro ao salvar data: " + err.message); }
  };

  const setoresList = setoresProp || [];
  const [mesNum, anoNum] = mesRef.split('-').map(Number).reverse();
  const anoMesNum = mesRef.split('-').map(Number);

  const lista = employees.filter(e =>
    e.status !== "Demitido" && !e.pre_cadastro && e.hora_entrada && e.hora_saida && e.dias_semana && e.dias_semana.length
  );
  const listaFiltrada = lista.filter(e =>
    (!filtroSetor || e.setor === filtroSetor) &&
    (!filtroNome || e.nome.toLowerCase().includes(filtroNome.toLowerCase()))
  );

  const IcoLua = () => (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ display:"inline-block", verticalAlign:"middle" }}>
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/>
    </svg>
  );
  const IcoCal = () => (
    <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" style={{ display:"inline-block", verticalAlign:"middle" }}>
      <rect x="3" y="4" width="18" height="18" rx="2"/><line x1="16" y1="2" x2="16" y2="6"/><line x1="8" y1="2" x2="8" y2="6"/><line x1="3" y1="10" x2="21" y2="10"/>
    </svg>
  );

  const DiaBadge = ({ dias }) => {
    let arr;
    if (Array.isArray(dias)) arr = dias.map(Number);
    else if (typeof dias === "string" && dias.trim()) arr = dias.split(",").map(Number);
    else arr = [];
    return (
      <div style={{ display:"flex", gap:2, flexWrap:"wrap" }}>
        {[0,1,2,3,4,5,6].map(d => (
          <span key={d} style={{
            padding:"2px 5px", borderRadius:3, fontSize:10, fontWeight:600,
            background: arr.includes(d) ? COLORS.primary : COLORS.bg,
            color: arr.includes(d) ? "#fff" : COLORS.textLight,
            border: `1px solid ${arr.includes(d) ? COLORS.primary : COLORS.border}`
          }}>{DIAS_NOMES[d]}</span>
        ))}
      </div>
    );
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      <div style={{ display:"flex", gap:10, flexWrap:"wrap", alignItems:"flex-end", background:COLORS.surface, borderRadius:10, padding:"14px 16px", border:`1px solid ${COLORS.border}` }}>
        <Input label="Buscar funcionário" value={filtroNome} onChange={e => setFiltroNome(e.target.value)} placeholder="Nome..." style={{ width:220 }} />
        <Select label="Setor" value={filtroSetor} onChange={e => setFiltroSetor(e.target.value)} style={{ width:200 }}>
          <option value="">Todos os setores</option>
          {setoresList.map(s => <option key={s.id} value={s.nome}>{s.nome}</option>)}
        </Select>
        <Input label="Mês de referência" type="month" value={mesRef} onChange={e => setMesRef(e.target.value)} style={{ width:150 }} />
        <div style={{ fontSize:12, color:COLORS.textMid, alignSelf:"center" }}>{listaFiltrada.length} funcionário(s)</div>
      </div>

      {listaFiltrada.length === 0 ? (
        <div style={{ padding:40, textAlign:"center", color:COLORS.textLight, fontSize:14 }}>
          Nenhum funcionário com horário cadastrado.
          {setAba && <> <button onClick={() => setAba("funcionarios")} style={{ color:COLORS.primary, background:"none", border:"none", cursor:"pointer", fontWeight:700 }}>Ir para Funcionários →</button></>}
        </div>
      ) : (
        <div style={{ overflowX:"auto" }}>
          <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13.5 }}>
            <thead>
              <tr style={{ background:COLORS.bg }}>
                {["Funcionário","Cargo","Setor / Subsetor","Turno","Entrada","Saída","Entrada Sáb","Saída Sáb","Dias/Mês","Dias de Trabalho",""].map(h => (
                  <th key={h} style={{ padding:"11px 14px", textAlign:"left", fontWeight:700, fontSize:13, whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {listaFiltrada.map((e, i) => {
                const noturno = isNoturno(e.turno);
                const ciclo = is12x36(e.turno);
                const dias = ciclo ? calcDias12x36(e.data_ref_12x36, anoMesNum[1], anoMesNum[0]) : null;
                return (
                  <tr key={e.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray }}>
                    <td style={{ padding:"11px 14px", fontWeight:700 }}>{e.nome}</td>
                    <td style={{ padding:"11px 14px", color:COLORS.textMid }}>{e.cargo || "—"}</td>
                    <td style={{ padding:"11px 14px" }}>
                      {e.setor || "—"}
                      {e.subsetor && <span style={{ color:COLORS.textLight }}> › {e.subsetor}</span>}
                    </td>
                    <td style={{ padding:"11px 14px" }}>
                      <div style={{ display:"flex", alignItems:"center", gap:5 }}>
                        <span>{e.turno || "—"}</span>
                        {noturno && (
                          <span style={{ color:"#6366f1" }} title="Adicional noturno"><IcoLua /></span>
                        )}
                        {ciclo && (
                          <div style={{ position:"relative", display:"inline-flex", cursor:"pointer" }} title={e.data_ref_12x36 ? `Ref. ciclo: ${e.data_ref_12x36}` : "Definir referência do ciclo"}>
                            <input
                              type="date"
                              ref={el => { dateInputRefs.current[e.id] = el; }}
                              defaultValue={e.data_ref_12x36 || ""}
                              onChange={ev => saveRef(e.id, ev.target.value)}
                              style={{ position:"absolute", inset:0, opacity:0, cursor:"pointer", width:"100%", height:"100%" }}
                            />
                            <span style={{ color: e.data_ref_12x36 ? COLORS.primary : COLORS.border, lineHeight:0, pointerEvents:"none" }}>
                              <IcoCal />
                            </span>
                          </div>
                        )}
                      </div>
                    </td>
                    <td style={{ padding:"11px 14px", fontWeight:600, color:COLORS.primary }}>{e.hora_entrada}</td>
                    <td style={{ padding:"11px 14px", fontWeight:600, color:COLORS.danger }}>{e.hora_saida}</td>
                    <td style={{ padding:"11px 14px", fontWeight:600, color: e.horario_sabado ? COLORS.primary : COLORS.textLight }}>
                      {e.horario_sabado ? e.horario_sabado.split(/\s+as\s+/i)[0] : "—"}
                    </td>
                    <td style={{ padding:"11px 14px", fontWeight:600, color: e.horario_sabado ? COLORS.danger : COLORS.textLight }}>
                      {e.horario_sabado ? (e.horario_sabado.split(/\s+as\s+/i)[1] || "—") : "—"}
                    </td>
                    <td style={{ padding:"11px 14px", textAlign:"center", fontWeight:600, color: dias != null ? (e.data_ref_12x36 ? COLORS.primaryDark : COLORS.textLight) : COLORS.textLight }}>
                      {ciclo ? (
                        e.data_ref_12x36 ? (
                          <span style={{ background:COLORS.primaryBg, border:`1px solid ${COLORS.primary}`, borderRadius:5, padding:"2px 8px", fontSize:12 }}>{dias}d</span>
                        ) : (
                          <span style={{ fontSize:11, color:COLORS.textLight }}>sem ref.</span>
                        )
                      ) : "—"}
                    </td>
                    <td style={{ padding:"11px 14px" }}><DiaBadge dias={e.turno === "12X36" ? "0,1,2,3,4,5,6" : e.dias_semana} /></td>
                    <td style={{ padding:"11px 14px" }}>
                      <Btn size="sm" variant="ghost" onClick={() => setAba && setAba("funcionarios")}>Editar</Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}


// ─── PONTO MENSAL ─────────────────────────────────────────────────────────────
function PontoMensal({ employees, token, usuario }) {
  const now = new Date();
  const [mesAno, setMesAno] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [filtroSetor, setFiltroSetor] = useState("");
  const [filtroNome, setFiltroNome] = useState("");
  const [dados, setDados] = useState([]);
  const [analise, setAnalise] = useState({});
  const [loading, setLoading] = useState(false);
  const [gerandoId, setGerandoId] = useState(null);
  const [msg, setMsg] = useState("");

  const [anoNum, mesNum] = mesAno.split('-').map(Number);
  const diasNoMes = new Date(anoNum, mesNum, 0).getDate();
  const dias = Array.from({ length: diasNoMes }, (_, i) => i + 1);
  const DSEM = ["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"];
  const MESES = ["","Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];

  const carregar = async () => {
    setLoading(true);
    try {
      const [rows, analiseRows] = await Promise.all([
        apiFetch("GET", `/api/registro-ponto/mensal?mes=${mesNum}&ano=${anoNum}`, undefined, token),
        apiFetch("GET", `/api/registro-ponto/analise-mensal?mes=${mesNum}&ano=${anoNum}`, undefined, token).catch(() => []),
      ]);
      setDados(Array.isArray(rows) ? rows : []);
      const aMap = {};
      for (const item of (Array.isArray(analiseRows) ? analiseRows : [])) {
        const diasMap = {};
        for (const d of item.dias) diasMap[d.dia] = d;
        aMap[item.funcionario_id] = { resumo: item.resumo, dias: diasMap };
      }
      setAnalise(aMap);
    } catch(e) { setMsg("❌ " + e.message); }
    setLoading(false);
  };
  useEffect(() => { carregar(); }, [mesAno, token]);

  // Monta mapa: funcId → { nome, turno, empresa, setor, dias: {d: {entrada, saida, offline, foraArea}} }
  const mapa = {};
  for (const e of employees.filter(f => f.status !== "Demitido")) {
    mapa[e.id] = { id: e.id, nome: e.nome, turno: e.turno, dias_semana: e.dias_semana, empresa: e.empresa, setor: e.setor, dias: {} };
  }
  for (const r of dados) {
    const fid = r.funcionario_id;
    if (!mapa[fid]) mapa[fid] = { id: fid, nome: r.func_nome || `#${fid}`, turno: "", empresa: "", setor: "", dias: {} };
    if (!mapa[fid].dias[r.dia]) mapa[fid].dias[r.dia] = { entrada: null, saida: null, offline: false, foraArea: false };
    const d = mapa[fid].dias[r.dia];
    if (r.tipo === "entrada" && !d.entrada) { d.entrada = r.hora; if (r.offline) d.offline = true; if (!r.dentro_area) d.foraArea = true; }
    if (r.tipo === "saida") { d.saida = r.hora; if (r.offline) d.offline = true; }
  }

  const lista = Object.values(mapa)
    .filter(f => (!filtroEmpresa || f.empresa === filtroEmpresa) && (!filtroSetor || f.setor === filtroSetor) && (!filtroNome || f.nome.toLowerCase().includes(filtroNome.toLowerCase())))
    .sort((a, b) => a.nome.localeCompare(b.nome, "pt-BR"));

  const calcMin = (funcDias) => {
    let t = 0;
    for (const d of Object.values(funcDias)) {
      if (d.entrada && d.saida) {
        const [eh, em] = d.entrada.split(":").map(Number);
        const [sh, sm] = d.saida.split(":").map(Number);
        let diff = (sh*60+sm)-(eh*60+em); if (diff < 0) diff += 1440; t += diff;
      }
    }
    return t;
  };
  const fmtHoras = (min) => min > 0 ? `${Math.floor(min/60)}h${min%60>0?String(min%60).padStart(2,"0"):""}` : "—";
  const fmtMin = (min) => min > 0 ? (min >= 60 ? `${Math.floor(min/60)}h${min%60>0?String(min%60).padStart(2,'0'):''}` : `${min}min`) : "—";
  const isDom = (d) => new Date(anoNum, mesNum-1, d).getDay() === 0;
  const isSab = (d) => new Date(anoNum, mesNum-1, d).getDay() === 6;
  const isHojeCheck = (d) => new Date(anoNum, mesNum-1, d).toDateString() === now.toDateString();

  const gerarFolha = async (func) => {
    setGerandoId(func.id);
    setMsg("");
    try {
      const r = await apiFetch("POST", "/api/registro-ponto/gerar-folha", { funcionario_id: func.id, mes: mesNum, ano: anoNum }, token);
      setMsg(`✅ Folha de ${r.funcionario} gerada! ${r.diasTrabalhados} dias / ${r.totalHoras}. Funcionário pode assinar na aba "Ponto" → "Folhas".`);
    } catch(e) { setMsg("❌ " + e.message); }
    setGerandoId(null);
  };

  const empresas = [...new Set(employees.map(e => e.empresa).filter(Boolean))].sort();
  const setores = [...new Set(employees.map(e => e.setor).filter(Boolean))].sort();

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {/* Filtros */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", background: COLORS.surface, borderRadius: 10, padding: "14px 16px", border: `1px solid ${COLORS.border}` }}>
        <Input label="Mês" type="month" value={mesAno} onChange={e => setMesAno(e.target.value)} style={{ width: 155 }} />
        <Select label="Empresa" value={filtroEmpresa} onChange={e => setFiltroEmpresa(e.target.value)} style={{ width: 175 }}>
          <option value="">Todas empresas</option>
          {empresas.map(em => <option key={em}>{em}</option>)}
        </Select>
        <Select label="Setor" value={filtroSetor} onChange={e => setFiltroSetor(e.target.value)} style={{ width: 155 }}>
          <option value="">Todos setores</option>
          {setores.map(s => <option key={s}>{s}</option>)}
        </Select>
        <Input label="Funcionário" value={filtroNome} onChange={e => setFiltroNome(e.target.value)} placeholder="Buscar..." style={{ width: 160 }} />
        <Btn onClick={carregar}>↻ Atualizar</Btn>
        <div style={{ fontSize: 11, color: COLORS.textMid, alignSelf: "center", paddingBottom: 2 }}>{lista.length} funcionário(s)</div>
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 14, fontSize: 11, color: COLORS.textMid, flexWrap: "wrap" }}>
        <span><b style={{ color: COLORS.primary }}>08:00</b> entrada</span>
        <span><b style={{ color: COLORS.danger }}>17:00</b> saída</span>
        <span><b style={{ color: COLORS.danger }}>✕</b> falta</span>
        <span><b style={{ color: COLORS.warning }}>⚠</b> sem saída</span>
        <span><b style={{ color: COLORS.warning }}>⏰</b> atraso</span>
        <span><b style={{ color: "#16a34a" }}>+H</b> hora extra</span>
        <span style={{ color: COLORS.textLight }}>— fim de semana</span>
        <span>📵 offline</span>
        <span>⚠️ fora da área</span>
      </div>

      {msg && <div style={{ background: msg.startsWith("✅") ? COLORS.primaryBg : COLORS.dangerBg, border: `1px solid ${msg.startsWith("✅") ? COLORS.primary : COLORS.danger}`, borderRadius: 8, padding: "10px 14px", fontSize: 13 }}>{msg}</div>}

      {/* Tabela */}
      <div style={{ background: COLORS.surface, borderRadius: 10, border: `1px solid ${COLORS.border}`, overflow: "hidden" }}>
        {loading ? (
          <div style={{ padding: 24, color: COLORS.textMid, fontSize: 13 }}>Carregando registros de {MESES[mesNum]}/{anoNum}...</div>
        ) : lista.length === 0 ? (
          <div style={{ padding: 24, color: COLORS.textMid, fontSize: 13 }}>Nenhum funcionário encontrado.</div>
        ) : (
          <div style={{ overflowX: "auto" }}>
            <table style={{ borderCollapse: "collapse", fontSize: 11, width: "100%" }}>
              <thead>
                <tr style={{ background: COLORS.bg }}>
                  <th style={{ padding: "10px 14px", textAlign: "left", fontWeight: 700, borderBottom: `2px solid ${COLORS.border}`, borderRight: `2px solid ${COLORS.border}`, position: "sticky", left: 0, background: COLORS.bg, zIndex: 2, minWidth: 170, whiteSpace: "nowrap" }}>
                    Funcionário
                  </th>
                  {dias.map(d => {
                    const dom = isDom(d), sab = isSab(d), hj = isHojeCheck(d);
                    return (
                      <th key={d} style={{ padding: "4px 2px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`, borderRight: `1px solid ${COLORS.border}`, minWidth: 44, maxWidth: 44, fontWeight: hj ? 800 : 600, color: dom ? COLORS.danger : sab ? COLORS.accent : hj ? COLORS.primary : COLORS.textMid, background: hj ? COLORS.primaryBg : COLORS.bg }}>
                        <div style={{ fontSize: 12 }}>{d}</div>
                        <div style={{ fontSize: 9, fontWeight: 400 }}>{DSEM[new Date(anoNum, mesNum-1, d).getDay()]}</div>
                      </th>
                    );
                  })}
                  <th style={{ padding: "8px 6px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`, borderLeft: `2px solid ${COLORS.border}`, whiteSpace: "nowrap", minWidth: 50, color: COLORS.textMid, fontWeight: 600, fontSize: 10 }}>Dias</th>
                  <th style={{ padding: "8px 6px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`, whiteSpace: "nowrap", minWidth: 55, color: COLORS.danger, fontWeight: 600, fontSize: 10 }}>Faltas</th>
                  <th style={{ padding: "8px 6px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`, whiteSpace: "nowrap", minWidth: 60, color: COLORS.warning, fontWeight: 600, fontSize: 10 }}>Atraso</th>
                  <th style={{ padding: "8px 6px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`, whiteSpace: "nowrap", minWidth: 60, color: "#16a34a", fontWeight: 600, fontSize: 10 }}>H.Extra</th>
                  <th style={{ padding: "8px 6px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`, whiteSpace: "nowrap", minWidth: 65, color: COLORS.primary, fontWeight: 600, fontSize: 10 }}>Horas</th>
                  <th style={{ padding: "8px 6px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`, whiteSpace: "nowrap", minWidth: 80, color: COLORS.textMid, fontWeight: 600, fontSize: 10 }}>Folha</th>
                </tr>
              </thead>
              <tbody>
                {lista.map((func, fi) => {
                  const bgRow = fi % 2 === 0 ? COLORS.white : COLORS.gray;
                  const diasTrab = Object.values(func.dias).filter(d => d.entrada).length;
                  const totalMin = calcMin(func.dias);
                  const af = analise[func.id];
                  return (
                    <tr key={func.id}>
                      <td style={{ padding: "8px 14px", borderRight: `2px solid ${COLORS.border}`, position: "sticky", left: 0, background: bgRow, zIndex: 1, whiteSpace: "nowrap" }}>
                        <div style={{ fontWeight: 700, fontSize: 12 }}>{func.nome}</div>
                        <div style={{ fontSize: 10, color: COLORS.textMid }}>{[func.turno, func.setor].filter(Boolean).join(" · ")}</div>
                      </td>
                      {dias.map(d => {
                        const reg = func.dias[d];
                        const hj = isHojeCheck(d);
                        const ad = af?.dias[d];
                        const dowD = new Date(anoNum, mesNum - 1, d).getDay();
                        const is12x36row = /12[Xx]36/i.test(func.turno);
                        const dsStr = func.dias_semana ? String(func.dias_semana) : "1,2,3,4,5";
                        const diaTrabalha = is12x36row || dsStr.split(",").map(Number).includes(dowD);
                        let bg = hj ? `${COLORS.primaryBg}66` : bgRow;
                        let content;
                        if (!diaTrabalha && !reg) {
                          content = <span style={{ color: COLORS.border, fontSize: 10 }}>—</span>;
                        } else if (ad?.falta && !reg) {
                          bg = hj ? COLORS.dangerBg : "#fff5f5";
                          content = <span style={{ color: COLORS.danger, fontSize: 14, fontWeight: 700 }}>✕</span>;
                        } else if (!reg) {
                          content = <span style={{ color: COLORS.primary, fontSize: 14 }}>✓</span>;
                        } else if (reg.entrada && !reg.saida) {
                          bg = "#fffbeb";
                          content = (
                            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <div style={{ color: COLORS.primary, fontWeight: 700, fontSize: 10 }}>{reg.entrada}</div>
                              <div style={{ fontSize: 8, color: COLORS.warning }}>⚠sem saída</div>
                              {reg.offline && <div style={{ fontSize: 8, color: COLORS.textLight }}>📵</div>}
                              {reg.foraArea && <div style={{ fontSize: 8, color: COLORS.danger }}>⚠️área</div>}
                            </div>
                          );
                        } else {
                          const [eh, em] = reg.entrada.split(":").map(Number);
                          const [sh, sm] = reg.saida.split(":").map(Number);
                          let diff = (sh*60+sm)-(eh*60+em); if (diff < 0) diff += 1440;
                          if (ad?.atraso > 0) bg = "#fff3e0";
                          else if (ad?.horas_extras > 0) bg = "#f0fdf4";
                          content = (
                            <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                              <div style={{ color: ad?.atraso > 0 ? COLORS.warning : COLORS.primary, fontWeight: 700, fontSize: 10 }}>{reg.entrada}</div>
                              <div style={{ color: COLORS.danger, fontWeight: 700, fontSize: 10 }}>{reg.saida}</div>
                              <div style={{ color: COLORS.textLight, fontSize: 9 }}>{Math.floor(diff/60)}h{diff%60>0?String(diff%60).padStart(2,"0"):""}</div>
                              {ad?.atraso > 0 && <div style={{ fontSize: 7, color: COLORS.warning, fontWeight: 700 }}>⏰+{ad.atraso}m</div>}
                              {ad?.horas_extras > 0 && <div style={{ fontSize: 7, color: "#16a34a", fontWeight: 700 }}>+{fmtMin(ad.horas_extras)}</div>}
                              {reg.offline && <div style={{ fontSize: 8, color: COLORS.textLight }}>📵</div>}
                            </div>
                          );
                        }
                        return <td key={d} style={{ padding: "3px 2px", textAlign: "center", borderRight: `1px solid ${COLORS.border}`, background: bg, verticalAlign: "middle", minWidth: 44 }}>{content}</td>;
                      })}
                      <td style={{ padding: "8px 6px", textAlign: "center", borderLeft: `2px solid ${COLORS.border}`, fontWeight: 700, fontSize: 12 }}>{diasTrab}</td>
                      <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, fontSize: 12, color: af?.resumo.faltas > 0 ? COLORS.danger : COLORS.textLight }}>
                        {af ? af.resumo.faltas : "—"}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, fontSize: 11, color: af?.resumo.atraso_min > 0 ? COLORS.warning : COLORS.textLight }}>
                        {af ? fmtMin(af.resumo.atraso_min) : "—"}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, fontSize: 11, color: af?.resumo.horas_extras_min > 0 ? "#16a34a" : COLORS.textLight }}>
                        {af ? fmtMin(af.resumo.horas_extras_min) : "—"}
                      </td>
                      <td style={{ padding: "8px 6px", textAlign: "center", fontWeight: 700, fontSize: 12, color: COLORS.primary }}>{fmtHoras(totalMin)}</td>
                      <td style={{ padding: "6px", textAlign: "center" }}>
                        {diasTrab > 0
                          ? <Btn size="sm" variant="primary" onClick={() => gerarFolha(func)} disabled={gerandoId === func.id}>{gerandoId === func.id ? "⏳" : "Gerar"}</Btn>
                          : <span style={{ fontSize: 10, color: COLORS.textLight }}>—</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </div>
  );
}

// ─── PONTO DIA A DIA ──────────────────────────────────────────────────────────
function PontoDiaADia({ employees, token }) {
  const [data, setData] = useState(new Date().toISOString().slice(0,10));
  const [filtroFunc, setFiltroFunc] = useState("");
  const [regs, setRegs] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const buscar = async () => {
    setCarregando(true);
    try {
      let url = `/api/registro-ponto?data=${data}`;
      if (filtroFunc) url += `&funcionario_id=${filtroFunc}`;
      const rows = await apiFetch("GET", url, undefined, token);
      setRegs(Array.isArray(rows) ? rows : []);
    } catch(e) {}
    setCarregando(false);
  };
  useEffect(() => { buscar(); }, [data, token]);
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end", background: COLORS.surface, borderRadius: 10, padding: "14px 16px", border: `1px solid ${COLORS.border}` }}>
        <Input label="Data" type="date" value={data} onChange={e => setData(e.target.value)} />
        <Select label="Funcionário" value={filtroFunc} onChange={e => setFiltroFunc(e.target.value)} style={{ width: 200 }}>
          <option value="">Todos</option>
          {employees.filter(e => e.status !== "Demitido").sort((a,b) => a.nome.localeCompare(b.nome)).map(e => <option key={e.id} value={e.id}>{e.nome}</option>)}
        </Select>
        <Btn onClick={buscar}>↻ Buscar</Btn>
        <div style={{ fontSize: 12, color: COLORS.textMid, alignSelf: "center" }}>{regs.length} registro(s)</div>
      </div>
      <Card>
        {carregando ? <div style={{ color: COLORS.textMid, fontSize: 13 }}>Carregando...</div> : regs.length === 0 ? <div style={{ color: COLORS.textMid, fontSize: 13 }}>Nenhum registro nesta data.</div> : (
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.bg }}>
                {["Funcionário","Tipo","Horário","GPS","Área","Offline"].map(h => <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: COLORS.textMid, fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>)}
              </tr>
            </thead>
            <tbody>
              {regs.map(r => (
                <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "8px 10px", fontWeight: 600 }}>{r.func_nome || r.funcionario_nome}</td>
                  <td style={{ padding: "8px 10px" }}><Badge color={r.tipo === "entrada" ? COLORS.primary : COLORS.danger}>{r.tipo === "entrada" ? "▶ Entrada" : "⏹ Saída"}</Badge></td>
                  <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>{new Date(r.registrado_em).toLocaleString("pt-BR")}</td>
                  <td style={{ padding: "8px 10px", fontSize: 11, color: COLORS.textMid }}>{r.latitude ? `${parseFloat(r.latitude).toFixed(4)}, ${parseFloat(r.longitude).toFixed(4)}` : "—"}</td>
                  <td style={{ padding: "8px 10px" }}>{r.dentro_area ? <Badge color={COLORS.primary}>✅ Dentro</Badge> : <Badge color={COLORS.warning}>⚠️ Fora</Badge>}</td>
                  <td style={{ padding: "8px 10px" }}>{r.offline ? <Badge color={COLORS.warning}>Sim</Badge> : "—"}</td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ─── OCORRÊNCIAS DE PONTO ─────────────────────────────────────────────────────
function OcorrenciasPonto({ token, employees }) {
  const [ocorrencias, setOcorrencias] = useState([]);
  const [filtro, setFiltro] = useState("pendente");
  const [obsModal, setObsModal] = useState(null);
  const [obsTexto, setObsTexto] = useState("");
  const atestadoInputRef = useRef(null);
  const [uploadingId, setUploadingId] = useState(null);

  useEffect(() => {
    apiFetch("GET", "/api/registro-ponto/ocorrencias", undefined, token)
      .then(r => setOcorrencias(Array.isArray(r) ? r : [])).catch(() => {});
  }, [token]);

  const resolver = async (id, status) => {
    try {
      await apiFetch("PUT", `/api/registro-ponto/ocorrencias/${id}`, { status, gestor_obs: obsTexto || null }, token);
      setOcorrencias(prev => prev.map(o => o.id === id ? { ...o, status, gestor_obs: obsTexto || null } : o));
      setObsModal(null); setObsTexto("");
    } catch(e) { alert("Erro: " + e.message); }
  };

  const uploadAtestado = async (id, file) => {
    if (!file) return;
    const form = new FormData();
    form.append("atestado", file);
    try {
      const resp = await fetch(`/api/registro-ponto/ocorrencias/${id}/atestado`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: form,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.erro || "Erro no upload");
      setOcorrencias(prev => prev.map(o => o.id === id ? { ...o, atestado_nome: data.atestado_nome, atestado_path: data.atestado_path } : o));
    } catch(e) { alert("Erro ao enviar atestado: " + e.message); }
  };

  const STATUS_COR = { pendente: COLORS.warning, justificada: COLORS.info, confirmada: COLORS.danger, rejeitada: "#16a34a", estornada: COLORS.textMid };
  const STATUS_LABEL = { pendente: "Pendente", justificada: "Justificada", confirmada: "Confirmada", rejeitada: "Não lançado", estornada: "Estornado" };
  const lista = ocorrencias.filter(o => filtro === "todos" || o.status === filtro);

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      <Card>
        <div style={{ display: "flex", alignItems: "center", gap: 12, marginBottom: 14, flexWrap: "wrap" }}>
          <span style={{ fontWeight: 700, fontSize: 14 }}>Ocorrências de Ponto</span>
          <span style={{ fontSize: 12, color: COLORS.textMid }}>Atrasos e faltas — aprovação e gestão de atestados</span>
          <div style={{ marginLeft: "auto", display: "flex", gap: 6, flexWrap: "wrap" }}>
            {["pendente","justificada","confirmada","rejeitada","estornada","todos"].map(s => (
              <button key={s} onClick={() => setFiltro(s)} style={{
                padding: "4px 10px", borderRadius: 6, border: `1px solid ${filtro === s ? COLORS.primary : COLORS.border}`,
                background: filtro === s ? COLORS.primaryBg : "none", cursor: "pointer", fontSize: 12,
                color: filtro === s ? COLORS.primary : COLORS.textMid, fontWeight: filtro === s ? 700 : 400,
              }}>{STATUS_LABEL[s] || "Todos"}</button>
            ))}
          </div>
        </div>

        {lista.length === 0 ? (
          <div style={{ color: COLORS.textMid, fontSize: 13, padding: "20px 0", textAlign: "center" }}>
            Nenhuma ocorrência {filtro !== "todos" ? `com status "${STATUS_LABEL[filtro]}"` : ""}.
          </div>
        ) : (
          <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
            {/* input global para upload de atestado */}
            <input ref={atestadoInputRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }}
              onChange={e => { if (e.target.files[0] && uploadingId) uploadAtestado(uploadingId, e.target.files[0]); }} />
            {lista.map(o => (
              <div key={o.id} style={{ padding: "14px 16px", background: COLORS.bg, borderRadius: 10, border: `1px solid ${COLORS.border}`, display: "flex", alignItems: "flex-start", gap: 14, flexWrap: "wrap" }}>
                <div style={{ flex: 1, minWidth: 200 }}>
                  <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 4, flexWrap: "wrap" }}>
                    <span style={{ fontWeight: 700, fontSize: 14 }}>{o.funcionario_nome}</span>
                    <Badge color={o.tipo === "atraso" ? COLORS.warning : COLORS.danger}>
                      {o.tipo === "atraso" ? `⏰ Atraso ${o.minutos}min` : "✕ Falta"}
                    </Badge>
                    <Badge color={STATUS_COR[o.status]}>{STATUS_LABEL[o.status]}</Badge>
                  </div>
                  <div style={{ fontSize: 12, color: COLORS.textMid }}>
                    {new Date(o.data + "T00:00").toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
                  </div>
                  {o.justificativa && (
                    <div style={{ marginTop: 6, fontSize: 12, padding: "6px 10px", background: "#eff6ff", borderRadius: 6, borderLeft: `3px solid ${COLORS.info}` }}>
                      <strong>Justificativa:</strong> {o.justificativa}
                    </div>
                  )}
                  {/* Atestado médico */}
                  {o.atestado_path ? (
                    <div style={{ marginTop: 6, display: "flex", alignItems: "center", gap: 8, fontSize: 12 }}>
                      <span>📎</span>
                      <a href={o.atestado_path} target="_blank" rel="noreferrer" style={{ color: COLORS.primary, textDecoration: "underline" }}>
                        {o.atestado_nome || "Atestado anexado"}
                      </a>
                      <button onClick={() => { setUploadingId(o.id); atestadoInputRef.current?.click(); }} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textMid, fontSize: 11 }}>Substituir</button>
                    </div>
                  ) : (
                    <button onClick={() => { setUploadingId(o.id); atestadoInputRef.current?.click(); }} style={{ marginTop: 6, background: "none", border: `1px dashed ${COLORS.border}`, borderRadius: 6, padding: "4px 10px", cursor: "pointer", fontSize: 12, color: COLORS.textMid, display: "flex", alignItems: "center", gap: 4 }}>
                      📎 Anexar atestado médico
                    </button>
                  )}
                  {o.gestor_obs && (
                    <div style={{ marginTop: 4, fontSize: 12, color: COLORS.textMid, fontStyle: "italic" }}>Obs administrador: {o.gestor_obs}</div>
                  )}
                </div>
                <div style={{ display: "flex", flexDirection: "column", gap: 6, flexShrink: 0 }}>
                  {(o.status === "pendente" || o.status === "justificada") && (
                    <>
                      <Btn size="sm" variant="danger" onClick={() => setObsModal({ id: o.id, acao: "confirmada", label: "Confirmar falta/atraso" })}>Confirmar</Btn>
                      <Btn size="sm" variant="ghost" onClick={() => setObsModal({ id: o.id, acao: "rejeitada", label: "Não lançar (aceitar)" })}>Não lançar</Btn>
                    </>
                  )}
                  {o.status === "confirmada" && (
                    <Btn size="sm" variant="ghost" onClick={() => setObsModal({ id: o.id, acao: "estornada", label: "Estornar falta/atraso lançado" })}>↩ Estornar</Btn>
                  )}
                  {(o.status === "estornada" || o.status === "rejeitada") && (
                    <Btn size="sm" variant="danger" onClick={() => setObsModal({ id: o.id, acao: "confirmada", label: "Relançar falta/atraso" })}>Relançar</Btn>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </Card>

      {obsModal && (
        <Modal title={obsModal.label} onClose={() => { setObsModal(null); setObsTexto(""); }}>
          <div style={{ fontSize: 13, color: COLORS.textMid, marginBottom: 12 }}>
            {obsModal.acao === "confirmada" && "A falta/atraso será contabilizada na folha do funcionário."}
            {obsModal.acao === "rejeitada" && "A falta/atraso NÃO será contabilizada (justificativa ou atestado aceito)."}
            {obsModal.acao === "estornada" && "A falta/atraso confirmada será revertida e deixará de contar na folha."}
          </div>
          <Input label="Observação do administrador (opcional)" value={obsTexto} onChange={e => setObsTexto(e.target.value)} placeholder="Ex: atestado verificado, aprovado..." />
          <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
            <Btn variant="ghost" onClick={() => { setObsModal(null); setObsTexto(""); }}>Cancelar</Btn>
            <Btn variant={obsModal.acao === "confirmada" ? "danger" : "primary"} onClick={() => resolver(obsModal.id, obsModal.acao)}>
              {obsModal.acao === "confirmada" ? "Confirmar e Lançar" : obsModal.acao === "estornada" ? "↩ Estornar" : "Não lançar"}
            </Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── PONTO MODULO (wrapper unificado) ─────────────────────────────────────────
function PontoModulo({ employees, ponto, setPonto, token, usuario, setores, empresas, abaInicial, onAbaChange }) {
  const isAdmin = ["admin","administrador","rh"].includes(usuario?.perfil);
  const [confirmOpts, setConfirmOpts] = useState(null);
  const [aba, setAba] = useState(abaInicial || (isAdmin ? "mensal" : "bater"));
  useEffect(() => { if (abaInicial && abaInicial !== aba) setAba(abaInicial); }, [abaInicial]);
  const setAbaSync = (a) => { setAba(a); if (onAbaChange) onAbaChange(a); };
  const [areas, setAreas] = useState([]);
  const [modalArea, setModalArea] = useState(null);
  const [formArea, setFormArea] = useState({ nome: "", latitude: "", longitude: "", raio: 100 });

  useEffect(() => {
    apiFetch("GET", "/api/registro-ponto/areas", undefined, token)
      .then(r => setAreas(Array.isArray(r) ? r : [])).catch(() => {});
  }, [token]);

  const salvarArea = async () => {
    if (!formArea.nome || !formArea.latitude || !formArea.longitude) { alert("Preencha nome, latitude e longitude."); return; }
    try {
      if (modalArea?.id) {
        await apiFetch("PUT", `/api/registro-ponto/areas/${modalArea.id}`, formArea, token);
        setAreas(prev => prev.map(a => a.id === modalArea.id ? { ...a, ...formArea } : a));
      } else {
        const nova = await apiFetch("POST", "/api/registro-ponto/areas", formArea, token);
        setAreas(prev => [...prev, nova]);
      }
      setModalArea(null);
    } catch(e) { alert(e.message); }
  };
  const excluirArea = (id) => {
    setConfirmOpts({ msg: "Remover esta área?", onOk: async () => {
      try { await apiFetch("DELETE", `/api/registro-ponto/areas/${id}`, undefined, token); setAreas(prev => prev.filter(a => a.id !== id)); } catch(e) { alert(e.message); }
    }});
  };

  const ABAS_ADMIN = [
    { id: "mensal",      label: "📅 Mensal" },
    { id: "diaadia",     label: "🕐 Dia a Dia" },
    { id: "lancamento",  label: "📋 Lançamento Manual" },
    { id: "ocorrencias", label: "⚠️ Ocorrências" },
    { id: "areas",       label: "📍 Áreas de Ponto" },
  ];

  if (!isAdmin) {
    return <BaterPonto token={token} usuario={usuario} />;
  }

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 0 }}>
      {confirmOpts && <ConfirmModal msg={confirmOpts.msg} onOk={confirmOpts.onOk} onClose={() => setConfirmOpts(null)} />}
      {/* Mensal */}
      {aba === "mensal" && <PontoMensal employees={employees} token={token} usuario={usuario} />}

      {/* Dia a Dia */}
      {aba === "diaadia" && <PontoDiaADia employees={employees} token={token} />}

      {/* Lançamento Manual */}
      {aba === "lancamento" && <PontoLancamentos employees={employees} ponto={ponto} setPonto={setPonto} token={token} usuario={usuario} />}

      {/* Áreas */}
      {aba === "areas" && (
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle action={<Btn size="sm" onClick={() => { setModalArea({}); setFormArea({ nome: "", latitude: "", longitude: "", raio: 100 }); }}>+ Nova Área</Btn>}>
              Áreas Permitidas para Ponto
            </SectionTitle>
            <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 14 }}>
              Cadastre os locais onde funcionários podem bater ponto. Cada funcionário pode ser vinculado a uma área específica no cadastro.
            </div>
            {areas.length === 0
              ? <div style={{ color: COLORS.textMid, fontSize: 13 }}>Nenhuma área cadastrada. Sem áreas, qualquer localização é aceita.</div>
              : <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
                {areas.map(a => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                    <div style={{ fontSize: 22 }}>📍</div>
                    <div style={{ flex: 1 }}>
                      <div style={{ fontWeight: 700, fontSize: 14 }}>{a.nome}</div>
                      <div style={{ fontSize: 12, color: COLORS.textMid }}>{parseFloat(a.latitude).toFixed(6)}, {parseFloat(a.longitude).toFixed(6)} · raio {a.raio}m</div>
                    </div>
                    <div style={{ display: "flex", gap: 6 }}>
                      <Btn size="sm" variant="ghost" onClick={() => { setModalArea(a); setFormArea({ nome: a.nome, latitude: a.latitude, longitude: a.longitude, raio: a.raio }); }}>Editar</Btn>
                      <Btn size="sm" variant="danger" onClick={() => excluirArea(a.id)}>Excluir</Btn>
                    </div>
                  </div>
                ))}
              </div>
            }
          </Card>
          {modalArea && (
            <Modal title={modalArea.id ? "Editar Área" : "Nova Área"} onClose={() => setModalArea(null)}>
              <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
                <Input label="Nome do local" value={formArea.nome} onChange={e => setFormArea(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Sede, Filial Centro..." />
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
                  <Input label="Latitude" value={formArea.latitude} onChange={e => setFormArea(f => ({ ...f, latitude: e.target.value }))} placeholder="-23.5505" />
                  <Input label="Longitude" value={formArea.longitude} onChange={e => setFormArea(f => ({ ...f, longitude: e.target.value }))} placeholder="-46.6333" />
                </div>
                <Input label="Raio (metros)" type="number" value={formArea.raio} onChange={e => setFormArea(f => ({ ...f, raio: Number(e.target.value) }))} />
                <div style={{ fontSize: 12, color: COLORS.textMid, background: COLORS.bg, borderRadius: 8, padding: "10px 12px" }}>
                  💡 Abra o Google Maps, clique com botão direito no local e copie as coordenadas.
                </div>
                <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
                  <Btn variant="ghost" onClick={() => setModalArea(null)}>Cancelar</Btn>
                  <Btn onClick={salvarArea}>Salvar</Btn>
                </div>
              </div>
            </Modal>
          )}
        </div>
      )}

      {/* Ocorrências — aprovação administrador */}
      {aba === "ocorrencias" && <OcorrenciasPonto token={token} employees={employees} />}

    </div>
  );
}

// ─── HISTÓRICO DE AFASTAMENTOS ───────────────────────────────────────────────
function HistoricoAfastamentos({ employees, afastamentos, token, usuario }) {
  const TIPOS = ["Sem atestado", "Férias", "Atestado Médico", "Licença Maternidade", "Licença Paternidade", "Acidente de Trabalho", "Outros"];
  const [filtroEmp, setFiltroEmp]     = useState("");
  const [filtroTipo, setFiltroTipo]   = useState("");
  const [filtroInicio, setFiltroInicio] = useState("");
  const [filtroFim, setFiltroFim]     = useState("");
  const [filtroMotivo, setFiltroMotivo] = useState("");
  const [faltasRaw, setFaltasRaw]     = useState([]);

  useEffect(() => {
    apiFetch("GET", "/api/ponto-terceiro/faltas-historico", undefined, token)
      .then(rows => setFaltasRaw(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [token]);

  const empMap = useMemo(() => {
    const m = {};
    employees.forEach(e => { m[e.id] = e.nome; });
    return m;
  }, [employees]);

  // Agrupa dias consecutivos de falta por funcionário em períodos
  const faltasPeriodos = useMemo(() => {
    const p2 = n => String(n).padStart(2, '0');
    const byFunc = {};
    faltasRaw.forEach(r => {
      const key = r.funcionario_id;
      if (!byFunc[key]) byFunc[key] = [];
      byFunc[key].push({ ...r, dateStr: `${r.ano}-${p2(r.mes)}-${p2(r.dia)}` });
    });
    const result = [];
    Object.entries(byFunc).forEach(([funcId, dias]) => {
      dias.sort((a, b) => a.dateStr.localeCompare(b.dateStr));
      let grupo = null;
      dias.forEach(d => {
        if (!grupo) { grupo = { funcId: Number(funcId), inicio: d.dateStr, fim: d.dateStr, dias: 1, motivo: d.motivo || "" }; return; }
        const prev = new Date(grupo.fim);
        prev.setDate(prev.getDate() + 1);
        const prevStr = prev.toISOString().slice(0, 10);
        if (d.dateStr === prevStr) {
          grupo.fim = d.dateStr;
          grupo.dias++;
          if (!grupo.motivo && d.motivo) grupo.motivo = d.motivo;
        } else {
          result.push(grupo);
          grupo = { funcId: Number(funcId), inicio: d.dateStr, fim: d.dateStr, dias: 1, motivo: d.motivo || "" };
        }
      });
      if (grupo) result.push(grupo);
    });
    return result.map((p, i) => ({
      id: `falta-${i}`,
      funcionarioId: p.funcId,
      tipo: "Sem atestado",
      inicio: p.inicio,
      fim: p.fim,
      dias: p.dias,
      motivo: p.motivo,
      atestado_nome: null,
      _isFalta: true,
    }));
  }, [faltasRaw]);

  const rows = useMemo(() => {
    const tudo = [...afastamentos, ...faltasPeriodos];
    return tudo.filter(a => {
      if (filtroEmp   && String(a.funcionarioId) !== filtroEmp) return false;
      if (filtroTipo  && a.tipo !== filtroTipo) return false;
      if (filtroMotivo && !((a.motivo || "").toLowerCase().includes(filtroMotivo.toLowerCase()))) return false;
      if (filtroInicio && a.inicio < filtroInicio) return false;
      if (filtroFim   && a.fim   > filtroFim)   return false;
      return true;
    }).sort((a, b) => (b.inicio || "").localeCompare(a.inicio || ""));
  }, [afastamentos, faltasPeriodos, filtroEmp, filtroTipo, filtroMotivo, filtroInicio, filtroFim]);

  const fmt = d => d ? d.slice(0,10).split("-").reverse().join("/") : "—";

  const filtroAtivo = filtroEmp || filtroTipo || filtroInicio || filtroFim || filtroMotivo;
  const limparFiltros = () => { setFiltroEmp(""); setFiltroTipo(""); setFiltroInicio(""); setFiltroFim(""); setFiltroMotivo(""); };

  const iStyle = { width: "100%", padding: "4px 6px", border: "1px solid #c8d4e8", borderRadius: 4, fontSize: 12, background: "#fff", boxSizing: "border-box" };

  return (
    <div style={{ padding: "0 0 32px" }}>
      <style>{`
        @media print {
          .rh-no-print { display: none !important; }
          body * { visibility: hidden; }
          #historico-print-area { visibility: visible; position: absolute; left: 0; top: 0; width: 100%; }
          #historico-print-area * { visibility: visible; }
          #historico-print-area .hist-filter-row { display: none !important; }
          #historico-print-area table { font-size: 11px; width: 100%; border-collapse: collapse; }
          #historico-print-area th, #historico-print-area td { padding: 5px 8px !important; }
          #historico-print-area tr { page-break-inside: avoid; }
          @page { margin: 1.5cm; size: landscape; }
        }
      `}</style>

      <div className="rh-no-print" style={{ display: "flex", justifyContent: "space-between", alignItems: "center", marginBottom: 12 }}>
        <div style={{ fontSize: 13, color: "#666" }}>
          {rows.length} registro(s)
          {filtroAtivo && <span style={{ marginLeft: 8, color: "#1e3a5f", fontWeight: 600 }}>• filtro ativo</span>}
        </div>
        <div style={{ display: "flex", gap: 8 }}>
          {filtroAtivo && <Btn variant="ghost" onClick={limparFiltros}>Limpar filtros</Btn>}
          <Btn onClick={() => window.print()} style={{ display: "flex", alignItems: "center", gap: 6 }}>
            🖨️ Imprimir
          </Btn>
        </div>
      </div>

      <div id="historico-print-area">
        <Card>
          <div style={{ overflowX: "auto" }}>
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13.5 }}>
              <thead>
                <tr style={{ background: "#1e3a5f", color: "#fff" }}>
                  <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Funcionário</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Tipo</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Início</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Fim</th>
                  <th style={{ padding: "10px 12px", textAlign: "center", whiteSpace: "nowrap" }}>Dias</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }}>Motivo</th>
                  <th style={{ padding: "10px 12px", textAlign: "left", whiteSpace: "nowrap" }} className="rh-no-print">Documento</th>
                </tr>
                <tr style={{ background: "#d0daea" }} className="hist-filter-row rh-no-print">
                  <td style={{ padding: "4px 6px" }}>
                    <select value={filtroEmp} onChange={e => setFiltroEmp(e.target.value)} style={iStyle}>
                      <option value="">Todos</option>
                      {employees.filter(e => e.status !== "Demitido").map(e => <option key={e.id} value={String(e.id)}>{e.nome}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)} style={iStyle}>
                      <option value="">Todos</option>
                      {TIPOS.map(t => <option key={t}>{t}</option>)}
                    </select>
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <input type="date" value={filtroInicio} onChange={e => setFiltroInicio(e.target.value)} style={iStyle} title="A partir de" />
                  </td>
                  <td style={{ padding: "4px 6px" }}>
                    <input type="date" value={filtroFim} onChange={e => setFiltroFim(e.target.value)} style={iStyle} title="Até" />
                  </td>
                  <td />
                  <td style={{ padding: "4px 6px" }}>
                    <input type="text" value={filtroMotivo} onChange={e => setFiltroMotivo(e.target.value)} placeholder="🔍 buscar..." style={iStyle} />
                  </td>
                  <td />
                </tr>
              </thead>
              <tbody>
                {rows.length === 0 && (
                  <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: "#888" }}>Nenhum registro encontrado.</td></tr>
                )}
                {rows.map((a, i) => (
                  <tr key={a.id} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc", borderBottom: "1px solid #e5e7eb" }}>
                    <td style={{ padding: "9px 12px" }}>{empMap[a.funcionarioId] || `#${a.funcionarioId}`}</td>
                    <td style={{ padding: "9px 12px" }}>
                      <span style={{
                        background: a.tipo === "Férias" ? "#d1fae5"
                          : a.tipo === "Sem atestado" ? "#fef3c7"
                          : a.tipo === "Atestado Médico" ? "#fee2e2"
                          : a.tipo === "Acidente de Trabalho" ? "#ffedd5"
                          : "#e0e7ff",
                        color: a.tipo === "Férias" ? "#065f46"
                          : a.tipo === "Sem atestado" ? "#92400e"
                          : a.tipo === "Atestado Médico" ? "#991b1b"
                          : a.tipo === "Acidente de Trabalho" ? "#9a3412"
                          : "#3730a3",
                        borderRadius: 12, padding: "2px 10px", fontSize: 12, fontWeight: 600
                      }}>{a.tipo}</span>
                    </td>
                    <td style={{ padding: "9px 12px" }}>{fmt(a.inicio)}</td>
                    <td style={{ padding: "9px 12px" }}>{fmt(a.fim)}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>{a.dias ?? "—"}</td>
                    <td style={{ padding: "9px 12px", maxWidth: 260, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>{a.motivo || "—"}</td>
                    <td style={{ padding: "9px 12px" }} className="rh-no-print">
                      {a.atestado_nome
                        ? <span
                            onClick={async () => {
                              try {
                                const res = await fetch(`/api/ferias/${a.id}/atestado`, { headers: { Authorization: `Bearer ${token}` } });
                                if (!res.ok) { alert('Erro ao abrir documento.'); return; }
                                const blob = await res.blob();
                                const url = URL.createObjectURL(blob);
                                window.open(url, '_blank');
                                setTimeout(() => URL.revokeObjectURL(url), 60000);
                              } catch { alert('Erro ao abrir documento.'); }
                            }}
                            style={{ color: "#1e3a5f", fontWeight: 600, textDecoration: "underline", cursor: "pointer" }}
                          >📎 {a.atestado_nome}</span>
                        : <span style={{ color: "#aaa" }}>—</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
              <tfoot>
                <tr style={{ background: "#f1f5f9", fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: "9px 12px" }}>Total</td>
                  <td style={{ padding: "9px 12px", textAlign: "center" }}>{rows.reduce((s, a) => s + (a.dias || 0), 0)}</td>
                  <td colSpan={2} />
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      </div>
    </div>
  );
}

// ─── PONTO DE TERCEIRO ────────────────────────────────────────────────────────
function PontoTerceiro({ employees, token, usuario, afastamentosGlobal, setAfastamentos }) {
  const now = new Date();
  const [confirmOpts, setConfirmOpts] = useState(null);
  const [mesAno, setMesAno] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [faltas, setFaltas] = useState({}); // { "funcId_dia": true }
  const [extras, setExtras] = useState({}); // { funcId: horas }
  const [trabFeriado, setTrabFeriado] = useState({}); // { "funcId_dia": true } — trabalhou no feriado
  const [feriados, setFeriados] = useState([]); // [{id, data, nome, cidade}]
  const [modalFeriado, setModalFeriado] = useState(false);
  const [formFeriado, setFormFeriado] = useState({ data: "", data_fim: "", nome: "", cidade: "Brasil" });
  const [salvandoFeriado, setSalvandoFeriado] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [filtro, setFiltro] = useState("");
  const [uploadingAtestado, setUploadingAtestado] = useState(null);
  const [msgAtestado, setMsgAtestado] = useState({});
  const atestadoInputRef = useRef({});
  const [obsMode, setObsMode] = useState(false);
  const [observacoes, setObservacoes] = useState({}); // { "funcId_dia": texto }
  const [obsPopover, setObsPopover] = useState(null); // { funcId, dia, texto, x, y }
  const [savingObs, setSavingObs] = useState(false);
  // Modal de ausência (abre ao clicar em dia trabalhado)
  const [modalFalta, setModalFalta] = useState(null); // { funcId, dia, empNome }
  const [formFalta, setFormFalta] = useState({ tipo: "Sem atestado", inicio: "", fim: "", motivo: "" });
  const [pdfFalta, setPdfFalta] = useState(null);
  const [salvandoFalta, setSalvandoFalta] = useState(false);
  const pdfFaltaRef = useRef(null);
  const [motivosFaltas, setMotivosFaltas] = useState({}); // { "funcId_dia": motivo }
  const [modalVerFalta, setModalVerFalta] = useState(null); // { funcId, dia, empNome }

  const [anoNum, mesNum] = mesAno.split('-').map(Number);
  const daysInMonth = new Date(anoNum, mesNum, 0).getDate();
  const days = Array.from({ length: daysInMonth }, (_, i) => i + 1);
  const DIAS_SEMANA = ["D","S","T","Q","Q","S","S"];
  const getDow = (dia) => new Date(anoNum, mesNum - 1, dia).getDay();
  const isWeekend = (dia) => { const d = getDow(dia); return d === 0 || d === 6; };

  // Carrega faltas/extras/trabFeriado e feriados do mês
  useEffect(() => {
    apiFetch("GET", `/api/ponto-terceiro?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => {
        const f = {}, ex = {}, tf = {};
        rows.forEach(r => {
          if (r.tipo === 'falta') f[`${r.funcionario_id}_${r.dia}`] = true;
          if (r.tipo === 'extra') ex[r.funcionario_id] = parseFloat(r.horas_extra) || 0;
          if (r.tipo === 'trabalhado_feriado') tf[`${r.funcionario_id}_${r.dia}`] = true;
        });
        setFaltas(f); setExtras(ex); setTrabFeriado(tf);
      }).catch(() => {});
    apiFetch("GET", `/api/ponto-observacoes?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => {
        const obs = {};
        (rows || []).forEach(r => { obs[`${r.funcionario_id}_${r.dia}`] = r.texto; });
        setObservacoes(obs);
      }).catch(() => {});
    carregarFeriados();
  }, [mesAno, token]);

  const carregarFeriados = () => {
    apiFetch("GET", `/api/feriados?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => setFeriados(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  };

  // Afastamentos do mês — usa estado global (atualiza em tempo real ao criar novo afastamento)
  const mesIniStr = `${anoNum}-${String(mesNum).padStart(2,'0')}-01`;
  const mesFimStr = new Date(anoNum, mesNum, 0).toISOString().slice(0, 10);
  const afastamentos = (afastamentosGlobal || []).filter(af => {
    const ini = af.inicio || '0000-00-00';
    const fim = af.fim || '9999-12-31';
    return ini <= mesFimStr && fim >= mesIniStr;
  });

  // Retorna tipo de afastamento de um funcionário em um dia
  const getDiaAfastamento = (funcId, dia) => {
    const dataStr = `${anoNum}-${String(mesNum).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    for (const af of afastamentos) {
      if (Number(af.funcionarioId) !== Number(funcId)) continue;
      const inicio = af.inicio || '0000-00-00';
      const fim = af.fim || '9999-12-31';
      if (dataStr >= inicio && dataStr <= fim) return af.tipo; // "Férias" | "Atestado Médico" | etc.
    }
    return null;
  };

  // Afastamentos únicos por funcionário (painel de atestado)
  const afastamentosPorFunc = {};
  afastamentos.forEach(af => {
    if (af.tipo !== 'Férias') {
      if (!afastamentosPorFunc[af.funcionarioId]) afastamentosPorFunc[af.funcionarioId] = [];
      afastamentosPorFunc[af.funcionarioId].push(af);
    }
  });

  const toggleFalta = (funcId, dia) => {
    const key = `${funcId}_${dia}`;
    setFaltas(prev => {
      const n = { ...prev };
      if (n[key]) {
        delete n[key];
        // Apaga observação associada à falta removida
        if (observacoes[key]) {
          setObservacoes(o => { const no = { ...o }; delete no[key]; return no; });
          apiFetch("DELETE", "/api/ponto-observacoes", { funcionario_id: funcId, mes: mesNum, ano: anoNum, dia }, token).catch(() => {});
        }
      } else {
        n[key] = true;
      }
      return n;
    });
    setSaved(false);
  };

  const abrirModalFalta = (emp, dia) => {
    const dateStr = `${anoNum}-${String(mesNum).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    setModalFalta({ funcId: emp.id, dia, empNome: emp.nome });
    setFormFalta({ tipo: "Sem atestado", inicio: dateStr, fim: "", motivo: "" });
    setPdfFalta(null);
  };

  const salvarFalta = async () => {
    if (!formFalta.inicio) return alert("Informe a data de início.");
    const docObrigatorio = formFalta.tipo !== "Sem atestado" && formFalta.tipo !== "Outros";
    if (docObrigatorio && !pdfFalta) return alert("Documento obrigatório para este tipo de ausência. Anexe o arquivo antes de salvar.");
    setSalvandoFalta(true);
    try {
      if (formFalta.tipo === "Sem atestado") {
        const toYMD = s => s.split('-').map(Number);
        const dim = (y, m) => new Date(y, m, 0).getDate(); // dias no mês
        const [sy, sm, sd] = toYMD(formFalta.inicio);
        const [ey, em, ed] = formFalta.fim ? toYMD(formFalta.fim) : [sy, sm, sd];
        const novasFaltas = {};
        const novosMotivosFaltas = {};
        const outrasMeses = {};
        // Itera dia a dia de inicio até fim (aritmética pura, sem new Date no loop)
        let cy = sy, cm = sm, cd = sd;
        while (true) {
          if (cy === anoNum && cm === mesNum) {
            const key = `${modalFalta.funcId}_${cd}`;
            novasFaltas[key] = true;
            novosMotivosFaltas[key] = formFalta.motivo || "";
          } else {
            const k = `${cy}-${String(cm).padStart(2, '0')}`;
            if (!outrasMeses[k]) outrasMeses[k] = { ano: cy, mes: cm, dias: [] };
            outrasMeses[k].dias.push(cd);
          }
          if (cy === ey && cm === em && cd === ed) break;
          cd++;
          if (cd > dim(cy, cm)) { cd = 1; cm++; if (cm > 12) { cm = 1; cy++; } }
        }
        // Mês atual: atualiza estado local
        setFaltas(prev => ({ ...prev, ...novasFaltas }));
        setSaved(false);
        // Salva motivos do mês atual no banco (ponto_observacoes)
        for (const [key, texto] of Object.entries(novosMotivosFaltas)) {
          const [fid, d] = key.split('_').map(Number);
          if (texto.trim()) {
            apiFetch("PUT", "/api/ponto-observacoes", { funcionario_id: fid, mes: mesNum, ano: anoNum, dia: d, texto: texto.trim() }, token)
              .then(() => setObservacoes(prev => ({ ...prev, [key]: texto.trim() })))
              .catch(() => {});
          }
        }
        // Outros meses: salva direto no banco
        for (const { ano, mes, dias } of Object.values(outrasMeses)) {
          try {
            const existentes = await apiFetch("GET", `/api/ponto-terceiro?mes=${mes}&ano=${ano}`, undefined, token);
            const filtrado = (existentes || []).filter(r =>
              !(r.tipo === 'falta' && r.funcionario_id === modalFalta.funcId && dias.includes(r.dia))
            );
            const novas = dias.map(dia => ({ funcionario_id: modalFalta.funcId, dia, tipo: 'falta', horas_extra: 0 }));
            await apiFetch("POST", "/api/ponto-terceiro", { mes, ano, lancamentos: [...filtrado, ...novas] }, token);
            // Salva motivo para outros meses também
            if (formFalta.motivo.trim()) {
              for (const dia of dias) {
                apiFetch("PUT", "/api/ponto-observacoes", { funcionario_id: modalFalta.funcId, mes, ano, dia, texto: formFalta.motivo.trim() }, token).catch(() => {});
              }
            }
          } catch {}
        }
      } else {
        const novo = await apiFetch("POST", "/api/ferias", {
          funcionario_id: modalFalta.funcId,
          tipo: formFalta.tipo,
          data_inicio: formFalta.inicio,
          data_fim: formFalta.fim || null,
          motivo: formFalta.motivo,
        }, token);
        if (pdfFalta && novo.id) {
          const fd = new FormData();
          fd.append("atestado", pdfFalta);
          await fetch(`/api/ferias/${novo.id}/atestado`, {
            method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
          }).catch(() => {});
        }
        if (setAfastamentos) setAfastamentos(prev => [...prev, adaptAfastamento(novo)]);
      }
      setModalFalta(null);
    } catch(e) { alert("Erro ao salvar: " + e.message); }
    setSalvandoFalta(false);
  };

  const abrirObs = (funcId, dia, e) => {
    const rect = e.currentTarget.getBoundingClientRect();
    const x = Math.min(rect.left, window.innerWidth - 260);
    const y = Math.min(rect.bottom + 6, window.innerHeight - 170);
    setObsPopover({ funcId, dia, texto: observacoes[`${funcId}_${dia}`] || "", x, y });
  };

  const salvarObs = async () => {
    if (!obsPopover) return;
    const { funcId, dia, texto } = obsPopover;
    const key = `${funcId}_${dia}`;
    setSavingObs(true);
    try {
      if (texto.trim()) {
        await apiFetch("PUT", "/api/ponto-observacoes", { funcionario_id: funcId, mes: mesNum, ano: anoNum, dia, texto: texto.trim() }, token);
        setObservacoes(prev => ({ ...prev, [key]: texto.trim() }));
      } else {
        await apiFetch("DELETE", "/api/ponto-observacoes", { funcionario_id: funcId, mes: mesNum, ano: anoNum, dia }, token);
        setObservacoes(prev => { const n = { ...prev }; delete n[key]; return n; });
      }
      setObsPopover(null);
    } catch(err) { alert("Erro ao salvar observação: " + err.message); }
    setSavingObs(false);
  };

  const removerObs = async () => {
    if (!obsPopover) return;
    const { funcId, dia } = obsPopover;
    setSavingObs(true);
    try {
      await apiFetch("DELETE", "/api/ponto-observacoes", { funcionario_id: funcId, mes: mesNum, ano: anoNum, dia }, token);
      setObservacoes(prev => { const n = { ...prev }; delete n[`${funcId}_${dia}`]; return n; });
      setObsPopover(null);
    } catch(err) { alert("Erro ao remover observação: " + err.message); }
    setSavingObs(false);
  };

  // Retorna o feriado lançado para a data/cidade do funcionário, se houver
  const getFeriado = (funcId, dia) => {
    const emp = employees.find(e => e.id === funcId);
    const cidade = emp?.cidade || "";
    const dataStr = `${anoNum}-${String(mesNum).padStart(2,'0')}-${String(dia).padStart(2,'0')}`;
    return feriados.find(f => feriadoCobreData(f, dataStr) && (f.cidade === 'Brasil' || f.cidade === cidade)) || null;
  };

  // Roxo (padrão) → Verde (trabalhou) → Vermelho (faltou, desconta prêmio e falta) → Roxo
  const toggleFeriado = (funcId, dia) => {
    const key = `${funcId}_${dia}`;
    const isTrab = !!trabFeriado[key];
    const isFaltaFer = !!faltas[key];
    if (isTrab) {
      setTrabFeriado(prev => { const n = { ...prev }; delete n[key]; return n; });
      setFaltas(prev => ({ ...prev, [key]: true }));
    } else if (isFaltaFer) {
      setFaltas(prev => { const n = { ...prev }; delete n[key]; return n; });
    } else {
      setTrabFeriado(prev => ({ ...prev, [key]: true }));
    }
    setSaved(false);
  };

  const salvarFeriado = async () => {
    if (!formFeriado.data) return alert("Informe a data do feriado.");
    if (formFeriado.data_fim && formFeriado.data_fim < formFeriado.data) return alert("Data fim não pode ser antes da data início.");
    setSalvandoFeriado(true);
    try {
      await apiFetch("POST", "/api/feriados", { data: formFeriado.data, data_fim: formFeriado.data_fim || null, nome: formFeriado.nome || null, cidade: formFeriado.cidade || 'Brasil' }, token);
      setFormFeriado({ data: "", data_fim: "", nome: "", cidade: "Brasil" });
      carregarFeriados();
    } catch(e) { alert(e.message); }
    setSalvandoFeriado(false);
  };

  const apagarFeriado = (id) => {
    setConfirmOpts({ msg: "Remover este feriado?", onOk: async () => {
      try {
        await apiFetch("DELETE", `/api/feriados/${id}`, undefined, token);
        carregarFeriados();
      } catch(e) { alert(e.message); }
    }});
  };

  const setExtra = (funcId, val) => {
    setExtras(prev => ({ ...prev, [funcId]: parseFloat(val) || 0 }));
    setSaved(false);
  };

  const salvar = async () => {
    setSaving(true);
    const lancamentos = [];
    Object.keys(faltas).forEach(key => {
      const [funcId, dia] = key.split('_').map(Number);
      lancamentos.push({ funcionario_id: funcId, dia, tipo: 'falta', horas_extra: 0 });
    });
    Object.entries(extras).forEach(([funcId, horas]) => {
      if (horas > 0) lancamentos.push({ funcionario_id: parseInt(funcId), dia: 0, tipo: 'extra', horas_extra: horas });
    });
    Object.keys(trabFeriado).forEach(key => {
      const [funcId, dia] = key.split('_').map(Number);
      lancamentos.push({ funcionario_id: funcId, dia, tipo: 'trabalhado_feriado', horas_extra: 0 });
    });
    try {
      await apiFetch("POST", "/api/ponto-terceiro", { mes: mesNum, ano: anoNum, lancamentos }, token);
      setSaved(true);
    } catch(e) { alert(e.message); }
    setSaving(false);
  };

  const limparMes = () => {
    setConfirmOpts({ msg: `Limpar todos os lançamentos de ponto de ${mesAno}? Isso remove faltas e horas extras do mês.`, onOk: async () => {
      setSaving(true);
      try {
        await apiFetch("POST", "/api/ponto-terceiro", { mes: mesNum, ano: anoNum, lancamentos: [] }, token);
        setFaltas({});
        setExtras({});
        setTrabFeriado({});
        setSaved(true);
      } catch(e) { alert(e.message); }
      setSaving(false);
    }});
  };

  const uploadAtestadoPonto = async (afId, file) => {
    setUploadingAtestado(afId);
    setMsgAtestado(prev => ({ ...prev, [afId]: "" }));
    const fd = new FormData();
    fd.append("atestado", file);
    try {
      const resp = await fetch(`/api/ferias/${afId}/atestado`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.erro || "Erro ao enviar");
      setMsgAtestado(prev => ({ ...prev, [afId]: `✅ ${data.nome}` }));
    } catch(e) {
      setMsgAtestado(prev => ({ ...prev, [afId]: `❌ ${e.message}` }));
    }
    setUploadingAtestado(null);
  };

  const baixarAtestado = (afId) => {
    const a = document.createElement('a');
    a.href = `/api/ferias/${afId}/atestado`;
    a.setAttribute('download', '');
    // Adiciona token via query string não é ideal; usaremos abertura direta com fetch+blob
    fetch(`/api/ferias/${afId}/atestado`, { headers: { Authorization: `Bearer ${token}` } })
      .then(r => r.blob()).then(blob => {
        const url = URL.createObjectURL(blob);
        a.href = url;
        document.body.appendChild(a); a.click();
        setTimeout(() => { URL.revokeObjectURL(url); document.body.removeChild(a); }, 1000);
      });
  };

  const ativos = employees.filter(e => e.status !== "Demitido" && !e.pre_cadastro && e.nome.toLowerCase().includes(filtro.toLowerCase()));
  // Exclui faltas que caem em dias de afastamento/férias (mesma lógica do grid)
  const totalFaltas = Object.keys(faltas).filter(key => {
    const [fid, dia] = key.split('_').map(Number);
    return !getDiaAfastamento(fid, dia);
  }).length;
  const totalExtras = Object.values(extras).reduce((s, v) => s + v, 0);
  const totalAfastados = Object.values(afastamentosPorFunc).length;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {confirmOpts && <ConfirmModal msg={confirmOpts.msg} onOk={confirmOpts.onOk} onClose={() => setConfirmOpts(null)} />}

      {/* Modal ver falta — abre ao clicar em falta existente */}
      {modalVerFalta && (
        <Modal title="Detalhe da Falta" onClose={() => setModalVerFalta(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 4 }}>Funcionário</div>
              <div style={{ fontSize: 14 }}>{modalVerFalta.empNome}</div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 4 }}>Data</div>
              <div style={{ fontSize: 14 }}>
                {String(modalVerFalta.dia).padStart(2, '0')}/{String(mesNum).padStart(2, '0')}/{anoNum}
              </div>
            </div>
            <div>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 4 }}>Motivo</div>
              <div style={{ fontSize: 14, color: (observacoes[`${modalVerFalta.funcId}_${modalVerFalta.dia}`] || motivosFaltas[`${modalVerFalta.funcId}_${modalVerFalta.dia}`]) ? COLORS.text : COLORS.textMid }}>
                {observacoes[`${modalVerFalta.funcId}_${modalVerFalta.dia}`] || motivosFaltas[`${modalVerFalta.funcId}_${modalVerFalta.dia}`] || "Sem motivo registrado"}
              </div>
            </div>
          </div>
          <div style={{ display: "flex", justifyContent: "space-between", marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setModalVerFalta(null)}>Fechar</Btn>
            <Btn variant="danger" onClick={() => {
              setConfirmOpts({
                msg: `Remover falta de ${modalVerFalta.empNome} no dia ${String(modalVerFalta.dia).padStart(2, '0')}/${String(mesNum).padStart(2, '0')}?`,
                onOk: () => { toggleFalta(modalVerFalta.funcId, modalVerFalta.dia); setModalVerFalta(null); }
              });
            }}>Remover falta</Btn>
          </div>
        </Modal>
      )}

      {/* Modal de ausência — abre ao clicar em dia trabalhado */}
      {modalFalta && (
        <Modal title="Registrar Ausência" onClose={() => setModalFalta(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
            <Select label="Funcionário" value={String(modalFalta.funcId)} disabled onChange={() => {}}>
              <option value={String(modalFalta.funcId)}>{modalFalta.empNome}</option>
            </Select>
            <Select label="Tipo" value={formFalta.tipo} onChange={e => { setFormFalta(f => ({ ...f, tipo: e.target.value })); setPdfFalta(null); if (pdfFaltaRef.current) pdfFaltaRef.current.value = ""; }}>
              <option>Sem atestado</option>
              <option>Atestado Médico</option>
              <option>Licença Maternidade</option>
              <option>Licença Paternidade</option>
              <option>Acidente de Trabalho</option>
              <option>Outros</option>
            </Select>
            <Input label="Data Início" type="date" value={formFalta.inicio} onChange={e => setFormFalta(f => ({ ...f, inicio: e.target.value }))} />
            <Input label="Data Fim" type="date" value={formFalta.fim} min={formFalta.inicio || undefined} onChange={e => setFormFalta(f => ({ ...f, fim: e.target.value }))} />

            <div style={{ gridColumn: "1 / -1" }}>
              <Input label="Motivo" value={formFalta.motivo} onChange={e => setFormFalta(f => ({ ...f, motivo: e.target.value }))} placeholder="Descreva o motivo..." />
            </div>
            {formFalta.tipo !== "Sem atestado" && (
              <div style={{ gridColumn: "1 / -1" }}>
                <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 }}>
                  Documento (PDF, JPG, PNG — máx. 10MB)
                  {formFalta.tipo !== "Outros" && <span style={{ color: "#dc2626", marginLeft: 4 }}>* obrigatório</span>}
                </div>
                <input ref={pdfFaltaRef} type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display: "none" }}
                  onChange={e => setPdfFalta(e.target.files[0] || null)} />
                <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
                  <Btn size="sm" variant="ghost" onClick={() => pdfFaltaRef.current?.click()}>
                    📎 {pdfFalta ? "Trocar arquivo" : "Anexar documento"}
                  </Btn>
                  {pdfFalta && (
                    <span style={{ fontSize: 12, color: COLORS.primary, fontWeight: 600 }}>
                      {pdfFalta.name}
                      <button onClick={() => { setPdfFalta(null); if (pdfFaltaRef.current) pdfFaltaRef.current.value = ""; }}
                        style={{ marginLeft: 8, background: "none", border: "none", cursor: "pointer", color: "#dc2626", fontSize: 14 }}>✕</button>
                    </span>
                  )}
                </div>
              </div>
            )}
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setModalFalta(null)}>Cancelar</Btn>
            <Btn onClick={salvarFalta} disabled={salvandoFalta}>{salvandoFalta ? "Salvando..." : "Salvar"}</Btn>
          </div>
        </Modal>
      )}

      {/* Toolbar */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Input label="Mês de referência" type="month" value={mesAno} onChange={e => { setMesAno(e.target.value); setSaved(false); }} />
        <Input label="Filtrar funcionário" value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Nome..." style={{ minWidth: 200 }} />
        <Btn onClick={salvar} disabled={saving}>
          {saving ? "⏳ Salvando..." : saved ? "✅ Salvo!" : "Salvar Lançamentos"}
        </Btn>
        <Btn variant="ghost" onClick={limparMes} disabled={saving} style={{ color: COLORS.danger, borderColor: COLORS.danger }}>
          Limpar Mês
        </Btn>
        <Btn variant="ghost" onClick={() => setModalFeriado(true)} style={{ color: "#9333ea", borderColor: "#9333ea" }}>
          + Feriado
        </Btn>
        <Btn variant="ghost" onClick={() => { setObsMode(m => !m); setObsPopover(null); }}
          style={{ color: obsMode ? "#f97316" : COLORS.textMid, borderColor: obsMode ? "#f97316" : COLORS.border, background: obsMode ? "#fff7ed" : undefined, fontWeight: obsMode ? 700 : undefined }}>
          ✏️ {obsMode ? "Obs (ativo)" : "Obs"}
        </Btn>
      </div>

      {/* Modal de lançamento de feriado */}
      {modalFeriado && (
        <Modal titulo="Feriados" onClose={() => setModalFeriado(false)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14, minWidth: 340 }}>
            <div style={{ fontSize: 12, color: COLORS.textMid }}>
              Ao lançar um feriado, os funcionários da cidade selecionada (ou todos, se "Brasil") terão o(s) dia(s) marcado(s) automaticamente como roxo no grid. Deixe "Data Fim" vazio para um feriado de dia único, ou preencha para lançar um período.
            </div>
            <Input label="Data (início)" type="date" value={formFeriado.data} onChange={e => setFormFeriado(f => ({ ...f, data: e.target.value }))} />
            <Input label="Data Fim (opcional, para período)" type="date" value={formFeriado.data_fim} onChange={e => setFormFeriado(f => ({ ...f, data_fim: e.target.value }))} min={formFeriado.data || undefined} />
            <Input label="Nome do feriado (opcional)" value={formFeriado.nome} onChange={e => setFormFeriado(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Corpus Christi" />
            <Input label="Cidade (ou 'Brasil' para todos)" value={formFeriado.cidade} onChange={e => setFormFeriado(f => ({ ...f, cidade: e.target.value }))} placeholder="Brasil" />
            <Btn onClick={salvarFeriado} disabled={salvandoFeriado}>{salvandoFeriado ? "Salvando..." : "Adicionar Feriado"}</Btn>
            {feriados.length > 0 && (
              <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 10, display: "flex", flexDirection: "column", gap: 6, maxHeight: 200, overflowY: "auto" }}>
                <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMid }}>Feriados deste mês:</div>
                {feriados.map(f => (
                  <div key={f.id} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", fontSize: 12, padding: "4px 8px", background: "#f3e8ff", borderRadius: 6 }}>
                    <span>{String(f.data).slice(0,10)}{f.data_fim ? ` a ${String(f.data_fim).slice(0,10)}` : ""} — {f.nome || "Feriado"} ({f.cidade})</span>
                    <button onClick={() => apagarFeriado(f.id)} style={{ border: "none", background: "none", color: COLORS.danger, cursor: "pointer", fontWeight: 700 }}>✕</button>
                  </div>
                ))}
              </div>
            )}
            <div style={{ display: "flex", justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setModalFeriado(false)}>Fechar</Btn>
            </div>
          </div>
        </Modal>
      )}

      {/* Cards resumo */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        <Card style={{ borderTop: `3px solid ${COLORS.primary}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>Funcionários</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.primary }}>{ativos.length}</div>
        </Card>
        <Card style={{ borderTop: `3px solid ${COLORS.danger}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>Faltas no mês</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.danger }}>{totalFaltas}</div>
        </Card>
        <Card style={{ borderTop: `3px solid ${COLORS.accent}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>H. Extras totais</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: COLORS.accent }}>{totalExtras}h</div>
        </Card>
        <Card style={{ borderTop: "3px solid #f59e0b" }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>Afastados/Férias</div>
          <div style={{ fontSize: 22, fontWeight: 800, color: "#f59e0b" }}>{totalAfastados + afastamentos.filter(a => a.tipo === 'ferias').length > 0 ? afastamentos.length : 0}</div>
        </Card>
      </div>

      {/* Legenda */}
      <div style={{ display: "flex", gap: 12, fontSize: 12, color: COLORS.textMid, alignItems: "center", flexWrap: "wrap" }}>
        <span style={{ fontWeight: 600 }}>Legenda:</span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 20, height: 20, background: "#dcfce7", border: "1px solid #86efac", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#16a34a", fontWeight: 700 }}>✓</span> Trabalhado
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 20, height: 20, background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#ef4444", fontWeight: 700 }}>✗</span> Falta
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 20, height: 20, background: "#fef9c3", border: "1px solid #fde68a", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>🏖</span> Férias
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 20, height: 20, background: "#fff7ed", border: "1px solid #fed7aa", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 12 }}>🏥</span> Afastamento
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 20, height: 20, background: "#f3e8ff", border: "1px solid #d8b4fe", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 11, color: "#9333ea", fontWeight: 700 }}>★</span> Feriado
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ width: 20, height: 20, background: "#f3f4f6", border: "1px solid #d1d5db", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#9ca3af" }}>—</span> Fim de semana
        </span>
        <span style={{ display: "flex", alignItems: "center", gap: 4 }}>
          <span style={{ position: "relative", width: 20, height: 20, display: "inline-flex" }}>
            <span style={{ width: 20, height: 20, background: "#dcfce7", border: "1px solid #bbf7d0", borderRadius: 4, display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, color: "#16a34a" }}>✓</span>
            <span style={{ position: "absolute", top: 1, right: 1, width: 6, height: 6, borderRadius: "50%", background: "#f97316", border: "1px solid #fff" }} />
          </span> Obs. lançada
        </span>
      </div>

      {/* Grade */}
      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ borderCollapse: "collapse", fontSize: 12, minWidth: "max-content", width: "100%" }}>
            <thead>
              <tr style={{ background: COLORS.sidebar }}>
                <th style={{ padding: "10px 14px", textAlign: "left", color: "#94a3b8", fontWeight: 600, fontSize: 11, minWidth: 180, position: "sticky", left: 0, background: COLORS.sidebar, zIndex: 2 }}>FUNCIONÁRIO</th>
                {days.map(d => (
                  <th key={d} style={{
                    padding: "4px 0", textAlign: "center", width: 32, minWidth: 32,
                    color: isWeekend(d) ? "#475569" : "#94a3b8", fontWeight: 600,
                  }}>
                    <div style={{ fontSize: 10, color: isWeekend(d) ? "#475569" : "#64748b" }}>{DIAS_SEMANA[getDow(d)]}</div>
                    <div style={{ fontSize: 11 }}>{d}</div>
                  </th>
                ))}
                <th style={{ padding: "10px 12px", textAlign: "center", color: "#f59e0b", fontWeight: 600, fontSize: 11, minWidth: 80 }}>H. EXTRA</th>
                <th style={{ padding: "10px 12px", textAlign: "center", color: "#94a3b8", fontWeight: 600, fontSize: 11, minWidth: 60 }}>FALTAS</th>
              </tr>
            </thead>
            <tbody>
              {ativos.map((emp, idx) => {
                // Dias em que o funcionário NÃO trabalha (folga), baseado na escala dele
                const is12x36emp = /12[Xx]36/i.test(emp.turno);
                const trabSet = new Set(emp.dias_semana && emp.dias_semana.length ? emp.dias_semana : [1,2,3,4,5]);
                const isFolga = (d) => is12x36emp ? false : !trabSet.has(new Date(anoNum, mesNum - 1, d).getDay());
                const faltasFunc = days.filter(d => {
                  if (!faltas[`${emp.id}_${d}`] || getDiaAfastamento(emp.id, d)) return false;
                  return !!getFeriado(emp.id, d) || !isFolga(d);
                }).length;
                const rowBg = idx % 2 === 0 ? COLORS.white : COLORS.gray;
                return (
                  <tr key={emp.id} style={{ background: rowBg, borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "8px 14px", fontWeight: 600, fontSize: 13, position: "sticky", left: 0, background: rowBg, zIndex: 1, borderRight: `1px solid ${COLORS.border}` }}>
                      <div>{emp.nome}</div>
                      <div style={{ fontSize: 10, color: COLORS.textMid, fontWeight: 400 }}>{emp.cargo}</div>
                    </td>
                    {days.map(d => {
                      const key = `${emp.id}_${d}`;
                      const tipoAf = getDiaAfastamento(emp.id, d);
                      const isFalta = !!faltas[key];
                      const fim = isFolga(d);
                      const hasObs = !!observacoes[key];
                      const obsTxt = observacoes[key] || "";
                      // dot: pointerEvents ativo para o title funcionar no hover
                      const dot = hasObs ? (
                        <span title={obsTxt} style={{ position: "absolute", top: 3, right: 3, width: 6, height: 6, borderRadius: "50%", background: "#f97316", zIndex: 2, boxShadow: "0 0 0 1px #fff", cursor: "default" }} />
                      ) : null;
                      // Em modo Obs: visual idêntico ao normal, só o cursor muda para crosshair
                      const obsCursor = obsMode ? "crosshair" : undefined;

                      if (tipoAf === 'Férias') {
                        return (
                          <td key={d} title={hasObs ? obsTxt : undefined} style={{ padding: "3px 2px", textAlign: "center", position: "relative", cursor: obsCursor }} onClick={obsMode ? e => abrirObs(emp.id, d, e) : undefined}>
                            {dot}
                            <span title="Férias" style={{ display: "inline-flex", width: 26, height: 26, borderRadius: 4, background: "#fef9c3", border: "1px solid #fde68a", alignItems: "center", justifyContent: "center", fontSize: 13, pointerEvents: "none" }}>🏖</span>
                          </td>
                        );
                      }
                      if (tipoAf && tipoAf !== 'Férias') {
                        return (
                          <td key={d} title={hasObs ? obsTxt : undefined} style={{ padding: "3px 2px", textAlign: "center", position: "relative", cursor: obsCursor }} onClick={obsMode ? e => abrirObs(emp.id, d, e) : undefined}>
                            {dot}
                            <span title={`${tipoAf} — para excluir, acesse a aba Afastamentos`} style={{ display: "inline-flex", width: 26, height: 26, borderRadius: 4, background: "#fff7ed", border: "1px solid #fed7aa", alignItems: "center", justifyContent: "center", fontSize: 13, pointerEvents: "none" }}>🏥</span>
                          </td>
                        );
                      }
                      const feriadoDia = getFeriado(emp.id, d);
                      if (feriadoDia) {
                        const isTrab = !!trabFeriado[key];
                        const isFaltaFer = !!faltas[key];
                        let bg = "#f3e8ff", color = "#9333ea", label = "★", cellTitle = `Feriado${feriadoDia.nome ? ': ' + feriadoDia.nome : ''} — clique para marcar trabalhado`;
                        if (isTrab) { bg = "#dcfce7"; color = "#16a34a"; label = "✓"; cellTitle = "Trabalhou no feriado — clique para marcar falta"; }
                        else if (isFaltaFer) { bg = "#fee2e2"; color = "#ef4444"; label = "✗"; cellTitle = "Faltou no feriado — clique para voltar a feriado"; }
                        return (
                          <td key={d} title={hasObs ? obsTxt : undefined} style={{ padding: "3px 2px", textAlign: "center", position: "relative" }}>
                            {dot}
                            <button
                              onClick={obsMode ? e => abrirObs(emp.id, d, e) : () => toggleFeriado(emp.id, d)}
                              title={cellTitle}
                              style={{
                                width: 26, height: 26, borderRadius: 4, border: "none", cursor: obsMode ? "crosshair" : "pointer",
                                background: bg, color, fontWeight: 800, fontSize: 13,
                                display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all .1s",
                              }}
                            >{label}</button>
                          </td>
                        );
                      }
                      return (
                        <td key={d} title={hasObs ? obsTxt : undefined} style={{ padding: "3px 2px", textAlign: "center", position: "relative" }}>
                          {dot}
                          {fim ? (
                            <span onClick={obsMode ? e => abrirObs(emp.id, d, e) : undefined}
                              style={{ display: "inline-flex", width: 26, height: 26, borderRadius: 4, background: COLORS.gray, alignItems: "center", justifyContent: "center", fontSize: 10, color: COLORS.textLight, cursor: obsMode ? "crosshair" : "default" }}>
                              —
                            </span>
                          ) : (
                            <button
                              onClick={obsMode ? e => abrirObs(emp.id, d, e) : isFalta ? () => setModalVerFalta({ funcId: emp.id, dia: d, empNome: emp.nome }) : () => abrirModalFalta(emp, d)}
                              title={isFalta ? "Falta — clique para ver detalhes" : "Trabalhado — clique para registrar ausência"}
                              style={{
                                width: 26, height: 26, borderRadius: 4, border: "none", cursor: obsMode ? "crosshair" : "pointer",
                                background: isFalta ? "#fee2e2" : "#dcfce7",
                                color: isFalta ? "#ef4444" : "#16a34a",
                                fontWeight: 800, fontSize: 13,
                                display: "inline-flex", alignItems: "center", justifyContent: "center", transition: "all .1s",
                              }}
                            >{isFalta ? "✗" : "✓"}</button>
                          )}
                        </td>
                      );
                    })}
                    {/* H. Extra */}
                    <td style={{ padding: "4px 8px", textAlign: "center" }}>
                      <input
                        type="number" min="0" step="0.5"
                        value={extras[emp.id] || ""}
                        onChange={e => setExtra(emp.id, e.target.value)}
                        placeholder="0"
                        style={{
                          width: 60, textAlign: "center", border: `1px solid ${COLORS.border}`,
                          borderRadius: 6, padding: "4px 6px", fontSize: 12,
                          background: (extras[emp.id] || 0) > 0 ? "#fffbeb" : COLORS.surface,
                          color: (extras[emp.id] || 0) > 0 ? "#d97706" : COLORS.text,
                          fontWeight: (extras[emp.id] || 0) > 0 ? 700 : 400, outline: "none",
                        }}
                      />
                    </td>
                    {/* Total faltas (não conta dias de afastamento/férias) */}
                    <td style={{ padding: "4px 8px", textAlign: "center", fontWeight: 700, color: faltasFunc > 0 ? COLORS.danger : COLORS.textMid, fontSize: 13 }}>
                      {faltasFunc > 0 ? faltasFunc : "—"}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      {/* Popover de observação */}
      {obsPopover && (
        <div style={{ position: "fixed", top: obsPopover.y, left: obsPopover.x, zIndex: 9999, background: "#fff", border: `1.5px solid #f97316`, borderRadius: 10, boxShadow: "0 8px 24px rgba(15,23,42,0.18)", padding: 14, width: 248 }}>
          <div style={{ fontSize: 12, fontWeight: 700, color: "#f97316", marginBottom: 8 }}>
            ✏️ Observação — dia {obsPopover.dia}
          </div>
          <textarea
            autoFocus
            value={obsPopover.texto}
            onChange={e => setObsPopover(p => ({ ...p, texto: e.target.value }))}
            onKeyDown={e => { if (e.key === "Enter" && (e.ctrlKey || e.metaKey)) salvarObs(); if (e.key === "Escape") setObsPopover(null); }}
            placeholder="Escreva a observação..."
            rows={3}
            style={{ width: "100%", borderRadius: 6, border: `1.5px solid #e2e8f0`, padding: "6px 8px", fontSize: 12, resize: "none", boxSizing: "border-box", outline: "none" }}
          />
          <div style={{ fontSize: 10, color: COLORS.textLight, marginBottom: 6 }}>Ctrl+Enter para salvar · Esc para fechar</div>
          <div style={{ display: "flex", gap: 6 }}>
            <button onClick={salvarObs} disabled={savingObs}
              style={{ flex: 1, background: "#f97316", color: "#fff", border: "none", borderRadius: 6, padding: "6px 0", fontSize: 12, cursor: "pointer", fontWeight: 600 }}>
              {savingObs ? "..." : "Salvar"}
            </button>
            {observacoes[`${obsPopover.funcId}_${obsPopover.dia}`] && (
              <button onClick={removerObs} disabled={savingObs}
                style={{ background: "#fee2e2", color: "#ef4444", border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
                Apagar
              </button>
            )}
            <button onClick={() => setObsPopover(null)}
              style={{ background: "#f1f5f9", color: COLORS.textMid, border: "none", borderRadius: 6, padding: "6px 10px", fontSize: 12, cursor: "pointer" }}>
              ✕
            </button>
          </div>
        </div>
      )}
      {obsPopover && <div style={{ position: "fixed", inset: 0, zIndex: 9998 }} onClick={() => setObsPopover(null)} />}
    </div>
  );
}

// ─── VT RECARGA ───────────────────────────────────────────────────────────────
function VTRecarga({ token, usuario, tipo }) {
  const hoje = new Date();
  const curMes = hoje.getMonth() + 1;
  const [mes, setMes] = useState(curMes === 12 ? 1 : curMes + 1);
  const [ano, setAno] = useState(curMes === 12 ? hoje.getFullYear() + 1 : hoje.getFullYear());
  const [dados, setDados] = useState([]);
  const [feriados, setFeriados] = useState(new Set());
  const [afastamentosRef, setAfastamentosRef] = useState({});
  const [loading, setLoading] = useState(false);
  const [erro, setErro] = useState(null);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEscala, setFiltroEscala] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [salvando, setSalvando] = useState({});
  const [localEdits, setLocalEdits] = useState({});
  const [msgSalvo, setMsgSalvo] = useState("");

  const isDirty = Object.keys(localEdits).length > 0;

  // Faltas sempre do mês anterior ao selecionado
  const mesFaltas = mes === 1 ? 12 : mes - 1;
  const anoFaltas = mes === 1 ? ano - 1 : ano;

  const confirmarTroca = (fn) => {
    if (isDirty && !window.confirm("Há alterações não salvas. Deseja descartar e continuar?")) return;
    fn();
  };

  useEffect(() => { setLocalEdits({}); setMsgSalvo(""); setFiltroEscala(""); setFiltroEmpresa(""); }, [mes, ano, tipo]);

  const getCreditoLocal = (r) => localEdits[r.funcionario_id]?.credito ?? parseFloat(r[`credito_${tipo}`] || 0).toFixed(2);

  const salvarTudoVT = async () => {
    setMsgSalvo("");
    try {
      await Promise.all(linhas.map(r => salvarCredito(r.funcionario_id, getCreditoLocal(r))));
      setMsgSalvo("ok");
      setTimeout(() => setMsgSalvo(""), 4000);
    } catch(e) { setMsgSalvo("erro"); }
  };

  useEffect(() => {
    setLoading(true);
    setErro(null);
    const pad = n => String(n).padStart(2, '0');
    Promise.all([
      apiFetch("GET", `/api/beneficios/vt-recarga?mes=${mes}&ano=${ano}`, undefined, token),
      apiFetch("GET", `/api/ponto-terceiro?mes=${mesFaltas}&ano=${anoFaltas}`, undefined, token).catch(() => []),
      apiFetch("GET", `/api/feriados?mes=${mesFaltas}&ano=${anoFaltas}`, undefined, token).catch(() => []),
      apiFetch("GET", `/api/ferias/por-mes?mes=${mesFaltas}&ano=${anoFaltas}`, undefined, token).catch(() => []),
    ]).then(([rows, pontoTerceiro, feriadosList, afastamentosList]) => {
      const afMap = {};
      (afastamentosList || []).forEach(af => {
        const fid = af.funcionario_id;
        if (!afMap[fid]) afMap[fid] = [];
        afMap[fid].push({ inicio: af.data_inicio, fim: af.data_fim || '9999-12-31' });
      });
      setAfastamentosRef(afMap);
      const faltasMap = {};
      (pontoTerceiro || []).forEach(r => {
        if (r.tipo === 'falta') {
          const dateStr = `${anoFaltas}-${pad(mesFaltas)}-${pad(r.dia)}`;
          const coberto = (afMap[r.funcionario_id] || []).some(af => dateStr >= af.inicio && dateStr <= af.fim);
          if (!coberto) faltasMap[r.funcionario_id] = (faltasMap[r.funcionario_id] || 0) + 1;
        }
      });
      // Feriados do mês de REFERÊNCIA
      const feriadosDias = new Set();
      const totalDiasRef = new Date(anoFaltas, mesFaltas, 0).getDate();
      (feriadosList || []).forEach(f => {
        const inicioStr = f.data; const fimStr = f.data_fim || f.data;
        for (let d = 1; d <= totalDiasRef; d++) {
          const dStr = `${anoFaltas}-${pad(mesFaltas)}-${pad(d)}`;
          if (dStr >= inicioStr && dStr <= fimStr) feriadosDias.add(d);
        }
      });
      setFeriados(feriadosDias);
      setDados((rows || []).map(r => ({ ...r, faltas: faltasMap[r.funcionario_id] || 0 })));
    }).catch(e => setErro(e.message || "Erro ao carregar dados")).finally(() => setLoading(false));
  }, [mes, ano, tipo, token]);

  const diasNoMes = (m, a) => new Date(a, m, 0).getDate();

  // Dias a trabalhar = mês a CARREGAR (mes/ano); faltas/feriados/afastamentos = mês de REFERÊNCIA
  const diasATrabalhar = (row) => {
    const total = diasNoMes(mes, ano);
    if (row.turno === "12X36") return Math.ceil(total / 2);
    const diasStr = row.dias_semana ? String(row.dias_semana) : "1,2,3,4,5";
    if (diasStr === "0,1,2,3,4,5,6") return Math.ceil(total / 2);
    let count = 0;
    for (let d = 1; d <= total; d++) {
      const dow = new Date(ano, mes - 1, d).getDay();
      if (diasStr.includes(String(dow))) count++;
    }
    return count;
  };

  const diasTrabalhados = (row) => {
    const total = diasNoMes(mes, ano);
    if (row.turno === "12X36") return Math.ceil(total / 2);
    const diasStr = row.dias_semana ? String(row.dias_semana) : "1,2,3,4,5";
    if (diasStr === "0,1,2,3,4,5,6") return Math.ceil(total / 2);
    let count = 0;
    for (let d = 1; d <= total; d++) {
      if (feriados.has(d)) continue;
      const dow = new Date(ano, mes - 1, d).getDay();
      if (diasStr.includes(String(dow))) count++;
    }
    return count;
  };

  const escalaLabel = (row) => {
    if (row.turno === "12X36") return "12X36";
    const ds = String(row.dias_semana || "");
    if (ds === "0,1,2,3,4,5,6") return "12X36";
    if (ds === "1,2,3,4,5,6")   return "Seg a Sáb";
    if (ds === "1,2,3,4,5")     return "Seg a Sex";
    if (ds === "1,3,5")          return "Seg/Qua/Sex";
    if (ds === "2,4,6")          return "Ter/Qui/Sáb";
    if (ds)                      return ds;
    return "—";
  };

  const vtDia = (row) => parseFloat(row[`vt_valor_${tipo}`]) || 0;

  // Conta dias úteis do funcionário no mês de REFERÊNCIA cobertos por férias/afastamento
  const diasAfastamento = (row) => {
    const afs = afastamentosRef[row.funcionario_id] || [];
    if (!afs.length) return 0;
    const p = n => String(n).padStart(2, '0');
    const totalRef = diasNoMes(mesFaltas, anoFaltas);
    const dsStr = row.dias_semana ? String(row.dias_semana) : "1,2,3,4,5";
    const is12x36 = row.turno === "12X36" || dsStr === "0,1,2,3,4,5,6";
    if (is12x36) {
      // 12x36: conta dias cobertos e divide por 2 (aprox.)
      let covered = 0;
      for (let d = 1; d <= totalRef; d++) {
        const ds2 = `${anoFaltas}-${p(mesFaltas)}-${p(d)}`;
        if (afs.some(af => ds2 >= af.inicio && ds2 <= af.fim)) covered++;
      }
      return Math.ceil(covered / 2);
    }
    let count = 0;
    for (let d = 1; d <= totalRef; d++) {
      const ds2 = `${anoFaltas}-${p(mesFaltas)}-${p(d)}`;
      const dow = new Date(anoFaltas, mesFaltas - 1, d).getDay();
      if (dsStr.includes(String(dow)) && afs.some(af => ds2 >= af.inicio && ds2 <= af.fim)) count++;
    }
    return count;
  };

  const totalCarregar = (row) => {
    const val = vtDia(row);
    const credito = parseFloat(row[`credito_${tipo}`] || 0);
    if (tipo === "gasolina") return Math.max(0, val - credito);
    return Math.max(0, val * diasATrabalhar(row) - credito);
  };

  const feriadosDoFuncionario = (row) => {
    if (row.turno === "12X36") return 0;
    const diasStr = row.dias_semana ? String(row.dias_semana) : "1,2,3,4,5";
    if (diasStr === "0,1,2,3,4,5,6") return 0;
    let count = 0;
    for (const d of feriados) {
      const dow = new Date(anoFaltas, mesFaltas - 1, d).getDay();
      if (diasStr.includes(String(dow))) count++;
    }
    return count;
  };

  const salvarCredito = async (fid, val) => {
    setSalvando(s => ({ ...s, [`c${fid}`]: true }));
    try {
      await apiFetch("PUT", `/api/beneficios/vt-recarga/${fid}`, { mes, ano, tipo, credito_cartao: parseFloat(val) || 0 }, token);
      setDados(prev => prev.map(r => r.funcionario_id === fid ? { ...r, [`credito_${tipo}`]: parseFloat(val) || 0 } : r));
    } catch(e) {}
    setSalvando(s => ({ ...s, [`c${fid}`]: false }));
  };

  const MESES_VT = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
  const TIPO_LABELS_VT = { bhbus: "BHBus", otimo: "Ótimo", gasolina: "Gasolina", betimcard: "Betim Card", outros: "Outros", nao_pega: "Não pega VT" };

  const todosVT = dados.filter(r => parseVtTipos(r.vt_tipo).includes(tipo));
  const escalasDispVT = [...new Set(todosVT.map(r => escalaLabel(r)))].filter(e => e && e !== "—").sort();
  const empresasDispVT = [...new Set(todosVT.map(r => r.empresa || '').filter(Boolean))].sort();

  const linhas = todosVT
    .filter(r => !filtroNome || r.nome.toLowerCase().includes(filtroNome.toLowerCase()))
    .filter(r => !filtroEscala || escalaLabel(r) === filtroEscala)
    .filter(r => !filtroEmpresa || (r.empresa || '') === filtroEmpresa);

  const totalTipo = linhas.reduce((s, r) => s + totalCarregar(r), 0);

  const resumoEscalasVT = (() => {
    const map = {};
    linhas.forEach(r => {
      const lbl = escalaLabel(r);
      if (!(lbl in map)) map[lbl] = diasATrabalhar(r);
    });
    return map;
  })();

  const imprimir = () => {
    const titulo = `Recarga VT — ${TIPO_LABELS_VT[tipo]} — ${MESES_VT[mes-1]}/${ano} (faltas ref. ${MESES_VT[mesFaltas-1]}/${anoFaltas})`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title>
    <style>
      @page { size: landscape; margin: 15mm; }
      body { font-family: Arial, sans-serif; font-size: 12px; }
      h2 { margin-bottom: 8px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #1e3a5f; color: #fff; padding: 7px 8px; text-align: left; font-size: 11px; }
      td { padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
      tr:nth-child(even) td { background: #f5f8ff; }
      .num { text-align: right; }
      tfoot td { font-weight: bold; background: #e8f0fe; }
    </style></head><body>
    <h2>${titulo}</h2>
    <table>
      <thead><tr>
        <th>Funcionário</th><th>Empresa</th><th>Escala</th><th>Dias a Trabalhar</th>
        <th class="num">VT/dia (R$)</th><th class="num">Crédito Cartão (R$)</th><th class="num">Total a Carregar (R$)</th>
      </tr></thead>
      <tbody>${linhas.map(r => {
        const dm = diasATrabalhar(r);
        const afDias = diasAfastamento(r);
        const total = totalCarregar(r);
        return `<tr>
          <td>${r.nome}</td>
          <td>${r.empresa || '—'}</td>
          <td>${escalaLabel(r)}</td>
          <td class="num">${dm}</td>
          <td class="num">${vtDia(r).toFixed(2)}</td>
          <td class="num">${parseFloat(getCreditoLocal(r) || 0).toFixed(2)}</td>
          <td class="num"><b>${total.toFixed(2)}</b></td>
        </tr>`;
      }).join("")}</tbody>
      <tfoot><tr>
        <td colspan="6">TOTAL</td>
        <td class="num">${linhas.reduce((s,r) => s + totalCarregar(r), 0).toFixed(2)}</td>
      </tr></tfoot>
    </table>
    </body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.print(); };
  };

  return (
    <div>
      <div style={{ marginBottom: 20 }}>
        <div style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: "0.08em" }}>Vale Transporte</div>
      </div>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 18 }}>
        <select value={mes} onChange={e => confirmarTroca(() => setMes(parseInt(e.target.value)))} style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface }}>
          {MESES_VT.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={ano} onChange={e => confirmarTroca(() => setAno(parseInt(e.target.value)))} style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface }}>
          {[ano-1, ano, ano+1].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          placeholder="Buscar funcionário..."
          value={filtroNome}
          onChange={e => setFiltroNome(e.target.value)}
          style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface, minWidth: 200 }}
        />
        <Btn size="sm" onClick={salvarTudoVT} style={{ background: "#16a34a", color: "#fff" }}>💾 Salvar Lançamentos</Btn>
        {msgSalvo === "ok" && <span style={{ color: "#16a34a", fontWeight: 700, fontSize: 13 }}>✅ Salvo com sucesso!</span>}
        {msgSalvo === "erro" && <span style={{ color: "#dc2626", fontWeight: 700, fontSize: 13 }}>❌ Erro ao salvar</span>}
        {isDirty && msgSalvo === "" && <span style={{ color: "#b45309", fontWeight: 700, fontSize: 13 }}>⚠️ Alterações não salvas</span>}
        <Btn size="sm" variant="print" onClick={imprimir} style={{ marginLeft: "auto" }}>🖨️ Imprimir {TIPO_LABELS_VT[tipo]}</Btn>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, color: COLORS.textMid, fontSize: 12 }}>
        <span style={{ width: 16, height: 16, borderRadius: "50%", background: COLORS.border, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>i</span>
        <span title={`Dias a trabalhar calculados em ${MESES_VT[mes-1]}/${ano}. Faltas e afastamentos de referência: ${MESES_VT[mesFaltas-1]}/${anoFaltas}.`}>VT para: <b>{MESES_VT[mes-1]}/{ano}</b> — de referência: <b>{MESES_VT[mesFaltas-1]}/{anoFaltas}</b></span>
      </div>

      {Object.keys(resumoEscalasVT).length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 14 }}>
          {Object.entries(resumoEscalasVT).map(([lbl, dias]) => (
            <span key={lbl} style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, color: COLORS.primary, fontWeight: 700 }}>
              {lbl}: <span style={{ fontWeight: 400 }}>{dias} dias</span>
            </span>
          ))}
        </div>
      )}

      {erro && (
        <div style={{ background: "#fef2f2", border: "1px solid #fca5a5", borderRadius: 10, padding: "12px 16px", marginBottom: 16, color: "#dc2626", fontSize: 13 }}>
          ⚠️ Erro ao carregar: {erro}
        </div>
      )}
      {loading ? (
        <div style={{ textAlign: "center", padding: 40, color: COLORS.textMid }}>Carregando...</div>
      ) : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}>Funcionário</th>
                <ThFilter label="Empresa" value={filtroEmpresa} onChange={setFiltroEmpresa} options={empresasDispVT} align="left" />
                <ThFilter label="Escala" value={filtroEscala} onChange={setFiltroEscala} options={escalasDispVT} align="center" />
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>{tipo === "gasolina" ? "—" : "Dias a Trabalhar"}</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>{tipo === "gasolina" ? "Valor Mensal (R$)" : "VT/dia (R$)"}</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>Crédito Cartão (R$)</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>Total a Carregar (R$)</th>
              </tr>
            </thead>
            <tbody>
              {linhas.length === 0 && !erro && (
                <tr><td colSpan={7} style={{ textAlign: "center", padding: 32, color: COLORS.textMid }}>
                  Nenhum funcionário com tipo <strong>{TIPO_LABELS_VT[tipo]}</strong> definido.
                  <div style={{ fontSize: 12, marginTop: 6 }}>Configure o tipo de VT em <strong>RH → Funcionários</strong> no cadastro de cada funcionário.</div>
                </td></tr>
              )}
              {linhas.map((r, i) => {
                const dm = diasATrabalhar(r);
                const afDias = diasAfastamento(r);
                const total = totalCarregar(r);
                return (
                  <tr key={r.funcionario_id} style={{ background: i % 2 === 0 ? COLORS.surface : COLORS.surfaceAlt || "#f5f8ff" }}>
                    <td style={{ padding: "9px 12px" }}>{r.nome}</td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: COLORS.textMid }}>{r.empresa || '—'}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", fontSize: 12, color: COLORS.textMid }}>{escalaLabel(r)}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>{tipo === "gasolina" ? "—" : dm}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", fontWeight: 600 }}>
                      {vtDia(r).toFixed(2)}
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>
                      <input
                        type="number" min="0" step="0.01"
                        value={getCreditoLocal(r)}
                        onChange={e => setLocalEdits(le => ({ ...le, [r.funcionario_id]: { ...le[r.funcionario_id], credito: e.target.value } }))}
                        onFocus={e => e.target.select()}
                        onBlur={e => setLocalEdits(le => ({ ...le, [r.funcionario_id]: { ...le[r.funcionario_id], credito: (parseFloat(e.target.value) || 0).toFixed(2) } }))}
                        onWheel={e => e.target.blur()}
                        style={{ width: 80, textAlign: "center", padding: "4px 6px", borderRadius: 6, border: `1.5px solid ${COLORS.border}`, fontSize: 13 }}
                      />
                      {salvando[`c${r.funcionario_id}`] && <span style={{ marginLeft: 4, fontSize: 11, color: COLORS.textMid }}>...</span>}
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 700, color: total > 0 ? "#16a34a" : COLORS.textMid }}>R$ {total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            {linhas.length > 0 && (
              <tfoot>
                <tr style={{ background: "#e8f0fe", fontWeight: 700 }}>
                  <td colSpan={6} style={{ padding: "10px 12px", textAlign: "right" }}>TOTAL A CARREGAR</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#1e3a5f", fontSize: 15 }}>R$ {totalTipo.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ─── VA RECARGA ───────────────────────────────────────────────────────────────
function VARecarga({ token, usuario }) {
  const hoje = new Date();
  const curMesVA = hoje.getMonth() + 1;
  const [mes, setMes] = useState(curMesVA === 12 ? 1 : curMesVA + 1);
  const [ano, setAno] = useState(curMesVA === 12 ? hoje.getFullYear() + 1 : hoje.getFullYear());
  const [dados, setDados] = useState([]);
  const [afastamentosRef, setAfastamentosRef] = useState({});
  const [loading, setLoading] = useState(false);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEscala, setFiltroEscala] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");
  const [salvando, setSalvando] = useState({});
  const [feriados, setFeriados] = useState(new Set());
  const [faltasManuais, setFaltasManuais] = useState({});
  const faltasManuaisRef = useRef({});

  // Faltas sempre do mês anterior ao selecionado
  const mesFaltasVA = mes === 1 ? 12 : mes - 1;
  const anoFaltasVA = mes === 1 ? ano - 1 : ano;

  useEffect(() => {
    setLoading(true);
    const pad = n => String(n).padStart(2, '0');
    setFaltasManuais({});
    faltasManuaisRef.current = {};
    Promise.all([
      apiFetch("GET", `/api/beneficios/vt-recarga?mes=${mes}&ano=${ano}`, undefined, token),
      apiFetch("GET", `/api/ponto-terceiro?mes=${mesFaltasVA}&ano=${anoFaltasVA}`, undefined, token).catch(() => []),
      apiFetch("GET", `/api/ferias/por-mes?mes=${mesFaltasVA}&ano=${anoFaltasVA}`, undefined, token).catch(() => []),
      apiFetch("GET", `/api/beneficios/va-faltas-manuais?mes=${mes}&ano=${ano}`, undefined, token).catch(() => ({})),
      apiFetch("GET", `/api/feriados?mes=${mesFaltasVA}&ano=${anoFaltasVA}`, undefined, token).catch(() => []),
    ]).then(([rows, pontoTerceiro, afastamentosList, faltasManDB, feriadosList]) => {
      setFaltasManuais(faltasManDB || {});
      faltasManuaisRef.current = faltasManDB || {};
      // Montar Set de dias com feriado no mês de referência
      const feriadosDias = new Set();
      const totalDiasRef = new Date(anoFaltasVA, mesFaltasVA, 0).getDate();
      const pad = n => String(n).padStart(2, '0');
      (feriadosList || []).forEach(f => {
        const ini = f.data; const fim = f.data_fim || f.data;
        for (let d = 1; d <= totalDiasRef; d++) {
          const dStr = `${anoFaltasVA}-${pad(mesFaltasVA)}-${pad(d)}`;
          if (dStr >= ini && dStr <= fim) feriadosDias.add(d);
        }
      });
      setFeriados(feriadosDias);
      const afMap = {};
      (afastamentosList || []).forEach(af => {
        const fid = af.funcionario_id;
        if (!afMap[fid]) afMap[fid] = [];
        afMap[fid].push({ inicio: af.data_inicio, fim: af.data_fim || '9999-12-31', tipo: af.tipo });
      });
      setAfastamentosRef(afMap);
      const faltasMap = {};
      (pontoTerceiro || []).forEach(r => {
        if (r.tipo === 'falta') {
          const dateStr = `${anoFaltasVA}-${pad(mesFaltasVA)}-${pad(r.dia)}`;
          const coberto = (afMap[r.funcionario_id] || []).some(af => dateStr >= af.inicio && dateStr <= af.fim);
          if (!coberto) {
            if (!faltasMap[r.funcionario_id]) faltasMap[r.funcionario_id] = [];
            faltasMap[r.funcionario_id].push(r.dia);
          }
        }
      });
      setDados((rows || []).map(r => ({ ...r, faltasDias: faltasMap[r.funcionario_id] || [] })));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [mes, ano, token]);

  const diasNoMes = (m, a) => new Date(a, m, 0).getDate();

  const is12x36Row = (row) => {
    const ds = row.dias_semana ? String(row.dias_semana) : "";
    return row.turno === "12X36" || ds === "0,1,2,3,4,5,6";
  };

  // Dias efetivamente trabalhados conforme escala (informativo)
  const diasTrabalhados = (row) => {
    const total = diasNoMes(mes, ano);
    if (is12x36Row(row)) return Math.ceil(total / 2);
    const diasStr = row.dias_semana ? String(row.dias_semana) : "1,2,3,4,5";
    let count = 0;
    for (let d = 1; d <= total; d++) {
      const dow = new Date(ano, mes - 1, d).getDay();
      if (diasStr.includes(String(dow))) count++;
    }
    return count;
  };

  // Dias a receber VA: 12x36 usa floor (conservador até ter referência de ciclo real); demais escalas só Seg-Sex
  const diasAReceber = (row) => {
    const total = diasNoMes(mes, ano);
    if (is12x36Row(row)) return Math.floor(total / 2);
    let count = 0;
    for (let d = 1; d <= total; d++) {
      const dow = new Date(ano, mes - 1, d).getDay();
      if (dow >= 1 && dow <= 5) count++;
    }
    return count;
  };

  // Faltas que descontam VA: para não-12x36, só conta faltas de Seg-Sex
  const faltasVA = (row) => {
    const dias = row.faltasDias || [];
    if (is12x36Row(row)) return dias.length;
    return dias.filter(d => {
      const dow = new Date(anoFaltasVA, mesFaltasVA - 1, d).getDay();
      return dow >= 1 && dow <= 5;
    }).length;
  };

  const escalaLabel = (row) => {
    if (row.turno === "12X36") return "12X36";
    const ds = String(row.dias_semana || "");
    if (ds === "0,1,2,3,4,5,6") return "12X36";
    if (ds === "1,2,3,4,5,6")   return "Seg a Sáb";
    if (ds === "1,2,3,4,5")     return "Seg a Sex";
    if (ds === "1,3,5")          return "Seg/Qua/Sex";
    if (ds === "2,4,6")          return "Ter/Qui/Sáb";
    if (ds)                      return ds;
    return "—";
  };

  const vaDia = (row) => parseFloat(row.vr_dia_padrao) || parseFloat(row.va_dia_legado) || 0;

  const diasAfastamentoVA = (row) => {
    const afs = (afastamentosRef[row.funcionario_id] || []).filter(af => af.tipo !== 'ferias');
    if (!afs.length) return 0;
    const p = n => String(n).padStart(2, '0');
    const totalRef = new Date(anoFaltasVA, mesFaltasVA, 0).getDate();
    if (is12x36Row(row)) {
      let covered = 0;
      for (let d = 1; d <= totalRef; d++) {
        const ds2 = `${anoFaltasVA}-${p(mesFaltasVA)}-${p(d)}`;
        if (afs.some(af => ds2 >= af.inicio && ds2 <= af.fim)) covered++;
      }
      return Math.ceil(covered / 2);
    }
    // não-12x36: só conta afastamentos em dias Seg-Sex
    let count = 0;
    for (let d = 1; d <= totalRef; d++) {
      const dow = new Date(anoFaltasVA, mesFaltasVA - 1, d).getDay();
      if (dow < 1 || dow > 5) continue;
      const ds2 = `${anoFaltasVA}-${p(mesFaltasVA)}-${p(d)}`;
      if (afs.some(af => ds2 >= af.inicio && ds2 <= af.fim)) count++;
    }
    return count;
  };

  // Feriados só contam para não-12x36, e só em dias Seg-Sex
  const feriadosDoFuncionarioVA = (row) => {
    if (is12x36Row(row)) return 0;
    let count = 0;
    for (const d of feriados) {
      const dow = new Date(anoFaltasVA, mesFaltasVA - 1, d).getDay();
      if (dow >= 1 && dow <= 5) count++;
    }
    return count;
  };

  const totalReceber = (row) => {
    const afDias = diasAfastamentoVA(row);
    const faltas = faltasVA(row) + (faltasManuais[row.funcionario_id] || 0);
    const feriFunc = feriadosDoFuncionarioVA(row);
    const uteis = Math.max(0, diasAReceber(row) - afDias - faltas - feriFunc);
    return Math.max(0, vaDia(row) * uteis);
  };

  const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

  const escalasDispVA = [...new Set(dados.map(r => escalaLabel(r)))].filter(e => e && e !== "—").sort();
  const empresasDispVA = [...new Set(dados.map(r => r.empresa || '').filter(Boolean))].sort();

  const dadosFiltrados = dados
    .filter(r => !filtroNome || r.nome.toLowerCase().includes(filtroNome.toLowerCase()))
    .filter(r => !filtroEscala || escalaLabel(r) === filtroEscala)
    .filter(r => !filtroEmpresa || (r.empresa || '') === filtroEmpresa);
  const totalGeral = dadosFiltrados.reduce((s, r) => s + totalReceber(r), 0);

  const resumoEscalasVA = (() => {
    const map = {};
    dadosFiltrados.forEach(r => {
      const lbl = escalaLabel(r);
      if (!(lbl in map)) map[lbl] = diasAReceber(r);
    });
    return map;
  })();

  const imprimir = () => {
    const titulo = `Vale Alimentação — ${MESES[mes-1]}/${ano} (faltas ref. ${MESES[mesFaltasVA-1]}/${anoFaltasVA})`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title>
    <style>
      @page { size: landscape; margin: 15mm; }
      body { font-family: Arial, sans-serif; font-size: 12px; }
      h2 { margin-bottom: 8px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #1e3a5f; color: #fff; padding: 7px 8px; text-align: center; font-size: 11px; }
      th:first-child { text-align: left; }
      td { padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 11px; text-align: center; }
      td:first-child { text-align: left; }
      tr:nth-child(even) td { background: #f5f8ff; }
      tfoot td { font-weight: bold; background: #e8f0fe; }
    </style></head><body>
    <h2>${titulo}</h2>
    <table>
      <thead><tr>
        <th>Funcionário</th><th>Empresa</th><th>Escala</th><th>Dias Trabalhados</th><th>Dias a Receber</th><th>Férias/Afasta</th><th>Feriados</th><th>Faltas VA</th><th>Falta Manual</th><th>VA/dia (R$)</th><th>Total (R$)</th>
      </tr></thead>
      <tbody>${dadosFiltrados.map(r => {
        const dt = diasTrabalhados(r);
        const dr = diasAReceber(r);
        const afDias = diasAfastamentoVA(r);
        const feriFunc = feriadosDoFuncionarioVA(r);
        const faltas = faltasVA(r);
        const manual = faltasManuais[r.funcionario_id] || 0;
        const total = totalReceber(r);
        return `<tr>
          <td>${r.nome}</td>
          <td>${r.empresa || '—'}</td>
          <td>${escalaLabel(r)}</td>
          <td>${dt}</td><td>${dr}</td><td>${afDias || "—"}</td><td>${feriFunc || "—"}</td><td>${faltas || "—"}</td><td>${manual || "—"}</td>
          <td>${vaDia(r).toFixed(2)}</td>
          <td><b>${total.toFixed(2)}</b></td>
        </tr>`;
      }).join("")}</tbody>
      <tfoot><tr>
        <td colspan="10">TOTAL</td>
        <td>${dadosFiltrados.reduce((s,r) => s + totalReceber(r), 0).toFixed(2)}</td>
      </tr></tfoot>
    </table></body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.print(); };
  };

  return (
    <div>
      <style>{`@media print { @page { size: landscape; } }`}</style>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 18 }}>
        <select value={mes} onChange={e => setMes(parseInt(e.target.value))} style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface }}>
          {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={ano} onChange={e => setAno(parseInt(e.target.value))} style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface }}>
          {[ano-1, ano, ano+1].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          placeholder="Buscar funcionário..."
          value={filtroNome}
          onChange={e => setFiltroNome(e.target.value)}
          style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface, minWidth: 200 }}
        />
        <div style={{ marginLeft: "auto" }}>
          <Btn size="sm" variant="print" onClick={imprimir}>🖨️ Imprimir</Btn>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, color: COLORS.textMid, fontSize: 12 }}>
        <span style={{ width: 16, height: 16, borderRadius: "50%", background: COLORS.border, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>i</span>
        <span title={`Dias a trabalhar calculados em ${MESES[mes-1]}/${ano}. Faltas, feriados e afastamentos de referência: ${MESES[mesFaltasVA-1]}/${anoFaltasVA}.`}>VA para: <b>{MESES[mes-1]}/{ano}</b> — de referência: <b>{MESES[mesFaltasVA-1]}/{anoFaltasVA}</b></span>
      </div>

      {Object.keys(resumoEscalasVA).length > 0 && (
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap", alignItems: "center", marginBottom: 10 }}>
          {Object.entries(resumoEscalasVA).map(([lbl, dias]) => (
            <span key={lbl} style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}`, borderRadius: 20, padding: "3px 12px", fontSize: 12, color: COLORS.primary, fontWeight: 700 }}>
              {lbl}: <span style={{ fontWeight: 400 }}>{dias} dias</span>
            </span>
          ))}
          <span style={{ fontSize: 11, color: COLORS.textMid, marginLeft: 4 }}>Sáb e Dom não contam para VA — exceto 12X36</span>
        </div>
      )}

      {loading ? <div style={{ textAlign: "center", padding: 40, color: COLORS.textMid }}>Carregando...</div> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}>Funcionário</th>
                <ThFilter label="Empresa" value={filtroEmpresa} onChange={setFiltroEmpresa} options={empresasDispVA} align="left" />
                <ThFilter label="Escala" value={filtroEscala} onChange={setFiltroEscala} options={escalasDispVA} align="center" />
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>Dias Trabalhados</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>Dias a Receber</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>Férias/Afasta (ref. {MESES[mesFaltasVA-1]})</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>Feriados</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>Faltas VA (ref. {MESES[mesFaltasVA-1]})</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>Falta Manual</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "center", whiteSpace: "nowrap" }}>VA/dia (R$)</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>Total (R$)</th>
              </tr>
            </thead>
            <tbody>
              {dadosFiltrados.length === 0 && (
                <tr><td colSpan={10} style={{ textAlign: "center", padding: 32, color: COLORS.textMid }}>Nenhum funcionário encontrado.</td></tr>
              )}
              {dadosFiltrados.map((r, i) => {
                const dt = diasTrabalhados(r);
                const dr = diasAReceber(r);
                const afDias = diasAfastamentoVA(r);
                const feriFunc = feriadosDoFuncionarioVA(r);
                const faltas = faltasVA(r);
                const uteis = Math.max(0, dr - afDias - feriFunc - faltas - (faltasManuais[r.funcionario_id] || 0));
                const total = totalReceber(r);
                return (
                  <tr key={r.funcionario_id} style={{ background: i % 2 === 0 ? COLORS.surface : COLORS.surfaceAlt || "#f5f8ff" }}>
                    <td style={{ padding: "9px 12px" }}>{r.nome}</td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: COLORS.textMid }}>{r.empresa || '—'}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", fontSize: 12, color: COLORS.textMid }}>{escalaLabel(r)}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center" }}>{dt}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", fontWeight: 600, color: "#1e3a5f" }}>{dr}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", color: afDias > 0 ? COLORS.warning : COLORS.textLight, fontWeight: afDias > 0 ? 700 : 400 }}>{afDias > 0 ? afDias : "—"}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", color: feriFunc > 0 ? "#b45309" : COLORS.textLight, fontWeight: feriFunc > 0 ? 700 : 400 }}>{feriFunc > 0 ? feriFunc : "—"}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", color: faltas > 0 ? "#dc2626" : undefined, fontWeight: faltas > 0 ? 700 : undefined }}>{faltas || "—"}</td>
                    <td style={{ padding: "4px 8px", textAlign: "center" }}>
                      <input
                        type="number" min="0" max="31"
                        value={faltasManuais[r.funcionario_id] || ""}
                        onChange={e => {
                          const v = parseInt(e.target.value) || 0;
                          faltasManuaisRef.current = { ...faltasManuaisRef.current, [r.funcionario_id]: v };
                          setFaltasManuais(prev => ({ ...prev, [r.funcionario_id]: v }));
                        }}
                        onBlur={e => {
                          const v = parseInt(e.target.value) || 0;
                          apiFetch("POST", "/api/beneficios/va-faltas-manuais", { funcionario_id: r.funcionario_id, mes, ano, faltas: v }, token).catch(() => {});
                        }}
                        style={{ width: 54, padding: "4px 6px", borderRadius: 6, border: "1.5px solid #d1d5db", textAlign: "center", fontSize: 13, color: (faltasManuais[r.funcionario_id] || 0) > 0 ? "#dc2626" : undefined, fontWeight: (faltasManuais[r.funcionario_id] || 0) > 0 ? 700 : undefined }}
                      />
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "center", fontWeight: 600 }}>{vaDia(r).toFixed(2)}</td>
                    <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 700, color: total > 0 ? "#16a34a" : COLORS.textMid }}>R$ {total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            {dadosFiltrados.length > 0 && (
              <tfoot>
                <tr style={{ background: "#e8f0fe", fontWeight: 700 }}>
                  <td colSpan={10} style={{ padding: "10px 12px", textAlign: "right" }}>TOTAL</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#1e3a5f", fontSize: 15 }}>R$ {totalGeral.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ─── VA FLEX RECARGA ─────────────────────────────────────────────────────────
function VAFlexRecarga({ token }) {
  const hoje = new Date();
  const curMes = hoje.getMonth() + 1;
  const [mes, setMes] = useState(curMes === 12 ? 1 : curMes + 1);
  const [ano, setAno] = useState(curMes === 12 ? hoje.getFullYear() + 1 : hoje.getFullYear());
  const [dados, setDados] = useState([]);
  const [afastamentosRef, setAfastamentosRef] = useState({});
  const [faltasDias, setFaltasDias] = useState({});
  const [loading, setLoading] = useState(false);
  const [filtroNome, setFiltroNome] = useState("");
  const [filtroEscala, setFiltroEscala] = useState("");
  const [filtroEmpresa, setFiltroEmpresa] = useState("");

  const mesFaltas = mes === 1 ? 12 : mes - 1;
  const anoFaltas = mes === 1 ? ano - 1 : ano;

  const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];

  const tipoLabel = (tipo) => {
    if (tipo === 'ferias') return 'Férias';
    if (tipo === 'atestado') return 'Atestado';
    return 'Afastamento';
  };

  const fmtData = (str) => {
    if (!str || str === '9999-12-31') return null;
    const [, m, d] = str.split('-');
    return `${d}/${m}`;
  };

  useEffect(() => {
    setLoading(true);
    const pad = n => String(n).padStart(2, '0');
    Promise.all([
      apiFetch("GET", `/api/beneficios/vt-recarga?mes=${mes}&ano=${ano}`, undefined, token),
      apiFetch("GET", `/api/ponto-terceiro?mes=${mesFaltas}&ano=${anoFaltas}`, undefined, token).catch(() => []),
      apiFetch("GET", `/api/ferias/por-mes?mes=${mesFaltas}&ano=${anoFaltas}`, undefined, token).catch(() => []),
    ]).then(([rows, pontoTerceiro, afastamentosList]) => {
      // afastamentos com tipo
      const afMap = {};
      (afastamentosList || []).forEach(af => {
        const fid = af.funcionario_id;
        if (!afMap[fid]) afMap[fid] = [];
        afMap[fid].push({ inicio: af.data_inicio, fim: af.data_fim || '9999-12-31', tipo: af.tipo });
      });
      setAfastamentosRef(afMap);
      // faltas com dias
      const faltasCount = {};
      const faltasDiasMap = {};
      (pontoTerceiro || []).forEach(r => {
        if (r.tipo === 'falta') {
          const dateStr = `${anoFaltas}-${pad(mesFaltas)}-${pad(r.dia)}`;
          const coberto = (afMap[r.funcionario_id] || []).some(af => dateStr >= af.inicio && dateStr <= af.fim);
          if (!coberto) {
            faltasCount[r.funcionario_id] = (faltasCount[r.funcionario_id] || 0) + 1;
            if (!faltasDiasMap[r.funcionario_id]) faltasDiasMap[r.funcionario_id] = [];
            faltasDiasMap[r.funcionario_id].push(r.dia);
          }
        }
      });
      setFaltasDias(faltasDiasMap);
      setDados((rows || []).map(r => ({ ...r, faltas: faltasCount[r.funcionario_id] || 0 })));
    }).catch(() => {}).finally(() => setLoading(false));
  }, [mes, ano, token]);

  const escalaLabel = (row) => {
    if (row.turno === "12X36") return "12X36";
    const ds = String(row.dias_semana || "");
    if (ds === "0,1,2,3,4,5,6") return "12X36";
    if (ds === "1,2,3,4,5,6")   return "Seg a Sáb";
    if (ds === "1,2,3,4,5")     return "Seg a Sex";
    if (ds === "1,3,5")          return "Seg/Qua/Sex";
    if (ds === "2,4,6")          return "Ter/Qui/Sáb";
    if (ds)                      return ds;
    return "—";
  };

  const temAusencia = (row) => row.faltas > 0 || (afastamentosRef[row.funcionario_id] || []).length > 0;

  // Retorna array de strings descrevendo cada ausência
  const ausenciaDetalhes = (row) => {
    const partes = [];
    const afs = afastamentosRef[row.funcionario_id] || [];
    afs.forEach(af => {
      const inicio = fmtData(af.inicio);
      const fim = fmtData(af.fim);
      const label = tipoLabel(af.tipo);
      partes.push(fim ? `${label}: ${inicio} a ${fim}` : `${label}: desde ${inicio}`);
    });
    const dias = faltasDias[row.funcionario_id] || [];
    if (dias.length > 0) {
      const diasStr = dias.sort((a,b) => a-b).map(d => `dia ${d}`).join(', ');
      partes.push(`Falta: ${diasStr}`);
    }
    return partes;
  };

  const escalasDispFlex = [...new Set(dados.map(r => escalaLabel(r)))].filter(e => e && e !== "—").sort();
  const empresasDispFlex = [...new Set(dados.map(r => r.empresa || '').filter(Boolean))].sort();

  const dadosFiltrados = dados
    .filter(r => !filtroNome || r.nome.toLowerCase().includes(filtroNome.toLowerCase()))
    .filter(r => !filtroEscala || escalaLabel(r) === filtroEscala)
    .filter(r => !filtroEmpresa || (r.empresa || '') === filtroEmpresa);
  const totalGeral = dadosFiltrados.reduce((s, r) => s + (temAusencia(r) ? 0 : 200), 0);

  const imprimir = () => {
    const titulo = `Auxílio Cesta Básica — ${MESES[mes-1]}/${ano} (ausências ref. ${MESES[mesFaltas-1]}/${anoFaltas})`;
    const html = `<!DOCTYPE html><html><head><meta charset="utf-8"><title>${titulo}</title>
    <style>
      @page { size: landscape; margin: 15mm; }
      body { font-family: Arial, sans-serif; font-size: 12px; }
      h2 { margin-bottom: 8px; }
      table { width: 100%; border-collapse: collapse; }
      th { background: #1e3a5f; color: #fff; padding: 7px 8px; text-align: left; font-size: 11px; }
      td { padding: 6px 8px; border-bottom: 1px solid #ddd; font-size: 11px; }
      td:last-child { text-align: right; }
      tr:nth-child(even) td { background: #f5f8ff; }
      tfoot td { font-weight: bold; background: #e8f0fe; }
    </style></head><body>
    <h2>${titulo}</h2>
    <table>
      <thead><tr>
        <th>Funcionário</th><th>Empresa</th><th>Escala</th><th>Ausências (ref. ${MESES[mesFaltas-1]}/${anoFaltas})</th><th>Total (R$)</th>
      </tr></thead>
      <tbody>${dadosFiltrados.map(r => {
        const ausente = temAusencia(r);
        const det = ausenciaDetalhes(r);
        return `<tr>
          <td>${r.nome}</td>
          <td>${r.empresa || '—'}</td>
          <td>${escalaLabel(r)}</td>
          <td>${det.length ? det.join(' / ') : '—'}</td>
          <td><b>${ausente ? "0.00" : "200.00"}</b></td>
        </tr>`;
      }).join("")}</tbody>
      <tfoot><tr>
        <td colspan="4">TOTAL</td>
        <td>${totalGeral.toFixed(2)}</td>
      </tr></tfoot>
    </table></body></html>`;
    const w = window.open("", "_blank");
    w.document.write(html);
    w.document.close();
    w.onload = () => { w.print(); };
  };

  return (
    <div>
      <style>{`@media print { @page { size: landscape; } }`}</style>
      <div style={{ display: "flex", gap: 12, alignItems: "center", flexWrap: "wrap", marginBottom: 18 }}>
        <select value={mes} onChange={e => setMes(parseInt(e.target.value))} style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface }}>
          {MESES.map((m, i) => <option key={i} value={i+1}>{m}</option>)}
        </select>
        <select value={ano} onChange={e => setAno(parseInt(e.target.value))} style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface }}>
          {[ano-1, ano, ano+1].map(a => <option key={a} value={a}>{a}</option>)}
        </select>
        <input
          placeholder="Buscar funcionário..."
          value={filtroNome}
          onChange={e => setFiltroNome(e.target.value)}
          style={{ padding: "7px 12px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, fontSize: 14, background: COLORS.surface, minWidth: 200 }}
        />
        <div style={{ marginLeft: "auto" }}>
          <Btn size="sm" variant="print" onClick={imprimir}>🖨️ Imprimir</Btn>
        </div>
      </div>

      <div style={{ display: "flex", alignItems: "center", gap: 6, marginBottom: 14, color: COLORS.textMid, fontSize: 12 }}>
        <span style={{ width: 16, height: 16, borderRadius: "50%", background: COLORS.border, color: "#fff", display: "inline-flex", alignItems: "center", justifyContent: "center", fontSize: 10, fontWeight: 700, flexShrink: 0 }}>i</span>
        <span title={`Auxílio Cesta Básica: R$200 por colaborador. Qualquer ausência no mês de referência (${MESES[mesFaltas-1]}/${anoFaltas}) — falta, atestado, férias ou afastamento — zera o benefício.`}>
          Auxílio para: <b>{MESES[mes-1]}/{ano}</b> — ausências de referência: <b>{MESES[mesFaltas-1]}/{anoFaltas}</b>
        </span>
      </div>

      {loading ? <div style={{ textAlign: "center", padding: 40, color: COLORS.textMid }}>Carregando...</div> : (
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}>Funcionário</th>
                <ThFilter label="Empresa" value={filtroEmpresa} onChange={setFiltroEmpresa} options={empresasDispFlex} align="left" />
                <ThFilter label="Escala" value={filtroEscala} onChange={setFiltroEscala} options={escalasDispFlex} align="center" />
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "left", whiteSpace: "nowrap" }}>Ausências (ref. {MESES[mesFaltas-1]})</th>
                <th style={{ padding: "10px 12px", color: COLORS.textMid, fontWeight: 700, textAlign: "right", whiteSpace: "nowrap" }}>Total (R$)</th>
              </tr>
            </thead>
            <tbody>
              {dadosFiltrados.length === 0 && (
                <tr><td colSpan={5} style={{ textAlign: "center", padding: 32, color: COLORS.textMid }}>Nenhum funcionário encontrado com esses filtros.</td></tr>
              )}
              {dadosFiltrados.map((r, i) => {
                const ausente = temAusencia(r);
                const detalhes = ausenciaDetalhes(r);
                const total = ausente ? 0 : 200;
                return (
                  <tr key={r.funcionario_id} style={{ background: i % 2 === 0 ? COLORS.surface : COLORS.surfaceAlt || "#f5f8ff" }}>
                    <td style={{ padding: "9px 12px" }}>{r.nome}</td>
                    <td style={{ padding: "9px 12px", fontSize: 12, color: COLORS.textMid }}>{r.empresa || '—'}</td>
                    <td style={{ padding: "9px 12px", textAlign: "center", fontSize: 12, color: COLORS.textMid }}>{escalaLabel(r)}</td>
                    <td style={{ padding: "9px 12px" }}>
                      {detalhes.length === 0
                        ? <span style={{ color: COLORS.textLight }}>—</span>
                        : detalhes.map((d, idx) => (
                          <div key={idx} style={{ color: "#dc2626", fontWeight: 600, fontSize: 12 }}>{d}</div>
                        ))
                      }
                    </td>
                    <td style={{ padding: "9px 12px", textAlign: "right", fontWeight: 700, color: total > 0 ? "#16a34a" : COLORS.textMid }}>R$ {total.toFixed(2)}</td>
                  </tr>
                );
              })}
            </tbody>
            {dadosFiltrados.length > 0 && (
              <tfoot>
                <tr style={{ background: "#e8f0fe", fontWeight: 700 }}>
                  <td colSpan={4} style={{ padding: "10px 12px", textAlign: "right" }}>TOTAL</td>
                  <td style={{ padding: "10px 12px", textAlign: "right", color: "#1e3a5f", fontSize: 15 }}>R$ {totalGeral.toFixed(2)}</td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      )}
    </div>
  );
}

// ─── BENEFÍCIOS ───────────────────────────────────────────────────────────────
function Beneficios({ employees, token, usuario, tipo }) {
  // tipo: "vt" | "va" | undefined (ambos)
  const [dados, setDados] = useState({});
  const [filtro, setFiltro] = useState("");
  const [salvando, setSalvando] = useState({});
  const [msg, setMsg] = useState("");

  useEffect(() => {
    apiFetch("GET", "/api/beneficios", undefined, token)
      .then(rows => {
        const m = {};
        rows.forEach(r => { m[r.funcionario_id] = { vt: parseFloat(r.vale_transporte) || 0, va: parseFloat(r.vale_alimentacao) || 0 }; });
        setDados(m);
      }).catch(() => {});
  }, [token]);

  const ativos = employees.filter(e => e.status !== "Demitido" && !e.pre_cadastro && e.nome.toLowerCase().includes(filtro.toLowerCase()));
  const get = (id, campo) => dados[id]?.[campo] ?? 0;
  const set = (id, campo, val) => setDados(prev => ({ ...prev, [id]: { ...prev[id], [campo]: parseFloat(val) || 0 } }));

  const salvar = async (emp) => {
    setSalvando(prev => ({ ...prev, [emp.id]: true }));
    try {
      const body = tipo === "vt" ? { vale_transporte: get(emp.id,'vt'), vale_alimentacao: get(emp.id,'va') }
                 : tipo === "va" ? { vale_alimentacao: get(emp.id,'va'), vale_transporte: get(emp.id,'vt') }
                 : { vale_transporte: get(emp.id,'vt'), vale_alimentacao: get(emp.id,'va') };
      await apiFetch("PUT", `/api/beneficios/${emp.id}`, body, token);
      setMsg(`✅ ${emp.nome.split(' ')[0]} salvo.`);
      setTimeout(() => setMsg(""), 3000);
    } catch(e) { alert(e.message); }
    setSalvando(prev => ({ ...prev, [emp.id]: false }));
  };

  const fmt = (v) => `R$ ${parseFloat(v||0).toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;
  const totalVT = ativos.reduce((s, e) => s + get(e.id,'vt'), 0);
  const totalVA = ativos.reduce((s, e) => s + get(e.id,'va'), 0);
  const isVT = !tipo || tipo === "vt";
  const isVA = !tipo || tipo === "va";
  const cor = tipo === "va" ? COLORS.accent : COLORS.info;
  const total = tipo === "vt" ? totalVT : tipo === "va" ? totalVA : totalVT + totalVA;
  const totalDesc = tipo === "vt" ? totalVT * 0.06 : tipo === "va" ? totalVA * 0.06 : (totalVT + totalVA) * 0.06;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 12 }}>
        <Card style={{ borderTop: `3px solid ${cor}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>{tipo === "va" ? "VA" : "VT"} Diário (soma)</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: cor }}>{fmt(total)}</div>
          <div style={{ fontSize: 11, color: COLORS.textMid, marginTop: 2 }}>valor/dia cadastrado por funcionário</div>
        </Card>
        <Card style={{ borderTop: `3px solid ${COLORS.danger}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>Desc. 6% diário (soma)</div>
          <div style={{ fontSize: 20, fontWeight: 800, color: COLORS.danger }}>{fmt(totalDesc)}</div>
          <div style={{ fontSize: 11, color: COLORS.textMid, marginTop: 2 }}>O total real é calculado pelo Financeiro</div>
        </Card>
      </div>

      <div style={{ display: "flex", gap: 10, alignItems: "flex-end" }}>
        <Input label="Filtrar funcionário" value={filtro} onChange={e => setFiltro(e.target.value)} placeholder="Nome..." style={{ minWidth: 220 }} />
        {msg && <div style={{ fontSize: 13, color: COLORS.primary, fontWeight: 600 }}>{msg}</div>}
      </div>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.bg }}>
                {["Funcionário","Cargo",
                  ...(isVT ? [`🚌 VT por dia (R$)`, "Desc. VT/dia (6%)"] : []),
                  ...(isVA ? [`🍽 VA por dia (R$)`, "Desc. VA/dia (6%)"] : []),
                  ""].map(h => (
                  <th key={h} style={{ padding: "10px 14px", textAlign: "left", color: COLORS.textMid, fontWeight: 600, fontSize: 11, whiteSpace: "nowrap", borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ativos.map((emp, idx) => {
                const vt = get(emp.id, 'vt');
                const va = get(emp.id, 'va');
                return (
                  <tr key={emp.id} style={{ background: idx % 2 === 0 ? COLORS.white : COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "10px 14px", fontWeight: 700 }}>{emp.nome}</td>
                    <td style={{ padding: "10px 14px", color: COLORS.textMid }}>{emp.cargo}</td>
                    {isVT && <>
                      <td style={{ padding: "8px 14px" }}>
                        <input type="number" min="0" step="0.01" value={vt || ""} placeholder="0,00"
                          onChange={e => set(emp.id, 'vt', e.target.value)}
                          onBlur={() => salvar(emp)}
                          style={{ width: 100, border: `1.5px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 13, outline: "none", background: vt > 0 ? "#eff6ff" : COLORS.surface, color: COLORS.text }} />
                      </td>
                      <td style={{ padding: "10px 14px", color: COLORS.info, fontWeight: 600 }}>{fmt(vt * 0.06)}</td>
                    </>}
                    {isVA && <>
                      <td style={{ padding: "8px 14px" }}>
                        <input type="number" min="0" step="0.01" value={va || ""} placeholder="0,00"
                          onChange={e => set(emp.id, 'va', e.target.value)}
                          onBlur={() => salvar(emp)}
                          style={{ width: 100, border: `1.5px solid ${COLORS.border}`, borderRadius: 6, padding: "6px 10px", fontSize: 13, outline: "none", background: va > 0 ? "#fffbeb" : COLORS.surface, color: COLORS.text }} />
                      </td>
                      <td style={{ padding: "10px 14px", color: COLORS.accent, fontWeight: 600 }}>{fmt(va * 0.06)}</td>
                    </>}
                    <td style={{ padding: "8px 10px" }}>
                      <Btn size="sm" onClick={() => salvar(emp)} disabled={salvando[emp.id]}>
                        {salvando[emp.id] ? "..." : "Salvar"}
                      </Btn>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>

      <div style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}`, borderRadius: 8, padding: "10px 14px", fontSize: 12, color: COLORS.primaryDark }}>
        <strong>Como funciona:</strong> Cadastre o valor diário de {tipo === "vt" ? "VT" : tipo === "va" ? "VA" : "VT e VA"} de cada funcionário. O Financeiro multiplica pelos dias efetivamente trabalhados no mês, descontando faltas, férias e afastamentos. O desconto de 6% é calculado sobre o total mensal.
      </div>
    </div>
  );
}

// ─── RELATÓRIO FINANCEIRO ──────────────────────────────────────────────────────
function Financeiro({ employees, ponto, token }) {
  const [confirmOpts, setConfirmOpts] = useState(null);
  const [mes, setMes] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; });
  const [sortCol, setSortCol] = useState(null); const [sortDir, setSortDir] = useState("asc");
  const [filtros, setFiltros] = useState({});
  const [beneficios, setBeneficios] = useState({});
  const [pontoTerceiro, setPontoTerceiro] = useState({});
  const [afastamentosMes, setAfastamentosMes] = useState([]);
  const [erroAfastamentos, setErroAfastamentos] = useState("");
  const [refresh, setRefresh] = useState(0);
  const tableScrollRef = useRef(null);
  const [ajustes, setAjustes] = useState([]);
  const [modalAjuste, setModalAjuste] = useState(null);
  const [formAjuste, setFormAjuste] = useState({ tipo: 'bonus', valor: '', descricao: '' });
  const [salvandoAjuste, setSalvandoAjuste] = useState(false);
  const [modalCfg, setModalCfg] = useState(false);
  const [modalManual, setModalManual] = useState(false);
  const [cfg, setCfg] = useState({ adicional_noturno_pct: "39.00", inss_pct: "14.50", fgts_pct: "8.00", vt_desc_pct: "6.00", va_desc_pct: "20.00", adiantamento_pct: "0.00", mult_hora_extra: "1.5", mult_hora_extra_feriado: "2.0", divisor_normal: "220", divisor_12x36: "210" });
  const [salvandoCfg, setSalvandoCfg] = useState(false);
  const [toastCfg, setToastCfg] = useState("");
  const [feriadosMes, setFeriadosMes] = useState([]);
  const [diasComRegistro, setDiasComRegistro] = useState({});
  const [filhosMap, setFilhosMap] = useState({});

  useEffect(() => {
    apiFetch("GET", "/api/propostas/config-empresa", undefined, token)
      .then(d => { if (d && d.inss_pct != null) setCfg({ adicional_noturno_pct: String(d.adicional_noturno_pct ?? 39), inss_pct: String(d.inss_pct ?? 14.5), fgts_pct: String(d.fgts_pct ?? 8), vt_desc_pct: String(d.vt_desc_pct ?? 6), va_desc_pct: String(d.va_desc_pct ?? 20), adiantamento_pct: String(d.adiantamento_pct ?? 0), mult_hora_extra: String(d.mult_hora_extra ?? 1.5), mult_hora_extra_feriado: String(d.mult_hora_extra_feriado ?? 2.0), divisor_normal: String(d.divisor_normal ?? 220), divisor_12x36: String(d.divisor_12x36 ?? 210) }); })
      .catch(() => {});
  }, [token]);

  const salvarCfg = () => {
    setModalCfg(false);
    setConfirmOpts({ msg: "Confirmar as alterações nas configurações de cálculo da folha? Os novos valores serão aplicados imediatamente.", onOk: async () => {
      setSalvandoCfg(true);
      try {
        await apiFetch("POST", "/api/propostas/config-empresa", {
          adicional_noturno_pct: parseFloat(cfg.adicional_noturno_pct),
          inss_pct: parseFloat(cfg.inss_pct),
          fgts_pct: parseFloat(cfg.fgts_pct),
          vt_desc_pct: parseFloat(cfg.vt_desc_pct),
          va_desc_pct: parseFloat(cfg.va_desc_pct),
          adiantamento_pct: parseFloat(cfg.adiantamento_pct),
          mult_hora_extra: parseFloat(cfg.mult_hora_extra),
          mult_hora_extra_feriado: parseFloat(cfg.mult_hora_extra_feriado),
          divisor_normal: parseFloat(cfg.divisor_normal),
          divisor_12x36: parseFloat(cfg.divisor_12x36),
        }, token);
        setRefresh(r => r + 1);
        setToastCfg("Configurações salvas com sucesso!");
        setTimeout(() => setToastCfg(""), 3500);
      } catch(e) { alert("Erro: " + e.message); setModalCfg(true); }
      setSalvandoCfg(false);
    }});
  };
  const handleSort = (col) => { if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc"); else { setSortCol(col); setSortDir("asc"); } };
  const setFiltro = (col, val) => setFiltros(f => ({ ...f, [col]: val }));

  const FILTERABLE_COLS = new Set(["Nome","Cargo","Salário Base","Adiantamento","Ajustes","Total Deduções","Líquido"]);
  const thSort = (h, sticky) => {
    const ativo = sortCol === h;
    const fv = filtros[h] || "";
    const filterable = FILTERABLE_COLS.has(h);
    const thBase = { textAlign: "left", whiteSpace: "nowrap", background: (ativo || fv) ? COLORS.primaryBg : COLORS.bg, fontSize: 11, color: COLORS.textMid, fontWeight: 600, position: "sticky", top: 0, zIndex: sticky ? 5 : 3, ...(sticky ? { left: 0 } : {}) };
    if (filterable) {
      return (
        <th key={h} style={{ ...thBase, padding: "4px 6px", verticalAlign: "middle" }}>
          <div style={{ display: "flex", alignItems: "center", gap: 3 }}>
            <input
              value={fv}
              onChange={e => setFiltro(h, e.target.value)}
              placeholder={h}
              style={{ flex: 1, fontSize: 11, padding: "4px 7px", borderRadius: 4, border: `1px solid ${fv ? COLORS.primary : COLORS.border}`, outline: "none", background: fv ? COLORS.white : "transparent", color: COLORS.text, boxSizing: "border-box", minWidth: 55, fontWeight: fv ? 700 : 500 }}
            />
            <span onClick={() => handleSort(h)} style={{ cursor: "pointer", opacity: ativo ? 1 : 0.3, fontSize: 10, userSelect: "none", flexShrink: 0 }}>{ativo ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}</span>
          </div>
        </th>
      );
    }
    return (
      <th key={h} style={{ ...thBase, padding: "10px 10px", verticalAlign: "middle" }}>
        <div onClick={() => handleSort(h)} style={{ cursor: "pointer", userSelect: "none", display: "flex", alignItems: "center", gap: 4 }}>
          {h}<span style={{ opacity: ativo ? 1 : 0.35, fontSize: 10 }}>{ativo ? (sortDir === "asc" ? "▲" : "▼") : "⇅"}</span>
        </div>
      </th>
    );
  };

  const [anoNum, mesNum] = mes.split('-').map(Number);

  useEffect(() => {
    if (!token) return;
    apiFetch("GET", `/api/beneficios/vt-recarga?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => {
        const m = {};
        rows.forEach(r => {
          const vtDiario = (parseFloat(r.vt_valor_otimo)||0) + (parseFloat(r.vt_valor_bhbus)||0) +
            (parseFloat(r.vt_valor_gasolina)||0) + (parseFloat(r.vt_valor_betimcard)||0) + (parseFloat(r.vt_valor_outros)||0);
          const vaDiario = parseFloat(r.vr_dia_padrao) || parseFloat(r.va_dia_legado) || 0;
          m[r.funcionario_id] = { vt: vtDiario, va: vaDiario };
        });
        setBeneficios(m);
      }).catch(() => {});
    apiFetch("GET", "/api/funcionarios-filhos", undefined, token)
      .then(rows => {
        const m = {};
        (rows || []).forEach(r => { if (!m[r.funcionario_id]) m[r.funcionario_id] = []; m[r.funcionario_id].push(r); });
        setFilhosMap(m);
      }).catch(() => {});
  }, [token, refresh, mesNum, anoNum]);

  useEffect(() => {
    if (!token) return;
    // Busca dados brutos e calcula faltas/extras no frontend
    apiFetch("GET", `/api/ponto-terceiro?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => {
        const resultado = {};
        const registros = {};
        (rows || []).forEach(r => {
          const fid = Number(r.funcionario_id);
          if (!resultado[fid]) resultado[fid] = { faltas: 0, horas_extra: 0 };
          if (r.tipo === 'falta') resultado[fid].faltas += 1;
          if (r.tipo === 'extra') resultado[fid].horas_extra += parseFloat(r.horas_extra) || 0;
          if (r.tipo === 'falta' || r.tipo === 'trabalhado_feriado') {
            if (!registros[fid]) registros[fid] = new Set();
            registros[fid].add(Number(r.dia));
          }
        });
        setPontoTerceiro(resultado);
        setDiasComRegistro(registros);
      })
      .catch(() => {});
    apiFetch("GET", `/api/feriados?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => setFeriadosMes(Array.isArray(rows) ? rows : []))
      .catch(() => {});
    apiFetch("GET", `/api/ferias/por-mes?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => {
        const arr = Array.isArray(rows) ? rows : [];
        setAfastamentosMes(arr);
        setErroAfastamentos("");
      })
      .catch(e => setErroAfastamentos(e.message));
    apiFetch("GET", `/api/ajustes-financeiros?mes=${mesNum}&ano=${anoNum}`, undefined, token)
      .then(rows => setAjustes(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [token, mes, refresh]);

  const salvarAjuste = async () => {
    if (!formAjuste.valor || parseFloat(formAjuste.valor) <= 0) return alert("Informe um valor válido.");
    setSalvandoAjuste(true);
    try {
      await apiFetch("POST", "/api/ajustes-financeiros", {
        funcionario_id: modalAjuste.funcId,
        mes: mesNum, ano: anoNum,
        tipo: formAjuste.tipo,
        valor: parseFloat(formAjuste.valor),
        descricao: formAjuste.descricao || null,
      }, token);
      setFormAjuste({ tipo: 'bonus', valor: '', descricao: '' });
      const rows = await apiFetch("GET", `/api/ajustes-financeiros?mes=${mesNum}&ano=${anoNum}`, undefined, token);
      setAjustes(Array.isArray(rows) ? rows : []);
    } catch(e) { alert(e.message); }
    setSalvandoAjuste(false);
  };

  const apagarAjuste = (id) => {
    setConfirmOpts({ msg: "Remover este lançamento?", onOk: async () => {
      try {
        await apiFetch("DELETE", `/api/ajustes-financeiros/${id}`, undefined, token);
        setAjustes(prev => prev.filter(a => a.id !== id));
      } catch(e) { alert(e.message); }
    }});
  };

  const adNotPct       = parseFloat(cfg.adicional_noturno_pct) || 0;
  const fgtsPct           = parseFloat(cfg.fgts_pct) || 0;
  const vtDescPct         = parseFloat(cfg.vt_desc_pct) || 0;
  const vaDescPct         = parseFloat(cfg.va_desc_pct) || 0;
  const adiantamentoPct   = parseFloat(cfg.adiantamento_pct) || 0;
  const multExtra         = parseFloat(cfg.mult_hora_extra) || 1.5;
  const multExtraFeriado  = parseFloat(cfg.mult_hora_extra_feriado) || 2.0;
  const divisorNormal     = parseFloat(cfg.divisor_normal) || 220;
  const divisorDoze       = parseFloat(cfg.divisor_12x36) || 210;

  // Tabela progressiva INSS 2026 (Portaria MPS/MF)
  const calcINSS = (base) => {
    const faixas = [
      { limite: 1518.00, aliq: 0.075 },
      { limite: 2793.88, aliq: 0.09  },
      { limite: 4190.83, aliq: 0.12  },
      { limite: 8157.41, aliq: 0.14  },
    ];
    let total = 0, anterior = 0;
    for (const f of faixas) {
      if (base <= anterior) break;
      total += (Math.min(base, f.limite) - anterior) * f.aliq;
      anterior = f.limite;
    }
    return Math.round(total * 100) / 100;
  };

  function calcDias12x36(dataRef, mes, ano) {
    if (!dataRef) return 15;
    const [ry, rm, rd] = dataRef.split('-').map(Number);
    const refMs = Date.UTC(ry, rm - 1, rd);
    const diasNoMes = new Date(ano, mes, 0).getDate();
    let count = 0;
    for (let d = 1; d <= diasNoMes; d++) {
      const diff = Math.round((Date.UTC(ano, mes - 1, d) - refMs) / 86400000);
      if (Math.abs(diff) % 2 === 0) count++;
    }
    return count;
  }

  const totalDiasUteis = (e) => {
    if (/12[Xx]36/i.test(e.turno)) return calcDias12x36(e.data_ref_12x36, mesNum, anoNum);
    const trabSet = new Set(e.dias_semana && e.dias_semana.length ? e.dias_semana : [1,2,3,4,5]);
    const daysInMonth = new Date(anoNum, mesNum, 0).getDate();
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      if (trabSet.has(new Date(anoNum, mesNum - 1, d).getDay())) count++;
    }
    return count;
  };

  // VA: 12x36 → todos os dias trabalhados. Normal → somente seg-sex da escala.
  const totalDiasUteisVA = (e) => {
    if (/12[Xx]36/i.test(e.turno)) return calcDias12x36(e.data_ref_12x36, mesNum, anoNum);
    const trabSet = new Set(e.dias_semana && e.dias_semana.length ? e.dias_semana : [1,2,3,4,5]);
    const SEG_SEX = new Set([1, 2, 3, 4, 5]);
    const daysInMonth = new Date(anoNum, mesNum, 0).getDate();
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(anoNum, mesNum - 1, d).getDay();
      if (trabSet.has(dow) && SEG_SEX.has(dow)) count++;
    }
    return count;
  };

  // Normaliza data para YYYY-MM-DD independente do formato vindo do banco
  const normDate = (d) => d ? String(d).slice(0, 10) : null;

  // Conta dias de férias/afastamento que coincidem com dias de trabalho do funcionário no mês
  const diasAfastadosTrabalhados = (funcId, diasSemana) => {
    const trabSet = new Set(diasSemana && diasSemana.length ? diasSemana : [1,2,3,4,5]);
    const daysInMonth = new Date(anoNum, mesNum, 0).getDate();
    const diasAf = afastamentosMes.filter(a => Number(a.funcionario_id) === Number(funcId));
    if (!diasAf.length) return 0;
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const dateStr = `${anoNum}-${String(mesNum).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const dow = new Date(anoNum, mesNum - 1, d).getDay();
      if (!trabSet.has(dow)) continue;
      const afastado = diasAf.some(a => {
        const ini = normDate(a.data_inicio) || '0000-00-00';
        const fim = normDate(a.data_fim) || '9999-12-31';
        return dateStr >= ini && dateStr <= fim;
      });
      if (afastado) count++;
    }
    return count;
  };

  // Conta dias de feriado (correspondentes à cidade do funcionário) em que ele não trabalhou e não foi marcado falta
  const feriadosNaoTrabalhados = (funcId, cidade, diasSemana) => {
    const trabSet = new Set(diasSemana && diasSemana.length ? diasSemana : [1,2,3,4,5]);
    const daysInMonth = new Date(anoNum, mesNum, 0).getDate();
    const feriadosFunc = feriadosMes.filter(f => f.cidade === 'Brasil' || f.cidade === cidade);
    if (!feriadosFunc.length) return 0;
    const registrados = diasComRegistro[funcId] || new Set();
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(anoNum, mesNum - 1, d).getDay();
      if (!trabSet.has(dow)) continue;
      const dateStr = `${anoNum}-${String(mesNum).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const isFeriado = feriadosFunc.some(f => feriadoCobreData(f, dateStr));
      if (isFeriado && !registrados.has(d)) count++;
    }
    return count;
  };

  // Conta DSRs do mês por escala:
  // • Normal (5x2, 6x1…): domingos + feriados que caem em dias trabalhados (não domingo)
  // • 12x36: domingos + feriados que caem nos dias de FOLGA do ciclo
  const contarDSRsPorEscala = (emp) => {
    const daysInMonth = new Date(anoNum, mesNum, 0).getDate();
    const feriadosFunc = feriadosMes.filter(f => f.cidade === 'Brasil' || f.cidade === emp.cidade);
    const is12x36 = /12[Xx]36/i.test(emp.turno);
    const trabSet = new Set(emp.dias_semana && emp.dias_semana.length ? emp.dias_semana : [1,2,3,4,5]);
    let count = 0;
    for (let d = 1; d <= daysInMonth; d++) {
      const dow = new Date(anoNum, mesNum - 1, d).getDay();
      const dateStr = `${anoNum}-${String(mesNum).padStart(2,'0')}-${String(d).padStart(2,'0')}`;
      const isFeriado = feriadosFunc.some(f => feriadoCobreData(f, dateStr));
      if (is12x36 && emp.data_ref_12x36) {
        const [ry, rm, rd] = emp.data_ref_12x36.split('-').map(Number);
        const diff = Math.round((Date.UTC(anoNum, mesNum - 1, d) - Date.UTC(ry, rm - 1, rd)) / 86400000);
        const isDiaFolga = Math.abs(diff) % 2 !== 0;
        if (isDiaFolga && (dow === 0 || isFeriado)) count++;
      } else {
        if (dow === 0) { count++; } // domingo sempre é DSR
        else if (isFeriado && trabSet.has(dow)) { count++; } // feriado em dia trabalhado
      }
    }
    return count;
  };

  const dados = employees.filter(e => e.status !== "Demitido" && !e.pre_cadastro).map(e => {
    // Horas extras: primeiro tenta ponto-terceiro, fallback lançamento manual
    const pt = pontoTerceiro[e.id];
    const reg = Array.isArray(ponto) ? ponto.find(p => p.funcionario_id === e.id && p.mes == mesNum && p.ano == anoNum) : null;
    const extra = pt ? (pt.horas_extra || 0) : (reg?.horas_extras || 0);
    const faltas = pt ? (pt.faltas || 0) : 0;
    const descontoFalta = faltas > 0 ? (e.salario / 30) * faltas : 0;
    const divisorEmp = /12[Xx]36/i.test(e.turno) ? divisorDoze : divisorNormal;
    const valorExtra = (e.salario / divisorEmp) * multExtra * extra;
    const adNot = (/noturno/i.test(e.turno) || /noturno/i.test(e.cargo)) ? e.salario * adNotPct / 100 : 0;
    const uteis = totalDiasUteis(e);
    const diasAf = diasAfastadosTrabalhados(e.id, e.dias_semana);
    const feriadosNT = feriadosNaoTrabalhados(e.id, e.cidade, e.dias_semana);
    const diasEfetivos = Math.max(0, uteis - diasAf - faltas - feriadosNT);
    const uteisVA = totalDiasUteisVA(e);
    const diasVA = Math.max(0, uteisVA - diasAf - faltas - feriadosNT);
    const vtDiario = beneficios[e.id]?.vt || 0;
    const vaDiario = beneficios[e.id]?.va || 0;
    const vt = vtDiario * diasEfetivos;
    const va = vaDiario * diasVA;
    // CCT: funcionário paga até vtDescPct% do salário, nunca mais que o custo do VT
    const descVT = Math.min(e.salario * vtDescPct / 100, vt);
    const descVA = va * vaDescPct / 100;
    // Ajustes: adiantamento, bonus, desconto
    const ajustesFunc = ajustes.filter(a => Number(a.funcionario_id) === Number(e.id));
    const isAutoDesc = (desc) => desc && desc.toLowerCase().includes('adiantamento_automatico');
    const totalAdiantamentoManual = ajustesFunc.filter(a => a.tipo === 'adiantamento' && !isAutoDesc(a.descricao)).reduce((s, a) => s + parseFloat(a.valor || 0), 0);
    const totalAdiantamentoAutoDb = ajustesFunc.filter(a => a.tipo === 'adiantamento' && isAutoDesc(a.descricao)).reduce((s, a) => s + parseFloat(a.valor || 0), 0);
    const totalAdiantamento = totalAdiantamentoManual + totalAdiantamentoAutoDb;
    const totalBonus = ajustesFunc.filter(a => a.tipo === 'bonus').reduce((s, a) => s + parseFloat(a.valor || 0), 0);
    const totalDesconto = ajustesFunc.filter(a => a.tipo === 'desconto').reduce((s, a) => s + parseFloat(a.valor || 0), 0);
    const adiantamentoAuto = (e.adiantamento && totalAdiantamentoManual === 0 && totalAdiantamentoAutoDb === 0) ? e.salario * adiantamentoPct / 100 : 0;
    const hoje = new Date();
    const filhosFunc = filhosMap[e.id] || [];
    const filhosElegiveis = filhosFunc.filter(f => {
      if (!f.data_nascimento) return false;
      return (hoje - new Date(f.data_nascimento)) / (1000 * 60 * 60 * 24 * 365.25) < 14;
    });
    const salFamilia = (filhosElegiveis.length > 0 && e.salario <= 1908.52) ? filhosElegiveis.length * 68.48 : 0;
    // Reflexo DSR: variáveis (ad. noturno e hora extra) refletem nos domingos/feriados do mês
    // Denominador = diasEfetivos (dias realmente trabalhados); DSRs calculados por escala
    const dsrDias = contarDSRsPorEscala(e);
    const reflexoAdNot = (adNot > 0 && diasEfetivos > 0) ? adNot * dsrDias / diasEfetivos : 0;
    const reflexoExtra = (valorExtra > 0 && diasEfetivos > 0) ? valorExtra * dsrDias / diasEfetivos : 0;
    const dsrHorista = reflexoAdNot + reflexoExtra;
    const SAL_MIN_FEDERAL = 1621.00;
    const INSALUBRIDADE_GRAU = { minimo: 10, medio: 20, maximo: 40 };
    const insalubridade = e.insalubridade_grau && INSALUBRIDADE_GRAU[e.insalubridade_grau]
      ? SAL_MIN_FEDERAL * INSALUBRIDADE_GRAU[e.insalubridade_grau] / 100
      : 0;
    const totalBruto = e.salario + valorExtra + totalBonus + adNot + salFamilia + dsrHorista + insalubridade;
    // INSS: tabela progressiva 2026 sobre remuneração total (exceto sal. família — isento)
    const inssBase = Math.max(0, totalBruto - salFamilia);
    const inss = calcINSS(inssBase);
    const fgts = inssBase * fgtsPct / 100;
    const totalLiquido = totalBruto - inss - descVT - descVA - descontoFalta - totalAdiantamento - adiantamentoAuto - totalDesconto;
    const emAfastamento = diasAf > 0 || faltas > 0;
    return { ...e, extra, faltas, descontoFalta, valorExtra, adNot, reflexoAdNot, reflexoExtra, dsrHorista, dsrDias, insalubridade, inss, fgts, vt, va, vtDiario, vaDiario, diasAf, feriadosNT, diasEfetivos, diasVA, uteis, descVT, descVA, salFamilia, filhosElegiveis: filhosElegiveis.length, totalBruto, totalLiquido, emAfastamento, totalAdiantamento, totalAdiantamentoManual, totalAdiantamentoAutoDb, adiantamentoAuto, totalBonus, totalDesconto, ajustesFunc };
  });

  const totais = dados.reduce((acc, e) => ({
    bruto: acc.bruto + e.totalBruto,
    liquido: acc.liquido + e.totalLiquido,
    inss: acc.inss + e.inss,
    fgts: acc.fgts + e.fgts,
    adNot: acc.adNot + e.adNot,
    reflexoAdNot: acc.reflexoAdNot + e.reflexoAdNot,
    reflexoExtra: acc.reflexoExtra + e.reflexoExtra,
    dsrHorista: acc.dsrHorista + e.dsrHorista,
    insalubridade: acc.insalubridade + e.insalubridade,
    vt: acc.vt + e.vt,
    va: acc.va + e.va,
    descBenef: acc.descBenef + e.descVT + e.descVA,
    descFaltas: acc.descFaltas + e.descontoFalta,
    adiantamentos: acc.adiantamentos + e.totalAdiantamento + e.adiantamentoAuto,
    bonus: acc.bonus + e.totalBonus,
    descontos: acc.descontos + e.totalDesconto,
  }), { bruto: 0, liquido: 0, inss: 0, fgts: 0, adNot: 0, reflexoAdNot: 0, reflexoExtra: 0, dsrHorista: 0, insalubridade: 0, vt: 0, va: 0, descBenef: 0, descFaltas: 0, adiantamentos: 0, bonus: 0, descontos: 0 });

  const dadosFiltrados = dados.filter(e => {
    const colMap = { "Nome": e.nome, "Cargo": e.cargo, "Salário Base": String(e.salario), "H.Extra": String(e.extra), "Faltas": String(e.faltas), "VT": String(e.descVT), "VA": String(e.descVA), "INSS": String(e.inss), "Ad. Not.": String(e.adNot), "Líquido": String(e.totalLiquido), "Adiantamento": String(e.adiantamentoAuto + (e.totalAdiantamentoAutoDb||0) + (e.totalAdiantamentoManual||0)), "Ajustes": String((e.totalBonus||0) + (e.totalDesconto||0)), "Total Deduções": String(e.inss + e.descVT + e.descVA + e.descontoFalta + (e.adiantamentoAuto||0) + (e.totalAdiantamentoAutoDb||0) + (e.totalAdiantamentoManual||0) + (e.totalDesconto||0)) };
    return Object.entries(filtros).every(([col, val]) => !val || String(colMap[col] ?? "").toLowerCase().includes(val.toLowerCase()));
  });
  const dadosOrdenados = !sortCol ? dadosFiltrados : [...dadosFiltrados].sort((a, b) => {
    const keyFn = e => ({ "Nome": e.nome, "Cargo": e.cargo, "Salário Base": e.salario, "H.Extra": e.extra, "Faltas": e.faltas, "VT": e.descVT, "VA": e.descVA, "INSS": e.inss, "Ad. Not.": e.adNot, "Líquido": e.totalLiquido }[sortCol]);
    const va = keyFn(a), vb = keyFn(b);
    return typeof va === "number" ? (sortDir === "asc" ? va - vb : vb - va) : (sortDir === "asc" ? String(va).localeCompare(String(vb),"pt-BR") : String(vb).localeCompare(String(va),"pt-BR"));
  });

  const fmt = (v) => `R$ ${parseFloat(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  const exportarCSV = () => {
    const linhas = ["Nome,Cargo,Salário Base,H.Extra,Valor Extra,Faltas,Desc.Faltas,VT,VA,Desc.VT(6%),Desc.VA(6%),INSS(9%),FGTS(8%),Líquido"];
    dados.forEach(e => {
      linhas.push(`${e.nome},${e.cargo},${e.salario.toFixed(2)},${e.extra},${e.valorExtra.toFixed(2)},${e.faltas},${e.descontoFalta.toFixed(2)},${e.vt.toFixed(2)},${e.va.toFixed(2)},${e.descVT.toFixed(2)},${e.descVA.toFixed(2)},${e.inss.toFixed(2)},${e.fgts.toFixed(2)},${e.totalLiquido.toFixed(2)}`);
    });
    linhas.push(`,,,,,,,,,,,,,Total,${totais.liquido.toFixed(2)}`);
    const blob = new Blob(["\uFEFF" + linhas.join("\n")], { type: "text/csv; charset=utf-8" });
    const a = document.createElement("a"); a.href = URL.createObjectURL(blob); a.download = `relatorio_financeiro_${mes}.csv`; a.click();
  };

  const TIPO_AJUSTE_LABEL = { adiantamento: 'Adiantamento', bonus: 'Bonificação', desconto: 'Desconto' };
  const TIPO_AJUSTE_COLOR = { adiantamento: COLORS.warning, bonus: '#16a34a', desconto: COLORS.danger };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      {confirmOpts && <ConfirmModal msg={confirmOpts.msg} onOk={confirmOpts.onOk} onClose={() => setConfirmOpts(null)} />}
      {/* Modal ajustes por funcionário */}
      {modalAjuste && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.45)", zIndex: 1000, display: "flex", alignItems: "center", justifyContent: "center" }}
          onClick={e => { if (e.target === e.currentTarget) setModalAjuste(null); }}>
          <div style={{ background: COLORS.surface, borderRadius: 14, padding: 28, minWidth: 420, maxWidth: 520, boxShadow: "0 8px 40px #0003" }}>
            <div style={{ fontWeight: 800, fontSize: 16, marginBottom: 16 }}>
              Lançamentos — {modalAjuste.nome}
            </div>

            {/* Lista de ajustes existentes */}
            {ajustes.filter(a => Number(a.funcionario_id) === Number(modalAjuste.funcId)).length > 0 && (
              <div style={{ marginBottom: 16 }}>
                {ajustes.filter(a => Number(a.funcionario_id) === Number(modalAjuste.funcId)).map(a => (
                  <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 8, padding: "6px 10px", background: COLORS.bg, borderRadius: 8, marginBottom: 6, fontSize: 13 }}>
                    <span style={{ fontWeight: 700, color: TIPO_AJUSTE_COLOR[a.tipo], minWidth: 90 }}>{TIPO_AJUSTE_LABEL[a.tipo]}</span>
                    <span style={{ fontWeight: 700, flex: 1 }}>{fmt(a.valor)}</span>
                    {a.descricao && a.descricao.toLowerCase().includes('adiantamento_automatico')
                      ? <span style={{ fontSize: 11, background: "#fef3c7", color: "#92400e", borderRadius: 4, padding: "2px 8px", fontWeight: 700, flex: 2 }}>Gerado automaticamente</span>
                      : <span style={{ color: COLORS.textMid, fontSize: 12, flex: 2 }}>{a.descricao || "—"}</span>
                    }
                    <button onClick={() => apagarAjuste(a.id)} style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.danger, fontSize: 14, padding: "2px 6px" }}>✕</button>
                  </div>
                ))}
              </div>
            )}

            {/* Formulário novo ajuste */}
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              <div>
                <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid, display: "block", marginBottom: 4 }}>Tipo</label>
                <select value={formAjuste.tipo} onChange={e => setFormAjuste(f => ({ ...f, tipo: e.target.value }))}
                  style={{ width: "100%", padding: "8px 10px", borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13 }}>
                  <option value="bonus">Bonificação / Benefício</option>
                  <option value="desconto">Desconto / Custo</option>
                </select>
              </div>
              <Input label="Valor (R$)" type="number" min="0" step="0.01" value={formAjuste.valor}
                onChange={e => setFormAjuste(f => ({ ...f, valor: e.target.value }))} />
            </div>
            <Input label="Descrição (ex: Salário Família, uniforme, adiantamento quinzenal)"
              value={formAjuste.descricao} onChange={e => setFormAjuste(f => ({ ...f, descricao: e.target.value }))} />
            <div style={{ display: "flex", gap: 10, marginTop: 16 }}>
              <Btn onClick={salvarAjuste} disabled={salvandoAjuste}>{salvandoAjuste ? "Salvando..." : "Adicionar"}</Btn>
              <Btn variant="secondary" onClick={() => setModalAjuste(null)}>Fechar</Btn>
            </div>
          </div>
        </div>
      )}

      {/* Toast sucesso configurações */}
      {toastCfg && (
        <div style={{ position:"fixed", bottom:24, right:24, background:"#16a34a", color:"#fff", padding:"13px 22px", borderRadius:12, zIndex:9999, fontWeight:700, fontSize:14, boxShadow:"0 4px 24px #0004", display:"flex", alignItems:"center", gap:10 }}>
          ✓ {toastCfg}
        </div>
      )}

      {/* Modal Configurações */}
      {modalCfg && (
        <Modal titulo="Percentuais aplicados" onClose={() => setModalCfg(false)}>
          <div style={{ display:"flex", flexDirection:"column", gap:14, minWidth:320, maxHeight:"80vh", overflowY:"auto" }}>
            <div style={{ fontSize:12, color:COLORS.textMid }}>Percentuais aplicados globalmente. Valores padrão conforme <strong>CCT 2026 SINDEAC/SEAC MG</strong>.</div>

            {/* Salários e encargos */}
            <div style={{ fontSize:11, fontWeight:700, color:COLORS.primary, textTransform:"uppercase", letterSpacing:1, borderBottom:`1px solid ${COLORS.border}`, paddingBottom:6 }}>Encargos</div>
            {[
              { key:"adicional_noturno_pct", label:"Adicional Noturno (%)", hint:"CCT MG 2026: 39%" },
              { key:"fgts_pct", label:"FGTS — custo empresa (%)", hint:"CLT: 8%" },
            ].map(({ key, label, hint }) => (
              <div key={key}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid }}>{label}</label>
                  <span style={{ fontSize:10, color:COLORS.primary, background:COLORS.primaryBg, padding:"1px 8px", borderRadius:10, flexShrink:0 }}>{hint}</span>
                </div>
                <input type="number" step="0.01" value={cfg[key]} onChange={ev => setCfg(c => ({ ...c, [key]: ev.target.value }))} className="rh-input"
                  style={{ border:`1.5px solid ${COLORS.border}`, borderRadius:8, padding:"9px 12px", fontSize:13.5, background:COLORS.gray, color:COLORS.text, width:"100%", outline:"none" }} />
              </div>
            ))}
            {/* INSS — informativo, não configurável */}
            <div style={{ background:"#6366f108", border:"1px solid #6366f130", borderRadius:8, padding:"10px 14px", display:"flex", alignItems:"flex-start", gap:10 }}>
              <svg width="15" height="15" viewBox="0 0 20 20" fill="none" stroke="#6366f1" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink:0, marginTop:1 }}>
                <circle cx="10" cy="10" r="9"/><line x1="10" y1="9" x2="10" y2="14"/><circle cx="10" cy="6" r="0.5" fill="#6366f1" strokeWidth="1"/>
              </svg>
              <div>
                <div style={{ fontSize:11, fontWeight:700, color:"#6366f1", marginBottom:3 }}>INSS — tabela progressiva 2026 (automático)</div>
                <div style={{ fontSize:11, color:COLORS.textMid, lineHeight:1.6 }}>
                  Calculado automaticamente pela tabela vigente — não é configurável.<br/>
                  Até R$&nbsp;1.518,00 → 7,5% · R$&nbsp;1.518–2.793,88 → 9% · R$&nbsp;2.793–4.190,83 → 12% · R$&nbsp;4.190–8.157,41 → 14%<br/>
                  Base: remuneração total (exceto Salário Família, isento).
                </div>
              </div>
            </div>

            {/* Benefícios */}
            <div style={{ fontSize:11, fontWeight:700, color:COLORS.primary, textTransform:"uppercase", letterSpacing:1, borderBottom:`1px solid ${COLORS.border}`, paddingBottom:6, marginTop:4 }}>Desconto de Benefícios (% sobre o valor pago)</div>
            {[
              { key:"vt_desc_pct", label:"Desconto VT", hint:"CLT: máx. 6%" },
              { key:"va_desc_pct", label:"Desconto VA / Alimentação", hint:"CCT MG 2026: máx. 20%" },
            ].map(({ key, label, hint }) => (
              <div key={key}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid }}>{label}</label>
                  <span style={{ fontSize:10, color:COLORS.primary, background:COLORS.primaryBg, padding:"1px 8px", borderRadius:10, flexShrink:0 }}>{hint}</span>
                </div>
                <input type="number" step="0.01" value={cfg[key]} onChange={ev => setCfg(c => ({ ...c, [key]: ev.target.value }))} className="rh-input"
                  style={{ border:`1.5px solid ${COLORS.border}`, borderRadius:8, padding:"9px 12px", fontSize:13.5, background:COLORS.gray, color:COLORS.text, width:"100%", outline:"none" }} />
              </div>
            ))}

            {/* Adiantamento */}
            <div style={{ fontSize:11, fontWeight:700, color:COLORS.primary, textTransform:"uppercase", letterSpacing:1, borderBottom:`1px solid ${COLORS.border}`, paddingBottom:6, marginTop:4 }}>Adiantamento Automático</div>
            <div>
              <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid }}>Adiantamento (% do salário base)</label>
                <span style={{ fontSize:10, color:COLORS.textMid, background:COLORS.bg, padding:"1px 8px", borderRadius:10, flexShrink:0 }}>0% = desativado</span>
              </div>
              <input type="number" step="0.01" value={cfg.adiantamento_pct} onChange={ev => setCfg(c => ({ ...c, adiantamento_pct: ev.target.value }))} className="rh-input"
                style={{ border:`1.5px solid ${COLORS.border}`, borderRadius:8, padding:"9px 12px", fontSize:13.5, background:COLORS.gray, color:COLORS.text, width:"100%", outline:"none" }} />
            </div>

            {/* Horas Extras */}
            <div style={{ fontSize:11, fontWeight:700, color:COLORS.primary, textTransform:"uppercase", letterSpacing:1, borderBottom:`1px solid ${COLORS.border}`, paddingBottom:6, marginTop:4 }}>Horas Extras — Multiplicador</div>
            {[
              { key:"mult_hora_extra", label:"Multiplicador — hora extra normal", hint:"CCT MG 2026: 1,5× (50%)" },
              { key:"mult_hora_extra_feriado", label:"Multiplicador — domingo / feriado", hint:"CCT MG 2026: 2,0× (100%)" },
            ].map(({ key, label, hint }) => (
              <div key={key}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid }}>{label}</label>
                  <span style={{ fontSize:10, color:COLORS.primary, background:COLORS.primaryBg, padding:"1px 8px", borderRadius:10, flexShrink:0 }}>{hint}</span>
                </div>
                <input type="number" step="0.01" value={cfg[key]} onChange={ev => setCfg(c => ({ ...c, [key]: ev.target.value }))} className="rh-input"
                  style={{ border:`1.5px solid ${COLORS.border}`, borderRadius:8, padding:"9px 12px", fontSize:13.5, background:COLORS.gray, color:COLORS.text, width:"100%", outline:"none" }} />
              </div>
            ))}

            {/* Divisores */}
            <div style={{ fontSize:11, fontWeight:700, color:COLORS.primary, textTransform:"uppercase", letterSpacing:1, borderBottom:`1px solid ${COLORS.border}`, paddingBottom:6, marginTop:4 }}>Divisor de Horas (cálculo do valor/hora)</div>
            {[
              { key:"divisor_normal", label:"Divisor — jornada padrão (h/mês)", hint:"CLT: 220h" },
              { key:"divisor_12x36", label:"Divisor — jornada 12×36 (h/mês)", hint:"CCT MG 2026: 210h" },
            ].map(({ key, label, hint }) => (
              <div key={key}>
                <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:4 }}>
                  <label style={{ fontSize:11, fontWeight:600, color:COLORS.textMid }}>{label}</label>
                  <span style={{ fontSize:10, color:COLORS.primary, background:COLORS.primaryBg, padding:"1px 8px", borderRadius:10, flexShrink:0 }}>{hint}</span>
                </div>
                <input type="number" step="1" value={cfg[key]} onChange={ev => setCfg(c => ({ ...c, [key]: ev.target.value }))} className="rh-input"
                  style={{ border:`1.5px solid ${COLORS.border}`, borderRadius:8, padding:"9px 12px", fontSize:13.5, background:COLORS.gray, color:COLORS.text, width:"100%", outline:"none" }} />
              </div>
            ))}

            <div style={{ display:"flex", justifyContent:"flex-end", gap:10, marginTop:6 }}>
              <Btn variant="ghost" onClick={() => setModalCfg(false)}>Cancelar</Btn>
              <Btn onClick={salvarCfg} disabled={salvandoCfg}>{salvandoCfg ? "Salvando..." : "Salvar"}</Btn>
            </div>
          </div>
        </Modal>
      )}

      <div style={{ display: "flex", gap: 8, alignItems: "flex-end", flexWrap:"wrap" }}>
        <Input label="Mês de referência" type="month" value={mes} onChange={e => setMes(e.target.value)} />
        {/* Atualizar — ícone */}
        <button title="Atualizar dados" onClick={() => setRefresh(r => r + 1)}
          style={{ height:36, width:36, borderRadius:8, border:`1px solid ${COLORS.border}`, background:COLORS.bg, cursor:"pointer", color:COLORS.textMid, display:"flex", alignItems:"center", justifyContent:"center", flexShrink:0 }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M23 4v6h-6"/><path d="M1 20v-6h6"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10"/><path d="M20.49 15a9 9 0 0 1-14.85 3.36L1 14"/>
          </svg>
        </button>
        {/* Exportar CSV */}
        <button onClick={exportarCSV}
          style={{ height:36, padding:"0 14px", borderRadius:8, border:"none", background:"linear-gradient(180deg,#369502 0%,#2a7100 100%)", cursor:"pointer", color:"#fff", display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:600, flexShrink:0, boxShadow:"0 1px 2px rgba(46,122,0,0.25),0 3px 10px rgba(46,122,0,0.18)" }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round">
            <path d="M8 2v9M4 8l4 5 4-5"/><path d="M2 14h12"/>
          </svg>
          Exportar CSV
        </button>
        {/* Percentuais aplicados */}
        <button onClick={() => setModalCfg(true)}
          style={{ height:36, padding:"0 14px", borderRadius:8, border:`1px solid ${COLORS.border}`, background:COLORS.surface, cursor:"pointer", color:COLORS.text, display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:500, flexShrink:0 }}>
          <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round">
            <circle cx="5.5" cy="5.5" r="1.5"/><circle cx="10.5" cy="10.5" r="1.5"/><line x1="3" y1="13" x2="13" y2="3"/>
          </svg>
          Percentuais aplicados
        </button>
        {/* Manual de cálculos */}
        <button onClick={() => setModalManual(true)}
          title="Manual de cálculos"
          style={{ height:36, padding:"0 14px", borderRadius:8, border:`1px solid #6366f140`, background:"#6366f108", cursor:"pointer", color:"#6366f1", display:"flex", alignItems:"center", gap:6, fontSize:12, fontWeight:500, flexShrink:0 }}>
          <svg width="14" height="14" viewBox="0 0 20 20" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
            <circle cx="10" cy="10" r="9"/><line x1="10" y1="9" x2="10" y2="14"/><circle cx="10" cy="6" r="0.5" fill="currentColor" strokeWidth="1"/>
          </svg>
          Como calculamos
        </button>
      </div>
      {/* Modal — Manual de cálculos */}
      {modalManual && (
        <Modal titulo="Manual de Cálculos — Folha de Pagamento" onClose={() => setModalManual(false)}>
          <div style={{ maxHeight: "70vh", overflowY: "auto", paddingRight: 4 }}>
            {[
              {
                titulo: "Salário Base",
                cor: COLORS.primary,
                icone: "💼",
                corpo: [
                  "É o salário contratual bruto cadastrado no perfil do funcionário.",
                  "Todos os adicionais e reflexos são calculados sobre ou somados a este valor para compor o Total Bruto.",
                ]
              },
              {
                titulo: "Salário Família",
                cor: "#15803d",
                icone: "👨‍👩‍👧",
                corpo: [
                  "Benefício pago por filho com menos de 14 anos, conforme tabela do INSS.",
                  "Valor atual: R$ 68,48 por filho elegível.",
                  "Condição: salário do funcionário deve ser ≤ R$ 1.908,52.",
                  "Os filhos são cadastrados na aba de dependentes do funcionário.",
                ]
              },
              {
                titulo: "Adicional Noturno (Ad. Not.)",
                cor: "#6366f1",
                icone: "🌙",
                corpo: [
                  "Aplica-se a funcionários cujo turno ou cargo contenha a palavra 'noturno'.",
                  "Conforme CCT 2026 SINDEAC/SEAC-MG: adicional de 39% sobre o salário base mensal.",
                  "Fórmula: Ad. Not. = Salário × 39%",
                  "Válido para jornadas integralmente noturnas (22h–5h), caso típico em conservação e limpeza.",
                  "Divisor de referência: 220h (normal) ou 210h (12×36).",
                ]
              },
              {
                titulo: "Reflexo do Ad. Noturno no DSR",
                cor: "#6366f1",
                icone: "🔁",
                corpo: [
                  "Parcelas variáveis (ad. noturno) refletem proporcionalmente nos Domingos e Feriados do mês (DSR).",
                  "Fórmula: Refl. Ad. Not. = Valor Ad. Noturno ÷ Dias Efetivos × DSRs do mês.",
                  "Dias Efetivos = dias úteis – afastamentos – faltas – feriados não trabalhados.",
                  "DSRs contados conforme a escala do funcionário (ver seção DSR abaixo).",
                ]
              },
              {
                titulo: "Hora Extra",
                cor: "#15803d",
                icone: "⏱",
                corpo: [
                  "Horas extras são lançadas manualmente na aba correspondente.",
                  "Fórmula: Valor Extra = (Salário ÷ Divisor) × Multiplicador × Horas",
                  "Multiplicador padrão: 1,5× (50% — dias normais, conforme CCT 2026).",
                  "Multiplicador feriado: 2,0× (100% — configurável em Percentuais aplicados).",
                  "⚠ O sistema ainda não diferencia automaticamente horas em feriado vs. dias normais — o multiplicador único se aplica a todas as horas lançadas.",
                ]
              },
              {
                titulo: "Reflexo da Hora Extra no DSR",
                cor: "#15803d",
                icone: "🔁",
                corpo: [
                  "O valor das horas extras também reflete nos DSRs do mês.",
                  "Fórmula: Refl. Extra = Valor Hora Extra ÷ Dias Efetivos × DSRs do mês.",
                ]
              },
              {
                titulo: "DSR Horista (Descanso Semanal Remunerado)",
                cor: "#0891b2",
                icone: "📅",
                corpo: [
                  "Soma dos reflexos das parcelas variáveis (ad. noturno + hora extra) nos DSRs do mês.",
                  "DSR Horista = Reflexo Ad. Not. + Reflexo Extra.",
                  "Para escala normal: DSRs = todos os domingos + feriados que caem em dias de trabalho do funcionário.",
                  "Para escala 12×36: DSRs = dias de folga (dias com diferença ímpar em relação à data de referência) que sejam domingo ou feriado.",
                  "A data de referência 12×36 é cadastrada no perfil do funcionário.",
                ]
              },
              {
                titulo: "Insalubridade (NR-15)",
                cor: "#d97706",
                icone: "⚗️",
                corpo: [
                  "Adicional previsto na Norma Regulamentadora 15 do MTE para atividades insalubres.",
                  "Calculado sobre o Salário Mínimo Federal (2026: R$ 1.621,00).",
                  "Grau Mínimo: 10% × R$ 1.621,00 = R$ 162,10",
                  "Grau Médio: 20% × R$ 1.621,00 = R$ 324,20",
                  "Grau Máximo: 40% × R$ 1.621,00 = R$ 648,40",
                  "O grau é definido no cadastro do funcionário e somado ao Total Bruto mensalmente.",
                ]
              },
              {
                titulo: "INSS",
                cor: "#b91c1c",
                icone: "🏛",
                corpo: [
                  "Calculado pela tabela progressiva 2026 sobre a remuneração total (exceto Salário Família, que é isento).",
                  "Base INSS = Total Bruto − Salário Família",
                  "Faixas vigentes (cada faixa incide só sobre a parcela dentro dela):",
                  "Até R$ 1.518,00 → 7,5%",
                  "De R$ 1.518,01 a R$ 2.793,88 → 9%",
                  "De R$ 2.793,89 a R$ 4.190,83 → 12%",
                  "De R$ 4.190,84 a R$ 8.157,41 → 14%",
                  "Teto máximo: R$ 951,63 (acima de R$ 8.157,41 o desconto não aumenta).",
                ]
              },
              {
                titulo: "Vale Transporte (VT)",
                cor: "#b91c1c",
                icone: "🚌",
                corpo: [
                  "Conforme Lei 7.418/85 e CCT 2026: funcionário contribui com até 6% do salário base.",
                  "Fórmula: Desc. VT = min(Salário × 6%, Custo total do VT no mês)",
                  "Se o custo do VT for menor que 6% do salário, desconta-se apenas o custo real.",
                  "O tipo de cartão e os valores são cadastrados no perfil do funcionário.",
                ]
              },
              {
                titulo: "Vale Alimentação / Refeição (VA)",
                cor: "#b91c1c",
                icone: "🍽",
                corpo: [
                  "Desconto de 20% sobre o valor total do benefício de alimentação.",
                  "Conforme CCT 2026 SINDEAC/SEAC-MG: VR R$ 31,34/dia útil ou VA cesta R$ 200,00/mês.",
                  "Valor e número de dias úteis com direito ao benefício são configurados no perfil do funcionário.",
                ]
              },
              {
                titulo: "Adiantamento (40%)",
                cor: "#b91c1c",
                icone: "💳",
                corpo: [
                  "Quando ativado no cadastro do funcionário, o sistema calcula automaticamente 40% do salário bruto como adiantamento quinzenal.",
                  "Adiantamentos manuais lançados na aba de Ajustes também são deduzidos aqui.",
                  "O total de adiantamentos é descontado do líquido final.",
                ]
              },
              {
                titulo: "Faltas e Dias Efetivos",
                cor: "#b91c1c",
                icone: "📋",
                corpo: [
                  "Desconto por falta = Salário ÷ 30 × nº de faltas (divisor CLT para mensalistas).",
                  "Dias Efetivos = Dias Úteis − Afastamentos − Faltas − Feriados não trabalhados.",
                  "Os dias efetivos são usados como denominador nos cálculos de reflexo DSR.",
                ]
              },
              {
                titulo: "Total Bruto e Líquido",
                cor: COLORS.primary,
                icone: "💰",
                corpo: [
                  "Total Bruto = Salário Base + H. Extra + Bônus + Ad. Noturno + Sal. Família + DSR Horista + Insalubridade.",
                  "Total Líquido = Total Bruto − INSS − Desc. VT − Desc. VA − Desc. Faltas − Adiantamentos − Descontos manuais.",
                  "O FGTS (8%) é calculado mas não é deduzido do líquido — é encargo da empresa.",
                ]
              },
            ].map((sec, i) => (
              <div key={i} style={{ marginBottom: 20, borderLeft: `3px solid ${sec.cor}`, paddingLeft: 14 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: sec.cor, marginBottom: 6 }}>{sec.icone} {sec.titulo}</div>
                <ul style={{ margin: 0, padding: 0, listStyle: "none", display: "flex", flexDirection: "column", gap: 4 }}>
                  {sec.corpo.map((linha, j) => (
                    <li key={j} style={{ fontSize: 12, color: COLORS.text, lineHeight: 1.6 }}>
                      {linha.startsWith("Fórmula:") || linha.startsWith("Faixas") || linha.startsWith("Grau") || linha.startsWith("De ") || linha.startsWith("Até ") || linha.startsWith("Acima") || linha.startsWith("Para ") || linha.startsWith("Total ") ?
                        <span style={{ fontFamily: "monospace", background: COLORS.bg, borderRadius: 4, padding: "1px 6px", fontSize: 11, color: COLORS.text }}>{linha}</span>
                        : <span>• {linha}</span>
                      }
                    </li>
                  ))}
                </ul>
              </div>
            ))}
            <div style={{ fontSize: 11, color: COLORS.textMid, borderTop: `1px solid ${COLORS.border}`, paddingTop: 10, marginTop: 4 }}>
              Referência: CCT 2026 SINDEAC/SEAC-MG · NR-15 MTE · Tabela INSS 2026 · Salário Mínimo Federal R$ 1.621,00
            </div>
          </div>
        </Modal>
      )}

      <div style={{ display: "grid", gridTemplateColumns: "repeat(4,1fr)", gap: 12 }}>
        {/* Card 1 — Bruto */}
        <Card style={{ borderTop: `4px solid ${COLORS.primary}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>Total Bruto</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: COLORS.primary }}>{fmt(totais.bruto)}</div>
          <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
            {totais.adNot > 0 && <div style={{ fontSize: 10, color: "#6366f1" }}>+ Ad. Noturno: {fmt(totais.adNot)}</div>}
            {totais.bonus > 0 && <div style={{ fontSize: 10, color: "#16a34a" }}>+ Bônus: {fmt(totais.bonus)}</div>}
          </div>
        </Card>

        {/* Card 2 — Descontos */}
        <Card style={{ borderTop: `4px solid ${COLORS.danger}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>Descontos</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: COLORS.danger }}>
            -{fmt(totais.inss + totais.adiantamentos + totais.descBenef + totais.descFaltas + totais.descontos)}
          </div>
          <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
            {totais.inss > 0 && <div style={{ fontSize: 10, color: COLORS.warning }}>INSS: {fmt(totais.inss)}</div>}
            {totais.adiantamentos > 0 && <div style={{ fontSize: 10, color: COLORS.warning }}>Adiant.: {fmt(totais.adiantamentos)}</div>}
            {totais.descBenef > 0 && <div style={{ fontSize: 10, color: COLORS.textMid }}>Desc. VT/VA: {fmt(totais.descBenef)}</div>}
            {totais.descFaltas > 0 && <div style={{ fontSize: 10, color: COLORS.danger }}>Faltas: {fmt(totais.descFaltas)}</div>}
            {totais.descontos > 0 && <div style={{ fontSize: 10, color: COLORS.danger }}>Outros desc.: {fmt(totais.descontos)}</div>}
          </div>
        </Card>

        {/* Card 3 — Custo patronal (informativo) */}
        <Card style={{ borderTop: `4px solid ${COLORS.warning}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>FGTS Patronal <span style={{ fontSize: 9, fontWeight: 400 }}>(informativo)</span></div>
          <div style={{ fontSize: 15, fontWeight: 900, color: COLORS.warning }}>{fmt(totais.fgts)}</div>
          <div style={{ marginTop: 4, display: "flex", flexDirection: "column", gap: 2 }}>
            <div style={{ fontSize: 10, color: COLORS.textMid }}>FGTS: {fmt(totais.fgts)}</div>
            {(totais.vt + totais.va) > 0 && <div style={{ fontSize: 10, color: COLORS.info }}>VT+VA: {fmt(totais.vt + totais.va)}</div>}
          </div>
        </Card>

        {/* Card 4 — Líquido */}
        <Card style={{ borderTop: `4px solid ${COLORS.accent}` }}>
          <div style={{ fontSize: 11, color: COLORS.textMid, fontWeight: 600 }}>Líquido a Pagar</div>
          <div style={{ fontSize: 15, fontWeight: 900, color: COLORS.accent }}>{fmt(totais.liquido)}</div>
          <div style={{ marginTop: 4, fontSize: 10, color: COLORS.textMid }}>
            Bruto − descontos = líquido
          </div>
        </Card>
      </div>

      {erroAfastamentos && (
        <div style={{ background: "#fee2e2", border: "1px solid #fca5a5", borderRadius: 8, padding: "8px 14px", fontSize: 12, color: "#991b1b" }}>
          ⚠️ Erro ao carregar afastamentos/férias: {erroAfastamentos} — dias de afastamento não serão descontados.
        </div>
      )}

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div ref={tableScrollRef} style={{ overflow: "auto", WebkitOverflowScrolling: "touch", maxHeight: "calc(100vh - 320px)" }}>
          <table style={{ width: "max-content", minWidth: "100%", borderCollapse: "collapse", fontSize: 11 }}>
            <thead>
              <tr style={{ background: COLORS.bg }}>
                {["Nome","Cargo","Salário Base","Sal. Família","Ad. Not.","Refl. Ad.Not.","H.Extra","Refl. Extra","DSR Horista","Insalubridade","Faltas","Dias efetivos","VT","VA","INSS","Adiantamento","Ajustes","Total Deduções","Líquido"].map((h,idx) => thSort(h, idx === 0))}
                <th style={{ padding: "10px 14px", fontSize: 11, color: COLORS.textMid, fontWeight: 600, position: "sticky", top: 0, zIndex: 3, background: COLORS.bg }}></th>
              </tr>
            </thead>
            <tbody>
              {dadosOrdenados.map((e, i) => (
                <tr key={e.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                  <td style={{ padding: "10px 14px", fontWeight: 700, whiteSpace: "nowrap", position: "sticky", left: 0, zIndex: 2, background: i % 2 === 0 ? COLORS.white : COLORS.gray, borderRight: `1px solid ${COLORS.border}` }}>{e.nome}</td>
                  <td style={{ padding: "10px 14px", color: COLORS.textMid, whiteSpace: "nowrap" }}>{e.cargo}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap" }}>{fmt(e.salario)}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.salFamilia > 0 ? "#15803d" : COLORS.textMid, fontWeight: e.salFamilia > 0 ? 700 : 400 }} title={e.salFamilia > 0 ? `${e.filhosElegiveis} filho(s) < 14 anos × R$ 68,48` : ""}>{e.salFamilia > 0 ? `+${fmt(e.salFamilia)}` : "—"}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.adNot > 0 ? "#15803d" : COLORS.textMid, fontWeight: e.adNot > 0 ? 700 : 400 }}>{e.adNot > 0 ? `+${fmt(e.adNot)}` : "—"}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.reflexoAdNot > 0 ? "#15803d" : COLORS.textMid, fontWeight: e.reflexoAdNot > 0 ? 700 : 400 }} title={e.reflexoAdNot > 0 ? `Ad.Not. ÷ ${e.diasEfetivos}d trabalhados × ${e.dsrDias} DSRs` : ""}>{e.reflexoAdNot > 0 ? `+${fmt(e.reflexoAdNot)}` : "—"}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.extra > 0 ? "#15803d" : COLORS.textMid, fontWeight: e.extra > 0 ? 700 : 400 }}>{e.extra > 0 ? `+${e.extra}h` : "—"}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.reflexoExtra > 0 ? "#15803d" : COLORS.textMid, fontWeight: e.reflexoExtra > 0 ? 700 : 400 }} title={e.reflexoExtra > 0 ? `Valor extra ÷ ${e.diasEfetivos}d trabalhados × ${e.dsrDias} DSRs` : ""}>{e.reflexoExtra > 0 ? `+${fmt(e.reflexoExtra)}` : "—"}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.dsrHorista > 0 ? "#15803d" : COLORS.textMid, fontWeight: e.dsrHorista > 0 ? 700 : 400 }} title={e.dsrHorista > 0 ? `Refl. Ad.Not. + Refl. Extra` : ""}>{e.dsrHorista > 0 ? `+${fmt(e.dsrHorista)}` : "—"}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.insalubridade > 0 ? "#15803d" : COLORS.textMid, fontWeight: e.insalubridade > 0 ? 700 : 400 }} title={e.insalubridade > 0 ? `NR-15 Grau ${e.insalubridade_grau}: ${({minimo:"10%",medio:"20%",maximo:"40%"}[e.insalubridade_grau])} × R$ 1.621,00` : ""}>{e.insalubridade > 0 ? `+${fmt(e.insalubridade)}` : "—"}</td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.faltas > 0 ? "#b91c1c" : COLORS.textMid, fontWeight: e.faltas > 0 ? 700 : 400 }}>{e.faltas > 0 ? `${e.faltas}d (-${fmt(e.descontoFalta)})` : "—"}</td>
                  <td style={{ padding: "10px 14px", fontSize: 12, whiteSpace: "nowrap" }}>
                    <span title={`Úteis: ${e.uteis} | Afastamento: ${e.diasAf} | Faltas: ${e.faltas} | Feriados não trabalhados: ${e.feriadosNT}`}
                      style={{ fontWeight: 700, color: e.diasEfetivos < e.uteis ? COLORS.warning : COLORS.textMid }}>
                      {e.diasEfetivos}d
                    </span>
                    {e.diasAf > 0 && <span style={{ fontSize: 10, color: COLORS.textMid, marginLeft: 4 }}>(-{e.diasAf}af)</span>}
                    {e.feriadosNT > 0 && <span style={{ fontSize: 10, color: "#9333ea", marginLeft: 4 }}>(-{e.feriadosNT}fer)</span>}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.descVT > 0 ? "#b91c1c" : COLORS.textMid, fontWeight: e.descVT > 0 ? 700 : 400 }}>
                    {e.descVT > 0 ? <span title={`6% × R$${fmt(e.vt)} VT`}>-{fmt(e.descVT)}</span> : "—"}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: e.descVA > 0 ? "#b91c1c" : COLORS.textMid, fontWeight: e.descVA > 0 ? 700 : 400 }}>
                    {e.descVA > 0 ? <span title={`20% × R$${fmt(e.va)} VA`}>-{fmt(e.descVA)}</span> : "—"}
                  </td>
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: "#b91c1c", fontWeight: 700 }}>{fmt(e.inss)}</td>
                  {/* Coluna Adiantamento */}
                  <td style={{ padding: "10px 6px", whiteSpace: "nowrap" }}>
                    {(e.adiantamentoAuto + e.totalAdiantamentoAutoDb + e.totalAdiantamentoManual) > 0 ? (
                      <span style={{ color: "#b91c1c", fontWeight: 700 }}>-{fmt(e.adiantamentoAuto + e.totalAdiantamentoAutoDb + e.totalAdiantamentoManual)}</span>
                    ) : <span style={{ color: COLORS.textMid }}>—</span>}
                  </td>
                  {/* Coluna Ajustes (bônus e descontos manuais) */}
                  <td style={{ padding: "10px 6px", whiteSpace: "nowrap" }}>
                    {(e.totalBonus === 0 && e.totalDesconto === 0) ? (
                      <span style={{ color: COLORS.textMid }}>—</span>
                    ) : (
                      <div style={{ display: "flex", flexDirection: "column", gap: 2 }}>
                        {e.totalBonus > 0 && <span style={{ color: "#15803d", fontWeight: 700 }}>+{fmt(e.totalBonus)}</span>}
                        {e.totalDesconto > 0 && <span style={{ color: "#b91c1c", fontWeight: 700 }}>-{fmt(e.totalDesconto)}</span>}
                      </div>
                    )}
                  </td>
                  {/* Coluna Total Deduções */}
                  <td style={{ padding: "10px 14px", whiteSpace: "nowrap", color: "#b91c1c", fontWeight: 800 }}>
                    {(() => { const tot = e.inss + e.descVT + e.descVA + e.descontoFalta + e.adiantamentoAuto + e.totalAdiantamentoAutoDb + e.totalAdiantamentoManual + e.totalDesconto; return tot > 0 ? `-${fmt(tot)}` : <span style={{ color: COLORS.textMid, fontWeight: 400 }}>—</span>; })()}
                  </td>
                  <td style={{ padding: "10px 14px", fontWeight: 800, color: COLORS.primary, whiteSpace: "nowrap" }}>{fmt(e.totalLiquido)}</td>
                  <td style={{ padding: "6px 10px" }}>
                    <button onClick={() => { setModalAjuste({ funcId: e.id, nome: e.nome }); setFormAjuste({ tipo: 'bonus', valor: '', descricao: '' }); }}
                      title="Lançar adiantamento, bônus ou desconto"
                      style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}40`, borderRadius: 7, cursor: "pointer", padding: "5px 8px", color: COLORS.primary, lineHeight: 1, display: "flex", alignItems: "center", gap: 4, fontSize: 10, fontWeight: 700 }}>
                      <svg width="13" height="13" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                        <path d="M3 2h10v12l-2-1-2 1-2-1-2 1V2z"/><line x1="6" y1="7" x2="10" y2="7"/><line x1="8" y1="5" x2="8" y2="9"/>
                      </svg>
                    </button>
                  </td>
                </tr>
              ))}
              <tr style={{ background: COLORS.primary, fontWeight: 800 }}>
                <td colSpan={19} style={{ padding: "11px 14px", textAlign: "right", color: "#fff", letterSpacing: 1 }}>TOTAL LÍQUIDO A PAGAR</td>
                <td style={{ padding: "11px 14px", color: "#fff", fontSize: 15, fontWeight: 900, whiteSpace: "nowrap" }}>{fmt(totais.liquido)}</td>
              </tr>
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── PROPOSTAS COMERCIAIS ─────────────────────────────────────────────────────
const CLAUSULAS_PADRAO = [
  { titulo: "CLÁUSULA 1ª – DO OBJETO", texto: "O presente Contrato tem por objeto a prestação de serviços de {objeto}, a serem executados nas dependências de {local_servico}, conforme especificações técnicas e quantitativos de mão de obra descritos no Anexo I." },
  { titulo: "CLÁUSULA 2ª – DO PRAZO DE VIGÊNCIA", texto: "O presente Contrato terá vigência de {vigencia_inicio} a {vigencia_fim}, podendo ser prorrogado por períodos iguais e sucessivos, mediante acordo formal entre as partes, com antecedência mínima de {prazo_renovacao_meses} (trinta) dias antes do seu vencimento." },
  { titulo: "CLÁUSULA 3ª – DO VALOR E FORMA DE PAGAMENTO", texto: "Pelos serviços contratados, a CONTRATANTE pagará à CONTRATADA o valor mensal de R$ {valor_total} ({valor_total_extenso}), vencível todo dia {dia_vencimento} de cada mês subsequente ao da prestação dos serviços, mediante {forma_pagamento}. O não pagamento na data acordada implicará multa de 2% (dois por cento) sobre o valor devido, acrescido de juros moratórios de 1% (um por cento) ao mês e atualização monetária pelo {indice_reajuste}." },
  { titulo: "CLÁUSULA 4ª – DO REAJUSTE", texto: "O valor contratual será reajustado anualmente, a cada {periodicidade_reajuste} ({periodicidade_reajuste_extenso}) meses de vigência, com base na variação do índice {indice_reajuste} acumulado no período, ou pelo índice de reajuste da Convenção Coletiva de Trabalho da categoria, prevalecendo o maior, sem prejuízo de repasse de quaisquer encargos trabalhistas determinados em legislação ou CCT." },
  { titulo: "CLÁUSULA 5ª – DAS OBRIGAÇÕES DA CONTRATADA", texto: "À CONTRATADA incumbe:\na) Fornecer mão de obra qualificada, devidamente registrada e portando os EPIs necessários;\nb) Efetuar o pagamento dos salários dos empregados alocados até o 5º (quinto) dia útil de cada mês;\nc) Recolher todos os encargos sociais, trabalhistas e previdenciários (INSS, FGTS, IRRF e demais obrigações legais);\nd) Fornecer uniformes, crachás e equipamentos necessários à execução dos serviços;\ne) Substituir, em até 24 (vinte e quatro) horas, qualquer colaborador que se ausentar ou cujo desempenho não atenda às expectativas da CONTRATANTE;\nf) Apresentar, mensalmente, cópia da GFIP, GPS e folha de pagamento dos empregados alocados;\ng) Manter sigilo absoluto sobre informações e dados da CONTRATANTE." },
  { titulo: "CLÁUSULA 6ª – DAS OBRIGAÇÕES DA CONTRATANTE", texto: "À CONTRATANTE incumbe:\na) Efetuar os pagamentos nas datas acordadas;\nb) Disponibilizar local adequado e seguro para execução dos serviços;\nc) Fornecer, quando necessário, os materiais e insumos para realização dos serviços;\nd) Comunicar à CONTRATADA, com antecedência mínima de 48 (quarenta e oito) horas, eventuais alterações no cronograma de trabalho;\ne) Não exercer o poder de direção ou subordinação direta sobre os colaboradores da CONTRATADA, respeitando a relação de trabalho existente entre estes e a CONTRATADA." },
  { titulo: "CLÁUSULA 7ª – DA RESPONSABILIDADE TRABALHISTA E PREVIDENCIÁRIA", texto: "A CONTRATADA é a única empregadora dos trabalhadores alocados na execução dos serviços objeto deste Contrato, sendo responsável integral e exclusiva pelo cumprimento de todas as obrigações trabalhistas e previdenciárias previstas na legislação vigente, na Convenção Coletiva de Trabalho da categoria e no presente instrumento.\nA CONTRATANTE não manterá nenhum vínculo empregatício com os empregados da CONTRATADA, não respondendo, em hipótese alguma, pelos atos por eles praticados, ressalvada a responsabilidade subsidiária prevista na Súmula 331 do TST." },
  { titulo: "CLÁUSULA 8ª – DA RESCISÃO", texto: "O presente Contrato poderá ser rescindido:\na) Por qualquer das partes, mediante aviso prévio por escrito com antecedência mínima de 30 (trinta) dias;\nb) Imediatamente, por qualquer das partes, em caso de descumprimento das obrigações contratuais;\nc) Por força maior ou caso fortuito, devidamente comprovado.\nNa hipótese de rescisão antecipada sem justa causa por parte da CONTRATANTE, esta arcará com multa rescisória equivalente a 10% (dez por cento) do valor total dos meses restantes do contrato." },
  { titulo: "CLÁUSULA 9ª – DA PROTEÇÃO DE DADOS (LGPD)", texto: "As partes comprometem-se a tratar os dados pessoais a que tiverem acesso em razão deste Contrato em estrita conformidade com a Lei Geral de Proteção de Dados Pessoais (Lei nº 13.709/2018 – LGPD), adotando medidas técnicas e organizacionais adequadas para proteger os dados contra acessos não autorizados e situações acidentais ou ilícitas de destruição, perda, alteração, comunicação ou difusão." },
  { titulo: "CLÁUSULA 10ª – DAS DISPOSIÇÕES GERAIS", texto: "O presente Contrato obriga as partes e seus sucessores a qualquer título. Os casos omissos serão resolvidos de comum acordo entre as partes, à luz das disposições legais aplicáveis. Qualquer alteração neste instrumento somente terá validade se formalizada por escrito, em Termo Aditivo assinado por ambas as partes." },
  { titulo: "CLÁUSULA 11ª – DO FORO", texto: "Fica eleito o Foro da Comarca de {cidade_contratada} para dirimir quaisquer dúvidas ou litígios oriundos do presente Contrato, com renúncia expressa a qualquer outro, por mais privilegiado que seja." },
];

function calcularServico(s) {
  const pct = (v) => { const n = parseFloat(v); return isNaN(n) ? 0 : n; };
  const sal = parseFloat(s.salario_base) || 0;
  const encargos = sal * pct(s.encargos_pct) / 100;
  const provisoes = sal * pct(s.provisoes_pct) / 100;
  const beneficios = (parseFloat(s.vt_mensal) || 0) + (parseFloat(s.va_mensal) || 0) +
    (parseFloat(s.plano_saude) || 0) + (parseFloat(s.outros_beneficios) || 0);
  const operacional = (parseFloat(s.uniforme_epi) || 0) + (parseFloat(s.materiais) || 0);
  const subtotal = sal + encargos + provisoes + beneficios + operacional;
  const overhead = subtotal * pct(s.overhead_pct) / 100;
  const custoTotal = subtotal + overhead;
  const margem = custoTotal * pct(s.margem_pct) / 100;
  const precoSemImposto = custoTotal + margem;
  const impostosPct = pct(s.impostos_pct);
  const valorUnitario = impostosPct >= 100 ? precoSemImposto : precoSemImposto / (1 - impostosPct / 100);
  const qtd = parseInt(s.qtd_postos) || parseInt(s.qtd) || 1;
  return {
    sal, encargos, provisoes, beneficios, operacional,
    subtotal, overhead, custoTotal, margem, precoSemImposto,
    impostos: valorUnitario * impostosPct / 100,
    valorUnitario, valorTotal: valorUnitario * qtd,
  };
}

const SERVICO_VAZIO = {
  cargo: "", qtd: 1, turno: "Manhã", local_key: "",
  salario_base: "", encargos_pct: "", provisoes_pct: "",
  vt_mensal: "", va_mensal: "", plano_saude: "", outros_beneficios: "",
  uniforme_epi: "", materiais: "", overhead_pct: "", margem_pct: "", impostos_pct: "",
};

function Propostas({ token, clientes, telaInicial, empresaFiltroId, modoNovoInicial, onVoltar }) {
  const [confirmOpts, setConfirmOpts] = useState(null);
  const [tela, setTela] = useState(telaInicial || "lista"); // lista | editor
  const [propostas, setPropostas] = useState([]);
  const [configEmpresa, setConfigEmpresa] = useState({});
  const [abaEditor, setAbaEditor] = useState("dados"); // dados | calculo | contrato
  const [salvando, setSalvando] = useState(false);
  const [editandoId, setEditandoId] = useState(null);
  const [filtro, setFiltro] = useState("");
  const [filtroStatus, setFiltroStatus] = useState("");
  const [modalConfigEmp, setModalConfigEmp] = useState(false);
  const [formConfigEmp, setFormConfigEmp] = useState({});
  const [salvandoConfigEmp, setSalvandoConfigEmp] = useState(false);
  const [empresasCad, setEmpresasCad] = useState([]);
  const [empresaFaturadoraId, setEmpresaFaturadoraId] = useState(empresaFiltroId ? String(empresaFiltroId) : "");
  const [modalConverter, setModalConverter] = useState(false);
  const [convertendo, setConvertendo] = useState(false);
  const [resultConverter, setResultConverter] = useState(null);

  // Dados da proposta
  const [dados, setDados] = useState({
    numero: "", status: "rascunho", cliente_id: "", cliente_nome: "", cliente_cnpj: "",
    cliente_endereco: "", cliente_cidade: "", cliente_uf: "", cliente_representante: "",
    local_servico: "", objeto: "conservação, limpeza e manutenção de áreas comuns",
    data_proposta: new Date().toISOString().slice(0,10),
    vigencia_inicio: "", vigencia_fim: "", sem_vigencia_fim: false,
    prazo_renovacao_meses: "12", sem_renovacao: false,
    sem_financeiro: false,
    dia_vencimento: "10", forma_pagamento: "boleto bancário",
    indice_reajuste: "INPC", periodicidade_reajuste: "12",
    valor_total: "0", observacoes: "",
  });
  const [servicos, setServicos] = useState([{ ...SERVICO_VAZIO }]);
  const [clausulas, setClausulas] = useState(CLAUSULAS_PADRAO.map((c,i) => ({ ...c, ordem: i+1 })));
  const [locais, setLocais] = useState([]);
  const [localForm, setLocalForm] = useState(null); // null | {mode:'add'|'edit', _key, tipo, nome, pai_key, endereco, usa_endereco_pai}
  const localKeyRef = useRef(0);
  const newLocalKey = () => `lk${++localKeyRef.current}`;
  const [calcsExpandidos, setCalcsExpandidos] = useState({});

  // ── Novos estados para redesign proposta/contrato ──────────────────────────
  const [modoNovo, setModoNovo] = useState(modoNovoInicial || "proposta"); // "proposta" | "contrato"
  const [tipoPessoa, setTipoPessoa] = useState("pj"); // "pj" | "pf"
  const [dadosCli, setDadosCli] = useState({
    tipo_pessoa: "pj",
    razao_social: "", nome_fantasia: "", cnpj: "", cpf: "",
    logradouro: "", numero: "", complemento: "", bairro: "", cep: "",
    cidade: "", ibge: "", uf: "",
    contato_nome: "", contato_email: "", contato_telefone: "",
    financeiro_nome: "", financeiro_email: "", financeiro_telefone: "",
    inscricao_estadual: "", ie_isento: false,
    simples_nacional: false,
    ramo_atividade: "",
    inscricao_municipal: "", im_isento: false,
  });
  const [servicosForm, setServicosForm] = useState([]);
  const [buscaServico, setBuscaServico] = useState("");
  const [fcontr, setFcontr] = useState({
    data_inicio: "", data_fim: "", sem_fim: false,
    dia_vencimento: "10", forma_pagamento: "pix",
    indice_reajuste: "INPC", periodicidade_reajuste: "12",
    valor_total_contrato: "", duracao_meses: "",
  });
  const [clienteExId, setClienteExId] = useState("");
  const [buscaCliente, setBuscaCliente] = useState("");
  const [salvandoContr, setSalvandoContr] = useState(false);

  const fmt = (v) => `R$ ${parseFloat(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const fmtN = (v, d=2) => parseFloat(v||0).toFixed(d);

  const STATUS_COLOR = { rascunho: COLORS.textMid, enviada: COLORS.info, convertida: COLORS.primary };
  const STATUS_LABEL = { rascunho:"Rascunho", enviada:"Enviada", convertida:"Convertida" };

  const carregar = () => {
    apiFetch("GET", "/api/propostas", undefined, token).then(r => setPropostas(Array.isArray(r)?r:[])).catch(()=>{});
    apiFetch("GET", "/api/propostas/config-empresa", undefined, token).then(r => { setConfigEmpresa(r||{}); setFormConfigEmp(r||{}); }).catch(()=>{});
    apiFetch("GET", "/api/empresas", undefined, token).then(r => setEmpresasCad(Array.isArray(r)?r:[])).catch(()=>{});
  };

  const abrirConfigEmp = () => { setFormConfigEmp({...configEmpresa}); setModalConfigEmp(true); };
  const salvarConfigEmp = async () => {
    setSalvandoConfigEmp(true);
    try {
      await apiFetch("POST", "/api/propostas/config-empresa", formConfigEmp, token);
      setConfigEmpresa({...formConfigEmp});
      setModalConfigEmp(false);
    } catch(e) { alert(e.message); }
    setSalvandoConfigEmp(false);
  };
  useEffect(() => { if(token) carregar(); }, [token]);

  useEffect(() => {
    if (!empresaFiltroId || !empresasCad.length) return;
    const em = empresasCad.find(e => String(e.id) === String(empresaFiltroId));
    if (!em) return;
    const cfg = { _empresa_id: em.id, razao_social: em.razao_social||em.nome||"", cnpj: em.cnpj||"", endereco: em.endereco||"", numero: em.numero||"", complemento: em.complemento||"", cidade: em.cidade||"", uf: em.uf||"", cep: em.cep||"", telefone: em.telefone||"", email: em.email||"", site: em.site||"", responsavel_nome: em.responsavel_nome||"", responsavel_cargo: em.responsavel_cargo||"", logo_base64: em.logo_base64||null };
    setConfigEmpresa(cfg);
    setFormConfigEmp(cfg);
    setEmpresaFaturadoraId(String(em.id));
    apiFetch("POST", "/api/propostas/config-empresa", cfg, token).catch(()=>{});
  }, [empresaFiltroId, empresasCad]);

  useEffect(() => {
    if (empresaFaturadoraId || !configEmpresa._empresa_id) return;
    setEmpresaFaturadoraId(String(configEmpresa._empresa_id));
  }, [configEmpresa._empresa_id]);

  const svcsAtivos = servicosForm.length > 0 ? servicosForm : servicos;
  const totalGeral = svcsAtivos.reduce((s, sv) => s + calcularServico(sv).valorTotal, 0);

  useEffect(() => {
    setDados(d => ({ ...d, valor_total: totalGeral.toFixed(2) }));
  }, [totalGeral]);

  const novoEditor = (modo = "proposta") => {
    setModoNovo(modo);
    setEditandoId(null);
    setDados({ numero: `P-${new Date().getFullYear()}-${String(propostas.length+1).padStart(3,'0')}`, status:"rascunho", cliente_id:"", cliente_nome:"", cliente_cnpj:"", cliente_endereco:"", cliente_cidade:"", cliente_uf:"", cliente_representante:"", local_servico:"", objeto:"conservação, limpeza e manutenção de áreas comuns", data_proposta:new Date().toISOString().slice(0,10), vigencia_inicio:"", vigencia_fim:"", prazo_renovacao_meses:"12", dia_vencimento:"10", forma_pagamento:"boleto bancário", indice_reajuste:"INPC", periodicidade_reajuste:"12", valor_total:"0", observacoes:"" });
    setServicos([{ ...SERVICO_VAZIO }]);
    setServicosForm([]);
    setClausulas(CLAUSULAS_PADRAO.map((c,i) => ({ ...c, ordem: i+1 })));
    setLocais([]);
    setLocalForm(null);
    setAbaEditor("dados");
    setTipoPessoa("pj");
    setDadosCli({ tipo_pessoa:"pj", razao_social:"", nome_fantasia:"", cnpj:"", cpf:"", logradouro:"", numero:"", complemento:"", bairro:"", cep:"", cidade:"", ibge:"", uf:"", contato_nome:"", contato_email:"", contato_telefone:"", financeiro_nome:"", financeiro_email:"", financeiro_telefone:"", inscricao_estadual:"", ie_isento:false, simples_nacional:false, ramo_atividade:"", inscricao_municipal:"", im_isento:false });
    setFcontr({ data_inicio:"", data_fim:"", sem_fim:false, dia_vencimento:"10", forma_pagamento:"pix", indice_reajuste:"INPC", periodicidade_reajuste:"12", valor_total_contrato:"", duracao_meses:"" });
    setClienteExId("");
    setBuscaServico("");
    // Auto-seleciona primeira empresa se nenhuma configurada
    if (!configEmpresa.razao_social && empresasCad.length > 0) {
      const em = empresasCad[0];
      setConfigEmpresa({ _empresa_id: em.id, razao_social: em.razao_social||em.nome||"", cnpj: em.cnpj||"", endereco: em.endereco||"", numero: em.numero||"", complemento: em.complemento||"", cidade: em.cidade||"", uf: em.uf||"", cep: em.cep||"", telefone: em.telefone||"", email: em.email||"", site: em.site||"", responsavel_nome: em.responsavel_nome||"", responsavel_cargo: em.responsavel_cargo||"", logo_base64: em.logo_base64||null});
    }
    setTela("editor");
  };

  const abrirEditar = async (p) => {
    try {
      const d = await apiFetch("GET", `/api/propostas/${p.id}`, undefined, token);
      setEditandoId(p.id);
      setModoNovo("proposta");
      setDados({ ...d, cliente_id: d.cliente_id||"", valor_total: d.valor_total||"0", prazo_renovacao_meses: d.prazo_renovacao_meses||"12", dia_vencimento: d.dia_vencimento||"10", periodicidade_reajuste: d.periodicidade_reajuste||"12" });
      const svcs = d.servicos?.length ? d.servicos : [{ ...SERVICO_VAZIO }];
      setServicos(svcs);
      // converte formato antigo para servicosForm
      setServicosForm(svcs.map(s => ({
        descricao: s.cargo || s.descricao || "",
        cnae: s.cnae || "",
        qtd_postos: parseInt(s.qtd_postos) || parseInt(s.qtd) || 1,
        salario_base: s.salario_base||"", encargos_pct: s.encargos_pct||"",
        provisoes_pct: s.provisoes_pct||"", vt_mensal: s.vt_mensal||"",
        va_mensal: s.va_mensal||"", plano_saude: s.plano_saude||"",
        outros_beneficios: s.outros_beneficios||"", uniforme_epi: s.uniforme_epi||"",
        materiais: s.materiais||"", overhead_pct: s.overhead_pct||"",
        margem_pct: s.margem_pct||"", impostos_pct: s.impostos_pct||"",
      })));
      setClausulas(d.clausulas?.length ? d.clausulas : CLAUSULAS_PADRAO.map((c,i)=>({...c,ordem:i+1})));
      setLocais(d.locais?.length ? d.locais : []);
      setLocalForm(null);
      setAbaEditor("dados");
      setBuscaServico("");
      setTela("editor");
    } catch(e) { alert(e.message); }
  };

  const salvar = async (statusOverride) => {
    if (!dados.cliente_nome) return alert("Informe o nome do cliente.");
    setSalvando(true);
    const svcsParaSalvar = servicosForm.length > 0 ? servicosForm : servicos;
    const payload = {
      dados: { ...dados, status: statusOverride || dados.status, valor_total: totalGeral.toFixed(2), empresa_id: empresaFaturadoraId || null },
      servicos: svcsParaSalvar.map(s => ({ ...s, ...calcularServico(s), valor_unitario: calcularServico(s).valorUnitario, valor_total: calcularServico(s).valorTotal })),
      clausulas,
      locais,
    };
    try {
      if (editandoId) {
        await apiFetch("PUT", `/api/propostas/${editandoId}`, payload, token);
      } else {
        const r = await apiFetch("POST", "/api/propostas", payload, token);
        setEditandoId(r.id);
      }
      if (statusOverride) setDados(d => ({...d, status: statusOverride}));
      carregar();
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const apagar = (id) => {
    setConfirmOpts({ msg: "Apagar esta proposta?", onOk: async () => {
      try { await apiFetch("DELETE", `/api/propostas/${id}`, undefined, token); carregar(); } catch(e) { alert(e.message); }
    }});
  };

  const preencherCliente = (clienteId) => {
    const c = clientes?.find(cl => String(cl.id) === String(clienteId));
    if (!c) return;
    setDados(d => ({ ...d, cliente_id: clienteId, cliente_nome: c.nome, cliente_cnpj: c.cnpj||"", cliente_endereco: c.endereco||"", cliente_cidade: c.cidade||"", cliente_uf: c.uf||"" }));
  };

  const preencherClienteNoCli = (clienteId) => {
    const c = clientes?.find(cl => String(cl.id) === String(clienteId));
    if (!c) return;
    setClienteExId(clienteId);
    setDadosCli(d => ({
      ...d,
      razao_social: c.nome||"", nome_fantasia: c.nome_fantasia||"",
      cnpj: c.cnpj||"", cpf: c.cpf||"",
      logradouro: c.endereco||"", cidade: c.cidade||"", uf: c.uf||"",
      contato_nome: c.contato_nome||"", contato_email: c.email||"",
      contato_telefone: c.telefone||"",
    }));
  };

  const salvarNovoContrato = async () => {
    const nome = tipoPessoa === "pj" ? dadosCli.razao_social : dadosCli.razao_social;
    if (!nome.trim()) return alert("Informe a Razão Social / Nome do cliente.");
    setSalvandoContr(true);
    try {
      // 1. Criar ou usar cliente existente
      let cliente_id = clienteExId;
      if (!cliente_id) {
        const cliRes = await apiFetch("POST", "/api/contratos/clientes", {
          nome: dadosCli.razao_social,
          cnpj: tipoPessoa === "pj" ? dadosCli.cnpj : "",
          cpf: tipoPessoa === "pf" ? dadosCli.cpf : "",
          endereco: `${dadosCli.logradouro}${dadosCli.numero ? ", "+dadosCli.numero : ""}${dadosCli.bairro ? " - "+dadosCli.bairro : ""}`,
          cidade: dadosCli.cidade, uf: dadosCli.uf, cep: dadosCli.cep,
          telefone: dadosCli.contato_telefone, email: dadosCli.contato_email,
          contato_nome: dadosCli.contato_nome, segmento: dadosCli.ramo_atividade,
          empresa_id: empresaFaturadoraId || null,
        }, token);
        cliente_id = String(cliRes.id);
      }
      // 2. Criar contrato
      const totalContr = parseFloat(fcontr.valor_total_contrato) || 0;
      const dur = parseInt(fcontr.duracao_meses) || 0;
      const mensal = dur > 0 ? totalContr / dur : 0;
      const contRes = await apiFetch("POST", "/api/contratos/contratos", {
        cliente_id,
        objeto: servicosForm.map(s => s.descricao).join(", ") || "Prestação de Serviços",
        data_inicio: fcontr.data_inicio,
        data_fim: fcontr.sem_fim ? "" : fcontr.data_fim,
        valor_mensal: mensal.toFixed(2),
        dia_vencimento: fcontr.dia_vencimento,
        forma_pagamento: fcontr.forma_pagamento,
        reajuste_indice: fcontr.indice_reajuste,
        status: "ativo",
        servicos_json: JSON.stringify(servicosForm),
        empresa_id: empresaFaturadoraId || null,
      }, token);
      await apiFetch("POST", `/api/contratos/contratos/${contRes.id}/gerar-cobrancas`, {}, token).catch(()=>{});
      alert(`Contrato criado com sucesso! ID: ${contRes.id}`);
      setTela("lista");
    } catch(e) { alert("Erro: "+e.message); }
    setSalvandoContr(false);
  };

  // ─── GERAÇÃO DE PDF ────────────────────────────────────────────────────────
  const gerarPDF = async () => {
    await salvar();
    // Busca dados atualizados da empresa emissora antes de gerar
    let emp = configEmpresa;
    try {
      const fresh = await apiFetch("GET", "/api/propostas/config-empresa", undefined, token);
      if (fresh && fresh.razao_social) { emp = fresh; setConfigEmpresa(fresh); }
    } catch(e) {}
    if (!emp.razao_social) {
      alert("Configure a empresa emissora antes de gerar o PDF.\nVá em: ⚙️ Config Empresa (botão no topo da lista de propostas).");
      return;
    }
    const logoHtml = emp.logo_base64
      ? `<img src="${emp.logo_base64}" style="max-height:80px;max-width:240px;object-fit:contain;display:block"/>`
      : `<div style="font-size:20pt;font-weight:900;color:#1e3a5f;letter-spacing:-0.5px">${emp.razao_social}</div>`;

    const valorExtenso = (v) => {
      // Simplified extenso for the contract
      return `${fmt(v).replace('R$ ','')}`;
    };

    const linhasServico = servicos.map(s => {
      const c = calcularServico(s);
      return `<tr>
        <td>${s.cargo}</td>
        <td style="text-align:center">${s.qtd}</td>
        <td>${s.turno||'—'}</td>
        <td style="text-align:right">${fmt(c.valorUnitario)}</td>
        <td style="text-align:right;font-weight:700">${fmt(c.valorTotal)}</td>
      </tr>`;
    }).join('');

    const clausulasHtml = clausulas.map(c => {
      let texto = c.texto
        .replace(/{objeto}/g, dados.objeto||'—')
        .replace(/{local_servico}/g, dados.local_servico||'—')
        .replace(/{vigencia_inicio}/g, dados.vigencia_inicio ? new Date(dados.vigencia_inicio+'T12:00').toLocaleDateString("pt-BR") : '—')
        .replace(/{vigencia_fim}/g, dados.vigencia_fim ? new Date(dados.vigencia_fim+'T12:00').toLocaleDateString("pt-BR") : '—')
        .replace(/{prazo_renovacao_meses}/g, dados.prazo_renovacao_meses)
        .replace(/{dia_vencimento}/g, dados.dia_vencimento)
        .replace(/{forma_pagamento}/g, dados.forma_pagamento||'—')
        .replace(/{indice_reajuste}/g, dados.indice_reajuste||'INPC')
        .replace(/{periodicidade_reajuste}/g, dados.periodicidade_reajuste)
        .replace(/{periodicidade_reajuste_extenso}/g, dados.periodicidade_reajuste)
        .replace(/{valor_total}/g, fmt(totalGeral))
        .replace(/{valor_total_extenso}/g, valorExtenso(totalGeral))
        .replace(/{cidade_contratada}/g, emp.cidade||dados.cliente_cidade||'—');
      return `<div class="clausula"><div class="clausula-titulo">${c.titulo}</div><div class="clausula-texto">${texto.replace(/\n/g,'<br/>')}</div></div>`;
    }).join('');

    const html = `<!DOCTYPE html><html lang="pt-BR"><head>
<meta charset="UTF-8">
<title>Proposta / Contrato ${dados.numero}</title>
<style>
  * { margin:0; padding:0; box-sizing:border-box; }
  body { font-family: 'Arial', sans-serif; font-size: 11pt; color: #1a1a1a; line-height: 1.55; }
  .page { max-width: 210mm; margin: 0 auto; padding: 18mm 20mm; }
  .header { display:flex; justify-content:space-between; align-items:flex-start; border-bottom:3px solid #1e3a5f; padding-bottom:14px; margin-bottom:20px; }
  .empresa-info { font-size:10pt; color:#555; line-height:1.4; }
  .empresa-info strong { color:#1e3a5f; font-size:12pt; display:block; margin-bottom:3px; }
  .titulo-doc { text-align:center; margin:24px 0 20px; }
  .titulo-doc h1 { font-size:18pt; color:#1e3a5f; text-transform:uppercase; letter-spacing:2px; }
  .titulo-doc .numero { font-size:11pt; color:#666; margin-top:4px; }
  .secao { margin-bottom:20px; }
  .secao-titulo { font-size:12pt; font-weight:700; color:#1e3a5f; border-bottom:1px solid #1e3a5f33; padding-bottom:5px; margin-bottom:10px; text-transform:uppercase; letter-spacing:0.5px; }
  .grid-2 { display:grid; grid-template-columns:1fr 1fr; gap:8px 24px; font-size:10.5pt; }
  .campo { margin-bottom:5px; }
  .campo label { font-size:9pt; color:#666; display:block; }
  .campo span { font-weight:600; }
  table.servicos { width:100%; border-collapse:collapse; font-size:10.5pt; margin-top:8px; }
  table.servicos th { background:#1e3a5f; color:#fff; padding:7px 10px; text-align:left; font-size:9.5pt; }
  table.servicos td { padding:6px 10px; border-bottom:1px solid #e5e7eb; }
  table.servicos tr:nth-child(even) td { background:#f8fafc; }
  .total-row td { font-weight:800; font-size:12pt; background:#f0f4ff!important; border-top:2px solid #1e3a5f; color:#1e3a5f; }
  .clausula { margin-bottom:16px; page-break-inside:avoid; }
  .clausula-titulo { font-weight:700; font-size:10.5pt; color:#1e3a5f; margin-bottom:5px; }
  .clausula-texto { font-size:10pt; text-align:justify; line-height:1.65; }
  .assinaturas { margin-top:50px; display:grid; grid-template-columns:1fr 1fr; gap:40px; page-break-inside:avoid; }
  .ass-bloco { border-top:1px solid #333; padding-top:8px; text-align:center; font-size:9.5pt; }
  .ass-bloco strong { display:block; font-size:10pt; }
  .rodape { margin-top:30px; text-align:center; font-size:8.5pt; color:#999; border-top:1px solid #e5e7eb; padding-top:8px; }
  .badge-status { display:inline-block; padding:3px 12px; border-radius:20px; font-size:9pt; font-weight:700; background:#1e3a5f22; color:#1e3a5f; }
  @page { size: A4 portrait; margin: 15mm 18mm; }
  @media print { body { -webkit-print-color-adjust:exact; print-color-adjust:exact; } .page { padding:0; max-width:100%; } }
</style></head><body>
<div class="page">
  <div class="header">
    <div>${logoHtml}<div class="empresa-info" style="margin-top:6px">
      <strong>${emp.razao_social||''}</strong>
      ${emp.cnpj?`CNPJ: ${emp.cnpj}`:''}${emp.endereco?` | ${emp.endereco}`:''}${emp.cidade?` — ${emp.cidade}/${emp.uf||''}`:''}<br/>
      ${emp.telefone?`Tel: ${emp.telefone}`:''}${emp.email?` | ${emp.email}`:''}${emp.site?` | ${emp.site}`:''}
    </div></div>
    <div style="text-align:right;font-size:9pt;color:#666">
      <span class="badge-status">${STATUS_LABEL[dados.status]||dados.status}</span><br/>
      <span style="font-size:9.5pt;margin-top:4px;display:block">Proposta Nº <strong>${dados.numero||'—'}</strong></span>
      <span>Data: ${dados.data_proposta ? new Date(dados.data_proposta+'T12:00').toLocaleDateString("pt-BR") : '—'}</span>
    </div>
  </div>

  <div class="titulo-doc">
    <h1>Proposta de Prestação de Serviços</h1>
    <div class="numero">Contrato de Terceirização de Serviços</div>
  </div>

  <div class="secao">
    <div class="secao-titulo">Partes Contratantes</div>
    <div class="grid-2">
      <div>
        <div style="font-weight:700;margin-bottom:6px;color:#1e3a5f">CONTRATADA (Prestadora de Serviços)</div>
        <div class="campo"><label>Razão Social</label><span>${emp.razao_social||'—'}</span></div>
        <div class="campo"><label>CNPJ</label><span>${emp.cnpj||'—'}</span></div>
        <div class="campo"><label>Endereço</label><span>${emp.endereco||'—'}${emp.cidade?`, ${emp.cidade}/${emp.uf||''}`:''}</span></div>
        <div class="campo"><label>Representante Legal</label><span>${emp.responsavel_nome||'—'} — ${emp.responsavel_cargo||'—'}</span></div>
      </div>
      <div>
        <div style="font-weight:700;margin-bottom:6px;color:#1e3a5f">CONTRATANTE (Cliente)</div>
        <div class="campo"><label>Razão Social</label><span>${dados.cliente_nome||'—'}</span></div>
        <div class="campo"><label>CNPJ</label><span>${dados.cliente_cnpj||'—'}</span></div>
        <div class="campo"><label>Endereço</label><span>${dados.cliente_endereco||'—'}${dados.cliente_cidade?`, ${dados.cliente_cidade}/${dados.cliente_uf||''}`:''}</span></div>
        <div class="campo"><label>Representante Legal</label><span>${dados.cliente_representante||'—'}</span></div>
      </div>
    </div>
  </div>

  <div class="secao">
    <div class="secao-titulo">Informações do Contrato</div>
    <div class="grid-2">
      <div class="campo"><label>Local de Prestação dos Serviços</label><span>${dados.local_servico||'—'}</span></div>
      <div class="campo"><label>Serviços Prestados</label><span>${dados.objeto||'—'}</span></div>
      <div class="campo"><label>Vigência</label><span>${dados.vigencia_inicio?new Date(dados.vigencia_inicio+'T12:00').toLocaleDateString("pt-BR"):'—'} a ${dados.vigencia_fim?new Date(dados.vigencia_fim+'T12:00').toLocaleDateString("pt-BR"):'—'}</span></div>
      <div class="campo"><label>Renovação Automática</label><span>A cada ${dados.prazo_renovacao_meses} meses</span></div>
      <div class="campo"><label>Vencimento da Fatura</label><span>Todo dia ${dados.dia_vencimento} do mês</span></div>
      <div class="campo"><label>Forma de Pagamento</label><span>${dados.forma_pagamento||'—'}</span></div>
      <div class="campo"><label>Reajuste Anual</label><span>Índice ${dados.indice_reajuste} — a cada ${dados.periodicidade_reajuste} meses</span></div>
    </div>
  </div>

  <div class="secao">
    <div class="secao-titulo">Quadro de Mão de Obra e Valores</div>
    <table class="servicos">
      <thead><tr><th>Cargo / Função</th><th style="text-align:center">Qtd.</th><th>Turno</th><th style="text-align:right">Valor Unitário/Mês</th><th style="text-align:right">Subtotal/Mês</th></tr></thead>
      <tbody>
        ${linhasServico}
        <tr class="total-row">
          <td colspan="4" style="text-align:right">VALOR TOTAL MENSAL DO CONTRATO</td>
          <td style="text-align:right">${fmt(totalGeral)}</td>
        </tr>
      </tbody>
    </table>
  </div>

  <div class="secao" style="page-break-before:always">
    <div class="secao-titulo">Cláusulas Contratuais</div>
    ${clausulasHtml}
  </div>

  <div class="assinaturas">
    <div class="ass-bloco">
      <strong>${emp.razao_social||'CONTRATADA'}</strong>
      ${emp.responsavel_nome||'Representante Legal'} — ${emp.responsavel_cargo||''}<br/>
      Data: ___/___/______
    </div>
    <div class="ass-bloco">
      <strong>${dados.cliente_nome||'CONTRATANTE'}</strong>
      ${dados.cliente_representante||'Representante Legal'}<br/>
      Data: ___/___/______
    </div>
  </div>
  <div class="assinaturas" style="margin-top:30px">
    <div class="ass-bloco">1ª Testemunha<br/>Nome: ________________________<br/>CPF: ___.___.___-__</div>
    <div class="ass-bloco">2ª Testemunha<br/>Nome: ________________________<br/>CPF: ___.___.___-__</div>
  </div>

  ${dados.observacoes ? `<div class="secao" style="margin-top:24px"><div class="secao-titulo">Observações</div><div style="font-size:10pt">${dados.observacoes}</div></div>` : ''}

  <div class="rodape">
    Documento gerado em ${new Date().toLocaleDateString("pt-BR")} — Sistema RH Facilitae |
    ${emp.razao_social||''} ${emp.cnpj?`| CNPJ ${emp.cnpj}`:''}
  </div>
</div>
<script>window.onload=function(){window.print();}</script>
</body></html>`;

    const win = window.open("","_blank");
    win.document.write(html);
    win.document.close();
  };

  // ─── CALCULADORA DE PREÇOS (por serviço) ──────────────────────────────────
  const renderCalculo = () => {
    const svcs = servicosForm.length > 0 ? servicosForm : servicos;
    const setFn = servicosForm.length > 0 ? setServicosForm : setServicos;
    if (servicosForm.length === 0 && servicos.every(s => !s.cargo && !s.descricao)) {
      return (
        <div style={{ padding:"24px", textAlign:"center", color:COLORS.textMid, fontSize:13, background:COLORS.bg, borderRadius:10, border:`1px dashed ${COLORS.border}` }}>
          Selecione ao menos um serviço em "Serviços Prestados" para calcular custos.
        </div>
      );
    }
    return (
    <div style={{ display:"flex", flexDirection:"column", gap:20 }}>
      {svcs.map((s, idx) => {
        const c = calcularServico(s);
        const upd = (field, val) => setFn(prev => prev.map((sv,i) => i===idx ? {...sv,[field]:val} : sv));
        const expandido = !!calcsExpandidos[idx];
        const toggle = () => setCalcsExpandidos(prev => ({...prev,[idx]:!prev[idx]}));
        return (
          <Card key={idx} style={{ border:`2px solid ${COLORS.primary}22` }}>
            {/* Cabeçalho sempre visível — clicável para expandir */}
            <div onClick={toggle} style={{ display:"flex", justifyContent:"space-between", alignItems:"center", cursor:"pointer", marginBottom: expandido ? 14 : 0 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontSize:16, transition:"transform .2s", display:"inline-block", transform: expandido ? "rotate(90deg)" : "rotate(0deg)" }}>▶</span>
                <span style={{ fontWeight:800, fontSize:14 }}>
                  {s.descricao || s.cargo || `Serviço ${idx+1}`} — {s.qtd_postos || s.qtd || 1} posto(s)
                </span>
                {!expandido && <span style={{ fontSize:12, color:COLORS.textMid }}>(clique para calcular custos)</span>}
              </div>
              <div style={{ display:"flex", gap:6, alignItems:"center" }} onClick={e=>e.stopPropagation()}>
                <span style={{ fontWeight:900, fontSize:16, color: c.valorTotal>0?"#16a34a":COLORS.textMid }}>{c.valorTotal>0?fmt(c.valorTotal):"—"}/mês</span>
                <Btn size="sm" variant="ghost" title="Duplicar esta função"
                  onClick={() => setFn(prev => { const copia = {...prev[idx]}; const nova = [...prev]; nova.splice(idx+1, 0, copia); return nova; })}>
                  📋
                </Btn>
                {svcs.length > 1 && (
                  <Btn size="sm" variant="danger" title="Remover"
                    onClick={() => setFn(prev => prev.filter((_,i) => i !== idx))}>
                    ✕
                  </Btn>
                )}
              </div>
            </div>
            {expandido && <>
              <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10, marginBottom:14 }}>
                <Input label="Salário Base (CCT) R$" type="number" value={s.salario_base} onChange={e=>upd('salario_base',e.target.value)} placeholder="Ex: 1600.00" />
                <Input label="Encargos Sociais %" type="number" value={s.encargos_pct} onChange={e=>upd('encargos_pct',e.target.value)} placeholder="Ex: 34.00" />
                <Input label="Provisões (13º/Férias) %" type="number" value={s.provisoes_pct} onChange={e=>upd('provisoes_pct',e.target.value)} placeholder="Ex: 22.77" />
                <Input label="Vale Transporte R$/mês" type="number" value={s.vt_mensal} onChange={e=>upd('vt_mensal',e.target.value)} />
                <Input label="Vale Alimentação R$/mês" type="number" value={s.va_mensal} onChange={e=>upd('va_mensal',e.target.value)} />
                <Input label="Plano de Saúde R$/mês" type="number" value={s.plano_saude} onChange={e=>upd('plano_saude',e.target.value)} />
                <Input label="Outros Benefícios R$/mês" type="number" value={s.outros_beneficios} onChange={e=>upd('outros_beneficios',e.target.value)} />
                <Input label="Uniforme/EPI R$/mês" type="number" value={s.uniforme_epi} onChange={e=>upd('uniforme_epi',e.target.value)} />
                <Input label="Materiais/Insumos R$/mês" type="number" value={s.materiais} onChange={e=>upd('materiais',e.target.value)} />
                <Input label="Overhead Admin. %" type="number" value={s.overhead_pct} onChange={e=>upd('overhead_pct',e.target.value)} placeholder="Ex: 10" />
                <Input label="Margem de Lucro %" type="number" value={s.margem_pct} onChange={e=>upd('margem_pct',e.target.value)} placeholder="Ex: 15" />
                <Input label="Impostos (ISS+PIS+COFINS) %" type="number" value={s.impostos_pct} onChange={e=>upd('impostos_pct',e.target.value)} placeholder="Ex: 8.65" />
              </div>
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:12 }}>
                <tbody>
                  {[
                    ["Salário Base (CCT)", fmt(c.sal), ""],
                    [`Encargos Sociais (INSS+FGTS+RAT+Sist.S) ${s.encargos_pct||0}%`, fmt(c.encargos), COLORS.warning],
                    [`Provisões (13º, Férias, Aviso Prévio) ${s.provisoes_pct||0}%`, fmt(c.provisoes), COLORS.warning],
                    ["Benefícios (VT + VA + Saúde + Outros)", fmt(c.beneficios), COLORS.info],
                    ["Materiais, Uniformes/EPI", fmt(c.operacional), COLORS.info],
                    ["Subtotal Custo Direto", fmt(c.subtotal), "", true],
                    [`Overhead Administrativo ${s.overhead_pct||0}%`, fmt(c.overhead), COLORS.textMid],
                    ["Custo Total", fmt(c.custoTotal), "", true],
                    [`Margem de Lucro ${s.margem_pct||0}%`, fmt(c.margem), "#16a34a"],
                    [`Impostos ${s.impostos_pct||0}% (sobre preço de venda)`, fmt(c.impostos), COLORS.danger],
                    [`Preço por Funcionário/Mês`, fmt(c.valorUnitario), COLORS.primary, true],
                    [`× ${s.qtd_postos || s.qtd || 1} colaborador(es) = TOTAL MENSAL`, fmt(c.valorTotal), COLORS.primary, true, true],
                  ].map(([desc, val, color, bold, big]) => (
                    <tr key={desc} style={{ borderBottom:`1px solid ${COLORS.border}`, background: big ? COLORS.primaryBg : "transparent" }}>
                      <td style={{ padding:"5px 8px", color: color||COLORS.text, fontWeight: bold?700:400, fontSize: big?13:12 }}>{desc}</td>
                      <td style={{ padding:"5px 8px", textAlign:"right", color: color||COLORS.text, fontWeight: bold?700:400, fontSize: big?13:12, whiteSpace:"nowrap" }}>{val}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </>}
          </Card>
        );
      })}
      <Card style={{ background:COLORS.primaryBg, border:`2px solid ${COLORS.primary}` }}>
        <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
          <span style={{ fontWeight:800, fontSize:16 }}>VALOR TOTAL DO CONTRATO</span>
          <span style={{ fontWeight:900, fontSize:22, color:COLORS.primary }}>{fmt(totalGeral)}/mês</span>
        </div>
        <div style={{ fontSize:12, color:COLORS.textMid, marginTop:4 }}>
          {svcs.length} tipo(s) de serviço | {svcs.reduce((s,sv)=>s+parseInt(sv.qtd_postos||sv.qtd||1),0)} colaborador(es) total
        </div>
      </Card>
    </div>
    );
  };

  // ─── LISTA DE PROPOSTAS ────────────────────────────────────────────────────
  if (tela === "lista") {
    const thInp = { width:"100%", padding:"4px 6px", borderRadius:6, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:11, color:COLORS.text, boxSizing:"border-box" };
    const propostasFiltradas = propostas.filter(p => {
      if (filtro && !p.cliente_nome?.toLowerCase().includes(filtro.toLowerCase()) && !p.numero?.includes(filtro) && !p.objeto?.toLowerCase().includes(filtro.toLowerCase())) return false;
      if (filtroStatus && p.status !== filtroStatus) return false;
      return true;
    });
    return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {confirmOpts && <ConfirmModal msg={confirmOpts.msg} onOk={confirmOpts.onOk} onClose={() => setConfirmOpts(null)} />}
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <Btn onClick={()=>novoEditor("proposta")}>+ Nova Proposta</Btn>
        {(filtro||filtroStatus) && (
          <Btn size="sm" variant="ghost" onClick={()=>{ setFiltro(""); setFiltroStatus(""); }}>✕ Limpar filtros</Btn>
        )}
        <div style={{ marginLeft:"auto", display:"flex", gap:10 }}>
          {["rascunho","enviada","convertida"].map(st => (
            <span key={st} style={{ fontSize:11, color:STATUS_COLOR[st], fontWeight:700, cursor:"pointer" }} onClick={()=>setFiltroStatus(filtroStatus===st?"":st)}>
              {propostas.filter(p=>p.status===st).length} {STATUS_LABEL[st]}
            </span>
          ))}
        </div>
      </div>

      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:10 }}>
        {[
          { label:"Propostas Enviadas", val: propostas.filter(p=>p.status==="enviada").length, color:COLORS.primary },
          { label:"Valor Total (enviadas)", val: fmt(propostas.filter(p=>p.status==="enviada").reduce((s,p)=>s+parseFloat(p.valor_total||0),0)), color:"#16a34a" },
          { label:"Rascunhos", val: propostas.filter(p=>p.status==="rascunho").length, color:COLORS.textMid },
          { label:"Convertidas em Contrato", val: propostas.filter(p=>p.status==="convertida").length, color:COLORS.accent },
        ].map(card=>(
          <Card key={card.label} style={{ borderTop:`4px solid ${card.color}`, cursor:"pointer" }} onClick={()=>{}}>
            <div style={{ fontSize:11,color:COLORS.textMid,fontWeight:600 }}>{card.label}</div>
            <div style={{ fontSize:20,fontWeight:900,color:card.color }}>{card.val}</div>
          </Card>
        ))}
      </div>

      <Card style={{ padding:0, overflow:"hidden" }}>
        <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
          <thead>
            <tr style={{ background:COLORS.bg }}>
              <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top", minWidth:90 }}>
                <div style={{ fontWeight:600, fontSize:11, color:COLORS.textMid, marginBottom:4 }}>Nº / Cliente</div>
                <input value={filtro} onChange={e=>setFiltro(e.target.value)} placeholder="Buscar..." style={thInp} />
              </th>
              <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top", minWidth:100 }}>
                <div style={{ fontWeight:600, fontSize:11, color:COLORS.textMid, marginBottom:4 }}>Status</div>
                <select value={filtroStatus} onChange={e=>setFiltroStatus(e.target.value)} style={thInp}>
                  <option value="">Todos</option>
                  <option value="rascunho">Rascunho</option>
                  <option value="enviada">Enviada</option>
                  <option value="convertida">Convertida</option>
                </select>
              </th>
              <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                <div style={{ fontWeight:600, fontSize:11, color:COLORS.textMid }}>Objeto</div>
              </th>
              <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                <div style={{ fontWeight:600, fontSize:11, color:COLORS.textMid }}>Vigência</div>
              </th>
              <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                <div style={{ fontWeight:600, fontSize:11, color:COLORS.textMid }}>Valor</div>
              </th>
              <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                <div style={{ fontWeight:600, fontSize:11, color:COLORS.textMid }}>Criado em</div>
              </th>
              <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                <div style={{ fontWeight:600, fontSize:11, color:COLORS.textMid }}>Ações</div>
              </th>
            </tr>
          </thead>
          <tbody>
            {propostasFiltradas.map((p,i)=>(
              <tr key={p.id} style={{ background:i%2===0?COLORS.surface:COLORS.bg, borderBottom:`1px solid ${COLORS.border}` }}>
                <td style={{ padding:"10px 14px" }}>
                  <div style={{ fontWeight:700 }}>{p.numero||`#${p.id}`}</div>
                  <div style={{ fontSize:12, color:COLORS.textMid }}>{p.cliente_nome}</div>
                </td>
                <td style={{ padding:"10px 14px" }}>
                  <span style={{ background:STATUS_COLOR[p.status]+"22",color:STATUS_COLOR[p.status],padding:"3px 10px",borderRadius:20,fontWeight:700,fontSize:11 }}>{STATUS_LABEL[p.status]}</span>
                </td>
                <td style={{ padding:"10px 14px",color:COLORS.textMid,maxWidth:160,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{p.objeto||"—"}</td>
                <td style={{ padding:"10px 14px",fontSize:12,color:COLORS.textMid }}>
                  {p.vigencia_inicio?new Date(p.vigencia_inicio+'T12:00').toLocaleDateString("pt-BR"):"—"}
                  {p.vigencia_fim&&<><br/>{`→ ${new Date(p.vigencia_fim+'T12:00').toLocaleDateString("pt-BR")}`}</>}
                </td>
                <td style={{ padding:"10px 14px",fontWeight:700,color:"#16a34a" }}>{fmt(p.valor_total)}</td>
                <td style={{ padding:"10px 14px",fontSize:11,color:COLORS.textMid }}>{new Date(p.criado_em).toLocaleDateString("pt-BR")}</td>
                <td style={{ padding:"8px 14px" }}>
                  <div style={{ display:"flex", gap:5, flexWrap:"wrap" }}>
                    <Btn size="sm" onClick={()=>abrirEditar(p)}>Ver / Editar</Btn>
                    {p.status !== "convertida" && <Btn size="sm" style={{ background:"#16a34a",color:"#fff" }} onClick={async ()=>{ await abrirEditar(p); setModalConverter(true); }}>Converter</Btn>}
                    <Btn size="sm" variant="danger" onClick={()=>apagar(p.id)}>Excluir</Btn>
                  </div>
                </td>
              </tr>
            ))}
            {!propostasFiltradas.length&&<tr><td colSpan={7} style={{ padding:"40px",textAlign:"center",color:COLORS.textMid }}>{propostas.length ? "Nenhuma proposta encontrada com os filtros aplicados." : "Nenhuma proposta cadastrada"}</td></tr>}
          </tbody>
        </table>
      </Card>

      {/* Modal Converter em Contrato */}
      {modalConverter && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.55)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center" }}>
          <div style={{ background:COLORS.surface,borderRadius:16,padding:28,width:"min(520px,96vw)",boxShadow:"0 8px 40px rgba(0,0,0,0.3)",display:"flex",flexDirection:"column",gap:16 }}>
            {resultConverter ? (
              <>
                <div style={{ textAlign:"center" }}>
                  <div style={{ fontSize:40,marginBottom:8 }}>✅</div>
                  <div style={{ fontWeight:800,fontSize:18,color:"#16a34a" }}>Proposta Convertida!</div>
                </div>
                <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                  {[
                    { icon:"👥", label:"Cliente criado/vinculado", val:`ID #${resultConverter.cliente_id}` },
                    { icon:"📋", label:"Contrato gerado", val:`ID #${resultConverter.contrato_id}` },
                    { icon:"💰", label:"Cobranças mensais geradas", val:`${resultConverter.cobrancas} cobranças` },
                  ].map(item=>(
                    <div key={item.label} style={{ display:"flex",gap:12,alignItems:"center",padding:"10px 14px",background:COLORS.bg,borderRadius:8 }}>
                      <span style={{ fontSize:20 }}>{item.icon}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:12,color:COLORS.textMid }}>{item.label}</div>
                        <div style={{ fontWeight:700,color:"#16a34a" }}>{item.val}</div>
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
                  <Btn variant="ghost" onClick={()=>{ setModalConverter(false); setResultConverter(null); setTela("lista"); }}>Fechar</Btn>
                </div>
              </>
            ) : (
              <>
                <div style={{ fontWeight:800,fontSize:17 }}>Converter Proposta em Contrato</div>
                <div style={{ fontSize:13,color:COLORS.textMid,lineHeight:1.6 }}>
                  Ao confirmar, serão criados automaticamente:
                </div>
                <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                  {[
                    { icon:"👥", label:"Cliente", val: dados.cliente_nome || "—", sub: dados.cliente_cnpj||"" },
                    { icon:"📧", label:"E-mail do cliente (para boleto/NFS-e)", val: dados.email_cliente||"⚠️ Não informado — preencha na aba Dados", warn: !dados.email_cliente },
                    { icon:"📋", label:"Contrato", val: `Nº ${dados.numero||"—"} — ${dados.objeto||"—"}`, sub: dados.vigencia_inicio ? `Vigência: ${new Date(dados.vigencia_inicio+'T12:00').toLocaleDateString("pt-BR")} → ${dados.vigencia_fim?new Date(dados.vigencia_fim+'T12:00').toLocaleDateString("pt-BR"):"indeterminado"}` : "Sem data de início" },
                    { icon:"💰", label:"Cobranças mensais de", val: fmt(totalGeral||parseFloat(dados.valor_total)||0), sub: `Vencimento todo dia ${dados.dia_vencimento||10}` },
                  ].map(item=>(
                    <div key={item.label} style={{ display:"flex",gap:12,alignItems:"center",padding:"10px 14px",background:item.warn?`${COLORS.warning}15`:COLORS.bg,borderRadius:8,border:item.warn?`1px solid ${COLORS.warning}`:""  }}>
                      <span style={{ fontSize:20 }}>{item.icon}</span>
                      <div style={{ flex:1 }}>
                        <div style={{ fontSize:11,color:COLORS.textMid,fontWeight:600 }}>{item.label}</div>
                        <div style={{ fontWeight:700,color:item.warn?COLORS.warning:COLORS.text,fontSize:13 }}>{item.val}</div>
                        {item.sub&&<div style={{ fontSize:11,color:COLORS.textLight }}>{item.sub}</div>}
                      </div>
                    </div>
                  ))}
                </div>
                <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
                  <Btn variant="ghost" onClick={()=>setModalConverter(false)}>Cancelar</Btn>
                  <Btn style={{ background:"#16a34a",color:"#fff" }} disabled={convertendo} onClick={async ()=>{
                    setConvertendo(true);
                    try {
                      if (!editandoId) { alert("Salve a proposta antes de converter."); setConvertendo(false); return; }
                      await salvar();
                      const r = await apiFetch("POST", `/api/propostas/${editandoId}/converter`, {}, token);
                      setResultConverter(r);
                      setDados(d=>({...d,status:"convertida"}));
                      carregar();
                    } catch(e) { alert("Erro: "+e.message); }
                    setConvertendo(false);
                  }}>{convertendo?"Convertendo...":"✅ Confirmar Conversão"}</Btn>
                </div>
              </>
            )}
          </div>
        </div>
      )}

      {/* Modal Config Empresa */}
      {modalConfigEmp && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,0.45)",zIndex:500,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={()=>setModalConfigEmp(false)}>
          <div onClick={e=>e.stopPropagation()} style={{ background:COLORS.surface,borderRadius:16,padding:28,width:"min(440px,96vw)",boxShadow:"0 8px 40px rgba(0,0,0,0.22)",display:"flex",flexDirection:"column",gap:16 }}>
            <div style={{ fontWeight:800,fontSize:17 }}>🏢 Empresa Emissora das Propostas</div>
            <div style={{ fontSize:13,color:COLORS.textMid }}>
              Selecione qual empresa cadastrada aparece no cabeçalho do PDF das propostas. Os dados são puxados automaticamente do cadastro.
            </div>
            {empresasCad.length === 0 ? (
              <div style={{ padding:"16px",background:COLORS.bg,borderRadius:8,fontSize:13,color:COLORS.textMid,textAlign:"center" }}>
                Nenhuma empresa cadastrada. Vá em <strong>⚙️ Configurações → 🏢 Empresas</strong> para cadastrar.
              </div>
            ) : (
              <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
                {empresasCad.map(em => {
                  const selecionada = String(formConfigEmp._empresa_id) === String(em.id);
                  return (
                    <button key={em.id} onClick={() => setFormConfigEmp({ _empresa_id: em.id, razao_social: em.razao_social||em.nome||"", cnpj: em.cnpj||"", endereco: em.endereco||"", numero: em.numero||"", complemento: em.complemento||"", cidade: em.cidade||"", uf: em.uf||"", cep: em.cep||"", telefone: em.telefone||"", email: em.email||"", site: em.site||"", responsavel_nome: em.responsavel_nome||"", responsavel_cargo: em.responsavel_cargo||"", logo_base64: em.logo_base64||null})}
                      style={{ display:"flex",alignItems:"center",gap:14,padding:"12px 16px",borderRadius:10,border:`2px solid ${selecionada?COLORS.primary:COLORS.border}`,background:selecionada?COLORS.primaryBg:COLORS.surface,cursor:"pointer",textAlign:"left",transition:"all .15s" }}>
                      {em.logo_base64
                        ? <img src={em.logo_base64} alt="logo" style={{ width:48,height:48,objectFit:"contain",borderRadius:6,flexShrink:0 }} />
                        : <div style={{ width:48,height:48,borderRadius:6,background:COLORS.bg,display:"flex",alignItems:"center",justifyContent:"center",fontSize:20,flexShrink:0 }}>🏢</div>
                      }
                      <div>
                        <div style={{ fontWeight:700,fontSize:14,color:selecionada?COLORS.primary:COLORS.text }}>{em.nome}</div>
                        <div style={{ fontSize:12,color:COLORS.textMid }}>{em.cnpj||""}{em.cidade?` — ${em.cidade}${em.uf?`/${em.uf}`:""}`:""}</div>
                        {em.responsavel_nome && <div style={{ fontSize:11,color:COLORS.textLight }}>{em.responsavel_nome}{em.responsavel_cargo?` · ${em.responsavel_cargo}`:""}</div>}
                      </div>
                      {selecionada && <span style={{ marginLeft:"auto",color:COLORS.primary,fontWeight:900,fontSize:18 }}>✓</span>}
                    </button>
                  );
                })}
              </div>
            )}
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end" }}>
              <Btn variant="ghost" onClick={()=>setModalConfigEmp(false)}>Cancelar</Btn>
              <Btn onClick={salvarConfigEmp} disabled={salvandoConfigEmp||!formConfigEmp._empresa_id}>{salvandoConfigEmp?"Salvando...":"Confirmar"}</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
  } // end if (tela === "lista")

  // ─── EDITOR DE PROPOSTA / CONTRATO ────────────────────────────────────────
  const selStyle = { width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,color:COLORS.text,boxSizing:"border-box" };
  const secHdr = (t) => <div style={{ fontWeight:800,fontSize:14,color:COLORS.primary,borderBottom:`2px solid ${COLORS.primaryBg}`,paddingBottom:6,marginBottom:12,textTransform:"uppercase",letterSpacing:"0.06em" }}>{t}</div>;

  const renderEmpresaFaturadora = () => {
    const fixada = !!empresaFiltroId; // empresa determinada pela topbar
    const emSel = empresasCad.find(x => String(x.id) === String(empresaFaturadoraId));
    const aplicarEmpresa = (id) => {
      setEmpresaFaturadoraId(id);
      const em = empresasCad.find(x => String(x.id) === String(id));
      if (em) {
        const cfg = { _empresa_id: em.id, razao_social: em.razao_social||em.nome||"", cnpj: em.cnpj||"", endereco: em.endereco||"", numero: em.numero||"", complemento: em.complemento||"", cidade: em.cidade||"", uf: em.uf||"", cep: em.cep||"", telefone: em.telefone||"", email: em.email||"", site: em.site||"", responsavel_nome: em.responsavel_nome||"", responsavel_cargo: em.responsavel_cargo||"", logo_base64: em.logo_base64||null };
        setConfigEmpresa(cfg);
      }
    };
    return (
      <Card>
        {secHdr("🏢 Empresa Faturadora")}
        {fixada ? (
          <div style={{ fontSize:12, color:COLORS.textMid, marginBottom:8 }}>
            Definida pelo filtro da barra superior.
          </div>
        ) : (
          <div style={{ marginBottom: emSel ? 10 : 0 }}>
            <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Selecione a empresa que irá faturar</label>
            <select value={empresaFaturadoraId} onChange={e => aplicarEmpresa(e.target.value)} style={selStyle}>
              <option value="">— Selecione a empresa faturadora —</option>
              {empresasCad.map(em => (
                <option key={em.id} value={em.id}>{em.razao_social || em.nome}</option>
              ))}
            </select>
            {!empresaFaturadoraId && <div style={{ fontSize:11,color:COLORS.danger,marginTop:4 }}>⚠️ Selecione a empresa faturadora antes de salvar.</div>}
          </div>
        )}
        {emSel && (
          <div style={{ display:"flex",alignItems:"center",gap:12,padding:"10px 14px",background:COLORS.bg,borderRadius:8 }}>
            {emSel.logo_base64 && <img src={emSel.logo_base64} alt="logo" style={{ height:36,objectFit:"contain",borderRadius:4 }} />}
            <div>
              <div style={{ fontWeight:700,fontSize:13 }}>{emSel.razao_social||emSel.nome}</div>
              {emSel.cnpj && <div style={{ fontSize:11,color:COLORS.textMid }}>CNPJ: {emSel.cnpj}</div>}
              {emSel.cidade && <div style={{ fontSize:11,color:COLORS.textMid }}>{emSel.cidade}{emSel.uf ? ` / ${emSel.uf}` : ""}</div>}
            </div>
            {fixada && <span style={{ marginLeft:"auto",fontSize:11,color:COLORS.textMid,fontStyle:"italic" }}>via topbar</span>}
          </div>
        )}
      </Card>
    );
  };

  // ── Seção comum: Dados do Cliente ─────────────────────────────────────────
  const renderDadosCli = () => (
    <Card>
      {secHdr("👤 Dados do Cliente (Contratante)")}
      {/* Seleção cliente existente — modo contrato */}
      {modoNovo === "contrato" && clientes?.length > 0 && (
        <div style={{ marginBottom:14 }}>
          <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Selecionar cliente existente</label>
          <input value={buscaCliente} onChange={e=>setBuscaCliente(e.target.value)}
            placeholder="Buscar por nome fantasia ou razão social..."
            style={{ width:"100%",padding:"7px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,color:COLORS.text,boxSizing:"border-box",marginBottom:6 }} />
          <select value={clienteExId} onChange={e=>{ preencherClienteNoCli(e.target.value); setBuscaCliente(""); }} style={selStyle}>
            <option value="">— Preencher manualmente —</option>
            {clientes.filter(c=>{
              if (!buscaCliente) return true;
              const q = buscaCliente.toLowerCase();
              return (c.nome_fantasia||"").toLowerCase().includes(q) || c.nome.toLowerCase().includes(q) || (c.cnpj||"").includes(q);
            }).map(c=><option key={c.id} value={c.id}>{c.nome_fantasia ? `${c.nome_fantasia} — ${c.nome}` : c.nome}</option>)}
          </select>
        </div>
      )}
      {/* Toggle PJ / PF */}
      <div style={{ display:"flex",gap:4,marginBottom:14 }}>
        {[["pj","Pessoa Jurídica (CNPJ)"],["pf","Pessoa Física (CPF)"]].map(([v,l])=>(
          <button key={v} onClick={()=>{setTipoPessoa(v);setDadosCli(d=>({...d,tipo_pessoa:v}));}}
            style={{ padding:"6px 16px",borderRadius:20,border:`2px solid ${tipoPessoa===v?COLORS.primary:COLORS.border}`,
              background:tipoPessoa===v?COLORS.primaryBg:COLORS.surface,fontWeight:tipoPessoa===v?700:400,
              fontSize:12,cursor:"pointer",color:tipoPessoa===v?COLORS.primary:COLORS.text }}>
            {l}
          </button>
        ))}
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10 }}>
        <Input label="Razão Social / Nome *" value={dadosCli.razao_social} onChange={e=>setDadosCli(d=>({...d,razao_social:e.target.value}))} />
        <Input label="Nome Fantasia" value={dadosCli.nome_fantasia} onChange={e=>setDadosCli(d=>({...d,nome_fantasia:e.target.value}))} />
        {tipoPessoa === "pj"
          ? <Input label="CNPJ" value={dadosCli.cnpj} onChange={e=>setDadosCli(d=>({...d,cnpj:e.target.value}))} placeholder="00.000.000/0001-00" />
          : <Input label="CPF" value={dadosCli.cpf} onChange={e=>setDadosCli(d=>({...d,cpf:e.target.value}))} placeholder="000.000.000-00" />
        }
        <Input label="Logradouro" value={dadosCli.logradouro} onChange={e=>setDadosCli(d=>({...d,logradouro:e.target.value}))} />
        <Input label="Número" value={dadosCli.numero} onChange={e=>setDadosCli(d=>({...d,numero:e.target.value}))} />
        <Input label="Complemento" value={dadosCli.complemento} onChange={e=>setDadosCli(d=>({...d,complemento:e.target.value}))} />
        <Input label="Bairro" value={dadosCli.bairro} onChange={e=>setDadosCli(d=>({...d,bairro:e.target.value}))} />
        <Input label="CEP" value={dadosCli.cep} onChange={e=>setDadosCli(d=>({...d,cep:e.target.value}))} placeholder="00000-000" />
        <Input label="Cidade" value={dadosCli.cidade} onChange={e=>setDadosCli(d=>({...d,cidade:e.target.value}))} />
        <Input label="Código IBGE" value={dadosCli.ibge} onChange={e=>setDadosCli(d=>({...d,ibge:e.target.value}))} />
        <Input label="UF" value={dadosCli.uf} onChange={e=>setDadosCli(d=>({...d,uf:e.target.value.slice(0,2).toUpperCase()}))} />
      </div>
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginTop:10 }}>
        <div style={{ gridColumn:"span 3",fontWeight:700,fontSize:12,color:COLORS.textMid,paddingTop:6 }}>Contato Principal</div>
        <Input label="Nome" value={dadosCli.contato_nome} onChange={e=>setDadosCli(d=>({...d,contato_nome:e.target.value}))} />
        <Input label="E-mail" type="email" value={dadosCli.contato_email} onChange={e=>setDadosCli(d=>({...d,contato_email:e.target.value}))} />
        <Input label="Telefone" value={dadosCli.contato_telefone} onChange={e=>setDadosCli(d=>({...d,contato_telefone:e.target.value}))} />
        <div style={{ gridColumn:"span 3",fontWeight:700,fontSize:12,color:COLORS.textMid,paddingTop:6 }}>Contato Financeiro</div>
        <Input label="Nome" value={dadosCli.financeiro_nome} onChange={e=>setDadosCli(d=>({...d,financeiro_nome:e.target.value}))} />
        <Input label="E-mail" type="email" value={dadosCli.financeiro_email} onChange={e=>setDadosCli(d=>({...d,financeiro_email:e.target.value}))} />
        <Input label="Telefone" value={dadosCli.financeiro_telefone} onChange={e=>setDadosCli(d=>({...d,financeiro_telefone:e.target.value}))} />
      </div>
      {tipoPessoa === "pj" && (
        <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginTop:10 }}>
          <div style={{ gridColumn:"span 3",fontWeight:700,fontSize:12,color:COLORS.textMid,paddingTop:6 }}>Dados Fiscais</div>
          <div>
            <Input label="Inscrição Estadual" value={dadosCli.ie_isento?"ISENTO":dadosCli.inscricao_estadual}
              onChange={e=>setDadosCli(d=>({...d,inscricao_estadual:e.target.value}))} disabled={dadosCli.ie_isento} />
            <label style={{ display:"flex",alignItems:"center",gap:4,marginTop:4,fontSize:11,cursor:"pointer" }}>
              <input type="checkbox" checked={!!dadosCli.ie_isento} onChange={e=>setDadosCli(d=>({...d,ie_isento:e.target.checked,inscricao_estadual:e.target.checked?"ISENTO":""}))} />
              Isento de IE
            </label>
          </div>
          <div>
            <Input label="Inscrição Municipal" value={dadosCli.im_isento?"ISENTO":dadosCli.inscricao_municipal}
              onChange={e=>setDadosCli(d=>({...d,inscricao_municipal:e.target.value}))} disabled={dadosCli.im_isento} />
            <label style={{ display:"flex",alignItems:"center",gap:4,marginTop:4,fontSize:11,cursor:"pointer" }}>
              <input type="checkbox" checked={!!dadosCli.im_isento} onChange={e=>setDadosCli(d=>({...d,im_isento:e.target.checked,inscricao_municipal:e.target.checked?"ISENTO":""}))} />
              Isento de IM
            </label>
          </div>
          <div>
            <Input label="Ramo de Atividade" value={dadosCli.ramo_atividade} onChange={e=>setDadosCli(d=>({...d,ramo_atividade:e.target.value}))} />
            <label style={{ display:"flex",alignItems:"center",gap:4,marginTop:8,fontSize:11,cursor:"pointer" }}>
              <input type="checkbox" checked={!!dadosCli.simples_nacional} onChange={e=>setDadosCli(d=>({...d,simples_nacional:e.target.checked}))} />
              Optante pelo Simples Nacional
            </label>
          </div>
        </div>
      )}
    </Card>
  );

  // ── Seção comum: Serviços Prestados ───────────────────────────────────────
  const renderServicos = () => (
    <Card>
      {secHdr("🛠️ Serviços Prestados")}
      {/* Barra de busca */}
      <Input placeholder="Buscar serviço..." value={buscaServico} onChange={e=>setBuscaServico(e.target.value)} style={{ maxWidth:380,marginBottom:14 }} />
      {/* Grade de cards SERVICOS_PRESET */}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(200px,1fr))",gap:10,marginBottom:16 }}>
        {SERVICOS_PRESET
          .filter(p=>!buscaServico||p.descricao.toLowerCase().includes(buscaServico.toLowerCase())||p.cnae.includes(buscaServico))
          .map(preset=>{
            const jaAdicionado = servicosForm.some(s=>s.descricao===preset.descricao);
            return (
              <button key={preset.cnae+preset.descricao}
                onClick={()=>{ if(jaAdicionado) return; setServicosForm(prev=>[...prev,{
                  descricao:preset.descricao, cnae:preset.cnae, qtd_postos:1,
                  salario_base:"", encargos_pct:"", provisoes_pct:"",
                  vt_mensal:"", va_mensal:"", plano_saude:"", outros_beneficios:"",
                  uniforme_epi:"", materiais:"", overhead_pct:"", margem_pct:"", impostos_pct:"",
                }]); }}
                style={{ display:"flex",flexDirection:"column",alignItems:"flex-start",gap:4,padding:"10px 12px",
                  borderRadius:10,border:`2px solid ${jaAdicionado?COLORS.primary:COLORS.border}`,
                  background:jaAdicionado?COLORS.primaryBg:COLORS.surface,cursor:jaAdicionado?"not-allowed":"pointer",
                  textAlign:"left",transition:"all .15s" }}>
                <div style={{ fontWeight:700,fontSize:12,color:jaAdicionado?COLORS.primary:COLORS.text }}>{preset.descricao}</div>
                <div style={{ fontSize:10,color:COLORS.textMid }}>CNAE {preset.cnae}</div>
                {jaAdicionado && <div style={{ fontSize:10,color:COLORS.primary,fontWeight:700 }}>✓ Adicionado</div>}
              </button>
            );
          })
        }
      </div>
      {/* Lista dos serviços adicionados */}
      {servicosForm.length > 0 && (
        <>
          <div style={{ fontWeight:700,fontSize:12,color:COLORS.textMid,marginBottom:8,textTransform:"uppercase",letterSpacing:1 }}>
            Serviços selecionados ({servicosForm.length})
          </div>
          <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
            {servicosForm.map((s,idx)=>(
              <div key={idx} style={{ display:"flex",alignItems:"center",gap:10,padding:"10px 14px",background:COLORS.bg,borderRadius:8,border:`1px solid ${COLORS.border}` }}>
                <div style={{ flex:1 }}>
                  <div style={{ fontWeight:700,fontSize:13 }}>{s.descricao}</div>
                  {s.cnae && <div style={{ fontSize:11,color:COLORS.textMid }}>CNAE {s.cnae}</div>}
                </div>
                <div style={{ display:"flex",alignItems:"center",gap:6 }}>
                  <label style={{ fontSize:11,color:COLORS.textMid }}>Postos:</label>
                  <input type="number" min="1" value={s.qtd_postos||1}
                    onChange={e=>setServicosForm(prev=>prev.map((sv,i)=>i===idx?{...sv,qtd_postos:parseInt(e.target.value)||1}:sv))}
                    style={{ width:60,padding:"4px 8px",borderRadius:6,border:`1px solid ${COLORS.border}`,fontSize:13,textAlign:"center" }} />
                </div>
                <button onClick={()=>setServicosForm(prev=>prev.filter((_,i)=>i!==idx))}
                  style={{ background:"none",border:"none",color:COLORS.danger,cursor:"pointer",fontSize:18,padding:"4px 6px" }}>✕</button>
              </div>
            ))}
          </div>
        </>
      )}
    </Card>
  );

  // ── Seção comum: Vigência e Condições ─────────────────────────────────────
  const renderVigencia = (isCont=false) => (
    <Card>
      {secHdr("📅 Vigência e Condições Financeiras")}
      <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10 }}>
        <Input label="Início da Vigência" type="date" value={isCont?fcontr.data_inicio:dados.vigencia_inicio}
          onChange={e=>isCont?setFcontr(f=>({...f,data_inicio:e.target.value})):setDados(d=>({...d,vigencia_inicio:e.target.value}))} />
        {isCont ? (
          <div>
            <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Fim da Vigência</label>
            {fcontr.sem_fim
              ? <div style={{ padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:"#f3f4f6",fontSize:13,color:COLORS.textMid }}>Indeterminado</div>
              : <input type="date" value={fcontr.data_fim} onChange={e=>setFcontr(f=>({...f,data_fim:e.target.value}))} style={{ ...selStyle }} />}
            <label style={{ display:"flex",alignItems:"center",gap:4,marginTop:4,fontSize:11,cursor:"pointer" }}>
              <input type="checkbox" checked={!!fcontr.sem_fim} onChange={e=>setFcontr(f=>({...f,sem_fim:e.target.checked,data_fim:e.target.checked?"":f.data_fim}))} />
              Prazo indeterminado
            </label>
          </div>
        ) : (
          <div>
            <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Fim da Vigência</label>
            {dados.sem_vigencia_fim
              ? <div style={{ padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:"#f3f4f6",fontSize:13,color:COLORS.textMid }}>Indeterminado</div>
              : <input type="date" value={dados.vigencia_fim} onChange={e=>setDados(d=>({...d,vigencia_fim:e.target.value}))} style={{ ...selStyle }} />}
            <label style={{ display:"flex",alignItems:"center",gap:4,marginTop:4,fontSize:11,cursor:"pointer" }}>
              <input type="checkbox" checked={!!dados.sem_vigencia_fim} onChange={e=>setDados(d=>({...d,sem_vigencia_fim:e.target.checked,vigencia_fim:e.target.checked?"":d.vigencia_fim}))} />
              Prazo indeterminado
            </label>
          </div>
        )}
        <div>
          <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Forma de Pagamento</label>
          <select value={isCont?fcontr.forma_pagamento:dados.forma_pagamento}
            onChange={e=>isCont?setFcontr(f=>({...f,forma_pagamento:e.target.value})):setDados(d=>({...d,forma_pagamento:e.target.value}))} style={selStyle}>
            {["pix","boleto bancário","transferência bancária (TED)","depósito bancário"].map(o=><option key={o}>{o}</option>)}
          </select>
        </div>
        <Input label="Dia de Vencimento" type="number" min="1" max="28"
          value={isCont?fcontr.dia_vencimento:dados.dia_vencimento}
          onChange={e=>isCont?setFcontr(f=>({...f,dia_vencimento:e.target.value})):setDados(d=>({...d,dia_vencimento:e.target.value}))} />
        <div>
          <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Índice de Reajuste</label>
          <select value={isCont?fcontr.indice_reajuste:dados.indice_reajuste}
            onChange={e=>isCont?setFcontr(f=>({...f,indice_reajuste:e.target.value})):setDados(d=>({...d,indice_reajuste:e.target.value}))} style={selStyle}>
            {["INPC","IPCA","IGP-M","CCT (Convenção Coletiva)"].map(o=><option key={o}>{o}</option>)}
          </select>
        </div>
        <Input label="Periodicidade Reajuste (meses)" type="number"
          value={isCont?fcontr.periodicidade_reajuste:dados.periodicidade_reajuste}
          onChange={e=>isCont?setFcontr(f=>({...f,periodicidade_reajuste:e.target.value})):setDados(d=>({...d,periodicidade_reajuste:e.target.value}))} />
      </div>
      {isCont && (
        <div style={{ display:"grid",gridTemplateColumns:"repeat(3,1fr)",gap:10,marginTop:14,padding:"14px",background:COLORS.bg,borderRadius:8 }}>
          <Input label="Valor Total do Contrato R$" type="number" value={fcontr.valor_total_contrato}
            onChange={e=>setFcontr(f=>({...f,valor_total_contrato:e.target.value}))} placeholder="Ex: 120000.00" />
          <Input label="Duração (meses)" type="number" value={fcontr.duracao_meses}
            onChange={e=>setFcontr(f=>({...f,duracao_meses:e.target.value}))} placeholder="Ex: 12" />
          <div>
            <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Valor Mensal (calculado)</label>
            <div style={{ padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.primary}33`,background:COLORS.primaryBg,fontSize:14,fontWeight:800,color:COLORS.primary }}>
              {fmt(fcontr.valor_total_contrato && fcontr.duracao_meses > 0
                ? parseFloat(fcontr.valor_total_contrato) / parseInt(fcontr.duracao_meses)
                : 0)}/mês
            </div>
          </div>
        </div>
      )}
    </Card>
  );

  // ── Editor modo PROPOSTA ──────────────────────────────────────────────────
  if (modoNovo === "proposta") return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      {/* Barra superior */}
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <Btn variant="ghost" onClick={()=>setTela("lista")}>← Voltar</Btn>
        <span style={{ fontWeight:800, fontSize:16 }}>{editandoId ? `Editando ${dados.numero}` : "Nova Proposta"}</span>
        <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
          <span style={{ fontSize:11,fontWeight:700,padding:"3px 10px",borderRadius:20,
            background:{rascunho:"#f3f4f6",enviada:"#dbeafe",convertida:"#dcfce7"}[dados.status]||"#f3f4f6",
            color:{rascunho:COLORS.textMid,enviada:COLORS.info,convertida:"#16a34a"}[dados.status]||COLORS.textMid }}>
            {({rascunho:"Rascunho",enviada:"Enviada",convertida:"Convertida"})[dados.status]||dados.status}
          </span>
          <Btn variant="ghost" onClick={()=>salvar()} disabled={salvando}>{salvando?"Salvando...":"Salvar Rascunho"}</Btn>
          {editandoId && <>
            <Btn variant="accent" onClick={gerarPDF}>Gerar PDF</Btn>
            {dados.status === "rascunho" && (
              <Btn onClick={()=>{ if(window.confirm("Marcar esta proposta como Enviada ao cliente?")) salvar("enviada"); }} disabled={salvando}
                style={{ background:COLORS.info,color:"#fff",border:"none" }}>
                Marcar como Enviada
              </Btn>
            )}
            {dados.status === "enviada" && (
              <Btn variant="accent" style={{ background:"#16a34a" }} onClick={()=>setModalConverter(true)}>
                Converter em Contrato
              </Btn>
            )}
          </>}
        </div>
      </div>

      {/* Empresa emissora */}
      {empresasCad.length > 0 && (
        <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderRadius:12, padding:"12px 16px" }}>
          <div style={{ fontSize:11, fontWeight:700, color:COLORS.textMid, textTransform:"uppercase", letterSpacing:1, marginBottom:10 }}>🏢 Empresa que emite a proposta</div>
          <div style={{ display:"flex", gap:10, overflowX:"auto", paddingBottom:2 }}>
            {empresasCad.map(em => {
              const sel = String(configEmpresa._empresa_id) === String(em.id) || (!configEmpresa._empresa_id && empresasCad[0]?.id === em.id);
              return (
                <button key={em.id} onClick={() => {
                  const cfg = { _empresa_id: em.id, razao_social: em.razao_social||em.nome||"", cnpj: em.cnpj||"", endereco: em.endereco||"", numero: em.numero||"", complemento: em.complemento||"", cidade: em.cidade||"", uf: em.uf||"", cep: em.cep||"", telefone: em.telefone||"", email: em.email||"", site: em.site||"", responsavel_nome: em.responsavel_nome||"", responsavel_cargo: em.responsavel_cargo||"", logo_base64: em.logo_base64||null};
                  setConfigEmpresa(cfg); setFormConfigEmp(cfg);
                  apiFetch("POST", "/api/propostas/config-empresa", cfg, token).catch(()=>{});
                }} style={{ display:"flex", alignItems:"center", gap:10, padding:"10px 14px",
                  borderRadius:10, border:`2px solid ${sel ? COLORS.primary : COLORS.border}`,
                  background: sel ? COLORS.primaryBg : COLORS.surface,
                  cursor:"pointer", whiteSpace:"nowrap", flexShrink:0 }}>
                  {em.logo_base64
                    ? <img src={em.logo_base64} alt="logo" style={{ width:36, height:36, objectFit:"contain", borderRadius:6 }} />
                    : <div style={{ width:36, height:36, borderRadius:6, background:COLORS.bg, display:"flex", alignItems:"center", justifyContent:"center", fontSize:16 }}>🏢</div>
                  }
                  <div>
                    <div style={{ fontWeight:700, fontSize:13, color: sel ? COLORS.primary : COLORS.text }}>{em.nome}</div>
                    {em.cnpj && <div style={{ fontSize:11, color:COLORS.textMid }}>{em.cnpj}</div>}
                  </div>
                  {sel && <span style={{ color:COLORS.primary, fontWeight:900, fontSize:16, marginLeft:4 }}>✓</span>}
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Identificação */}
      <Card>
        {secHdr("📋 Identificação da Proposta")}
        <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:10 }}>
          <Input label="Nº da Proposta" value={dados.numero} onChange={e=>setDados(d=>({...d,numero:e.target.value}))} />
          <Input label="Data da Proposta" type="date" value={dados.data_proposta} onChange={e=>setDados(d=>({...d,data_proposta:e.target.value}))} />
          <Input label="Objeto / Descrição" value={dados.objeto} onChange={e=>setDados(d=>({...d,objeto:e.target.value}))} placeholder="Ex: limpeza, portaria, jardinagem" />
        </div>
      </Card>

      {renderEmpresaFaturadora()}
      {renderDadosCli()}
      {renderServicos()}
      {renderVigencia(false)}

      <Card>
        <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Observações Gerais</label>
        <textarea value={dados.observacoes} onChange={e=>setDados(d=>({...d,observacoes:e.target.value}))} rows={3}
          style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,resize:"vertical",boxSizing:"border-box" }}
          placeholder="Informações adicionais sobre a proposta ou condições especiais..." />
      </Card>

      {/* Calculadora */}
      <div style={{ borderTop:`2px solid ${COLORS.border}`, paddingTop:16 }}>
        <div style={{ fontWeight:800, fontSize:15, color:COLORS.primary, marginBottom:14, display:"flex", alignItems:"center", gap:8 }}>
          <span>🧮</span> Calculadora de Custos
          {servicosForm.length === 0 && <span style={{ fontSize:12,color:COLORS.textMid,fontWeight:400 }}>— adicione serviços acima para calcular</span>}
        </div>
        {renderCalculo()}
      </div>

      {/* Cláusulas */}
      <div style={{ borderTop:`2px solid ${COLORS.border}`, paddingTop:16, display:"flex", flexDirection:"column", gap:14 }}>
        <Card>
          <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
            <div>
              <div style={{ fontWeight:700, color:COLORS.primary }}>📄 Editor de Cláusulas Contratuais</div>
              <div style={{ fontSize:12, color:COLORS.textMid, marginTop:2 }}>Clique em qualquer cláusula para editar. Você pode adicionar, remover e reordenar.</div>
            </div>
            <div style={{ display:"flex", gap:8 }}>
              <Btn size="sm" onClick={()=>setClausulas(prev=>[...prev,{titulo:`CLÁUSULA ${prev.length+1}ª – NOVA CLÁUSULA`,texto:"Clique para editar o texto desta cláusula...",ordem:prev.length+1}])}>+ Adicionar Cláusula</Btn>
              <Btn size="sm" variant="accent" onClick={gerarPDF}>Gerar PDF</Btn>
            </div>
          </div>
        </Card>
        {clausulas.map((c, idx)=>(
          <Card key={idx} style={{ border:`1px solid ${COLORS.border}` }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", marginBottom:8 }}>
              <input value={c.titulo} onChange={e=>setClausulas(prev=>prev.map((cl,i)=>i===idx?{...cl,titulo:e.target.value}:cl))}
                style={{ fontWeight:700,fontSize:13,color:COLORS.primary,border:"none",background:"transparent",width:"80%",outline:"none",padding:0 }} />
              <div style={{ display:"flex", gap:6 }}>
                {idx>0&&<button onClick={()=>{const arr=[...clausulas];[arr[idx-1],arr[idx]]=[arr[idx],arr[idx-1]];setClausulas(arr);}} style={{ background:"none",border:"none",cursor:"pointer",color:COLORS.textMid,fontSize:14 }}>↑</button>}
                {idx<clausulas.length-1&&<button onClick={()=>{const arr=[...clausulas];[arr[idx+1],arr[idx]]=[arr[idx],arr[idx+1]];setClausulas(arr);}} style={{ background:"none",border:"none",cursor:"pointer",color:COLORS.textMid,fontSize:14 }}>↓</button>}
                <button onClick={()=>setClausulas(prev=>prev.filter((_,i)=>i!==idx))} style={{ background:"none",border:"none",cursor:"pointer",color:COLORS.danger,fontSize:14 }}>✕</button>
              </div>
            </div>
            <textarea value={c.texto} onChange={e=>setClausulas(prev=>prev.map((cl,i)=>i===idx?{...cl,texto:e.target.value}:cl))}
              rows={5} style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.bg,fontSize:12,resize:"vertical",boxSizing:"border-box",lineHeight:1.6 }} />
            <div style={{ fontSize:11, color:COLORS.textMid, marginTop:4 }}>
              Variáveis: {"{objeto}"} {"{vigencia_inicio}"} {"{vigencia_fim}"} {"{valor_total}"} {"{dia_vencimento}"} {"{indice_reajuste}"}
            </div>
          </Card>
        ))}
      </div>
    </div>
  );

  // ── Editor modo CONTRATO ATIVO ────────────────────────────────────────────
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:14 }}>
      <div style={{ display:"flex", gap:10, alignItems:"center", flexWrap:"wrap" }}>
        <Btn variant="ghost" onClick={()=>{ if(onVoltar) onVoltar(); else setTela("lista"); }}>← Voltar</Btn>
        <span style={{ fontWeight:800, fontSize:16, color:"#16a34a" }}>+ Novo Contrato Ativo</span>
        <div style={{ marginLeft:"auto" }}>
          <Btn style={{ background:"#16a34a",color:"#fff",border:"none" }} onClick={salvarNovoContrato} disabled={salvandoContr}>
            {salvandoContr?"Salvando...":"✅ Salvar Contrato"}
          </Btn>
        </div>
      </div>

      {renderEmpresaFaturadora()}
      {renderDadosCli()}
      {renderServicos()}
      {renderVigencia(true)}

      <Card>
        <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Observações</label>
        <textarea value={dados.observacoes} onChange={e=>setDados(d=>({...d,observacoes:e.target.value}))} rows={3}
          style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,resize:"vertical",boxSizing:"border-box" }}
          placeholder="Observações sobre o contrato..." />
      </Card>
    </div>
  );
}

// ─── ADIANTAMENTO DE SALÁRIO ──────────────────────────────────────────────────
function Adiantamento({ employees, token }) {
  const now = new Date();
  const [mes, setMes] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);

  const [anoNum, mesNum] = mes.split('-').map(Number);
  const mesNome = new Date(anoNum, mesNum - 1, 1).toLocaleString("pt-BR", { month: "long", year: "numeric" });

  const ativos = employees.filter(e => e.status !== "Demitido");

  const fmt = (v) => `R$ ${parseFloat(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`;

  const selecionados = ativos.filter(e => e.adiantamento);
  const totalAdiant = selecionados.reduce((s, e) => s + parseFloat(e.salario || 0) * 0.4, 0);

  const fmtPagamento = (e) => {
    const fc = e.pagamento || {};
    // Prioridade: conta padrão cadastrada na nova tabela
    if (fc.fcTipo === "dinheiro") return "Dinheiro";
    if (fc.fcTipo === "pix_chave" && fc.fcPixChave) {
      return `PIX (${fc.fcPixTipo||"chave"}): ${fc.fcPixChave}`;
    }
    if (fc.fcTipo && fc.fcTipo !== "pix_chave" && fc.fcConta) {
      const tl = { conta_corrente:"CC", conta_poupanca:"CP", ted:"TED" };
      const label = tl[fc.fcTipo] || fc.fcTipo;
      const banco = fc.fcBancoNome || fc.fcBancoCodigo || "";
      return `${label}${banco ? " — "+banco : ""} Ag. ${fc.fcAgencia||"?"}/${fc.fcConta||"?"}`;
    }
    // Fallback: campos legados
    const tipo = fc.tipo || "";
    if (fc.chavePix) return `PIX (${fc.tipoPix||"chave"}): ${fc.chavePix}`;
    if (fc.pixConta) {
      const banco = fc.pixBancoNome || fc.pixBancoCodigo || "";
      return `${tipo||"CC"}${banco?" — "+banco:""} Ag. ${fc.pixAgencia||"?"}/${fc.pixConta||"?"}`;
    }
    if (tipo === "Dinheiro") return "Dinheiro";
    return tipo || "Não informado";
  };

  const gerarPDF = () => {
    if (selecionados.length === 0) return alert("Nenhum colaborador selecionado para adiantamento.");
    const linhas = selecionados.map(e => {
      const valor = parseFloat(e.salario || 0) * 0.4;
      return `
        <tr>
          <td>${e.nome}</td>
          <td>${e.cargo || "—"}</td>
          <td style="text-align:right">${fmt(e.salario)}</td>
          <td style="text-align:right; font-weight:700; color:#1e40af">${fmt(valor)}</td>
          <td>${fmtPagamento(e)}</td>
        </tr>`;
    }).join("");

    const html = `<!DOCTYPE html><html><head><meta charset="UTF-8">
      <title>Adiantamento de Salário — ${mesNome}</title>
      <style>
        body { font-family: Arial, sans-serif; font-size: 13px; color: #111; margin: 30px; }
        h1 { font-size: 18px; margin-bottom: 4px; }
        .sub { color: #555; font-size: 12px; margin-bottom: 20px; }
        table { width: 100%; border-collapse: collapse; }
        th { background: #1e3a5f; color: #fff; padding: 8px 12px; text-align: left; font-size: 12px; }
        td { padding: 8px 12px; border-bottom: 1px solid #e5e7eb; font-size: 13px; }
        tr:nth-child(even) td { background: #f8fafc; }
        .total-row td { font-weight: 700; font-size: 14px; border-top: 2px solid #1e3a5f; }
        .footer { margin-top: 30px; font-size: 11px; color: #888; }
        @media print { body { margin: 15mm; } }
      </style></head><body>
      <h1>Adiantamento de Salário</h1>
      <div class="sub">Referência: ${mesNome} &nbsp;|&nbsp; Gerado em: ${new Date().toLocaleDateString("pt-BR")} &nbsp;|&nbsp; ${selecionados.length} colaborador(es)</div>
      <table>
        <thead><tr>
          <th>Colaborador</th><th>Cargo</th><th style="text-align:right">Salário Bruto</th>
          <th style="text-align:right">Adiantamento (40%)</th><th>Forma de Pagamento</th>
        </tr></thead>
        <tbody>${linhas}
          <tr class="total-row">
            <td colspan="3" style="text-align:right">TOTAL A PAGAR</td>
            <td style="text-align:right; color:#1e40af">${fmt(totalAdiant)}</td>
            <td></td>
          </tr>
        </tbody>
      </table>
      <div class="footer">Sistema RH Facilitae &mdash; Documento gerado automaticamente</div>
      <script>window.onload = function(){ window.print(); }</script>
      </body></html>`;

    const win = window.open("", "_blank");
    win.document.write(html);
    win.document.close();
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", flexWrap: "wrap" }}>
        <Input label="Mês de referência" type="month" value={mes} onChange={e => setMes(e.target.value)} />
        <Btn variant="accent" onClick={gerarPDF}>Gerar PDF</Btn>
        <div style={{ marginLeft: "auto", display: "flex", gap: 16, alignItems: "center" }}>
          <span style={{ fontSize: 13, color: COLORS.textMid }}>{selecionados.length} colaborador(es) com adiantamento</span>
          {selecionados.length > 0 && (
            <span style={{ fontWeight: 800, fontSize: 15, color: COLORS.primary }}>Total: {fmt(totalAdiant)}</span>
          )}
        </div>
      </div>

      <Card style={{ fontSize: 12, color: COLORS.textMid, padding: "10px 16px", background: COLORS.primaryBg }}>
        Colaboradores marcados para adiantamento conforme cadastro. O valor corresponde a <strong>40% do salário bruto</strong>. Para alterar, edite o cadastro do colaborador.
      </Card>

      <Card style={{ padding: 0, overflow: "hidden" }}>
        <div style={{ overflowX: "auto" }}>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
            <thead>
              <tr style={{ background: COLORS.bg }}>
                <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: 11, color: COLORS.textMid, width: 48, textAlign: "center" }}></th>
                <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: 11, color: COLORS.textMid }}>Colaborador</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: 11, color: COLORS.textMid }}>Cargo</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: 11, color: COLORS.textMid, textAlign: "right" }}>Salário Bruto</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: 11, color: COLORS.primary, textAlign: "right" }}>Adiantamento (40%)</th>
                <th style={{ padding: "10px 14px", fontWeight: 600, fontSize: 11, color: COLORS.textMid }}>Forma de Pagamento</th>
              </tr>
            </thead>
            <tbody>
              {ativos.map((e, i) => {
                const marcado = !!e.adiantamento;
                const valor = parseFloat(e.salario || 0) * 0.4;
                return (
                  <tr key={e.id}
                    style={{ background: marcado ? COLORS.primaryBg : (i % 2 === 0 ? COLORS.white : COLORS.gray), borderBottom: `1px solid ${COLORS.border}`, cursor: "default", opacity: marcado ? 1 : 0.45 }}>
                    <td style={{ padding: "10px 14px", textAlign: "center" }}>
                      <input type="checkbox" checked={marcado} disabled readOnly />
                    </td>
                    <td style={{ padding: "10px 14px", fontWeight: marcado ? 700 : 400 }}>{e.nome}</td>
                    <td style={{ padding: "10px 14px", color: COLORS.textMid }}>{e.cargo || "—"}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right" }}>{fmt(e.salario)}</td>
                    <td style={{ padding: "10px 14px", textAlign: "right", fontWeight: 700, color: marcado ? COLORS.primary : COLORS.textMid }}>
                      {marcado ? fmt(valor) : "—"}
                    </td>
                    <td style={{ padding: "10px 14px", fontSize: 12, color: COLORS.textMid }}>{fmtPagamento(e)}</td>
                  </tr>
                );
              })}
            </tbody>
            {selecionados.length > 0 && (
              <tfoot>
                <tr style={{ background: COLORS.bg, fontWeight: 800 }}>
                  <td colSpan={4} style={{ padding: "11px 14px", textAlign: "right" }}>TOTAL A PAGAR</td>
                  <td style={{ padding: "11px 14px", textAlign: "right", color: COLORS.primary, fontSize: 15 }}>{fmt(totalAdiant)}</td>
                  <td></td>
                </tr>
              </tfoot>
            )}
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── COMPROVANTES DE PAGAMENTO ────────────────────────────────────────────────
function Comprovantes({ employees, token }) {
  const [comprovantes, setComprovantes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filtroFunc, setFiltroFunc] = useState('');
  const [filtroTipo, setFiltroTipo] = useState('');
  const [filtroMes, setFiltroMes] = useState('');
  const [filtroAno, setFiltroAno] = useState(String(new Date().getFullYear()));
  const [modalImportar, setModalImportar] = useState(false);
  const [importFiles, setImportFiles] = useState([]);
  const [importItems, setImportItems] = useState([]);
  const [importTipo, setImportTipo] = useState('salario');
  const [importTipoLabel, setImportTipoLabel] = useState('');
  const [importando, setImportando] = useState(false);
  const [importErr, setImportErr] = useState(null);
  const [importOk, setImportOk] = useState(null);
  const fileInputRef = useRef();

  function normNome(n) {
    return (n || '').toLowerCase().normalize('NFD').replace(/[̀-ͯ]/g, '').replace(/\s+/g, ' ').trim();
  }

  function parseComprovante(filename) {
    const base = filename.replace(/\.pdf$/i, '');
    const m = base.match(/^[^_]+_(\d{4}-\d{2}-\d{2})_[\d.-]+_\d+_(.+)$/);
    if (m) return { data: m[1], nome: m[2] };
    return { data: null, nome: base };
  }

  function matchFunc(nome) {
    const n = normNome(nome);
    return employees.find(e => normNome(e.nome) === n) || null;
  }

  function handleFilesSelected(files) {
    const arr = Array.from(files).filter(f => f.name.toLowerCase().endsWith('.pdf'));
    setImportFiles(arr);
    const items = arr.map(f => {
      const parsed = parseComprovante(f.name);
      const func = matchFunc(parsed.nome);
      return {
        filename: f.name,
        data: parsed.data,
        nomeDetectado: parsed.nome,
        funcionario_id: func ? String(func.id) : '',
        funcionarioNome: func ? func.nome : '',
        matched: !!func,
      };
    });
    setImportItems(items);
    setImportErr(null);
    setImportOk(null);
  }

  async function handleImportar() {
    if (!importItems.length) return;
    const semFuncionario = importItems.filter(it => !it.funcionario_id);
    if (semFuncionario.length) {
      setImportErr(`${semFuncionario.length} arquivo(s) sem funcionário vinculado. Corrija antes de importar.`);
      return;
    }
    if (importTipo === 'outros' && !importTipoLabel.trim()) {
      setImportErr('Informe o nome do tipo de comprovante.'); return;
    }
    setImportando(true); setImportErr(null);
    try {
      const fd = new FormData();
      fd.append('tipo', importTipo);
      fd.append('tipo_label', importTipo === 'outros' ? importTipoLabel.trim() : '');
      const assignments = importItems.map(it => ({
        filename: it.filename,
        funcionario_id: it.funcionario_id,
        data_comprovante: it.data || null,
      }));
      fd.append('assignments', JSON.stringify(assignments));
      importFiles.forEach(f => fd.append('files', f, f.name));

      const resp = await fetch('/api/comprovantes', {
        method: 'POST',
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      const data = await resp.json();
      if (!resp.ok) throw new Error(data.erro || 'Erro ao importar');
      setImportOk(`${data.salvos} comprovante(s) importado(s) com sucesso!`);
      setImportFiles([]); setImportItems([]);
      carregarComprovantes();
      setTimeout(() => { setModalImportar(false); setImportOk(null); }, 2000);
    } catch(e) { setImportErr(e.message); }
    finally { setImportando(false); }
  }

  async function carregarComprovantes() {
    setLoading(true);
    try {
      let qs = `ano=${filtroAno}`;
      if (filtroMes) qs += `&mes=${filtroMes}`;
      if (filtroFunc) qs += `&funcionario_id=${filtroFunc}`;
      if (filtroTipo) qs += `&tipo=${filtroTipo}`;
      const data = await apiFetch('GET', `/api/comprovantes?${qs}`, undefined, token);
      setComprovantes(Array.isArray(data) ? data : []);
    } catch(e) { setComprovantes([]); }
    setLoading(false);
  }

  useEffect(() => { carregarComprovantes(); }, [filtroFunc, filtroTipo, filtroMes, filtroAno]);

  function abrirArquivo(id) {
    window.open(`/api/comprovantes/${id}/arquivo?t=${token}`, '_blank');
  }

  async function excluirComprovante(id) {
    if (!window.confirm('Excluir este comprovante?')) return;
    await apiFetch('DELETE', `/api/comprovantes/${id}`, undefined, token);
    setComprovantes(prev => prev.filter(c => c.id !== id));
  }

  const TIPO_LABEL = { salario: 'Pagamento de Salário', adiantamento: 'Adiantamento de Salário', outros: 'Outros' };
  const TIPO_COLOR = { salario: COLORS.primary, adiantamento: COLORS.warning, outros: COLORS.info };

  const anos = [];
  for (let a = new Date().getFullYear(); a >= 2023; a--) anos.push(a);

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 20 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12 }}>
        <h2 style={{ margin: 0, fontSize: 18, fontWeight: 800, color: COLORS.text }}>Comprovantes de Pagamento</h2>
        <Btn variant="primary" onClick={() => { setModalImportar(true); setImportFiles([]); setImportItems([]); setImportErr(null); setImportOk(null); }}>
          + Importar Comprovantes
        </Btn>
      </div>

      {/* Filtros */}
      <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap', alignItems: 'flex-end' }}>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid }}>Funcionário</label>
          <select value={filtroFunc} onChange={e => setFiltroFunc(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13, minWidth: 200 }}>
            <option value=''>Todos</option>
            {employees.filter(e => e.status === 'ativo').sort((a,b) => a.nome.localeCompare(b.nome)).map(e => (
              <option key={e.id} value={e.id}>{e.nome}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid }}>Tipo</label>
          <select value={filtroTipo} onChange={e => setFiltroTipo(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13 }}>
            <option value=''>Todos</option>
            <option value='salario'>Pagamento de Salário</option>
            <option value='adiantamento'>Adiantamento de Salário</option>
            <option value='outros'>Outros</option>
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid }}>Mês</label>
          <select value={filtroMes} onChange={e => setFiltroMes(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13 }}>
            <option value=''>Todos</option>
            {['Jan','Fev','Mar','Abr','Mai','Jun','Jul','Ago','Set','Out','Nov','Dez'].map((m,i) => (
              <option key={i+1} value={i+1}>{m}</option>
            ))}
          </select>
        </div>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
          <label style={{ fontSize: 11, fontWeight: 600, color: COLORS.textMid }}>Ano</label>
          <select value={filtroAno} onChange={e => setFiltroAno(e.target.value)}
            style={{ padding: '7px 10px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13 }}>
            {anos.map(a => <option key={a} value={a}>{a}</option>)}
          </select>
        </div>
      </div>

      {/* Tabela */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        {loading ? (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMid }}>Carregando...</div>
        ) : comprovantes.length === 0 ? (
          <div style={{ padding: 40, textAlign: 'center', color: COLORS.textMid }}>Nenhum comprovante encontrado.</div>
        ) : (
          <div style={{ overflowX: 'auto' }}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
              <thead>
                <tr style={{ background: COLORS.bg }}>
                  {['Data', 'Funcionário', 'Tipo', 'Arquivo', 'Importado em', ''].map(h => (
                    <th key={h} style={{ padding: '10px 14px', textAlign: 'left', fontSize: 11, color: COLORS.textMid, fontWeight: 600, whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {comprovantes.map((c, i) => (
                  <tr key={c.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: '10px 14px', whiteSpace: 'nowrap' }}>
                      {c.data_comprovante ? new Date(c.data_comprovante + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                    </td>
                    <td style={{ padding: '10px 14px', fontWeight: 600 }}>{c.funcionario_nome}</td>
                    <td style={{ padding: '10px 14px' }}>
                      <span style={{ background: TIPO_COLOR[c.tipo] + '20', color: TIPO_COLOR[c.tipo], borderRadius: 6, padding: '3px 10px', fontSize: 12, fontWeight: 700 }}>
                        {c.tipo === 'outros' && c.tipo_label ? c.tipo_label : TIPO_LABEL[c.tipo]}
                      </span>
                    </td>
                    <td style={{ padding: '10px 14px', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                      <span style={{ fontSize: 12, color: COLORS.textMid }} title={c.filename_original}>{c.filename_original}</span>
                    </td>
                    <td style={{ padding: '10px 14px', fontSize: 12, color: COLORS.textMid, whiteSpace: 'nowrap' }}>
                      {new Date(c.created_at).toLocaleString('pt-BR', { day:'2-digit', month:'2-digit', year:'numeric', hour:'2-digit', minute:'2-digit' })}
                    </td>
                    <td style={{ padding: '8px 10px', whiteSpace: 'nowrap' }}>
                      <button onClick={() => abrirArquivo(c.id)}
                        style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}`, borderRadius: 6, cursor: 'pointer', padding: '4px 10px', fontSize: 12, color: COLORS.primary, fontWeight: 600, marginRight: 6 }}>
                        Ver PDF
                      </button>
                      <button onClick={() => excluirComprovante(c.id)}
                        style={{ background: 'none', border: 'none', cursor: 'pointer', color: COLORS.danger, fontSize: 14, padding: '4px 6px' }} title="Excluir">✕</button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Card>

      {/* Modal Importar */}
      {modalImportar && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: 20 }}>
          <div style={{ background: COLORS.surface, borderRadius: 16, padding: 28, width: '100%', maxWidth: 780, maxHeight: '90vh', overflowY: 'auto', boxShadow: '0 8px 40px rgba(0,0,0,0.18)' }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20 }}>
              <h3 style={{ margin: 0, fontSize: 16, fontWeight: 800, color: COLORS.text }}>Importar Comprovantes</h3>
              <button onClick={() => setModalImportar(false)} style={{ background: 'none', border: 'none', fontSize: 20, cursor: 'pointer', color: COLORS.textMid }}>✕</button>
            </div>

            {/* Tipo */}
            <div style={{ display: 'flex', gap: 12, marginBottom: 16, flexWrap: 'wrap', alignItems: 'flex-end' }}>
              <div style={{ flex: 1, minWidth: 200 }}>
                <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: 'block', marginBottom: 4 }}>Tipo de Comprovante</label>
                <select value={importTipo} onChange={e => setImportTipo(e.target.value)}
                  style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13 }}>
                  <option value='salario'>Comprovante de Pagamento de Salário</option>
                  <option value='adiantamento'>Adiantamento de Salário</option>
                  <option value='outros'>Outros</option>
                </select>
              </div>
              {importTipo === 'outros' && (
                <div style={{ flex: 1, minWidth: 200 }}>
                  <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: 'block', marginBottom: 4 }}>Nome do Tipo</label>
                  <input value={importTipoLabel} onChange={e => setImportTipoLabel(e.target.value)}
                    placeholder="Ex: 13º Salário, Férias..."
                    style={{ width: '100%', padding: '8px 10px', borderRadius: 8, border: `1px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13, boxSizing: 'border-box' }} />
                </div>
              )}
            </div>

            {/* Seleção de arquivos */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, display: 'block', marginBottom: 6 }}>Selecionar PDFs</label>
              <div style={{ display: 'flex', gap: 12, alignItems: 'center' }}>
                <input ref={fileInputRef} type='file' accept='.pdf' multiple style={{ display: 'none' }}
                  onChange={e => handleFilesSelected(e.target.files)} />
                <button onClick={() => fileInputRef.current?.click()}
                  style={{ background: COLORS.primaryBg, border: `1px solid ${COLORS.primary}`, borderRadius: 8, padding: '8px 18px', fontSize: 13, color: COLORS.primary, fontWeight: 700, cursor: 'pointer' }}>
                  📎 Selecionar PDFs
                </button>
                {importFiles.length > 0 && (
                  <span style={{ fontSize: 13, color: COLORS.textMid }}>{importFiles.length} arquivo(s) selecionado(s)</span>
                )}
              </div>
              <p style={{ margin: '6px 0 0', fontSize: 11, color: COLORS.textMid }}>
                Selecione todos os PDFs da pasta de uma vez (Ctrl+A ou Shift+Click no seletor de arquivos).
              </p>
            </div>

            {/* Preview */}
            {importItems.length > 0 && (
              <div style={{ marginBottom: 16 }}>
                <div style={{ fontSize: 13, fontWeight: 700, color: COLORS.text, marginBottom: 8 }}>
                  Pré-visualização — {importItems.filter(it => it.matched).length} de {importItems.length} vinculados automaticamente
                </div>
                <div style={{ border: `1px solid ${COLORS.border}`, borderRadius: 10, overflow: 'hidden', maxHeight: 340, overflowY: 'auto' }}>
                  <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 12 }}>
                    <thead>
                      <tr style={{ background: COLORS.bg, position: 'sticky', top: 0 }}>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textMid, fontWeight: 600 }}>Arquivo</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textMid, fontWeight: 600 }}>Data</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textMid, fontWeight: 600 }}>Nome Detectado</th>
                        <th style={{ padding: '8px 12px', textAlign: 'left', color: COLORS.textMid, fontWeight: 600 }}>Funcionário</th>
                      </tr>
                    </thead>
                    <tbody>
                      {importItems.map((it, idx) => (
                        <tr key={idx} style={{ background: idx % 2 === 0 ? COLORS.white : COLORS.gray, borderBottom: `1px solid ${COLORS.border}` }}>
                          <td style={{ padding: '7px 12px', maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }} title={it.filename}>
                            <span style={{ color: COLORS.textMid }}>{it.filename}</span>
                          </td>
                          <td style={{ padding: '7px 12px', whiteSpace: 'nowrap' }}>
                            {it.data ? new Date(it.data + 'T12:00:00').toLocaleDateString('pt-BR') : '—'}
                          </td>
                          <td style={{ padding: '7px 12px', fontWeight: 600 }}>{it.nomeDetectado}</td>
                          <td style={{ padding: '7px 12px', minWidth: 200 }}>
                            {it.matched ? (
                              <span style={{ color: '#16a34a', fontWeight: 600 }}>✓ {it.funcionarioNome}</span>
                            ) : (
                              <select value={it.funcionario_id}
                                onChange={e => {
                                  const fid = e.target.value;
                                  const func = employees.find(f => String(f.id) === fid);
                                  setImportItems(prev => prev.map((p, i) => i === idx
                                    ? { ...p, funcionario_id: fid, funcionarioNome: func?.nome || '', matched: !!func }
                                    : p));
                                }}
                                style={{ width: '100%', padding: '5px 8px', borderRadius: 6, border: `1px solid ${COLORS.danger}`, background: '#fff5f5', fontSize: 12 }}>
                                <option value=''>⚠ Selecionar funcionário...</option>
                                {employees.filter(e => e.status === 'ativo').sort((a,b) => a.nome.localeCompare(b.nome)).map(e => (
                                  <option key={e.id} value={e.id}>{e.nome}</option>
                                ))}
                              </select>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {importErr && <div style={{ background: '#fee2e2', border: '1px solid #fca5a5', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#991b1b', marginBottom: 12 }}>{importErr}</div>}
            {importOk && <div style={{ background: '#dcfce7', border: '1px solid #86efac', borderRadius: 8, padding: '8px 14px', fontSize: 13, color: '#166534', marginBottom: 12 }}>{importOk}</div>}

            <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end' }}>
              <Btn variant="ghost" onClick={() => setModalImportar(false)}>Cancelar</Btn>
              {importItems.length > 0 && (
                <Btn variant="primary" onClick={handleImportar} disabled={importando}>
                  {importando ? 'Importando...' : `Importar ${importItems.filter(it => it.funcionario_id).length} comprovante(s)`}
                </Btn>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

// ─── CONFIG NFS-e (tela de configurações) ────────────────────────────────────
function ConfigNFe({ token }) {
  const EMPTY = { api_key:"", api_key_producao:"", cnpj_emitente:"", razao_social:"", municipio_codigo:"3106200", serie:"1", ambiente:"homologacao", inscricao_municipal:"", simples_nacional:"2", regime_especial:"0", aliquota_iss:"0", tipo_retencao_iss:"1", logradouro:"", numero_end:"", bairro:"", cep:"", ctrib_nac:"170602", ibs_cbs_cst:"040", ibs_cbs_class_trib:"1001000" };
  const [form, setForm] = useState(EMPTY);
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    apiFetch("GET", "/api/contratos/config-nfe", undefined, token)
      .then(r => { if(r?.cnpj_emitente || r?.api_key) setForm(f=>({...EMPTY,...r,simples_nacional:String(r.simples_nacional||2),regime_especial:String(r.regime_especial||0),tipo_retencao_iss:String(r.tipo_retencao_iss||1),aliquota_iss:String(r.aliquota_iss||0)})); })
      .catch(()=>{});
  }, [token]);

  const salvar = async () => {
    setSalvando(true);
    try {
      await apiFetch("POST", "/api/contratos/config-nfe", form, token);
      setMsg("✅ Configuração salva com sucesso!");
      setTimeout(()=>setMsg(""),3000);
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const lbl = { fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:3 };
  const inp = { width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${COLORS.border}`, background:COLORS.bg, fontSize:13, color:COLORS.text, boxSizing:"border-box" };
  const F = (label, key, ph="") => <div><label style={lbl}>{label}</label><input value={form[key]||""} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} placeholder={ph} style={inp} /></div>;
  const S = (label, key, opts) => <div><label style={lbl}>{label}</label><select value={form[key]||""} onChange={e=>setForm(f=>({...f,[key]:e.target.value}))} style={inp}>{opts.map(([v,l])=><option key={v} value={v}>{l}</option>)}</select></div>;

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16, maxWidth:680 }}>
      <Card>
        <div style={{ fontWeight:800, fontSize:16, marginBottom:2 }}>🧾 NFS-e — FocusNFE DPS</div>
        <div style={{ fontSize:12, color:COLORS.textMid, marginBottom:20 }}>Padrão NFS-e Nacional · Emissão automática nas cobranças</div>

        <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>Ambiente e Tokens</div>
        <div style={{ display:"grid",gridTemplateColumns:"1fr",gap:8,marginBottom:16 }}>
          {S("Ambiente", "ambiente", [["homologacao","🧪 Homologação (testes)"],["producao","✅ Produção"]])}
          <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
            {F("Token Homologação", "api_key", "HDDW5woipCO...")}
            {F("Token Produção", "api_key_producao", "vt9DRHTuXId...")}
          </div>
        </div>

        <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>Emitente (Prestador)</div>
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16 }}>
          {F("CNPJ *", "cnpj_emitente", "65.451.642/0001-52")}
          {F("Razão Social / Nome MEI *", "razao_social")}
          {F("Inscrição Municipal", "inscricao_municipal", "Opcional")}
          {F("Código IBGE Município *", "municipio_codigo", "3106200")}
          {F("CEP", "cep", "30000-000")}
          {F("Logradouro", "logradouro")}
          {F("Número", "numero_end")}
          {F("Bairro", "bairro")}
          {F("Série DPS", "serie", "1")}
        </div>

        <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>Tributação ISS</div>
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16 }}>
          {S("Simples Nacional *", "simples_nacional", [["1","Não optante"],["2","MEI"],["3","ME/EPP"]])}
          {S("Regime Especial *", "regime_especial", [["0","0 — Nenhum"],["1","1 — Ato Cooperado"],["2","2 — Estimativa"],["3","3 — Microempresa Municipal"],["5","5 — Profissional Autônomo"],["6","6 — Sociedade de Profissionais"]])}
          {F("Código Tributação Nacional ISS (cTribNac) *", "ctrib_nac", "170602")}
          {F("Alíquota ISS % (0 para MEI/DAS)", "aliquota_iss", "0")}
          {S("Tipo Retenção ISS", "tipo_retencao_iss", [["1","1 — Não Retido (MEI/DAS)"],["2","2 — Retido pelo Tomador"],["3","3 — Retido pelo Intermediário"]])}
        </div>

        <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>IBS/CBS — Reforma Tributária</div>
        <div style={{ background:"#fef9c3",border:"1px solid #fde047",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#713f12",marginBottom:8 }}>
          MEI optante Simples Nacional com ISS fixo no DAS → CST <strong>040</strong> · Classificação <strong>1001000</strong>
        </div>
        <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:16 }}>
          {F("CST IBS/CBS (3 dígitos)", "ibs_cbs_cst", "040")}
          {F("Classificação Tributária (7 dígitos)", "ibs_cbs_class_trib", "1001000")}
        </div>

        <div style={{ display:"flex", gap:10, alignItems:"center" }}>
          <Btn onClick={salvar} disabled={salvando}>{salvando?"Salvando...":"💾 Salvar Configuração"}</Btn>
          {msg && <span style={{ fontSize:12, color:"#16a34a", fontWeight:700 }}>{msg}</span>}
        </div>
      </Card>

      <Card>
        <div style={{ fontWeight:700, marginBottom:10 }}>Status</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:13 }}>
          <div><span style={{ color:COLORS.textMid }}>Provedor:</span> <strong>FocusNFE (DPS Nacional)</strong></div>
          <div><span style={{ color:COLORS.textMid }}>Ambiente:</span> <strong style={{ color:form.ambiente==="producao"?"#16a34a":COLORS.warning }}>{form.ambiente==="producao"?"✅ Produção":"🧪 Homologação"}</strong></div>
          <div><span style={{ color:COLORS.textMid }}>CNPJ:</span> <strong>{form.cnpj_emitente||"—"}</strong></div>
          <div><span style={{ color:COLORS.textMid }}>Token homol.:</span> <strong>{form.api_key ? "✅ Configurado" : "❌ Não configurado"}</strong></div>
          <div><span style={{ color:COLORS.textMid }}>Token prod.:</span> <strong>{form.api_key_producao ? "✅ Configurado" : "❌ Não configurado"}</strong></div>
          <div><span style={{ color:COLORS.textMid }}>cTribNac:</span> <strong>{form.ctrib_nac||"—"}</strong></div>
        </div>
      </Card>
    </div>
  );
}

// ─── CONFIG BANCO INTER (tela de configurações) ───────────────────────────────
function ConfigBancoInter({ token }) {
  const [form, setForm] = useState({ client_id:"", client_secret:"", conta:"", ambiente:"sandbox" });
  const [config, setConfig] = useState({});
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState("");

  useEffect(() => {
    apiFetch("GET", "/api/contratos/config-inter", undefined, token).then(r => { if(r?.client_id) { setConfig(r); setForm(f=>({...f,...r,client_secret:""})); } }).catch(()=>{});
  }, [token]);

  const salvar = async () => {
    setSalvando(true);
    try {
      await apiFetch("POST", "/api/contratos/config-inter", form, token);
      setConfig({...form});
      setMsg("✅ Configuração salva!");
      setTimeout(()=>setMsg(""),3000);
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16, maxWidth:600 }}>
      <Card>
        <div style={{ fontWeight:800, fontSize:16, marginBottom:4 }}>🏦 Banco Inter — API de Cobrança</div>
        <div style={{ fontSize:13, color:COLORS.textMid, marginBottom:16 }}>
          Credenciais em: <strong>app.bancobinter.com → Desenvolvedores → Meus apps</strong>. Após configurar, o botão 📄 aparece nas cobranças para gerar boletos.
        </div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:12, marginBottom:12 }}>
          <div>
            <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Ambiente</label>
            <select value={form.ambiente} onChange={e=>setForm(f=>({...f,ambiente:e.target.value}))}
              style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13 }}>
              <option value="sandbox">Sandbox (testes)</option>
              <option value="producao">Produção</option>
            </select>
          </div>
          <Input label="Número da Conta" value={form.conta} onChange={e=>setForm(f=>({...f,conta:e.target.value}))} placeholder="Ex: 12345678" />
        </div>
        <Input label="Client ID" value={form.client_id} onChange={e=>setForm(f=>({...f,client_id:e.target.value}))} placeholder="client_id do seu app Inter" />
        <div style={{ marginTop:12 }}>
          <Input label={config?.client_id ? "Client Secret (em branco = não alterar)" : "Client Secret"} type="password" value={form.client_secret} onChange={e=>setForm(f=>({...f,client_secret:e.target.value}))} placeholder="••••••••••" />
        </div>
        <div style={{ marginTop:12, padding:"10px 14px", background:COLORS.bg, borderRadius:8, fontSize:12, color:COLORS.textMid }}>
          ℹ️ O sistema usa <strong>OAuth 2.0 client_credentials</strong> do Inter. O certificado mTLS não é necessário nesta versão (API v3 suporta autenticação por client_id/secret diretamente).
        </div>
        <div style={{ marginTop:16, display:"flex", gap:10, alignItems:"center" }}>
          <Btn onClick={salvar} disabled={salvando}>{salvando?"Salvando...":"Salvar Configuração"}</Btn>
          {msg && <span style={{ fontSize:12, color:"#16a34a", fontWeight:700 }}>{msg}</span>}
        </div>
      </Card>
      <Card>
        <div style={{ fontWeight:700, marginBottom:10 }}>Status atual</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:8, fontSize:13 }}>
          <div><span style={{ color:COLORS.textMid }}>Ambiente:</span> <strong style={{ color:config.ambiente==="producao"?"#16a34a":COLORS.warning }}>{config.ambiente||"—"}</strong></div>
          <div><span style={{ color:COLORS.textMid }}>Conta:</span> <strong>{config.conta||"—"}</strong></div>
          <div><span style={{ color:COLORS.textMid }}>Client ID:</span> <strong>{config.client_id ? "✅ Configurado" : "❌ Não configurado"}</strong></div>
        </div>
      </Card>
    </div>
  );
}

// ─── FINANCEIRO DASHBOARD ─────────────────────────────────────────────────────
function FinanceiroDashboard({ token }) {
  const [cobrancas, setCobrancas] = useState([]);
  const [ano] = useState(new Date().getFullYear());

  useEffect(() => {
    if (!token) return;
    apiFetch("GET", `/api/contratos/cobrancas?ano=${ano}`, undefined, token)
      .then(r => setCobrancas(Array.isArray(r) ? r : []))
      .catch(() => {});
  }, [token, ano]);

  const fmt = v => `R$ ${parseFloat(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const total    = cobrancas.reduce((s,c)=>s+parseFloat(c.valor||0),0);
  const recebido = cobrancas.filter(c=>c.status==="pago").reduce((s,c)=>s+parseFloat(c.valor||0),0);
  const aReceber = cobrancas.filter(c=>c.status==="pendente").reduce((s,c)=>s+parseFloat(c.valor||0),0);
  const emAtraso = cobrancas.filter(c=>c.status==="vencido").reduce((s,c)=>s+parseFloat(c.valor||0),0);

  const KPIS = [
    { label:"A Receber",    val:fmt(aReceber), color:COLORS.info,    sub:`${cobrancas.filter(c=>c.status==="pendente").length} cobr.` },
    { label:"Recebido",     val:fmt(recebido), color:"#16a34a",      sub:`${cobrancas.filter(c=>c.status==="pago").length} cobr.` },
    { label:"Em Atraso",    val:fmt(emAtraso), color:COLORS.danger,  sub:`${cobrancas.filter(c=>c.status==="vencido").length} cobr.` },
    { label:"Total Previsto",val:fmt(total),   color:COLORS.primary, sub:`${cobrancas.length} cobranças em ${ano}` },
  ];

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12 }}>
        {KPIS.map(k => (
          <Card key={k.label} style={{ borderTop:`4px solid ${k.color}` }}>
            <div style={{ fontSize:11, fontWeight:600, color:COLORS.textMid }}>{k.label}</div>
            <div style={{ fontSize:22, fontWeight:900, color:k.color, marginTop:4 }}>{k.val}</div>
            <div style={{ fontSize:11, color:COLORS.textLight, marginTop:2 }}>{k.sub}</div>
          </Card>
        ))}
      </div>

      {/* A Pagar (folha + adiantamento) */}
      <Card>
        <div style={{ fontWeight:800, fontSize:15, marginBottom:12 }}>💸 A Pagar</div>
        <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10 }}>
          {[
            { label:"Folha de Pagamento", desc:"Acesse em: Folha de Pagamento", color:COLORS.primary },
            { label:"Adiantamento de Salários", desc:"Acesse em: Folha de Pagamento → Adiantamento", color:COLORS.warning },
            { label:"Lançamentos Manuais", desc:"Em breve — lançamentos avulsos de despesas", color:COLORS.textMid },
          ].map(item => (
            <div key={item.label} style={{ background:COLORS.bg, borderRadius:10, padding:"14px 16px", borderLeft:`4px solid ${item.color}` }}>
              <div style={{ fontWeight:700, fontSize:13, color:item.color }}>{item.label}</div>
              <div style={{ fontSize:11, color:COLORS.textMid, marginTop:4 }}>{item.desc}</div>
            </div>
          ))}
        </div>
      </Card>

      {/* Extrato por mês */}
      <Card>
        <div style={{ fontWeight:800, fontSize:15, marginBottom:12 }}>📊 Receitas por mês — {ano}</div>
        {(() => {
          const porMes = Array.from({length:12},(_,i)=>i+1).map(mes => {
            const cobsMes = cobrancas.filter(c=>c.mes===mes);
            return { mes, total:cobsMes.reduce((s,c)=>s+parseFloat(c.valor||0),0), recebido:cobsMes.filter(c=>c.status==="pago").reduce((s,c)=>s+parseFloat(c.valor||0),0), count:cobsMes.length };
          });
          const maxVal = Math.max(...porMes.map(m=>m.total), 1);
          const MESES = ["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"];
          return (
            <div style={{ display:"flex", gap:6, alignItems:"flex-end", height:80 }}>
              {porMes.map((m,i) => (
                <div key={i} style={{ flex:1, display:"flex", flexDirection:"column", alignItems:"center", gap:3 }}>
                  <div style={{ width:"100%", display:"flex", flexDirection:"column", justifyContent:"flex-end", height:60, position:"relative" }}>
                    <div style={{ width:"100%", height:`${(m.total/maxVal)*100}%`, background:COLORS.primaryBg, borderRadius:"4px 4px 0 0", minHeight: m.total>0?4:0 }} />
                    <div style={{ position:"absolute", bottom:0, width:"100%", height:`${(m.recebido/maxVal)*100}%`, background:COLORS.primary, borderRadius:"4px 4px 0 0" }} />
                  </div>
                  <div style={{ fontSize:9, color:COLORS.textMid, textAlign:"center" }}>{MESES[i]}</div>
                </div>
              ))}
            </div>
          );
        })()}
        <div style={{ display:"flex", gap:16, marginTop:10, fontSize:11, color:COLORS.textMid }}>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}><span style={{ width:10, height:10, borderRadius:2, background:COLORS.primary, display:"inline-block" }} /> Recebido</span>
          <span style={{ display:"flex", alignItems:"center", gap:4 }}><span style={{ width:10, height:10, borderRadius:2, background:COLORS.primaryBg, display:"inline-block" }} /> Previsto</span>
        </div>
      </Card>
    </div>
  );
}

// ─── PASTA DE DOCUMENTOS DO CONTRATO ─────────────────────────────────────────
function ContratoDocumentos({ token, contrato }) {
  const [itens, setItens] = useState([]);
  const [caminho, setCaminho] = useState([]); // [{id, nome}] — trilha de pastas
  const [carregando, setCarregando] = useState(false);
  const [novaPastaNome, setNovaPastaNome] = useState("");
  const [criandoPasta, setCriandoPasta] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const uploadRef = useRef(null);

  const paiAtual = caminho.length > 0 ? caminho[caminho.length - 1].id : null;
  const nomeContrato = contrato.numero ? `Contrato ${contrato.numero}` : `Contrato #${contrato.id}`;
  const nomeCliente = contrato.cliente_nome || "Cliente";

  const carregar = () => {
    setCarregando(true);
    const q = paiAtual != null ? `?pai_id=${paiAtual}` : "";
    apiFetch("GET", `/api/contratos/contratos/${contrato.id}/pastas${q}`, undefined, token)
      .then(r => setItens(Array.isArray(r) ? r : []))
      .catch(() => setItens([]))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { carregar(); }, [paiAtual, contrato.id]);

  const entrarPasta = (item) => setCaminho(c => [...c, { id: item.id, nome: item.nome }]);
  const voltarPara = (idx) => setCaminho(c => c.slice(0, idx + 1));
  const voltarRaiz = () => setCaminho([]);

  const criarPasta = async () => {
    if (!novaPastaNome.trim()) return;
    setCriandoPasta(true);
    try {
      await apiFetch("POST", `/api/contratos/contratos/${contrato.id}/pastas`, { nome: novaPastaNome.trim(), pai_id: paiAtual }, token);
      setNovaPastaNome("");
      carregar();
    } catch(e) { setMsg("Erro: " + e.message); setTimeout(() => setMsg(""), 4000); }
    finally { setCriandoPasta(false); }
  };

  const excluirItem = async (item) => {
    const aviso = item.tipo === "pasta"
      ? `Excluir a pasta "${item.nome}" e todo o seu conteúdo?`
      : `Excluir o arquivo "${item.nome}"?`;
    if (!window.confirm(aviso)) return;
    try {
      await apiFetch("DELETE", `/api/contratos/contratos/${contrato.id}/pastas/${item.id}`, undefined, token);
      carregar();
    } catch(e) { setMsg("Erro: " + e.message); setTimeout(() => setMsg(""), 4000); }
  };

  const fazerUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      if (paiAtual != null) fd.append("pai_id", paiAtual);
      const resp = await fetch(`/api/contratos/contratos/${contrato.id}/pastas/upload`, {
        method: "POST",
        headers: { Authorization: `Bearer ${token}` },
        body: fd,
      });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.erro || resp.status); }
      setMsg("✅ Arquivo enviado.");
      setTimeout(() => setMsg(""), 3000);
      carregar();
    } catch(e) { setMsg("Erro ao enviar: " + e.message); setTimeout(() => setMsg(""), 4000); }
    finally { setUploading(false); if (uploadRef.current) uploadRef.current.value = ""; }
  };

  const fmtTamanho = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/1048576).toFixed(1)} MB`;
  };

  const breadcrumb = [{ id: null, nome: nomeCliente }, { id: "c", nome: nomeContrato }, ...caminho];

  return (
    <Card>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
        <span style={{ fontSize:15, fontWeight:700 }}>📁 Pasta do Contrato</span>
        {msg && <span style={{ fontSize:12, color: msg.startsWith("✅") ? "#16a34a" : COLORS.danger, marginLeft:8 }}>{msg}</span>}
      </div>

      {/* Breadcrumb */}
      <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap", marginBottom:12, fontSize:12 }}>
        {breadcrumb.map((b, i) => {
          const isLast = i === breadcrumb.length - 1;
          return (
            <span key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
              {i > 0 && <span style={{ color:COLORS.textLight }}>›</span>}
              {isLast ? (
                <span style={{ fontWeight:700, color:COLORS.text }}>{b.nome}</span>
              ) : (
                <button onClick={() => { if (i === 0 || i === 1) voltarRaiz(); else voltarPara(i - 2); }}
                  style={{ background:"none", border:"none", color:COLORS.primary, cursor:"pointer", padding:0, fontSize:12, fontWeight:600 }}>
                  {b.nome}
                </button>
              )}
            </span>
          );
        })}
      </div>

      {/* Toolbar */}
      <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12, flexWrap:"wrap" }}>
        <input value={novaPastaNome} onChange={e => setNovaPastaNome(e.target.value)}
          onKeyDown={e => e.key === "Enter" && criarPasta()}
          placeholder="Nome da nova pasta..."
          style={{ padding:"6px 10px", borderRadius:8, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text, width:180 }} />
        <Btn size="sm" onClick={criarPasta} disabled={!novaPastaNome.trim() || criandoPasta}>{criandoPasta ? "..." : "📁 Criar pasta"}</Btn>
        <input ref={uploadRef} type="file" style={{ display:"none" }} onChange={e => fazerUpload(e.target.files[0])} />
        <Btn size="sm" variant="ghost" onClick={() => uploadRef.current?.click()} disabled={uploading}>
          {uploading ? "Enviando..." : "📎 Enviar arquivo"}
        </Btn>
        {caminho.length > 0 && (
          <Btn size="sm" variant="ghost" onClick={() => setCaminho(c => c.slice(0, -1))}>← Voltar</Btn>
        )}
      </div>

      {/* Conteúdo */}
      {carregando ? (
        <div style={{ color:COLORS.textMid, fontSize:13, padding:"20px 0", textAlign:"center" }}>Carregando...</div>
      ) : itens.length === 0 ? (
        <div style={{ color:COLORS.textLight, fontSize:13, padding:"20px 0", textAlign:"center" }}>
          {caminho.length === 0 ? "Pasta vazia. Crie subpastas ou envie arquivos." : "Pasta vazia."}
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:8 }}>
          {itens.map(item => (
            <div key={item.id}
              onClick={() => item.tipo === "pasta" && entrarPasta(item)}
              style={{
                background:COLORS.bg, borderRadius:10, border:`1px solid ${COLORS.border}`,
                padding:"12px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:6,
                cursor: item.tipo === "pasta" ? "pointer" : "default",
                transition:"box-shadow .15s",
                position:"relative",
              }}
              onMouseEnter={e => { if (item.tipo === "pasta") e.currentTarget.style.boxShadow = "0 2px 12px #0002"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ fontSize:32 }}>{item.tipo === "pasta" ? "📁" : "📄"}</div>
              <div style={{ fontSize:11, fontWeight:600, textAlign:"center", wordBreak:"break-word", color:COLORS.text, lineHeight:1.3 }}>{item.nome}</div>
              {item.tipo === "arquivo" && item.tamanho > 0 && (
                <div style={{ fontSize:10, color:COLORS.textLight }}>{fmtTamanho(item.tamanho)}</div>
              )}
              <div style={{ display:"flex", gap:4, marginTop:4 }}>
                {item.tipo === "arquivo" && (
                  <a href={`/api/contratos/contratos/${contrato.id}/pastas/${item.id}/download`}
                    target="_blank" rel="noreferrer" style={{ textDecoration:"none" }}
                    onClick={e => e.stopPropagation()}>
                    <Btn size="sm" variant="ghost" style={{ padding:"2px 8px", fontSize:11 }}>⬇</Btn>
                  </a>
                )}
                <Btn size="sm" variant="danger" onClick={e => { e.stopPropagation(); excluirItem(item); }}
                  style={{ padding:"2px 8px", fontSize:11 }}>✕</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── PASTA DE DOCUMENTOS DO CLIENTE ──────────────────────────────────────────
function ClienteDocumentos({ token, cliente }) {
  const [itens, setItens] = useState([]);
  const [caminho, setCaminho] = useState([]);
  const [carregando, setCarregando] = useState(false);
  const [novaPastaNome, setNovaPastaNome] = useState("");
  const [criandoPasta, setCriandoPasta] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [msg, setMsg] = useState("");
  const uploadRef = useRef(null);

  const paiAtual = caminho.length > 0 ? caminho[caminho.length - 1].id : null;
  const nomeCliente = cliente.nome_fantasia || cliente.nome || `Cliente #${cliente.id}`;

  const carregar = () => {
    setCarregando(true);
    const q = paiAtual != null ? `?pai_id=${paiAtual}` : "";
    apiFetch("GET", `/api/contratos/clientes/${cliente.id}/pastas${q}`, undefined, token)
      .then(r => setItens(Array.isArray(r) ? r : []))
      .catch(() => setItens([]))
      .finally(() => setCarregando(false));
  };

  useEffect(() => { carregar(); }, [paiAtual, cliente.id]);

  const entrarPasta = (item) => setCaminho(c => [...c, { id: item.id, nome: item.nome }]);
  const voltarPara = (idx) => setCaminho(c => c.slice(0, idx + 1));
  const voltarRaiz = () => setCaminho([]);

  const criarPasta = async () => {
    if (!novaPastaNome.trim()) return;
    setCriandoPasta(true);
    try {
      await apiFetch("POST", `/api/contratos/clientes/${cliente.id}/pastas`, { nome: novaPastaNome.trim(), pai_id: paiAtual }, token);
      setNovaPastaNome(""); carregar();
    } catch(e) { setMsg("Erro: " + e.message); setTimeout(() => setMsg(""), 4000); }
    finally { setCriandoPasta(false); }
  };

  const excluirItem = async (item) => {
    const aviso = item.tipo === "pasta" ? `Excluir a pasta "${item.nome}" e todo o seu conteúdo?` : `Excluir o arquivo "${item.nome}"?`;
    if (!window.confirm(aviso)) return;
    try {
      await apiFetch("DELETE", `/api/contratos/clientes/${cliente.id}/pastas/${item.id}`, undefined, token);
      carregar();
    } catch(e) { setMsg("Erro: " + e.message); setTimeout(() => setMsg(""), 4000); }
  };

  const fazerUpload = async (file) => {
    if (!file) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("arquivo", file);
      if (paiAtual != null) fd.append("pai_id", paiAtual);
      const resp = await fetch(`/api/contratos/clientes/${cliente.id}/pastas/upload`, {
        method: "POST", headers: { Authorization: `Bearer ${token}` }, body: fd,
      });
      if (!resp.ok) { const e = await resp.json().catch(() => ({})); throw new Error(e.erro || resp.status); }
      setMsg("✅ Arquivo enviado."); setTimeout(() => setMsg(""), 3000); carregar();
    } catch(e) { setMsg("Erro ao enviar: " + e.message); setTimeout(() => setMsg(""), 4000); }
    finally { setUploading(false); if (uploadRef.current) uploadRef.current.value = ""; }
  };

  const fmtTamanho = (b) => {
    if (!b) return "";
    if (b < 1024) return `${b} B`;
    if (b < 1048576) return `${(b/1024).toFixed(1)} KB`;
    return `${(b/1048576).toFixed(1)} MB`;
  };

  const breadcrumb = [{ id: null, nome: nomeCliente }, ...caminho];

  return (
    <Card style={{ marginTop:16 }}>
      <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:12 }}>
        <span style={{ fontSize:15, fontWeight:700 }}>📁 Pastas do Cliente</span>
        {msg && <span style={{ fontSize:12, color: msg.startsWith("✅") ? "#16a34a" : COLORS.danger, marginLeft:8 }}>{msg}</span>}
      </div>
      <div style={{ display:"flex", alignItems:"center", gap:4, flexWrap:"wrap", marginBottom:12, fontSize:12 }}>
        {breadcrumb.map((b, i) => {
          const isLast = i === breadcrumb.length - 1;
          return (
            <span key={i} style={{ display:"flex", alignItems:"center", gap:4 }}>
              {i > 0 && <span style={{ color:COLORS.textLight }}>›</span>}
              {isLast ? (
                <span style={{ fontWeight:700, color:COLORS.text }}>{b.nome}</span>
              ) : (
                <button onClick={() => i === 0 ? voltarRaiz() : voltarPara(i - 1)}
                  style={{ background:"none", border:"none", color:COLORS.primary, cursor:"pointer", padding:0, fontSize:12, fontWeight:600 }}>
                  {b.nome}
                </button>
              )}
            </span>
          );
        })}
      </div>
      <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:12, flexWrap:"wrap" }}>
        <input value={novaPastaNome} onChange={e => setNovaPastaNome(e.target.value)}
          onKeyDown={e => e.key === "Enter" && criarPasta()}
          placeholder="Nome da nova pasta..."
          style={{ padding:"6px 10px", borderRadius:8, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:13, color:COLORS.text, width:180 }} />
        <Btn size="sm" onClick={criarPasta} disabled={!novaPastaNome.trim() || criandoPasta}>{criandoPasta ? "..." : "📁 Criar pasta"}</Btn>
        <input ref={uploadRef} type="file" style={{ display:"none" }} onChange={e => fazerUpload(e.target.files[0])} />
        <Btn size="sm" variant="ghost" onClick={() => uploadRef.current?.click()} disabled={uploading}>
          {uploading ? "Enviando..." : "📎 Enviar arquivo"}
        </Btn>
        {caminho.length > 0 && (
          <Btn size="sm" variant="ghost" onClick={() => setCaminho(c => c.slice(0, -1))}>← Voltar</Btn>
        )}
      </div>
      {carregando ? (
        <div style={{ color:COLORS.textMid, fontSize:13, padding:"20px 0", textAlign:"center" }}>Carregando...</div>
      ) : itens.length === 0 ? (
        <div style={{ color:COLORS.textLight, fontSize:13, padding:"20px 0", textAlign:"center" }}>
          {caminho.length === 0 ? "Pasta vazia. Crie subpastas ou envie arquivos." : "Pasta vazia."}
        </div>
      ) : (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, minmax(140px, 1fr))", gap:8 }}>
          {itens.map(item => (
            <div key={item.id}
              onClick={() => item.tipo === "pasta" && entrarPasta(item)}
              style={{ background:COLORS.bg, borderRadius:10, border:`1px solid ${COLORS.border}`,
                padding:"12px 10px", display:"flex", flexDirection:"column", alignItems:"center", gap:6,
                cursor: item.tipo === "pasta" ? "pointer" : "default", transition:"box-shadow .15s", position:"relative" }}
              onMouseEnter={e => { if (item.tipo === "pasta") e.currentTarget.style.boxShadow = "0 2px 12px #0002"; }}
              onMouseLeave={e => { e.currentTarget.style.boxShadow = "none"; }}>
              <div style={{ fontSize:32 }}>{item.tipo === "pasta" ? "📁" : "📄"}</div>
              <div style={{ fontSize:11, fontWeight:600, textAlign:"center", wordBreak:"break-word", color:COLORS.text, lineHeight:1.3 }}>{item.nome}</div>
              {item.tipo === "arquivo" && item.tamanho > 0 && (
                <div style={{ fontSize:10, color:COLORS.textLight }}>{fmtTamanho(item.tamanho)}</div>
              )}
              <div style={{ display:"flex", gap:4, marginTop:4 }}>
                {item.tipo === "arquivo" && (
                  <a href={`/api/contratos/clientes/${cliente.id}/pastas/${item.id}/download`}
                    target="_blank" rel="noreferrer" style={{ textDecoration:"none" }} onClick={e => e.stopPropagation()}>
                    <Btn size="sm" variant="ghost" style={{ padding:"2px 8px", fontSize:11 }}>⬇</Btn>
                  </a>
                )}
                <Btn size="sm" variant="danger" onClick={e => { e.stopPropagation(); excluirItem(item); }}
                  style={{ padding:"2px 8px", fontSize:11 }}>✕</Btn>
              </div>
            </div>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── SERVIÇOS PRESET (compartilhado entre Contratos e Propostas) ──────────────
const SERVICOS_PRESET = [
  { descricao:"Auxiliar de Limpeza / Faxineira",      cnae:"8121-4/00" },
  { descricao:"Porteiro / Controlador de Acesso",      cnae:"8111-7/00" },
  { descricao:"Manobrista",                            cnae:"5229-0/01" },
  { descricao:"Recepcionista",                         cnae:"8299-7/07" },
  { descricao:"Operador de CFTV",                      cnae:"8020-0/01" },
  { descricao:"Zelador / Conservador Predial",         cnae:"8121-4/00" },
  { descricao:"Copeira",                               cnae:"5611-2/01" },
  { descricao:"Jardineiro / Paisagismo",               cnae:"8130-3/00" },
  { descricao:"Auxiliar Administrativo",               cnae:"8211-3/00" },
  { descricao:"Vigilante",                             cnae:"8011-1/01" },
  { descricao:"Encarregado de Limpeza",                cnae:"8121-4/00" },
  { descricao:"Supervisor de Operações",               cnae:"8121-4/00" },
  { descricao:"Auxiliar de Serviços Gerais",           cnae:"8121-4/00" },
  { descricao:"Lavador de Veículos",                   cnae:"4520-0/07" },
  { descricao:"Técnico em Segurança do Trabalho",      cnae:"7490-1/04" },
];

// ─── CONTRATOS & CLIENTES ─────────────────────────────────────────────────────
function Contratos({ token, setores, setSetores, abaInicial, novaPropostaMode, hideTabs, empresaFiltroId }) {
  const [confirmOpts, setConfirmOpts] = useState(null);
  const [aba, setAba] = useState(abaInicial || "contratos");
  const [propostasKey, setPropostasKey] = useState(0);
  const [propostasTelaInicial, setPropostasTelaInicial] = useState(novaPropostaMode ? "editor" : "lista");
  const [propostasModoInicial, setPropostasModoInicial] = useState(novaPropostaMode ? "contrato" : "proposta");
  const [contratoEditorAberto, setContratoEditorAberto] = useState(false);
  const [clientes, setClientes] = useState([]);
  const [contratos, setContratos] = useState([]);
  const [cobrancas, setCobrancas] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [configNfe, setConfigNfe] = useState({});
  const [clienteEditando, setClienteEditando] = useState(null);
  const [modalContrato, setModalContrato] = useState(null);
  const [modalSetor, setModalSetor] = useState(null); // { contratoId }
  const [modalNfe, setModalNfe] = useState(false);
  const [detalhe, setDetalhe] = useState(null); // contrato aberto
  const [salvando, setSalvando] = useState(false);
  const [msg, setMsg] = useState("");
  const [filtro, setFiltro] = useState("");
  const [mesCob, setMesCob] = useState(() => { const n = new Date(); return `${n.getFullYear()}-${String(n.getMonth()+1).padStart(2,'0')}`; });
  const [cobView, setCobView] = useState("calendario");
  const [cobAll, setCobAll] = useState([]);
  const [cobAno, setCobAno] = useState(() => new Date().getFullYear());
  const [nfePreview, setNfePreview] = useState(null);
  const [nfeForm, setNfeForm] = useState({});
  const [calTooltip, setCalTooltip] = useState(null);

  const [fcli, setFcli] = useState({ nome:"", cnpj:"", nome_fantasia:"", endereco:"", bairro:"", cep:"", cidade:"", uf:"", telefone:"", email:"", contato_nome:"", segmento:"", observacoes:"", inscricao_estadual:"", inscricao_municipal:"", simples_nacional:false, empresa_id:null });
  const [servicosDetalhe, setServicosDetalhe] = useState([]);
  const [fset, setFset] = useState({ nome:"", cargo:"", qtd_funcionarios:"1", turno:"Manhã", valor_unitario:"" });
  const [fNfe, setFNfe] = useState({ api_key:"", api_key_producao:"", cnpj_emitente:"", razao_social:"", municipio_codigo:"3106200", serie:"1", ambiente:"homologacao", inscricao_municipal:"", simples_nacional:"2", regime_especial:"0", aliquota_iss:"0", tipo_retencao_iss:"1", logradouro:"", numero_end:"", bairro:"", cep:"", ctrib_nac:"170602", ibs_cbs_cst:"040", ibs_cbs_class_trib:"1001000" });
  const [configInter, setConfigInter] = useState({});
  const [funcContrato, setFuncContrato] = useState([]);
  const [allEmployees, setAllEmployees] = useState([]);
  const [addFuncId, setAddFuncId] = useState("");
  const [aditivos, setAditivos] = useState([]);
  const [modalAditivo, setModalAditivo] = useState(null); // null | {} | {id,...}
  const [fAd, setFAd] = useState({ numero:"", objeto:"", data_assinatura:"", qtd_colaboradores:"", funcoes:"", turno:"", valor_mensal:"", status:"ativo", observacoes:"" });
  const [salvandoAd, setSalvandoAd] = useState(false);
  const [loadingFunc, setLoadingFunc] = useState(false);
  const [fDetalhe, setFDetalhe] = useState({});
  const [salvandoDetalhe, setSalvandoDetalhe] = useState(false);
  const [docsContrato, setDocsContrato] = useState([]);
  const [issCodigos, setIssCodigos] = useState([]);
  const [issInput, setIssInput] = useState({ codigo:"", descricao:"" });
  const [filtroCliNome, setFiltroCliNome] = useState("");
  const [filtroCliSegmento, setFiltroCliSegmento] = useState("");
  const [filtroCliCidade, setFiltroCliCidade] = useState("");
  const [filtroContStatus, setFiltroContStatus] = useState("");
  const [filtroContCliente, setFiltroContCliente] = useState("");

  const fmt = (v) => `R$ ${parseFloat(v||0).toLocaleString("pt-BR",{minimumFractionDigits:2,maximumFractionDigits:2})}`;
  const STATUS_COLOR = { ativo:"#16a34a", encerrado:COLORS.danger, suspenso:COLORS.warning, proposta:COLORS.info };
  const STATUS_COB_COLOR = { pendente:COLORS.warning, pago:"#16a34a", vencido:COLORS.danger, cancelado:COLORS.textMid };

  const carregar = () => {
    apiFetch("GET", "/api/contratos/clientes", undefined, token).then(r => setClientes(Array.isArray(r)?r:[])).catch(()=>{});
    apiFetch("GET", "/api/contratos/contratos", undefined, token).then(r => setContratos(Array.isArray(r)?r:[])).catch(()=>{});
    apiFetch("GET", "/api/contratos/config-nfe", undefined, token).then(r => { if(r?.id) setFNfe(prev => ({...prev,...r})); setConfigNfe(r||{}); }).catch(()=>{});
    apiFetch("GET", "/api/contratos/config-inter", undefined, token).then(r => { setConfigInter(r||{}); }).catch(()=>{});
    apiFetch("GET", "/api/empresas", undefined, token).then(r => setEmpresas(Array.isArray(r)?r:[])).catch(()=>{});
  };
  useEffect(() => { if(token) carregar(); }, [token]);
  useEffect(() => {
    if(token) apiFetch("GET", "/api/funcionarios", undefined, token).then(r => setAllEmployees(Array.isArray(r)?r:[])).catch(()=>{});
  }, [token]);

  const carregarFuncContrato = async (contratoId) => {
    setLoadingFunc(true);
    try {
      const r = await apiFetch("GET", `/api/contratos/contratos/${contratoId}/funcionarios`, undefined, token);
      setFuncContrato(Array.isArray(r)?r:[]);
    } catch(e) {}
    setLoadingFunc(false);
  };

  const carregarAditivos = async (contratoId) => {
    try {
      const r = await apiFetch("GET", `/api/contratos/contratos/${contratoId}/aditivos`, undefined, token);
      setAditivos(Array.isArray(r)?r:[]);
    } catch(e) {}
  };

  const salvarAditivo = async () => {
    setSalvandoAd(true);
    try {
      if (modalAditivo?.id) {
        await apiFetch("PUT", `/api/contratos/contratos/${detalhe.id}/aditivos/${modalAditivo.id}`, fAd, token);
      } else {
        await apiFetch("POST", `/api/contratos/contratos/${detalhe.id}/aditivos`, fAd, token);
      }
      await carregarAditivos(detalhe.id);
      setModalAditivo(null);
    } catch(e) { alert(e.message); }
    setSalvandoAd(false);
  };

  const excluirAditivo = (adId) => {
    setConfirmOpts({ msg: "Excluir este aditivo?", onOk: async () => {
      try {
        await apiFetch("DELETE", `/api/contratos/contratos/${detalhe.id}/aditivos/${adId}`, undefined, token);
        setAditivos(prev => prev.filter(a => a.id !== adId));
      } catch(e) { alert(e.message); }
    }});
  };

  const [anoMes] = mesCob.split('-').map(Number);
  const mesNum = mesCob.split('-').map(Number)[1];
  const anoNum = mesCob.split('-').map(Number)[0];

  useEffect(() => {
    if(!token || cobView !== "lista") return;
    const [y,m] = mesCob.split('-');
    apiFetch("GET", `/api/contratos/cobrancas?mes=${parseInt(m)}&ano=${y}`, undefined, token)
      .then(r => setCobrancas(Array.isArray(r)?r:[]))
      .catch(()=>{});
  }, [token, mesCob, cobView]);

  useEffect(() => {
    if(!token || cobView !== "calendario") return;
    apiFetch("GET", `/api/contratos/cobrancas?ano=${cobAno}`, undefined, token)
      .then(r => setCobAll(Array.isArray(r)?r:[]))
      .catch(()=>{});
  }, [token, cobView, cobAno]);

  const salvarCliente = async () => {
    if (!fcli.nome) return alert("Informe o nome do cliente.");
    setSalvando(true);
    try {
      if (clienteEditando?.id) {
        await apiFetch("PUT", `/api/contratos/clientes/${clienteEditando.id}`, fcli, token);
        carregar(); setClienteEditando(null);
      } else {
        const lista2 = await apiFetch("POST", "/api/contratos/clientes", fcli, token);
        const lista = await apiFetch("GET", "/api/contratos/clientes", undefined, token);
        if (Array.isArray(lista)) setClientes(lista);
        setClienteEditando(null);
      }
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const apagarCliente = (id) => {
    setConfirmOpts({ msg: "Apagar cliente? Os contratos vinculados também serão removidos.", onOk: async () => {
      try { await apiFetch("DELETE", `/api/contratos/clientes/${id}`, undefined, token); carregar(); } catch(e) { alert(e.message); }
    }});
  };

  const carregarDocs = async (contratoId) => {
    try {
      const r = await apiFetch("GET", `/api/contratos/contratos/${contratoId}/documentos`, undefined, token);
      setDocsContrato(Array.isArray(r)?r:[]);
    } catch(e) {}
  };

  const abrirDetalhe = async (cont) => {
    try {
      const d = await apiFetch("GET", `/api/contratos/contratos/${cont.id}`, undefined, token);
      setDetalhe(d);
      setFDetalhe({
        numero: d.numero||"", objeto: d.objeto||"", data_inicio: d.data_inicio?.slice(0,10)||"",
        data_fim: d.data_fim?.slice(0,10)||"", valor_mensal: d.valor_mensal||"", dia_vencimento: d.dia_vencimento||"10",
        status: d.status||"ativo", local: d.local||"", observacoes: d.observacoes||"",
        cliente_id: d.cliente_id||"",
        nfe_ctrib_nac: d.nfe_ctrib_nac||"", nfe_aliquota_iss: d.nfe_aliquota_iss??'',
        nfe_tipo_retencao_iss: d.nfe_tipo_retencao_iss??'', nfe_descricao_padrao: d.nfe_descricao_padrao||"",
        local_logradouro: d.local_logradouro||"", local_numero: d.local_numero||"",
        local_complemento: d.local_complemento||"", local_bairro: d.local_bairro||"",
        local_cidade: d.local_cidade||"", local_uf: d.local_uf||"", local_cep: d.local_cep||"",
        forma_pagamento: d.forma_pagamento||"pix",
        reajuste_indice: d.reajuste_indice||"IGPM",
        reajuste_percentual: d.reajuste_percentual??'',
        prazo_aviso_rescisao: d.prazo_aviso_rescisao??'30',
        multa_rescisao: d.multa_rescisao??'',
      });
      try {
        const svcs = d.servicos_json ? JSON.parse(d.servicos_json) : [];
        setServicosDetalhe(svcs.length > 0 ? svcs : []);
      } catch(_) { setServicosDetalhe([]); }
      setAddFuncId("");
      carregarFuncContrato(cont.id);
      carregarAditivos(cont.id);
      carregarDocs(cont.id);
    } catch(e) { alert(e.message); }
  };

  const salvarDetalheContrato = () => {
    setConfirmOpts({ msg: "Confirmar alterações no contrato? As informações serão atualizadas.", onOk: async () => {
      setSalvandoDetalhe(true);
      try {
        const fd = new FormData();
        Object.entries(fDetalhe).forEach(([k,v]) => { if(v !== null && v !== undefined) fd.append(k, v); });
        const servicosValidos = servicosDetalhe.filter(s => s.descricao?.trim());
        fd.append("servicos_json", servicosValidos.length > 0 ? JSON.stringify(servicosValidos) : "");
        const res = await fetch(`/api/contratos/contratos/${detalhe.id}`, { method:"PUT", headers:{ Authorization:`Bearer ${token}` }, body:fd });
        const data = await res.json();
        if (!res.ok) throw new Error(data.erro || "Erro ao salvar");
        const d = await apiFetch("GET", `/api/contratos/contratos/${detalhe.id}`, undefined, token);
        setDetalhe(d);
        try { setServicosDetalhe(d.servicos_json ? JSON.parse(d.servicos_json) : []); } catch(_) {}
        setMsg("✅ Contrato atualizado!");
        setTimeout(()=>setMsg(""),3000);
        carregar();
      } catch(e) { alert(e.message); }
      setSalvandoDetalhe(false);
    }});
  };

  const salvarSetor = async () => {
    if (!fset.nome) return alert("Informe o nome do setor/posto.");
    setSalvando(true);
    try {
      await apiFetch("POST", `/api/contratos/contratos/${modalSetor.contratoId}/setores`, fset, token);
      // Recarrega detalhe
      const d = await apiFetch("GET", `/api/contratos/contratos/${modalSetor.contratoId}`, undefined, token);
      setDetalhe(d);
      // Atualiza setores globais
      apiFetch("GET", "/api/setores", undefined, token).then(r => setSetores && setSetores(r||[])).catch(()=>{});
      setFset({ nome:"", cargo:"", qtd_funcionarios:"1", turno:"Manhã", valor_unitario:"" });
      setMsg("✅ Setor criado e área de ponto adicionada automaticamente!");
      setTimeout(() => setMsg(""), 3000);
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const removerSetor = async (contratoId, setorId) => {
    if (!window.confirm("Remover este posto/setor do contrato?")) return;
    try {
      await apiFetch("DELETE", `/api/contratos/contratos/${contratoId}/setores/${setorId}`, undefined, token);
      const d = await apiFetch("GET", `/api/contratos/contratos/${contratoId}`, undefined, token);
      setDetalhe(d);
    } catch(e) { alert(e.message); }
  };

  const atualizarCobranca = async (id, campos) => {
    try {
      await apiFetch("PATCH", `/api/contratos/cobrancas/${id}`, campos, token);
      setCobrancas(prev => prev.map(c => c.id === id ? { ...c, ...campos } : c));
    } catch(e) { alert(e.message); }
  };

  const emitirNfe = async (cobId, formData) => {
    setSalvando(true);
    try {
      const r = await apiFetch("POST", `/api/contratos/cobrancas/${cobId}/emitir-nfe`, formData||{}, token);
      const msg = r.status === 'processando'
        ? `⏳ NFS-e enviada ao FocusNFE — processando. Clique em 🔄 para consultar o status.`
        : `✅ NFS-e autorizada! Número: ${r.numero}`;
      setMsg(msg);
      setTimeout(() => setMsg(""), 8000);
      setCobrancas(prev => prev.map(c => c.id === cobId ? { ...c, nfe_ref: r.ref, nfe_numero: r.numero, nfe_status: r.status } : c));
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const consultarNfe = async (cobId) => {
    setSalvando(true);
    try {
      const r = await apiFetch("GET", `/api/contratos/cobrancas/${cobId}/consultar-nfe`, undefined, token);
      setMsg(r.status === 'autorizado' ? `✅ NFS-e autorizada! Nº ${r.numero}` : `Status: ${r.status}`);
      setTimeout(() => setMsg(""), 5000);
      setCobrancas(prev => prev.map(c => c.id === cobId ? { ...c, nfe_numero: r.numero, nfe_status: r.status } : c));
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const salvarConfigNfe = async () => {
    setSalvando(true);
    try {
      await apiFetch("POST", "/api/contratos/config-nfe", fNfe, token);
      setMsg("✅ Configuração salva!");
      setTimeout(() => setMsg(""), 2500);
      setModalNfe(false);
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const gerarCobrancas = async (contratoId) => {
    setSalvando(true);
    try {
      const r = await apiFetch("POST", `/api/contratos/contratos/${contratoId}/gerar-cobrancas`, {}, token);
      setMsg(`✅ ${r.geradas} cobrança(s) gerada(s)! Redirecionando...`);
      setTimeout(() => setMsg(""), 4000);
      // Navega para o mês de início do contrato
      const dataIni = detalhe?.data_inicio || new Date().toISOString().slice(0,7)+"-01";
      const [y,m] = dataIni.slice(0,7).split('-');
      const novoMes = `${y}-${m.padStart(2,'0')}`;
      setMesCob(novoMes);
      setCobAno(parseInt(y));
      setCobView("lista");
      const rows = await apiFetch("GET", `/api/contratos/cobrancas?mes=${parseInt(m)}&ano=${y}`, undefined, token);
      setCobrancas(Array.isArray(rows)?rows:[]);
      setDetalhe(null);
      setAba("cobrancas");
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const gerarBoleto = async (cobId) => {
    setSalvando(true);
    try {
      const r = await apiFetch("POST", `/api/contratos/cobrancas/${cobId}/gerar-boleto`, {}, token);
      setMsg("✅ Boleto gerado!");
      setTimeout(()=>setMsg(""),3000);
      setCobrancas(prev => prev.map(c => c.id===cobId ? {...c, boleto_id:r.boleto_id, boleto_url:r.boleto_url, boleto_codigo:r.codigo, boleto_status:'emitido'} : c));
      if (r.boleto_url) window.open(r.boleto_url,"_blank");
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const verificarPagamento = async (cobId) => {
    setSalvando(true);
    try {
      const r = await apiFetch("POST", `/api/contratos/cobrancas/${cobId}/verificar-pagamento`, {}, token);
      if (r.pago) {
        setMsg("✅ Pagamento confirmado pelo banco!");
        setCobrancas(prev => prev.map(c => c.id===cobId ? {...c, boleto_status:'pago', status:'pago'} : c));
      } else {
        setMsg(`Status no banco: ${r.situacao||"Em aberto"}`);
      }
      setTimeout(()=>setMsg(""),4000);
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const enviarEmail = async (cobId) => {
    setSalvando(true);
    try {
      const r = await apiFetch("POST", `/api/contratos/cobrancas/${cobId}/enviar-email`, {}, token);
      setMsg(r.aviso || `✅ E-mail enviado para ${r.email}`);
      setTimeout(()=>setMsg(""),5000);
      setCobrancas(prev => prev.map(c => c.id===cobId ? {...c, email_enviado_em: new Date().toISOString()} : c));
    } catch(e) { alert(e.message); }
    setSalvando(false);
  };

  const contratosFiltrados = contratos.filter(c => {
    if (filtro && !c.cliente_nome?.toLowerCase().includes(filtro.toLowerCase()) && !c.numero?.includes(filtro) && !c.objeto?.toLowerCase().includes(filtro.toLowerCase())) return false;
    if (filtroContStatus && c.status !== filtroContStatus) return false;
    if (filtroContCliente && String(c.cliente_id) !== String(filtroContCliente)) return false;
    return true;
  });

  const totalMes = cobrancas.reduce((s,c) => s + parseFloat(c.valor||0), 0);
  const totalPago = cobrancas.filter(c=>c.status==='pago').reduce((s,c) => s + parseFloat(c.valor||0), 0);
  const totalPendente = cobrancas.filter(c=>c.status==='pendente'||c.status==='vencido').reduce((s,c) => s + parseFloat(c.valor||0), 0);

  const ABAS_CONT = [
    { id:"propostas", label:"📄 Propostas" },
    { id:"contratos", label:"📋 Contratos" },
    { id:"clientes",  label:"👥 Clientes" },
    { id:"cobrancas", label:"💰 Cobranças" },
  ];


  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {confirmOpts && <ConfirmModal msg={confirmOpts.msg} onOk={confirmOpts.onOk} onClose={() => setConfirmOpts(null)} />}
      {/* Abas internas — só mostra quando NÃO veio de rota direta da sidebar */}
      {!hideTabs && <div style={{ display:"flex", gap:4, borderBottom:`2px solid ${COLORS.border}`, paddingBottom:0 }}>
        {ABAS_CONT.map(a => (
          <button key={a.id} onClick={() => { setAba(a.id); setDetalhe(null); }}
            style={{ padding:"8px 18px", border:"none", borderBottom: aba===a.id ? `3px solid ${COLORS.primary}` : "3px solid transparent",
              background:"none", cursor:"pointer", fontWeight: aba===a.id ? 700:400, color: aba===a.id ? COLORS.primary : COLORS.textMid, fontSize:13 }}>
            {a.label}
          </button>
        ))}
        <div style={{ marginLeft:"auto", display:"flex", gap:8, alignItems:"center" }}>
          {msg && <span style={{ fontSize:12, color:"#16a34a", fontWeight:700 }}>{msg}</span>}
        </div>
      </div>}
      {hideTabs && msg && <div style={{ fontSize:12, color:"#16a34a", fontWeight:700, marginBottom:4 }}>{msg}</div>}

      {/* Modal Config NFS-e — FocusNFE DPS */}
      {modalNfe && (() => {
        const lbl = { fontSize:11, fontWeight:600, color:COLORS.textMid, display:"block", marginBottom:3 };
        const inp = { width:"100%", padding:"8px 10px", borderRadius:8, border:`1px solid ${COLORS.border}`, background:COLORS.bg, fontSize:13, color:COLORS.text, boxSizing:"border-box" };
        const F = (label, key, opts={}) => (
          <div style={opts.full?{gridColumn:"1/-1"}:{}}>
            <label style={lbl}>{label}</label>
            <input value={fNfe[key]||""} onChange={e=>setFNfe(f=>({...f,[key]:e.target.value}))} style={{ ...inp, ...(opts.style||{}) }} placeholder={opts.ph||""} />
          </div>
        );
        const S = (label, key, options) => (
          <div>
            <label style={lbl}>{label}</label>
            <select value={fNfe[key]||""} onChange={e=>setFNfe(f=>({...f,[key]:e.target.value}))} style={inp}>
              {options.map(([v,l])=><option key={v} value={v}>{l}</option>)}
            </select>
          </div>
        );
        return (
          <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center" }}
            onClick={e=>{ if(e.target===e.currentTarget) setModalNfe(false); }}>
            <div style={{ background:COLORS.surface,borderRadius:14,padding:28,width:620,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 8px 40px #0004" }}>
              <div style={{ fontWeight:800,fontSize:16,marginBottom:4 }}>⚙️ Configuração NFS-e — FocusNFE</div>
              <div style={{ fontSize:12,color:COLORS.textMid,marginBottom:18 }}>Provedor: FocusNFE · Padrão NFS-e Nacional (DPS)</div>

              {/* Ambiente & Tokens */}
              <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>1. Ambiente e Tokens</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr",gap:8,marginBottom:14 }}>
                <div>
                  <label style={lbl}>Ambiente</label>
                  <select value={fNfe.ambiente} onChange={e=>setFNfe(f=>({...f,ambiente:e.target.value}))} style={inp}>
                    <option value="homologacao">🧪 Homologação (testes)</option>
                    <option value="producao">✅ Produção</option>
                  </select>
                </div>
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8 }}>
                  {F("Token Homologação", "api_key", { ph:"HDDW5woipCO..." })}
                  {F("Token Produção", "api_key_producao", { ph:"vt9DRHTuXId..." })}
                </div>
              </div>

              {/* Emitente */}
              <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>2. Emitente (Prestador)</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14 }}>
                {F("CNPJ *", "cnpj_emitente", { ph:"65.451.642/0001-52" })}
                {F("Razão Social / Nome *", "razao_social", { ph:"Nome do MEI" })}
                {F("Inscrição Municipal", "inscricao_municipal", { ph:"Opcional" })}
                {F("Código IBGE Município *", "municipio_codigo", { ph:"3106200" })}
                {F("CEP", "cep", { ph:"30000-000" })}
                {F("Logradouro", "logradouro", { ph:"Rua/Av." })}
                {F("Número", "numero_end", { ph:"123" })}
                {F("Bairro", "bairro", { ph:"Centro" })}
                {F("Série DPS", "serie", { ph:"1" })}
              </div>

              {/* Tributação */}
              <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>3. Tributação ISS</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14 }}>
                {S("Simples Nacional *", "simples_nacional", [["1","Não optante"],["2","MEI"],["3","ME/EPP"]])}
                {S("Regime Especial Tributação *", "regime_especial", [["0","0 — Nenhum"],["1","1 — Ato Cooperado"],["2","2 — Estimativa"],["3","3 — Microempresa Municipal"],["4","4 — Notário/Registrador"],["5","5 — Profissional Autônomo"],["6","6 — Sociedade de Profissionais"],["9","9 — Outros"]])}
                {F("Cód. Tributação Nacional ISS (cTribNac 6 dígitos) *", "ctrib_nac", { ph:"170602", full:false })}
                {F("Alíquota ISS % (0 = DAS/MEI)", "aliquota_iss", { ph:"0" })}
                {S("Tipo Retenção ISS", "tipo_retencao_iss", [["1","1 — Não Retido (MEI/DAS)"],["2","2 — Retido pelo Tomador"],["3","3 — Retido pelo Intermediário"]])}
              </div>

              {/* IBS/CBS Reforma Tributária */}
              <div style={{ fontWeight:700,fontSize:12,color:COLORS.primary,marginBottom:8,borderBottom:`1px solid ${COLORS.border}`,paddingBottom:4 }}>4. IBS/CBS — Reforma Tributária</div>
              <div style={{ background:"#fef9c3",border:"1px solid #fde047",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#713f12",marginBottom:8 }}>
                Para MEI optante do Simples Nacional com ISS fixo no DAS, use CST <strong>040</strong> e classificação <strong>1001000</strong>.
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,marginBottom:14 }}>
                {F("CST IBS/CBS (3 dígitos)", "ibs_cbs_cst", { ph:"040" })}
                {F("Classificação Tributária IBS/CBS (7 dígitos)", "ibs_cbs_class_trib", { ph:"1001000" })}
              </div>

              <div style={{ display:"flex",gap:10,marginTop:4 }}>
                <Btn onClick={salvarConfigNfe} disabled={salvando}>{salvando?"Salvando...":"💾 Salvar Configuração"}</Btn>
                <Btn variant="ghost" onClick={()=>setModalNfe(false)}>Cancelar</Btn>
              </div>
            </div>
          </div>
        );
      })()}

      {/* Modal Preview NFS-e — editável */}
      {nfePreview && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1100,display:"flex",alignItems:"center",justifyContent:"center" }}
          onClick={e=>{ if(e.target===e.currentTarget) setNfePreview(null); }}>
          <div style={{ background:COLORS.surface,borderRadius:16,padding:28,width:580,maxHeight:"90vh",overflowY:"auto",boxShadow:"0 12px 48px #0004" }}>
            <div style={{ fontWeight:800,fontSize:17,marginBottom:2 }}>🧾 Prévia da Nota Fiscal</div>
            <div style={{ fontSize:12,color:COLORS.textMid,marginBottom:18 }}>Revise e edite os dados antes de emitir.</div>

            {/* Dados fixos */}
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:8,fontSize:13,background:COLORS.bg,borderRadius:10,padding:14,marginBottom:14 }}>
              <div><span style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:2 }}>Cliente / Tomador</span><strong>{nfePreview.cliente_nome||"—"}</strong></div>
              <div><span style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:2 }}>Contrato</span><strong>{nfePreview.contrato_numero ? `Nº ${nfePreview.contrato_numero}` : `#${nfePreview.contrato_id}`}</strong></div>
              <div><span style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:2 }}>Competência</span><strong>{String(nfePreview.mes).padStart(2,'0')}/{nfePreview.ano}</strong></div>
              <div style={{ display:"flex",alignItems:"flex-end",justifyContent:"flex-end" }}>
                <span style={{ fontSize:22,fontWeight:900,color:"#16a34a" }}>{fmt(nfePreview.valor)}</span>
              </div>
            </div>

            {/* CNPJ Tomador — editável + alerta se vazio */}
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:3 }}>CNPJ do Tomador (cliente)</label>
              <input value={nfeForm.cnpj_tomador||""} onChange={e=>setNfeForm(f=>({...f,cnpj_tomador:e.target.value}))}
                placeholder="00.000.000/0001-00"
                style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1.5px solid ${!nfeForm.cnpj_tomador?COLORS.danger:COLORS.border}`,background:COLORS.bg,fontSize:13,color:COLORS.text,boxSizing:"border-box" }} />
              {!nfeForm.cnpj_tomador && <div style={{ fontSize:11,color:COLORS.danger,marginTop:3 }}>⚠️ CNPJ não cadastrado. Informe aqui ou cadastre no cliente antes de emitir.</div>}
            </div>

            {/* Código tributação — editável, mostra de onde veio */}
            <div style={{ marginBottom:12 }}>
              <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:3 }}>
                Código Tributação Nacional ISS (cTribNac — 6 dígitos)
                {nfePreview.nfe_ctrib_nac ? <span style={{ fontWeight:400,color:COLORS.primary,marginLeft:6 }}>← do contrato: {nfePreview.nfe_ctrib_nac}</span> : (() => { let ic=[]; try{ic=nfePreview.iss_codigos?JSON.parse(nfePreview.iss_codigos):[]}catch(e){} return ic[0]?.codigo ? <span style={{ fontWeight:400,color:COLORS.primary,marginLeft:6 }}>← contrato (legado): {ic.map(c=>c.codigo).join(', ')}</span> : <span style={{ fontWeight:400,color:COLORS.textMid,marginLeft:6 }}>← config global</span>; })()}
              </label>
              <input value={nfeForm.ctrib_nac||""} onChange={e=>setNfeForm(f=>({...f,ctrib_nac:e.target.value.replace(/\D/g,'').slice(0,6)}))}
                placeholder="170602" maxLength={6}
                style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.bg,fontSize:13,color:COLORS.text,boxSizing:"border-box" }} />
            </div>

            {/* Descrição do serviço — editável */}
            <div style={{ marginBottom:16 }}>
              <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:3 }}>Discriminação / Descrição do Serviço</label>
              <textarea value={nfeForm.descricao_servico||""} onChange={e=>setNfeForm(f=>({...f,descricao_servico:e.target.value}))}
                rows={4} style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.bg,fontSize:13,color:COLORS.text,resize:"vertical",boxSizing:"border-box" }} />
            </div>

            <div style={{ display:"flex",gap:10 }}>
              <Btn onClick={async ()=>{ await emitirNfe(nfePreview.id, nfeForm); setNfePreview(null); }} disabled={salvando}>{salvando?"Emitindo...":"✅ Confirmar e Emitir NFS-e"}</Btn>
              <Btn variant="ghost" onClick={()=>setNfePreview(null)}>Cancelar</Btn>
            </div>
          </div>
        </div>
      )}

      {/* ABA: CLIENTES */}
      {aba === "clientes" && !detalhe && (
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          {clienteEditando !== null ? (
            <>
            <Card style={{ padding:24 }}>
              <div style={{ display:"flex",alignItems:"center",gap:10,marginBottom:20 }}>
                <button onClick={()=>setClienteEditando(null)} style={{ background:"none",border:"none",cursor:"pointer",color:COLORS.textMid,padding:"4px 6px",borderRadius:6,display:"flex",alignItems:"center",gap:4,fontSize:13 }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="15 18 9 12 15 6"/></svg>
                  Voltar
                </button>
                <div style={{ fontWeight:800,fontSize:16 }}>{clienteEditando?.id ? "Editar Cliente" : "Novo Cliente"}</div>
              </div>
              <div style={{ fontSize:11,fontWeight:700,color:COLORS.textMid,marginBottom:6,marginTop:2 }}>Identificação</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10 }}>
                <Input label="Razão Social *" value={fcli.nome} onChange={e=>setFcli(f=>({...f,nome:e.target.value}))} />
                <Input label="Nome Fantasia" value={fcli.nome_fantasia} onChange={e=>setFcli(f=>({...f,nome_fantasia:e.target.value}))} />
                <Input label="CNPJ" value={fcli.cnpj} onChange={e=>setFcli(f=>({...f,cnpj:e.target.value}))} placeholder="00.000.000/0001-00" />
                <div>
                  <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Segmento / Ramo</label>
                  <input value={fcli.segmento} onChange={e=>setFcli(f=>({...f,segmento:e.target.value}))}
                    placeholder="Ex: Condomínio Residencial, Automóveis..."
                    style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box",color:COLORS.text }} />
                </div>
                <div>
                  <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Empresa (qual empresa atende este cliente)</label>
                  <select value={fcli.empresa_id||""} onChange={e=>setFcli(f=>({...f,empresa_id:e.target.value||null}))}
                    style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box",color:COLORS.text }}>
                    <option value="">— Não definida —</option>
                    {empresas.map(e=><option key={e.id} value={e.id}>{e.razao_social||e.nome}</option>)}
                  </select>
                </div>
              </div>
              <div style={{ fontSize:11,fontWeight:700,color:COLORS.textMid,marginBottom:6 }}>Dados Fiscais</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10 }}>
                <Input label="Inscrição Estadual" value={fcli.inscricao_estadual} onChange={e=>setFcli(f=>({...f,inscricao_estadual:e.target.value}))} placeholder="Ex: ISENTO" />
                <Input label="Inscrição Municipal" value={fcli.inscricao_municipal} onChange={e=>setFcli(f=>({...f,inscricao_municipal:e.target.value}))} />
                <div style={{ display:"flex",alignItems:"center",gap:8,paddingTop:20 }}>
                  <input type="checkbox" id="chk-simples" checked={!!fcli.simples_nacional} onChange={e=>setFcli(f=>({...f,simples_nacional:e.target.checked}))} style={{ width:16,height:16,cursor:"pointer" }} />
                  <label htmlFor="chk-simples" style={{ fontSize:13,cursor:"pointer",color:COLORS.text,userSelect:"none" }}>Optante Simples Nacional</label>
                </div>
              </div>
              <div style={{ fontSize:11,fontWeight:700,color:COLORS.textMid,marginBottom:6 }}>Endereço</div>
              <div style={{ display:"grid",gridTemplateColumns:"2fr 1fr",gap:10,marginBottom:8 }}>
                <Input label="Logradouro / Endereço" value={fcli.endereco} onChange={e=>setFcli(f=>({...f,endereco:e.target.value}))} />
                <Input label="Bairro" value={fcli.bairro} onChange={e=>setFcli(f=>({...f,bairro:e.target.value}))} />
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 0.35fr 0.9fr",gap:10,marginBottom:10 }}>
                <Input label="Cidade" value={fcli.cidade} onChange={e=>setFcli(f=>({...f,cidade:e.target.value}))} />
                <Input label="CEP" value={fcli.cep} onChange={e=>setFcli(f=>({...f,cep:e.target.value}))} placeholder="00000-000" />
                <Input label="UF" value={fcli.uf} onChange={e=>setFcli(f=>({...f,uf:e.target.value.slice(0,2).toUpperCase()}))} placeholder="MG" />
                <Input label="Telefone" value={fcli.telefone} onChange={e=>setFcli(f=>({...f,telefone:e.target.value}))} />
              </div>
              <div style={{ fontSize:11,fontWeight:700,color:COLORS.textMid,marginBottom:6 }}>Contato</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10,marginBottom:10 }}>
                <Input label="Nome do Contato" value={fcli.contato_nome} onChange={e=>setFcli(f=>({...f,contato_nome:e.target.value}))} />
                <Input label="E-mail" value={fcli.email} onChange={e=>setFcli(f=>({...f,email:e.target.value}))} />
              </div>
              <div>
                <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Observações</label>
                <textarea value={fcli.observacoes} onChange={e=>setFcli(f=>({...f,observacoes:e.target.value}))}
                  rows={2} style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,resize:"vertical",boxSizing:"border-box" }} />
              </div>
              <div style={{ display:"flex",gap:10,marginTop:16 }}>
                <Btn onClick={salvarCliente} disabled={salvando}>{salvando?"Salvando...":"Salvar"}</Btn>
                <Btn variant="secondary" onClick={()=>setClienteEditando(null)}>Cancelar</Btn>
              </div>
            </Card>
            {clienteEditando?.id && (
              <ClienteDocumentos token={token} cliente={clienteEditando} />
            )}
            </>
          ) : (
            <>
              <div style={{ display:"flex",gap:10,alignItems:"flex-end",flexWrap:"wrap" }}>
                <Btn onClick={()=>{ setFcli({ nome:"",cnpj:"",nome_fantasia:"",endereco:"",bairro:"",cep:"",cidade:"",uf:"",telefone:"",email:"",contato_nome:"",segmento:"",observacoes:"",inscricao_estadual:"",inscricao_municipal:"",simples_nacional:false,empresa_id:null }); setClienteEditando({}); }}>+ Novo Cliente</Btn>
                <Input placeholder="Buscar por nome ou CNPJ..." value={filtroCliNome} onChange={e=>setFiltroCliNome(e.target.value)} style={{ minWidth:240 }} />
                <Select label="Segmento" value={filtroCliSegmento} onChange={e=>setFiltroCliSegmento(e.target.value)} style={{ width:200 }}>
                  <option value="">Todos os segmentos</option>
                  {[...new Set(clientes.filter(c=>c.segmento).map(c=>c.segmento))].sort().map(s=><option key={s} value={s}>{s}</option>)}
                </Select>
                <Select label="Cidade" value={filtroCliCidade} onChange={e=>setFiltroCliCidade(e.target.value)} style={{ width:180 }}>
                  <option value="">Todas as cidades</option>
                  {[...new Set(clientes.filter(c=>c.cidade).map(c=>c.cidade))].sort().map(c=><option key={c} value={c}>{c}</option>)}
                </Select>
                {(filtroCliNome||filtroCliSegmento||filtroCliCidade) && (
                  <Btn size="sm" variant="ghost" onClick={()=>{ setFiltroCliNome(""); setFiltroCliSegmento(""); setFiltroCliCidade(""); }}>✕ Limpar</Btn>
                )}
              </div>
              <Card style={{ padding:0,overflow:"hidden" }}>
                <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                  <thead><tr style={{ background:COLORS.bg }}>
                    {["Cliente","CNPJ","Empresa","Cidade","Contato","Segmento","Contratos","Ações"].map(h=>(
                      <th key={h} style={{ padding:"10px 14px",fontWeight:600,fontSize:11,color:COLORS.textMid,textAlign:"left" }}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {clientes.filter(c=>{
                      if (filtroCliNome && !c.nome.toLowerCase().includes(filtroCliNome.toLowerCase()) && !(c.cnpj||"").includes(filtroCliNome)) return false;
                      if (filtroCliSegmento && c.segmento !== filtroCliSegmento) return false;
                      if (filtroCliCidade && c.cidade !== filtroCliCidade) return false;
                      return true;
                    }).map((c,i)=>(
                      <tr key={c.id} style={{ background:i%2===0?COLORS.surface:COLORS.bg,borderBottom:`1px solid ${COLORS.border}` }}>
                        <td style={{ padding:"10px 14px",fontWeight:700 }}>
                          {c.nome_fantasia || c.nome}
                          {c.nome_fantasia && <div style={{ fontSize:11,color:COLORS.textMid,fontWeight:400 }}>{c.nome}</div>}
                        </td>
                        <td style={{ padding:"10px 14px",color:COLORS.textMid,fontSize:12 }}>
                          {c.cnpj||"—"}
                          {c.simples_nacional ? <div style={{ fontSize:10,color:"#16a34a",fontWeight:600 }}>Simples Nacional</div> : null}
                        </td>
                        <td style={{ padding:"10px 14px",fontSize:12,color:COLORS.textMid }}>
                          {c.empresa_id ? (empresas.find(e=>e.id===c.empresa_id)?.razao_social || empresas.find(e=>e.id===c.empresa_id)?.nome || "—") : "—"}
                        </td>
                        <td style={{ padding:"10px 14px",color:COLORS.textMid,fontSize:12 }}>
                          {c.cidade?`${c.cidade}/${c.uf}`:"—"}
                          {c.cep && <div style={{ fontSize:11 }}>CEP: {c.cep}</div>}
                        </td>
                        <td style={{ padding:"10px 14px",fontSize:12 }}>{c.contato_nome||"—"}{c.telefone&&<div style={{color:COLORS.textMid}}>{c.telefone}</div>}</td>
                        <td style={{ padding:"10px 14px",fontSize:12,color:COLORS.textMid }}>{c.segmento||"—"}</td>
                        <td style={{ padding:"10px 14px",textAlign:"center" }}>
                          <span style={{ fontWeight:700,color:COLORS.primary }}>{contratos.filter(ct=>ct.cliente_id===c.id).length}</span>
                        </td>
                        <td style={{ padding:"8px 14px",display:"flex",gap:4 }}>
                          <button title="Pastas" onClick={()=>{ setFcli({...c,simples_nacional:!!c.simples_nacional}); setClienteEditando(c); }}
                            style={{ background:"none",border:"none",cursor:"pointer",padding:"5px 7px",borderRadius:6,color:COLORS.textMid,display:"flex",alignItems:"center" }}
                            onMouseEnter={e=>e.currentTarget.style.color="#d97706"} onMouseLeave={e=>e.currentTarget.style.color=COLORS.textMid}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z"/></svg>
                          </button>
                          <button title="Editar" onClick={()=>{ setFcli({...c,simples_nacional:!!c.simples_nacional}); setClienteEditando(c); }}
                            style={{ background:"none",border:"none",cursor:"pointer",padding:"5px 7px",borderRadius:6,color:COLORS.textMid,display:"flex",alignItems:"center" }}
                            onMouseEnter={e=>e.currentTarget.style.color=COLORS.primary} onMouseLeave={e=>e.currentTarget.style.color=COLORS.textMid}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/><path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/></svg>
                          </button>
                          <button title="Excluir" onClick={()=>apagarCliente(c.id)}
                            style={{ background:"none",border:"none",cursor:"pointer",padding:"5px 7px",borderRadius:6,color:COLORS.textMid,display:"flex",alignItems:"center" }}
                            onMouseEnter={e=>e.currentTarget.style.color=COLORS.danger} onMouseLeave={e=>e.currentTarget.style.color=COLORS.textMid}>
                            <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6"/><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6"/><path d="M10 11v6"/><path d="M14 11v6"/><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2"/></svg>
                          </button>
                        </td>
                      </tr>
                    ))}
                    {!clientes.length && <tr><td colSpan={8} style={{ padding:"32px",textAlign:"center",color:COLORS.textMid }}>Nenhum cliente cadastrado</td></tr>}
                  </tbody>
                </table>
              </Card>
            </>
          )}
        </div>
      )}

      {/* Editor de contrato aberto a partir da lista de contratos */}
      {aba === "contratos" && contratoEditorAberto && (
        <Propostas key={propostasKey} token={token} clientes={clientes} telaInicial="editor" modoNovoInicial="contrato"
          onVoltar={()=>setContratoEditorAberto(false)} empresaFiltroId={empresaFiltroId} />
      )}

      {/* ABA: CONTRATOS */}
      {aba === "contratos" && !detalhe && !contratoEditorAberto && (() => {
        const thInp = { width:"100%", padding:"4px 6px", borderRadius:6, border:`1px solid ${COLORS.border}`, background:COLORS.surface, fontSize:11, color:COLORS.text, boxSizing:"border-box", marginTop:4 };
        return (
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          <div style={{ display:"flex",gap:10,alignItems:"center",flexWrap:"wrap" }}>
            <Btn onClick={()=>{ setPropostasKey(k=>k+1); setContratoEditorAberto(true); }}>
              + Novo Contrato Ativo
            </Btn>
            {(filtro||filtroContStatus||filtroContCliente) && (
              <Btn size="sm" variant="ghost" onClick={()=>{ setFiltro(""); setFiltroContStatus(""); setFiltroContCliente(""); }}>✕ Limpar filtros</Btn>
            )}
          </div>

          {/* Cards resumo */}
          <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fit,minmax(130px,1fr))",gap:10 }}>
            {[
              { label:"Contratos Ativos", val: contratos.filter(c=>c.status==="ativo").length, color:COLORS.primary },
              { label:"Propostas conv.", val: contratos.filter(c=>c.status==="proposta").length, color:COLORS.info },
              { label:"Total Aditivos", val: contratos.reduce((s,c)=>s+(parseInt(c.total_aditivos)||0),0), color:"#7c3aed" },
              { label:"Valor Mensal Total", val: fmt(contratos.filter(c=>c.status==="ativo").reduce((s,c)=>s+parseFloat(c.valor_mensal||0)+parseFloat(c.aditivos_valor||0),0)), color:"#16a34a" },
              { label:"Clientes Ativos", val: new Set(contratos.filter(c=>c.status==="ativo").map(c=>c.cliente_id)).size, color:COLORS.accent },
            ].map(card=>(
              <Card key={card.label} style={{ borderTop:`4px solid ${card.color}` }}>
                <div style={{ fontSize:11,color:COLORS.textMid,fontWeight:600 }}>{card.label}</div>
                <div style={{ fontSize:20,fontWeight:900,color:card.color }}>{card.val}</div>
              </Card>
            ))}
            {(() => {
              const ativos = contratos.filter(c => c.status === "ativo");
              const totalPostos = ativos.reduce((s,c) => s + (parseInt(c.total_postos)||0), 0);
              const postosVazios = ativos.reduce((s,c) => s + (parseInt(c.postos_vazios)||0), 0);
              return (
                <Card style={{ borderTop:`4px solid ${COLORS.warning}` }}>
                  <div style={{ fontSize:11,color:COLORS.textMid,fontWeight:600 }}>Postos</div>
                  <div style={{ fontSize:20,fontWeight:900,color:COLORS.warning }}>{totalPostos}</div>
                  <div style={{ display:"flex",alignItems:"center",gap:4,marginTop:4 }}>
                    <span style={{ fontSize:11,color:COLORS.textLight }}>Vazios:</span>
                    <span style={{ fontSize:13,fontWeight:700,color: postosVazios > 0 ? COLORS.danger : "#16a34a" }}>{postosVazios}</span>
                  </div>
                </Card>
              );
            })()}
          </div>

          <Card style={{ padding:0,overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:800 }}>
              <thead>
                <tr style={{ background:COLORS.bg }}>
                  <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top", minWidth:110 }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Contrato</div>
                    <input value={filtro} onChange={e=>setFiltro(e.target.value)} placeholder="Nº, cliente, objeto..." style={thInp} />
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top", minWidth:160 }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Cliente</div>
                    <select value={filtroContCliente} onChange={e=>setFiltroContCliente(e.target.value)} style={thInp}>
                      <option value="">Todos</option>
                      {clientes.map(c=><option key={c.id} value={c.id}>{c.nome_fantasia || c.nome}</option>)}
                    </select>
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Objeto</div>
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Vigência</div>
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Valor/Mês</div>
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"center", verticalAlign:"top" }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Colab.</div>
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top", minWidth:110 }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Status</div>
                    <select value={filtroContStatus} onChange={e=>setFiltroContStatus(e.target.value)} style={thInp}>
                      <option value="">Todos</option>
                      <option value="ativo">Ativo</option>
                      <option value="proposta">Proposta</option>
                      <option value="suspenso">Suspenso</option>
                      <option value="encerrado">Encerrado</option>
                    </select>
                  </th>
                  <th style={{ padding:"8px 10px", textAlign:"left", verticalAlign:"top" }}>
                    <div style={{ fontWeight:600,fontSize:11,color:COLORS.textMid }}>Ações</div>
                  </th>
                </tr>
              </thead>
              <tbody>
                {contratosFiltrados.map((c,i)=>(
                  <tr key={c.id} style={{ background:i%2===0?COLORS.surface:COLORS.bg,borderBottom:`1px solid ${COLORS.border}` }}>
                    <td style={{ padding:"10px 14px",fontWeight:700 }}>
                      <div style={{ display:"flex", alignItems:"center", gap:6 }}>
                        <span>{c.numero||`#${c.id}`}</span>
                        {c.total_aditivos > 0 && (
                          <span title={`${c.total_aditivos} aditivo(s)`} style={{ background:"#f0e6ff", color:"#7c3aed", border:"1px solid #c4b5fd", borderRadius:4, fontSize:10, fontWeight:700, padding:"1px 5px", whiteSpace:"nowrap" }}>
                            +{c.total_aditivos} AD
                          </span>
                        )}
                      </div>
                    </td>
                    <td style={{ padding:"10px 14px" }}>{c.cliente_nome}</td>
                    <td style={{ padding:"10px 14px",color:COLORS.textMid,maxWidth:200,whiteSpace:"nowrap",overflow:"hidden",textOverflow:"ellipsis" }}>{c.objeto||"—"}</td>
                    <td style={{ padding:"10px 14px",fontSize:12 }}>
                      {c.data_inicio ? new Date(c.data_inicio).toLocaleDateString("pt-BR") : "—"}
                      {c.data_fim && <><br/>{`→ ${new Date(c.data_fim).toLocaleDateString("pt-BR")}`}</>}
                    </td>
                    <td style={{ padding:"10px 14px",fontWeight:700,color:"#16a34a" }}>
                      {fmt(parseFloat(c.valor_mensal||0)+parseFloat(c.aditivos_valor||0))}
                      {parseFloat(c.aditivos_valor||0) > 0 && (
                        <div style={{ fontSize:10,fontWeight:400,color:COLORS.textMid }}>
                          {fmt(c.valor_mensal)} + {fmt(c.aditivos_valor)} AD
                        </div>
                      )}
                    </td>
                    <td style={{ padding:"10px 14px", textAlign:"center" }}>
                      <span style={{ fontWeight:700, color: c.total_colaboradores > 0 ? COLORS.primary : COLORS.textLight, fontSize:13 }}>
                        {c.total_colaboradores ?? 0}
                      </span>
                    </td>
                    <td style={{ padding:"10px 14px" }}>
                      <span style={{ background:STATUS_COLOR[c.status]+"22",color:STATUS_COLOR[c.status],padding:"3px 10px",borderRadius:20,fontWeight:700,fontSize:11 }}>
                        {c.status}
                      </span>
                    </td>
                    <td style={{ padding:"8px 14px" }}>
                      <div style={{ display:"flex", gap:6 }}>
                        <Btn size="sm" onClick={()=>abrirDetalhe(c)}>Ver detalhes</Btn>
                        {c.arquivo_nome && <a href={`/api/contratos/contratos/${c.id}/arquivo`} target="_blank" rel="noreferrer" style={{ textDecoration:"none" }}><Btn size="sm" variant="ghost">📎</Btn></a>}
                      </div>
                    </td>
                  </tr>
                ))}
                {!contratosFiltrados.length && <tr><td colSpan={8} style={{ padding:"32px",textAlign:"center",color:COLORS.textMid }}>{contratos.length ? "Nenhum contrato encontrado com os filtros aplicados." : "Nenhum contrato cadastrado"}</td></tr>}
              </tbody>
            </table>
            </div>
          </Card>
        </div>
        );
      })()}

      {/* DETALHE DO CONTRATO */}
      {detalhe && (
        <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
          <div style={{ display:"flex",gap:10,alignItems:"center" }}>
            <Btn variant="ghost" onClick={()=>setDetalhe(null)}>← Voltar</Btn>
            <span style={{ fontWeight:800,fontSize:16 }}>Contrato {detalhe.numero||`#${detalhe.id}`} — {detalhe.cliente_nome}</span>
            <span style={{ background:STATUS_COLOR[detalhe.status]+"22",color:STATUS_COLOR[detalhe.status],padding:"3px 12px",borderRadius:20,fontWeight:700,fontSize:12 }}>{detalhe.status}</span>
            <div style={{ marginLeft:"auto", display:"flex", gap:8 }}>
              {detalhe.status !== "encerrado" && (
                <Btn size="sm" variant="danger" onClick={async ()=>{
                  if (!window.confirm(`Cancelar o contrato ${detalhe.numero||"#"+detalhe.id}?\n\nTodas as cobranças pendentes/vencidas serão marcadas como CANCELADAS.\n\nEsta ação não pode ser desfeita.`)) return;
                  try {
                    const r = await apiFetch("POST", `/api/contratos/contratos/${detalhe.id}/cancelar`, {}, token);
                    setMsg(`✅ Contrato encerrado. ${r.cobrancas_canceladas} cobrança(s) cancelada(s).`);
                    setTimeout(()=>setMsg(""),5000);
                    const d = await apiFetch("GET", `/api/contratos/contratos/${detalhe.id}`, undefined, token);
                    setDetalhe(d);
                    setFDetalhe(f=>({...f, status:"encerrado"}));
                    carregar();
                  } catch(e) { alert(e.message); }
                }}>🚫 Cancelar</Btn>
              )}
              <Btn size="sm" variant="danger" onClick={async ()=>{
                const num = detalhe.numero || `#${detalhe.id}`;
                if (!window.confirm(`⚠️ EXCLUIR contrato ${num} permanentemente?\n\nIsso irá apagar:\n• Todo o histórico de cobranças\n• Aditivos e documentos\n• Desvinculará todos os funcionários\n\nEsta ação é IRREVERSÍVEL.`)) return;
                if (!window.confirm(`Confirme novamente: excluir permanentemente o contrato ${num}?`)) return;
                try {
                  await apiFetch("DELETE", `/api/contratos/contratos/${detalhe.id}/completo`, undefined, token);
                  setDetalhe(null);
                  carregar();
                  setMsg("✅ Contrato excluído.");
                  setTimeout(()=>setMsg(""),4000);
                } catch(e) { alert(e.message); }
              }}>🗑 Excluir</Btn>
            </div>
          </div>

          {/* Informações editáveis */}
          <Card>
            <div style={{ fontWeight:700,marginBottom:14 }}>📋 Informações do Contrato</div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr 1fr",gap:10,marginBottom:10 }}>
              <Input label="Número" value={fDetalhe.numero||""} onChange={e=>setFDetalhe(f=>({...f,numero:e.target.value}))} placeholder="Ex: 001/2026" />
              <div>
                <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Status</label>
                <select value={fDetalhe.status||"ativo"} onChange={e=>setFDetalhe(f=>({...f,status:e.target.value}))}
                  style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13 }}>
                  <option value="proposta">Proposta</option>
                  <option value="ativo">Ativo</option>
                  <option value="suspenso">Suspenso</option>
                  <option value="encerrado">Encerrado</option>
                </select>
              </div>
              <Input label="Valor Mensal (R$)" type="number" value={fDetalhe.valor_mensal||""} onChange={e=>setFDetalhe(f=>({...f,valor_mensal:e.target.value}))} />
              <Input label="Dia de Vencimento" type="number" min="1" max="28" value={fDetalhe.dia_vencimento||"10"} onChange={e=>setFDetalhe(f=>({...f,dia_vencimento:e.target.value}))} />
            </div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10 }}>
              <Input label="Início da Vigência" type="date" value={fDetalhe.data_inicio||""} onChange={e=>setFDetalhe(f=>({...f,data_inicio:e.target.value}))} />
              <Input label="Fim da Vigência" type="date" value={fDetalhe.data_fim||""} onChange={e=>setFDetalhe(f=>({...f,data_fim:e.target.value}))} />
              <Input label="Nome/Referência do Local" value={fDetalhe.local||""} onChange={e=>setFDetalhe(f=>({...f,local:e.target.value}))} placeholder="Ex: Sede do Cliente, Filial Norte" />
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Objeto / Descrição dos Serviços</label>
              <textarea value={fDetalhe.objeto||""} onChange={e=>setFDetalhe(f=>({...f,objeto:e.target.value}))} rows={3}
                placeholder="Ex: Prestação de serviços de conservação, limpeza e portaria..."
                style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,resize:"vertical",boxSizing:"border-box" }} />
            </div>
            <div style={{ marginBottom:10 }}>
              <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Observações</label>
              <textarea value={fDetalhe.observacoes||""} onChange={e=>setFDetalhe(f=>({...f,observacoes:e.target.value}))} rows={2}
                style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,resize:"vertical",boxSizing:"border-box" }} />
            </div>

            {/* Endereço do local de prestação */}
            <div style={{ borderTop:`1px solid ${COLORS.border}`,paddingTop:14,marginBottom:14 }}>
              <div style={{ fontWeight:600,fontSize:12,color:COLORS.textMid,marginBottom:10 }}>📍 Endereço do Local de Prestação</div>
              <div style={{ display:"grid",gridTemplateColumns:"2fr 0.5fr 1fr",gap:10,marginBottom:10 }}>
                <Input label="Logradouro" value={fDetalhe.local_logradouro||""} onChange={e=>setFDetalhe(f=>({...f,local_logradouro:e.target.value}))} placeholder="Rua, Av, Alameda..." />
                <Input label="Número" value={fDetalhe.local_numero||""} onChange={e=>setFDetalhe(f=>({...f,local_numero:e.target.value}))} placeholder="Ex: 100" />
                <Input label="Complemento" value={fDetalhe.local_complemento||""} onChange={e=>setFDetalhe(f=>({...f,local_complemento:e.target.value}))} placeholder="Sala, Bloco..." />
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 0.4fr 1fr",gap:10 }}>
                <Input label="Bairro" value={fDetalhe.local_bairro||""} onChange={e=>setFDetalhe(f=>({...f,local_bairro:e.target.value}))} placeholder="Bairro" />
                <Input label="Cidade" value={fDetalhe.local_cidade||""} onChange={e=>setFDetalhe(f=>({...f,local_cidade:e.target.value}))} placeholder="Cidade" />
                <Input label="UF" value={fDetalhe.local_uf||""} onChange={e=>setFDetalhe(f=>({...f,local_uf:e.target.value.toUpperCase().slice(0,2)}))} placeholder="MG" maxLength={2} />
                <Input label="CEP" value={fDetalhe.local_cep||""} onChange={e=>setFDetalhe(f=>({...f,local_cep:e.target.value}))} placeholder="00000-000" />
              </div>
            </div>

            {/* NFS-e por contrato */}
            <div style={{ borderTop:`1px solid ${COLORS.border}`,paddingTop:14,marginTop:4,marginBottom:14 }}>
              <div style={{ fontWeight:600,fontSize:12,color:COLORS.textMid,marginBottom:10 }}>🧾 Configuração NFS-e deste contrato <span style={{ fontWeight:400,fontSize:10 }}>(sobrescreve a config global)</span></div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10 }}>
                <div>
                  <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>cTribNac <span style={{ fontWeight:400,fontSize:10 }}>(6 dígitos)</span></label>
                  <input value={fDetalhe.nfe_ctrib_nac||""} onChange={e=>setFDetalhe(f=>({...f,nfe_ctrib_nac:e.target.value}))}
                    placeholder="Ex: 170602 (global se vazio)"
                    style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Alíquota ISS (%) <span style={{ fontWeight:400,fontSize:10 }}>(global se vazio)</span></label>
                  <input type="number" step="0.01" min="0" max="5" value={fDetalhe.nfe_aliquota_iss??""} onChange={e=>setFDetalhe(f=>({...f,nfe_aliquota_iss:e.target.value}))}
                    placeholder="Ex: 2.00"
                    style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box" }} />
                </div>
                <div>
                  <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Retenção ISS</label>
                  <select value={fDetalhe.nfe_tipo_retencao_iss??""} onChange={e=>setFDetalhe(f=>({...f,nfe_tipo_retencao_iss:e.target.value}))}
                    style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13 }}>
                    <option value="">— usar config global —</option>
                    <option value="1">1 — Não retido</option>
                    <option value="2">2 — Retido pelo tomador</option>
                    <option value="3">3 — Retido pelo intermediário</option>
                  </select>
                </div>
              </div>
              <div>
                <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Descrição padrão da NFS-e <span style={{ fontWeight:400,fontSize:10 }}>(vazio = gera automaticamente)</span></label>
                <textarea value={fDetalhe.nfe_descricao_padrao||""} onChange={e=>setFDetalhe(f=>({...f,nfe_descricao_padrao:e.target.value}))} rows={2}
                  placeholder="Ex: Serviços de limpeza e conservação predial — Contrato nº 001/2026"
                  style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,resize:"vertical",boxSizing:"border-box" }} />
              </div>
            </div>

            {/* Condições Financeiras */}
            <div style={{ borderTop:`1px solid ${COLORS.border}`,paddingTop:14,marginTop:4,marginBottom:14 }}>
              <div style={{ fontWeight:600,fontSize:12,color:COLORS.textMid,marginBottom:10 }}>💰 Condições Financeiras</div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr 1fr",gap:10,marginBottom:10 }}>
                <div>
                  <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Forma de Pagamento</label>
                  <select value={fDetalhe.forma_pagamento||"pix"} onChange={e=>setFDetalhe(f=>({...f,forma_pagamento:e.target.value}))}
                    style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13 }}>
                    <option value="pix">PIX</option>
                    <option value="boleto">Boleto Bancário</option>
                    <option value="transferencia">Transferência Bancária</option>
                    <option value="cheque">Cheque</option>
                    <option value="dinheiro">Dinheiro</option>
                  </select>
                </div>
                <div>
                  <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>Índice de Reajuste</label>
                  <select value={fDetalhe.reajuste_indice||"IGPM"} onChange={e=>setFDetalhe(f=>({...f,reajuste_indice:e.target.value}))}
                    style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13 }}>
                    <option value="IGPM">IGP-M</option>
                    <option value="IPCA">IPCA</option>
                    <option value="INPC">INPC</option>
                    <option value="IPCA-E">IPCA-E</option>
                    <option value="Fixo">Percentual Fixo</option>
                    <option value="Nenhum">Sem Reajuste</option>
                  </select>
                </div>
                <Input label="Percentual de Reajuste (%)" type="number" min="0" step="0.01" value={fDetalhe.reajuste_percentual??""} onChange={e=>setFDetalhe(f=>({...f,reajuste_percentual:e.target.value}))} placeholder="Ex: 5.5" />
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                <Input label="Prazo de Aviso para Rescisão (dias)" type="number" min="0" value={fDetalhe.prazo_aviso_rescisao??""} onChange={e=>setFDetalhe(f=>({...f,prazo_aviso_rescisao:e.target.value}))} placeholder="30" />
                <Input label="Multa por Rescisão (%)" type="number" min="0" step="0.01" value={fDetalhe.multa_rescisao??""} onChange={e=>setFDetalhe(f=>({...f,multa_rescisao:e.target.value}))} placeholder="Ex: 10" />
              </div>
            </div>

            {/* Serviços Prestados */}
            <div style={{ borderTop:`1px solid ${COLORS.border}`,paddingTop:14,marginTop:4,marginBottom:14 }}>
              <div style={{ fontWeight:600,fontSize:12,color:COLORS.textMid,marginBottom:10 }}>🔧 Serviços Prestados</div>
              {servicosDetalhe.map((sv, idx) => (
                <div key={idx} style={{ display:"grid",gridTemplateColumns:"3fr 2fr 0.8fr auto",gap:10,marginBottom:8,alignItems:"flex-end" }}>
                  <Input label={idx===0?"Descrição":undefined} value={sv.descricao||""}
                    onChange={e=>setServicosDetalhe(s=>s.map((x,i)=>i===idx?{...x,descricao:e.target.value}:x))}
                    placeholder="Ex: Limpeza e Conservação" />
                  <div>
                    {idx===0&&<label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4 }}>CNAE</label>}
                    <input value={sv.cnae||""} onChange={e=>setServicosDetalhe(s=>s.map((x,i)=>i===idx?{...x,cnae:e.target.value}:x))}
                      placeholder="Ex: 8121-4/00" list={`cnae-det-${idx}`}
                      style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box" }} />
                    <datalist id={`cnae-det-${idx}`}>
                      <option value="8121-4/00">8121-4/00 — Limpeza em prédios e domicílios</option>
                      <option value="8122-2/00">8122-2/00 — Controle de pragas</option>
                      <option value="8129-0/00">8129-0/00 — Limpeza n.e.</option>
                      <option value="8011-1/01">8011-1/01 — Vigilância e segurança</option>
                      <option value="8020-0/01">8020-0/01 — Monitoramento eletrônico</option>
                      <option value="8111-7/00">8111-7/00 — Serviços combinados para edifícios</option>
                    </datalist>
                  </div>
                  <Input label={idx===0?"Postos":undefined} type="number" min="1" value={sv.qtd_postos||"1"}
                    onChange={e=>setServicosDetalhe(s=>s.map((x,i)=>i===idx?{...x,qtd_postos:e.target.value}:x))} />
                  <button onClick={()=>setServicosDetalhe(s=>s.filter((_,i)=>i!==idx))}
                    style={{ background:COLORS.dangerBg,border:`1px solid ${COLORS.danger}33`,borderRadius:8,padding:"8px 10px",cursor:"pointer",color:COLORS.danger,fontWeight:700 }}>✕</button>
                </div>
              ))}
              <Btn size="sm" variant="ghost" onClick={()=>setServicosDetalhe(s=>[...s,{descricao:"",cnae:"",qtd_postos:"1"}])}>+ Adicionar Serviço</Btn>
            </div>

            <Btn onClick={salvarDetalheContrato} disabled={salvandoDetalhe}>{salvandoDetalhe?"Salvando...":"💾 Salvar Informações"}</Btn>
          </Card>

          {/* Arquivos do contrato */}
          <Card>
            <div style={{ fontWeight:700,marginBottom:12 }}>📎 Arquivos do Contrato</div>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:14 }}>
              <div>
                <div style={{ fontSize:12,fontWeight:600,color:COLORS.textMid,marginBottom:6 }}>Contrato Original (Proposta/PDF gerado)</div>
                {detalhe.arquivo_nome ? (
                  <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                    <span style={{ fontSize:13 }}>📄 {detalhe.arquivo_nome}</span>
                    <a href={`/api/contratos/contratos/${detalhe.id}/arquivo`} target="_blank" rel="noreferrer"><Btn size="sm" variant="ghost">⬇ Baixar</Btn></a>
                  </div>
                ) : <div style={{ fontSize:13,color:COLORS.textLight }}>Nenhum arquivo — use o PDF da proposta</div>}
              </div>
              <div>
                <div style={{ fontSize:12,fontWeight:600,color:COLORS.textMid,marginBottom:6 }}>
                  Contrato Assinado
                  {detalhe.assinado_em && <span style={{ color:"#16a34a",marginLeft:6 }}>✅ Recebido em {new Date(detalhe.assinado_em).toLocaleDateString("pt-BR")}</span>}
                </div>
                {detalhe.arquivo_assinado_nome ? (
                  <div style={{ display:"flex",gap:8,alignItems:"center" }}>
                    <span style={{ fontSize:13 }}>📄 {detalhe.arquivo_assinado_nome}</span>
                    <a href={`/api/contratos/contratos/${detalhe.id}/assinado`} target="_blank" rel="noreferrer"><Btn size="sm" variant="ghost">⬇ Baixar</Btn></a>
                  </div>
                ) : (
                  <label style={{ cursor:"pointer" }}>
                    <input type="file" accept=".pdf,.jpg,.jpeg,.png" style={{ display:"none" }} onChange={async e=>{
                      const file = e.target.files[0];
                      if (!file) return;
                      const fd = new FormData();
                      fd.append("arquivo", file);
                      try {
                        await fetch(`/api/contratos/contratos/${detalhe.id}/assinar`, { method:"POST", headers:{ Authorization:`Bearer ${token}` }, body:fd });
                        const d = await apiFetch("GET", `/api/contratos/contratos/${detalhe.id}`, undefined, token);
                        setDetalhe(d);
                        setMsg("✅ Contrato assinado anexado!");
                        setTimeout(()=>setMsg(""),3000);
                      } catch(err) { alert(err.message); }
                    }} />
                    <Btn size="sm">Anexar contrato</Btn>
                  </label>
                )}
              </div>
            </div>
          </Card>

          {/* Documentos Adicionais */}
          <Card>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
              <span style={{ fontWeight:700 }}>📂 Documentos Adicionais</span>
              <label style={{ cursor:"pointer" }}>
                <input type="file" accept=".pdf,.doc,.docx,.jpg,.jpeg,.png,.xls,.xlsx" style={{ display:"none" }} onChange={async e=>{
                  const file = e.target.files[0];
                  if (!file) return;
                  const fd = new FormData();
                  fd.append("arquivo", file);
                  try {
                    await fetch(`/api/contratos/contratos/${detalhe.id}/documentos`, { method:"POST", headers:{ Authorization:`Bearer ${token}` }, body:fd });
                    await carregarDocs(detalhe.id);
                    setMsg("✅ Documento anexado!");
                    setTimeout(()=>setMsg(""),2500);
                  } catch(err) { alert(err.message); }
                  e.target.value = "";
                }} />
                <Btn size="sm">+ Anexar Documento</Btn>
              </label>
            </div>
            {docsContrato.length === 0 ? (
              <div style={{ color:COLORS.textLight,fontSize:13,textAlign:"center",padding:"12px 0" }}>Nenhum documento adicional anexado</div>
            ) : (
              <div style={{ display:"flex",flexDirection:"column",gap:8 }}>
                {docsContrato.map(doc=>(
                  <div key={doc.id} style={{ display:"flex",alignItems:"center",gap:10,padding:"8px 12px",background:COLORS.bg,borderRadius:8,border:`1px solid ${COLORS.border}` }}>
                    <span style={{ fontSize:16 }}>📄</span>
                    <span style={{ flex:1,fontSize:13,fontWeight:500 }}>{doc.nome}</span>
                    <span style={{ fontSize:11,color:COLORS.textMid }}>{doc.criado_em ? new Date(doc.criado_em).toLocaleDateString("pt-BR") : ""}</span>
                    <a href={`/api/contratos/contratos/${detalhe.id}/documentos/${doc.id}/arquivo`} target="_blank" rel="noreferrer" style={{ textDecoration:"none" }}>
                      <Btn size="sm" variant="ghost">⬇ Baixar</Btn>
                    </a>
                    <Btn size="sm" variant="danger" onClick={async ()=>{
                      if (!window.confirm("Excluir este documento?")) return;
                      try {
                        await apiFetch("DELETE", `/api/contratos/contratos/${detalhe.id}/documentos/${doc.id}`, undefined, token);
                        setDocsContrato(prev=>prev.filter(d=>d.id!==doc.id));
                      } catch(err) { alert(err.message); }
                    }}>✕</Btn>
                  </div>
                ))}
              </div>
            )}
          </Card>

          {/* Funcionários Vinculados */}
          <Card>
            {(() => {
              const totalVagas = (detalhe.setores||[]).reduce((s,set) => s + parseInt(set.qtd_funcionarios||0), 0);
              const alocados = funcContrato.length;
              const pendente = totalVagas > alocados;
              const linkedIds = new Set(funcContrato.map(f => f.id));
              const disponiveis = allEmployees.filter(e => e.status !== "demitido" && !linkedIds.has(e.id));
              return (
                <>
                  <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
                    <div style={{ display:"flex",alignItems:"center",gap:10 }}>
                      <span style={{ fontWeight:700 }}>👷 Funcionários Vinculados</span>
                      {totalVagas > 0 && (
                        <span style={{ padding:"3px 12px",borderRadius:20,fontWeight:800,fontSize:12,
                          background: pendente ? "#FEF9C3" : "#DCFCE7",
                          color: pendente ? "#854D0E" : "#166534",
                          border: `1px solid ${pendente ? "#FDE68A" : "#BBF7D0"}` }}>
                          {alocados}/{totalVagas} alocados{pendente ? " ⚠️ Pendente" : " ✅ Completo"}
                        </span>
                      )}
                    </div>
                  </div>

                  {/* Adicionar funcionário */}
                  <div style={{ display:"flex",gap:8,marginBottom:14,alignItems:"center" }}>
                    <div style={{ flex:1 }}>
                      <select value={addFuncId} onChange={e=>setAddFuncId(e.target.value)}
                        style={{ width:"100%",padding:"8px 10px",borderRadius:8,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13 }}>
                        <option value="">Selecionar funcionário para vincular...</option>
                        {disponiveis.map(f => (
                          <option key={f.id} value={f.id}>{f.nome} — {f.cargo||"—"}{f.setor ? ` | ${f.setor}` : ""}</option>
                        ))}
                      </select>
                    </div>
                    <Btn size="sm" onClick={async () => {
                      if (!addFuncId) return;
                      setSalvando(true);
                      try {
                        await apiFetch("POST", `/api/contratos/contratos/${detalhe.id}/funcionarios`, { funcionario_id: parseInt(addFuncId) }, token);
                        await carregarFuncContrato(detalhe.id);
                        setAddFuncId("");
                      } catch(e) { alert(e.message); }
                      setSalvando(false);
                    }} disabled={salvando || !addFuncId}>+ Vincular</Btn>
                  </div>

                  {loadingFunc ? (
                    <div style={{ color:COLORS.textMid,fontSize:13,textAlign:"center",padding:16 }}>Carregando...</div>
                  ) : funcContrato.length === 0 ? (
                    <div style={{ color:COLORS.textMid,fontSize:13,textAlign:"center",padding:20 }}>
                      Nenhum funcionário vinculado{totalVagas > 0 ? ` — faltam ${totalVagas} funcionário(s)` : ""}
                    </div>
                  ) : (
                    <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                      <thead><tr style={{ background:COLORS.bg }}>
                        {["Nome","Cargo","Setor","Turno","Status",""].map(h=>(
                          <th key={h} style={{ padding:"8px 12px",fontWeight:600,fontSize:11,color:COLORS.textMid,textAlign:"left" }}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {funcContrato.map((f,i)=>(
                          <tr key={f.id} style={{ borderBottom:`1px solid ${COLORS.border}`,background:i%2===0?COLORS.surface:COLORS.bg }}>
                            <td style={{ padding:"8px 12px",fontWeight:700 }}>{f.nome}</td>
                            <td style={{ padding:"8px 12px",color:COLORS.textMid }}>{f.cargo||"—"}</td>
                            <td style={{ padding:"8px 12px",color:COLORS.textMid }}>{f.setor||"—"}{f.subsetor?` / ${f.subsetor}`:""}</td>
                            <td style={{ padding:"8px 12px",color:COLORS.textMid }}>{f.turno||"—"}</td>
                            <td style={{ padding:"8px 12px" }}>
                              <span style={{ fontSize:11,fontWeight:700,padding:"2px 8px",borderRadius:20,
                                background: f.status==="ativo"?"#DCFCE7":COLORS.bg,
                                color: f.status==="ativo"?"#166534":COLORS.textMid }}>
                                {f.status||"ativo"}
                              </span>
                            </td>
                            <td style={{ padding:"8px 12px" }}>
                              <Btn size="sm" variant="danger" onClick={async ()=>{
                                if (!window.confirm(`Desvincular ${f.nome} deste contrato?`)) return;
                                try {
                                  await apiFetch("DELETE", `/api/contratos/contratos/${detalhe.id}/funcionarios/${f.id}`, undefined, token);
                                  await carregarFuncContrato(detalhe.id);
                                } catch(e) { alert(e.message); }
                              }}>✕ Desvincular</Btn>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </>
              );
            })()}
          </Card>

          {/* Postos/Setores */}
          <Card>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
              <span style={{ fontWeight:700 }}>Postos / Setores</span>
              <Btn size="sm" onClick={()=>setModalSetor({ contratoId: detalhe.id })}>+ Adicionar Posto</Btn>
            </div>
            {msg && <div style={{ background:"#dcfce7",borderRadius:8,padding:"8px 12px",fontSize:12,color:"#166534",marginBottom:10 }}>{msg}</div>}

            {/* Modal setor inline */}
            {modalSetor?.contratoId === detalhe.id && (
              <div style={{ background:COLORS.bg,borderRadius:10,padding:16,marginBottom:14,border:`1px solid ${COLORS.border}` }}>
                <div style={{ fontWeight:700,marginBottom:10,fontSize:13 }}>Novo Posto/Setor</div>
                <div style={{ display:"grid",gridTemplateColumns:"repeat(5,1fr)",gap:10 }}>
                  <Input label="Nome do Setor/Posto *" value={fset.nome} onChange={e=>setFset(f=>({...f,nome:e.target.value}))} placeholder="Ex: Portaria, Limpeza" />
                  <Input label="Cargo" value={fset.cargo} onChange={e=>setFset(f=>({...f,cargo:e.target.value}))} placeholder="Ex: Porteiro" />
                  <Input label="Qtd. Funcionários" type="number" min="1" value={fset.qtd_funcionarios} onChange={e=>setFset(f=>({...f,qtd_funcionarios:e.target.value}))} />
                  <Input label="Turno" value={fset.turno} onChange={e=>setFset(f=>({...f,turno:e.target.value}))} placeholder="Manhã, Tarde, Noite" />
                  <Input label="Valor Unit./Mês" type="number" value={fset.valor_unitario} onChange={e=>setFset(f=>({...f,valor_unitario:e.target.value}))} />
                </div>
                <div style={{ display:"flex",gap:8,marginTop:10 }}>
                  <Btn size="sm" onClick={salvarSetor} disabled={salvando}>Adicionar</Btn>
                  <Btn size="sm" variant="ghost" onClick={()=>setModalSetor(null)}>Cancelar</Btn>
                </div>
              </div>
            )}

            {(detalhe.setores||[]).length === 0 ? (
              <div style={{ color:COLORS.textMid,fontSize:13,textAlign:"center",padding:24 }}>Nenhum posto cadastrado</div>
            ) : (
              <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                <thead><tr style={{ background:COLORS.bg }}>
                  {["Setor/Posto","Cargo","Qtd. Func.","Turno","Valor Unit.","Subtotal",""].map(h=>(
                    <th key={h} style={{ padding:"8px 12px",fontWeight:600,fontSize:11,color:COLORS.textMid,textAlign:"left" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(detalhe.setores||[]).map((s,i)=>(
                    <tr key={s.id} style={{ borderBottom:`1px solid ${COLORS.border}`,background:i%2===0?COLORS.surface:COLORS.bg }}>
                      <td style={{ padding:"8px 12px",fontWeight:700 }}>{s.nome}</td>
                      <td style={{ padding:"8px 12px",color:COLORS.textMid }}>{s.cargo||"—"}</td>
                      <td style={{ padding:"8px 12px",textAlign:"center" }}>{s.qtd_funcionarios}</td>
                      <td style={{ padding:"8px 12px",color:COLORS.textMid }}>{s.turno||"—"}</td>
                      <td style={{ padding:"8px 12px" }}>{fmt(s.valor_unitario)}</td>
                      <td style={{ padding:"8px 12px",fontWeight:700,color:"#16a34a" }}>{fmt(parseFloat(s.valor_unitario||0)*parseInt(s.qtd_funcionarios||1))}</td>
                      <td style={{ padding:"8px 12px" }}><Btn size="sm" variant="danger" onClick={()=>removerSetor(detalhe.id,s.id)}>Excluir</Btn></td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Cobranças do contrato */}
          <Card>
            <div style={{ display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:12 }}>
              <span style={{ fontWeight:700 }}>Cobranças ({(detalhe.cobrancas||[]).length})</span>
              <Btn size="sm" onClick={()=>gerarCobrancas(detalhe.id)} disabled={salvando}>↻ Gerar Cobranças</Btn>
            </div>
            {(detalhe.cobrancas||[]).length===0 ? (
              <div style={{ color:COLORS.textMid,fontSize:13,textAlign:"center",padding:24 }}>Sem cobranças — clique em "Gerar Cobranças"</div>
            ) : (
              <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13 }}>
                <thead><tr style={{ background:COLORS.bg }}>
                  {["Mês/Ano","Valor","Vencimento","Status","NFS-e","Ações"].map(h=>(
                    <th key={h} style={{ padding:"8px 12px",fontWeight:600,fontSize:11,color:COLORS.textMid }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(detalhe.cobrancas||[]).map((c,i)=>(
                    <tr key={c.id} style={{ borderBottom:`1px solid ${COLORS.border}`,background:i%2===0?COLORS.surface:COLORS.bg }}>
                      <td style={{ padding:"8px 12px",fontWeight:700 }}>{String(c.mes).padStart(2,'0')}/{c.ano}</td>
                      <td style={{ padding:"8px 12px" }}>{fmt(c.valor)}</td>
                      <td style={{ padding:"8px 12px",fontSize:12,color:COLORS.textMid }}>{c.data_vencimento?new Date(c.data_vencimento).toLocaleDateString("pt-BR"):"—"}</td>
                      <td style={{ padding:"8px 12px" }}>
                        <select value={c.status} onChange={e=>atualizarCobranca(c.id,{status:e.target.value})}
                          style={{ padding:"4px 8px",borderRadius:6,border:`1px solid ${COLORS.border}`,background:STATUS_COB_COLOR[c.status]+"22",color:STATUS_COB_COLOR[c.status],fontWeight:700,fontSize:12 }}>
                          <option value="pendente">Pendente</option>
                          <option value="pago">Pago</option>
                          <option value="vencido">Vencido</option>
                          <option value="cancelado">Cancelado</option>
                        </select>
                      </td>
                      <td style={{ padding:"8px 12px",fontSize:12 }}>
                        {c.nfe_numero ? <span style={{ color:"#16a34a",fontWeight:700 }}>#{c.nfe_numero}</span> : <span style={{ color:COLORS.textMid }}>—</span>}
                      </td>
                      <td style={{ padding:"8px 12px",display:"flex",gap:6 }}>
                        {!c.nfe_numero && configNfe?.api_key && (
                          <Btn size="sm" variant="accent" onClick={()=>setNfePreview(c)} disabled={salvando}>Emitir NFS-e</Btn>
                        )}
                        {c.status==='pendente' && (
                          <Btn size="sm" onClick={()=>atualizarCobranca(c.id,{status:'pago',data_pagamento:new Date().toISOString().slice(0,10)})}>✅ Pago</Btn>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* ADITIVOS */}
          <Card>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center", marginBottom:14 }}>
              <div style={{ display:"flex", alignItems:"center", gap:8 }}>
                <span style={{ fontWeight:700, fontSize:14 }}>Aditivos</span>
                {aditivos.length > 0 && (
                  <span style={{ background:"#f0e6ff", color:"#7c3aed", border:"1px solid #c4b5fd", borderRadius:12, fontSize:11, fontWeight:700, padding:"2px 8px" }}>
                    {aditivos.length}
                  </span>
                )}
              </div>
              <Btn size="sm" onClick={() => { setFAd({ numero:"", objeto:"", data_assinatura:"", qtd_colaboradores:"", funcoes:"", turno:"", valor_mensal:"", status:"ativo", observacoes:"" }); setModalAditivo({}); }}>+ Novo Aditivo</Btn>
            </div>

            {/* Modal aditivo */}
            {modalAditivo && (
              <div style={{ background:COLORS.bg, border:`1px solid ${COLORS.border}`, borderRadius:10, padding:18, marginBottom:16 }}>
                <div style={{ fontWeight:700, marginBottom:12 }}>{modalAditivo.id ? "Editar Aditivo" : "Novo Aditivo"}</div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                  <Input label="Número" value={fAd.numero} onChange={e=>setFAd(f=>({...f,numero:e.target.value}))} placeholder="Ex: 1ºAD031" />
                  <Input label="Data Assinatura" type="date" value={fAd.data_assinatura} onChange={e=>setFAd(f=>({...f,data_assinatura:e.target.value}))} />
                  <Input label="Valor Mensal (R$)" type="number" step="0.01" value={fAd.valor_mensal} onChange={e=>setFAd(f=>({...f,valor_mensal:e.target.value}))} />
                </div>
                <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr 1fr 1fr", gap:10, marginBottom:10 }}>
                  <Input label="Nº Colaboradores" type="number" value={fAd.qtd_colaboradores} onChange={e=>setFAd(f=>({...f,qtd_colaboradores:e.target.value}))} />
                  <Input label="Funções" value={fAd.funcoes} onChange={e=>setFAd(f=>({...f,funcoes:e.target.value}))} placeholder="Ex: 1 ASG, 1 Porteiro" />
                  <Input label="Turno" value={fAd.turno} onChange={e=>setFAd(f=>({...f,turno:e.target.value}))} placeholder="Diurno / Noturno" />
                  <Select label="Status" value={fAd.status} onChange={e=>setFAd(f=>({...f,status:e.target.value}))}>
                    <option value="ativo">Ativo</option>
                    <option value="encerrado">Encerrado</option>
                  </Select>
                </div>
                {fAd.status === "encerrado" && (
                  <div style={{ marginBottom:10 }}>
                    <Input label="Data Encerramento" type="date" value={fAd.data_encerramento||""} onChange={e=>setFAd(f=>({...f,data_encerramento:e.target.value}))} />
                  </div>
                )}
                <Input label="Objeto / Descrição" value={fAd.objeto} onChange={e=>setFAd(f=>({...f,objeto:e.target.value}))} placeholder="Descreva o que o aditivo adiciona/altera..." />
                <Input label="Observações" value={fAd.observacoes} onChange={e=>setFAd(f=>({...f,observacoes:e.target.value}))} style={{ marginTop:8 }} />
                <div style={{ display:"flex", gap:10, marginTop:14, justifyContent:"flex-end" }}>
                  <Btn variant="ghost" onClick={()=>setModalAditivo(null)}>Cancelar</Btn>
                  <Btn onClick={salvarAditivo} disabled={salvandoAd}>{salvandoAd?"Salvando...":"Salvar"}</Btn>
                </div>
              </div>
            )}

            {aditivos.length === 0 ? (
              <div style={{ color:COLORS.textLight, fontSize:13, textAlign:"center", padding:"20px 0" }}>Nenhum aditivo cadastrado.</div>
            ) : (
              <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
                <thead>
                  <tr style={{ background:COLORS.bg }}>
                    {["Número","Objeto","Assinatura","Colaboradores","Funções","Turno","Valor/Mês","Status",""].map(h=>(
                      <th key={h} style={{ padding:"8px 12px", textAlign:"left", fontSize:11, color:COLORS.textMid, fontWeight:600, whiteSpace:"nowrap" }}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {aditivos.map((a,i) => (
                    <tr key={a.id} style={{ background:i%2===0?COLORS.surface:COLORS.bg, borderBottom:`1px solid ${COLORS.border}` }}>
                      <td style={{ padding:"8px 12px", fontWeight:700, color:"#7c3aed" }}>{a.numero||"—"}</td>
                      <td style={{ padding:"8px 12px", color:COLORS.textMid, maxWidth:200, whiteSpace:"nowrap", overflow:"hidden", textOverflow:"ellipsis" }}>{a.objeto||"—"}</td>
                      <td style={{ padding:"8px 12px", fontSize:12 }}>{a.data_assinatura ? new Date(a.data_assinatura).toLocaleDateString("pt-BR") : "—"}</td>
                      <td style={{ padding:"8px 12px", textAlign:"center", fontWeight:700, color:COLORS.primary }}>{a.qtd_colaboradores||0}</td>
                      <td style={{ padding:"8px 12px", fontSize:12 }}>{a.funcoes||"—"}</td>
                      <td style={{ padding:"8px 12px", fontSize:12 }}>{a.turno||"—"}</td>
                      <td style={{ padding:"8px 12px", fontWeight:700, color:"#16a34a" }}>{a.valor_mensal > 0 ? fmt(a.valor_mensal) : "—"}</td>
                      <td style={{ padding:"8px 12px" }}>
                        <span style={{ background:a.status==="ativo"?"#dcfce7":"#fee2e2", color:a.status==="ativo"?"#166534":"#991b1b", padding:"2px 8px", borderRadius:12, fontSize:11, fontWeight:700 }}>
                          {a.status}
                        </span>
                      </td>
                      <td style={{ padding:"6px 8px", display:"flex", gap:4 }}>
                        <Btn size="sm" variant="ghost" onClick={()=>{ setFAd({...a,data_assinatura:a.data_assinatura?.slice(0,10)||"",data_encerramento:a.data_encerramento?.slice(0,10)||"",qtd_colaboradores:String(a.qtd_colaboradores||""),valor_mensal:String(a.valor_mensal||"")}); setModalAditivo(a); }}>Editar</Btn>
                        {a.arquivo_nome && (
                          <a href={`/api/contratos/contratos/${detalhe.id}/aditivos/${a.id}/arquivo`} target="_blank" rel="noreferrer" style={{ textDecoration:"none" }}>
                            <Btn size="sm" variant="ghost">Arquivo</Btn>
                          </a>
                        )}
                        <Btn size="sm" variant="danger" onClick={()=>excluirAditivo(a.id)}>✕</Btn>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
          </Card>

          {/* Pasta de documentos */}
          <ContratoDocumentos token={token} contrato={detalhe} />

        </div>
      )}

      {/* ABA: COBRANÇAS — TELA DE CONTROLE */}
      {aba === "cobrancas" && (
        <div style={{ display:"flex",flexDirection:"column",gap:14 }}>
          <div style={{ display:"flex",gap:12,alignItems:"flex-end",flexWrap:"wrap" }}>
            {cobView === "lista" ? (
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid }}>Mês</label>
                <div style={{ display:"flex",gap:4 }}>
                  <button onClick={()=>{ const [y,m]=mesCob.split('-').map(Number); const d=new Date(y,m-2,1); setMesCob(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }} style={{ border:`1px solid ${COLORS.border}`,borderRadius:6,background:COLORS.surface,padding:"4px 10px",cursor:"pointer",fontSize:14 }}>‹</button>
                  <span style={{ fontSize:15,fontWeight:800,padding:"4px 10px",minWidth:90,textAlign:"center" }}>{["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][parseInt(mesCob.split('-')[1])-1]}/{mesCob.split('-')[0]}</span>
                  <button onClick={()=>{ const [y,m]=mesCob.split('-').map(Number); const d=new Date(y,m,1); setMesCob(`${d.getFullYear()}-${String(d.getMonth()+1).padStart(2,'0')}`); }} style={{ border:`1px solid ${COLORS.border}`,borderRadius:6,background:COLORS.surface,padding:"4px 10px",cursor:"pointer",fontSize:14 }}>›</button>
                </div>
                <div style={{ fontSize:12,color:COLORS.textMid,alignSelf:"flex-end",paddingBottom:2 }}>
                  Legenda: <span title="NFS-e">🧾</span> NF &nbsp;|&nbsp; <span title="Boleto">📄</span> Boleto &nbsp;|&nbsp; <span title="E-mail">📧</span> E-mail &nbsp;|&nbsp; <span title="Pago">✅</span> Pago
                </div>
              </div>
            ) : (
              <div style={{ display:"flex",alignItems:"center",gap:8 }}>
                <label style={{ fontSize:11,fontWeight:600,color:COLORS.textMid }}>Ano</label>
                <div style={{ display:"flex",gap:4 }}>
                  <button onClick={()=>setCobAno(a=>a-1)} style={{ border:`1px solid ${COLORS.border}`,borderRadius:6,background:COLORS.surface,padding:"4px 10px",cursor:"pointer",fontSize:14 }}>‹</button>
                  <span style={{ fontSize:16,fontWeight:800,padding:"4px 10px" }}>{cobAno}</span>
                  <button onClick={()=>setCobAno(a=>a+1)} style={{ border:`1px solid ${COLORS.border}`,borderRadius:6,background:COLORS.surface,padding:"4px 10px",cursor:"pointer",fontSize:14 }}>›</button>
                </div>
              </div>
            )}
            <div style={{ marginLeft:"auto",display:"flex",gap:4 }}>
              <button onClick={()=>setCobView("lista")} style={{ padding:"6px 14px",borderRadius:8,border:`1.5px solid ${cobView==="lista"?COLORS.primary:COLORS.border}`,background:cobView==="lista"?COLORS.primaryBg:COLORS.surface,color:cobView==="lista"?COLORS.primary:COLORS.textMid,fontWeight:600,fontSize:12,cursor:"pointer" }}>☰ Lista</button>
              <button onClick={()=>setCobView("calendario")} style={{ padding:"6px 14px",borderRadius:8,border:`1.5px solid ${cobView==="calendario"?COLORS.primary:COLORS.border}`,background:cobView==="calendario"?COLORS.primaryBg:COLORS.surface,color:cobView==="calendario"?COLORS.primary:COLORS.textMid,fontWeight:600,fontSize:12,cursor:"pointer" }}>📅 Calendário</button>
            </div>
          </div>
          {cobView === "lista" && <>
          <div style={{ display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10 }}>
            {[
              { label:`Total ${["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][mesNum-1]}/${anoNum}`, val:fmt(totalMes), color:COLORS.primary, sub:`${cobrancas.length} cobrança(s)` },
              { label:"Recebido", val:fmt(totalPago), color:"#16a34a", sub:`${cobrancas.filter(c=>c.status==="pago").length} pago(s)` },
              { label:"A Receber", val:fmt(totalPendente), color:COLORS.warning, sub:`${cobrancas.filter(c=>c.status==="pendente").length} pendente(s)` },
              { label:"NFS-e Emitidas", val:cobrancas.filter(c=>c.nfe_numero).length, color:COLORS.info, sub:`${cobrancas.filter(c=>c.boleto_id).length} boleto(s) gerado(s)` },
            ].map(card=>(
              <Card key={card.label} style={{ borderTop:`4px solid ${card.color}` }}>
                <div style={{ fontSize:11,color:COLORS.textMid,fontWeight:600 }}>{card.label}</div>
                <div style={{ fontSize:20,fontWeight:900,color:card.color }}>{card.val}</div>
                <div style={{ fontSize:11,color:COLORS.textLight }}>{card.sub}</div>
              </Card>
            ))}
          </div>

          <Card style={{ padding:0,overflow:"hidden" }}>
            <div style={{ overflowX:"auto" }}>
            <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:900 }}>
              <thead><tr style={{ background:COLORS.bg }}>
                {["Cliente","Contrato","Mês","Valor","Vencimento","Status","Controle","Ações"].map(h=>(
                  <th key={h} style={{ padding:"10px 14px",fontWeight:600,fontSize:11,color:COLORS.textMid,textAlign:"left",whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {cobrancas.map((c,i)=>{
                  const nfeOk = !!c.nfe_numero;
                  const boletoOk = !!c.boleto_id;
                  const emailOk = !!c.email_enviado_em;
                  const pagoOk = c.status === "pago" || c.boleto_status === "pago";
                  return (
                    <tr key={c.id} style={{ background:i%2===0?COLORS.surface:COLORS.bg,borderBottom:`1px solid ${COLORS.border}` }}>
                      <td style={{ padding:"10px 14px",fontWeight:700,whiteSpace:"nowrap" }}>{c.cliente_nome}</td>
                      <td style={{ padding:"10px 14px",fontSize:12,color:COLORS.textMid }}>{c.contrato_numero||`#${c.contrato_id}`}</td>
                      <td style={{ padding:"10px 14px",whiteSpace:"nowrap" }}>{String(c.mes).padStart(2,'0')}/{c.ano}</td>
                      <td style={{ padding:"10px 14px",fontWeight:700 }}>{fmt(c.valor)}</td>
                      <td style={{ padding:"10px 14px",fontSize:12,color:COLORS.textMid,whiteSpace:"nowrap" }}>{c.data_vencimento?new Date(c.data_vencimento).toLocaleDateString("pt-BR"):"—"}</td>
                      <td style={{ padding:"6px 14px" }}>
                        <select value={c.status} onChange={e=>atualizarCobranca(c.id,{status:e.target.value})}
                          style={{ padding:"4px 8px",borderRadius:6,border:`1px solid ${COLORS.border}`,background:STATUS_COB_COLOR[c.status]+"22",color:STATUS_COB_COLOR[c.status],fontWeight:700,fontSize:12 }}>
                          <option value="pendente">Pendente</option>
                          <option value="pago">Pago</option>
                          <option value="vencido">Vencido</option>
                          <option value="cancelado">Cancelado</option>
                        </select>
                      </td>
                      {/* ÍCONES DE CONTROLE */}
                      <td style={{ padding:"6px 14px" }}>
                        <div style={{ display:"flex",gap:6,alignItems:"center" }}>
                          {/* 🧾 NFS-e */}
                          {c.nfe_status === 'processando' ? (
                            <button title="NFS-e processando — clique para consultar status" onClick={()=>consultarNfe(c.id)} disabled={salvando}
                              style={{ background:"none",border:`2px solid ${COLORS.warning}`,borderRadius:8,padding:"4px 8px",cursor:"pointer",fontSize:16 }}>
                              🧾<span style={{ fontSize:9,color:COLORS.warning,marginLeft:2,fontWeight:700 }}>⏳</span>
                            </button>
                          ) : nfeOk ? (
                            <button title={`NFS-e #${c.nfe_numero||c.nfe_ref} — Autorizada`} style={{ background:"none",border:`2px solid #16a34a`,borderRadius:8,padding:"4px 8px",cursor:"default",fontSize:16 }}>
                              🧾<span style={{ fontSize:9,color:"#16a34a",marginLeft:2,fontWeight:700 }}>✓</span>
                            </button>
                          ) : (
                            <button title={configNfe?.api_key?"Clique para emitir NFS-e":"Configure a NFS-e nas configurações"}
                              onClick={()=>{
                                if(!configNfe?.api_key) return;
                                // Monta form inicial da prévia
                                let issCodigos = [];
                                try { issCodigos = c.iss_codigos ? JSON.parse(c.iss_codigos) : []; } catch(e) {}
                                const ctrib = issCodigos[0]?.codigo?.replace(/\D/g,'') || configNfe?.ctrib_nac || '170602';
                                const MESES_N = ['Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
                                const desc = `Serviços de ${c.objeto||'Prestação de Serviços'} referente ao contrato nº ${c.contrato_numero||c.contrato_id}, período de competência ${MESES_N[(c.mes||1)-1]}/${c.ano}.`;
                                setNfeForm({ descricao_servico: desc, ctrib_nac: ctrib, cnpj_tomador: c.cliente_cnpj||'' });
                                setNfePreview(c);
                              }}
                              style={{ background:"none",border:`2px solid ${COLORS.border}`,borderRadius:8,padding:"4px 8px",cursor:configNfe?.api_key?"pointer":"default",fontSize:16,opacity:!configNfe?.api_key?0.4:1 }}>
                              🧾
                            </button>
                          )}
                          {/* 📄 Boleto */}
                          <button title={boletoOk?(c.boleto_url?"Clique para abrir o boleto":"Boleto gerado"):"Clique para gerar boleto Inter"}
                            onClick={()=>{ if(boletoOk&&c.boleto_url){ window.open(c.boleto_url,"_blank"); } else if(!boletoOk){ gerarBoleto(c.id); } }}
                            style={{ background:"none",border:`2px solid ${boletoOk?"#2563eb":COLORS.border}`,borderRadius:8,padding:"4px 8px",cursor:"pointer",fontSize:16 }}>
                            📄{boletoOk&&<span style={{ fontSize:9,color:"#2563eb",marginLeft:2,fontWeight:700 }}>✓</span>}
                          </button>
                          {/* 📧 E-mail */}
                          <button title={emailOk?`E-mail enviado em ${new Date(c.email_enviado_em).toLocaleDateString("pt-BR")}`:"Clique para enviar e-mail com boleto/NFS-e"}
                            onClick={()=>!emailOk&&enviarEmail(c.id)}
                            style={{ background:"none",border:`2px solid ${emailOk?"#16a34a":COLORS.border}`,borderRadius:8,padding:"4px 8px",cursor:emailOk?"default":"pointer",fontSize:16 }}>
                            📧{emailOk&&<span style={{ fontSize:9,color:"#16a34a",marginLeft:2,fontWeight:700 }}>✓</span>}
                          </button>
                          {/* ✅ Pago / Verificar */}
                          <button title={pagoOk?"Pago":(boletoOk?"Clique para verificar pagamento no banco":"Marque como pago manualmente")}
                            onClick={()=>{ if(!pagoOk){ if(boletoOk){ verificarPagamento(c.id); } else { atualizarCobranca(c.id,{status:'pago',data_pagamento:new Date().toISOString().slice(0,10)}); } } }}
                            style={{ background:"none",border:`2px solid ${pagoOk?"#16a34a":COLORS.border}`,borderRadius:8,padding:"4px 8px",cursor:pagoOk?"default":"pointer",fontSize:16 }}>
                            ✅{pagoOk&&<span style={{ fontSize:9,color:"#16a34a",marginLeft:2,fontWeight:700 }}>✓</span>}
                          </button>
                        </div>
                      </td>
                      <td style={{ padding:"6px 14px" }}>
                        {c.boleto_codigo && <div style={{ fontSize:10,color:COLORS.textLight,maxWidth:140,overflow:"hidden",textOverflow:"ellipsis" }} title={c.boleto_codigo}>{c.boleto_codigo}</div>}
                      </td>
                    </tr>
                  );
                })}
                {!cobrancas.length && <tr><td colSpan={8} style={{ padding:"32px",textAlign:"center",color:COLORS.textMid }}>Nenhuma cobrança no mês selecionado</td></tr>}
              </tbody>
            </table>
            </div>
          </Card>
          </>}

          {/* VISUALIZAÇÃO CALENDÁRIO */}
          {cobView === "calendario" && (() => {
            // Mapeia cobranças por data de emissão (vencimento - 10 dias)
            const porDia = {};
            cobAll.forEach(c => {
              if (!c.data_vencimento) return;
              const venc = new Date(c.data_vencimento);
              venc.setDate(venc.getDate() - 10);
              const key = `${venc.getFullYear()}-${venc.getMonth()+1}-${venc.getDate()}`;
              if (!porDia[key]) porDia[key] = { count:0, total:0, pago:0, items:[] };
              porDia[key].count++;
              porDia[key].total += parseFloat(c.valor||0);
              if (c.status === "pago") porDia[key].pago += parseFloat(c.valor||0);
              porDia[key].items.push(c);
            });
            const MESES = ["Janeiro","Fevereiro","Março","Abril","Maio","Junho","Julho","Agosto","Setembro","Outubro","Novembro","Dezembro"];
            const hoje = new Date();
            return (
              <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:14 }}>
                {MESES.map((nomeMes, mesIdx) => {
                  const primeiroDia = new Date(cobAno, mesIdx, 1);
                  const ultimoDia = new Date(cobAno, mesIdx+1, 0).getDate();
                  const iniciaSemana = primeiroDia.getDay(); // 0=dom
                  const diasNoMes = Array.from({ length: ultimoDia }, (_, i) => i+1);
                  // Dias com cobrança neste mês
                  const diasCom = {};
                  for (let d = 1; d <= ultimoDia; d++) {
                    const key = `${cobAno}-${mesIdx+1}-${d}`;
                    if (porDia[key]) diasCom[d] = porDia[key];
                  }
                  const totalMesCalendario = Object.values(diasCom).reduce((s,v)=>s+v.total,0);
                  const countMesCalendario = Object.values(diasCom).reduce((s,v)=>s+v.count,0);
                  return (
                    <Card key={mesIdx} style={{ padding:0, overflow:"hidden" }}>
                      <div style={{ background: countMesCalendario>0 ? COLORS.primaryBg : COLORS.bg, padding:"10px 14px", borderBottom:`1px solid ${COLORS.border}` }}>
                        <div style={{ fontWeight:800, fontSize:13, color: countMesCalendario>0 ? COLORS.primary : COLORS.textMid }}>{nomeMes}</div>
                        {countMesCalendario > 0 && (
                          <div style={{ fontSize:11, color:COLORS.primary, marginTop:2 }}>
                            {countMesCalendario} cobr. · {fmt(totalMesCalendario)}
                          </div>
                        )}
                      </div>
                      <div style={{ padding:"8px 10px" }}>
                        {/* Cabeçalhos dias semana */}
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:1, marginBottom:3 }}>
                          {["D","S","T","Q","Q","S","S"].map((d,i)=>(
                            <div key={i} style={{ textAlign:"center", fontSize:9, fontWeight:700, color:COLORS.textLight, padding:"2px 0" }}>{d}</div>
                          ))}
                        </div>
                        {/* Grade de dias */}
                        <div style={{ display:"grid", gridTemplateColumns:"repeat(7,1fr)", gap:2 }}>
                          {/* Células vazias antes do primeiro dia */}
                          {Array.from({ length: iniciaSemana }).map((_,i)=>(
                            <div key={`e${i}`} />
                          ))}
                          {diasNoMes.map(dia => {
                            const info = diasCom[dia];
                            const isHoje = cobAno === hoje.getFullYear() && mesIdx === hoje.getMonth() && dia === hoje.getDate();
                            const tooltipKey = `${cobAno}-${mesIdx}-${dia}`;
                            return (
                              <div key={dia}
                                onClick={()=>{ if(info){ const m = String(mesIdx+1).padStart(2,'0'); setMesCob(`${cobAno}-${m}`); setCobView("lista"); } }}
                                onMouseEnter={e=>{ if(info){ const rect=e.currentTarget.getBoundingClientRect(); setCalTooltip({ key:tooltipKey, info, x:rect.left, y:rect.bottom+6 }); } }}
                                onMouseLeave={()=>setCalTooltip(null)}
                                style={{
                                  textAlign:"center", fontSize:10, borderRadius:5, padding:"3px 1px",
                                  background: info ? COLORS.primary : isHoje ? COLORS.primaryBg : "transparent",
                                  color: info ? "#fff" : isHoje ? COLORS.primary : COLORS.text,
                                  fontWeight: info ? 800 : isHoje ? 700 : 400,
                                  cursor: info ? "pointer" : "default",
                                  lineHeight:1.2, position:"relative",
                                }}>
                                <div>{dia}</div>
                                {info && <div style={{ fontSize:8 }}>{info.count}cx</div>}
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    </Card>
                  );
                })}
              </div>
            );
          })()}
        </div>
      )}

      {/* ABA: PROPOSTAS */}
      {aba === "propostas" && (
        <Propostas key={propostasKey} token={token} clientes={clientes} telaInicial={propostasTelaInicial} modoNovoInicial={propostasModoInicial} empresaFiltroId={empresaFiltroId} />
      )}

      {/* Tooltip calendário (hover) */}
      {calTooltip && (
        <div style={{ position:"fixed", left:Math.min(calTooltip.x, window.innerWidth-220), top:calTooltip.y, zIndex:9999, background:COLORS.text, color:"#fff", borderRadius:10, padding:"10px 14px", fontSize:12, minWidth:180, boxShadow:"0 4px 24px #0005", pointerEvents:"none" }}>
          <div style={{ fontWeight:800, marginBottom:4 }}>📅 {calTooltip.info.count} cobrança(s)</div>
          <div>Total: <strong>{fmt(calTooltip.info.total)}</strong></div>
          {calTooltip.info.pago > 0 && <div style={{ color:"#86efac" }}>Pago: {fmt(calTooltip.info.pago)}</div>}
          {calTooltip.info.items.slice(0,3).map((item,i)=>(
            <div key={i} style={{ marginTop:4, borderTop:"1px solid rgba(255,255,255,.15)", paddingTop:4, fontSize:11 }}>
              {item.cliente_nome} · {fmt(item.valor)}
            </div>
          ))}
          {calTooltip.info.items.length > 3 && <div style={{ fontSize:10, color:"#94a3b8", marginTop:2 }}>+{calTooltip.info.items.length-3} mais...</div>}
          <div style={{ fontSize:10, color:"#94a3b8", marginTop:6 }}>Clique para ver na lista</div>
        </div>
      )}

    </div>
  );
}

// ─── SUPRIMENTOS ─────────────────────────────────────────────────────────────
function Suprimentos({ token, employees, setores, abaInicial }) {
  const [aba, setAba] = useState(abaInicial || "fornecedores");
  const ABAS = [
    { id:"fornecedores", label:"🏭 Fornecedores" },
    { id:"estoque",      label:"📦 Estoque" },
    { id:"itens-func",   label:"👕 EPIs/Uniformes" },
  ];

  // ─ FORNECEDORES ──────────────────────────────────────────────────────────────
  const [fornecedores, setFornecedores] = useState([]);
  const [buscaForn, setBuscaForn] = useState("");
  const [modalForn, setModalForn] = useState(null); // null | {} | {id,...}
  const [fforn, setFforn] = useState({});
  const [salvandoForn, setSalvandoForn] = useState(false);

  const carregarForn = () => apiFetch("GET", `/api/suprimentos/fornecedores?busca=${encodeURIComponent(buscaForn)}`, undefined, token)
    .then(r => setFornecedores(Array.isArray(r)?r:[])).catch(()=>{});

  useEffect(() => { if(token && aba==="fornecedores") carregarForn(); }, [token, aba, buscaForn]);

  const salvarForn = async () => {
    if(!fforn.nome) return alert("Informe o nome do fornecedor.");
    setSalvandoForn(true);
    try {
      if(modalForn?.id) await apiFetch("PUT", `/api/suprimentos/fornecedores/${modalForn.id}`, fforn, token);
      else await apiFetch("POST", "/api/suprimentos/fornecedores", fforn, token);
      carregarForn(); setModalForn(null);
    } catch(e) { alert(e.message); }
    setSalvandoForn(false);
  };
  const deletarForn = async (id) => {
    if(!window.confirm("Excluir fornecedor?")) return;
    await apiFetch("DELETE", `/api/suprimentos/fornecedores/${id}`, undefined, token).catch(()=>{});
    carregarForn();
  };

  // ─ PRODUTOS & ESTOQUE ────────────────────────────────────────────────────────
  const [produtos, setProdutos] = useState([]);
  const [saldo, setSaldo] = useState([]);
  const [movimentos, setMovimentos] = useState([]);
  const [abaEst, setAbaEst] = useState("saldo"); // saldo | entradas | saidas | novo-produto
  const [modalMov, setModalMov] = useState(null); // null | "entrada" | "saida"
  const [modalProd, setModalProd] = useState(null);
  const [fprod, setFprod] = useState({ nome:"", categoria:"geral", unidade:"un" });
  const [fmov, setFmov] = useState({});
  const [nfFile, setNfFile] = useState(null);
  const [salvandoEst, setSalvandoEst] = useState(false);

  const CATEGORIAS = [
    { id:"insumos", label:"Insumos" },
    { id:"limpeza", label:"Limpeza" },
    { id:"uniforme", label:"Uniforme" },
    { id:"epi", label:"EPI" },
    { id:"escritorio", label:"Escritório" },
    { id:"manutencao", label:"Manutenção" },
    { id:"alimentacao", label:"Alimentação" },
    { id:"geral", label:"Geral" },
  ];
  const CAT_COLORS = { insumos:"#6366f1",limpeza:"#06b6d4",uniforme:"#f59e0b",epi:"#ef4444",escritorio:"#8b5cf6",manutencao:"#64748b",alimentacao:"#16a34a",geral:"#94a3b8" };

  const carregarEstoque = () => {
    apiFetch("GET", "/api/suprimentos/produtos", undefined, token).then(r=>setProdutos(Array.isArray(r)?r:[])).catch(()=>{});
    apiFetch("GET", "/api/suprimentos/estoque/saldo", undefined, token).then(r=>setSaldo(Array.isArray(r)?r:[])).catch(()=>{});
    apiFetch("GET", "/api/suprimentos/estoque", undefined, token).then(r=>setMovimentos(Array.isArray(r)?r:[])).catch(()=>{});
    apiFetch("GET", "/api/suprimentos/fornecedores", undefined, token).then(r=>setFornecedores(Array.isArray(r)?r:[])).catch(()=>{});
  };

  useEffect(() => { if(token && aba==="estoque") carregarEstoque(); }, [token, aba]);

  const salvarProduto = async () => {
    if(!fprod.nome) return alert("Informe o nome.");
    setSalvandoEst(true);
    try {
      await apiFetch("POST", "/api/suprimentos/produtos", fprod, token);
      carregarEstoque(); setModalProd(null); setFprod({ nome:"", categoria:"geral", unidade:"un" });
    } catch(e) { alert(e.message); }
    setSalvandoEst(false);
  };

  const registrarEntrada = async () => {
    if(!fmov.produto_id || !fmov.quantidade) return alert("Informe produto e quantidade.");
    setSalvandoEst(true);
    try {
      const fd = new FormData();
      Object.entries(fmov).forEach(([k,v]) => { if(v!==undefined&&v!==null&&v!=="") fd.append(k,v); });
      if(nfFile) fd.append("nf_arquivo", nfFile);
      await fetch(`/api/suprimentos/estoque/entrada`, { method:"POST", headers:{ Authorization:`Bearer ${token}` }, body:fd });
      carregarEstoque(); setModalMov(null); setFmov({}); setNfFile(null);
    } catch(e) { alert(e.message); }
    setSalvandoEst(false);
  };

  const registrarSaida = async () => {
    if(!fmov.produto_id || !fmov.quantidade) return alert("Informe produto e quantidade.");
    setSalvandoEst(true);
    try {
      await apiFetch("POST", "/api/suprimentos/estoque/saida", fmov, token);
      carregarEstoque(); setModalMov(null); setFmov({});
    } catch(e) { alert(e.message); }
    setSalvandoEst(false);
  };

  // ─ ITENS-FUNC ────────────────────────────────────────────────────────────────
  const [itensFuncionarios, setItensFuncionarios] = useState([]);
  const [filtroFuncItens, setFiltroFuncItens] = useState("");

  useEffect(() => {
    if(token && aba==="itens-func") {
      apiFetch("GET", "/api/suprimentos/itens-funcionarios", undefined, token)
        .then(r=>setItensFuncionarios(Array.isArray(r)?r:[])).catch(()=>{});
    }
  }, [token, aba]);

  const itensFiltrados = itensFuncionarios.filter(i =>
    !filtroFuncItens || (i.funcionario_nome||"").toLowerCase().includes(filtroFuncItens.toLowerCase())
  );

  const fmtDt = (d) => { if(!d) return "-"; const p=d.slice(0,10).split('-'); return `${p[2]}/${p[1]}/${p[0]}`; };

  // ─── SUBSETORES para local ────────────────────────────────────────────────────
  const [subsetores, setSubsetores] = useState([]);
  const setorSel = fmov.setor_id ? setores?.find(s=>s.id===parseInt(fmov.setor_id)) : null;
  useEffect(() => {
    if(fmov.setor_id) apiFetch("GET",`/api/setores/${fmov.setor_id}/subsetores`,undefined,token).then(r=>setSubsetores(Array.isArray(r)?r:[])).catch(()=>{});
    else setSubsetores([]);
  }, [fmov.setor_id, token]);

  const fieldStyle = { width:"100%", padding:"8px 12px", borderRadius:8, border:`1px solid ${COLORS.border}`, fontSize:13, background:COLORS.bg, color:COLORS.text, boxSizing:"border-box" };
  const labelStyle = { fontSize:11, fontWeight:600, color:COLORS.textMid, marginBottom:3, display:"block" };

  return (
    <div style={{ display:"flex", flexDirection:"column", gap:16 }}>
      {/* Tabs */}
      <div style={{ display:"flex", gap:0, background:COLORS.surface, borderRadius:"12px 12px 0 0", border:`1px solid ${COLORS.border}`, borderBottom:"none", overflow:"hidden" }}>
        {ABAS.map(a => (
          <button key={a.id} onClick={()=>setAba(a.id)} style={{ flex:1, padding:"12px 16px", border:"none", borderBottom: aba===a.id ? `3px solid ${COLORS.primary}` : "3px solid transparent", background:"transparent", color: aba===a.id ? COLORS.primary : COLORS.textMid, fontWeight: aba===a.id ? 700 : 500, fontSize:13, cursor:"pointer" }}>
            {a.label}
          </button>
        ))}
      </div>

      {/* ─── FORNECEDORES ──────────────────────────────────────────────────── */}
      {aba === "fornecedores" && (
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          <div style={{ display:"flex",gap:8,alignItems:"center",flexWrap:"wrap" }}>
            <input value={buscaForn} onChange={e=>setBuscaForn(e.target.value)} placeholder="Buscar por nome ou CNPJ..." style={{ ...fieldStyle, width:260 }} />
            <Btn onClick={()=>{ setFforn({}); setModalForn({}); }}>+ Novo Fornecedor</Btn>
          </div>
          <Card style={{ padding:0, overflow:"hidden" }}>
            <table style={{ width:"100%", borderCollapse:"collapse", fontSize:13 }}>
              <thead><tr style={{ background:COLORS.bg }}>
                {["Nome","CNPJ","Segmento","Telefone","E-mail","Contato","Ações"].map(h=>(
                  <th key={h} style={{ padding:"10px 14px",fontWeight:600,fontSize:11,color:COLORS.textMid,textAlign:"left",whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {fornecedores.length===0 && <tr><td colSpan={7} style={{ padding:32, textAlign:"center", color:COLORS.textMid }}>Nenhum fornecedor cadastrado</td></tr>}
                {fornecedores.map((f,i)=>(
                  <tr key={f.id} style={{ borderTop:`1px solid ${COLORS.border}`, background:i%2===0?COLORS.surface:"transparent" }}>
                    <td style={{ padding:"8px 14px",fontWeight:600 }}>{f.nome}</td>
                    <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{f.cnpj||"-"}</td>
                    <td style={{ padding:"8px 14px" }}>{f.segmento||"-"}</td>
                    <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{f.telefone||"-"}</td>
                    <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{f.email||"-"}</td>
                    <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{f.contato_nome||"-"}</td>
                    <td style={{ padding:"8px 14px" }}>
                      <div style={{ display:"flex",gap:4 }}>
                        <Btn size="sm" onClick={()=>{ setFforn({...f}); setModalForn(f); }}>✏️</Btn>
                        <Btn size="sm" variant="danger" onClick={()=>deletarForn(f.id)}>🗑</Btn>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ─── ESTOQUE ───────────────────────────────────────────────────────── */}
      {aba === "estoque" && (
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          <div style={{ display:"flex",gap:8,flexWrap:"wrap",alignItems:"center" }}>
            <Btn onClick={()=>{ setFmov({ tipo:"entrada", local_tipo:"sede" }); setModalMov("entrada"); }}>+ Entrada</Btn>
            <Btn variant="secondary" onClick={()=>{ setFmov({ tipo:"saida", local_tipo:"sede" }); setModalMov("saida"); }}>- Saída</Btn>
            <Btn variant="ghost" onClick={()=>{ setFprod({ nome:"", categoria:"geral", unidade:"un" }); setModalProd(true); }}>+ Cadastrar Produto</Btn>
            <div style={{ marginLeft:"auto",display:"flex",gap:4 }}>
              {[{id:"saldo",l:"📊 Saldo"},{id:"entradas",l:"⬇ Entradas"},{id:"saidas",l:"⬆ Saídas"}].map(t=>(
                <button key={t.id} onClick={()=>setAbaEst(t.id)} style={{ padding:"6px 12px",borderRadius:8,border:`1.5px solid ${abaEst===t.id?COLORS.primary:COLORS.border}`,background:abaEst===t.id?COLORS.primaryBg:COLORS.surface,color:abaEst===t.id?COLORS.primary:COLORS.textMid,fontWeight:600,fontSize:12,cursor:"pointer" }}>{t.l}</button>
              ))}
            </div>
          </div>

          {abaEst === "saldo" && (
            <div style={{ display:"grid",gridTemplateColumns:"repeat(auto-fill,minmax(220px,1fr))",gap:10 }}>
              {saldo.length===0 && <div style={{ color:COLORS.textMid,padding:16 }}>Nenhum produto em estoque</div>}
              {saldo.map(s=>(
                <Card key={s.produto_id} style={{ borderLeft:`4px solid ${CAT_COLORS[s.categoria]||"#94a3b8"}` }}>
                  <div style={{ fontWeight:700,fontSize:14 }}>{s.nome}</div>
                  <div style={{ fontSize:11,color:COLORS.textMid,marginTop:2 }}>{CATEGORIAS.find(c=>c.id===s.categoria)?.label||s.categoria}</div>
                  <div style={{ fontSize:24,fontWeight:900,color:parseFloat(s.saldo)>0?COLORS.primary:COLORS.danger,marginTop:6 }}>{parseFloat(s.saldo).toFixed(s.unidade==="un"?0:2)} <span style={{ fontSize:13,fontWeight:400,color:COLORS.textMid }}>{s.unidade}</span></div>
                </Card>
              ))}
            </div>
          )}

          {(abaEst==="entradas"||abaEst==="saidas") && (
            <Card style={{ padding:0, overflow:"hidden" }}>
              <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:700 }}>
                <thead><tr style={{ background:COLORS.bg }}>
                  {["Produto","Categoria","Qtd","Data","Fornecedor/Funcionário","Local","Nota Fiscal","Obs"].map(h=>(
                    <th key={h} style={{ padding:"10px 14px",fontWeight:600,fontSize:11,color:COLORS.textMid,textAlign:"left",whiteSpace:"nowrap" }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {movimentos.filter(m=>m.tipo===(abaEst==="entradas"?"entrada":"saida")).length===0 && (
                    <tr><td colSpan={8} style={{ padding:32,textAlign:"center",color:COLORS.textMid }}>Nenhum registro</td></tr>
                  )}
                  {movimentos.filter(m=>m.tipo===(abaEst==="entradas"?"entrada":"saida")).map((m,i)=>(
                    <tr key={m.id} style={{ borderTop:`1px solid ${COLORS.border}`,background:i%2===0?COLORS.surface:"transparent" }}>
                      <td style={{ padding:"8px 14px",fontWeight:600 }}>{m.produto_nome}</td>
                      <td style={{ padding:"8px 14px" }}><span style={{ background:CAT_COLORS[m.categoria]+"22",color:CAT_COLORS[m.categoria],padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600 }}>{CATEGORIAS.find(c=>c.id===m.categoria)?.label||m.categoria}</span></td>
                      <td style={{ padding:"8px 14px",fontWeight:700 }}>{m.quantidade} {m.unidade}</td>
                      <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{fmtDt(m.data_movto)}</td>
                      <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{m.fornecedor_nome||m.funcionario_nome||"-"}</td>
                      <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{m.local_tipo==="setor"?`${m.setor_nome||""}${m.subsetor_nome?"/"+m.subsetor_nome:""}` : "Sede"}</td>
                      <td style={{ padding:"8px 14px" }}>{m.nf_numero ? <span style={{ fontSize:12 }}>#{m.nf_numero} {m.nf_arquivo_path && <a href={`/api/suprimentos/estoque/${m.id}/nf`} target="_blank" rel="noreferrer" style={{ fontSize:11,color:COLORS.primary }}>📎</a>}</span> : "-"}</td>
                      <td style={{ padding:"8px 14px",color:COLORS.textMid,maxWidth:120,overflow:"hidden",textOverflow:"ellipsis",whiteSpace:"nowrap" }}>{m.observacoes||"-"}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </Card>
          )}
        </div>
      )}

      {/* ─── ITENS-FUNCIONÁRIOS ────────────────────────────────────────────── */}
      {aba === "itens-func" && (
        <div style={{ display:"flex",flexDirection:"column",gap:12 }}>
          <input value={filtroFuncItens} onChange={e=>setFiltroFuncItens(e.target.value)} placeholder="Filtrar por funcionário..." style={{ ...fieldStyle, width:280 }} />
          <Card style={{ padding:0, overflow:"hidden" }}>
            <table style={{ width:"100%",borderCollapse:"collapse",fontSize:13,minWidth:600 }}>
              <thead><tr style={{ background:COLORS.bg }}>
                {["Funcionário","Cargo","Item","Categoria","Qtd","Data Entrega","Obs"].map(h=>(
                  <th key={h} style={{ padding:"10px 14px",fontWeight:600,fontSize:11,color:COLORS.textMid,textAlign:"left",whiteSpace:"nowrap" }}>{h}</th>
                ))}
              </tr></thead>
              <tbody>
                {itensFiltrados.length===0 && <tr><td colSpan={7} style={{ padding:32,textAlign:"center",color:COLORS.textMid }}>Nenhum item entregue a funcionários</td></tr>}
                {itensFiltrados.map((it,i)=>(
                  <tr key={it.id} style={{ borderTop:`1px solid ${COLORS.border}`,background:i%2===0?COLORS.surface:"transparent" }}>
                    <td style={{ padding:"8px 14px",fontWeight:600 }}>{it.funcionario_nome||"-"}</td>
                    <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{it.funcionario_cargo||"-"}</td>
                    <td style={{ padding:"8px 14px" }}>{it.produto_nome}</td>
                    <td style={{ padding:"8px 14px" }}><span style={{ background:CAT_COLORS[it.categoria]+"22",color:CAT_COLORS[it.categoria],padding:"2px 8px",borderRadius:10,fontSize:11,fontWeight:600 }}>{CATEGORIAS.find(c=>c.id===it.categoria)?.label||it.categoria}</span></td>
                    <td style={{ padding:"8px 14px",fontWeight:700 }}>{it.quantidade} {it.unidade}</td>
                    <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{fmtDt(it.data_movto)}</td>
                    <td style={{ padding:"8px 14px",color:COLORS.textMid }}>{it.observacoes||"-"}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        </div>
      )}

      {/* ─── MODAL FORNECEDOR ──────────────────────────────────────────────── */}
      {modalForn !== null && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={e=>{ if(e.target===e.currentTarget) setModalForn(null); }}>
          <Card style={{ width:520,maxHeight:"90vh",overflowY:"auto",padding:24 }}>
            <h3 style={{ margin:"0 0 16px",fontSize:16 }}>{modalForn.id?"Editar":"Novo"} Fornecedor</h3>
            <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:12 }}>
              {[
                { k:"nome",         l:"Nome*",      full:true },
                { k:"cnpj",         l:"CNPJ" },
                { k:"segmento",     l:"Segmento" },
                { k:"telefone",     l:"Telefone" },
                { k:"email",        l:"E-mail" },
                { k:"contato_nome", l:"Nome do Contato" },
                { k:"endereco",     l:"Endereço", full:true },
                { k:"cidade",       l:"Cidade" },
                { k:"uf",           l:"UF", short:true },
              ].map(({k,l,full,short})=>(
                <div key={k} style={{ gridColumn: full?"1/-1":short?"auto":"auto" }}>
                  <label style={labelStyle}>{l}</label>
                  <input value={fforn[k]||""} onChange={e=>setFforn(p=>({...p,[k]:e.target.value}))} style={{ ...fieldStyle, width:short?80:"100%" }} />
                </div>
              ))}
              <div style={{ gridColumn:"1/-1" }}>
                <label style={labelStyle}>Observações</label>
                <textarea value={fforn.observacoes||""} onChange={e=>setFforn(p=>({...p,observacoes:e.target.value}))} rows={3} style={{ ...fieldStyle,resize:"vertical" }} />
              </div>
            </div>
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
              <Btn variant="ghost" onClick={()=>setModalForn(null)}>Cancelar</Btn>
              <Btn onClick={salvarForn} disabled={salvandoForn}>{salvandoForn?"Salvando...":"Salvar"}</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ─── MODAL NOVO PRODUTO ────────────────────────────────────────────── */}
      {modalProd && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={e=>{ if(e.target===e.currentTarget) setModalProd(null); }}>
          <Card style={{ width:400,padding:24 }}>
            <h3 style={{ margin:"0 0 16px",fontSize:16 }}>Cadastrar Produto</h3>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <div><label style={labelStyle}>Nome*</label><input value={fprod.nome} onChange={e=>setFprod(p=>({...p,nome:e.target.value}))} style={fieldStyle} /></div>
              <div><label style={labelStyle}>Categoria</label>
                <select value={fprod.categoria} onChange={e=>setFprod(p=>({...p,categoria:e.target.value}))} style={fieldStyle}>
                  {CATEGORIAS.map(c=><option key={c.id} value={c.id}>{c.label}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Unidade (un, kg, L, cx...)</label><input value={fprod.unidade} onChange={e=>setFprod(p=>({...p,unidade:e.target.value}))} style={fieldStyle} /></div>
              <div><label style={labelStyle}>Descrição</label><textarea value={fprod.descricao||""} onChange={e=>setFprod(p=>({...p,descricao:e.target.value}))} rows={2} style={{ ...fieldStyle,resize:"vertical" }} /></div>
            </div>
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
              <Btn variant="ghost" onClick={()=>setModalProd(null)}>Cancelar</Btn>
              <Btn onClick={salvarProduto} disabled={salvandoEst}>{salvandoEst?"Salvando...":"Salvar"}</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ─── MODAL ENTRADA ─────────────────────────────────────────────────── */}
      {modalMov === "entrada" && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={e=>{ if(e.target===e.currentTarget){ setModalMov(null); setFmov({}); setNfFile(null); } }}>
          <Card style={{ width:500,maxHeight:"90vh",overflowY:"auto",padding:24 }}>
            <h3 style={{ margin:"0 0 16px",fontSize:16 }}>Registrar Entrada</h3>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <div><label style={labelStyle}>Produto*</label>
                <select value={fmov.produto_id||""} onChange={e=>setFmov(p=>({...p,produto_id:e.target.value}))} style={fieldStyle}>
                  <option value="">Selecionar produto</option>
                  {produtos.map(p=><option key={p.id} value={p.id}>{p.nome} ({CATEGORIAS.find(c=>c.id===p.categoria)?.label||p.categoria})</option>)}
                </select>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                <div><label style={labelStyle}>Quantidade*</label><input type="number" min="0.001" step="0.001" value={fmov.quantidade||""} onChange={e=>setFmov(p=>({...p,quantidade:e.target.value}))} style={fieldStyle} /></div>
                <div><label style={labelStyle}>Data Entrada</label><input type="date" value={fmov.data_entrada||new Date().toISOString().slice(0,10)} onChange={e=>setFmov(p=>({...p,data_entrada:e.target.value}))} style={fieldStyle} /></div>
              </div>
              <div><label style={labelStyle}>Fornecedor</label>
                <select value={fmov.fornecedor_id||""} onChange={e=>setFmov(p=>({...p,fornecedor_id:e.target.value}))} style={fieldStyle}>
                  <option value="">Sem fornecedor</option>
                  {fornecedores.map(f=><option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
              </div>
              <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                <div><label style={labelStyle}>Nº Nota Fiscal</label><input value={fmov.nf_numero||""} onChange={e=>setFmov(p=>({...p,nf_numero:e.target.value}))} style={fieldStyle} /></div>
                <div><label style={labelStyle}>Arquivo NF (PDF/imagem)</label><input type="file" accept=".pdf,.jpg,.jpeg,.png" onChange={e=>setNfFile(e.target.files[0]||null)} style={{ fontSize:12 }} /></div>
              </div>
              <div><label style={labelStyle}>Local</label>
                <select value={fmov.local_tipo||"sede"} onChange={e=>setFmov(p=>({...p,local_tipo:e.target.value,setor_id:"",subsetor_id:""}))} style={fieldStyle}>
                  <option value="sede">Sede</option>
                  <option value="setor">Setor/Subsetor</option>
                </select>
              </div>
              {fmov.local_tipo === "setor" && (
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <div><label style={labelStyle}>Setor</label>
                    <select value={fmov.setor_id||""} onChange={e=>setFmov(p=>({...p,setor_id:e.target.value,subsetor_id:""}))} style={fieldStyle}>
                      <option value="">Selecionar</option>
                      {(setores||[]).map(s=><option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </div>
                  <div><label style={labelStyle}>Subsetor</label>
                    <select value={fmov.subsetor_id||""} onChange={e=>setFmov(p=>({...p,subsetor_id:e.target.value}))} style={fieldStyle}>
                      <option value="">Nenhum</option>
                      {subsetores.map(s=><option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div><label style={labelStyle}>Observações</label><textarea value={fmov.observacoes||""} onChange={e=>setFmov(p=>({...p,observacoes:e.target.value}))} rows={2} style={{ ...fieldStyle,resize:"vertical" }} /></div>
            </div>
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
              <Btn variant="ghost" onClick={()=>{ setModalMov(null); setFmov({}); setNfFile(null); }}>Cancelar</Btn>
              <Btn onClick={registrarEntrada} disabled={salvandoEst}>{salvandoEst?"Salvando...":"Registrar Entrada"}</Btn>
            </div>
          </Card>
        </div>
      )}

      {/* ─── MODAL SAÍDA ───────────────────────────────────────────────────── */}
      {modalMov === "saida" && (
        <div style={{ position:"fixed",inset:0,background:"rgba(0,0,0,.5)",zIndex:1000,display:"flex",alignItems:"center",justifyContent:"center" }} onClick={e=>{ if(e.target===e.currentTarget){ setModalMov(null); setFmov({}); } }}>
          <Card style={{ width:460,maxHeight:"90vh",overflowY:"auto",padding:24 }}>
            <h3 style={{ margin:"0 0 16px",fontSize:16 }}>Registrar Saída</h3>
            <div style={{ display:"flex",flexDirection:"column",gap:10 }}>
              <div><label style={labelStyle}>Produto*</label>
                <select value={fmov.produto_id||""} onChange={e=>setFmov(p=>({...p,produto_id:e.target.value}))} style={fieldStyle}>
                  <option value="">Selecionar produto</option>
                  {saldo.filter(s=>parseFloat(s.saldo)>0).map(s=>(
                    <option key={s.produto_id} value={s.produto_id}>{s.nome} (saldo: {parseFloat(s.saldo).toFixed(2)} {s.unidade})</option>
                  ))}
                </select>
              </div>
              <div><label style={labelStyle}>Quantidade*</label><input type="number" min="0.001" step="0.001" value={fmov.quantidade||""} onChange={e=>setFmov(p=>({...p,quantidade:e.target.value}))} style={fieldStyle} /></div>
              <div><label style={labelStyle}>Destino — Funcionário (para EPI/Uniforme)</label>
                <select value={fmov.funcionario_id||""} onChange={e=>setFmov(p=>({...p,funcionario_id:e.target.value}))} style={fieldStyle}>
                  <option value="">Nenhum (saída interna)</option>
                  {(employees||[]).filter(f=>f.status!=="demitido").map(f=><option key={f.id} value={f.id}>{f.nome}</option>)}
                </select>
              </div>
              <div><label style={labelStyle}>Local de Saída</label>
                <select value={fmov.local_tipo||"sede"} onChange={e=>setFmov(p=>({...p,local_tipo:e.target.value,setor_id:"",subsetor_id:""}))} style={fieldStyle}>
                  <option value="sede">Sede</option>
                  <option value="setor">Setor/Subsetor</option>
                </select>
              </div>
              {fmov.local_tipo === "setor" && (
                <div style={{ display:"grid",gridTemplateColumns:"1fr 1fr",gap:10 }}>
                  <div><label style={labelStyle}>Setor</label>
                    <select value={fmov.setor_id||""} onChange={e=>setFmov(p=>({...p,setor_id:e.target.value,subsetor_id:""}))} style={fieldStyle}>
                      <option value="">Selecionar</option>
                      {(setores||[]).map(s=><option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </div>
                  <div><label style={labelStyle}>Subsetor</label>
                    <select value={fmov.subsetor_id||""} onChange={e=>setFmov(p=>({...p,subsetor_id:e.target.value}))} style={fieldStyle}>
                      <option value="">Nenhum</option>
                      {subsetores.map(s=><option key={s.id} value={s.id}>{s.nome}</option>)}
                    </select>
                  </div>
                </div>
              )}
              <div><label style={labelStyle}>Observações</label><textarea value={fmov.observacoes||""} onChange={e=>setFmov(p=>({...p,observacoes:e.target.value}))} rows={2} style={{ ...fieldStyle,resize:"vertical" }} /></div>
            </div>
            <div style={{ display:"flex",gap:8,justifyContent:"flex-end",marginTop:16 }}>
              <Btn variant="ghost" onClick={()=>{ setModalMov(null); setFmov({}); }}>Cancelar</Btn>
              <Btn onClick={registrarSaida} disabled={salvandoEst}>{salvandoEst?"Salvando...":"Registrar Saída"}</Btn>
            </div>
          </Card>
        </div>
      )}
    </div>
  );
}

// ─── SETORES & SUBSETORES ─────────────────────────────────────────────────────
function Setores({ setores, setSetores, token, usuario, employees }) {
  const [clientes, setClientes] = useState([]);
  const [grupos, setGrupos] = useState([]);
  const [loading, setLoading] = useState(true);
  const [modal, setModal] = useState(false);
  const [salvando, setSalvando] = useState(false);
  const [confirmDel, setConfirmDel] = useState(null);
  const [abertos, setAbertos] = useState({});

  const emptyForm = () => ({
    cliente_id: "", nome: "", endereco: "", usar_end_cliente: false,
    subgrupos: [], setores_diretos: [],
  });
  const [form, setForm] = useState(emptyForm());

  const carregar = async () => {
    setLoading(true);
    const [c, g] = await Promise.all([
      apiFetch("GET", "/api/contratos/clientes", undefined, token).catch(() => []),
      apiFetch("GET", "/api/contratos/grupos-clientes", undefined, token).catch(() => []),
    ]);
    setClientes(Array.isArray(c) ? c : []);
    setGrupos(Array.isArray(g) ? g : []);
    setLoading(false);
  };
  useEffect(() => { if (token) carregar(); }, [token]);

  const tree = useMemo(() => {
    const m = {};
    for (const g of grupos.filter(g => g.nivel === "grupo")) {
      if (!m[g.cliente_id]) m[g.cliente_id] = [];
      const subs = grupos.filter(s => s.nivel === "subgrupo" && s.pai_id === g.id);
      m[g.cliente_id].push({
        ...g,
        subgrupos: subs.map(sg => ({ ...sg, setores: grupos.filter(s => s.nivel === "setor" && s.pai_id === sg.id) })),
        setores_diretos: grupos.filter(s => s.nivel === "setor" && s.pai_id === g.id),
      });
    }
    return m;
  }, [grupos]);

  const clientesComGrupos = clientes.filter(c => tree[c.id]?.length > 0);
  const cliSel = clientes.find(c => String(c.id) === String(form.cliente_id));

  const addSg = () => setForm(f => ({ ...f, subgrupos: [...f.subgrupos, { _key: Date.now(), nome: "", endereco: "", usar_end_cliente: true, setores: [] }] }));
  const rmSg = k => setForm(f => ({ ...f, subgrupos: f.subgrupos.filter(s => s._key !== k) }));
  const updSg = (k, field, val) => setForm(f => ({ ...f, subgrupos: f.subgrupos.map(s => s._key === k ? { ...s, [field]: val } : s) }));
  const addStInSg = k => setForm(f => ({ ...f, subgrupos: f.subgrupos.map(s => s._key === k ? { ...s, setores: [...s.setores, { _key: Date.now(), nome: "" }] } : s) }));
  const rmStInSg = (sgk, sk) => setForm(f => ({ ...f, subgrupos: f.subgrupos.map(s => s._key === sgk ? { ...s, setores: s.setores.filter(x => x._key !== sk) } : s) }));
  const updStInSg = (sgk, sk, val) => setForm(f => ({ ...f, subgrupos: f.subgrupos.map(s => s._key === sgk ? { ...s, setores: s.setores.map(x => x._key === sk ? { ...x, nome: val } : x) } : s) }));
  const addSt = () => setForm(f => ({ ...f, setores_diretos: [...f.setores_diretos, { _key: Date.now(), nome: "" }] }));
  const rmSt = k => setForm(f => ({ ...f, setores_diretos: f.setores_diretos.filter(s => s._key !== k) }));
  const updSt = (k, val) => setForm(f => ({ ...f, setores_diretos: f.setores_diretos.map(s => s._key === k ? { ...s, nome: val } : s) }));

  const salvar = async () => {
    if (!form.cliente_id) return alert("Selecione um cliente.");
    if (!form.nome.trim()) return alert("Informe o nome do grupo.");
    setSalvando(true);
    try {
      await apiFetch("POST", "/api/contratos/grupos-clientes", {
        cliente_id: form.cliente_id, nome: form.nome.trim(),
        endereco: form.usar_end_cliente ? null : (form.endereco || null),
        usar_end_cliente: form.usar_end_cliente ? 1 : 0,
        subgrupos: form.subgrupos.filter(s => s.nome.trim()).map(s => ({
          nome: s.nome.trim(),
          endereco: s.usar_end_cliente ? null : (s.endereco || null),
          usar_end_cliente: s.usar_end_cliente ? 1 : 0,
          setores: s.setores.filter(x => x.nome.trim()).map(x => ({ nome: x.nome.trim() })),
        })),
        setores: form.setores_diretos.filter(s => s.nome.trim()).map(s => ({ nome: s.nome.trim() })),
      }, token);
      setModal(false); setForm(emptyForm()); carregar();
    } catch (e) { alert(e.message); }
    setSalvando(false);
  };

  // Paleta clara — mesma intensidade do primaryBg do sistema (#edf7e3)
  const G = { bg: "#edf7e3", text: "#2a7100", accent: "#369502", muted: "#6aae3a" };
  const B = { bg: "#eff6ff", text: "#1e40af", accent: "#2563eb", muted: "#60a5fa" };
  const P = { bg: "#f5f3ff", text: "#5b21b6", accent: "#7c3aed", muted: "#a78bfa" };

  // Input dentro dos balões claros
  const inp = (c) => ({
    width: "100%", padding: "7px 10px", borderRadius: 7,
    border: `1.5px solid ${c.accent}30`, background: "#fff",
    fontSize: 12, color: COLORS.text, boxSizing: "border-box", outline: "none",
  });

  // Label de campo
  const fl = (c) => ({ fontSize: 10, fontWeight: 700, color: c.muted, display: "block", marginBottom: 3, textTransform: "uppercase", letterSpacing: .5 });

  // Título do balão
  const BalloonTitle = ({ c, children }) => (
    <div style={{ display: "inline-flex", alignItems: "center", gap: 6, marginBottom: 10 }}>
      <div style={{ width: 3, height: 13, borderRadius: 2, background: c.accent }} />
      <span style={{ fontSize: 11, fontWeight: 900, color: c.text, textTransform: "uppercase", letterSpacing: .8 }}>{children}</span>
    </div>
  );

  // Checkbox pill claro
  const chkPill = (c) => ({
    display: "inline-flex", alignItems: "center", gap: 5, cursor: "pointer",
    background: "#fff", border: `1.5px solid ${c.accent}40`,
    borderRadius: 6, padding: "5px 10px", userSelect: "none",
  });

  // Botão X
  const XBtn = ({ onClick }) => (
    <button onClick={onClick}
      style={{ background: "rgba(0,0,0,.05)", border: "none", borderRadius: 5, cursor: "pointer", color: "#94a3b8", display: "flex", alignItems: "center", padding: "4px 5px" }}
      onMouseEnter={e => { e.currentTarget.style.background = "#fee2e2"; e.currentTarget.style.color = "#ef4444"; }}
      onMouseLeave={e => { e.currentTarget.style.background = "rgba(0,0,0,.05)"; e.currentTarget.style.color = "#94a3b8"; }}>
      <svg width="10" height="10" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
    </button>
  );

  // Mini botão de ação dentro dos balões
  const mb = (c) => ({
    background: "#fff", border: `1.5px solid ${c.accent}50`, borderRadius: 7,
    padding: "4px 12px", fontSize: 11, fontWeight: 700, color: c.text, cursor: "pointer",
  });

  if (loading) return <div style={{ padding: 48, textAlign: "center", color: COLORS.textMid }}>Carregando...</div>;

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>

      {confirmDel && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.5)", zIndex: 2000, display: "flex", alignItems: "center", justifyContent: "center" }} onClick={() => setConfirmDel(null)}>
          <div style={{ background: COLORS.surface, borderRadius: 14, padding: "22px 26px", maxWidth: 380, width: "90%", boxShadow: "0 16px 48px #0005" }} onClick={e => e.stopPropagation()}>
            <div style={{ fontWeight: 800, fontSize: 14, marginBottom: 6 }}>Excluir grupo?</div>
            <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 18 }}>
              "<strong>{confirmDel.nome}</strong>" e todos seus subgrupos e setores serão apagados.
            </div>
            <div style={{ display: "flex", gap: 8 }}>
              <Btn variant="danger" onClick={() => excluir(confirmDel.id)}>Excluir</Btn>
              <Btn variant="secondary" onClick={() => setConfirmDel(null)}>Cancelar</Btn>
            </div>
          </div>
        </div>
      )}

      <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
        <div style={{ fontSize: 12, color: COLORS.textMid }}>
          {grupos.filter(g => g.nivel === "grupo").length} grupo(s) · {grupos.filter(g => g.nivel === "subgrupo").length} subgrupo(s) · {grupos.filter(g => g.nivel === "setor").length} setor(es)
        </div>
        <Btn onClick={() => { setForm(emptyForm()); setModal(true); }}>+ Criar Grupo</Btn>
      </div>

      {clientesComGrupos.length === 0 && (
        <div style={{ textAlign: "center", padding: "40px 24px", background: COLORS.surface, borderRadius: 14, border: `2px dashed ${COLORS.border}` }}>
          <div style={{ fontWeight: 700, fontSize: 13, color: COLORS.text, marginBottom: 4 }}>Nenhum grupo cadastrado</div>
          <div style={{ fontSize: 12, color: COLORS.textMid }}>Clique em "+ Criar Grupo" para começar</div>
        </div>
      )}

      {clientesComGrupos.map(cli => (
        <div key={cli.id} style={{ background: COLORS.surface, borderRadius: 12, overflow: "hidden", border: `1px solid ${COLORS.border}` }}>
          <div style={{ padding: "10px 16px", background: "linear-gradient(135deg,#1e40af,#3b82f6)", display: "flex", alignItems: "center", gap: 10 }}>
            <div style={{ flex: 1, fontWeight: 800, fontSize: 13, color: "#fff" }}>{cli.nome_fantasia || cli.nome}</div>
            <div style={{ fontSize: 11, color: "rgba(255,255,255,.75)", background: "rgba(255,255,255,.15)", padding: "2px 9px", borderRadius: 20 }}>
              {(tree[cli.id] || []).length} grupo(s)
            </div>
          </div>
          <div style={{ padding: "8px 12px", display: "flex", flexDirection: "column", gap: 6 }}>
            {(tree[cli.id] || []).map(g => {
              const open = !!abertos[g.id];
              return (
                <div key={g.id} style={{ borderRadius: 10, overflow: "hidden", background: G.bg, border: `1.5px solid ${G.accent}40` }}>
                  <div style={{ display: "flex", alignItems: "center", padding: "8px 10px", gap: 8 }}>
                    <button onClick={() => setAbertos(a => ({ ...a, [g.id]: !a[g.id] }))}
                      style={{ background: "none", border: "none", cursor: "pointer", display: "flex", alignItems: "center", gap: 7, flex: 1, padding: 0 }}>
                      <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke={G.muted} strokeWidth="3" strokeLinecap="round" style={{ flexShrink: 0, transform: open ? "rotate(90deg)" : "none", transition: "transform .2s" }}><path d="M9 18l6-6-6-6" /></svg>
                      <span style={{ fontWeight: 700, fontSize: 12, color: G.text }}>{g.nome}</span>
                      {g.usar_end_cliente ? <span style={{ fontSize: 10, color: G.muted }}>· mesmo do cliente</span>
                        : g.endereco ? <span style={{ fontSize: 10, color: G.muted }}>· {g.endereco}</span> : null}
                    </button>
                    <button onClick={() => setConfirmDel(g)}
                      style={{ background: "none", border: "none", cursor: "pointer", color: "#cbd5e1", display: "flex", padding: 3 }}
                      onMouseEnter={e => e.currentTarget.style.color = "#ef4444"} onMouseLeave={e => e.currentTarget.style.color = "#cbd5e1"}>
                      <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><polyline points="3 6 5 6 21 6" /><path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" /><path d="M9 6V4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v2" /></svg>
                    </button>
                  </div>
                  {open && (
                    <div style={{ padding: "0 8px 8px" }}>
                      {g.subgrupos.map(sg => (
                        <div key={sg.id} style={{ background: B.bg, border: `1.5px solid ${B.accent}40`, borderRadius: 8, margin: "4px 0 0", padding: "6px 9px" }}>
                          <div style={{ fontWeight: 700, fontSize: 11, color: B.text }}>
                            {sg.nome}
                            {sg.usar_end_cliente ? <span style={{ fontWeight: 400, color: B.muted, marginLeft: 5 }}>· mesmo do grupo</span>
                              : sg.endereco ? <span style={{ fontWeight: 400, marginLeft: 5, color: B.muted }}>· {sg.endereco}</span> : null}
                          </div>
                          {sg.setores.map(s => (
                            <div key={s.id} style={{ padding: "2px 8px", background: P.bg, border: `1px solid ${P.accent}30`, borderRadius: 5, marginTop: 3 }}>
                              <span style={{ fontSize: 11, color: P.text, fontWeight: 600 }}>{s.nome}</span>
                            </div>
                          ))}
                        </div>
                      ))}
                      {g.setores_diretos.map(s => (
                        <div key={s.id} style={{ padding: "4px 9px", background: P.bg, border: `1px solid ${P.accent}30`, borderRadius: 7, marginTop: 4 }}>
                          <span style={{ fontSize: 11, color: P.text, fontWeight: 600 }}>{s.nome}</span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>
      ))}

      {/* MODAL */}
      {modal && (
        <div style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,.55)", zIndex: 1000, display: "flex", alignItems: "flex-start", justifyContent: "center", overflowY: "auto", padding: "24px 16px" }}
          onClick={e => { if (e.target === e.currentTarget) setModal(false); }}>
          <div style={{ width: "min(660px,100%)", marginBottom: 20, background: COLORS.surface, borderRadius: 16, boxShadow: "0 20px 56px #0005", overflow: "hidden" }} onClick={e => e.stopPropagation()}>

            <div style={{ padding: "13px 20px", borderBottom: `1px solid ${COLORS.border}`, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
              <div style={{ fontWeight: 800, fontSize: 14, color: COLORS.text }}>Criar Grupo</div>
              <button onClick={() => setModal(false)} style={{ background: COLORS.bg, border: "none", borderRadius: 6, width: 26, height: 26, display: "flex", alignItems: "center", justifyContent: "center", cursor: "pointer", color: COLORS.textMid }}>
                <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round"><line x1="18" y1="6" x2="6" y2="18" /><line x1="6" y1="6" x2="18" y2="18" /></svg>
              </button>
            </div>

            <div style={{ padding: "14px 18px", overflowY: "auto", maxHeight: "72vh", display: "flex", flexDirection: "column", gap: 12 }}>

              {/* Cliente fora do balão */}
              <div>
                <label style={{ fontSize: 11, fontWeight: 700, color: COLORS.textMid, display: "block", marginBottom: 4, textTransform: "uppercase", letterSpacing: .4 }}>Cliente</label>
                <select value={form.cliente_id} onChange={e => setForm(f => ({ ...f, cliente_id: e.target.value }))}
                  style={{ width: "100%", padding: "8px 11px", borderRadius: 8, border: `1.5px solid ${COLORS.border}`, background: COLORS.surface, fontSize: 13, color: COLORS.text, outline: "none" }}>
                  <option value="">— Selecione o cliente —</option>
                  {clientes.map(c => <option key={c.id} value={c.id}>{c.nome_fantasia || c.nome}</option>)}
                </select>
              </div>

              {/* ── BALÃO GRUPO ── */}
              <div style={{ background: G.bg, border: `1.5px solid ${G.accent}40`, borderRadius: 12, padding: "12px 14px" }}>
                <BalloonTitle c={G}>Grupo</BalloonTitle>

                <div style={{ display: "flex", alignItems: "flex-end", gap: 10, marginBottom: 8 }}>
                  <div style={{ flex: 1 }}>
                    <label style={fl(G)}>Nome do Grupo</label>
                    <input value={form.nome} onChange={e => setForm(f => ({ ...f, nome: e.target.value }))}
                      placeholder="Ex: Unidade Centro..." style={inp(G)} />
                  </div>
                  <label style={chkPill(G)}>
                    <input type="checkbox" checked={form.usar_end_cliente} onChange={e => setForm(f => ({ ...f, usar_end_cliente: e.target.checked, endereco: "" }))}
                      style={{ width: 13, height: 13, cursor: "pointer", accentColor: G.accent }} />
                    <span style={{ fontSize: 11, fontWeight: 600, color: G.text }}>Mesmo do cliente</span>
                  </label>
                </div>

                <div style={{ marginBottom: 10 }}>
                  <label style={fl(G)}>Endereço</label>
                  {form.usar_end_cliente ? (
                    <input value={cliSel?.endereco || "—"} readOnly
                      style={{ ...inp(G), background: "#f1f5f9", color: "#94a3b8", cursor: "not-allowed" }} />
                  ) : (
                    <input value={form.endereco} onChange={e => setForm(f => ({ ...f, endereco: e.target.value }))}
                      placeholder="Endereço do grupo..." style={inp(G)} />
                  )}
                </div>

                {/* Subgrupos */}
                {form.subgrupos.map((sg, sgi) => (
                  <div key={sg._key} style={{ background: B.bg, border: `1.5px solid ${B.accent}40`, borderRadius: 9, padding: "10px 12px", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 9 }}>
                      <BalloonTitle c={B}>Subgrupo {sgi + 1}</BalloonTitle>
                      <XBtn onClick={() => rmSg(sg._key)} />
                    </div>

                    <div style={{ display: "flex", alignItems: "flex-end", gap: 8, marginBottom: !sg.usar_end_cliente ? 8 : 0 }}>
                      <div style={{ flex: 1 }}>
                        <label style={fl(B)}>Nome</label>
                        <input value={sg.nome} onChange={e => updSg(sg._key, "nome", e.target.value)}
                          placeholder="Ex: Bloco A, Ala Norte..." style={inp(B)} />
                      </div>
                      <label style={chkPill(B)}>
                        <input type="checkbox" checked={sg.usar_end_cliente} onChange={e => updSg(sg._key, "usar_end_cliente", e.target.checked)}
                          style={{ width: 13, height: 13, cursor: "pointer", accentColor: B.accent }} />
                        <span style={{ fontSize: 11, fontWeight: 600, color: B.text }}>Mesmo do grupo</span>
                      </label>
                    </div>

                    {!sg.usar_end_cliente && (
                      <div style={{ marginBottom: 8 }}>
                        <label style={fl(B)}>Endereço</label>
                        <input value={sg.endereco} onChange={e => updSg(sg._key, "endereco", e.target.value)}
                          placeholder="Endereço do subgrupo..." style={inp(B)} />
                      </div>
                    )}

                    {/* Setores dentro do subgrupo */}
                    {sg.setores.map(s => (
                      <div key={s._key} style={{ background: P.bg, border: `1.5px solid ${P.accent}40`, borderRadius: 8, padding: "8px 10px", marginTop: 8 }}>
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                          <BalloonTitle c={P}>Setor</BalloonTitle>
                          <XBtn onClick={() => rmStInSg(sg._key, s._key)} />
                        </div>
                        <label style={fl(P)}>Nome do Setor</label>
                        <input value={s.nome} onChange={e => updStInSg(sg._key, s._key, e.target.value)}
                          placeholder="Ex: Recepção, Limpeza..." style={inp(P)} />
                      </div>
                    ))}

                    <button onClick={() => addStInSg(sg._key)} style={{ ...mb(P), marginTop: 8 }}>+ Setor</button>
                  </div>
                ))}

                {/* Setores diretos */}
                {form.setores_diretos.map(s => (
                  <div key={s._key} style={{ background: P.bg, border: `1.5px solid ${P.accent}40`, borderRadius: 8, padding: "8px 10px", marginBottom: 8 }}>
                    <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 7 }}>
                      <BalloonTitle c={P}>Setor</BalloonTitle>
                      <XBtn onClick={() => rmSt(s._key)} />
                    </div>
                    <label style={fl(P)}>Nome do Setor</label>
                    <input value={s.nome} onChange={e => updSt(s._key, e.target.value)}
                      placeholder="Ex: Recepção, Limpeza..." style={inp(P)} />
                  </div>
                ))}

                <div style={{ display: "flex", gap: 8, marginTop: 4 }}>
                  <button onClick={addSg} style={mb(B)}>+ Subgrupo</button>
                  <button onClick={addSt} style={mb(P)}>+ Setor</button>
                </div>
              </div>
            </div>

            <div style={{ padding: "12px 18px", borderTop: `1px solid ${COLORS.border}`, display: "flex", gap: 8 }}>
              <Btn onClick={salvar} disabled={salvando}>{salvando ? "Salvando..." : "Salvar Grupo"}</Btn>
              <Btn variant="secondary" onClick={() => setModal(false)}>Cancelar</Btn>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ─── EMPRESAS ─────────────────────────────────────────────────────────────────
function Empresas({ empresas, setEmpresas, token, usuario }) {
  const canEdit = ["admin","administrador","gestor"].includes(usuario?.perfil);
  const VAZIO = { nome: "", razao_social: "", cnpj: "", telefone: "", email: "", site: "",
    endereco: "", numero: "", complemento: "", cidade: "", uf: "", cep: "",
    responsavel_nome: "", responsavel_cargo: "", logo_base64: "",
    banco_nome: "", banco_codigo: "", banco_ispb: "", agencia: "", agencia_dv: "", conta: "", conta_dv: "" };
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(VAZIO);
  const [editId, setEditId] = useState(null);
  const [excluirId, setExcluirId] = useState(null);
  const [sortCol, setSortCol] = useState("nome");
  const [sortDir, setSortDir] = useState("asc");

  const handleSort = (col) => {
    if (sortCol === col) setSortDir(d => d === "asc" ? "desc" : "asc");
    else { setSortCol(col); setSortDir("asc"); }
  };

  const sorted = [...empresas].sort((a, b) => {
    const va = (a[sortCol] || "").toString().toLowerCase();
    const vb = (b[sortCol] || "").toString().toLowerCase();
    return sortDir === "asc" ? va.localeCompare(vb, "pt-BR") : vb.localeCompare(va, "pt-BR");
  });

  const abrirNova = () => { setForm(VAZIO); setEditId(null); setModal("form"); };
  const abrirEditar = (e) => {
    setForm({ nome: e.nome||"", razao_social: e.razao_social||"", cnpj: e.cnpj||"",
      telefone: e.telefone||"", email: e.email||"", site: e.site||"",
      endereco: e.endereco||"", numero: e.numero||"", complemento: e.complemento||"",
      cidade: e.cidade||"", uf: e.uf||"", cep: e.cep||"",
      responsavel_nome: e.responsavel_nome||"", responsavel_cargo: e.responsavel_cargo||"",
      logo_base64: e.logo_base64||"",
      banco_nome: e.banco_nome||"", banco_codigo: e.banco_codigo||"", banco_ispb: e.banco_ispb||"",
      agencia: e.agencia||"", agencia_dv: e.agencia_dv||"", conta: e.conta||"", conta_dv: e.conta_dv||"",
    });
    setEditId(e.id); setModal("form");
  };

  const salvar = async () => {
    const nome = form.nome.trim();
    if (!nome) return alert("Nome da empresa é obrigatório.");
    try {
      if (editId) {
        await apiFetch("PUT", `/api/empresas/${editId}`, { ...form, nome }, token);
        setEmpresas(prev => prev.map(e => e.id === editId ? { ...e, ...form, nome } : e));
      } else {
        const nova = await apiFetch("POST", "/api/empresas", { ...form, nome }, token);
        setEmpresas(prev => [...prev, { id: nova.id, ...form, nome }]);
      }
      setModal(null);
    } catch(e) { alert("Erro ao salvar: " + e.message); }
  };

  const confirmarExcluir = (id) => { setExcluirId(id); setModal("excluir"); };
  const excluir = async () => {
    try {
      await apiFetch("DELETE", `/api/empresas/${excluirId}`, undefined, token);
      setEmpresas(prev => prev.filter(e => e.id !== excluirId));
      setModal(null);
    } catch(e) { alert("Erro ao excluir: " + e.message); }
  };

  const campo = (key, label, ph, grid) => (
    <Input label={label} value={form[key]||""} placeholder={ph}
      style={grid ? { gridColumn: grid } : undefined}
      onChange={e => setForm(f => ({ ...f, [key]: e.target.value }))}
    />
  );

  const col = (label, key) => ({ label, sortable: true, active: sortCol === key, dir: sortDir, onClick: () => handleSort(key) });

  const fmtConta = (e) => {
    if (!e.banco_nome && !e.agencia && !e.conta) return "—";
    const banco = e.banco_nome ? `${e.banco_nome}${e.banco_codigo ? ` · ${e.banco_codigo}` : ""}` : "";
    const ag = e.agencia ? `Ag. ${e.agencia}${e.agencia_dv ? `-${e.agencia_dv}` : ""}` : "";
    const ct = e.conta ? `Cc. ${e.conta}${e.conta_dv ? `-${e.conta_dv}` : ""}` : "";
    return [banco, ag, ct].filter(Boolean).join(" · ");
  };

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", justifyContent: "flex-end" }}>
        {canEdit && <Btn onClick={abrirNova}>+ Nova Empresa</Btn>}
      </div>

      <Card style={{ padding: 0 }}>
        <RhTable cols={[col("Nome", "nome"), col("CNPJ", "cnpj"), col("Cidade", "cidade"), "Conta Bancária (Pix/CNAB)", "Ações"]}>
          {sorted.length === 0 && (
            <RhTr>
              <TD style={{ textAlign: "center", padding: 32 }} muted colSpan={5}>
                Nenhuma empresa cadastrada. Clique em <strong>+ Nova Empresa</strong> para começar.
              </TD>
            </RhTr>
          )}
          {sorted.map(e => (
            <RhTr key={e.id}>
              <TD bold>{e.nome}{e.razao_social && e.razao_social !== e.nome && <div style={{fontSize:11,color:COLORS.textLight,fontWeight:400}}>{e.razao_social}</div>}</TD>
              <TD muted>{e.cnpj || "—"}</TD>
              <TD muted>{e.cidade ? `${e.cidade}${e.uf ? `/${e.uf}` : ""}` : "—"}</TD>
              <TD muted style={{fontSize:12}}>{fmtConta(e)}</TD>
              <TD>
                {canEdit && (
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn size="sm" variant="ghost" onClick={() => abrirEditar(e)}>Editar</Btn>
                    <Btn size="sm" variant="danger" onClick={() => confirmarExcluir(e.id)}>Excluir</Btn>
                  </div>
                )}
              </TD>
            </RhTr>
          ))}
        </RhTable>
      </Card>

      {modal === "form" && (
        <Modal title={editId ? "Editar Empresa" : "Nova Empresa"} onClose={() => setModal(null)}>
          <div style={{ overflowX: "hidden" }}>
            {/* Dados gerais */}
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8 }}>Dados da Empresa</div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
              {campo("nome", "Nome Fantasia *", "Ex: Classe")}
              {campo("razao_social", "Razão Social", "Ex: Classe Multisserviços Ltda")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
              {campo("cnpj", "CNPJ", "00.000.000/0000-00")}
              {campo("telefone", "Telefone", "(31) 9 0000-0000")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
              {campo("email", "E-mail", "contato@empresa.com.br")}
              {campo("site", "Site", "www.empresa.com.br")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              {campo("responsavel_nome", "Responsável", "João da Silva")}
              {campo("responsavel_cargo", "Cargo", "Diretor")}
            </div>

            {/* Endereço */}
            <div style={{ fontSize: 11, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase", marginBottom: 8, borderTop: `1px solid ${COLORS.border}`, paddingTop: 10 }}>Endereço</div>
            <div style={{ display: "grid", gridTemplateColumns: "3fr 1fr", gap: 10, marginBottom: 8 }}>
              {campo("endereco", "Rua / Logradouro", "Av. Principal")}
              {campo("numero", "Nº", "100")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr 1fr", gap: 10, marginBottom: 8 }}>
              {campo("cidade", "Cidade", "Belo Horizonte")}
              {campo("cep", "CEP", "00000-000")}
              {campo("uf", "UF", "MG")}
            </div>
            <div style={{ marginBottom: 10 }}>
              {campo("complemento", "Complemento", "Sala 5, Bloco B")}
            </div>

            {/* Dados bancários */}
            <div style={{ fontSize: 11, fontWeight: 800, color: "#6366f1", letterSpacing: 1, textTransform: "uppercase", marginBottom: 8, borderTop: `1px solid ${COLORS.border}`, paddingTop: 10 }}>Dados Bancários (Pix em Lotes / CNAB 240)</div>
            <div style={{ marginBottom: 8 }}>
              <BancoPicker
                bancoNome={form.banco_nome}
                bancoCodigo={form.banco_codigo}
                bancoIspb={form.banco_ispb}
                onSelect={b => setForm(f => ({ ...f, banco_nome: b.nome, banco_codigo: b.codigo, banco_ispb: b.ispb }))}
              />
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 8 }}>
              {campo("agencia", "Agência", "0001")}
              {campo("agencia_dv", "DV Agência", "9")}
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10, marginBottom: 10 }}>
              {campo("conta", "Conta", "38707714")}
              {campo("conta_dv", "DV Conta", "6")}
            </div>

            {/* Logo */}
            <div style={{ borderTop: `1px solid ${COLORS.border}`, paddingTop: 10, marginBottom: 10 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: COLORS.textMid, marginBottom: 6 }}>Logo da Empresa (aparece nas propostas)</div>
              {form.logo_base64 && (
                <div style={{ display: "flex", alignItems: "center", gap: 10, marginBottom: 8 }}>
                  <img src={form.logo_base64} alt="logo" style={{ maxHeight: 50, maxWidth: 160, objectFit: "contain", border: `1px solid ${COLORS.border}`, borderRadius: 6, padding: 4 }} />
                  <Btn size="sm" variant="danger" onClick={() => setForm(f => ({ ...f, logo_base64: "" }))}>Remover</Btn>
                </div>
              )}
              <input type="file" accept="image/*" onChange={e => {
                const file = e.target.files[0];
                if (!file) return;
                if (file.size > 500 * 1024) return alert("Logo deve ter no máximo 500 KB.");
                const reader = new FileReader();
                reader.onload = ev => setForm(f => ({ ...f, logo_base64: ev.target.result }));
                reader.readAsDataURL(file);
              }} style={{ fontSize: 13 }} />
              <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 3 }}>PNG ou JPG, máx. 500 KB.</div>
            </div>

            <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 6 }}>
              <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
              <Btn onClick={salvar}>Salvar</Btn>
            </div>
          </div>
        </Modal>
      )}

      {modal === "excluir" && (
        <Modal title="Excluir Empresa" onClose={() => setModal(null)}>
          <p style={{ fontSize: 14, color: COLORS.textMid, marginBottom: 20 }}>
            Tem certeza que deseja excluir esta empresa? Esta ação não pode ser desfeita.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={excluir}>Excluir</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── USUÁRIOS ─────────────────────────────────────────────────────────────────
function Usuarios({ token, usuario }) {
  const [lista, setLista] = useState([]);
  const [tenants, setTenants] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ nome: "", email: "", senha: "", perfil: "visualizador", ativo: 1, tenant_id: "", funcionario_id: "" });
  const [sel, setSel] = useState(null);
  const [busca, setBusca] = useState("");
  const [filtroCliente, setFiltroCliente] = useState("");
  const [filtroPerfil, setFiltroPerfil] = useState("");
  const isSuper = ["admin"].includes(usuario?.perfil); // admin/superadmin = nível máximo

  const carregar = async () => {
    try { setLista(await apiFetch("GET", "/api/auth/usuarios", undefined, token)); }
    catch(e) { if (!e.message.includes("Token") && !e.message.includes("401")) alert("Erro: " + e.message); }
  };
  useEffect(() => { carregar(); }, [token]);

  useEffect(() => {
    if (!isSuper) return;
    apiFetch("GET", "/api/tenants", undefined, token)
      .then(rows => setTenants(rows))
      .catch(() => {});
  }, [token, isSuper]);

  const abrir = (u) => {
    setSel(u || null);
    setForm(u
      ? { nome: u.nome, email: u.email, senha: "", perfil: u.perfil || perfilOpts[0] || "rh", ativo: u.ativo, tenant_id: u.tenant_id || "", funcionario_id: u.funcionario_id || "", permissoes: (() => { try { return u.permissoes ? (typeof u.permissoes === "string" ? JSON.parse(u.permissoes) : u.permissoes) : null; } catch(e) { return null; } })() }
      : { nome: "", email: "", senha: "", perfil: "rh", ativo: 1, tenant_id: "", funcionario_id: "", permissoes: null });
    setModal("form");
  };

  const salvar = async () => {
    if (!form.nome || !form.email) return alert("Nome e email obrigatórios.");
    if (!sel && !form.senha) return alert("Senha obrigatória para novo usuário.");
    if (!form.perfil) return alert("Selecione o nível de acesso.");
    try {
      if (sel) {
        await apiFetch("PUT", `/api/auth/usuarios/${sel.id}`, form, token);
        setLista(prev => prev.map(u => u.id === sel.id ? { ...u, ...form } : u));
      } else {
        const novo = await apiFetch("POST", "/api/auth/usuarios", form, token);
        setLista(prev => [...prev, novo]);
      }
      setModal(null);
    } catch(e) { alert("Erro: " + e.message); }
  };

  const excluir = async () => {
    try {
      await apiFetch("DELETE", `/api/auth/usuarios/${sel.id}`, undefined, token);
      setLista(prev => prev.filter(u => u.id !== sel.id));
      setModal(null);
    } catch(e) { alert("Erro: " + e.message); }
  };

  const PERFIL_LABEL = { admin: "Admin", administrador: "Administrador", gestor: "Gestor", rh: "RH", visualizador: "Funcionário" };
  const PERFIL_COR   = { admin: COLORS.danger, administrador: COLORS.primary, gestor: "#7c3aed", rh: COLORS.accent, visualizador: COLORS.textMid };
  const PERFIL_ICON  = { admin: "A", administrador: "Ad", gestor: "G", rh: "RH", visualizador: "F" };
  const PERFIL_RANK  = { admin: 99, administrador: 3, gestor: 2, rh: 1, visualizador: 0 };
  const myRank = PERFIL_RANK[usuario?.perfil] ?? 0;
  const canEditUser = (u) => usuario?.perfil === "admin" || myRank >= (PERFIL_RANK[u.perfil] ?? 0);
  const canGrantAdmin = ["admin","administrador"].includes(usuario?.perfil);
  const perfilOpts = isSuper
    ? ["admin","administrador","gestor","rh","visualizador"]
    : canGrantAdmin
      ? ["administrador","gestor","rh","visualizador"]
      : usuario?.perfil === 'gestor'
        ? ["rh","visualizador"]
        : ["gestor","rh"];

  const listaFiltrada = lista.filter(u => {
    if (busca && !u.nome.toLowerCase().includes(busca.toLowerCase()) && !u.email.toLowerCase().includes(busca.toLowerCase())) return false;
    if (filtroCliente && String(u.tenant_id) !== String(filtroCliente)) return false;
    if (filtroPerfil && u.perfil !== filtroPerfil) return false;
    return true;
  });

  const cols = isSuper ? ["Usuário","E-mail","Cliente","Nível de Acesso","Status",""] : ["Usuário","E-mail","Nível de Acesso","Status",""];

  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <SectionTitle action={<Btn size="sm" onClick={() => abrir(null)}>+ Novo Usuário</Btn>}>
        Usuários do Sistema
      </SectionTitle>

      {/* Resumo de perfis */}
      {isSuper && (
        <div style={{ display: "flex", gap: 10, flexWrap: "wrap" }}>
          {["admin","administrador","gestor","rh","visualizador"].map(p => {
            const count = lista.filter(u => u.perfil === p).length;
            return (
              <div key={p} onClick={() => setFiltroPerfil(filtroPerfil === p ? "" : p)} style={{
                background: filtroPerfil === p ? PERFIL_COR[p] : COLORS.surface,
                border: `1.5px solid ${PERFIL_COR[p]}`,
                borderRadius: 10, padding: "8px 16px", cursor: "pointer",
                display: "flex", alignItems: "center", gap: 8,
                color: filtroPerfil === p ? "#fff" : PERFIL_COR[p],
                fontWeight: 600, fontSize: 13, transition: "all .15s",
              }}>
                <span>{PERFIL_ICON[p]}</span>
                <span>{PERFIL_LABEL[p]}</span>
                <span style={{ background: filtroPerfil === p ? "rgba(255,255,255,0.3)" : COLORS.bg, borderRadius: 8, padding: "1px 7px", fontSize: 12 }}>{count}</span>
              </div>
            );
          })}
          {(filtroPerfil || filtroCliente || busca) && (
            <Btn size="sm" variant="ghost" onClick={() => { setFiltroPerfil(""); setFiltroCliente(""); setBusca(""); }}>✕ Limpar filtros</Btn>
          )}
        </div>
      )}

      {/* Barra de busca e filtros */}
      <div style={{ display: "flex", gap: 10, flexWrap: "wrap", alignItems: "flex-end" }}>
        <Input label="Buscar" placeholder="Nome ou e-mail..." value={busca} onChange={e => setBusca(e.target.value)} style={{ minWidth: 220 }} />
        {isSuper && (
          <Select label="Filtrar por cliente" value={filtroCliente} onChange={e => setFiltroCliente(e.target.value)} style={{ minWidth: 200 }}>
            <option value="">Todos os clientes</option>
            <option value="null">— Sem cliente (admin)</option>
            {tenants.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
          </Select>
        )}
        <div style={{ fontSize: 12, color: COLORS.textMid, alignSelf: "center", paddingBottom: 2 }}>
          {listaFiltrada.length} de {lista.length} usuário{lista.length !== 1 ? "s" : ""}
        </div>
      </div>

      <Card>
        <RhTable cols={cols}>
          {listaFiltrada.map(u => {
            const tenantNome = tenants.find(t => t.id === u.tenant_id)?.nome || (u.tenant_id ? `#${u.tenant_id}` : "—");
            return (
              <RhTr key={u.id}>
                <TD bold>{u.nome}</TD>
                <TD muted>{u.email}</TD>
                {isSuper && <TD muted style={{ fontSize: 12 }}>{tenantNome}</TD>}
                <TD>
                  <span style={{ display: "inline-flex", alignItems: "center", gap: 5, background: COLORS.bg, border: `1px solid ${PERFIL_COR[u.perfil] || "#7c3aed"}`, borderRadius: 8, padding: "3px 10px", fontSize: 12, fontWeight: 600, color: PERFIL_COR[u.perfil] || "#7c3aed" }}>
                    {PERFIL_ICON[u.perfil] || "?"} {PERFIL_LABEL[u.perfil] || u.perfil || "—"}
                  </span>
                </TD>
                <TD><Badge color={u.ativo ? COLORS.primary : COLORS.danger}>{u.ativo ? "Ativo" : "Inativo"}</Badge></TD>
                <TD style={{ textAlign: "right" }}>
                  <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                    {canEditUser(u) && <Btn size="sm" variant="ghost" onClick={() => abrir(u)}>Editar</Btn>}
                    {canEditUser(u) && <Btn size="sm" variant="danger" onClick={() => { setSel(u); setModal("excluir"); }}>Excluir</Btn>}
                  </div>
                </TD>
              </RhTr>
            );
          })}
          {listaFiltrada.length === 0 && <RhTr><td colSpan={cols.length} style={{ padding: "20px 14px", color: COLORS.textLight, fontSize: 13 }}>Nenhum usuário encontrado.</td></RhTr>}
        </RhTable>
      </Card>

      {modal === "form" && (
        <Modal title={sel ? `Editar — ${sel.nome}` : "Novo Usuário"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gap: 14 }}>
            <Input label="Nome completo" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} />
            <Input label="E-mail (login)" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            <div>
              <Input label={sel ? "Nova senha (deixe em branco para não alterar)" : "Senha"} type="password" value={form.senha} onChange={e => setForm(p => ({ ...p, senha: e.target.value }))} placeholder={sel ? "••••••••" : ""} />
              {sel && <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 4 }}>A senha atual não pode ser visualizada. Preencha apenas para redefinir.</div>}
            </div>
            <Select label="Nível de acesso" value={form.perfil} onChange={e => setForm(p => ({ ...p, perfil: e.target.value }))}>
              {perfilOpts.map(p => <option key={p} value={p}>{PERFIL_ICON[p]} {PERFIL_LABEL[p]}</option>)}
            </Select>
            {isSuper && (
              <Select label="Cliente (empresa)" value={form.tenant_id} onChange={e => setForm(p => ({ ...p, tenant_id: e.target.value ? Number(e.target.value) : null }))}>
                <option value="">— Nenhum (Admin do sistema) —</option>
                {tenants.map(t => <option key={t.id} value={t.id}>{t.nome}</option>)}
              </Select>
            )}
            <Select label="Status" value={form.ativo} onChange={e => setForm(p => ({ ...p, ativo: Number(e.target.value) }))}>
              <option value={1}>Ativo</option>
              <option value={0}>Inativo</option>
            </Select>
            {isSuper && <Input label="ID do Funcionário vinculado (opcional)" type="number" value={form.funcionario_id} onChange={e => setForm(p => ({ ...p, funcionario_id: e.target.value }))} placeholder="Deixe vazio para admin/administrador" />}
          </div>

          {/* Painel de permissões — exibido quando editando rh ou gestor */}
          {["rh","gestor"].includes(form.perfil) && (() => {
            const SECOES_RH = [
              { id: "func-efetivos",        label: "Funcionários — Efetivos" },
              { id: "func-pre-cadastro",    label: "Funcionários — Pré Cadastro" },
              { id: "beneficios-vt",        label: "Vale Transporte" },
              { id: "beneficios-va",        label: "Vale Alimentação" },
              { id: "financeiro-adiantamento",     label: "Folha de Pagamento" },
              { id: "financeiro-relatorio",       label: "Relatório Financeiro" },
              { id: "financeiro-adiantamento-sal",label: "Adiantamento de Salário" },
              { id: "escala",              label: "Escalas e Turnos" },
              { id: "setores",             label: "Grupos e Setores" },
              { id: "ponto-faltas",        label: "Ponto — Faltas" },
              { id: "ponto-afastamentos",  label: "Ponto — Afastamentos" },
              { id: "ponto-ferias",        label: "Ponto — Férias" },
              { id: "ponto-historico",     label: "Ponto — Histórico" },
              { id: "rh-itens-func",       label: "EPIs/Uniformes" },
            ];
            const SECOES_FINANCEIRO = [
              { id: "financeiro",          label: "Financeiro" },
              { id: "cobrancas",           label: "Cobranças" },
              { id: "clientes",            label: "Clientes" },
              { id: "contratos",           label: "Contratos" },
              { id: "propostas",           label: "Propostas" },
            ];
            // permissoes null = usa padrão (tudo de rh, nada de financeiro)
            const perm = form.permissoes;
            const DEFAULT_RH = SECOES_RH.map(s => s.id);
            const ativas = perm || DEFAULT_RH;
            const toggle = (id) => {
              const base = perm || DEFAULT_RH;
              const nova = base.includes(id) ? base.filter(x => x !== id) : [...base, id];
              setForm(f => ({ ...f, permissoes: nova }));
            };
            return (
              <div style={{ border: `1.5px solid ${COLORS.border}`, borderRadius: 10, padding: "14px 16px", marginTop: 4 }}>
                <div style={{ fontSize: 12, fontWeight: 800, color: COLORS.primary, letterSpacing: 1, textTransform: "uppercase", marginBottom: 12 }}>
                  🔐 Permissões de Acesso
                </div>
                <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 10 }}>Módulos do RH</div>
                <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6, marginBottom: 14 }}>
                  {SECOES_RH.map(s => (
                    <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                      <input type="checkbox" checked={ativas.includes(s.id)} onChange={() => toggle(s.id)} />
                      {s.label}
                    </label>
                  ))}
                </div>
                {canGrantAdmin && (
                  <>
                    <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 10 }}>Módulos Financeiros (acesso restrito)</div>
                    <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 6 }}>
                      {SECOES_FINANCEIRO.map(s => (
                        <label key={s.id} style={{ display: "flex", alignItems: "center", gap: 8, fontSize: 13, cursor: "pointer" }}>
                          <input type="checkbox" checked={ativas.includes(s.id)} onChange={() => toggle(s.id)} />
                          {s.label}
                        </label>
                      ))}
                    </div>
                  </>
                )}
                <div style={{ marginTop: 10, fontSize: 11, color: COLORS.textLight }}>
                  {perm === null ? "Padrão: todos os módulos de RH, sem financeiro." : `${ativas.length} módulo(s) habilitado(s).`}
                  {perm !== null && <button type="button" onClick={() => setForm(f => ({ ...f, permissoes: null }))} style={{ marginLeft: 10, color: COLORS.primary, background: "none", border: "none", cursor: "pointer", fontSize: 11 }}>Restaurar padrão</button>}
                </div>
              </div>
            );
          })()}

          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={salvar}>Salvar</Btn>
          </div>
        </Modal>
      )}

      {modal === "excluir" && (
        <Modal title="Excluir Usuário" onClose={() => setModal(null)}>
          <p style={{ fontSize: 14, color: COLORS.textMid, marginBottom: 20 }}>
            Tem certeza que deseja excluir o usuário <strong>{sel?.nome}</strong>? Esta ação não pode ser desfeita.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={excluir}>Excluir</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── CLIENTES (superadmin) ────────────────────────────────────────────────────
function Clientes({ token }) {
  const [lista, setLista] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState({ nome: "", cnpj: "", email: "", telefone: "", plano: "basico", ativo: 1, dominio_login: "" });
  const [sel, setSel] = useState(null);

  const carregar = async () => {
    try { setLista(await apiFetch("GET", "/api/tenants", undefined, token)); }
    catch(e) { if (!e.message.includes("Token") && !e.message.includes("401")) alert("Erro: " + e.message); }
  };
  useEffect(() => { carregar(); }, [token]);

  const abrir = (t) => {
    setSel(t || null);
    setForm(t ? { nome: t.nome, cnpj: t.cnpj || "", email: t.email || "", telefone: t.telefone || "", plano: t.plano || "basico", ativo: t.ativo ?? 1, dominio_login: t.dominio_login || "" } : { nome: "", cnpj: "", email: "", telefone: "", plano: "basico", ativo: 1, dominio_login: "" });
    setModal("form");
  };

  const salvar = async () => {
    if (!form.nome) return alert("Nome obrigatório.");
    try {
      if (sel) {
        await apiFetch("PUT", `/api/tenants/${sel.id}`, form, token);
        setLista(prev => prev.map(t => t.id === sel.id ? { ...t, ...form } : t));
      } else {
        const novo = await apiFetch("POST", "/api/tenants", form, token);
        setLista(prev => [...prev, novo]);
      }
      setModal(null);
    } catch(e) { alert("Erro: " + e.message); }
  };

  const excluir = async () => {
    try {
      await apiFetch("DELETE", `/api/tenants/${sel.id}`, undefined, token);
      setLista(prev => prev.filter(t => t.id !== sel.id));
      setModal(null);
    } catch(e) { alert("Erro: " + e.message); }
  };

  const PLANO_COR = { basico: COLORS.textMid, profissional: COLORS.info, empresarial: COLORS.primary };

  return (
    <div>
      <SectionTitle action={<Btn size="sm" onClick={() => abrir(null)}>+ Novo Cliente</Btn>}>
        Clientes / Tenants
      </SectionTitle>
      <Card>
        <RhTable cols={["Nome","CNPJ","E-mail","Plano","Status",""]}>
          {lista.map(t => (
            <RhTr key={t.id}>
              <TD bold>{t.nome}</TD>
              <TD muted>{t.cnpj || "—"}</TD>
              <TD muted>{t.email || "—"}</TD>
              <TD><Badge color={PLANO_COR[t.plano] || COLORS.textMid}>{t.plano || "básico"}</Badge></TD>
              <TD><Badge color={t.ativo ? COLORS.primary : COLORS.danger}>{t.ativo ? "Ativo" : "Inativo"}</Badge></TD>
              <TD style={{ textAlign: "right" }}>
                <div style={{ display: "flex", gap: 6, justifyContent: "flex-end" }}>
                  <Btn size="sm" variant="ghost" onClick={() => abrir(t)}>Editar</Btn>
                  <Btn size="sm" variant="danger" onClick={() => { setSel(t); setModal("excluir"); }}>Excluir</Btn>
                </div>
              </TD>
            </RhTr>
          ))}
          {lista.length === 0 && <RhTr><td colSpan={6} style={{ padding: "20px 14px", color: COLORS.textLight, fontSize: 13 }}>Nenhum cliente cadastrado.</td></RhTr>}
        </RhTable>
      </Card>

      {modal === "form" && (
        <Modal title={sel ? "Editar Cliente" : "Novo Cliente"} onClose={() => setModal(null)}>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 14 }}>
            <div style={{ gridColumn: "1/-1" }}>
              <Input label="Nome" value={form.nome} onChange={e => setForm(p => ({ ...p, nome: e.target.value }))} />
            </div>
            <Input label="CNPJ" value={form.cnpj} onChange={e => setForm(p => ({ ...p, cnpj: e.target.value }))} />
            <Input label="Telefone" value={form.telefone} onChange={e => setForm(p => ({ ...p, telefone: e.target.value }))} />
            <Input label="E-mail" type="email" value={form.email} onChange={e => setForm(p => ({ ...p, email: e.target.value }))} />
            <div style={{ gridColumn: "1/-1" }}>
              <Input label="Domínio de login dos funcionários" value={form.dominio_login} onChange={e => setForm(p => ({ ...p, dominio_login: e.target.value }))} placeholder="Ex: classe.com.br" />
              <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 3 }}>
                Usado para gerar logins automáticos. Ex: <em>joao@classe.com.br</em>
              </div>
            </div>
            <Select label="Plano" value={form.plano} onChange={e => setForm(p => ({ ...p, plano: e.target.value }))}>
              <option value="basico">Básico</option>
              <option value="profissional">Profissional</option>
              <option value="empresarial">Empresarial</option>
            </Select>
            <Select label="Status" value={form.ativo} onChange={e => setForm(p => ({ ...p, ativo: Number(e.target.value) }))}>
              <option value={1}>Ativo</option>
              <option value={0}>Inativo</option>
            </Select>
          </div>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10, marginTop: 20 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn onClick={salvar}>Salvar</Btn>
          </div>
        </Modal>
      )}

      {modal === "excluir" && (
        <Modal title="Excluir Cliente" onClose={() => setModal(null)}>
          <p style={{ fontSize: 14, color: COLORS.textMid, marginBottom: 20 }}>
            Tem certeza que deseja excluir o cliente <strong>{sel?.nome}</strong>? Todos os dados do cliente serão removidos.
          </p>
          <div style={{ display: "flex", justifyContent: "flex-end", gap: 10 }}>
            <Btn variant="ghost" onClick={() => setModal(null)}>Cancelar</Btn>
            <Btn variant="danger" onClick={excluir}>Excluir</Btn>
          </div>
        </Modal>
      )}
    </div>
  );
}

// ─── GRÁFICOS & RELATÓRIOS ────────────────────────────────────────────────────
const CHART_COLORS = ["#16a34a","#3b82f6","#f59e0b","#ef4444","#8b5cf6","#06b6d4","#f97316","#84cc16"];

function KpiCard({ label, value, color, sub }) {
  return (
    <Card style={{ borderTop: `4px solid ${color || COLORS.primary}` }}>
      <div style={{ fontSize: 12, color: COLORS.textMid, fontWeight: 600, marginBottom: 4 }}>{label}</div>
      <div style={{ fontSize: 26, fontWeight: 900, color: color || COLORS.primary }}>{value}</div>
      {sub && <div style={{ fontSize: 11, color: COLORS.textLight, marginTop: 2 }}>{sub}</div>}
    </Card>
  );
}

function Graficos({ employees, ponto, afastamentos }) {
  const now = new Date();
  const [mesAno, setMesAno] = useState(`${now.getFullYear()}-${String(now.getMonth()+1).padStart(2,'0')}`);
  const [mesNum, anoNum] = mesAno.split('-').map(Number);

  // ── dados derivados ───────────────────────────────────────────────────────
  const ativos    = employees.filter(e => e.status === "Ativo");
  const demitidos = employees.filter(e => e.status === "Demitido");

  const statusData = [
    { name: "Ativo",     value: employees.filter(e => e.status === "Ativo").length },
    { name: "Férias",    value: employees.filter(e => e.status === "Férias").length },
    { name: "Afastado",  value: employees.filter(e => e.status === "Afastado").length },
    { name: "Demitido",  value: demitidos.length },
  ].filter(d => d.value > 0);

  const setorData = Object.entries(
    ativos.reduce((acc, e) => { const s = e.setor || "Sem setor"; acc[s] = (acc[s] || 0) + 1; return acc; }, {})
  ).map(([name, value]) => ({ name, value })).sort((a, b) => b.value - a.value);

  const pontoMes = ponto.filter(p => p.mes == mesNum && p.ano == anoNum);
  const extrasData = employees
    .filter(e => e.status !== "Demitido")
    .map(e => {
      const r = pontoMes.find(p => p.funcionario_id === e.id);
      return { name: e.nome.split(" ")[0], extras: parseFloat(r?.horas_extras || 0), faltas: parseFloat(r?.faltas || 0), normais: parseFloat(r?.horas_normais || 0) };
    })
    .filter(d => d.extras > 0 || d.faltas > 0 || d.normais > 0)
    .sort((a, b) => b.extras - a.extras);

  const salarioData = ativos
    .slice(0, 10)
    .map(e => ({ name: e.nome.split(" ")[0], salario: e.salario }))
    .sort((a, b) => b.salario - a.salario);

  const totalFolha  = ativos.reduce((s, e) => {
    const r = pontoMes.find(p => p.funcionario_id === e.id);
    return s + e.salario + ((e.salario / 220) * 1.5 * (r?.horas_extras || 0));
  }, 0);
  const totalExtras = pontoMes.reduce((s, p) => s + (parseFloat(p.horas_extras) || 0), 0);
  const totalFaltas = pontoMes.reduce((s, p) => s + (parseFloat(p.faltas) || 0), 0);
  const totalAfMes  = afastamentos.filter(a => {
    const d = new Date(a.inicio + "T00:00:00");
    return d.getMonth() + 1 === mesNum && d.getFullYear() === anoNum;
  }).length;

  const customTooltip = ({ active, payload, label }) => {
    if (!active || !payload?.length) return null;
    return (
      <div style={{ background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 8, padding: "10px 14px", fontSize: 12 }}>
        <div style={{ fontWeight: 700, marginBottom: 4, color: COLORS.text }}>{label}</div>
        {payload.map((p, i) => (
          <div key={i} style={{ color: p.color }}>{p.name}: <strong>{p.value}</strong></div>
        ))}
      </div>
    );
  };

  const gerarPDF = () => {
    const style = document.createElement("style");
    style.id = "print-graficos";
    style.innerHTML = `@media print {
      body > * { display: none !important; }
      #graficos-print { display: block !important; position: fixed; inset: 0; background: white; z-index: 99999; padding: 24px; }
    }`;
    document.head.appendChild(style);
    const el = document.getElementById("graficos-print");
    if (el) el.style.display = "block";
    window.print();
    setTimeout(() => {
      document.head.removeChild(style);
      if (el) el.style.display = "none";
    }, 500);
  };

  return (
    <div>
      {/* Print-only wrapper */}
      <div id="graficos-print" style={{ display: "none" }}>
        <div style={{ fontFamily: "Inter, sans-serif" }}>
          <h2 style={{ color: "#16a34a", marginBottom: 4 }}>Relatório — Gráficos & Indicadores</h2>
          <p style={{ color: "#64748b", fontSize: 13, marginTop: 0 }}>
            Referência: {String(mesNum).padStart(2,"0")}/{anoNum} · Gerado em {new Date().toLocaleDateString("pt-BR")}
          </p>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13, marginTop: 16 }}>
            <thead><tr style={{ background: "#f0fdf4" }}>
              {["Funcionário","H. Normais","H. Extras","Faltas","Salário Base"].map(h => (
                <th key={h} style={{ padding: "8px 12px", textAlign: "left", borderBottom: "2px solid #16a34a" }}>{h}</th>
              ))}
            </tr></thead>
            <tbody>
              {employees.filter(e => e.status !== "Demitido").map((e, i) => {
                const r = pontoMes.find(p => p.funcionario_id === e.id);
                return (
                  <tr key={e.id} style={{ background: i % 2 === 0 ? "#fff" : "#f8fafc" }}>
                    <td style={{ padding: "7px 12px", fontWeight: 600 }}>{e.nome}</td>
                    <td style={{ padding: "7px 12px" }}>{r?.horas_normais || 0}h</td>
                    <td style={{ padding: "7px 12px", color: "#f59e0b" }}>{r?.horas_extras || 0}h</td>
                    <td style={{ padding: "7px 12px", color: "#ef4444" }}>{r?.faltas || 0}</td>
                    <td style={{ padding: "7px 12px" }}>R$ {(e.salario || 0).toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          <div style={{ marginTop: 20, display: "flex", gap: 24 }}>
            <div><strong>Total Folha:</strong> R$ {totalFolha.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}</div>
            <div><strong>Total H. Extras:</strong> {totalExtras}h</div>
            <div><strong>Total Faltas:</strong> {totalFaltas} dias</div>
            <div><strong>Funcionários Ativos:</strong> {ativos.length}</div>
          </div>
        </div>
      </div>

      {/* Barra de filtro e ação */}
      <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 20, flexWrap: "wrap" }}>
        <Input label="Mês de referência" type="month" value={mesAno} onChange={e => setMesAno(e.target.value)} />
        <Btn variant="accent" onClick={gerarPDF}>Gerar PDF</Btn>
      </div>

      {/* KPIs */}
      <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fit, minmax(160px, 1fr))", gap: 14, marginBottom: 22 }}>
        <KpiCard label="Funcionários Ativos"  value={ativos.length}    color={COLORS.primary} sub={`${employees.length} total`} />
        <KpiCard label="Afastamentos no Mês"  value={totalAfMes}       color={COLORS.warning} sub="férias + atestados" />
        <KpiCard label="Horas Extras (mês)"   value={`${totalExtras}h`} color={COLORS.accent}  sub={`${String(mesNum).padStart(2,"0")}/${anoNum}`} />
        <KpiCard label="Faltas (dias)"         value={totalFaltas}      color={COLORS.danger}  sub={`${String(mesNum).padStart(2,"0")}/${anoNum}`} />
        <KpiCard label="Custo Estimado Folha"
          value={`R$ ${totalFolha.toLocaleString("pt-BR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`}
          color={COLORS.info} sub="salários + extras 50%" />
      </div>

      {/* Linha 1: Status + Setores */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 2fr", gap: 16, marginBottom: 16 }}>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 14, fontSize: 14 }}>Status dos Funcionários</div>
          <ResponsiveContainer width="100%" height={220}>
            <PieChart>
              <Pie data={statusData} cx="50%" cy="50%" innerRadius={55} outerRadius={85} paddingAngle={3} dataKey="value" label={({ name, value }) => `${name}: ${value}`} labelLine={false}>
                {statusData.map((_, i) => <Cell key={i} fill={CHART_COLORS[i]} />)}
              </Pie>
              <Tooltip content={customTooltip} />
            </PieChart>
          </ResponsiveContainer>
          <div style={{ display: "flex", flexWrap: "wrap", gap: "6px 14px", marginTop: 8 }}>
            {statusData.map((d, i) => (
              <div key={i} style={{ display: "flex", alignItems: "center", gap: 5, fontSize: 12 }}>
                <span style={{ width: 10, height: 10, borderRadius: "50%", background: CHART_COLORS[i], display: "inline-block" }} />
                <span style={{ color: COLORS.textMid }}>{d.name}: <strong style={{ color: COLORS.text }}>{d.value}</strong></span>
              </div>
            ))}
          </div>
        </Card>

        <Card>
          <div style={{ fontWeight: 700, marginBottom: 14, fontSize: 14 }}>Funcionários por Setor</div>
          {setorData.length === 0
            ? <div style={{ color: COLORS.textLight, fontSize: 13, padding: "40px 0", textAlign: "center" }}>Nenhum dado</div>
            : <ResponsiveContainer width="100%" height={220}>
                <BarChart data={setorData} margin={{ top: 4, right: 10, left: -20, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis dataKey="name" tick={{ fontSize: 11, fill: COLORS.textMid }} />
                  <YAxis allowDecimals={false} tick={{ fontSize: 11, fill: COLORS.textMid }} />
                  <Tooltip content={customTooltip} />
                  <Bar dataKey="value" name="Funcionários" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
                </BarChart>
              </ResponsiveContainer>
          }
        </Card>
      </div>

      {/* Linha 2: Extras & Faltas por funcionário */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ fontWeight: 700, marginBottom: 14, fontSize: 14 }}>
          Horas Extras e Faltas por Funcionário — {String(mesNum).padStart(2,"0")}/{anoNum}
        </div>
        {extrasData.length === 0
          ? <div style={{ color: COLORS.textLight, fontSize: 13, padding: "32px 0", textAlign: "center" }}>Nenhum lançamento de ponto para este mês.</div>
          : <ResponsiveContainer width="100%" height={240}>
              <BarChart data={extrasData} margin={{ top: 4, right: 10, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                <XAxis dataKey="name" tick={{ fontSize: 11, fill: COLORS.textMid }} />
                <YAxis tick={{ fontSize: 11, fill: COLORS.textMid }} />
                <Tooltip content={customTooltip} />
                <Legend wrapperStyle={{ fontSize: 12, color: COLORS.textMid }} />
                <Bar dataKey="extras" name="H. Extras" fill={COLORS.accent}  radius={[4, 4, 0, 0]} />
                <Bar dataKey="faltas" name="Faltas"    fill={COLORS.danger}  radius={[4, 4, 0, 0]} />
                <Bar dataKey="normais" name="H. Normais" fill={COLORS.primary} radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
        }
      </Card>

      {/* Linha 3: Salário base top 10 + Tabela resumo */}
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        <Card>
          <div style={{ fontWeight: 700, marginBottom: 14, fontSize: 14 }}>Salário Base — Top 10</div>
          {salarioData.length === 0
            ? <div style={{ color: COLORS.textLight, fontSize: 13, padding: "32px 0", textAlign: "center" }}>Nenhum funcionário</div>
            : <ResponsiveContainer width="100%" height={220}>
                <BarChart data={salarioData} layout="vertical" margin={{ top: 4, right: 30, left: 10, bottom: 0 }}>
                  <CartesianGrid strokeDasharray="3 3" stroke={COLORS.border} />
                  <XAxis type="number" tick={{ fontSize: 11, fill: COLORS.textMid }} tickFormatter={v => `R$${(v/1000).toFixed(0)}k`} />
                  <YAxis type="category" dataKey="name" tick={{ fontSize: 11, fill: COLORS.textMid }} width={70} />
                  <Tooltip content={customTooltip} formatter={v => `R$ ${v.toLocaleString("pt-BR", { minimumFractionDigits: 2 })}`} />
                  <Bar dataKey="salario" name="Salário" fill={COLORS.info} radius={[0, 4, 4, 0]} />
                </BarChart>
              </ResponsiveContainer>
          }
        </Card>

        <Card>
          <div style={{ fontWeight: 700, marginBottom: 14, fontSize: 14 }}>Resumo do Mês — Tabela</div>
          <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 12.5 }}>
            <thead>
              <tr style={{ background: COLORS.bg }}>
                {["Funcionário","H. Normais","H. Extras","Faltas"].map(h => (
                  <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: COLORS.textMid, fontWeight: 600 }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {employees.filter(e => e.status !== "Demitido").map((e, i) => {
                const r = pontoMes.find(p => p.funcionario_id === e.id);
                return (
                  <tr key={e.id} style={{ background: i % 2 === 0 ? COLORS.white : COLORS.gray }}>
                    <td style={{ padding: "7px 10px", fontWeight: 600, color: COLORS.text }}>{e.nome.split(" ").slice(0,2).join(" ")}</td>
                    <td style={{ padding: "7px 10px", color: COLORS.textMid }}>{r?.horas_normais || 0}h</td>
                    <td style={{ padding: "7px 10px", color: (r?.horas_extras || 0) > 0 ? COLORS.accent : COLORS.textMid }}>{r?.horas_extras || 0}h</td>
                    <td style={{ padding: "7px 10px", color: (r?.faltas || 0) > 0 ? COLORS.danger : COLORS.textMid }}>{r?.faltas || 0}</td>
                  </tr>
                );
              })}
              {employees.filter(e => e.status !== "Demitido").length === 0 && (
                <tr><td colSpan={4} style={{ padding: "20px 10px", textAlign: "center", color: COLORS.textLight }}>Nenhum funcionário ativo</td></tr>
              )}
            </tbody>
          </table>
        </Card>
      </div>
    </div>
  );
}

// ─── APP ──────────────────────────────────────────────────────────────────────
// SVG icons
const Icon = ({ d, size = 18, stroke = "currentColor", fill = "none" }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill={fill} stroke={stroke} strokeWidth={1.75} strokeLinecap="round" strokeLinejoin="round">
    {Array.isArray(d) ? d.map((p, i) => <path key={i} d={p} />) : <path d={d} />}
  </svg>
);
const MESES_FULL = ['','Janeiro','Fevereiro','Março','Abril','Maio','Junho','Julho','Agosto','Setembro','Outubro','Novembro','Dezembro'];
const ICONS = {
  dashboard:    ["M3 3h7v7H3z","M14 3h7v7h-7z","M14 14h7v7h-7z","M3 14h7v7H3z"],
  funcionarios: ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2","M23 21v-2a4 4 0 0 0-3-3.87","M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z","M16 3.13a4 4 0 0 1 0 7.75"],
  ponto:        ["M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z","M12 6v6l4 2"],
  ferias:       ["M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z"],
  escala:       ["M8 6h13","M8 12h13","M8 18h13","M3 6h.01","M3 12h.01","M3 18h.01"],
  financeiro:   ["M12 2v20","M17 5H9.5a3.5 3.5 0 0 0 0 7h5a3.5 3.5 0 0 1 0 7H6"],
  setores:      ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z","M9 22V12h6v10"],
  empresas:     ["M6 2h12a2 2 0 0 1 2 2v16a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2z","M9 7h6","M9 11h6","M9 15h4"],
  usuarios:     ["M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2","M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z","M16 11h6","M19 8v6"],
  clientes:     ["M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2","M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8z","M23 21v-2a4 4 0 0 0-3-3.87","M16 3.13a4 4 0 0 1 0 7.75"],
  graficos:     ["M18 20V10","M12 20V4","M6 20v-6"],
  beneficios:   ["M20.84 4.61a5.5 5.5 0 0 0-7.78 0L12 5.67l-1.06-1.06a5.5 5.5 0 0 0-7.78 7.78l1.06 1.06L12 21.23l7.78-7.78 1.06-1.06a5.5 5.5 0 0 0 0-7.78z","M12 8v8","M8 12h8"],
  terceiro:     ["M9 11l3 3L22 4","M21 12v7a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h11"],
  pagamento:    ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M12 18v-6","M9 15h6"],
  folhas:       ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 1 2-2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"],
  baterponto:   ["M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10z","M12 6v6l4 2","M17.657 4.343A8 8 0 0 1 19.07 7M4.93 7a8 8 0 0 1 1.414-2.657"],
  propostas:    ["M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8z","M14 2v6h6","M16 13H8","M16 17H8","M10 9H8"],
  contratos:    ["M9 12h6","M9 16h6","M9 8h2","M13 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V9z","M13 2v7h7"],
  cobrancas:    ["M2 7a2 2 0 0 1 2-2h16a2 2 0 0 1 2 2v10a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V7z","M16 11a1 1 0 1 0 2 0 1 1 0 0 0-2 0","M2 11h20"],
  transferencias: ["M5 12h14","M12 5l7 7-7 7"],
  add:          ["M12 5v14","M5 12h14"],
  suprimentos:  ["M20 7H4a2 2 0 0 0-2 2v6a2 2 0 0 0 2 2h16a2 2 0 0 0 2-2V9a2 2 0 0 0-2-2z","M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"],
  fornecedores: ["M3 9l9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z","M9 22V12h6v10"],
  estoque:      ["M5 8h14","M5 12h14","M5 16h14","M3 4h18v18H3z","M9 4v4","M15 4v4"],
};

// ─── BaterPonto ───────────────────────────────────────────────────────────────
function calcDistancia(lat1, lon1, lat2, lon2) {
  const R = 6371000;
  const dLat = (lat2 - lat1) * Math.PI / 180;
  const dLon = (lon2 - lon1) * Math.PI / 180;
  const a = Math.sin(dLat/2)**2 + Math.cos(lat1*Math.PI/180)*Math.cos(lat2*Math.PI/180)*Math.sin(dLon/2)**2;
  return R * 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1-a));
}
const OFFLINE_KEY = 'rh_ponto_offline';

function BaterPonto({ token, usuario }) {
  const isAdmin = ["admin","administrador","rh"].includes(usuario?.perfil);
  const [abaAdmin, setAbaAdmin] = useState("mensal");

  // GPS
  const [posicao, setPosicao] = useState(null);
  const [statusGPS, setStatusGPS] = useState("obtendo");
  const [areas, setAreas] = useState([]);
  const [areaAtual, setAreaAtual] = useState(null);
  const [distanciaM, setDistanciaM] = useState(null);

  // Registros
  const [registrosHoje, setRegistrosHoje] = useState([]);
  const [proximoTipo, setProximoTipo] = useState("entrada");
  const [batendo, setBatendo] = useState(false);
  const [msg, setMsg] = useState("");
  const [emFeriasAfastamento, setEmFeriasAfastamento] = useState(null);
  const [ocorrenciaHoje, setOcorrenciaHoje] = useState(null);
  const [modalJustificativa, setModalJustificativa] = useState(null); // { ocorrenciaId, minutos }
  const [justificativaTexto, setJustificativaTexto] = useState("");

  // Offline
  const [pendentes, setPendentes] = useState(() => {
    try { return JSON.parse(localStorage.getItem(OFFLINE_KEY) || "[]"); } catch { return []; }
  });
  const [online, setOnline] = useState(navigator.onLine);

  // Admin
  const [registros, setRegistros] = useState([]);
  const [areasConfig, setAreasConfig] = useState([]);
  const [filtroData, setFiltroData] = useState(new Date().toISOString().slice(0,10));
  const [filtroFunc, setFiltroFunc] = useState("");
  const now2 = new Date();
  const [mesMensal, setMesMensal] = useState(`${now2.getFullYear()}-${String(now2.getMonth()+1).padStart(2,'0')}`);
  const [dadosMensais, setDadosMensais] = useState([]);
  const [modalArea, setModalArea] = useState(null);
  const [formArea, setFormArea] = useState({ nome: "", latitude: "", longitude: "", raio: 100 });

  // Online/offline listener
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener("online", onOnline);
    window.addEventListener("offline", onOffline);
    return () => { window.removeEventListener("online", onOnline); window.removeEventListener("offline", onOffline); };
  }, []);

  // Sincroniza quando voltar online
  useEffect(() => {
    if (!online || pendentes.length === 0) return;
    apiFetch("POST", "/api/registro-ponto/sync", { registros: pendentes }, token)
      .then(res => {
        if (res.sincronizados > 0) {
          localStorage.setItem(OFFLINE_KEY, "[]");
          setPendentes([]);
          setMsg(`✅ ${res.sincronizados} registro(s) offline sincronizados!`);
          carregarHoje();
        }
      }).catch(() => {});
  }, [online]);

  // Carrega áreas
  useEffect(() => {
    apiFetch("GET", "/api/registro-ponto/areas", undefined, token)
      .then(rows => setAreas(Array.isArray(rows) ? rows : []))
      .catch(() => {});
  }, [token]);

  // Registros de hoje + status férias/afastamento
  const carregarHoje = async () => {
    if (isAdmin) return;
    try {
      const status = await apiFetch("GET", "/api/registro-ponto/status-hoje", undefined, token);
      const lista = Array.isArray(status.registros) ? status.registros : [];
      setRegistrosHoje(lista);
      setEmFeriasAfastamento(status.emFeriasAfastamento || null);
      setOcorrenciaHoje(status.ocorrencia || null);
      const ultimo = lista[lista.length - 1];
      setProximoTipo(ultimo?.tipo === "entrada" ? "saida" : "entrada");
    } catch(e) {}
  };
  useEffect(() => { carregarHoje(); }, [token]);

  // GPS watchPosition
  useEffect(() => {
    if (!navigator.geolocation) { setStatusGPS("erro"); return; }
    const id = navigator.geolocation.watchPosition(
      (pos) => {
        const lat = pos.coords.latitude;
        const lon = pos.coords.longitude;
        const prec = pos.coords.accuracy;
        setPosicao({ lat, lon, precisao: prec });
        setStatusGPS("ok");
        // Calcula área mais próxima
        let melhorArea = null, melhorDist = Infinity;
        for (const a of areas) {
          const d = calcDistancia(lat, lon, parseFloat(a.latitude), parseFloat(a.longitude));
          if (d < melhorDist) { melhorDist = d; melhorArea = a; }
        }
        setDistanciaM(melhorArea ? Math.round(melhorDist) : null);
        setAreaAtual(melhorArea && melhorDist <= melhorArea.raio ? melhorArea : null);
      },
      () => setStatusGPS("erro"),
      { enableHighAccuracy: true, timeout: 15000, maximumAge: 8000 }
    );
    return () => navigator.geolocation.clearWatch(id);
  }, [areas]);

  const baterPonto = async () => {
    setMsg("");
    if (statusGPS === "obtendo") { setMsg("⏳ Aguardando localização GPS..."); return; }
    if (statusGPS === "erro" && areas.length > 0 && online) {
      setMsg("❌ GPS indisponível. Ative a localização no celular."); return;
    }
    if (areas.length > 0 && !areaAtual && online) {
      const dist = distanciaM ? ` (você está a ${distanciaM}m do local)` : "";
      setMsg(`❌ Você está fora da área permitida${dist}. Aproxime-se do local de trabalho.`); return;
    }
    setBatendo(true);
    const registro = {
      tipo: proximoTipo,
      latitude: posicao?.lat || null,
      longitude: posicao?.lon || null,
      precisao: posicao?.precisao || null,
      registrado_em: (() => { const d = new Date(); const pad = n => String(n).padStart(2,'0'); return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}:${pad(d.getSeconds())}`; })(),
    };
    if (!online) {
      const novos = [...pendentes, registro];
      localStorage.setItem(OFFLINE_KEY, JSON.stringify(novos));
      setPendentes(novos);
      setProximoTipo(proximoTipo === "entrada" ? "saida" : "entrada");
      setMsg(`📵 Sem internet — ${proximoTipo === "entrada" ? "Entrada" : "Saída"} salva offline. Será enviada ao reconectar.`);
      setBatendo(false);
      return;
    }
    try {
      const resp = await apiFetch("POST", "/api/registro-ponto/bater", registro, token);
      setMsg(`✅ ${proximoTipo === "entrada" ? "Entrada" : "Saída"} registrada com sucesso!`);
      await carregarHoje();
      // Atraso detectado — oferece justificativa
      if (resp.atrasado && resp.ocorrenciaId) {
        setModalJustificativa({ ocorrenciaId: resp.ocorrenciaId, minutos: resp.minutosAtraso });
        setJustificativaTexto("");
      }
    } catch(e) { setMsg("❌ " + e.message); }
    setBatendo(false);
  };

  const enviarJustificativa = async () => {
    if (!modalJustificativa || !justificativaTexto.trim()) return;
    try {
      await apiFetch("POST", `/api/registro-ponto/ocorrencias/${modalJustificativa.ocorrenciaId}/justificar`, { justificativa: justificativaTexto }, token);
      setMsg("✅ Justificativa enviada. Aguarde aprovação do administrador.");
      setModalJustificativa(null);
      await carregarHoje();
    } catch(e) { alert("Erro: " + e.message); }
  };

  // Admin
  const carregarRegistros = async () => {
    try {
      let url = "/api/registro-ponto?";
      if (filtroData) url += `data=${filtroData}&`;
      if (filtroFunc) url += `funcionario_id=${filtroFunc}`;
      const rows = await apiFetch("GET", url, undefined, token);
      setRegistros(Array.isArray(rows) ? rows : []);
    } catch(e) {}
  };
  const carregarAreasConfig = async () => {
    try {
      const rows = await apiFetch("GET", "/api/registro-ponto/areas", undefined, token);
      setAreasConfig(Array.isArray(rows) ? rows : []);
    } catch(e) {}
  };
  const carregarMensal = async () => {
    const [anoM, mesM] = mesMensal.split('-').map(Number);
    try {
      const rows = await apiFetch("GET", `/api/registro-ponto/mensal?mes=${mesM}&ano=${anoM}`, undefined, token);
      setDadosMensais(Array.isArray(rows) ? rows : []);
    } catch(e) {}
  };

  useEffect(() => { if (isAdmin) { carregarRegistros(); carregarAreasConfig(); carregarMensal(); } }, [token, isAdmin]);
  useEffect(() => { if (isAdmin) carregarMensal(); }, [mesMensal]);

  const salvarArea = async () => {
    if (!formArea.nome || !formArea.latitude || !formArea.longitude) { alert("Preencha nome, latitude e longitude."); return; }
    try {
      if (modalArea?.id) {
        await apiFetch("PUT", `/api/registro-ponto/areas/${modalArea.id}`, formArea, token);
        setAreasConfig(prev => prev.map(a => a.id === modalArea.id ? { ...a, ...formArea } : a));
      } else {
        const nova = await apiFetch("POST", "/api/registro-ponto/areas", formArea, token);
        setAreasConfig(prev => [...prev, nova]);
      }
      setModalArea(null);
    } catch(e) { alert(e.message); }
  };

  const excluirArea = async (id) => {
    if (!window.confirm("Remover esta área?")) return;
    try {
      await apiFetch("DELETE", `/api/registro-ponto/areas/${id}`, undefined, token);
      setAreasConfig(prev => prev.filter(a => a.id !== id));
    } catch(e) { alert(e.message); }
  };

  const usarPosicaoAtual = () => {
    if (posicao) setFormArea(f => ({ ...f, latitude: posicao.lat.toFixed(7), longitude: posicao.lon.toFixed(7) }));
    else alert("GPS não disponível ainda.");
  };

  const horaFmt = (iso) => new Date(iso).toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" });

  // ── Render funcionário ────────────────────────────────────────────────────────
  if (!isAdmin) {
    const dentroArea = areaAtual !== null;
    const semAreas = areas.length === 0;
    const podeBater = (semAreas || dentroArea || !online) && statusGPS !== "obtendo";
    const corBotao = proximoTipo === "entrada" ? COLORS.primary : COLORS.danger;

    // cores dark para o layout do funcionário
    const DC = { bg: "#0f172a", card: "#1e293b", border: "#334155", text: "#f1f5f9", textMid: "#94a3b8", green: "#16a34a", greenBg: "#16a34a22", red: "#ef4444", warn: "#f59e0b" };

    return (
      <div style={{ display: "flex", flexDirection: "column", gap: 16, padding: "16px 14px 0", maxWidth: 520, margin: "0 auto" }}>

        {/* Banner férias / afastamento */}
        {emFeriasAfastamento && (
          <div style={{ borderRadius: 14, padding: "18px", background: "#1e3a5f", border: `2px solid #3b82f6`, textAlign: "center" }}>
            <div style={{ fontSize: 36 }}>{emFeriasAfastamento.tipo === "ferias" ? "🏖️" : "🏥"}</div>
            <div style={{ fontWeight: 700, fontSize: 16, marginTop: 8, color: "#93c5fd" }}>
              {emFeriasAfastamento.tipo === "ferias" ? "Você está de férias" : "Você está afastado"}
            </div>
            {emFeriasAfastamento.motivo && <div style={{ fontSize: 13, color: DC.textMid, marginTop: 4 }}>Motivo: {emFeriasAfastamento.motivo}</div>}
            {emFeriasAfastamento.data_fim && <div style={{ fontSize: 12, color: DC.textMid, marginTop: 2 }}>Retorno: {new Date(emFeriasAfastamento.data_fim + "T00:00").toLocaleDateString("pt-BR")}</div>}
          </div>
        )}

        {/* Banner ocorrência pendente */}
        {ocorrenciaHoje && ocorrenciaHoje.status === "pendente" && (
          <div style={{ borderRadius: 14, padding: "14px 16px", background: "#451a03", border: `2px solid ${DC.warn}`, display: "flex", alignItems: "center", gap: 12 }}>
            <span style={{ fontSize: 28 }}>⚠️</span>
            <div style={{ flex: 1 }}>
              <div style={{ fontWeight: 700, fontSize: 14, color: "#fbbf24" }}>
                {ocorrenciaHoje.tipo === "atraso" ? `Atraso de ${ocorrenciaHoje.minutos} min detectado` : "Falta registrada"}
              </div>
              <div style={{ fontSize: 12, color: DC.textMid, marginTop: 2 }}>Toque para justificar</div>
            </div>
            <button onClick={() => { setModalJustificativa({ ocorrenciaId: ocorrenciaHoje.id, minutos: ocorrenciaHoje.minutos }); setJustificativaTexto(""); }}
              style={{ background: DC.warn, color: "#000", border: "none", borderRadius: 8, padding: "8px 14px", fontWeight: 700, fontSize: 13, cursor: "pointer" }}>
              Justificar
            </button>
          </div>
        )}

        {/* Modal justificativa */}
        {modalJustificativa && (
          <Modal title={`Justificar ${modalJustificativa.minutos ? `atraso de ${modalJustificativa.minutos} min` : "ocorrência"}`} onClose={() => setModalJustificativa(null)}>
            <Input label="Justificativa" value={justificativaTexto} onChange={e => setJustificativaTexto(e.target.value)} placeholder="Ex: trânsito, emergência..." />
            <div style={{ display: "flex", gap: 10, marginTop: 16, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setModalJustificativa(null)}>Cancelar</Btn>
              <Btn onClick={enviarJustificativa} disabled={!justificativaTexto.trim()}>Enviar</Btn>
            </div>
          </Modal>
        )}

        {/* Status chips */}
        <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
          <span style={{ background: online ? "#16a34a33" : "#78350f33", color: online ? "#4ade80" : DC.warn, border: `1px solid ${online ? "#16a34a55" : DC.warn+"55"}`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
            {online ? "🌐 Online" : "📵 Offline"}
          </span>
          {pendentes.length > 0 && (
            <span style={{ background: "#78350f33", color: DC.warn, border: `1px solid ${DC.warn}55`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
              ⏳ {pendentes.length} registro(s) offline
            </span>
          )}
          <span style={{ background: "#1e293b", color: statusGPS === "ok" ? "#4ade80" : DC.textMid, border: `1px solid ${DC.border}`, borderRadius: 20, padding: "4px 12px", fontSize: 12, fontWeight: 600 }}>
            {statusGPS === "ok" ? `📍 GPS ±${Math.round(posicao?.precisao||0)}m` : statusGPS === "erro" ? "📍 Sem GPS" : "📍 Obtendo GPS..."}
          </span>
        </div>

        {/* Card área */}
        {areas.length > 0 && (
          <div style={{ borderRadius: 14, padding: "14px 18px", background: dentroArea ? "#16a34a22" : "#451a0344", border: `2px solid ${dentroArea ? DC.green : DC.warn}`, display: "flex", alignItems: "center", gap: 14 }}>
            <span style={{ fontSize: 36 }}>{dentroArea ? "✅" : "⚠️"}</span>
            <div>
              <div style={{ fontWeight: 700, fontSize: 15, color: dentroArea ? "#4ade80" : "#fbbf24" }}>
                {dentroArea ? `Em "${areaAtual.nome}"` : "Fora da área permitida"}
              </div>
              <div style={{ fontSize: 12, color: DC.textMid, marginTop: 3 }}>
                {dentroArea ? "Você pode bater o ponto agora" : distanciaM ? `${distanciaM}m do local mais próximo` : "Aguardando localização..."}
              </div>
            </div>
          </div>
        )}

        {/* Botão principal */}
        <div style={{ display: "flex", flexDirection: "column", alignItems: "center", gap: 16, padding: "10px 0 24px" }}>
          <div style={{ fontSize: 11, color: DC.textMid, textTransform: "uppercase", letterSpacing: 2 }}>
            Próximo registro
          </div>
          <button
            onClick={baterPonto}
            disabled={batendo || (!podeBater && online)}
            style={{
              width: 210, height: 210, borderRadius: "50%",
              background: (!podeBater && online) ? "#334155" : (proximoTipo === "entrada" ? "linear-gradient(135deg, #16a34a, #15803d)" : "linear-gradient(135deg, #ef4444, #b91c1c)"),
              border: `4px solid ${(!podeBater && online) ? "#475569" : (proximoTipo === "entrada" ? "#4ade80" : "#fca5a5")}`,
              cursor: (!podeBater && online) ? "not-allowed" : "pointer",
              color: "#fff", fontWeight: 900,
              display: "flex", flexDirection: "column", alignItems: "center", justifyContent: "center", gap: 6,
              boxShadow: (!podeBater && online) ? "none" : `0 0 60px ${proximoTipo === "entrada" ? "#16a34a88" : "#ef444488"}`,
              transition: "all .25s",
              WebkitTapHighlightColor: "transparent",
            }}
          >
            <span style={{ fontSize: 56 }}>{proximoTipo === "entrada" ? "▶" : "⏹"}</span>
            <span style={{ fontSize: 17, letterSpacing: 2 }}>{batendo ? "..." : proximoTipo === "entrada" ? "ENTRADA" : "SAÍDA"}</span>
          </button>
          <div style={{ fontSize: 14, color: DC.textMid, fontVariantNumeric: "tabular-nums", textAlign: "center" }}>
            {new Date().toLocaleString("pt-BR", { weekday: "long", hour: "2-digit", minute: "2-digit" })}
          </div>
        </div>

        {/* Mensagem */}
        {msg && (
          <div style={{
            background: msg.startsWith("✅") ? "#16a34a22" : msg.startsWith("📵") ? "#451a0333" : "#7f1d1d33",
            border: `1px solid ${msg.startsWith("✅") ? "#16a34a" : msg.startsWith("📵") ? DC.warn : DC.red}`,
            borderRadius: 12, padding: "12px 16px", fontSize: 14, color: DC.text,
          }}>
            {msg}
          </div>
        )}

        {/* Timeline de hoje */}
        <div style={{ background: DC.card, borderRadius: 14, padding: "16px", border: `1px solid ${DC.border}` }}>
          <div style={{ fontWeight: 700, marginBottom: 14, color: DC.text, fontSize: 14 }}>Registros de hoje</div>
          {registrosHoje.length === 0 && pendentes.length === 0 ? (
            <div style={{ color: DC.textMid, fontSize: 13, textAlign: "center", padding: "10px 0" }}>Nenhum registro hoje.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {registrosHoje.map((r, i) => (
                <div key={i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: r.tipo === "entrada" ? "#16a34a18" : "#ef444418", borderRadius: 10, border: `1px solid ${r.tipo === "entrada" ? "#16a34a44" : "#ef444444"}` }}>
                  <span style={{ fontSize: 22 }}>{r.tipo === "entrada" ? "▶" : "⏹"}</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: r.tipo === "entrada" ? "#4ade80" : "#fca5a5" }}>
                      {r.tipo === "entrada" ? "Entrada" : "Saída"}
                    </div>
                    <div style={{ fontSize: 12, color: DC.textMid }}>{horaFmt(r.registrado_em)}{r.offline ? " · offline" : ""}</div>
                  </div>
                </div>
              ))}
              {pendentes.map((r, i) => (
                <div key={"p"+i} style={{ display: "flex", alignItems: "center", gap: 12, padding: "10px 14px", background: "#78350f22", borderRadius: 10, border: `1px solid ${DC.warn}44`, opacity: 0.75 }}>
                  <span style={{ fontSize: 22 }}>⏳</span>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14, color: "#fbbf24" }}>{r.tipo === "entrada" ? "Entrada" : "Saída"} (offline)</div>
                    <div style={{ fontSize: 12, color: DC.textMid }}>Aguardando sincronização</div>
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    );
  }

  // ── Render admin ──────────────────────────────────────────────────────────────
  return (
    <div style={{ display: "flex", flexDirection: "column", gap: 16 }}>
      <div style={{ display: "flex", gap: 8, borderBottom: `2px solid ${COLORS.border}`, paddingBottom: 0 }}>
        {[["mensal","📅 Relatório Mensal"],["registros","📋 Registros do Dia"],["areas","📍 Áreas Permitidas"]].map(([t, label]) => (
          <button key={t} onClick={() => setAbaAdmin(t)} style={{
            background: "none", border: "none", padding: "8px 18px",
            borderBottom: abaAdmin === t ? `2px solid ${COLORS.primary}` : "2px solid transparent",
            color: abaAdmin === t ? COLORS.primary : COLORS.textMid,
            fontWeight: abaAdmin === t ? 700 : 400, cursor: "pointer", fontSize: 14,
          }}>
            {label}
          </button>
        ))}
      </div>

      {/* Aba Mensal */}
      {abaAdmin === "mensal" && (() => {
        const [anoM, mesM] = mesMensal.split('-').map(Number);
        const diasNoMes = new Date(anoM, mesM, 0).getDate();
        const dias = Array.from({ length: diasNoMes }, (_, i) => i + 1);
        // Agrupa: { nomeFunc: { dia: { entrada: "08:00", saida: "17:00" } } }
        const mapa = {};
        for (const r of dadosMensais) {
          const nome = r.func_nome || r.funcionario_nome || `ID ${r.funcionario_id}`;
          if (!mapa[nome]) mapa[nome] = {};
          if (!mapa[nome][r.dia]) mapa[nome][r.dia] = {};
          if (r.tipo === "entrada" && !mapa[nome][r.dia].entrada) mapa[nome][r.dia].entrada = r.hora;
          if (r.tipo === "saida") mapa[nome][r.dia].saida = r.hora;
        }
        const funcs = Object.keys(mapa).sort();
        const hoje = new Date();
        return (
          <Card>
            <div style={{ display: "flex", gap: 12, alignItems: "flex-end", marginBottom: 16, flexWrap: "wrap" }}>
              <Input label="Mês" type="month" value={mesMensal} onChange={e => setMesMensal(e.target.value)} />
              <div style={{ fontSize: 13, color: COLORS.textMid, alignSelf: "center" }}>
                {funcs.length} funcionário(s) com registros
              </div>
            </div>
            {funcs.length === 0 ? (
              <div style={{ color: COLORS.textMid, fontSize: 13 }}>Nenhum registro neste mês.</div>
            ) : (
              <div style={{ overflowX: "auto" }}>
                <table style={{ borderCollapse: "collapse", fontSize: 11, minWidth: 900 }}>
                  <thead>
                    <tr style={{ background: COLORS.bg }}>
                      <th style={{ padding: "8px 12px", textAlign: "left", fontWeight: 700, borderBottom: `2px solid ${COLORS.border}`, position: "sticky", left: 0, background: COLORS.bg, zIndex: 1, minWidth: 140 }}>
                        Funcionário
                      </th>
                      {dias.map(d => {
                        const dataD = new Date(anoM, mesM - 1, d);
                        const isHoje = dataD.toDateString() === hoje.toDateString();
                        const isDom = dataD.getDay() === 0;
                        const isSab = dataD.getDay() === 6;
                        return (
                          <th key={d} style={{
                            padding: "4px 6px", textAlign: "center", borderBottom: `2px solid ${COLORS.border}`,
                            minWidth: 52, fontWeight: isHoje ? 800 : 600,
                            color: isDom ? COLORS.danger : isSab ? COLORS.accent : isHoje ? COLORS.primary : COLORS.textMid,
                            background: isHoje ? COLORS.primaryBg : COLORS.bg,
                          }}>
                            <div>{d}</div>
                            <div style={{ fontSize: 9, fontWeight: 400 }}>
                              {["Dom","Seg","Ter","Qua","Qui","Sex","Sáb"][dataD.getDay()]}
                            </div>
                          </th>
                        );
                      })}
                    </tr>
                  </thead>
                  <tbody>
                    {funcs.map((nome, fi) => (
                      <tr key={nome} style={{ background: fi % 2 === 0 ? COLORS.white : COLORS.gray }}>
                        <td style={{ padding: "6px 12px", fontWeight: 600, borderRight: `1px solid ${COLORS.border}`, position: "sticky", left: 0, background: fi % 2 === 0 ? COLORS.white : COLORS.gray, zIndex: 1, whiteSpace: "nowrap" }}>
                          {nome}
                        </td>
                        {dias.map(d => {
                          const reg = mapa[nome][d];
                          const dataD = new Date(anoM, mesM - 1, d);
                          const isDom = dataD.getDay() === 0;
                          const isSab = dataD.getDay() === 6;
                          return (
                            <td key={d} style={{
                              padding: "4px 4px", textAlign: "center", borderRight: `1px solid ${COLORS.border}`,
                              background: (isDom || isSab) ? "#f9fafb" : undefined,
                              verticalAlign: "middle",
                            }}>
                              {reg ? (
                                <div style={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                  {reg.entrada && <div style={{ color: COLORS.primary, fontWeight: 700, fontSize: 10 }}>{reg.entrada}</div>}
                                  {reg.saida && <div style={{ color: COLORS.danger, fontWeight: 700, fontSize: 10 }}>{reg.saida}</div>}
                                  {reg.entrada && !reg.saida && <div style={{ color: COLORS.warning, fontSize: 9 }}>sem saída</div>}
                                </div>
                              ) : (
                                <span style={{ color: COLORS.border, fontSize: 10 }}>—</span>
                              )}
                            </td>
                          );
                        })}
                      </tr>
                    ))}
                  </tbody>
                </table>
                <div style={{ marginTop: 10, fontSize: 11, color: COLORS.textMid, display: "flex", gap: 16 }}>
                  <span><span style={{ color: COLORS.primary, fontWeight: 700 }}>▲ verde</span> = entrada</span>
                  <span><span style={{ color: COLORS.danger, fontWeight: 700 }}>▲ vermelho</span> = saída</span>
                  <span>— = sem registro</span>
                </div>
              </div>
            )}
          </Card>
        );
      })()}

      {/* Aba Registros */}
      {abaAdmin === "registros" && (
        <Card>
          <div style={{ display: "flex", gap: 10, flexWrap: "wrap", marginBottom: 14, alignItems: "flex-end" }}>
            <Input label="Data" type="date" value={filtroData} onChange={e => setFiltroData(e.target.value)} />
            <Input label="ID Funcionário" value={filtroFunc} onChange={e => setFiltroFunc(e.target.value)} placeholder="Todos" style={{ width: 140 }} />
            <Btn onClick={carregarRegistros}>Filtrar</Btn>
          </div>
          {registros.length === 0 ? (
            <div style={{ color: COLORS.textMid, fontSize: 13 }}>Nenhum registro encontrado.</div>
          ) : (
            <table style={{ width: "100%", borderCollapse: "collapse", fontSize: 13 }}>
              <thead>
                <tr style={{ background: COLORS.bg }}>
                  {["Funcionário","Tipo","Horário","GPS","Área","Offline"].map(h => (
                    <th key={h} style={{ padding: "8px 10px", textAlign: "left", color: COLORS.textMid, fontWeight: 600, borderBottom: `1px solid ${COLORS.border}` }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {registros.map(r => (
                  <tr key={r.id} style={{ borderBottom: `1px solid ${COLORS.border}` }}>
                    <td style={{ padding: "8px 10px", fontWeight: 600 }}>{r.func_nome || r.funcionario_nome}</td>
                    <td style={{ padding: "8px 10px" }}>
                      <Badge color={r.tipo === "entrada" ? COLORS.primary : COLORS.danger}>
                        {r.tipo === "entrada" ? "▶ Entrada" : "⏹ Saída"}
                      </Badge>
                    </td>
                    <td style={{ padding: "8px 10px", whiteSpace: "nowrap" }}>
                      {new Date(r.registrado_em).toLocaleString("pt-BR")}
                    </td>
                    <td style={{ padding: "8px 10px", fontSize: 11, color: COLORS.textMid }}>
                      {r.latitude ? `${parseFloat(r.latitude).toFixed(4)}, ${parseFloat(r.longitude).toFixed(4)}` : "—"}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {r.dentro_area ? <Badge color={COLORS.primary}>✅ Dentro</Badge> : <Badge color={COLORS.warning}>⚠️ Fora</Badge>}
                    </td>
                    <td style={{ padding: "8px 10px" }}>
                      {r.offline ? <Badge color={COLORS.warning}>Sim</Badge> : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      )}

      {/* Aba Áreas */}
      {abaAdmin === "areas" && (
        <Card>
          <SectionTitle action={<Btn size="sm" onClick={() => { setModalArea({}); setFormArea({ nome: "", latitude: "", longitude: "", raio: 100 }); }}>+ Nova Área</Btn>}>
            Áreas Permitidas para Ponto
          </SectionTitle>
          <div style={{ fontSize: 12, color: COLORS.textMid, marginBottom: 14 }}>
            Cada área define um local onde os funcionários podem bater ponto. O raio é em metros.
          </div>
          {areasConfig.length === 0 ? (
            <div style={{ color: COLORS.textMid, fontSize: 13 }}>Nenhuma área cadastrada. Sem áreas, qualquer localização é aceita.</div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: 10 }}>
              {areasConfig.map(a => (
                <div key={a.id} style={{ display: "flex", alignItems: "center", gap: 12, padding: "12px 14px", background: COLORS.bg, borderRadius: 8, border: `1px solid ${COLORS.border}` }}>
                  <div style={{ fontSize: 22 }}>📍</div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontWeight: 700, fontSize: 14 }}>{a.nome}</div>
                    <div style={{ fontSize: 12, color: COLORS.textMid }}>
                      {parseFloat(a.latitude).toFixed(6)}, {parseFloat(a.longitude).toFixed(6)} — raio {a.raio}m
                    </div>
                  </div>
                  <div style={{ display: "flex", gap: 6 }}>
                    <Btn size="sm" variant="ghost" onClick={() => { setModalArea(a); setFormArea({ nome: a.nome, latitude: a.latitude, longitude: a.longitude, raio: a.raio }); }}>Editar</Btn>
                    <Btn size="sm" variant="danger" onClick={() => excluirArea(a.id)}>Excluir</Btn>
                  </div>
                </div>
              ))}
            </div>
          )}
        </Card>
      )}

      {/* Modal área */}
      {modalArea && (
        <Modal title={modalArea.id ? "Editar Área" : "Nova Área"} onClose={() => setModalArea(null)}>
          <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
            <Input label="Nome do local" value={formArea.nome} onChange={e => setFormArea(f => ({ ...f, nome: e.target.value }))} placeholder="Ex: Sede, Filial Centro..." />
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 10 }}>
              <Input label="Latitude" value={formArea.latitude} onChange={e => setFormArea(f => ({ ...f, latitude: e.target.value }))} placeholder="-23.5505" />
              <Input label="Longitude" value={formArea.longitude} onChange={e => setFormArea(f => ({ ...f, longitude: e.target.value }))} placeholder="-46.6333" />
            </div>
            <Input label="Raio (metros)" type="number" value={formArea.raio} onChange={e => setFormArea(f => ({ ...f, raio: Number(e.target.value) }))} />
            <div style={{ fontSize: 12, color: COLORS.textMid, background: COLORS.bg, borderRadius: 8, padding: "10px 12px" }}>
              <strong>Como obter as coordenadas:</strong> abra o Google Maps, clique com botão direito no local e copie as coordenadas. Ou clique abaixo se estiver no local agora.
            </div>
            <Btn variant="ghost" onClick={usarPosicaoAtual}>Usar minha localização</Btn>
            <div style={{ display: "flex", gap: 8, justifyContent: "flex-end" }}>
              <Btn variant="ghost" onClick={() => setModalArea(null)}>Cancelar</Btn>
              <Btn onClick={salvarArea}>Salvar</Btn>
            </div>
          </div>
        </Modal>
      )}
    </div>
  );
}

const PERFIS_TODOS   = ["admin","administrador","rh","gestor","visualizador"];
const PERFIS_GESTAO  = ["administrador","rh"];
const PERFIS_GESTOR  = ["administrador"];

// admin         = super-admin (Bruno), acesso a tudo, todos os tenants
// administrador = antigo gestor, acesso completo ao próprio tenant
// gestor        = RH + Suprimentos + Config (empresas + usuários rh/funcionario)
// rh            = RH sem config nem financeiro
const RH_PERFIS  = ["administrador","rh","gestor","visualizador"];
const ADM_PERFIS = ["administrador","gestor"];

// ─── PIX EM LOTES ─────────────────────────────────────────────────────────────
function PixEmLotes({ token, usuario, employees, abaInicial, empresaFiltroId }) {
  const [aba, setAba] = useState(abaInicial || "lancamentos");
  const [dashboard, setDashboard] = useState(null);
  const [lancamentos, setLancamentos] = useState([]);
  const [lotes, setLotes] = useState([]);
  const [remessas, setRemessas] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loteAtivo, setLoteAtivo] = useState(null);
  const [lancamentosLote, setLancamentosLote] = useState([]);
  const [sel, setSel] = useState([]);
  const [modal, setModal] = useState(null);
  const [modalMode, setModalMode] = useState("manual");
  const [form, setForm] = useState({ funcionario_id: "", valor: "", data_pagamento: "", competencia: "", documento: "", observacao: "" });
  const [importMes, setImportMes] = useState(String(new Date().getMonth()+1).padStart(2,"0"));
  const [importAno, setImportAno] = useState(String(new Date().getFullYear()));
  const [importItems, setImportItems] = useState([]);
  const [importSel, setImportSel] = useState([]);
  const [importLoading, setImportLoading] = useState(false);
  const [importErr, setImportErr] = useState(null);
  const [importBuscou, setImportBuscou] = useState(false);
  const [nomeLote, setNomeLote] = useState("");
  const [renomearLoteId, setRenomearLoteId] = useState(null);
  const [renomearNome, setRenomearNome] = useState("");
  const [concilFile, setConcilFile] = useState(null);
  const [concilRemessaId, setConcilRemessaId] = useState("");
  const [concilResultado, setConcilResultado] = useState(null);
  const [err, setErr] = useState(null);
  const [ok, setOk] = useState(null);
  const [empresas, setEmpresas] = useState([]);
  const [empresaId, setEmpresaId] = useState("");
  const [funcContaPadrao, setFuncContaPadrao] = useState(null);
  const [funcBusca, setFuncBusca] = useState("");
  const [funcDropOpen, setFuncDropOpen] = useState(false);
  const [filtroEmpresaImport, setFiltroEmpresaImport] = useState("");

  const fmtR$ = v => parseFloat(v||0).toLocaleString("pt-BR",{style:"currency",currency:"BRL"});
  const fmtDt = d => { if (!d) return "—"; const p = String(d).substring(0,10).split("-"); return p.length===3?`${p[2]}/${p[1]}/${p[0]}`:"—"; };
  const hoje = () => new Date().toISOString().slice(0,10);
  const mesAtual = () => new Date().toISOString().slice(0,7);

  const SLABEL = { rascunho:"Rascunho", aprovado:"Aprovado", em_lote:"Em Lote", cancelado:"Cancelado", quitado:"Quitado" };
  const SCOR   = { rascunho:COLORS.textMid, aprovado:"#10b981", em_lote:"#6366f1", cancelado:COLORS.danger, quitado:"#0ea5e9" };
  const SLOTE_LABEL = { pendente:"Pendente", gerado:"Gerado", pago:"Pago", aberto:"Pendente", efetivado:"Pago", conciliado:"Pago", conciliado_com_erros:"Com Erros" };
  const SLOTE_COR   = { pendente:"#ef4444", gerado:"#3b82f6", pago:"#10b981", aberto:"#ef4444", efetivado:"#10b981", conciliado:"#10b981", conciliado_com_erros:"#f59e0b" };

  const carregarDashboard = async () => {
    try { setDashboard(await apiFetch("GET","/api/pix-lotes/dashboard",undefined,token)); } catch(e) { setErr(e.message); }
  };
  const carregarLancamentos = async () => {
    setLoading(true);
    try { setLancamentos(await apiFetch("GET","/api/pix-lotes/lancamentos",undefined,token)); } catch(e) { setErr(e.message); }
    setLoading(false);
  };
  const carregarLotes = async () => {
    try { setLotes(await apiFetch("GET","/api/pix-lotes/lotes",undefined,token)); } catch(e) { setErr(e.message); }
  };
  const carregarRemessas = async () => {
    try { setRemessas(await apiFetch("GET","/api/pix-lotes/remessas",undefined,token)); } catch(e) { setErr(e.message); }
  };

  useEffect(() => {
    apiFetch("GET", "/api/empresas", undefined, token).then(list => {
      setEmpresas(list||[]);
      const classe = (list||[]).find(e => /classe/i.test(e.nome||""));
      if (classe) setEmpresaId(String(classe.id));
      else if (list?.length===1) setEmpresaId(String(list[0].id));
    }).catch(()=>{});
  }, [token]);

  useEffect(() => {
    if (empresaFiltroId) setEmpresaId(String(empresaFiltroId));
  }, [empresaFiltroId]);

  useEffect(() => {
    if (aba === "dashboard") carregarDashboard();
    if (aba === "lancamentos") { carregarLancamentos(); carregarLotes(); }
    if (aba === "lotes") { carregarLotes(); carregarRemessas(); }
    if (aba === "conciliacao") { carregarRemessas(); }
  }, [aba]);

  useEffect(() => {
    if (loteAtivo) {
      apiFetch("GET",`/api/pix-lotes/lotes/${loteAtivo}/lancamentos`,undefined,token)
        .then(rows => setLancamentosLote(rows||[]))
        .catch(() => setLancamentosLote([]));
    }
  }, [loteAtivo]);

  const salvarLancamento = async () => {
    if (!form.funcionario_id || !form.valor || !form.data_pagamento) { setErr("Funcionário, valor e data são obrigatórios."); return; }
    setLoading(true); setErr(null);
    try {
      await apiFetch("POST","/api/pix-lotes/lancamentos",form,token);
      setOk("Lançamento criado!"); setModal(null);
      setForm({ funcionario_id:"",valor:"",data_pagamento:hoje(),competencia:mesAtual(),documento:"",observacao:"" });
      carregarLancamentos();
    } catch(e) { setErr(e.message); }
    setLoading(false);
  };

  const excluirLote = async (id) => {
    if (!window.confirm("Excluir este lote e todos os seus lançamentos e remessas? Isso não pode ser desfeito.")) return;
    try {
      await apiFetch("DELETE", `/api/pix-lotes/lotes/${id}`, undefined, token);
      setLotes(prev => prev.filter(l => l.id !== id));
      if (loteAtivo === id) setLoteAtivo(null);
      setLancamentosLote([]);
    } catch(e) { alert("Erro: " + e.message); }
  };

  const excluirLancamento = async id => {
    if (!window.confirm("Excluir este lançamento?")) return;
    try {
      await apiFetch("DELETE",`/api/pix-lotes/lancamentos/${id}`,undefined,token);
      setLancamentos(prev => prev.filter(l=>l.id!==id));
      setSel(prev => prev.filter(x=>x!==id));
    } catch(e) { setErr(e.message); }
  };

  const excluirSelecionados = async () => {
    if (!sel.length) return;
    if (!window.confirm(`Excluir ${sel.length} lançamento(s) selecionado(s)?`)) return;
    let ok = 0;
    for (const id of sel) {
      try {
        await apiFetch("DELETE",`/api/pix-lotes/lancamentos/${id}`,undefined,token);
        ok++;
      } catch(_) {}
    }
    setLancamentos(prev => prev.filter(l=>!sel.includes(l.id)));
    setSel([]);
    setOk(`${ok} lançamento(s) excluído(s).`);
  };

  const carregarImport = async () => {
    setImportLoading(true); setImportItems([]); setImportErr(null); setImportBuscou(false);
    try {
      let items = [];
      if (modalMode === "folha") {
        // Usa employees já carregados — evita problema de tenant na API
        const mm = String(importMes).padStart(2,"0");
        items = (employees||[])
          .filter(f => (f.status||"").toLowerCase() !== "demitido")
          .map(f => {
            const pg = f.pagamento || {};
            const emp = empresas.find(e => String(e.id) === String(f.empresa_id));
            return {
              funcionario_id: f.id,
              funcionario_nome: f.nome,
              empresa_id: f.empresa_id || null,
              empresa_nome: emp?.nome || f.empresa || f.empresa_nome || null,
              valor: parseFloat(f.salario || 0),
              data_pagamento: `${importAno}-${mm}-05`,
              competencia: `${importAno}-${mm}`,
              documento: `SAL${importAno}${mm}`,
              fc_pix_chave: pg.fcPixChave || null,
              fc_conta: pg.fcConta || null,
              fc_agencia: pg.fcAgencia || null,
            };
          });
      } else if (modalMode === "auxilio") {
        const mm = String(importMes).padStart(2,"0");
        items = (employees||[])
          .filter(f => (f.status||"").toLowerCase() !== "demitido")
          .map(f => {
            const pg = f.pagamento || {};
            const emp = empresas.find(e => String(e.id) === String(f.empresa_id));
            return {
              funcionario_id: f.id,
              funcionario_nome: f.nome,
              empresa_id: f.empresa_id || null,
              empresa_nome: emp?.nome || f.empresa || f.empresa_nome || null,
              valor: 200,
              data_pagamento: `${importAno}-${mm}-05`,
              competencia: `${importAno}-${mm}`,
              documento: `AUX${importAno}${mm}`,
              observacao: "Auxílio Cesta Básica",
              fc_pix_chave: pg.fcPixChave || null,
              fc_conta: pg.fcConta || null,
              fc_agencia: pg.fcAgencia || null,
            };
          });
      } else {
        const raw = await apiFetch("GET",`/api/pix-lotes/importar/adiantamento?mes=${importMes}&ano=${importAno}`,undefined,token);
        items = Array.isArray(raw) ? raw : [];
      }
      setImportItems(items);
      setImportSel(items.map((_,i)=>i).filter(i=>!!(items[i].fc_pix_chave||items[i].fc_conta)));
      setImportBuscou(true);
    } catch(e) {
      console.error("Erro importar:", e);
      setImportErr(e.message||"Erro ao buscar dados.");
    }
    setImportLoading(false);
  };

  const confirmarImport = async () => {
    const items = importItems.filter((_,i)=>importSel.includes(i));
    if (!items.length) { setErr("Selecione ao menos um item."); return; }
    setLoading(true); setErr(null);
    let count = 0;
    for (const item of items) {
      try {
        await apiFetch("POST","/api/pix-lotes/lancamentos",{ funcionario_id:item.funcionario_id, valor:item.valor, data_pagamento:item.data_pagamento, competencia:item.competencia, documento:item.documento||"", observacao:item.observacao||"" },token);
        count++;
      } catch(_) {}
    }
    setOk(`${count} lançamento(s) importado(s)!`);
    setModal(null); carregarLancamentos();
    setLoading(false);
  };

  const empresaSel = empresas.find(e => String(e.id) === empresaId) || null;

  const criarLote = async () => {
    if (!empresaSel) { setErr("Selecione a empresa pagadora antes de criar o lote."); return; }
    if (!nomeLote.trim()) { setErr("Nome do lote é obrigatório."); return; }
    if (!sel.length) { setErr("Selecione ao menos um lançamento."); return; }
    const funcIds = sel.map(id=>lancamentos.find(l=>l.id===id)?.funcionario_id).filter(Boolean);
    const dupes = funcIds.filter((id,i)=>funcIds.indexOf(id)!==i);
    if (dupes.length) {
      const names = [...new Set(sel.map(id=>lancamentos.find(l=>l.id===id)).filter(l=>dupes.includes(l?.funcionario_id)).map(l=>l?.funcionario_nome||"?"))];
      setErr(`Funcionários duplicados: ${names.join(", ")}. Desmarque os duplicados.`); return;
    }
    setLoading(true); setErr(null);
    try {
      const r = await apiFetch("POST","/api/pix-lotes/lotes",{ ids:sel, nome:nomeLote, empresa_id:empresaId },token);
      setOk(`Lote "${nomeLote}" criado! Total: ${fmtR$(r.total)} | ${r.qtd} item(s).`);
      setModal(null); setNomeLote(""); setSel([]);
      carregarLancamentos(); carregarLotes();
    } catch(e) { setErr(e.message); }
    setLoading(false);
  };

  const renomearLote = async () => {
    if (!renomearNome.trim()) { setErr("Nome é obrigatório."); return; }
    try {
      await apiFetch("PATCH",`/api/pix-lotes/lotes/${renomearLoteId}`,{nome:renomearNome},token);
      setOk("Lote renomeado."); setModal(null); setRenomearLoteId(null); setRenomearNome("");
      carregarLotes();
    } catch(e) { setErr(e.message); }
  };

  const gerarRemessa = async loteId => {
    if (!empresaSel) { setErr("Selecione a empresa pagadora para gerar a remessa."); return; }
    setErr(null);
    const body = { empresa_id:empresaSel.id, cnpj:empresaSel.cnpj, razao_social:empresaSel.razao_social||empresaSel.nome, banco_codigo:empresaSel.banco_codigo, banco_ispb:empresaSel.banco_ispb, banco_nome:empresaSel.banco_nome, agencia:empresaSel.agencia, agencia_dv:empresaSel.agencia_dv, conta:empresaSel.conta, conta_dv:empresaSel.conta_dv };
    try {
      const res = await fetch(`/api/pix-lotes/lotes/${loteId}/remessa`, { method:"POST", headers:{Authorization:`Bearer ${token}`,"Content-Type":"application/json"}, body:JSON.stringify(body) });
      if (!res.ok) { const j=await res.json(); throw new Error(j.erro||"Erro ao gerar remessa"); }
      const blob = await res.blob();
      const cd = res.headers.get("Content-Disposition")||"";
      const nm = (cd.match(/filename="?([^"]+)"?/)||[])[1]||`remessa_lote${loteId}.REM`;
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a"); a.href=url; a.download=nm; a.click();
      URL.revokeObjectURL(url);
      setOk(`Arquivo ${nm} gerado.`); carregarLotes(); carregarRemessas();
    } catch(e) { setErr(e.message); }
  };

  const enviarConciliacao = async () => {
    if (!concilRemessaId) { setErr("Selecione a remessa relacionada antes de processar."); return; }
    if (!concilFile) { setErr("Selecione o arquivo de retorno .RET"); return; }
    setErr(null); setOk(null); setConcilResultado(null); setLoading(true);
    try {
      const texto = await concilFile.text();
      const r = await apiFetch("POST","/api/pix-lotes/conciliacao",{ remessa_id:concilRemessaId, retorno_nome:concilFile.name, retorno_conteudo:texto },token);
      setConcilResultado(r);
      if (r.aprovado) {
        setOk(r.mensagem);
        setConcilFile(null); setConcilRemessaId("");
      }
      carregarRemessas(); carregarLotes();
    } catch(e) { setErr(e.message); }
    setLoading(false);
  };

  const ABAS = [
    { id:"dashboard",   label:"Dashboard" },
    { id:"lancamentos", label:"Lançamentos" },
    { id:"lotes",       label:"Lotes e Remessas" },
    { id:"conciliacao", label:"Conciliação" },
  ];

  const funcAtivos = (employees||[]).filter(f => f.status!=="demitido" || !f.status);
  const getLancEmpresa = (l) => {
    if (l.empresa_nome) return l.empresa_nome;
    const emp = (employees||[]).find(e => e.id === l.funcionario_id);
    return emp?.empresa || null;
  };
  const lancPendentesAll = lancamentos.filter(l => l.status==="rascunho" || l.status==="aprovado");
  const lancPendentes = empresaFiltroId
    ? lancPendentesAll.filter(l => {
        const emp = (employees||[]).find(e => e.id === l.funcionario_id);
        return emp ? String(emp.empresa_id) === String(empresaFiltroId) : false;
      })
    : lancPendentesAll;
  const totalSel = sel.reduce((s,id)=>s+parseFloat(lancamentos.find(l=>l.id===id)?.valor||0),0);
  const funcIdsSel = sel.map(id=>lancamentos.find(l=>l.id===id)?.funcionario_id).filter(Boolean);
  const dupesSel = funcIdsSel.filter((id,i)=>funcIdsSel.indexOf(id)!==i);
  const temDuplicados = dupesSel.length > 0;

  const toggleSel = id => setSel(prev => prev.includes(id) ? prev.filter(x=>x!==id) : [...prev,id]);
  const toggleTodos = () => {
    const ids = lancPendentes.map(l=>l.id);
    setSel(ids.every(id=>sel.includes(id)) ? [] : ids);
  };

  const BadgeStatus = ({s, forLote}) => {
    const label = forLote ? (SLOTE_LABEL[s]||s) : (SLABEL[s]||s);
    const cor   = forLote ? (SLOTE_COR[s]||COLORS.textMid) : (SCOR[s]||COLORS.textMid);
    return <span style={{fontSize:11,fontWeight:700,color:cor,background:cor+"18",padding:"2px 8px",borderRadius:10}}>{label}</span>;
  };

  const empresaLogo = (emp) => emp?.logo_base64 || null;

  return (
    <div style={{padding:0}}>

      {/* Alertas */}
      {err&&<div style={{background:"#fee2e2",border:"1px solid #fca5a5",borderRadius:6,padding:"9px 14px",marginBottom:12,fontSize:13,color:"#dc2626",display:"flex",justifyContent:"space-between",alignItems:"center"}}>{err}<button onClick={()=>setErr(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#dc2626",fontSize:18}}>×</button></div>}
      {ok&&<div style={{background:"#d1fae5",border:"1px solid #6ee7b7",borderRadius:6,padding:"9px 14px",marginBottom:12,fontSize:13,color:"#065f46",display:"flex",justifyContent:"space-between",alignItems:"center"}}>{ok}<button onClick={()=>setOk(null)} style={{background:"none",border:"none",cursor:"pointer",color:"#065f46",fontSize:18}}>×</button></div>}


      {/* ── DASHBOARD ── */}
      {aba==="dashboard"&&(
        <div>
          {!dashboard?<div style={{textAlign:"center",padding:40,color:COLORS.textLight}}>Carregando...</div>:(
            <div style={{display:"flex",flexDirection:"column",gap:14}}>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                {[{label:"Favorecidos",val:dashboard.favorecidos_count||0,sub:"funcionários com lançamentos"},{label:"Total de lançamentos",val:dashboard.total_lanc,sub:"histórico completo"},{label:"Valor total registrado",val:fmtR$(dashboard.total_valor),sub:"inclui todos os status"},{label:"Arquivos .REM",val:dashboard.total_rem,sub:"remessas geradas"}].map(k=>(
                  <div key={k.label} style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:"14px 16px"}}>
                    <div style={{fontSize:11,color:COLORS.textMid,fontWeight:500,marginBottom:8}}>{k.label}</div>
                    <div style={{fontSize:22,fontWeight:900,color:COLORS.text,lineHeight:1}}>{k.val}</div>
                    <div style={{fontSize:11,color:COLORS.textLight,marginTop:5}}>{k.sub}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"repeat(4,1fr)",gap:10}}>
                {[{label:"A pagar (rascunho)",val:fmtR$(dashboard.pendentes),color:"#f59e0b",sub:`${(dashboard.por_status||[]).find(s=>s.status==="aprovado")?.cnt||0} lançamentos`},{label:"Em Lote",val:fmtR$(dashboard.em_lote),color:"#6366f1",sub:`${(dashboard.por_status||[]).find(s=>s.status==="em_lote")?.cnt||0} lançamentos`},{label:"Lotes criados",val:dashboard.total_lotes,color:"#8b5cf6",sub:"total de lotes"},{label:"Empresas cadastradas",val:empresas.length,color:"#0ea5e9",sub:`${empresas.filter(e=>e.ativo!==0).length} ativas`}].map(k=>(
                  <div key={k.label} style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderLeft:`3px solid ${k.color}`,borderRadius:6,padding:"14px 16px"}}>
                    <div style={{fontSize:11,color:COLORS.textMid,fontWeight:500,marginBottom:8}}>{k.label}</div>
                    <div style={{fontSize:22,fontWeight:900,color:k.color,lineHeight:1}}>{k.val}</div>
                    <div style={{fontSize:11,color:COLORS.textLight,marginTop:5}}>{k.sub}</div>
                  </div>
                ))}
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:18}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>Distribuição por situação</div>
                  <div style={{fontSize:11,color:COLORS.textLight,marginBottom:14}}>Quantidade e valor por status</div>
                  {(dashboard.por_status||[]).map(s=>{const max=Math.max(...(dashboard.por_status||[{total:1}]).map(x=>parseFloat(x.total||0)));const pct=max>0?Math.max(3,(parseFloat(s.total)/max)*100):3;return(<div key={s.status} style={{marginBottom:12}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:12}}><span style={{fontWeight:600,color:SCOR[s.status]||COLORS.textMid}}>{SLABEL[s.status]||s.status}</span><span style={{color:COLORS.textMid}}>{s.cnt} · {fmtR$(s.total)}</span></div><div style={{background:COLORS.bg,borderRadius:3,height:6,overflow:"hidden"}}><div style={{width:`${pct}%`,height:"100%",background:SCOR[s.status]||COLORS.primary,borderRadius:3}}/></div></div>);})}
                  {!(dashboard.por_status?.length)&&<div style={{color:COLORS.textLight,fontSize:12,textAlign:"center",padding:16}}>Nenhum dado.</div>}
                </div>
                <div style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:18}}>
                  <div style={{fontWeight:700,fontSize:13,marginBottom:2}}>Valores por mês</div>
                  <div style={{fontSize:11,color:COLORS.textLight,marginBottom:14}}>Últimos 6 meses com registros</div>
                  {(dashboard.por_mes||[]).map(m=>{const max=Math.max(...(dashboard.por_mes||[{total:1}]).map(x=>parseFloat(x.total||0)));const pct=max>0?Math.max(3,(parseFloat(m.total)/max)*100):3;const[ano,mes]=(m.mes||"").split("-");return(<div key={m.mes} style={{marginBottom:12}}><div style={{display:"flex",justifyContent:"space-between",marginBottom:4,fontSize:12}}><span style={{fontWeight:600}}>{mes}/{ano}</span><span style={{color:COLORS.textMid}}>{fmtR$(m.total)}</span></div><div style={{background:COLORS.bg,borderRadius:3,height:6,overflow:"hidden"}}><div style={{width:`${pct}%`,height:"100%",background:COLORS.primary,borderRadius:3}}/></div></div>);})}
                  {!(dashboard.por_mes?.length)&&<div style={{color:COLORS.textLight,fontSize:12,textAlign:"center",padding:16}}>Nenhum dado.</div>}
                </div>
              </div>
              <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:14}}>
                <div style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:6,overflow:"hidden"}}>
                  <div style={{padding:"10px 16px",borderBottom:`1px solid ${COLORS.border}`,fontWeight:700,fontSize:13}}>Últimos Lançamentos</div>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr style={{background:COLORS.bg}}>
                      {["Favorecido","Data","Valor","Situação"].map(h=><th key={h} style={{padding:"7px 12px",textAlign:h==="Valor"?"right":h==="Situação"?"center":"left",fontSize:10,color:COLORS.textMid,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {(dashboard.ultimos||[]).map(l=><tr key={l.id} style={{borderTop:`1px solid ${COLORS.border}`}}><td style={{padding:"8px 12px",fontSize:12,fontWeight:500}}>{l.funcionario_nome||"—"}</td><td style={{padding:"8px 12px",fontSize:12,color:COLORS.textMid}}>{fmtDt(l.data_pagamento)}</td><td style={{padding:"8px 12px",fontSize:12,textAlign:"right",fontWeight:700}}>{fmtR$(l.valor)}</td><td style={{padding:"8px 12px",textAlign:"center"}}><BadgeStatus s={l.status}/></td></tr>)}
                      {!dashboard.ultimos?.length&&<tr><td colSpan={4} style={{padding:20,textAlign:"center",color:COLORS.textLight,fontSize:12}}>Nenhum lançamento.</td></tr>}
                    </tbody>
                  </table>
                </div>
                <div style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:6,overflow:"hidden"}}>
                  <div style={{padding:"10px 16px",borderBottom:`1px solid ${COLORS.border}`,fontWeight:700,fontSize:13}}>Últimos Lotes</div>
                  <table style={{width:"100%",borderCollapse:"collapse"}}>
                    <thead><tr style={{background:COLORS.bg}}>
                      {["Nome do Lote","Itens","Total","Situação"].map(h=><th key={h} style={{padding:"7px 12px",textAlign:h==="Total"?"right":h==="Itens"||h==="Situação"?"center":"left",fontSize:10,color:COLORS.textMid,fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em"}}>{h}</th>)}
                    </tr></thead>
                    <tbody>
                      {(dashboard.ult_lotes||[]).map(l=><tr key={l.id} style={{borderTop:`1px solid ${COLORS.border}`}}><td style={{padding:"8px 12px",fontSize:12,fontWeight:600}}>{l.descricao||`Lote #${l.id}`}</td><td style={{padding:"8px 12px",fontSize:12,textAlign:"center",color:COLORS.textMid}}>{l.qtd_itens||"—"}</td><td style={{padding:"8px 12px",fontSize:12,textAlign:"right",fontWeight:700}}>{fmtR$(l.total)}</td><td style={{padding:"8px 12px",textAlign:"center"}}><BadgeStatus s={l.status} forLote/></td></tr>)}
                      {!dashboard.ult_lotes?.length&&<tr><td colSpan={4} style={{padding:20,textAlign:"center",color:COLORS.textLight,fontSize:12}}>Nenhum lote.</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ── LANÇAMENTOS ── */}
      {aba==="lancamentos"&&(
        <div style={{display:"flex",flexDirection:"column",gap:16}}>

          {/* Action bar — tudo em uma linha */}
          <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",gap:8,flexWrap:"wrap"}}>
            {/* Esquerda: 4 cards comparativos */}
            <div style={{display:"flex",gap:6,alignItems:"center",flexWrap:"wrap"}}>
              {/* Card funcionários total */}
              <div style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:5,padding:"5px 12px",display:"flex",gap:5,alignItems:"baseline"}}>
                <span style={{fontSize:16,fontWeight:900,color:COLORS.textMid}}>{lancPendentes.length}</span>
                <span style={{fontSize:10,color:COLORS.textLight,fontWeight:600,textTransform:"uppercase"}}>total func.</span>
              </div>
              {/* Card funcionários selecionados */}
              <div style={{background:sel.length>0?"#eff6ff":COLORS.surface,border:`1px solid ${sel.length>0?"#bfdbfe":COLORS.border}`,borderRadius:5,padding:"5px 12px",display:"flex",gap:5,alignItems:"baseline"}}>
                <span style={{fontSize:16,fontWeight:900,color:sel.length>0?"#1d4ed8":COLORS.textMid}}>{sel.length}</span>
                <span style={{fontSize:10,color:sel.length>0?"#3b82f6":COLORS.textLight,fontWeight:600,textTransform:"uppercase"}}>selecionados</span>
              </div>
              <span style={{color:COLORS.border,fontSize:16,fontWeight:300}}>|</span>
              {/* Card valor total */}
              {(()=>{const totalTudo=lancPendentes.reduce((s,l)=>s+parseFloat(l.valor||0),0);return(
              <div style={{background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:5,padding:"5px 12px",display:"flex",gap:5,alignItems:"baseline"}}>
                <span style={{fontSize:14,fontWeight:900,color:COLORS.textMid}}>{fmtR$(totalTudo)}</span>
                <span style={{fontSize:10,color:COLORS.textLight,fontWeight:600,textTransform:"uppercase"}}>valor total</span>
              </div>
              );})()}
              {/* Card valor selecionado */}
              <div style={{background:sel.length>0?"#eff6ff":COLORS.surface,border:`1px solid ${sel.length>0?"#bfdbfe":COLORS.border}`,borderRadius:5,padding:"5px 12px",display:"flex",gap:5,alignItems:"baseline"}}>
                <span style={{fontSize:14,fontWeight:900,color:sel.length>0?COLORS.primary:COLORS.textMid}}>{fmtR$(totalSel)}</span>
                <span style={{fontSize:10,color:sel.length>0?"#3b82f6":COLORS.textLight,fontWeight:600,textTransform:"uppercase"}}>selecionado</span>
              </div>
              {temDuplicados&&<span style={{fontSize:11,color:"#dc2626",fontWeight:700,background:"#fff5f5",border:"1px solid #fca5a5",borderRadius:5,padding:"4px 8px"}}>⚠ Duplicados</span>}
              {sel.length===0&&<span style={{fontSize:12,color:COLORS.textLight,marginLeft:4}}>Selecione funcionários para criar um lote</span>}
            </div>

            {/* Direita: empresa + ações */}
            <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
              {/* Seletor de empresa compacto */}
              <div style={{display:"flex",alignItems:"center",gap:6,background:COLORS.surface,border:`1px solid ${empresaSel?"#bfdbfe":COLORS.border}`,borderRadius:6,padding:"4px 8px 4px 6px"}}>
                {empresaSel&&empresaLogo(empresaSel)&&(
                  <img src={empresaLogo(empresaSel)} alt="" style={{height:22,width:"auto",objectFit:"contain",maxWidth:60,flexShrink:0}}/>
                )}
                {!empresaSel&&<span style={{fontSize:16,lineHeight:1}}>🏢</span>}
                <select value={empresaId} onChange={e=>setEmpresaId(e.target.value)}
                  style={{border:"none",background:"transparent",fontSize:12,color:empresaSel?"#1d4ed8":COLORS.textMid,fontWeight:empresaSel?600:400,cursor:"pointer",outline:"none",maxWidth:200,minWidth:120}}>
                  <option value="">— Empresa pagadora —</option>
                  {empresas.map(e=><option key={e.id} value={String(e.id)}>{e.nome}</option>)}
                </select>
                {empresaSel&&empresaSel.banco_nome&&(
                  <span style={{fontSize:10,color:COLORS.textMid,borderLeft:`1px solid ${COLORS.border}`,paddingLeft:6,whiteSpace:"nowrap"}}>
                    {empresaSel.banco_nome}{empresaSel.agencia?` · Ag. ${empresaSel.agencia}`:""}
                  </span>
                )}
                {!empresaSel&&<span style={{fontSize:10,color:"#f59e0b",fontWeight:600}}>⚠</span>}
              </div>

              {sel.length>0&&(
                <button onClick={excluirSelecionados}
                  style={{background:"none",border:`1px solid ${COLORS.danger}`,color:COLORS.danger,borderRadius:5,padding:"8px 14px",fontWeight:600,cursor:"pointer",fontSize:13}}>
                  🗑 Excluir ({sel.length})
                </button>
              )}
              {sel.length>0&&(
                <button onClick={()=>{if(!empresaSel){setErr("Selecione a empresa pagadora antes de criar o lote.");return;}setNomeLote("");setErr(null);setModal("criarLote");}}
                  style={{background:temDuplicados||!empresaSel?"#94a3b8":COLORS.primary,color:"#fff",border:"none",borderRadius:5,padding:"8px 16px",fontWeight:700,cursor:"pointer",fontSize:13}}>
                  Criar Lote ({sel.length})
                </button>
              )}
              <button onClick={()=>{setForm({funcionario_id:"",valor:"",data_pagamento:hoje(),competencia:mesAtual(),documento:"",observacao:""});setModalMode("manual");setImportItems([]);setImportSel([]);setErr(null);setFuncBusca("");setFuncDropOpen(false);setModal("novo");}}
                style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:5,padding:"8px 16px",fontWeight:700,cursor:"pointer",fontSize:13}}>
                + Novo Lançamento
              </button>
            </div>
          </div>

          {/* Tabela de lançamentos pendentes */}
          <div style={{border:`1px solid ${COLORS.border}`,borderRadius:6,overflow:"hidden",background:COLORS.surface}}>
            <div style={{padding:"12px 16px",borderBottom:`1px solid ${COLORS.border}`,display:"flex",justifyContent:"space-between",alignItems:"center",gap:10,flexWrap:"wrap"}}>
              <div>
                <div style={{fontWeight:700,fontSize:14}}>Lançamentos Pendentes</div>
                <div style={{fontSize:11,color:COLORS.textLight,marginTop:2}}>
                  {lancPendentes.length}{empresaFiltroId && lancPendentes.length !== lancPendentesAll.length ? ` de ${lancPendentesAll.length}` : ""} lançamento(s) aguardando inclusão em lote
                </div>
              </div>
              <div style={{display:"flex",gap:8,alignItems:"center",flexWrap:"wrap"}}>
                <button onClick={carregarLancamentos} style={{background:COLORS.bg,border:`1px solid ${COLORS.border}`,borderRadius:4,padding:"5px 10px",fontSize:12,cursor:"pointer",color:COLORS.textMid}}>Atualizar</button>
              </div>
            </div>
            {loading&&<div style={{padding:16,textAlign:"center",color:COLORS.textLight,fontSize:12}}>Carregando...</div>}
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead>
                  <tr style={{background:"#f1f5f9"}}>
                    <th style={{padding:"8px 14px",textAlign:"center",width:40,borderBottom:"1px solid #c9d4dd"}}>
                      <input type="checkbox" checked={lancPendentes.length>0&&lancPendentes.every(l=>sel.includes(l.id))} onChange={toggleTodos} style={{cursor:"pointer",width:15,height:15}} title="Selecionar todos"/>
                    </th>
                    {["Funcionário","Empresa","Competência","Data Pagamento","Valor","Status","Ações"].map(h=>(
                      <th key={h} style={{padding:"8px 12px",textAlign:h==="Valor"?"right":h==="Ações"||h==="Status"?"center":"left",fontSize:10,color:"#475569",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid #c9d4dd"}}>{h}</th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {lancPendentes.map(l=>{
                    const isDupe=dupesSel.includes(l.funcionario_id)&&sel.includes(l.id);
                    return(
                      <tr key={l.id} style={{borderTop:"1px solid #e2e8f0",background:isDupe?"#fff1f2":sel.includes(l.id)?"#eff6ff":"#fff",cursor:"pointer"}} onClick={()=>toggleSel(l.id)}>
                        <td style={{padding:"9px 14px",textAlign:"center"}} onClick={e=>e.stopPropagation()}>
                          <input type="checkbox" checked={sel.includes(l.id)} onChange={()=>toggleSel(l.id)} style={{cursor:"pointer",width:15,height:15}}/>
                        </td>
                        <td style={{padding:"9px 12px"}}>
                          <div style={{fontWeight:600,color:COLORS.text}}>{l.funcionario_nome||"—"}</div>
                          {isDupe&&<div style={{fontSize:10,color:"#ef4444",fontWeight:600}}>⚠ Duplicado</div>}
                          {(l.fc_pix_chave||l.fc_conta||l.chave_pix)&&<div style={{fontSize:11,color:COLORS.textLight}}>{l.fc_tipo&&l.fc_tipo!=="pix_chave"?`Ag. ${l.fc_agencia||"?"}/${l.fc_conta||"?"}`:l.fc_pix_chave?`PIX: ${l.fc_pix_chave}`:`PIX: ${l.chave_pix}`}</div>}
                        </td>
                        <td style={{padding:"9px 12px",fontSize:12,color:COLORS.textMid,whiteSpace:"nowrap"}}>{getLancEmpresa(l)||"—"}</td>
                        <td style={{padding:"9px 12px",fontSize:12,color:COLORS.textMid}}>{l.competencia||"—"}</td>
                        <td style={{padding:"9px 12px",fontSize:12,color:COLORS.textMid,whiteSpace:"nowrap"}}>{fmtDt(l.data_pagamento)}</td>
                        <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,fontSize:13,color:COLORS.text}}>{fmtR$(l.valor)}</td>
                        <td style={{padding:"9px 12px",textAlign:"center"}}><BadgeStatus s={l.status}/></td>
                        <td style={{padding:"9px 12px",textAlign:"center"}} onClick={e=>e.stopPropagation()}>
                          <button onClick={()=>excluirLancamento(l.id)} style={{background:"none",border:`1px solid ${COLORS.border}`,borderRadius:4,padding:"3px 8px",cursor:"pointer",fontSize:11,color:COLORS.danger}}>Excluir</button>
                        </td>
                      </tr>
                    );
                  })}
                  {!lancPendentes.length&&!loading&&(
                    <tr><td colSpan={8} style={{padding:28,textAlign:"center",color:COLORS.textLight,fontSize:13,background:"#fff"}}>
                      {empresaFiltroId ? "Nenhum lançamento para esta empresa." : 'Nenhum lançamento pendente. Clique em "+ Novo Lançamento" para adicionar.'}
                    </td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>

          {/* Histórico de lotes */}
          <div style={{border:"1px solid #c9d4dd",borderRadius:6,overflow:"hidden",background:"#fff"}}>
            <div style={{padding:"12px 16px",borderBottom:"1px solid #c9d4dd",display:"flex",justifyContent:"space-between",alignItems:"center",background:"#fff"}}>
              <div>
                <div style={{fontWeight:700,fontSize:14,color:COLORS.text}}>Histórico de Lotes</div>
                <div style={{fontSize:11,color:COLORS.textLight,marginTop:2}}>Lançamentos incluídos em lote saem da lista acima e ficam registrados aqui</div>
              </div>
              <button onClick={()=>setAba("lotes")} style={{background:"#f1f5f9",border:"1px solid #c9d4dd",borderRadius:5,padding:"6px 12px",fontSize:12,cursor:"pointer",fontWeight:500,color:COLORS.text}}>Ver lotes e remessas →</button>
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                <thead><tr style={{background:"#f1f5f9"}}>
                  {["Nome do Lote","Criado em","Funcionários","Total","Situação",""].map((h,i)=>(
                    <th key={i} style={{padding:"8px 14px",textAlign:h==="Total"?"right":h===""||h==="Funcionários"||h==="Situação"?"center":"left",fontSize:10,color:"#475569",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid #c9d4dd"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {lotes.map((l,li)=>(
                    <tr key={l.id} style={{borderTop:"1px solid #e2e8f0",background:li%2===1?"#f8fafc":"#fff"}}>
                      <td style={{padding:"9px 14px",fontWeight:600,color:COLORS.text}}>{l.descricao||`Lote #${l.id}`}</td>
                      <td style={{padding:"9px 14px",fontSize:12,color:COLORS.textMid,whiteSpace:"nowrap"}}>{fmtDt((l.criado_em||"").substring(0,10))} {(l.criado_em||"").substring(11,16)}</td>
                      <td style={{padding:"9px 14px",textAlign:"center",fontSize:12,color:COLORS.textMid}}>{l.qtd_itens}</td>
                      <td style={{padding:"9px 14px",textAlign:"right",fontWeight:700,color:COLORS.text}}>{fmtR$(l.total)}</td>
                      <td style={{padding:"9px 14px",textAlign:"center"}}><BadgeStatus s={l.status} forLote/></td>
                      <td style={{padding:"9px 14px",textAlign:"center"}}>
                        <div style={{display:"flex",gap:6,justifyContent:"center"}}>
                          <button onClick={()=>{setRenomearLoteId(l.id);setRenomearNome(l.descricao||"");setModal("renomear");}}
                            style={{background:"none",border:"1px solid #c9d4dd",borderRadius:4,padding:"3px 8px",cursor:"pointer",fontSize:11,color:COLORS.textMid}}>Renomear</button>
                          <button onClick={()=>excluirLote(l.id)}
                            style={{background:"none",border:`1px solid ${COLORS.danger}`,borderRadius:4,padding:"3px 8px",cursor:"pointer",fontSize:11,color:COLORS.danger}}>✕ Excluir</button>
                        </div>
                      </td>
                    </tr>
                  ))}
                  {!lotes.length&&<tr><td colSpan={6} style={{padding:20,textAlign:"center",color:COLORS.textLight,fontSize:12,background:"#fff"}}>Nenhum lote criado.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>

        </div>
      )}

      {/* ── LOTES E REMESSAS ── */}
      {aba==="lotes"&&(
        <div style={{display:"grid",gridTemplateColumns:"300px 1fr",gap:20}}>
          <div>
            <div style={{fontWeight:700,fontSize:13,marginBottom:10}}>Lotes</div>
            <div style={{display:"flex",flexDirection:"column",gap:8}}>
              {lotes.map(l=>{
                const cor=SLOTE_COR[l.status]||COLORS.textMid;
                return(
                  <div key={l.id} onClick={()=>setLoteAtivo(l.id)} style={{background:loteAtivo===l.id?`${COLORS.primary}12`:COLORS.surface,border:`1px solid ${loteAtivo===l.id?COLORS.primary:COLORS.border}`,borderLeft:`3px solid ${cor}`,borderRadius:8,padding:"11px 14px",cursor:"pointer",transition:"all .15s"}}>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:4}}>
                      <div style={{fontWeight:700,fontSize:13}}>{l.descricao||`Lote #${l.id}`}</div>
                      <div style={{display:"flex",alignItems:"center",gap:6}}>
                        <BadgeStatus s={l.status} forLote/>
                        <button onClick={e=>{e.stopPropagation();excluirLote(l.id);}}
                          style={{background:"none",border:`1px solid ${COLORS.danger}`,borderRadius:4,padding:"2px 6px",cursor:"pointer",fontSize:10,color:COLORS.danger,lineHeight:1}}>✕</button>
                      </div>
                    </div>
                    <div style={{display:"flex",gap:10,fontSize:12,color:COLORS.textMid}}>
                      <span>{l.qtd_itens} pag.</span>
                      <span style={{fontWeight:700,color:COLORS.text}}>{fmtR$(l.total)}</span>
                    </div>
                    <div style={{fontSize:11,color:COLORS.textLight,marginTop:2}}>{fmtDt(l.criado_em?.substring?.(0,10)||l.criado_em)}</div>
                  </div>
                );
              })}
              {!lotes.length&&<div style={{textAlign:"center",padding:24,color:COLORS.textLight,fontSize:12}}>Nenhum lote criado.</div>}
            </div>
          </div>
          <div>
            {!loteAtivo?(
              <div style={{textAlign:"center",padding:40,color:COLORS.textLight}}>Selecione um lote à esquerda.</div>
            ):(()=>{
              const loteInfo=lotes.find(l=>l.id===loteAtivo);
              const isPendente=["aberto","pendente"].includes(loteInfo?.status);
              return(
                <>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <div>
                      <div style={{fontWeight:800,fontSize:16}}>{loteInfo?.descricao||`Lote #${loteInfo?.id}`}</div>
                      <div style={{fontSize:13,color:COLORS.textMid,marginTop:2}}>{loteInfo?.qtd_itens} pagamento(s) · Total: {fmtR$(loteInfo?.total)}</div>
                      {loteInfo&&<div style={{marginTop:6}}><BadgeStatus s={loteInfo.status} forLote/></div>}
                    </div>
                    <div style={{display:"flex",gap:8}}>
                      {(isPendente||loteInfo?.status==="gerado")&&(
                        <button onClick={()=>gerarRemessa(loteAtivo)} style={{background:isPendente?"#6366f1":COLORS.bg,color:isPendente?"#fff":COLORS.textMid,border:`1px solid ${isPendente?"#6366f1":COLORS.border}`,borderRadius:6,padding:"8px 16px",fontWeight:700,cursor:"pointer",fontSize:13}}>
                          {isPendente?"↓ Gerar .REM":"↓ Baixar novamente"}
                        </button>
                      )}
                    </div>
                  </div>
                  <div style={{overflowX:"auto",border:"1px solid #c9d4dd",borderRadius:6,marginBottom:16,background:"#fff",overflow:"hidden"}}>
                    <table style={{width:"100%",borderCollapse:"collapse",fontSize:13}}>
                      <thead><tr style={{background:"#f1f5f9"}}>
                        {["Funcionário","Chave PIX / Conta","Data Pag.","Valor","Status"].map(h=>(
                          <th key={h} style={{padding:"8px 12px",textAlign:h==="Valor"?"right":h==="Status"?"center":"left",fontWeight:700,color:"#475569",fontSize:10,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid #c9d4dd"}}>{h}</th>
                        ))}
                      </tr></thead>
                      <tbody>
                        {lancamentosLote.map((l,li)=>(
                          <tr key={l.id} style={{borderTop:"1px solid #e2e8f0",background:li%2===1?"#f8fafc":"#fff"}}>
                            <td style={{padding:"9px 12px",color:COLORS.text}}><div style={{fontWeight:600}}>{l.funcionario_nome||"—"}</div></td>
                            <td style={{padding:"9px 12px",fontSize:12,color:COLORS.textMid}}>
                              {(()=>{
                                const emp = employees.find(e=>e.id===l.funcionario_id);
                                const pg  = emp?.pagamento||{};
                                const fcTipo     = pg.fcTipo     || l.fc_tipo     || "";
                                const fcPixChave = pg.fcPixChave || l.fc_pix_chave|| "";
                                const fcPixTipo  = pg.fcPixTipo  || l.fc_pix_tipo || "";
                                const fcAgencia  = pg.fcAgencia  || l.fc_agencia  || "";
                                const fcConta    = pg.fcConta    || l.fc_conta    || "";
                                if (fcTipo && fcTipo!=="pix_chave") return `Ag/Cc: ${fcAgencia||"?"}/${fcConta||"?"}`;
                                if (fcPixChave) return `PIX(${fcPixTipo||""}): ${fcPixChave}`;
                                if (l.chave_pix) return `PIX(${l.tipo_pix||""}): ${l.chave_pix}`;
                                return <span style={{color:COLORS.danger}}>Sem dados</span>;
                              })()}
                            </td>
                            <td style={{padding:"9px 12px",fontSize:12,color:COLORS.textMid,whiteSpace:"nowrap"}}>{fmtDt(l.data_pagamento)}</td>
                            <td style={{padding:"9px 12px",textAlign:"right",fontWeight:700,color:COLORS.text}}>{fmtR$(l.valor)}</td>
                            <td style={{padding:"9px 12px",textAlign:"center"}}><BadgeStatus s={l.status}/></td>
                          </tr>
                        ))}
                        {!lancamentosLote.length&&<tr><td colSpan={5} style={{padding:24,textAlign:"center",color:COLORS.textLight,background:"#fff"}}>Nenhum pagamento neste lote.</td></tr>}
                      </tbody>
                    </table>
                  </div>
                  {remessas.filter(r=>r.lote_id===loteAtivo).length>0&&(
                    <div style={{border:`1px solid #c9d4dd`,borderRadius:6,overflow:"hidden",background:"#fff"}}>
                      <div style={{padding:"10px 14px",borderBottom:"1px solid #c9d4dd",background:"#fff"}}>
                        <div style={{fontWeight:700,fontSize:13,color:COLORS.text}}>Remessas / Retornos</div>
                        <div style={{fontSize:11,color:COLORS.textLight,marginTop:2}}>Arquivos gerados e status de conciliação</div>
                      </div>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead><tr style={{background:"#f1f5f9"}}>
                          {["Arquivo .REM","Retorno .RET","Gerado em","Situação"].map((h,i)=>(
                            <th key={i} style={{padding:"8px 14px",textAlign:h==="Situação"?"center":"left",fontSize:10,color:"#475569",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid #c9d4dd"}}>{h}</th>
                          ))}
                        </tr></thead>
                        <tbody>
                          {remessas.filter(r=>r.lote_id===loteAtivo).map((r,ri)=>(
                            <tr key={r.id} style={{borderTop:"1px solid #e2e8f0",background:ri%2===1?"#f8fafc":"#fff"}}>
                              <td style={{padding:"9px 14px",fontWeight:600,color:COLORS.text}}>{r.arquivo_nome||"—"}</td>
                              <td style={{padding:"9px 14px",color:r.retorno_nome?COLORS.text:COLORS.textLight}}>{r.retorno_nome||<span style={{fontStyle:"italic",fontSize:11}}>Pendente</span>}</td>
                              <td style={{padding:"9px 14px",color:COLORS.textMid,whiteSpace:"nowrap"}}>{fmtDt(r.criado_em?.substring?.(0,10)||r.criado_em)}</td>
                              <td style={{padding:"9px 14px",textAlign:"center"}}><BadgeStatus s={r.status} forLote/></td>
                            </tr>
                          ))}
                        </tbody>
                      </table>
                    </div>
                  )}
                </>
              );
            })()}
          </div>
        </div>
      )}

      {/* ── CONCILIAÇÃO ── */}
      {aba==="conciliacao"&&(
        <div style={{display:"grid",gridTemplateColumns:"minmax(300px,480px) 1fr",gap:20,alignItems:"start"}}>
          {/* Coluna esquerda: formulário + resultado */}
          <div style={{display:"flex",flexDirection:"column",gap:16}}>
            <Card style={{padding:24}}>
              <div style={{fontWeight:800,fontSize:15,marginBottom:4}}>Importar Arquivo de Retorno (.RET)</div>
              <div style={{fontSize:12,color:COLORS.textLight,marginBottom:20}}>Após o banco processar o pagamento, importe o arquivo de retorno CNAB 240 para conciliar e confirmar os pagamentos. O lote ficará com status Pago.</div>
              <div style={{marginBottom:14}}>
                <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Remessa relacionada <span style={{color:COLORS.danger}}>*</span></label>
                <select value={concilRemessaId} onChange={e=>setConcilRemessaId(e.target.value)} style={{width:"100%",padding:"8px 10px",borderRadius:5,border:`1px solid ${!concilRemessaId?COLORS.danger:COLORS.border}`,background:COLORS.surface,fontSize:13}}>
                  <option value="">Selecione a remessa...</option>
                  {remessas.filter(r=>r.status!=="conciliado").map(r=><option key={r.id} value={r.id}>{r.arquivo_nome} — {r.lote_descricao||`Lote #${r.lote_id}`} ({fmtDt(r.criado_em?.substring?.(0,10)||r.criado_em)})</option>)}
                </select>
              </div>
              <div style={{marginBottom:20}}>
                <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Arquivo de Retorno (.RET)</label>
                <div style={{border:`2px dashed ${COLORS.border}`,borderRadius:8,padding:24,textAlign:"center",background:COLORS.bg,cursor:"pointer"}}
                  onDragOver={e=>e.preventDefault()} onDrop={e=>{e.preventDefault();const f=e.dataTransfer.files[0];if(f)setConcilFile(f);}}>
                  <input type="file" accept=".RET,.ret,.txt" style={{display:"none"}} id="ret-upload" onChange={e=>setConcilFile(e.target.files[0])}/>
                  <label htmlFor="ret-upload" style={{cursor:"pointer"}}>
                    <div style={{fontSize:28}}>📂</div>
                    <div style={{fontSize:13,color:COLORS.textMid,marginTop:4}}>{concilFile?<span style={{color:COLORS.primary,fontWeight:700}}>{concilFile.name}</span>:"Clique ou arraste o arquivo .RET aqui"}</div>
                  </label>
                </div>
              </div>
              <button onClick={enviarConciliacao} disabled={!concilFile||loading} style={{width:"100%",background:COLORS.primary,color:"#fff",border:"none",borderRadius:5,padding:"11px 0",fontWeight:700,fontSize:14,cursor:!concilFile||loading?"not-allowed":"pointer",opacity:!concilFile||loading?0.6:1}}>
                {loading?"Processando...":"Processar Conciliação"}
              </button>
            </Card>

            {/* Resultado da conciliação */}
            {concilResultado && (() => {
              const _aprov = concilResultado.aprovado || (concilResultado.erros||[]).length === 0;
              return (
              <Card style={{padding:20,border:`2px solid ${_aprov?"#16a34a":COLORS.danger}`}}>
                {_aprov ? (
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:12}}>
                      <div style={{width:32,height:32,borderRadius:"50%",background:"#dcfce7",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke="#16a34a" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round"><polyline points="4 10 8 14 16 6"/></svg>
                      </div>
                      <div>
                        <div style={{fontWeight:800,fontSize:14,color:"#15803d"}}>Conciliação aprovada</div>
                        <div style={{fontSize:12,color:COLORS.textMid}}>{concilResultado.quitados} pagamento(s) confirmado(s) — status alterado para <strong>Pago</strong></div>
                      </div>
                    </div>
                  </div>
                ) : (
                  <div>
                    <div style={{display:"flex",alignItems:"center",gap:10,marginBottom:14}}>
                      <div style={{width:32,height:32,borderRadius:"50%",background:"#fee2e2",display:"flex",alignItems:"center",justifyContent:"center",flexShrink:0}}>
                        <svg width="16" height="16" viewBox="0 0 20 20" fill="none" stroke={COLORS.danger} strokeWidth="2.5" strokeLinecap="round"><line x1="5" y1="5" x2="15" y2="15"/><line x1="15" y1="5" x2="5" y2="15"/></svg>
                      </div>
                      <div>
                        <div style={{fontWeight:800,fontSize:14,color:COLORS.danger}}>Conciliação com divergências</div>
                        <div style={{fontSize:12,color:COLORS.textMid}}>{(concilResultado.erros||[]).length} erro(s) encontrado(s) — status <strong>não alterado</strong></div>
                      </div>
                    </div>
                    <div style={{display:"flex",flexDirection:"column",gap:8}}>
                      {(concilResultado.erros||[]).map((e,i) => {
                        const cor = e.tipo==="AUSENTE" ? "#b45309" : e.tipo==="VALOR_DIVERGENTE" ? "#7c3aed" : COLORS.danger;
                        const bg  = e.tipo==="AUSENTE" ? "#fef9c3" : e.tipo==="VALOR_DIVERGENTE" ? "#ede9fe" : "#fee2e2";
                        const tag = e.tipo==="AUSENTE" ? "AUSENTE" : e.tipo==="VALOR_DIVERGENTE" ? "VALOR DIVERGENTE" : e.tipo==="INESPERADO" ? "INESPERADO" : "RECUSADO";
                        return (
                          <div key={i} style={{background:bg,border:`1px solid ${cor}30`,borderRadius:7,padding:"10px 14px",display:"flex",gap:10,alignItems:"flex-start"}}>
                            <span style={{fontSize:10,fontWeight:800,color:cor,background:cor+"20",padding:"2px 7px",borderRadius:8,whiteSpace:"nowrap",marginTop:1,flexShrink:0}}>{tag}</span>
                            <div style={{fontSize:12,color:"#1e293b",lineHeight:1.5}}>
                              <div style={{fontWeight:600}}>{e.nome || e.doc}</div>
                              <div style={{color:cor}}>{e.mensagem}</div>
                              {e.tipo==="VALOR_DIVERGENTE" && (
                                <div style={{fontSize:11,color:COLORS.textMid,marginTop:2}}>
                                  Esperado: R$ {parseFloat(e.valorEsperado||0).toFixed(2).replace(".",",")} · Retorno: R$ {parseFloat(e.valorRetorno||0).toFixed(2).replace(".",",")}
                                </div>
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                    {concilResultado.quitados > 0 && (
                      <div style={{marginTop:12,fontSize:12,color:COLORS.textMid,borderTop:`1px solid ${COLORS.border}`,paddingTop:10}}>
                        {concilResultado.quitados} pagamento(s) confirmado(s) no arquivo, mas o lote não foi marcado como pago enquanto houver divergências.
                      </div>
                    )}
                  </div>
                )}
              </Card>
              );
            })()}
          </div>

          {/* Coluna direita: histórico */}
          <div style={{border:"1px solid #c9d4dd",borderRadius:6,overflow:"hidden",background:"#fff"}}>
            <div style={{padding:"12px 16px",borderBottom:"1px solid #c9d4dd",background:"#fff"}}>
              <div style={{fontWeight:700,fontSize:14,color:COLORS.text}}>Histórico de Remessas / Retornos</div>
              <div style={{fontSize:11,color:COLORS.textLight,marginTop:2}}>Todos os arquivos .REM gerados e seus retornos</div>
            </div>
            <div style={{overflowX:"auto"}}>
              <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                <thead><tr style={{background:"#f1f5f9"}}>
                  {["Arquivo .REM","Retorno .RET","Lote","Total","Gerado em","Situação"].map((h,i)=>(
                    <th key={i} style={{padding:"8px 12px",textAlign:h==="Total"?"right":h==="Situação"?"center":"left",fontSize:10,color:"#475569",fontWeight:700,textTransform:"uppercase",letterSpacing:"0.06em",borderBottom:"1px solid #c9d4dd"}}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {remessas.map((r,ri)=>(
                    <tr key={r.id} style={{borderTop:"1px solid #e2e8f0",background:ri%2===1?"#f8fafc":"#fff"}}>
                      <td style={{padding:"9px 12px",fontWeight:600,fontSize:12,color:COLORS.text}}>{r.arquivo_nome||"—"}</td>
                      <td style={{padding:"9px 12px",fontSize:12,color:r.retorno_nome?COLORS.text:COLORS.textLight}}>{r.retorno_nome||<span style={{fontStyle:"italic"}}>Pendente</span>}</td>
                      <td style={{padding:"9px 12px",fontSize:12,color:COLORS.textMid}}>{r.lote_descricao||`Lote #${r.lote_id}`}</td>
                      <td style={{padding:"9px 12px",fontWeight:700,textAlign:"right",fontSize:12}}>{fmtR$(r.lote_total)}</td>
                      <td style={{padding:"9px 12px",fontSize:12,color:COLORS.textMid,whiteSpace:"nowrap"}}>{fmtDt(r.criado_em?.substring?.(0,10)||r.criado_em)}</td>
                      <td style={{padding:"9px 12px",textAlign:"center"}}><BadgeStatus s={r.status} forLote/></td>
                    </tr>
                  ))}
                  {!remessas.length&&<tr><td colSpan={6} style={{padding:28,textAlign:"center",color:COLORS.textLight,fontSize:13,background:"#fff"}}>Nenhuma remessa gerada.</td></tr>}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: NOVO LANÇAMENTO ── */}
      {modal==="novo"&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:COLORS.surface,borderRadius:12,padding:28,width:560,maxWidth:"95vw",maxHeight:"90vh",overflowY:"auto"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
              <div style={{fontWeight:800,fontSize:16}}>Novo Lançamento</div>
              <button onClick={()=>{setModal(null);setFuncBusca("");setFuncDropOpen(false);}} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:COLORS.textMid}}>×</button>
            </div>
            {/* Mode tabs */}
            <div style={{display:"flex",borderBottom:`1px solid ${COLORS.border}`,marginBottom:20,gap:2}}>
              {[["manual","Manual"],["folha","Folha de Pagamento"],["adiantamento","Adiantamento de Salário"],["auxilio","Auxílio"]].map(([id,label])=>(
                <button key={id} onClick={()=>{setModalMode(id);setImportItems([]);setImportSel([]);setImportErr(null);setImportBuscou(false);}}
                  style={{background:"none",border:"none",borderBottom:`2px solid ${modalMode===id?"#6366f1":"transparent"}`,padding:"8px 14px",fontSize:13,fontWeight:modalMode===id?700:400,color:modalMode===id?"#6366f1":COLORS.textMid,cursor:"pointer",marginBottom:-1}}>
                  {label}
                </button>
              ))}
            </div>
            {err&&<div style={{background:"#fee2e2",borderRadius:6,padding:"8px 12px",marginBottom:14,fontSize:13,color:"#dc2626"}}>{err}</div>}

            {/* Manual */}
            {modalMode==="manual"&&(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div>
                  <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Funcionário *</label>
                  <div style={{position:"relative"}}>
                    <input
                      value={funcBusca}
                      onChange={e=>{setFuncBusca(e.target.value);setFuncDropOpen(true);}}
                      onFocus={()=>setFuncDropOpen(true)}
                      onBlur={()=>setTimeout(()=>setFuncDropOpen(false),150)}
                      placeholder="Digite para buscar..."
                      style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${form.funcionario_id?COLORS.primary:COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box"}}
                    />
                    {funcDropOpen&&(()=>{
                      const busca=funcBusca.toLowerCase();
                      const filtrados=funcAtivos.filter(f=>f.nome.toLowerCase().includes(busca)).slice(0,20);
                      if(!filtrados.length) return null;
                      return (
                        <div style={{position:"absolute",top:"100%",left:0,right:0,background:COLORS.surface,border:`1px solid ${COLORS.border}`,borderRadius:6,zIndex:200,maxHeight:220,overflowY:"auto",boxShadow:"0 4px 16px rgba(0,0,0,0.12)",marginTop:2}}>
                          {filtrados.map(f=>{
                            const pg=f.pagamento||{};
                            const tem=pg.fcPixChave||pg.fcConta;
                            return (
                              <div key={f.id}
                                onMouseDown={()=>{
                                  setForm(fv=>({...fv,funcionario_id:String(f.id)}));
                                  setFuncBusca(f.nome);
                                  setFuncDropOpen(false);
                                  setFuncContaPadrao(null);
                                  apiFetch("GET",`/api/funcionarios/${f.id}/contas`,undefined,token)
                                    .then(contas=>{const p=(Array.isArray(contas)?contas:[]).find(c=>c.padrao===1)||contas[0]||null;setFuncContaPadrao(p);})
                                    .catch(()=>{});
                                }}
                                style={{padding:"9px 12px",cursor:"pointer",fontSize:13,display:"flex",justifyContent:"space-between",alignItems:"center",borderBottom:`1px solid ${COLORS.border}`}}
                                onMouseEnter={e=>e.currentTarget.style.background=COLORS.bg}
                                onMouseLeave={e=>e.currentTarget.style.background="transparent"}>
                                <span>{f.nome}</span>
                                {!tem&&<span style={{fontSize:10,background:"#fee2e2",color:"#dc2626",borderRadius:3,padding:"1px 5px",fontWeight:600}}>SEM PIX</span>}
                              </div>
                            );
                          })}
                        </div>
                      );
                    })()}
                  </div>
                  {form.funcionario_id&&(()=>{
                    let info=null;
                    if(funcContaPadrao===null) info=<span style={{color:COLORS.textLight}}>Verificando conta bancária...</span>;
                    else if(!funcContaPadrao) info=<span style={{color:COLORS.danger}}>⚠ Sem dados bancários cadastrados</span>;
                    else if(funcContaPadrao.tipo==="pix_chave") info=<span style={{color:"#10b981"}}>PIX ({funcContaPadrao.pix_tipo||"chave"}): {funcContaPadrao.pix_chave}</span>;
                    else if(funcContaPadrao.conta) info=<span style={{color:"#6366f1"}}>{funcContaPadrao.banco_nome?funcContaPadrao.banco_nome+" — ":""} Ag. {funcContaPadrao.agencia||"?"}/{funcContaPadrao.conta}{funcContaPadrao.conta_dv?`-${funcContaPadrao.conta_dv}`:""}</span>;
                    else info=<span style={{color:COLORS.danger}}>⚠ Sem dados bancários válidos</span>;
                    return <div style={{marginTop:6,fontSize:12,background:COLORS.bg,borderRadius:6,padding:"7px 10px"}}>{info}</div>;
                  })()}
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div>
                    <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Valor *</label>
                    <input type="number" step="0.01" min="0" value={form.valor} onChange={e=>setForm(f=>({...f,valor:e.target.value}))} style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box"}} placeholder="0,00"/>
                  </div>
                  <div>
                    <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Data de Pagamento *</label>
                    <input type="date" value={form.data_pagamento} onChange={e=>setForm(f=>({...f,data_pagamento:e.target.value}))} style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                </div>
                <div style={{display:"grid",gridTemplateColumns:"1fr 1fr",gap:12}}>
                  <div>
                    <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Competência</label>
                    <input type="month" value={form.competencia} onChange={e=>setForm(f=>({...f,competencia:e.target.value}))} style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box"}}/>
                  </div>
                  <div>
                    <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Nº Documento</label>
                    <input value={form.documento} onChange={e=>setForm(f=>({...f,documento:e.target.value}))} style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box"}} placeholder="Ex: SAL202501"/>
                  </div>
                </div>
                <div>
                  <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Observação</label>
                  <input value={form.observacao} onChange={e=>setForm(f=>({...f,observacao:e.target.value}))} style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13,boxSizing:"border-box"}}/>
                </div>
                <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                  <button onClick={()=>setModal(null)} style={{background:COLORS.bg,border:`1px solid ${COLORS.border}`,borderRadius:5,padding:"9px 18px",cursor:"pointer",fontSize:13}}>Cancelar</button>
                  <button onClick={salvarLancamento} disabled={loading} style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:5,padding:"9px 22px",fontWeight:700,cursor:"pointer",fontSize:13,opacity:loading?0.6:1}}>{loading?"Salvando...":"Salvar Lançamento"}</button>
                </div>
              </div>
            )}

            {/* Folha / Adiantamento */}
            {(modalMode==="folha"||modalMode==="adiantamento"||modalMode==="auxilio")&&(
              <div style={{display:"flex",flexDirection:"column",gap:14}}>
                <div style={{display:"flex",gap:10,alignItems:"flex-end"}}>
                  <div style={{flex:1}}>
                    <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Mês *</label>
                    <select value={importMes} onChange={e=>setImportMes(e.target.value)} style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13}}>
                      {Array.from({length:12},(_,i)=>i+1).map(m=><option key={m} value={String(m).padStart(2,"0")}>{String(m).padStart(2,"0")} - {["Jan","Fev","Mar","Abr","Mai","Jun","Jul","Ago","Set","Out","Nov","Dez"][m-1]}</option>)}
                    </select>
                  </div>
                  <div style={{flex:1}}>
                    <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Ano *</label>
                    <select value={importAno} onChange={e=>setImportAno(e.target.value)} style={{width:"100%",padding:"9px 10px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:13}}>
                      {[2023,2024,2025,2026].map(a=><option key={a} value={String(a)}>{a}</option>)}
                    </select>
                  </div>
                  <button onClick={carregarImport} disabled={importLoading} style={{background:"#6366f1",color:"#fff",border:"none",borderRadius:5,padding:"9px 16px",fontWeight:700,cursor:"pointer",fontSize:13,whiteSpace:"nowrap",opacity:importLoading?0.6:1}}>
                    {importLoading?"Buscando...":"Buscar"}
                  </button>
                </div>
                <div style={{fontSize:12,color:COLORS.textLight}}>
                  {modalMode==="folha"?"Serão listados os funcionários ativos com o salário base cadastrado.":modalMode==="auxilio"?"Serão listados os funcionários ativos com valor fixo de R$ 200,00 (Auxílio Cesta Básica).":"Serão listados os adiantamentos de salário lançados para o mês selecionado."}
                </div>
                {importItems.length>0&&(()=>{
                  const empImport = [...new Set(importItems.map(x=>x.empresa_nome).filter(Boolean))].sort().map(n=>({id:n,nome:n}));
                  const itensVisiveis = filtroEmpresaImport ? importItems.map((item,i)=>({item,i})).filter(({item})=>(item.empresa_nome||"")===filtroEmpresaImport) : importItems.map((item,i)=>({item,i}));
                  return (
                  <>
                    <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",fontSize:12,gap:8,flexWrap:"wrap"}}>
                      <span>{itensVisiveis.length}{filtroEmpresaImport?` de ${importItems.length}`:""} registro(s)</span>
                      <div style={{display:"flex",gap:8,alignItems:"center"}}>
                        {empImport.length > 0 && (
                          <select value={filtroEmpresaImport} onChange={e=>setFiltroEmpresaImport(e.target.value)}
                            style={{padding:"3px 8px",borderRadius:4,border:`1px solid ${filtroEmpresaImport?COLORS.primary:COLORS.border}`,background:COLORS.surface,fontSize:11,color:filtroEmpresaImport?COLORS.primary:COLORS.text,fontWeight:filtroEmpresaImport?700:400,cursor:"pointer"}}>
                            <option value="">Todas as empresas</option>
                            {empImport.map(e=><option key={e.id} value={e.id}>{e.nome}</option>)}
                          </select>
                        )}
                        <button onClick={()=>setImportSel(itensVisiveis.filter(({item})=>!!(item.fc_pix_chave||item.fc_conta)).map(({i})=>i))} style={{background:"none",border:`1px solid ${COLORS.border}`,borderRadius:4,padding:"3px 8px",fontSize:11,cursor:"pointer"}}>Todos</button>
                        <button onClick={()=>setImportSel(prev=>prev.filter(i=>!itensVisiveis.some(x=>x.i===i)))} style={{background:"none",border:`1px solid ${COLORS.border}`,borderRadius:4,padding:"3px 8px",fontSize:11,cursor:"pointer"}}>Nenhum</button>
                      </div>
                    </div>
                    <div style={{maxHeight:260,overflowY:"auto",border:`1px solid ${COLORS.border}`,borderRadius:6}}>
                      <table style={{width:"100%",borderCollapse:"collapse",fontSize:12}}>
                        <thead><tr style={{background:COLORS.bg,position:"sticky",top:0}}>
                          <th style={{padding:"7px 10px",width:32}}></th>
                          <th style={{padding:"7px 10px",textAlign:"left",fontWeight:600,color:COLORS.textMid}}>Funcionário</th>
                          {empImport.length > 0 && <th style={{padding:"7px 10px",textAlign:"left",fontWeight:600,color:COLORS.textMid}}>Empresa</th>}
                          <th style={{padding:"7px 10px",textAlign:"right",fontWeight:600,color:COLORS.textMid}}>Valor</th>
                          <th style={{padding:"7px 10px",textAlign:"left",fontWeight:600,color:COLORS.textMid}}>PIX / Conta</th>
                        </tr></thead>
                        <tbody>
                          {itensVisiveis.map(({item,i})=>{
                            const temPix = !!(item.fc_pix_chave||item.fc_conta);
                            const selecionado = importSel.includes(i);
                            return (
                            <tr key={i}
                              style={{borderTop:`1px solid ${COLORS.border}`,
                                background:!temPix?"#fff5f5":selecionado?"#eff6ff":"transparent",
                                opacity:temPix?1:0.7,
                                cursor:temPix?"pointer":"not-allowed"}}
                              onClick={()=>temPix&&setImportSel(prev=>prev.includes(i)?prev.filter(x=>x!==i):[...prev,i])}>
                              <td style={{padding:"7px 10px",textAlign:"center"}}>
                                <input type="checkbox" checked={selecionado} disabled={!temPix} onChange={()=>{}} style={{cursor:temPix?"pointer":"not-allowed"}}/>
                              </td>
                              <td style={{padding:"7px 10px",fontWeight:500,color:!temPix?"#dc2626":undefined}}>
                                {item.funcionario_nome||"—"}
                                {!temPix&&<span style={{marginLeft:6,fontSize:10,background:"#fee2e2",color:"#dc2626",borderRadius:3,padding:"1px 5px",fontWeight:600}}>SEM PIX</span>}
                              </td>
                              {empImport.length > 0 && <td style={{padding:"7px 10px",fontSize:11,color:COLORS.textMid,whiteSpace:"nowrap"}}>{item.empresa_nome||"—"}</td>}
                              <td style={{padding:"7px 10px",textAlign:"right",fontWeight:700,color:!temPix?"#dc2626":undefined}}>{fmtR$(item.valor)}</td>
                              <td style={{padding:"7px 10px"}}>{item.fc_pix_chave?<span style={{color:"#10b981"}}>PIX: {item.fc_pix_chave}</span>:item.fc_conta?<span style={{color:"#6366f1"}}>Cc: {item.fc_agencia||"?"}/{item.fc_conta}</span>:<span style={{color:"#dc2626",fontWeight:600}}>⚠ Sem dados bancários</span>}</td>
                            </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                    <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                      <button onClick={()=>setModal(null)} style={{background:COLORS.bg,border:`1px solid ${COLORS.border}`,borderRadius:5,padding:"9px 18px",cursor:"pointer",fontSize:13}}>Cancelar</button>
                      <button onClick={confirmarImport} disabled={loading||!importSel.length} style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:5,padding:"9px 22px",fontWeight:700,cursor:"pointer",fontSize:13,opacity:loading||!importSel.length?0.5:1}}>
                        {loading?"Importando...":`Importar Selecionados (${importSel.length})`}
                      </button>
                    </div>
                  </>
                  );
                })()}
                {importErr&&(
                  <div style={{background:"#fee2e2",borderRadius:6,padding:"10px 12px",fontSize:13,color:"#dc2626"}}>
                    ⚠ {importErr}
                  </div>
                )}
                {!importErr&&importItems.length===0&&!importLoading&&(
                  <div style={{textAlign:"center",padding:24,color:importBuscou?"#dc2626":COLORS.textLight,fontSize:13}}>
                    {importBuscou
                      ? (modalMode==="adiantamento"?"Nenhum adiantamento encontrado para este mês.":modalMode==="auxilio"?"Nenhum funcionário ativo encontrado.":"Nenhum funcionário ativo encontrado.")
                      : "Selecione mês e ano e clique em Buscar."}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── MODAL: CRIAR LOTE ── */}
      {modal==="criarLote"&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:COLORS.surface,borderRadius:12,padding:28,width:480,maxWidth:"95vw"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:20}}>
              <div style={{fontWeight:800,fontSize:16}}>Criar Lote de Pagamento</div>
              <button onClick={()=>setModal(null)} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:COLORS.textMid}}>×</button>
            </div>
            {err&&<div style={{background:"#fee2e2",borderRadius:6,padding:"8px 12px",marginBottom:14,fontSize:13,color:"#dc2626"}}>{err}</div>}
            <div style={{display:"flex",flexDirection:"column",gap:16}}>
              <div>
                <label style={{fontSize:12,fontWeight:700,color:COLORS.textMid,display:"block",marginBottom:4}}>Nome do Lote <span style={{color:COLORS.danger}}>*</span></label>
                <input value={nomeLote} onChange={e=>setNomeLote(e.target.value)} placeholder="Ex: Folha Jan/2026, Adiantamento Fev/2026..."
                  style={{width:"100%",padding:"10px 12px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:14,boxSizing:"border-box",fontWeight:500}} autoFocus/>
              </div>
              <div style={{background:COLORS.bg,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:14}}>
                <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>Empresa pagadora</div>
                {empresaSel?<div style={{fontSize:13,color:"#2563eb",fontWeight:500}}>{empresaSel.nome}{empresaSel.cnpj?` · ${empresaSel.cnpj}`:""}</div>:<div style={{color:COLORS.danger,fontSize:12}}>⚠ Nenhuma empresa selecionada. Feche e selecione a empresa.</div>}
              </div>
              <div style={{background:COLORS.bg,border:`1px solid ${COLORS.border}`,borderRadius:6,padding:14}}>
                <div style={{fontWeight:700,fontSize:13,marginBottom:8}}>Resumo</div>
                <div style={{display:"flex",gap:16,fontSize:13}}><span><strong>{sel.length}</strong> funcionário(s)</span><span>Total: <strong style={{color:COLORS.primary}}>{fmtR$(totalSel)}</strong></span></div>
                {temDuplicados&&<div style={{marginTop:8,background:"#fee2e2",borderRadius:5,padding:"7px 10px",fontSize:12,color:"#dc2626"}}>⚠ Há funcionários duplicados. Feche e desmarque os duplicados.</div>}
              </div>
              <div style={{display:"flex",gap:10,justifyContent:"flex-end"}}>
                <button onClick={()=>{setModal(null);setNomeLote("");}} style={{background:COLORS.bg,border:`1px solid ${COLORS.border}`,borderRadius:5,padding:"10px 20px",cursor:"pointer",fontSize:13}}>Cancelar</button>
                <button onClick={criarLote} disabled={loading||!nomeLote.trim()||!empresaSel||temDuplicados}
                  style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:5,padding:"10px 24px",fontWeight:700,cursor:"pointer",fontSize:13,opacity:loading||!nomeLote.trim()||!empresaSel||temDuplicados?0.5:1}}>
                  {loading?"Criando...":"Criar Lote"}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* ── MODAL: RENOMEAR LOTE ── */}
      {modal==="renomear"&&(
        <div style={{position:"fixed",inset:0,background:"rgba(0,0,0,0.5)",display:"flex",alignItems:"center",justifyContent:"center",zIndex:1000}}>
          <div style={{background:COLORS.surface,borderRadius:12,padding:28,width:420,maxWidth:"95vw"}}>
            <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:18}}>
              <div style={{fontWeight:800,fontSize:15}}>Renomear Lote</div>
              <button onClick={()=>{setModal(null);setRenomearLoteId(null);setRenomearNome("");}} style={{background:"none",border:"none",fontSize:22,cursor:"pointer",color:COLORS.textMid}}>×</button>
            </div>
            {err&&<div style={{background:"#fee2e2",borderRadius:6,padding:"8px 12px",marginBottom:14,fontSize:13,color:"#dc2626"}}>{err}</div>}
            <div>
              <label style={{fontSize:12,fontWeight:600,color:COLORS.textMid,display:"block",marginBottom:4}}>Novo nome <span style={{color:COLORS.danger}}>*</span></label>
              <input value={renomearNome} onChange={e=>setRenomearNome(e.target.value)} onKeyDown={e=>e.key==="Enter"&&renomearLote()}
                style={{width:"100%",padding:"10px 12px",borderRadius:5,border:`1px solid ${COLORS.border}`,background:COLORS.surface,fontSize:14,boxSizing:"border-box"}} autoFocus placeholder="Nome do lote..."/>
            </div>
            <div style={{display:"flex",gap:10,justifyContent:"flex-end",marginTop:18}}>
              <button onClick={()=>{setModal(null);setRenomearLoteId(null);setRenomearNome("");}} style={{background:COLORS.bg,border:`1px solid ${COLORS.border}`,borderRadius:5,padding:"9px 18px",cursor:"pointer",fontSize:13}}>Cancelar</button>
              <button onClick={renomearLote} disabled={!renomearNome.trim()} style={{background:COLORS.primary,color:"#fff",border:"none",borderRadius:5,padding:"9px 22px",fontWeight:700,cursor:"pointer",fontSize:13,opacity:!renomearNome.trim()?0.5:1}}>Salvar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

const MENU_SECTIONS = [
  {
    id: "prop-cont-sec", label: "Propostas e Contratos", iconKey: "contratos", color: "#3b82f6", perfis: ["administrador"],
    items: [
      { id: "pc-propostas", label: "Propostas", iconKey: "propostas", perfis: ["administrador"] },
      { id: "pc-contratos", label: "Contratos", iconKey: "contratos", perfis: ["administrador"] },
      { id: "pc-clientes",  label: "Clientes",  iconKey: "clientes",  perfis: ["administrador"] },
    ],
  },
  {
    id: "financeiro-sec", label: "Financeiro", iconKey: "financeiro", color: "#10b981", perfis: ["administrador"],
    items: [
      { id: "financeiro", label: "Financeiro", iconKey: "financeiro", perfis: ["administrador"] },
      { id: "cobrancas",  label: "Cobranças",  iconKey: "cobrancas",  perfis: ["administrador"] },
    ],
  },
  {
    id: "transferencias-sec", label: "Transferências", iconKey: "transferencias", color: "#6366f1", perfis: ["administrador"],
    items: [
      { id: "pix-em-lotes", label: "Pix em Lotes", iconKey: "transferencias", perfis: ["administrador"],
        children: [
          { id: "pix-lotes-dashboard",   label: "Dashboard",        iconKey: "dashboard",       perfis: ["administrador"] },
          { id: "pix-lotes-lancamentos", label: "Lançamentos",      iconKey: "transferencias",  perfis: ["administrador"] },
          { id: "pix-lotes-remessas",    label: "Lotes e Remessas", iconKey: "transferencias",  perfis: ["administrador"] },
          { id: "pix-lotes-conciliacao", label: "Conciliação",      iconKey: "transferencias",  perfis: ["administrador"] },
        ],
      },
    ],
  },
  {
    id: "rh-sec", label: "RH", iconKey: "funcionarios", color: "#f59e0b", perfis: ["administrador","gestor","rh"],
    items: [
      { id: "funcionarios",            label: "Funcionários",         iconKey: "funcionarios", perfis: ["administrador","gestor","rh"],
        children: [
          { id: "func-efetivos",     label: "Efetivos",     iconKey: "funcionarios", perfis: ["administrador","gestor","rh"] },
          { id: "func-pre-cadastro", label: "Pré Cadastro", iconKey: "funcionarios", perfis: ["administrador","gestor","rh"] },
        ],
      },
      { id: "beneficios-vt",           label: "Vale Transporte",      iconKey: "beneficios",   perfis: ["administrador","gestor","rh"],
        children: [
          { id: "vt-bhbus",     label: "BHBus",      iconKey: "beneficios", perfis: ["administrador","gestor","rh"] },
          { id: "vt-otimo",    label: "Ótimo",      iconKey: "beneficios", perfis: ["administrador","gestor","rh"] },
          { id: "vt-gasolina", label: "Gasolina",   iconKey: "beneficios", perfis: ["administrador","gestor","rh"] },
          { id: "vt-betimcard",label: "Betim Card", iconKey: "beneficios", perfis: ["administrador","gestor","rh"] },
          { id: "vt-outros",   label: "Outros",     iconKey: "beneficios", perfis: ["administrador","gestor","rh"] },
        ],
      },
      { id: "beneficios-va",            label: "Vale Alimentação",     iconKey: "beneficios",   perfis: ["administrador","gestor","rh"],
        children: [
          { id: "beneficios-va-normal", label: "VA",   iconKey: "beneficios", perfis: ["administrador","gestor","rh"] },
          { id: "beneficios-va-flex",   label: "Auxílio", iconKey: "beneficios", perfis: ["administrador","gestor","rh"] },
        ],
      },
      { id: "financeiro-adiantamento", label: "Folha de Pagamento",   iconKey: "pagamento",    perfis: ["administrador","gestor","rh"],
        children: [
          { id: "financeiro-relatorio",   label: "Relatório Financeiro",    iconKey: "pagamento", perfis: ["administrador","gestor","rh"] },
          { id: "financeiro-adiantamento-sal", label: "Adiantamento de Salário", iconKey: "pagamento", perfis: ["administrador","gestor","rh"] },
          { id: "comprovantes",           label: "Comprovantes",            iconKey: "pagamento", perfis: ["administrador","gestor","rh"] },
        ],
      },
      { id: "escala",                  label: "Escalas e Turnos",     iconKey: "escala",       perfis: ["administrador","gestor","rh"] },
      { id: "setores",                 label: "Grupos e Setores", iconKey: "setores",      perfis: ["administrador","gestor","rh"] },
      { id: "ponto-terceiro",          label: "Ponto",                iconKey: "terceiro",     perfis: ["administrador","gestor","rh"],
        children: [
          { id: "ponto-faltas",       label: "Faltas",       iconKey: "terceiro", perfis: ["administrador","gestor","rh"] },
          { id: "ponto-afastamentos", label: "Afastamentos", iconKey: "ferias",   perfis: ["administrador","gestor","rh"] },
          { id: "ponto-ferias",       label: "Férias",       iconKey: "ferias",   perfis: ["administrador","gestor","rh"] },
          { id: "ponto-historico",    label: "Histórico",    iconKey: "graficos", perfis: ["administrador","gestor","rh"] },
        ],
      },
      { id: "rh-itens-func",           label: "EPIs/Uniformes",       iconKey: "estoque",      perfis: ["administrador","gestor","rh"] },
    ],
  },
  {
    id: "suprimentos-sec", label: "Suprimentos", iconKey: "suprimentos", color: "#0891b2", perfis: ["administrador"],
    items: [
      { id: "suprimentos-fornecedores", label: "Fornecedores",  iconKey: "fornecedores", perfis: ["administrador"] },
      { id: "suprimentos-estoque",      label: "Estoque",       iconKey: "estoque",      perfis: ["administrador"] },
      { id: "suprimentos-itens-func",   label: "EPIs/Uniformes por Funcionário", iconKey: "funcionarios", perfis: ["administrador"] },
    ],
  },
];

const MENUS = [
  { id: "dashboard", label: "Dashboard",             iconKey: "dashboard", perfis: RH_PERFIS },
  { id: "graficos",  label: "Gráficos & Relatórios", iconKey: "graficos",  perfis: RH_PERFIS },
];

// ─── FÉRIAS & BENEFÍCIOS (Férias/Afastamentos + Benefícios) ──────────────────
function FeriasBeneficios({ employees, setEmployees, afastamentos, setAfastamentos, token, usuario, abaInicial }) {
  const [aba, setAba] = useState(abaInicial || "ferias");
  const ABAS = [
    { id: "ferias",    label: "🏖 Férias & Afastamentos" },
    { id: "beneficios",label: "🎁 Benefícios (VT/VA)" },
  ];
  return (
    <div style={{ display:"flex", flexDirection:"column", gap:0 }}>
      <div style={{ display:"flex", gap:0, background:COLORS.surface, borderRadius:"12px 12px 0 0", border:`1px solid ${COLORS.border}`, borderBottom:"none", overflow:"hidden" }}>
        {ABAS.map(a => (
          <button key={a.id} onClick={() => setAba(a.id)} style={{
            flex:1, padding:"14px 20px", border:"none", borderBottom: aba===a.id ? `3px solid ${COLORS.primary}` : "3px solid transparent",
            background: aba===a.id ? COLORS.primaryBg : "transparent",
            cursor:"pointer", fontWeight: aba===a.id ? 800 : 400,
            color: aba===a.id ? COLORS.primary : COLORS.textMid, fontSize:14,
            transition:"all .15s",
          }}>{a.label}</button>
        ))}
      </div>
      <div style={{ background:COLORS.surface, border:`1px solid ${COLORS.border}`, borderTop:"none", borderRadius:"0 0 12px 12px", padding:24 }}>
        {aba === "ferias"     && <FeriasAfastamentos employees={employees} setEmployees={setEmployees} afastamentos={afastamentos} setAfastamentos={setAfastamentos} token={token} usuario={usuario} />}
        {aba === "beneficios" && <Beneficios employees={employees} token={token} usuario={usuario} />}
      </div>
    </div>
  );
}

// ─── FINANCEIRO & ADIANTAMENTO (legado — mantido para compatibilidade) ─────────
function FinanceiroAdiantamento({ employees, ponto, token }) {
  return <Financeiro employees={employees} ponto={ponto} token={token} />;
}

// ─── TELA DE BACKUPS ──────────────────────────────────────────────────────────
function BackupScreen({ token, usuario }) {
  const [lista, setLista]           = useState([]);
  const [maxSlots, setMaxSlots]     = useState(5);
  const [tenants, setTenants]       = useState([]);
  const [tenantSel, setTenantSel]   = useState('');
  const [gerando, setGerando]       = useState(false);
  const [gerandoManual, setGerandoManual] = useState(false);
  const [excluindo, setExcluindo]   = useState(null);
  const [carregando, setCarregando] = useState(true);

  const headers = { Authorization: `Bearer ${token}` };
  const isAdmin = usuario?.perfil === 'admin';

  async function carregar() {
    setCarregando(true);
    try {
      const [rLista, rTenants] = await Promise.all([
        fetch('/api/backup/lista', { headers }).then(r => r.json()),
        fetch('/api/backup/tenants', { headers }).then(r => r.json()),
      ]);
      setLista(rLista.arquivos || []);
      setMaxSlots(rLista.max || 5);
      const ts = Array.isArray(rTenants) ? rTenants : [];
      setTenants(ts);
      if (!tenantSel && ts.length > 0) setTenantSel(String(ts[0].id));
    } catch(e) { alert('Erro ao carregar backups: ' + e.message); }
    finally { setCarregando(false); }
  }

  useEffect(() => { carregar(); }, []);

  async function gerarAuto() {
    setGerando(true);
    try {
      const r = await fetch('/api/backup/gerar', { method: 'POST', headers });
      const d = await r.json();
      if (!d.ok) throw new Error(d.erro);
      await carregar();
    } catch(e) { alert('Erro ao gerar backup: ' + e.message); }
    finally { setGerando(false); }
  }

  async function gerarManual() {
    if (!tenantSel) return;
    const tenant = tenants.find(t => String(t.id) === tenantSel);
    setGerandoManual(true);
    try {
      const r = await fetch('/api/backup/manual', {
        method: 'POST',
        headers: { ...headers, 'Content-Type': 'application/json' },
        body: JSON.stringify({ tenant_id: tenantSel, nome_empresa: tenant?.nome }),
      });
      const d = await r.json();
      if (!d.ok) throw new Error(d.erro);
      await carregar();
    } catch(e) { alert('Erro ao gerar backup: ' + e.message); }
    finally { setGerandoManual(false); }
  }

  async function excluir(nome) {
    if (!window.confirm(`Excluir permanentemente "${nome}"?`)) return;
    setExcluindo(nome);
    try {
      const r = await fetch(`/api/backup/${encodeURIComponent(nome)}`, { method: 'DELETE', headers });
      const d = await r.json();
      if (!d.ok) throw new Error(d.erro);
      setLista(prev => prev.filter(b => b.nome !== nome));
    } catch(e) { alert('Erro ao excluir: ' + e.message); }
    finally { setExcluindo(null); }
  }

  function baixar(nome) {
    const a = document.createElement('a');
    a.href = `/api/backup/download/${encodeURIComponent(nome)}?token=${token}`;
    a.download = nome;
    a.click();
  }

  function fmtBytes(b) {
    if (!b) return '—';
    if (b < 1024 * 1024) return (b / 1024).toFixed(1) + ' KB';
    return (b / 1024 / 1024).toFixed(1) + ' MB';
  }

  function fmtData(d) { return new Date(d).toLocaleString('pt-BR'); }

  function proximoBackupAuto() {
    const now = new Date();
    let d = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 3, 0, 0);
    if (d.getDate() % 2 === 0 && d > now) return d;
    d.setDate(d.getDate() + 1);
    while (d.getDate() % 2 !== 0) d.setDate(d.getDate() + 1);
    d.setHours(3, 0, 0, 0);
    return d;
  }

  if (!isAdmin) return (
    <div style={{ padding: 40, textAlign: 'center', color: '#94a3b8' }}>Acesso restrito ao administrador.</div>
  );

  const slots    = lista.length;
  const slotPct  = Math.min(100, Math.round((slots / maxSlots) * 100));
  const proximo  = proximoBackupAuto();
  const proxStr  = proximo.toLocaleDateString('pt-BR', { day: '2-digit', month: '2-digit', year: 'numeric' }) + ' às 03:00';

  return (
    <div style={{ maxWidth: 780 }}>
      <div style={{ fontSize: 22, fontWeight: 700, color: '#1e3a5f', marginBottom: 2 }}>💾 Backups</div>
      <div style={{ fontSize: 13, color: '#64748b', marginBottom: 20 }}>
        Próximo backup automático: <strong>{proxStr}</strong>
      </div>

      {/* Barra de slots */}
      <Card style={{ marginBottom: 16 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
          <div style={{ fontSize: 13, fontWeight: 600, color: '#1e3a5f' }}>Slots utilizados</div>
          <div style={{ fontSize: 13, fontWeight: 700, color: slots >= maxSlots ? '#ef4444' : '#475569' }}>
            {slots} de {maxSlots}
          </div>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {Array.from({ length: maxSlots }, (_, i) => (
            <div key={i} style={{
              flex: 1, height: 14, borderRadius: 4,
              background: i < slots
                ? (slots >= maxSlots ? '#ef4444' : slots >= maxSlots - 1 ? '#f59e0b' : '#22c55e')
                : '#e5e7eb',
              transition: 'background .3s',
            }} />
          ))}
        </div>
        <div style={{ fontSize: 11, color: '#94a3b8', marginTop: 6 }}>
          Ao atingir {maxSlots} backups, o mais antigo é excluído automaticamente ao gerar um novo.
        </div>
      </Card>

      {/* Ações */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 20 }}>
        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 6 }}>🌐 Backup Automático</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 14 }}>
            Inclui <strong>todas as empresas</strong> separadas por pasta. SQL + documentos. Compressão máxima.
          </div>
          <Btn onClick={gerarAuto} disabled={gerando} style={{ width: '100%' }}>
            {gerando ? '⏳ Gerando...' : '+ Gerar agora (todas as empresas)'}
          </Btn>
        </Card>

        <Card>
          <div style={{ fontSize: 13, fontWeight: 700, color: '#1e3a5f', marginBottom: 6 }}>🏢 Backup Manual</div>
          <div style={{ fontSize: 12, color: '#64748b', marginBottom: 8 }}>Selecione a empresa:</div>
          <select
            value={tenantSel}
            onChange={e => setTenantSel(e.target.value)}
            style={{ width: '100%', padding: '7px 10px', borderRadius: 6, border: '1px solid #d1d5db', fontSize: 13, marginBottom: 10, color: '#1e293b' }}
          >
            {tenants.map(t => <option key={t.id} value={String(t.id)}>{t.nome}</option>)}
          </select>
          <Btn onClick={gerarManual} disabled={gerandoManual || !tenantSel} style={{ width: '100%' }}>
            {gerandoManual ? '⏳ Gerando...' : '⬇ Gerar backup desta empresa'}
          </Btn>
        </Card>
      </div>

      {/* Lista de backups */}
      <Card style={{ padding: 0, overflow: 'hidden' }}>
        <div style={{ padding: '14px 16px', borderBottom: '1px solid #e5e7eb', fontWeight: 700, fontSize: 14, color: '#1e3a5f' }}>
          Backups salvos
        </div>
        {carregando ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>Carregando...</div>
        ) : lista.length === 0 ? (
          <div style={{ textAlign: 'center', padding: 32, color: '#94a3b8' }}>
            Nenhum backup salvo ainda.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: 13 }}>
            <thead>
              <tr style={{ background: '#f1f5f9' }}>
                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>Arquivo</th>
                <th style={{ padding: '10px 16px', textAlign: 'left', color: '#475569', fontWeight: 600 }}>Data</th>
                <th style={{ padding: '10px 16px', textAlign: 'right', color: '#475569', fontWeight: 600 }}>Tamanho</th>
                <th style={{ padding: '10px 16px', textAlign: 'center', color: '#475569', fontWeight: 600, width: 120 }}>Ações</th>
              </tr>
            </thead>
            <tbody>
              {lista.map((b, i) => (
                <tr key={b.nome} style={{ background: i % 2 === 0 ? '#fff' : '#f8fafc', borderBottom: '1px solid #e5e7eb' }}>
                  <td style={{ padding: '10px 16px', fontFamily: 'monospace', fontSize: 12, color: '#1e3a5f', fontWeight: 600 }}>{b.nome}</td>
                  <td style={{ padding: '10px 16px', color: '#475569' }}>{fmtData(b.data)}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'right', color: '#475569' }}>{fmtBytes(b.tamanho)}</td>
                  <td style={{ padding: '10px 16px', textAlign: 'center' }}>
                    <div style={{ display: 'flex', gap: 6, justifyContent: 'center' }}>
                      <Btn size="sm" variant="ghost" onClick={() => baixar(b.nome)} title="Baixar">⬇</Btn>
                      <Btn size="sm" variant="ghost"
                        disabled={excluindo === b.nome}
                        onClick={() => excluir(b.nome)}
                        title="Excluir"
                        style={{ color: '#ef4444', borderColor: '#fca5a5' }}>
                        {excluindo === b.nome ? '...' : '🗑'}
                      </Btn>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </Card>
    </div>
  );
}

// ─── BOTÃO BACKUP ─────────────────────────────────────────────────────────────
function BotaoBackup({ token, onClose }) {
  const [baixando, setBaixando] = useState(false);

  async function fazerBackup() {
    setBaixando(true);
    try {
      const res = await fetch('/api/backup', {
        headers: { Authorization: `Bearer ${token}` },
      });
      if (!res.ok) throw new Error('Erro ao gerar backup');
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement('a');
      const nome = res.headers.get('Content-Disposition')?.match(/filename="(.+)"/)?.[1]
                   || `backup-rh-${new Date().toISOString().slice(0,10)}.zip`;
      a.href     = url;
      a.download = nome;
      a.click();
      URL.revokeObjectURL(url);
      onClose?.();
    } catch (e) {
      alert('Falha ao gerar backup: ' + e.message);
    } finally {
      setBaixando(false);
    }
  }

  return (
    <button onClick={fazerBackup} disabled={baixando} style={{
      width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start",
      padding: "12px 16px", background: "transparent",
      border: "none", borderBottom: `1px solid #e5e7eb`, cursor: baixando ? "wait" : "pointer",
      textAlign: "left", opacity: baixando ? 0.6 : 1,
    }}>
      <span style={{ fontWeight: 600, fontSize: 14, color: "#1e3a5f" }}>
        {baixando ? "⏳ Gerando backup..." : "💾 Backup dos dados"}
      </span>
      <span style={{ fontSize: 11, color: "#64748b", marginTop: 2 }}>
        Baixar banco de dados + arquivos em .zip
      </span>
    </button>
  );
}

export default function App() {
  const isAdminUser = (u) => u?.perfil === "admin";
  const [aba, setAba] = useState(() => {
    try {
      const saved = localStorage.getItem("rh_aba");
      if (saved) return saved;
      const u = JSON.parse(localStorage.getItem("rh_usuario") || "null");
      return isAdminUser(u) ? "clientes" : u?.perfil === "gestor" ? "func-efetivos" : "dashboard";
    } catch { return "dashboard"; }
  });
  const navTo = (a) => { try { localStorage.setItem("rh_aba", a); } catch {} setAba(a); };
  const [token, setToken] = useState(() => localStorage.getItem("rh_token") || "");
  const [usuario, setUsuario] = useState(() => { try { return JSON.parse(localStorage.getItem("rh_usuario") || "null"); } catch { return null; } });
  const [carregando, setCarregando] = useState(true);
  const [employees, setEmployees] = useState([]);
  const [ponto, setPonto] = useState([]);
  const [afastamentos, setAfastamentos] = useState([]);
  const [escala, setEscala] = useState([]);
  const [setores, setSetores] = useState([]);
  const [empresas, setEmpresas] = useState([]);
  const [observacoes, setObservacoes] = useState([]);
  const [pontoAba, setPontoAba] = useState("mensal");
  const [configOpen, setConfigOpen] = useState(false);
  const [configAba, setConfigAba] = useState("usuarios");
  const [confirmReset, setConfirmReset] = useState(false);
  const [resetando, setResetando] = useState(false);
  const [isMobile, setIsMobile] = useState(() => window.innerWidth < 768);
  const [menuOpen, setMenuOpen] = useState(() => window.innerWidth >= 768);
  const [openSections, setOpenSections] = useState([]);
  const toggleSection = (id) => setOpenSections(prev => prev.includes(id) ? prev.filter(s=>s!==id) : [...prev, id]);
  const [openSubItems, setOpenSubItems] = useState([]);
  const toggleSubItem = (id) => setOpenSubItems(prev => prev.includes(id) ? prev.filter(s=>s!==id) : [...prev, id]);
  const [darkMode, setDarkMode] = useState(() => localStorage.getItem("rh_dark") === "1");
  const [appVer, setAppVer] = useState("");
  const [empresaFiltroId, setEmpresaFiltroId] = useState("");
  const [empDrop, setEmpDrop] = useState(false);
  const empDropRef = useRef(null);

  useEffect(() => {
    if (!empDrop) return;
    const close = (e) => { if (empDropRef.current && !empDropRef.current.contains(e.target)) setEmpDrop(false); };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, [empDrop]);

  useEffect(() => {
    const onResize = () => {
      const mobile = window.innerWidth < 768;
      setIsMobile(mobile);
      setMenuOpen(!mobile);
    };
    window.addEventListener("resize", onResize);
    return () => window.removeEventListener("resize", onResize);
  }, []);
  const [tenantNome, setTenantNome] = useState("");
  const [tenantDominio, setTenantDominio] = useState("facilitae.com.br");

  const toggleDark = () => {
    const next = !darkMode;
    Object.assign(COLORS, next ? COLORS_DARK : COLORS_LIGHT);
    document.body.style.background = next ? COLORS_DARK.bg : COLORS_LIGHT.bg;
    localStorage.setItem("rh_dark", next ? "1" : "0");
    setDarkMode(next);
  };

  const handleLogin = (tok, user) => {
    localStorage.setItem("rh_token", tok);
    localStorage.setItem("rh_usuario", JSON.stringify(user));
    setToken(tok);
    setUsuario(user);
    if (user?.perfil === "visualizador") setAba("ponto");
    else if (user?.perfil === "gestor") setAba("func-efetivos");
    else if (user?.perfil === "admin") setAba("clientes");
  };

  const handleLogout = (expirado) => {
    localStorage.removeItem("rh_token");
    localStorage.removeItem("rh_usuario");
    localStorage.removeItem("rh_aba");
    setToken(""); setUsuario(null);
    setEmployees([]); setAfastamentos([]); setEscala([]); setSetores([]); setEmpresas([]); setObservacoes([]);
    if (expirado) alert("⏱️ Sua sessão expirou. Faça login novamente.");
  };

  useEffect(() => {
    const onExpirado = () => handleLogout(true);
    window.addEventListener("rh:sessao-expirada", onExpirado);
    return () => window.removeEventListener("rh:sessao-expirada", onExpirado);
  }, []);

  useEffect(() => {
    let ver = null;
    const check = () => {
      fetch("/version.json?_=" + Date.now())
        .then(r => r.json())
        .then(d => {
          if (d.version) setAppVer(d.version);
          if (ver === null) { ver = d.v; return; }
          if (d.v !== ver) window.location.reload();
        })
        .catch(() => {});
    };
    check();
    const id = setInterval(check, 60000);
    return () => clearInterval(id);
  }, []);

  useEffect(() => {
    if (!token) { setCarregando(false); return; }
    apiFetch("GET", "/api/auth/tenant", undefined, token).then(d => { setTenantNome(d.nome || ""); setTenantDominio(d.dominio_login || "facilitae.com.br"); }).catch(() => {});
    const carregar = async () => {
      setCarregando(true);
      try {
        const [funcs, emps, sets, subsets, escs, fer, obs] = await Promise.all([
          apiFetch("GET", "/api/funcionarios", undefined, token),
          apiFetch("GET", "/api/empresas", undefined, token),
          apiFetch("GET", "/api/setores", undefined, token),
          apiFetch("GET", "/api/setores/subsetores", undefined, token),
          apiFetch("GET", "/api/escalas", undefined, token),
          apiFetch("GET", "/api/ferias", undefined, token),
          apiFetch("GET", "/api/observacoes", undefined, token),
        ]);
        setEmployees(funcs.map(adaptFuncionario));
        setEmpresas(emps);
        setSetores(sets.map(s => ({ ...s, subsetores: subsets.filter(sb => sb.setor_id === s.id) })));
        setEscala(escs.map(adaptEscala));
        setAfastamentos(fer.map(adaptAfastamento));
        setObservacoes(obs.map(adaptObservacao));
      } catch(e) {
        if (e.message.includes("401") || e.message.includes("403")) handleLogout();
      } finally {
        setCarregando(false);
      }
    };
    carregar();
  }, [token]);

  if (!token) return <LoginScreen onLogin={handleLogin} />;

  if (carregando) return (
    <div style={{ minHeight: "100vh", display: "flex", alignItems: "center", justifyContent: "center", background: COLORS.bg, fontFamily: "'Inter', system-ui, sans-serif" }}>
      <div style={{ textAlign: "center", color: COLORS.textMid }}>
        <div style={{ fontSize: 18, fontWeight: 600, marginBottom: 8, color: COLORS.text }}>Carregando...</div>
        <div style={{ fontSize: 13 }}>Conectando ao servidor</div>
      </div>
    </div>
  );

  // ── Layout exclusivo do Admin (painel super-admin: Clientes + Usuários + Backups) ─
  if (isAdminUser(usuario)) {
    const ADMIN_MENUS = [
      { id: "clientes",  label: "Clientes",  icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><rect x="2" y="7" width="20" height="14" rx="2"/><path d="M16 21V5a2 2 0 0 0-2-2h-4a2 2 0 0 0-2 2v16"/></svg> },
      { id: "usuarios",  label: "Usuários",  icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2"/><circle cx="9" cy="7" r="4"/><path d="M23 21v-2a4 4 0 0 0-3-3.87"/><path d="M16 3.13a4 4 0 0 1 0 7.75"/></svg> },
      { id: "backups",   label: "Backups",   icon: <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"/><polyline points="17 8 12 3 7 8"/><line x1="12" y1="3" x2="12" y2="15"/></svg> },
    ];
    return (
      <div style={{ display: "flex", minHeight: "100vh", background: "transparent", fontFamily: "'Inter', system-ui, sans-serif", color: COLORS.text }}>
        <aside style={{ width: 220, flexShrink: 0, background: COLORS.sidebar, backdropFilter: "blur(20px)", WebkitBackdropFilter: "blur(20px)", display: "flex", flexDirection: "column", position: "fixed", top: 10, left: 8, bottom: 10, zIndex: 100, borderRadius: 18, boxShadow: "0 8px 40px rgba(0,0,0,0.40)" }}>
          <div style={{ padding: "20px 18px", borderBottom: `1px solid ${COLORS.border}` }}>
            <img src="/olavio.png" alt="Olavio" style={{ height: 56, objectFit: "contain", maxWidth: 160 }} />
          </div>
          <div style={{ padding: "20px 18px 8px", fontSize: 10, fontWeight: 700, color: COLORS.textLight, textTransform: "uppercase", letterSpacing: "0.1em" }}>Administração</div>
          <nav style={{ flex: 1, padding: "0 10px", display: "flex", flexDirection: "column", gap: 2 }}>
            {ADMIN_MENUS.map(m => {
              const active = aba === m.id;
              return (
                <button key={m.id} onClick={() => setAba(m.id)} style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "10px 12px", background: active ? "linear-gradient(135deg,#1f5500,#2e7a00)" : "transparent", border: "none", borderRadius: 10, cursor: "pointer", color: active ? "#fff" : COLORS.textMid, fontSize: 13, fontWeight: active ? 600 : 400, textAlign: "left", transition: "background .15s,color .15s", boxShadow: active ? "0 4px 14px rgba(46,122,0,0.35)" : "none" }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(46,122,0,0.09)"; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                  <span style={{ flexShrink: 0, display: "flex" }}>{m.icon}</span>
                  <span>{m.label}</span>
                </button>
              );
            })}
          </nav>
          <div style={{ padding: "14px", borderTop: `1px solid ${COLORS.border}` }}>
            <div style={{ display: "flex", alignItems: "center", gap: 10, background: "rgba(46,122,0,0.07)", borderRadius: 10, padding: "10px 12px", marginBottom: 8 }}>
              <div style={{ width: 30, height: 30, borderRadius: 8, background: "linear-gradient(135deg,#1f5500,#2e7a00)", display: "flex", alignItems: "center", justifyContent: "center", flexShrink: 0 }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div style={{ flex: 1, minWidth: 0 }}>
                <div style={{ fontSize: 12, fontWeight: 700, color: COLORS.text, whiteSpace: "nowrap", overflow: "hidden", textOverflow: "ellipsis" }}>{usuario?.nome}</div>
                <div style={{ fontSize: 10, color: COLORS.textLight }}>Admin</div>
              </div>
            </div>
            <button onClick={() => { toggleDark(); }} title={darkMode ? "Modo claro" : "Modo noturno"} style={{ width: "100%", padding: "7px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", color: COLORS.textMid, fontSize: 12, marginBottom: 6, display: "flex", alignItems: "center", justifyContent: "center", gap: 6 }}>
              {darkMode ? "☀️ Modo claro" : "🌙 Modo noturno"}
            </button>
            <button onClick={handleLogout} style={{ width: "100%", padding: "8px 12px", background: "transparent", border: `1px solid ${COLORS.border}`, borderRadius: 8, cursor: "pointer", color: COLORS.textMid, fontSize: 12, fontWeight: 500, transition: "all .15s" }}
              onMouseEnter={e => { e.currentTarget.style.color = "#EF4444"; e.currentTarget.style.borderColor = "rgba(239,68,68,0.3)"; }}
              onMouseLeave={e => { e.currentTarget.style.color = COLORS.textMid; e.currentTarget.style.borderColor = COLORS.border; }}>
              Sair do sistema
            </button>
          </div>
        </aside>
        <main style={{ flex: 1, marginLeft: 220, padding: 32, overflowY: "auto", background: "transparent" }}>
          <div style={{ marginBottom: 28, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div>
              <h1 style={{ fontSize: 22, fontWeight: 700, color: COLORS.text, margin: 0, letterSpacing: "-0.04em" }}>
                {aba === "clientes" ? "Clientes" : aba === "backups" ? "Backups" : "Usuários"}
              </h1>
              <p style={{ fontSize: 13, color: COLORS.textMid, margin: "4px 0 0" }}>
                {aba === "clientes" ? "Gerencie os clientes e tenants do sistema" : aba === "backups" ? "Backups automáticos e manuais do sistema" : "Gerencie os acessos ao sistema"}
              </p>
            </div>
            <div style={{ fontSize: 12, color: COLORS.textLight }}>
              {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
            </div>
          </div>
          {aba === "clientes" && <Clientes token={token} usuario={usuario} />}
          {aba === "usuarios" && <Usuarios token={token} usuario={usuario} />}
          {aba === "backups"  && <BackupScreen token={token} usuario={usuario} />}
        </main>
      </div>
    );
  }

  const isClasse = usuario?.tenant_nome?.toLowerCase().includes('classe');
  const sidebarLogoSrc = isClasse ? (darkMode ? "/classe-bc.png" : "/classe-pt.png") : (darkMode ? "/olavio-pt.png" : "/olavio-bc.png");
  const topbarLogoSrc  = darkMode ? "/olavio-pt.png" : "/olavio-bc.png";
  const empresaSel = empresaFiltroId ? (empresas.find(e => String(e.id) === empresaFiltroId) || null) : null;
  const employeesFiltrados = empresaFiltroId
    ? employees.filter(e => {
        if (e.empresa_id != null) return String(e.empresa_id) === empresaFiltroId;
        if (empresaSel) return (e.empresa || "").toLowerCase().trim() === (empresaSel.nome || "").toLowerCase().trim();
        return false;
      })
    : employees;
  const sideW = isMobile || menuOpen ? 240 : 64;
  const sideVisible = !isMobile || menuOpen;
  const ge = (() => {
    const flat = MENUS.find(m => m.id === aba);
    if (flat) return flat;
    for (const sec of MENU_SECTIONS) {
      for (const item of sec.items || []) {
        if (item.id === aba) return item;
        for (const child of item.children || []) {
          if (child.id === aba) return child;
        }
      }
    }
    return null;
  })();

  return (
    <div style={{ display: "flex", minHeight: "100vh", background: "transparent", fontFamily: "'Inter', system-ui, sans-serif", color: COLORS.text, letterSpacing: "-0.01em" }}>

      {/* Overlay mobile */}
      {isMobile && menuOpen && (
        <div onClick={() => setMenuOpen(false)} style={{ position: "fixed", inset: 0, background: "rgba(0,0,0,0.5)", zIndex: 99 }} />
      )}

      {/* ── Sidebar ── */}
      <aside style={{
        width: sideW, flexShrink: 0, background: COLORS.sidebar,
        display: "flex", flexDirection: "column",
        position: "fixed", top: 12, left: 12, bottom: 12, zIndex: 100,
        borderRadius: 18,
        boxShadow: "0 0 0 1px rgba(0,0,0,0.06), 0 8px 32px rgba(0,0,0,0.10)",
        transform: sideVisible ? "translateX(0)" : "translateX(-110%)",
        transition: "transform .22s cubic-bezier(.4,0,.2,1), width .22s cubic-bezier(.4,0,.2,1)",
        overflow: "hidden",
      }}>
        {/* Logo */}
        <div style={{ padding: menuOpen ? "12px 16px" : "12px 8px", display: "flex", alignItems: "center", justifyContent: "center", borderBottom: `1px solid ${COLORS.border}`, width: "100%", boxSizing: "border-box", minHeight: menuOpen ? 104 : 60 }}>
          {empresaSel?.logo_base64 ? (
            <img src={empresaSel.logo_base64} alt={empresaSel.nome} style={{ height: menuOpen ? 80 : 36, objectFit: "contain", maxWidth: menuOpen ? 210 : 44 }} />
          ) : !empresaFiltroId && empresas.filter(e => e.logo_base64).length > 1 ? (
            <div style={{ display: "flex", alignItems: "center", gap: 6, overflow: "hidden" }}>
              {empresas.filter(e => e.logo_base64).slice(0, 3).map((em, i) => (
                <div key={em.id} style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 1, minWidth: 0 }}>
                  {i > 0 && <div style={{ width: 1, alignSelf: "stretch", background: COLORS.border, flexShrink: 0 }} />}
                  <img src={em.logo_base64} alt={em.nome} style={{ height: menuOpen ? 52 : 24, objectFit: "contain", maxWidth: menuOpen ? 90 : 28, flexShrink: 1, minWidth: 0 }} />
                </div>
              ))}
            </div>
          ) : (
            <img src={sidebarLogoSrc} alt="" style={{ height: menuOpen ? 80 : 36, objectFit: "contain", maxWidth: menuOpen ? 210 : 44 }} />
          )}
        </div>

        {/* Nav */}
        <nav style={{ flex: 1, padding: "10px 8px", display: "flex", flexDirection: "column", gap: 1, overflowY: "auto" }}>
          {(() => {
            const canManage = ["administrador","admin"].includes(usuario?.perfil);

            const renderNavBtn = (m, extra) => {
              const active = aba === m.id;
              return (
                <button key={m.id} onClick={() => { setAba(m.id); if (isMobile) setMenuOpen(false); }}
                  title={menuOpen ? undefined : m.label}
                  style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: menuOpen ? "12px 13px" : "12px 0", justifyContent: menuOpen ? "flex-start" : "center",
                    background: active ? COLORS.primary : "transparent",
                    border: "none", borderRadius: 10, cursor: "pointer",
                    color: active ? "#fff" : COLORS.textMid,
                    fontSize: 13, fontWeight: active ? 600 : 400, textAlign: "left",
                    transition: "background .15s, color .15s, box-shadow .15s",
                    boxShadow: active ? "0 2px 8px rgba(46,122,0,0.28)" : "none",
                    ...extra }}
                  onMouseEnter={e => { if (!active) e.currentTarget.style.background = "rgba(46,122,0,0.07)"; }}
                  onMouseLeave={e => { if (!active) e.currentTarget.style.background = "transparent"; }}>
                  <span style={{ flexShrink: 0, display: "flex" }}><Icon d={ICONS[m.iconKey]} size={17} stroke={active ? "#fff" : COLORS.textMid} /></span>
                  {menuOpen && <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden" }}>{m.label}</span>}
                </button>
              );
            };

            const dashboard = MENUS.find(m => m.id === "dashboard" && m.perfis.includes(usuario?.perfil));
            const graficos  = MENUS.find(m => m.id === "graficos"  && m.perfis.includes(usuario?.perfil));

            return (
              <>
                {dashboard && renderNavBtn(dashboard)}

                {MENU_SECTIONS.filter(s => s.perfis.some(p => p === usuario?.perfil)).map(sec => {
                  const isOpen = openSections.includes(sec.id);
                  const secHasActive = sec.items.some(i => aba === i.id || i.children?.some(c => aba === c.id));
                  return (
                    <div key={sec.id} style={{ marginTop: 4 }}>
                      <button onClick={() => toggleSection(sec.id)}
                        title={menuOpen ? undefined : sec.label}
                        style={{ display: "flex", alignItems: "center", gap: 10, width: "100%", padding: menuOpen ? "11px 12px" : "11px 0", justifyContent: menuOpen ? "flex-start" : "center", background: (isOpen || secHasActive) ? `${sec.color}11` : "transparent", border: "none", borderRadius: 10, cursor: "pointer", color: sec.color, fontSize: 11, fontWeight: 700, letterSpacing: "0.05em", textTransform: "uppercase", textAlign: "left", transition: "background .15s" }}
                        onMouseEnter={e => { e.currentTarget.style.background = `${sec.color}18`; }}
                        onMouseLeave={e => { e.currentTarget.style.background = (isOpen || secHasActive) ? `${sec.color}11` : "transparent"; }}>
                        <span style={{ flexShrink: 0, display: "flex" }}><Icon d={ICONS[sec.iconKey]} size={15} stroke={sec.color} /></span>
                        {menuOpen && <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden" }}>{sec.label}</span>}
                        {menuOpen && <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: isOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s", color: sec.color }}><path d="M6 9l6 6 6-6"/></svg>}
                      </button>

                      {isOpen && (
                        <div style={{ marginLeft: menuOpen ? 8 : 0, marginTop: 2, display: "flex", flexDirection: "column", gap: 1 }}>
                          {sec.items.filter(i => {
                            if (!i.perfis.includes(usuario?.perfil)) return false;
                            if (usuario?.permissoes && !canManage) {
                              if (!i.children) return usuario.permissoes.includes(i.id);
                              return i.children.some(c => usuario.permissoes.includes(c.id));
                            }
                            return true;
                          }).map(item => {
                            const hasChildren = (item.children?.length ?? 0) > 0;
                            const subOpen = openSubItems.includes(item.id);
                            const childActive = item.children?.some(c => aba === c.id);
                            const itemActive = aba === item.id;
                            return (
                              <div key={item.id}>
                                <button
                                  onClick={() => { if (hasChildren) toggleSubItem(item.id); else { navTo(item.id); if (isMobile) setMenuOpen(false); } }}
                                  title={menuOpen ? undefined : item.label}
                                  style={{ display: "flex", alignItems: "center", gap: 9, width: "100%", padding: menuOpen ? "11px 10px" : "11px 0", justifyContent: menuOpen ? "flex-start" : "center", background: (itemActive || childActive) ? `${sec.color}18` : "transparent", border: "none", borderLeft: menuOpen ? `2px solid ${(itemActive || childActive) ? sec.color : sec.color+"55"}` : "none", borderRadius: menuOpen ? "0 8px 8px 0" : 8, cursor: "pointer", color: (itemActive || childActive) ? sec.color : COLORS.text, fontSize: 13, fontWeight: (itemActive || childActive) ? 700 : 400, textAlign: "left", transition: "all .15s" }}
                                  onMouseEnter={e => { if (!itemActive && !childActive) e.currentTarget.style.background = `${sec.color}0d`; }}
                                  onMouseLeave={e => { if (!itemActive && !childActive) e.currentTarget.style.background = "transparent"; }}>
                                  <span style={{ flexShrink: 0, display: "flex" }}><Icon d={ICONS[item.iconKey]} size={15} stroke={(itemActive || childActive) ? sec.color : COLORS.textMid} /></span>
                                  {menuOpen && <span style={{ flex: 1, whiteSpace: "nowrap", overflow: "hidden" }}>{item.label}</span>}
                                  {menuOpen && hasChildren && <svg width="11" height="11" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round" style={{ flexShrink: 0, transform: subOpen ? "rotate(180deg)" : "rotate(0deg)", transition: "transform .2s" }}><path d="M6 9l6 6 6-6"/></svg>}
                                </button>
                                {hasChildren && subOpen && menuOpen && (
                                  <div style={{ marginLeft: 16, marginTop: 1, display: "flex", flexDirection: "column", gap: 1 }}>
                                    {item.children.filter(c => {
                                      if (!c.perfis.includes(usuario?.perfil)) return false;
                                      if (usuario?.permissoes && !canManage) return usuario.permissoes.includes(c.id);
                                      return true;
                                    }).map(child => {
                                      const cActive = aba === child.id;
                                      return (
                                        <button key={child.id} onClick={() => { navTo(child.id); if (isMobile) setMenuOpen(false); }}
                                          style={{ display: "flex", alignItems: "center", gap: 8, width: "100%", padding: "6px 10px", background: cActive ? `${sec.color}18` : "transparent", border: "none", borderLeft: `2px solid ${cActive ? sec.color : sec.color+"33"}`, borderRadius: "0 8px 8px 0", cursor: "pointer", color: cActive ? sec.color : COLORS.textMid, fontSize: 12, fontWeight: cActive ? 700 : 400, textAlign: "left", transition: "all .15s" }}
                                          onMouseEnter={e => { if (!cActive) e.currentTarget.style.background = `${sec.color}0d`; }}
                                          onMouseLeave={e => { if (!cActive) e.currentTarget.style.background = "transparent"; }}>
                                          <span style={{ width: 6, height: 6, borderRadius: "50%", background: cActive ? sec.color : COLORS.border, flexShrink: 0 }} />
                                          <span style={{ whiteSpace: "nowrap", overflow: "hidden" }}>{child.label}</span>
                                        </button>
                                      );
                                    })}
                                  </div>
                                )}
                              </div>
                            );
                          })}
                        </div>
                      )}
                    </div>
                  );
                })}

                {graficos && renderNavBtn(graficos, { marginTop: 8 })}
              </>
            );
          })()}
        </nav>

        {/* Botão colapsar */}
        <button onClick={() => setMenuOpen(o => !o)}
          style={{ margin: "8px", padding: "9px", background: "rgba(46,122,0,0.07)", border: `1px solid ${COLORS.border}`, borderRadius: 9, cursor: "pointer", color: COLORS.textMid, display: isMobile ? "none" : "flex", alignItems: "center", justifyContent: menuOpen ? "flex-end" : "center", transition: "background .15s" }}>
          <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            {menuOpen ? <path d="M15 18l-6-6 6-6"/> : <path d="M9 18l6-6-6-6"/>}
          </svg>
        </button>
      </aside>

      {/* ── Main area ── */}
      <div style={{ flex: 1, display: "flex", flexDirection: "column", minWidth: 0, marginLeft: isMobile ? 0 : sideW + 24, transition: "margin-left .22s cubic-bezier(.4,0,.2,1)", background: COLORS.bg }}>
        <header style={{ position: "sticky", top: 12, zIndex: 50, background: COLORS.sidebar, borderRadius: 14, boxShadow: `0 0 0 1px ${COLORS.border}, 0 4px 16px rgba(0,0,0,0.07)`, margin: "0 12px", padding: "0 24px", height: 56, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
          {/* Esquerda: hamburger (mobile) + ícone + label da página */}
          <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
            {isMobile && (
              <button onClick={() => setMenuOpen(o => !o)} style={{ background: "none", border: "none", cursor: "pointer", padding: "4px 6px", color: COLORS.textMid, display: "flex", alignItems: "center", borderRadius: 8 }}>
                <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.2" strokeLinecap="round"><line x1="3" y1="6" x2="21" y2="6"/><line x1="3" y1="12" x2="21" y2="12"/><line x1="3" y1="18" x2="21" y2="18"/></svg>
              </button>
            )}
            {ge && <span style={{ display: "flex", color: COLORS.textMid }}><Icon d={ICONS[ge.iconKey]} size={19} /></span>}
            {ge && <span style={{ fontWeight: 700, fontSize: 17, color: COLORS.text }}>{ge.label}</span>}
          </div>

          {/* Direita: seletor empresa + logo + data + dark mode + user chip + gear */}
          <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
            <img src={topbarLogoSrc} alt="Olávio" style={{ height: 44, objectFit: "contain" }} />
            {appVer && <span style={{ fontSize: 11, color: COLORS.textLight, fontWeight: 500, letterSpacing: "0.03em" }}>v{appVer}</span>}
            {/* Seletor de empresa global */}
            {empresas.length > 1 && (() => {
              const logosAll = empresas.filter(e => e.logo_base64);
              const emSel = empresaFiltroId ? empresas.find(e => String(e.id) === empresaFiltroId) : null;
              return (
                <div ref={empDropRef} style={{ position: "relative" }}>
                  <button onClick={() => setEmpDrop(p => !p)} style={{
                    display: "flex", alignItems: "center", gap: 7, padding: "5px 10px",
                    borderRadius: 8, border: `1.5px solid ${empresaFiltroId ? COLORS.primary : COLORS.border}`,
                    background: empresaFiltroId ? COLORS.primaryBg : COLORS.surface,
                    cursor: "pointer", outline: "none", boxShadow: "0 1px 3px rgba(30,41,59,0.06)"
                  }}>
                    {emSel
                      ? emSel.logo_base64
                        ? <img src={emSel.logo_base64} alt={emSel.nome} style={{ height: 22, maxWidth: 60, objectFit: "contain" }} />
                        : <span style={{ fontSize: 13, fontWeight: 700, color: COLORS.primary }}>{emSel.nome}</span>
                      : logosAll.length > 0
                        ? <div style={{ display: "flex", alignItems: "center", gap: 4 }}>
                            {logosAll.slice(0, 3).map((em, i) => (
                              <div key={em.id} style={{ display: "flex", alignItems: "center", gap: 4 }}>
                                {i > 0 && <div style={{ width: 1, height: 16, background: COLORS.border }} />}
                                <img src={em.logo_base64} alt={em.nome} style={{ height: 20, maxWidth: 50, objectFit: "contain" }} />
                              </div>
                            ))}
                          </div>
                        : <span style={{ fontSize: 13, color: COLORS.text }}>Todas as empresas</span>
                    }
                    <span style={{ fontSize: 10, color: COLORS.textLight, marginLeft: 2 }}>▾</span>
                  </button>
                  {empDrop && (
                    <div style={{
                      position: "absolute", top: "calc(100% + 6px)", right: 0, zIndex: 9999,
                      background: COLORS.surface, border: `1px solid ${COLORS.border}`,
                      borderRadius: 10, boxShadow: "0 4px 16px rgba(30,41,59,0.13)", minWidth: 200, overflow: "hidden"
                    }}>
                      {[{ id: "", nome: "Todas as empresas", logo_base64: null }, ...empresas].map((em, i) => {
                        const sel = String(em.id) === String(empresaFiltroId) || (!em.id && !empresaFiltroId);
                        const logosGrupo = !em.id ? empresas.filter(e => e.logo_base64) : [];
                        return (
                          <button key={em.id || "all"} onClick={() => { setEmpresaFiltroId(String(em.id)); setEmpDrop(false); }} style={{
                            display: "flex", alignItems: "center", gap: 10, width: "100%", padding: "9px 14px",
                            background: sel ? COLORS.primaryBg : "transparent", border: "none",
                            borderTop: i > 0 ? `1px solid ${COLORS.border}` : "none",
                            cursor: "pointer", textAlign: "left"
                          }}>
                            {em.logo_base64
                              ? <img src={em.logo_base64} alt={em.nome} style={{ height: 24, width: 48, objectFit: "contain", flexShrink: 0 }} />
                              : em.id
                                ? <div style={{ width: 48, height: 24, borderRadius: 4, background: COLORS.bg, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 14, flexShrink: 0 }}>🏢</div>
                                : logosGrupo.length > 0
                                  ? <div style={{ display: "flex", alignItems: "center", gap: 3, width: 48, flexShrink: 0 }}>
                                      {logosGrupo.slice(0, 2).map((le, li) => (
                                        <div key={le.id} style={{ display: "flex", alignItems: "center", gap: 3 }}>
                                          {li > 0 && <div style={{ width: 1, height: 14, background: COLORS.border }} />}
                                          <img src={le.logo_base64} alt={le.nome} style={{ height: 18, maxWidth: 20, objectFit: "contain" }} />
                                        </div>
                                      ))}
                                    </div>
                                  : <div style={{ width: 48, height: 24, display: "flex", alignItems: "center", justifyContent: "center", fontSize: 16, flexShrink: 0 }}>🏢</div>
                            }
                            <span style={{ fontSize: 13, fontWeight: sel ? 700 : 400, color: sel ? COLORS.primary : COLORS.text }}>{em.nome}</span>
                            {sel && <span style={{ marginLeft: "auto", color: COLORS.primary, fontWeight: 900 }}>✓</span>}
                          </button>
                        );
                      })}
                    </div>
                  )}
                </div>
              );
            })()}
            <span style={{ fontSize: 13, color: COLORS.textLight }}>
              {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long" })}
            </span>
            {/* Dark mode */}
            <button onClick={toggleDark} title={darkMode ? "Modo claro" : "Modo noturno"}
              style={{ background: COLORS.surface, border: `1.5px solid ${COLORS.border}`, borderRadius: 8, padding: "6px 9px", cursor: "pointer", display: "flex", alignItems: "center", color: COLORS.textMid, transition: "background .15s", boxShadow: "0 1px 3px rgba(30,41,59,0.06)" }}>
              {darkMode
                ? <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="5"/><line x1="12" y1="1" x2="12" y2="3"/><line x1="12" y1="21" x2="12" y2="23"/><line x1="4.22" y1="4.22" x2="5.64" y2="5.64"/><line x1="18.36" y1="18.36" x2="19.78" y2="19.78"/><line x1="1" y1="12" x2="3" y2="12"/><line x1="21" y1="12" x2="23" y2="12"/><line x1="4.22" y1="19.78" x2="5.64" y2="18.36"/><line x1="18.36" y1="5.64" x2="19.78" y2="4.22"/></svg>
                : <svg width="15" height="15" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z"/></svg>
              }
            </button>
            {/* User chip */}
            <div style={{ display: "flex", alignItems: "center", gap: 8, background: COLORS.surface, border: `1.5px solid ${COLORS.border}`, borderRadius: 10, padding: "6px 12px 6px 8px", boxShadow: "0 1px 3px rgba(30,41,59,0.06)" }}>
              <div style={{ width: 26, height: 26, borderRadius: "50%", background: "linear-gradient(135deg,#1f5500,#2e7a00)", display: "flex", alignItems: "center", justifyContent: "center" }}>
                <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="#fff" strokeWidth="2.2" strokeLinecap="round" strokeLinejoin="round"><path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"/><circle cx="12" cy="7" r="4"/></svg>
              </div>
              <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.2 }}>
                <span style={{ fontSize: 13, fontWeight: 600, color: COLORS.text }}>{usuario?.nome || "Administrador"}</span>
                <span style={{ fontSize: 10, color: COLORS.textLight, textTransform: "capitalize" }}>{usuario?.perfil || ""}</span>
              </div>
              <button onClick={handleLogout} title="Sair"
                style={{ background: "none", border: "none", cursor: "pointer", color: COLORS.textLight, fontSize: 16, marginLeft: 4, padding: "2px 4px", borderRadius: 5, lineHeight: 1 }}
                onMouseEnter={e => e.currentTarget.style.color = COLORS.danger}
                onMouseLeave={e => e.currentTarget.style.color = COLORS.textLight}>
                ⏏
              </button>
            </div>
            {/* Gear — administrador, gestor e rh */}
            {["administrador","gestor","rh"].includes(usuario?.perfil) && (
              <button onClick={() => setConfigOpen(o => !o)} title="Configurações"
                style={{ background: configOpen ? COLORS.primaryBg : COLORS.surface, border: `1.5px solid ${configOpen ? COLORS.primary : COLORS.border}`, borderRadius: 8, padding: "6px 9px", cursor: "pointer", display: "flex", alignItems: "center", color: configOpen ? COLORS.primary : COLORS.textMid, transition: "all .15s", boxShadow: "0 1px 3px rgba(30,41,59,0.06)" }}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"><circle cx="12" cy="12" r="3"/><path d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06A1.65 1.65 0 0 0 4.68 15a1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06A1.65 1.65 0 0 0 9 4.68a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06A1.65 1.65 0 0 0 19.4 9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z"/></svg>
              </button>
            )}
          </div>
        </header>
        {/* Modal Configurações */}
        {configOpen && (
          <div style={{ position: "fixed", inset: 0, zIndex: 300, display: "flex", alignItems: "flex-start", justifyContent: "flex-end", paddingTop: 68, paddingRight: 16 }} onClick={() => setConfigOpen(false)}>
            <div onClick={e => e.stopPropagation()} style={{ background: "#FFFFFF", border: `1px solid ${COLORS.border}`, borderRadius: 16, boxShadow: "0 8px 32px rgba(15,23,42,0.14)", width: 260, overflow: "hidden" }}>
              <div style={{ padding: "12px 16px", borderBottom: `1px solid ${COLORS.border}`, fontWeight: 700, fontSize: 13, color: COLORS.textMid, textTransform: "uppercase", letterSpacing: 0.5 }}>
                ⚙️ Configurações
              </div>
              {[
                { id: "usuarios",    label: "Usuarios",       desc: "Gerenciar acessos ao sistema",     perfis: ["administrador","gestor","rh"] },
                { id: "empresas",    label: "Empresas",       desc: "Cadastrar e editar empresas",      perfis: ["administrador","gestor"] },
                { id: "nfe-config",  label: "🧾 NFS-e",       desc: "Configuração de nota fiscal",      perfis: ["administrador"] },
                { id: "banco-inter", label: "🏦 Banco Inter", desc: "Configuração de boleto automático",perfis: ["administrador"] },
              ].filter(item => item.perfis.includes(usuario?.perfil)).map(item => (
                <button key={item.id} onClick={() => { setAba(item.id); setConfigOpen(false); }} style={{
                  width: "100%", display: "flex", flexDirection: "column", alignItems: "flex-start",
                  padding: "12px 16px", background: aba === item.id ? COLORS.primaryBg : "transparent",
                  border: "none", borderBottom: `1px solid ${COLORS.border}`, cursor: "pointer",
                  textAlign: "left", transition: "background .15s",
                }}>
                  <span style={{ fontWeight: 600, fontSize: 14, color: aba === item.id ? COLORS.primary : COLORS.text }}>{item.label}</span>
                  <span style={{ fontSize: 11, color: COLORS.textMid, marginTop: 2 }}>{item.desc}</span>
                </button>
              ))}
              <BotaoBackup token={token} onClose={() => setConfigOpen(false)} />
            </div>
          </div>
        )}

        {/* Content */}
        <main style={{ flex: 1, padding: isMobile ? "16px 12px" : 28, overflowY: "auto", background: COLORS.bg }}>
          {/* Banner de boas-vindas — só no Dashboard */}
          {aba === "dashboard" && (
            <div style={{ marginBottom: 22, padding: "14px 20px", background: COLORS.surface, border: `1px solid ${COLORS.border}`, borderRadius: 16, display: "flex", alignItems: "center", justifyContent: "space-between", flexWrap: "wrap", gap: 8, boxShadow: "0 1px 3px rgba(30,41,59,0.05)" }}>
              <div>
                <div style={{ fontSize: 15, fontWeight: 700, color: COLORS.text }}>
                  Bem-vindo, {usuario?.nome?.split(" ")[0] || "Usuário"}! 👋
                </div>
                {(empresaSel || tenantNome) && (
                  <div style={{ fontSize: 12, color: COLORS.textMid, marginTop: 2 }}>
                    <span style={{ color: COLORS.textLight }}>Empresa:</span> <strong style={{ color: COLORS.primary }}>{empresaSel ? empresaSel.nome : tenantNome}</strong>
                  </div>
                )}
              </div>
              <div style={{ fontSize: 12, color: COLORS.textLight }}>
                {new Date().toLocaleDateString("pt-BR", { weekday: "long", day: "numeric", month: "long", year: "numeric" })}
              </div>
            </div>
          )}

          {aba === "dashboard"    && <Dashboard employees={employeesFiltrados} afastamentos={afastamentos} escala={escala} ponto={ponto} observacoes={observacoes} setObservacoes={setObservacoes} token={token} usuario={usuario} setAba={setAba} />}

          {/* Propostas e Contratos */}
          {aba === "pc-propostas" && <Contratos key="pc-propostas" token={token} setores={setores} setSetores={setSetores} abaInicial="propostas" hideTabs empresaFiltroId={empresaFiltroId} />}
          {aba === "pc-contratos" && <Contratos key="pc-contratos" token={token} setores={setores} setSetores={setSetores} abaInicial="contratos" hideTabs />}
          {aba === "pc-clientes"  && <Contratos key="pc-clientes"  token={token} setores={setores} setSetores={setSetores} abaInicial="clientes"  hideTabs />}

          {/* Financeiro */}
          {aba === "financeiro"   && <FinanceiroDashboard token={token} />}
          {aba === "cobrancas"    && <Contratos key="cobrancas" token={token} setores={setores} setSetores={setSetores} abaInicial="cobrancas" hideTabs />}
          {(aba === "pix-em-lotes" || aba === "pix-lotes-lancamentos") && <PixEmLotes key="pix-lancamentos" abaInicial="lancamentos" token={token} usuario={usuario} employees={employeesFiltrados} empresaFiltroId={empresaFiltroId} />}
          {aba === "pix-lotes-dashboard"   && <PixEmLotes key="pix-dashboard"   abaInicial="dashboard"   token={token} usuario={usuario} employees={employeesFiltrados} empresaFiltroId={empresaFiltroId} />}
          {aba === "pix-lotes-remessas"    && <PixEmLotes key="pix-remessas"    abaInicial="lotes"       token={token} usuario={usuario} employees={employeesFiltrados} empresaFiltroId={empresaFiltroId} />}
          {aba === "pix-lotes-conciliacao" && <PixEmLotes key="pix-conciliacao" abaInicial="conciliacao" token={token} usuario={usuario} employees={employeesFiltrados} empresaFiltroId={empresaFiltroId} />}

          {/* RH */}
          {(aba === "funcionarios" || aba === "func-efetivos") && <Funcionarios key="func-efetivos" abaInicial="efetivos" employees={employeesFiltrados} setEmployees={setEmployees} setores={setores} empresas={empresas} setEmpresas={setEmpresas} setPonto={setPonto} setAfastamentos={setAfastamentos} setEscala={setEscala} token={token} usuario={usuario} tenantDominio={tenantDominio} />}
          {aba === "func-pre-cadastro" && <Funcionarios key="func-pre-cadastro" abaInicial="pre-cadastro" employees={employeesFiltrados} setEmployees={setEmployees} setores={setores} empresas={empresas} setEmpresas={setEmpresas} setPonto={setPonto} setAfastamentos={setAfastamentos} setEscala={setEscala} token={token} usuario={usuario} tenantDominio={tenantDominio} />}
          {aba === "ponto"        && <PontoModulo employees={employeesFiltrados} ponto={ponto} setPonto={setPonto} token={token} usuario={usuario} setores={setores} empresas={empresas} abaInicial={pontoAba} onAbaChange={setPontoAba} />}
          {aba === "ponto-faltas"       && <PontoTerceiro employees={employeesFiltrados} token={token} usuario={usuario} afastamentosGlobal={afastamentos} setAfastamentos={setAfastamentos} />}
          {aba === "ponto-afastamentos" && <FeriasAfastamentos employees={employeesFiltrados} setEmployees={setEmployees} afastamentos={afastamentos} setAfastamentos={setAfastamentos} token={token} usuario={usuario} abaInicial="afastamentos" hideTabSwitch />}
          {aba === "ponto-ferias"       && <FeriasAfastamentos employees={employeesFiltrados} setEmployees={setEmployees} afastamentos={afastamentos} setAfastamentos={setAfastamentos} token={token} usuario={usuario} abaInicial="ferias" hideTabSwitch />}
          {aba === "ponto-historico"    && <HistoricoAfastamentos employees={employeesFiltrados} afastamentos={afastamentos} token={token} usuario={usuario} />}
          {aba === "escala"       && <Escala employees={employeesFiltrados} setEmployees={setEmployees} token={token} usuario={usuario} setores={setores} setAba={setAba} />}
          {aba === "setores"      && <Setores setores={setores} setSetores={setSetores} token={token} usuario={usuario} employees={employeesFiltrados} />}
          {aba === "ferias-beneficios" && <FeriasBeneficios employees={employeesFiltrados} setEmployees={setEmployees} afastamentos={afastamentos} setAfastamentos={setAfastamentos} token={token} usuario={usuario} />}
          {aba === "vt-bhbus"     && <VTRecarga token={token} usuario={usuario} tipo="bhbus" />}
          {aba === "vt-otimo"    && <VTRecarga token={token} usuario={usuario} tipo="otimo" />}
          {aba === "vt-gasolina" && <VTRecarga token={token} usuario={usuario} tipo="gasolina" />}
          {aba === "vt-betimcard"&& <VTRecarga token={token} usuario={usuario} tipo="betimcard" />}
          {aba === "vt-outros"   && <VTRecarga token={token} usuario={usuario} tipo="outros" />}
          {aba === "beneficios-va-normal" && <VARecarga token={token} usuario={usuario} />}
          {aba === "beneficios-va-flex"   && <VAFlexRecarga token={token} />}
          {aba === "financeiro-adiantamento" && <Financeiro employees={employeesFiltrados} ponto={ponto} token={token} />}
          {aba === "financeiro-relatorio"   && <Financeiro employees={employeesFiltrados} ponto={ponto} token={token} />}
          {aba === "financeiro-adiantamento-sal" && <Adiantamento employees={employeesFiltrados} token={token} />}
          {aba === "comprovantes" && <Comprovantes employees={employeesFiltrados} token={token} />}

          {/* Suprimentos */}
          {aba === "suprimentos-fornecedores" && <Suprimentos token={token} employees={employees} setores={setores} abaInicial="fornecedores" />}
          {aba === "suprimentos-estoque"      && <Suprimentos token={token} employees={employees} setores={setores} abaInicial="estoque" />}
          {aba === "suprimentos-itens-func"   && <Suprimentos token={token} employees={employees} setores={setores} abaInicial="itens-func" />}
          {aba === "rh-itens-func"            && <Suprimentos token={token} employees={employees} setores={setores} abaInicial="itens-func" />}

          {/* Gráficos */}
          {aba === "graficos"     && <Graficos employees={employeesFiltrados} ponto={ponto} afastamentos={afastamentos} />}

          {/* Configurações (gestor + administrador) */}
          {aba === "config-usuarios" && <Usuarios token={token} usuario={usuario} />}

          {/* Config (gear menu) */}
          {aba === "empresas"     && <Empresas empresas={empresas} setEmpresas={setEmpresas} token={token} usuario={usuario} />}
          {aba === "usuarios"     && <Usuarios token={token} usuario={usuario} />}
          {aba === "clientes"     && <Clientes token={token} usuario={usuario} />}
          {aba === "nfe-config"   && <ConfigNFe token={token} />}
          {aba === "banco-inter"  && <ConfigBancoInter token={token} />}
          {aba === "backups"      && <BackupScreen token={token} usuario={usuario} />}
        </main>
      </div>
    </div>
  );
}
